import {
  Box,
  Button,
  Flex,
  Heading,
  Modal,
  ModalBody,
  ModalCloseButton,
  ModalContent,
  ModalFooter,
  ModalHeader,
  ModalOverlay,
  Select,
  Spacer,
  Stack,
  Table,
  Tbody,
  Td,
  Text,
  Th,
  Thead,
  Tr,
  useDisclosure,
  useToast,
} from "@chakra-ui/react";
import {
  CategoryScale,
  Chart as ChartJS,
  Legend,
  LineElement,
  LinearScale,
  PointElement,
  Tooltip,
} from "chart.js";
import ChartDataLabels from "chartjs-plugin-datalabels";
import dayjs from "dayjs";
import Decimal from 'decimal.js';
import React, {
  useCallback,
  useContext,
  useEffect,
  useRef,
  useState
} from "react";
import * as XLSX from "xlsx";
import { CurrencyNameJPY, DCFItems, EquipmentUsageType, Messages, PaybackPeriodItems, ProfitAndLossItems, PurposeType, RecoverySimulationItems, SettingKey, WorkflowId, isSimpleApplicationForm, isProfitLossHidden } from "../../contexts/constants";
import {
  BomData,
  CBomDataWithMonthly,
  ConfirmorInfo,
  Currency,
  EquipmentType,
  MonthlyProfitAndLossData,
  MonthValue,
  MonthlyExpenseData,
  NonWorkingHoursType,
  OriginalProfitAndLossByMonthTable,
  OriginalProfitAndLossTable,
  PagePermissions,
  PartNo,
  ProcessBomRowForCalculation,
  ProductionCapacityBenchmark,
  ProductionLineType,
  ProjectAdditionalInfo,
  ProjectContextType,
  ProjectWithRdsData,
  UniqueProcessType,
  UpdateImprovementEffectByProcess,
  UpdateInvestmentProfitLossData,
  UpdateSummaryInputData,
  WorkingDaysType,
  RationalizationSummaryInputData,
  RationalizationProfitLossItem,
  RationalizationProfitLossItemType,
  RationalizationInvestmentProfitLossData,
  RationalizationProfitAndLossByYear,
  RationalizationImprovementEffectByProcess,
  RationalizationImprovementEffectItem,
  RationalizationImprovementTotalItem,
} from "../../contexts/types";
import { WebAPIContext } from '../../contexts/WebAPIContext';
import { useAppSelector } from "../../store/hooks";
import {
  calcAggregateCapexByMonth,
  calcCapexAmountByMonth,
  calcCapexAmountByYear,
  calcDevSalesByPartNoMonth,
  calcDiscountedCashFlow,
  calcEquipmentDepreciationByYearMonth,
  calcInterestRateByYear,
  calcLabelsByMonth,
  calcMoldSalesByPartNoMonth,
  calcMonthlyRecovery,
  calcMonthsBetweenDates,
  calcNopat,
  calcNopatByMonth,
  calcPartNoSalesMergeByYear,
  calcPaybackPeriod,
  calcPresentValue,
  calcProductSalesByMonth,
  calcProductSalesByYear,
  calcProfitRate,
  calcProfitRateArray,
  calcRecoverySimulation,
  calcRoic,
  calcThroughputRate,
  calcTotalCapexAmount,
  calcTotalCycleTimeByMonth,
  calcTotalMaterialCostsByPartNoWithMonthlyData,
  calcTotalQuantityByMonth,
  calcTotalSalesByPartNoMonth,
  calcTotalSalesByPartNoYear,
  calcUndepreciated,
  calculateCustomerSpecifiedPartsCostByMonth,
  calculateProjectSalesSum,
  calculateScrapCostByMonth,
  convertEquipmentData,
  convertMonthlyExpenseData,
  convertMonthlyToYearly,
  convertMonthlyValueData,
  convertQuantitiesByPartNoMonth,
  convertToStringArray,
  convertToYearMonthObject,
  generateYearMonthList,
  getFiscalYearsString,
  getMaterialCostByPartNoByMonth,
  getMaterialCostByPartNoYear,
  getPricesByPartNoMonth,
  getPricesByPartNoYear,
  getProjectSalesByMonth,
  getQuantitiesByPartNoMonth,
  getQuantitiesByPartNoYear,
  getTotalFiscalYearsByPartNo,
  getTotalFiscalYearsByProject,
  getFiscalYearsByProjectDates,
  getTotalMaterialCostByMonth,
  getTotalMaterialCostByYear,
  getTotalSalesByMonth,
  getTotalSalesMoldDevByMonth,
  getTotalSalesMoldDevByYear,
  mergeSalesAndSubtractCosts,
  mergeYearMonthValues,
  roundNumericStringsDeep,
  subtractDecimalArray,
  subtractYearMonthValues,
  sumDecimalArray,
} from "../../utils/calculation";
import {
  convertCurrency,
} from '../../utils/CurrencyConverter';
import { convertCurrencyByMonth } from '../../utils/reviewCalculation';
import {
  formatCostSum,
  formatNumber,
  formatDateByLocale,
  hasDecimal,
  getLocalCurrencyId,
  getTaxRateFromSnapshot,
  resolveDisplayPartNo,
} from '../../utils/utils';
import { useTranslation } from "react-i18next";
import { getConfirmor, updateConfirm } from "../../utils/workflow";
import FileUploadModal from "../Modal/FileUploadModal";
import ConfirmorList from "./ConfirmorList";
import { DCFExplanationPopover } from "./DCFExplanationPopover";
import { ProjectContext, getPermissions } from "./InvestmentScreeningDetail";
import { PaybackPeriodPopover } from "./PaybackPeriodPopover";
import { ROICExplanationPopover } from "./ROICExplanationPopover";
import { ImprovementTotalExplanationPopover } from "./Popover/ImprovementTotalExplanationPopover";
import { UpdateImprovementExplanationPopover } from "./Popover/UpdateImprovementExplanationPopover";
import { UpdateProfitLossExplanationPopover } from "./Popover/UpdateProfitLossExplanationPopover";
import { RationalizationBeforeProfitLossExplanationPopover } from "./Popover/RationalizationBeforeProfitLossExplanationPopover";
import { RationalizationAfterProfitLossExplanationPopover } from "./Popover/RationalizationAfterProfitLossExplanationPopover";
import { RationalizationImprovementExplanationPopover } from "./Popover/RationalizationImprovementExplanationPopover";
import { RationalizationImprovementTotalExplanationPopover } from "./Popover/RationalizationImprovementTotalExplanationPopover";
import { ProfitLossExplanationPopover } from "./ProfitLossExplanationPopover";
import { RecoverySimulationExplanationPopover } from "./RecoverySimulationExplanationPopover";

ChartJS.register(
  LineElement,
  PointElement,
  LinearScale,
  CategoryScale,
  Tooltip,
  Legend,
  ChartDataLabels,
);

interface Confirmor {
  projectProfitablityStatement?: {
    companyDivision?: string[];
    companyHeadDivision?: string[];
  };
}

/**
 * 新規投資申請画面(対象PJの損益試算)
 * @returns
 */
const ProjectProfitablityStatement: React.FC<any> = ({ projectId, monthlyRateMap, initialPurpose, initialApplicationFormType, initialProfitLossDisplayType }) => {

  // i18n
  const { t } = useTranslation();
  // ダイアログのタイトルを定義
  const TITLE = t("features.InvestmentScreening.ProjectProfitablityStatement.title");
  // Toastメッセージを表示するためのフック
  const toast = useToast();
  // WebAPIContextを使用してAPI操作を行う
  const WebAPI = useContext(WebAPIContext)
  // モーダルの開閉状態を管理するフック
  const { isOpen, onOpen, onClose } = useDisclosure();
  // ログインユーザーの権限情報を取得
  const { userPermissions, reviewDate, currencyData } = useContext(ProjectContext) as ProjectContextType;
  // Reduxからログイン中の従業員IDを取得
  const employeeId = useAppSelector((state) => state.employee.employeeId);
  // Reduxからログイン中の部署IDを取得
  const divisionId = useAppSelector((state) => state.employee.divisionId);
  // ファイルアップロードモーダルの開閉状態を管理するフック
  const { isOpen: isOpenFileUploadModal, onOpen: onOpenFileUploadModal, onClose: onCloseFileUploadModal } = useDisclosure();

  // -----------------------------
  // style
  // -----------------------------

  const defaultTableStyle = {
    size: "sm",
    variant: "simple",
    sx: {
      tableLayout: "auto",
    },
  };

  const defaultThStyle: React.CSSProperties = {
    textAlign: "center",
    height: "20px",
    minWidth: "190px",
    whiteSpace: "nowrap",
  };

  const defaultTdStyle: React.CSSProperties = {
    textAlign: "right",
    height: "20px",
    whiteSpace: "nowrap",
  };

  // -----------------------------
  // useRef
  // -----------------------------

  const refs: Record<string, React.RefObject<HTMLButtonElement>> = {
    companyDivision: useRef<HTMLButtonElement>(null),
    salesDivision: useRef<HTMLButtonElement>(null),
    procurementDivision: useRef<HTMLButtonElement>(null),
  };

  // -----------------------------
  // useState
  // -----------------------------

  // #region 【DBデータ】

  // 【DBデータ】 T_CEAS_PROJECT データを管理
  const [capexProjectData, setCapexProjectData] = useState<ProjectWithRdsData>({});
  /** ローカル通貨ID（会社/工場スナップショットから取得） */
  const localCurrencyId = getLocalCurrencyId(capexProjectData?.companyPlantSnapshot);

  /** 簡易モード判定（capexProjectData未取得時は親から渡された初期値で判定 → ちらつき防止） */
  const isSimpleMode = isSimpleApplicationForm(
    capexProjectData?.purpose ?? initialPurpose,
    capexProjectData?.applicationFormType ?? initialApplicationFormType,
  );
  /** 損益非表示モード判定（簡易様式かつ「表示しない」選択時。表・グラフを一切表示しない） */
  const isProfitLossHiddenMode = isProfitLossHidden(
    capexProjectData?.purpose ?? initialPurpose,
    capexProjectData?.applicationFormType ?? initialApplicationFormType,
    capexProjectData?.profitLossDisplayType ?? initialProfitLossDisplayType,
  );
  /** 汎用設備かどうか */
  const isGeneralPurpose = capexProjectData?.equipmentUsageType === EquipmentUsageType.GeneralPurpose.code;
  const labelQuantity = isGeneralPurpose
    ? t("features.InvestmentScreening.ProjectProfitablityStatement.label.estimatedQuantity")
    : t("features.InvestmentScreening.ProjectProfitablityStatement.label.salesQuantity");
  const labelPrice = isGeneralPurpose
    ? t("features.InvestmentScreening.ProjectProfitablityStatement.label.estimatedPrice")
    : t("features.InvestmentScreening.ProjectProfitablityStatement.label.salesPrice");
  const labelQuantityAndPrice = isGeneralPurpose
    ? t("features.InvestmentScreening.ProjectProfitablityStatement.label.estimatedQuantityAndPrice")
    : t("features.InvestmentScreening.ProjectProfitablityStatement.label.salesQuantityAndPrice");
  // 【DBデータ】 M_CEAS_PART_NO データを管理
  const [capexPartNoData, setCapexPartNoData] = useState<PartNo[]>([]);
  // 【DBデータ】 M_CEAS_EQUIPMENT データを管理
  const [capexEquipmentData, setCapexEquipmentData] = useState<EquipmentType[]>([]);
  // 【DBデータ】 M_CEAS_PRODUCTION_LINE データを管理
  const [productionLineData, setProductionLineData] = useState<ProductionLineType>({});
  // 【DBデータ】 M_CEAS_UNIQUE_PROCESS データを管理
  const [uniqueProcessData, setUniqueProcessData] = useState<UniqueProcessType[]>([]);
  // 【DBデータ】 R_CEAS_BOM データを管理
  const [bomData, setBomData] = useState<BomData[]>([]);
  // 【DBデータ】 M_CEAS_TAX データを管理
  const [taxRate, setTaxRate] = useState<Decimal>(new Decimal(0));
  // 【DBデータ】 M_CEAS_SETTING .discountRate データを管理
  const [discountRate, setDiscountRate] = useState<Decimal>(new Decimal(0));
  // 【DBデータ】 r_ceas_workflow データを管理
  const [confirmor, setConfirmor] = useState<Confirmor | null>(null);
  // 【DBデータ】 T_CEAS_PROJECT_ADDITIONAL_INFO データを管理
  const [projectAdditionalInfo, setProjectAdditionalInfo] = useState<ProjectAdditionalInfo>({});

  // #endregion

  // 振り返りモード: 月別レートで変換するラッパー関数（コンポーネントレベル）
  // render関数内の表示時変換で使用。yearMonth引数なしの場合は従来の単一レートにフォールバック。
  const convertWithRate = useCallback((
    amount: number | Decimal,
    from: string,
    to: string,
    yearMonth?: string
  ): Decimal => {
    if (reviewDate && yearMonth && monthlyRateMap && monthlyRateMap.size > 0) {
      return convertCurrencyByMonth(amount, from, to, yearMonth, monthlyRateMap, currencyData);
    }
    return convertCurrency(amount, from, to, currencyData);
  }, [reviewDate, monthlyRateMap, currencyData]);

  // #region 【画面項目】

  // #region 【画面項目】【共通】

  // 【画面項目】【共通】 年度配列
  const [fiscalYearsString, setFiscalYearsString] = useState<string[]>([]);

  // #endregion

  // #region 【画面項目】【PartNo別】

  // 【画面項目】【PartNo別】 販売数量
  const [quantityByPartNo, setQuantityByPartNo] = useState<{ partNo: string, quantities: Decimal[] }[]>([]);
  // 【画面項目】【PartNo別】 販売単価（加重平均）
  const [priceByPartNo, setPriceByPartNo] = useState<{ partNo: string, prices: Decimal[] }[]>([]);
  // 【画面項目】【PartNo別】 製品売上
  const [productSalesByPartNo, setProductSalesByPartNo] = useState<{ partNo: string, totalSales: Decimal[] }[]>([]);
  // 【画面項目】【PartNo別】 金型売上
  const [moldSalesByPartNo, setMoldSalesByPartNo] = useState<{ partNo: string, totalSales: Decimal[] }[]>([]);
  // 【画面項目】【PartNo別】 開発費売上
  const [devSalesByPartNo, setDevSalesByPartNo] = useState<{ partNo: string, totalSales: Decimal[] }[]>([]);
  // 【画面項目】【PartNo別】 売上合計
  const [totalSalesByPartNo, setTotalSalesByPartNo] = useState<{ partNo: string, totalSales: Decimal[] }[]>([]);
  // 【画面項目】【PartNo別】 材料単価（年度別加重平均）
  const [materialCostByPartNo, setMaterialCostByPartNo] = useState<{ partNo: string, materialCosts: Decimal[] }[]>([]);
  // 【画面項目】【PartNo別】 材料費
  const [totalMaterialCostByPartNo, setTotalMaterialCostByPartNo] = useState<{ partNo: string, totalMaterialCosts: Decimal[] }[]>([]);
  // 【画面項目】【PartNo別】 スループット額
  const [throughputByPartNo, setThroughputByPartNo] = useState<{ partNo: string, throughputs: Decimal[] }[]>([]);
  // 【画面項目】【PartNo別】 スループット率
  const [throughputRateByPartNo, setThroughputRateByPartNo] = useState<{ partNo: string, throughputRate: Decimal[] }[]>([]);

  // #endregion

  // #region 【画面項目】【PartNo別 - 表示用（選択通貨）】

  // 【表示用】【PartNo別】 販売単価（加重平均）
  const [displayPriceByPartNo, setDisplayPriceByPartNo] = useState<{ partNo: string, prices: Decimal[] }[]>([]);
  // 【表示用】【PartNo別】 製品売上
  const [displayProductSalesByPartNo, setDisplayProductSalesByPartNo] = useState<{ partNo: string, totalSales: Decimal[] }[]>([]);
  // 【表示用】【PartNo別】 金型売上
  const [displayMoldSalesByPartNo, setDisplayMoldSalesByPartNo] = useState<{ partNo: string, totalSales: Decimal[] }[]>([]);
  // 【表示用】【PartNo別】 開発費売上
  const [displayDevSalesByPartNo, setDisplayDevSalesByPartNo] = useState<{ partNo: string, totalSales: Decimal[] }[]>([]);
  // 【表示用】【PartNo別】 売上合計
  const [displayTotalSalesByPartNo, setDisplayTotalSalesByPartNo] = useState<{ partNo: string, totalSales: Decimal[] }[]>([]);
  // 【表示用】【PartNo別】 材料単価（年度別加重平均）
  const [displayMaterialCostByPartNo, setDisplayMaterialCostByPartNo] = useState<{ partNo: string, materialCosts: Decimal[] }[]>([]);
  // 【表示用】【PartNo別】 材料費
  const [displayTotalMaterialCostByPartNo, setDisplayTotalMaterialCostByPartNo] = useState<{ partNo: string, totalMaterialCosts: Decimal[] }[]>([]);
  // 【表示用】【PartNo別】 スループット額
  const [displayThroughputByPartNo, setDisplayThroughputByPartNo] = useState<{ partNo: string, throughputs: Decimal[] }[]>([]);

  // #endregion

  // #region 【年度別損益】【計算用】

  // 【年度別損益】【計算用】売上合計
  const [totalSalesByYear, setTotalSalesByYear] = useState<Decimal[]>([]);
  // 【年度別損益】【計算用】製品売上
  const [productSalesByYear, setProductSalesByYear] = useState<Decimal[]>([]);
  // 【年度別損益】【計算用】PJ単位金型回収
  const [moldSalesByYear, setMoldSalesByYear] = useState<Decimal[]>([]);
  // 【年度別損益】【計算用】PJ単位開発費回収
  const [devSalesByYear, setDevSalesByYear] = useState<Decimal[]>([]);
  // 【年度別損益】【計算用】PJ単位設備費回収
  const [equipmentSalesByYear, setEquipmentSalesByYear] = useState<Decimal[]>([]);
  // 【年度別損益】【計算用】売上原価
  const [salesCostByYear, setSalesCostByYear] = useState<Decimal[]>([]);
  // 【年度別損益】【計算用】材料費
  const [totalMaterialCostByYear, setTotalMaterialCostByYear] = useState<Decimal[]>([]);
  // 【年度別損益】【計算用】外注加工費
  const [outsideCostByYear, setOutsideCostByYear] = useState<Decimal[]>([]);
  // 【年度別損益】【計算用】生産工場労務費
  const [laborCostByYear, setLaborCostByYear] = useState<Decimal[]>([]);
  // 【年度別損益】【計算用】国内工場労務費
  const [domesticLaborCostByYear, setDomesticLaborCostByYear] = useState<Decimal[]>([]);
  // 【年度別損益】【計算用】生産工場経費
  const [factoryExpensesByYear, setFactoryExpensesByYear] = useState<Decimal[]>([]);
  // 【年度別損益】【計算用】生産工場経費-償却費以外の工場経費
  const [overHeadCostByYear, setOverHeadCostByYear] = useState<Decimal[]>([]);
  // 【年度別損益】【計算用】生産工場経費-減価償却費
  const [depreciationByYear, setDepreciationByYear] = useState<Decimal[]>([]);
  // 【年度別損益】【計算用】生産工場経費-投資対象設備
  const [equipmentDepreciationByYear, setEquipmentDepreciationByYear] = useState<Decimal[]>([]);
  // 【年度別損益】【計算用】生産工場経費-工場建物
  const [factoryCostByYear, setFactoryCostByYear] = useState<Decimal[]>([]);
  // 【年度別損益】【計算用】生産工場経費-過去PJの投資対象設備
  const [pastEquipmentCostByYear, setPastEquipmentCostByYear] = useState<Decimal[]>([]);
  // 【年度別損益】【計算用】生産工場経費-その他の設備
  const [otherEquipmentCostByYear, setOtherEquipmentCostByYear] = useState<Decimal[]>([]);
  // 【年度別損益】【計算用】国内工場経費
  const [domesticFactoryExpensesByYear, setDomesticFactoryExpensesByYear] = useState<Decimal[]>([]);
  // 【年度別損益】【計算用】国内工場経費-償却費以外の工場経費
  const [domesticOverHeadCostByYear, setDomesticOverHeadCostByYear] = useState<Decimal[]>([]);
  // 【年度別損益】【計算用】国内工場経費-減価償却費
  const [domesticDepreciationByYear, setDomesticDepreciationByYear] = useState<Decimal[]>([]);
  // 【年度別損益】【計算用】国内工場経費-投資対象設備
  const [domesticInvestmentDepreciationByYear, setDomesticInvestmentDepreciationByYear] = useState<Decimal[]>([]);
  // 【年度別損益】【計算用】国内工場経費-工場建物
  const [domesticFactoryBuildingDepreciationByYear, setDomesticFactoryBuildingDepreciationByYear] = useState<Decimal[]>([]);
  // 【年度別損益】【計算用】国内工場経費-その他の設備
  const [domesticOtherDepreciationByYear, setDomesticOtherDepreciationByYear] = useState<Decimal[]>([]);
  // 【年度別損益】【計算用】仕損費
  const [scrapCostByYear, setScrapCostByYear] = useState<Decimal[]>([]);
  // 【年度別損益】【計算用】販売費及び一般管理費
  const [sGAExpensesByYear, setSGAExpensesByYear] = useState<Decimal[]>([]);
  // 【年度別損益】【計算用】生産工場販管費
  const [appliedLaborCostByYear, setAppliedLaborCostByYear] = useState<Decimal[]>([]);
  // 【年度別損益】【計算用】国内工場販管費
  const [domesticSgaCostByYear, setDomesticSgaCostByYear] = useState<Decimal[]>([]);
  // 【年度別損益】【計算用】本社・販社販管費
  const [salesManagementCostByYear, setSalesManagementCostByYear] = useState<Decimal[]>([]);
  // 【年度別損益】【計算用】開発費
  const [devCostByYear, setDevCostByYear] = useState<Decimal[]>([]);
  // 【年度別損益】【計算用】ロイヤリティ
  const [royaltyByYear, setRoyaltyByYear] = useState<Decimal[]>([]);
  // 【年度別損益】【計算用】その他の経費
  const [otherCostByYear, setOtherCostByYear] = useState<Decimal[]>([]);
  // 【年度別損益】【計算用】営業利益
  const [profitByYear, setProfitByYear] = useState<Decimal[]>([]);
  // 【年度別損益】【計算用】営業利益率
  const [profitRateByYear, setProfitRateByYear] = useState<Decimal[]>([]);
  // 【年度別損益】【計算用】実質的営業利益率
  const [realOperatingProfitRateByYear, setRealOperatingProfitRateByYear] = useState<Decimal[]>([]);

  // #endregion

  // #region 【年度別損益】【画面表示用】

  // 【年度別損益】【画面表示用】売上合計
  const [displayTotalSalesByYear, setDisplayTotalSalesByYear] = useState<Decimal[]>([]);
  // 【年度別損益】【画面表示用】製品売上
  const [displayProductSalesByYear, setDisplayProductSalesByYear] = useState<Decimal[]>([]);
  // 【年度別損益】【画面表示用】 PJ単位金型回収
  const [displayMoldSalesByYear, setDisplayMoldSalesByYear] = useState<Decimal[]>([]);
  // 【年度別損益】【画面表示用】PJ単位開発費回収
  const [displayDevSalesByYear, setDisplayDevSalesByYear] = useState<Decimal[]>([]);
  // 【年度別損益】【画面表示用】PJ単位設備費回収
  const [displayEquipmentSalesByYear, setDisplayEquipmentSalesByYear] = useState<Decimal[]>([]);
  // 【年度別損益】【画面表示用】売上原価
  const [displaySalesCostByYear, setDisplaySalesCostByYear] = useState<Decimal[]>([]);
  // 【年度別損益】【画面表示用】材料費
  const [displayTotalMaterialCostByYear, setDisplayTotalMaterialCostByYear] = useState<Decimal[]>([]);
  // 【年度別損益】【画面表示用】外注加工費
  const [displayOutsideCostByYear, setDisplayOutsideCostByYear] = useState<Decimal[]>([]);
  // 【年度別損益】【画面表示用】生産工場労務費
  const [displayLaborCostByYear, setDisplayLaborCostByYear] = useState<Decimal[]>([]);
  // 【年度別損益】【画面表示用】国内工場労務費
  const [displayDomesticLaborCostByYear, setDisplayDomesticLaborCostByYear] = useState<Decimal[]>([]);
  // 【年度別損益】【画面表示用】 生産工場経費
  const [displayFactoryExpensesByYear, setDisplayFactoryExpensesByYear] = useState<Decimal[]>([]);
  // 【年度別損益】【画面表示用】生産工場経費-償却費以外の工場経費
  const [displayOverHeadCostByYear, setDisplayOverHeadCostByYear] = useState<Decimal[]>([]);
  // 【年度別損益】【画面表示用】生産工場経費-減価償却費
  const [displayDepreciationByYear, setDisplayDepreciationByYear] = useState<Decimal[]>([]);
  // 【年度別損益】【画面表示用】生産工場経費-投資対象設備
  const [displayEquipmentDepreciationByYear, setDisplayEquipmentDepreciationByYear] = useState<Decimal[]>([]);
  // 【年度別損益】【画面表示用】生産工場経費-工場建物
  const [displayFactoryCostByYear, setDisplayFactoryCostByYear] = useState<Decimal[]>([]);
  // 【年度別損益】【画面表示用】生産工場経費-過去PJの投資対象設備
  const [displayPastEquipmentCostByYear, setDisplayPastEquipmentCostByYear] = useState<Decimal[]>([]);
  // 【年度別損益】【画面表示用】生産工場経費-その他の設備
  const [displayOtherEquipmentCostByYear, setDisplayOtherEquipmentCostByYear] = useState<Decimal[]>([]);
  // 【年度別損益】【画面表示用】国内工場経費
  const [displayDomesticFactoryExpensesByYear, setDisplayDomesticFactoryExpensesByYear] = useState<Decimal[]>([]);
  // 【年度別損益】【画面表示用】国内工場経費-償却費以外の工場経費
  const [displayDomesticOverHeadCostByYear, setDisplayDomesticOverHeadCostByYear] = useState<Decimal[]>([]);
  // 【年度別損益】【画面表示用】国内工場経費-減価償却費
  const [displayDomesticDepreciationByYear, setDisplayDomesticDepreciationByYear] = useState<Decimal[]>([]);
  // 【年度別損益】【画面表示用】国内工場経費-投資対象設備
  const [displayDomesticInvestmentDepreciationByYear, setDisplayDomesticInvestmentDepreciationByYear] = useState<Decimal[]>([]);
  // 【年度別損益】【画面表示用】国内工場経費-工場建物
  const [displayDomesticFactoryBuildingDepreciationByYear, setDisplayDomesticFactoryBuildingDepreciationByYear] = useState<Decimal[]>([]);
  // 【年度別損益】【画面表示用】国内工場経費-その他の設備
  const [displayDomesticOtherDepreciationByYear, setDisplayDomesticOtherDepreciationByYear] = useState<Decimal[]>([]);
  // 【年度別損益】【画面表示用】仕損費
  const [displayScrapCostByYear, setDisplayScrapCostByYear] = useState<Decimal[]>([]);
  // 【年度別損益】【画面表示用】販売費及び一般管理費
  const [displaySGAExpensesByYear, setDisplaySGAExpensesByYear] = useState<Decimal[]>([]);
  // 【年度別損益】【画面表示用】生産工場販管費
  const [displayAppliedLaborCostByYear, setDisplayAppliedLaborCostByYear] = useState<Decimal[]>([]);
  // 【年度別損益】【画面表示用】国内工場販管費
  const [displayDomesticSgaCostByYear, setDisplayDomesticSgaCostByYear] = useState<Decimal[]>([]);
  // 【年度別損益】【画面表示用】本社・販社販管費
  const [displaySalesManagementCostByYear, setDisplaySalesManagementCostByYear] = useState<Decimal[]>([]);
  // 【年度別損益】【画面表示用】開発費
  const [displayDevCostByYear, setDisplayDevCostByYear] = useState<Decimal[]>([]);
  // 【年度別損益】【画面表示用】ロイヤリティ
  const [displayRoyaltyByYear, setDisplayRoyaltyByYear] = useState<Decimal[]>([]);
  // 【年度別損益】【画面表示用】その他の経費
  const [displayOtherCostByYear, setDisplayOtherCostByYear] = useState<Decimal[]>([]);
  // 【年度別損益】【画面表示用】営業利益
  const [displayProfitByYear, setDisplayProfitByYear] = useState<Decimal[]>([]);
  // 【年度別損益】【画面表示用】営業利益率
  const [displayProfitRateByYear, setDisplayProfitRateByYear] = useState<Decimal[]>([]);
  // 【年度別損益】【画面表示用】実質的営業利益率
  const [displayRealOperatingProfitRateByYear, setDisplayRealOperatingProfitRateByYear] = useState<Decimal[]>([]);

  // #endregion

  // #region 【年度別損益・更新前】【計算用】

  // 【年度別損益・更新前】【計算用】売上合計
  const [beforeUpdateTotalSalesByYear, setBeforeUpdateTotalSalesByYear] = useState<Decimal[]>([]);
  // 【年度別損益・更新前】【計算用】製品売上
  const [beforeUpdateProductSalesByYear, setBeforeUpdateProductSalesByYear] = useState<Decimal[]>([]);
  // 【年度別損益・更新前】【計算用】 PJ単位金型回収
  const [beforeUpdateMoldSalesByYear, setBeforeUpdateMoldSalesByYear] = useState<Decimal[]>([]);
  // 【年度別損益・更新前】【計算用】PJ単位開発費回収
  const [beforeUpdateDevSalesByYear, setBeforeUpdateDevSalesByYear] = useState<Decimal[]>([]);
  // 【年度別損益・更新前】【計算用】PJ単位設備費回収
  const [beforeUpdateEquipmentSalesByYear, setBeforeUpdateEquipmentSalesByYear] = useState<Decimal[]>([]);
  // 【年度別損益・更新前】【計算用】売上原価
  const [beforeUpdateSalesCostByYear, setBeforeUpdateSalesCostByYear] = useState<Decimal[]>([]);
  // 【年度別損益・更新前】【計算用】材料費
  const [beforeUpdateTotalMaterialCostByYear, setBeforeUpdateTotalMaterialCostByYear] = useState<Decimal[]>([]);
  // 【年度別損益・更新前】【計算用】外注加工費
  const [beforeUpdateOutsideCostByYear, setBeforeUpdateOutsideCostByYear] = useState<Decimal[]>([]);
  // 【年度別損益・更新前】【計算用】 生産工場労務費
  const [beforeUpdateLaborCostByYear, setBeforeUpdateLaborCostByYear] = useState<Decimal[]>([]);
  // 【年度別損益・更新前】【計算用】国内工場労務費
  const [beforeUpdateDomesticLaborCostByYear, setBeforeUpdateDomesticLaborCostByYear] = useState<Decimal[]>([]);
  // 【年度別損益・更新前】【計算用】 生産工場経費
  const [beforeUpdateFactoryExpensesByYear, setBeforeUpdateFactoryExpensesByYear] = useState<Decimal[]>([]);
  // 【年度別損益・更新前】【計算用】生産工場経費-償却費以外の工場経費
  const [beforeUpdateOverHeadCostByYear, setBeforeUpdateOverHeadCostByYear] = useState<Decimal[]>([]);
  // 【年度別損益・更新前】【計算用】生産工場経費-減価償却費
  const [beforeUpdateDepreciationByYear, setBeforeUpdateDepreciationByYear] = useState<Decimal[]>([]);
  // 【年度別損益・更新前】【計算用】生産工場経費-投資対象設備
  const [beforeUpdateEquipmentDepreciationByYear, setBeforeUpdateEquipmentDepreciationByYear] = useState<Decimal[]>([]);
  // 【年度別損益・更新前】【計算用】生産工場経費-工場建物
  const [beforeUpdateFactoryCostByYear, setBeforeUpdateFactoryCostByYear] = useState<Decimal[]>([]);
  // 【年度別損益・更新前】【計算用】生産工場経費-過去PJの投資対象設備
  const [beforeUpdatePastEquipmentCostByYear, setBeforeUpdatePastEquipmentCostByYear] = useState<Decimal[]>([]);
  // 【年度別損益・更新前】【計算用】生産工場経費-その他の設備
  const [beforeUpdateOtherEquipmentCostByYear, setBeforeUpdateOtherEquipmentCostByYear] = useState<Decimal[]>([]);
  // 【年度別損益・更新前】【計算用】国内工場経費
  const [beforeUpdateDomesticFactoryExpensesByYear, setBeforeUpdateDomesticFactoryExpensesByYear] = useState<Decimal[]>([]);
  // 【年度別損益・更新前】【計算用】国内工場経費-償却費以外の工場経費
  const [beforeUpdateDomesticOverHeadCostByYear, setBeforeUpdateDomesticOverHeadCostByYear] = useState<Decimal[]>([]);
  // 【年度別損益・更新前】【計算用】国内工場経費-減価償却費
  const [beforeUpdateDomesticDepreciationByYear, setBeforeUpdateDomesticDepreciationByYear] = useState<Decimal[]>([]);
  // 【年度別損益・更新前】【計算用】国内工場経費-投資対象設備
  const [beforeUpdateDomesticInvestmentDepreciationByYear, setBeforeUpdateDomesticInvestmentDepreciationByYear] = useState<Decimal[]>([]);
  // 【年度別損益・更新前】【計算用】国内工場経費-工場建物
  const [beforeUpdateDomesticFactoryBuildingDepreciationByYear, setBeforeUpdateDomesticFactoryBuildingDepreciationByYear] = useState<Decimal[]>([]);
  // 【年度別損益・更新前】【計算用】国内工場経費-その他の設備
  const [beforeUpdateDomesticOtherDepreciationByYear, setBeforeUpdateDomesticOtherDepreciationByYear] = useState<Decimal[]>([]);
  // 【年度別損益・更新前】【計算用】仕損費
  const [beforeUpdateScrapCostByYear, setBeforeUpdateScrapCostByYear] = useState<Decimal[]>([]);
  // 【年度別損益・更新前】【計算用】販売費及び一般管理費
  const [beforeUpdateSGAExpensesByYear, setBeforeUpdateSGAExpensesByYear] = useState<Decimal[]>([]);
  // 【年度別損益・更新前】【計算用】生産工場販管費
  const [beforeUpdateAppliedLaborCostByYear, setBeforeUpdateAppliedLaborCostByYear] = useState<Decimal[]>([]);
  // 【年度別損益・更新前】【計算用】国内工場販管費
  const [beforeUpdateDomesticSgaCostByYear, setBeforeUpdateDomesticSgaCostByYear] = useState<Decimal[]>([]);
  // 【年度別損益・更新前】【計算用】本社・販社販管費
  const [beforeUpdateSalesManagementCostByYear, setBeforeUpdateSalesManagementCostByYear] = useState<Decimal[]>([]);
  // 【年度別損益・更新前】【計算用】開発費
  const [beforeUpdateDevCostByYear, setBeforeUpdateDevCostByYear] = useState<Decimal[]>([]);
  // 【年度別損益・更新前】【計算用】ロイヤリティ
  const [beforeUpdateRoyaltyByYear, setBeforeUpdateRoyaltyByYear] = useState<Decimal[]>([]);
  // 【年度別損益・更新前】【計算用】その他の経費
  const [beforeUpdateOtherCostByYear, setBeforeUpdateOtherCostByYear] = useState<Decimal[]>([]);
  // 【年度別損益・更新前】【計算用】営業利益
  const [beforeUpdateProfitByYear, setBeforeUpdateProfitByYear] = useState<Decimal[]>([]);
  // 【年度別損益・更新前】【計算用】営業利益率
  const [beforeUpdateProfitRateByYear, setBeforeUpdateProfitRateByYear] = useState<Decimal[]>([]);
  // 【年度別損益・更新前】【計算用】実質的営業利益率
  const [beforeUpdateRealOperatingProfitRateByYear, setBeforeUpdateRealOperatingProfitRateByYear] = useState<Decimal[]>([]);

  // #endregion

  // #region 【年度別損益・更新前】【画面表示用】

  // 【年度別損益・更新前】【画面表示用】売上合計
  const [displayBeforeUpdateTotalSalesByYear, setDisplayBeforeUpdateTotalSalesByYear] = useState<Decimal[]>([]);
  // 【年度別損益・更新前】【画面表示用】製品売上
  const [displayBeforeUpdateProductSalesByYear, setDisplayBeforeUpdateProductSalesByYear] = useState<Decimal[]>([]);
  // 【年度別損益・更新前】【画面表示用】PJ単位金型回収
  const [displayBeforeUpdateMoldSalesByYear, setDisplayBeforeUpdateMoldSalesByYear] = useState<Decimal[]>([]);
  // 【年度別損益・更新前】【画面表示用】PJ単位開発費回収
  const [displayBeforeUpdateDevSalesByYear, setDisplayBeforeUpdateDevSalesByYear] = useState<Decimal[]>([]);
  // 【年度別損益・更新前】【画面表示用】PJ単位設備費回収
  const [displayBeforeUpdateEquipmentSalesByYear, setDisplayBeforeUpdateEquipmentSalesByYear] = useState<Decimal[]>([]);
  // 【年度別損益・更新前】【画面表示用】売上原価
  const [displayBeforeUpdateSalesCostByYear, setDisplayBeforeUpdateSalesCostByYear] = useState<Decimal[]>([]);
  // 【年度別損益・更新前】【画面表示用】材料費
  const [displayBeforeUpdateTotalMaterialCostByYear, setDisplayBeforeUpdateTotalMaterialCostByYear] = useState<Decimal[]>([]);
  // 【年度別損益・更新前】【画面表示用】外注加工費
  const [displayBeforeUpdateOutsideCostByYear, setDisplayBeforeUpdateOutsideCostByYear] = useState<Decimal[]>([]);
  // 【年度別損益・更新前】【画面表示用】生産工場労務費
  const [displayBeforeUpdateLaborCostByYear, setDisplayBeforeUpdateLaborCostByYear] = useState<Decimal[]>([]);
  // 【年度別損益・更新前】【画面表示用】国内工場労務費
  const [displayBeforeUpdateDomesticLaborCostByYear, setDisplayBeforeUpdateDomesticLaborCostByYear] = useState<Decimal[]>([]);
  // 【年度別損益・更新前】【画面表示用】生産工場経費
  const [displayBeforeUpdateFactoryExpensesByYear, setDisplayBeforeUpdateFactoryExpensesByYear] = useState<Decimal[]>([]);
  // 【年度別損益・更新前】【画面表示用】生産工場経費-償却費以外の工場経費
  const [displayBeforeUpdateOverHeadCostByYear, setDisplayBeforeUpdateOverHeadCostByYear] = useState<Decimal[]>([]);
  // 【年度別損益・更新前】【画面表示用】生産工場経費-減価償却費
  const [displayBeforeUpdateDepreciationByYear, setDisplayBeforeUpdateDepreciationByYear] = useState<Decimal[]>([]);
  // 【年度別損益・更新前】【画面表示用】生産工場経費-投資対象設備
  const [displayBeforeUpdateEquipmentDepreciationByYear, setDisplayBeforeUpdateEquipmentDepreciationByYear] = useState<Decimal[]>([]);
  // 【年度別損益・更新前】【画面表示用】生産工場経費-工場建物
  const [displayBeforeUpdateFactoryCostByYear, setDisplayBeforeUpdateFactoryCostByYear] = useState<Decimal[]>([]);
  // 【年度別損益・更新前】【画面表示用】生産工場経費-過去PJの投資対象設備
  const [displayBeforeUpdatePastEquipmentCostByYear, setDisplayBeforeUpdatePastEquipmentCostByYear] = useState<Decimal[]>([]);
  // 【年度別損益・更新前】【画面表示用】生産工場経費-その他の設備
  const [displayBeforeUpdateOtherEquipmentCostByYear, setDisplayBeforeUpdateOtherEquipmentCostByYear] = useState<Decimal[]>([]);
  // 【年度別損益・更新前】【画面表示用】国内工場経費
  const [displayBeforeUpdateDomesticFactoryExpensesByYear, setDisplayBeforeUpdateDomesticFactoryExpensesByYear] = useState<Decimal[]>([]);
  // 【年度別損益・更新前】【画面表示用】国内工場経費-償却費以外の工場経費
  const [displayBeforeUpdateDomesticOverHeadCostByYear, setDisplayBeforeUpdateDomesticOverHeadCostByYear] = useState<Decimal[]>([]);
  // 【年度別損益・更新前】【画面表示用】国内工場経費-減価償却費
  const [displayBeforeUpdateDomesticDepreciationByYear, setDisplayBeforeUpdateDomesticDepreciationByYear] = useState<Decimal[]>([]);
  // 【年度別損益・更新前】【画面表示用】国内工場経費-投資対象設備
  const [displayBeforeUpdateDomesticInvestmentDepreciationByYear, setDisplayBeforeUpdateDomesticInvestmentDepreciationByYear] = useState<Decimal[]>([]);
  // 【年度別損益・更新前】【画面表示用】国内工場経費-工場建物
  const [displayBeforeUpdateDomesticFactoryBuildingDepreciationByYear, setDisplayBeforeUpdateDomesticFactoryBuildingDepreciationByYear] = useState<Decimal[]>([]);
  // 【年度別損益・更新前】【画面表示用】国内工場経費-その他の設備
  const [displayBeforeUpdateDomesticOtherDepreciationByYear, setDisplayBeforeUpdateDomesticOtherDepreciationByYear] = useState<Decimal[]>([]);
  // 【年度別損益・更新前】【画面表示用】仕損費
  const [displayBeforeUpdateScrapCostByYear, setDisplayBeforeUpdateScrapCostByYear] = useState<Decimal[]>([]);
  // 【年度別損益・更新前】【画面表示用】販売費及び一般管理費
  const [displayBeforeUpdateSGAExpensesByYear, setDisplayBeforeUpdateSGAExpensesByYear] = useState<Decimal[]>([]);
  // 【年度別損益・更新前】【画面表示用】生産工場販管費
  const [displayBeforeUpdateAppliedLaborCostByYear, setDisplayBeforeUpdateAppliedLaborCostByYear] = useState<Decimal[]>([]);
  // 【年度別損益・更新前】【画面表示用】国内工場販管費
  const [displayBeforeUpdateDomesticSgaCostByYear, setDisplayBeforeUpdateDomesticSgaCostByYear] = useState<Decimal[]>([]);
  // 【年度別損益・更新前】【画面表示用】本社・販社販管費
  const [displayBeforeUpdateSalesManagementCostByYear, setDisplayBeforeUpdateSalesManagementCostByYear] = useState<Decimal[]>([]);
  // 【年度別損益・更新前】【画面表示用】開発費
  const [displayBeforeUpdateDevCostByYear, setDisplayBeforeUpdateDevCostByYear] = useState<Decimal[]>([]);
  // 【年度別損益・更新前】【画面表示用】ロイヤリティ
  const [displayBeforeUpdateRoyaltyByYear, setDisplayBeforeUpdateRoyaltyByYear] = useState<Decimal[]>([]);
  // 【年度別損益・更新前】【画面表示用】その他の経費
  const [displayBeforeUpdateOtherCostByYear, setDisplayBeforeUpdateOtherCostByYear] = useState<Decimal[]>([]);
  // 【年度別損益・更新前】【画面表示用】営業利益
  const [displayBeforeUpdateProfitByYear, setDisplayBeforeUpdateProfitByYear] = useState<Decimal[]>([]);
  // 【年度別損益・更新前】【画面表示用】営業利益率
  const [displayBeforeUpdateProfitRateByYear, setDisplayBeforeUpdateProfitRateByYear] = useState<Decimal[]>([]);
  // 【年度別損益・更新前】【画面表示用】実質的営業利益率
  const [displayBeforeUpdateRealOperatingProfitRateByYear, setDisplayBeforeUpdateRealOperatingProfitRateByYear] = useState<Decimal[]>([]);

  // #endregion

  // #region 【年度別損益・合理化前】【計算用】

  // 【年度別損益・合理化前】【計算用】売上合計
  const [beforeRationalizationTotalSalesByYear, setBeforeRationalizationTotalSalesByYear] = useState<Decimal[]>([]);
  // 【年度別損益・合理化前】【計算用】製品売上
  const [beforeRationalizationProductSalesByYear, setBeforeRationalizationProductSalesByYear] = useState<Decimal[]>([]);
  // 【年度別損益・合理化前】【計算用】 PJ単位金型回収
  const [beforeRationalizationMoldSalesByYear, setBeforeRationalizationMoldSalesByYear] = useState<Decimal[]>([]);
  // 【年度別損益・合理化前】【計算用】PJ単位開発費回収
  const [beforeRationalizationDevSalesByYear, setBeforeRationalizationDevSalesByYear] = useState<Decimal[]>([]);
  // 【年度別損益・合理化前】【計算用】PJ単位設備費回収
  const [beforeRationalizationEquipmentSalesByYear, setBeforeRationalizationEquipmentSalesByYear] = useState<Decimal[]>([]);
  // 【年度別損益・合理化前】【計算用】売上原価
  const [beforeRationalizationSalesCostByYear, setBeforeRationalizationSalesCostByYear] = useState<Decimal[]>([]);
  // 【年度別損益・合理化前】【計算用】材料費
  const [beforeRationalizationTotalMaterialCostByYear, setBeforeRationalizationTotalMaterialCostByYear] = useState<Decimal[]>([]);
  // 【年度別損益・合理化前】【計算用】外注加工費
  const [beforeRationalizationOutsideCostByYear, setBeforeRationalizationOutsideCostByYear] = useState<Decimal[]>([]);
  // 【年度別損益・合理化前】【計算用】 生産工場労務費
  const [beforeRationalizationLaborCostByYear, setBeforeRationalizationLaborCostByYear] = useState<Decimal[]>([]);
  // 【年度別損益・合理化前】【計算用】国内工場労務費
  const [beforeRationalizationDomesticLaborCostByYear, setBeforeRationalizationDomesticLaborCostByYear] = useState<Decimal[]>([]);
  // 【年度別損益・合理化前】【計算用】 生産工場経費
  const [beforeRationalizationFactoryExpensesByYear, setBeforeRationalizationFactoryExpensesByYear] = useState<Decimal[]>([]);
  // 【年度別損益・合理化前】【計算用】生産工場経費-償却費以外の工場経費
  const [beforeRationalizationOverHeadCostByYear, setBeforeRationalizationOverHeadCostByYear] = useState<Decimal[]>([]);
  // 【年度別損益・合理化前】【計算用】生産工場経費-減価償却費
  const [beforeRationalizationDepreciationByYear, setBeforeRationalizationDepreciationByYear] = useState<Decimal[]>([]);
  // 【年度別損益・合理化前】【計算用】生産工場経費-投資対象設備
  const [beforeRationalizationEquipmentDepreciationByYear, setBeforeRationalizationEquipmentDepreciationByYear] = useState<Decimal[]>([]);
  // 【年度別損益・合理化前】【計算用】生産工場経費-工場建物
  const [beforeRationalizationFactoryCostByYear, setBeforeRationalizationFactoryCostByYear] = useState<Decimal[]>([]);
  // 【年度別損益・合理化前】【計算用】生産工場経費-過去PJの投資対象設備
  const [beforeRationalizationPastEquipmentCostByYear, setBeforeRationalizationPastEquipmentCostByYear] = useState<Decimal[]>([]);
  // 【年度別損益・合理化前】【計算用】生産工場経費-その他の設備
  const [beforeRationalizationOtherEquipmentCostByYear, setBeforeRationalizationOtherEquipmentCostByYear] = useState<Decimal[]>([]);
  // 【年度別損益・合理化前】【計算用】国内工場経費
  const [beforeRationalizationDomesticFactoryExpensesByYear, setBeforeRationalizationDomesticFactoryExpensesByYear] = useState<Decimal[]>([]);
  // 【年度別損益・合理化前】【計算用】国内工場経費-償却費以外の工場経費
  const [beforeRationalizationDomesticOverHeadCostByYear, setBeforeRationalizationDomesticOverHeadCostByYear] = useState<Decimal[]>([]);
  // 【年度別損益・合理化前】【計算用】国内工場経費-減価償却費
  const [beforeRationalizationDomesticDepreciationByYear, setBeforeRationalizationDomesticDepreciationByYear] = useState<Decimal[]>([]);
  // 【年度別損益・合理化前】【計算用】国内工場経費-投資対象設備
  const [beforeRationalizationDomesticInvestmentDepreciationByYear, setBeforeRationalizationDomesticInvestmentDepreciationByYear] = useState<Decimal[]>([]);
  // 【年度別損益・合理化前】【計算用】国内工場経費-工場建物
  const [beforeRationalizationDomesticFactoryBuildingDepreciationByYear, setBeforeRationalizationDomesticFactoryBuildingDepreciationByYear] = useState<Decimal[]>([]);
  // 【年度別損益・合理化前】【計算用】国内工場経費-その他の設備
  const [beforeRationalizationDomesticOtherDepreciationByYear, setBeforeRationalizationDomesticOtherDepreciationByYear] = useState<Decimal[]>([]);
  // 【年度別損益・合理化前】【計算用】仕損費
  const [beforeRationalizationScrapCostByYear, setBeforeRationalizationScrapCostByYear] = useState<Decimal[]>([]);
  // 【年度別損益・合理化前】【計算用】販売費及び一般管理費
  const [beforeRationalizationSGAExpensesByYear, setBeforeRationalizationSGAExpensesByYear] = useState<Decimal[]>([]);
  // 【年度別損益・合理化前】【計算用】生産工場販管費
  const [beforeRationalizationAppliedLaborCostByYear, setBeforeRationalizationAppliedLaborCostByYear] = useState<Decimal[]>([]);
  // 【年度別損益・合理化前】【計算用】国内工場販管費
  const [beforeRationalizationDomesticSgaCostByYear, setBeforeRationalizationDomesticSgaCostByYear] = useState<Decimal[]>([]);
  // 【年度別損益・合理化前】【計算用】本社・販社販管費
  const [beforeRationalizationSalesManagementCostByYear, setBeforeRationalizationSalesManagementCostByYear] = useState<Decimal[]>([]);
  // 【年度別損益・合理化前】【計算用】開発費
  const [beforeRationalizationDevCostByYear, setBeforeRationalizationDevCostByYear] = useState<Decimal[]>([]);
  // 【年度別損益・合理化前】【計算用】ロイヤリティ
  const [beforeRationalizationRoyaltyByYear, setBeforeRationalizationRoyaltyByYear] = useState<Decimal[]>([]);
  // 【年度別損益・合理化前】【計算用】その他の経費
  const [beforeRationalizationOtherCostByYear, setBeforeRationalizationOtherCostByYear] = useState<Decimal[]>([]);
  // 【年度別損益・合理化前】【計算用】営業利益
  const [beforeRationalizationProfitByYear, setBeforeRationalizationProfitByYear] = useState<Decimal[]>([]);
  // 【年度別損益・合理化前】【計算用】営業利益率
  const [beforeRationalizationProfitRateByYear, setBeforeRationalizationProfitRateByYear] = useState<Decimal[]>([]);
  // 【年度別損益・合理化前】【計算用】実質的営業利益率
  const [beforeRationalizationRealOperatingProfitRateByYear, setBeforeRationalizationRealOperatingProfitRateByYear] = useState<Decimal[]>([]);
  // 【年度別損益・合理化前】【計算用】客先指定部品材料費
  const [beforeRationalizationCustomerSpecifiedPartsCostByYear, setBeforeRationalizationCustomerSpecifiedPartsCostByYear] = useState<Decimal[]>([]);

  // #endregion

  // #region 【年度別損益・合理化前】【画面表示用】

  // 【年度別損益・合理化前】【画面表示用】売上合計
  const [displayBeforeRationalizationTotalSalesByYear, setDisplayBeforeRationalizationTotalSalesByYear] = useState<Decimal[]>([]);
  // 【年度別損益・合理化前】【画面表示用】製品売上
  const [displayBeforeRationalizationProductSalesByYear, setDisplayBeforeRationalizationProductSalesByYear] = useState<Decimal[]>([]);
  // 【年度別損益・合理化前】【画面表示用】PJ単位金型回収
  const [displayBeforeRationalizationMoldSalesByYear, setDisplayBeforeRationalizationMoldSalesByYear] = useState<Decimal[]>([]);
  // 【年度別損益・合理化前】【画面表示用】PJ単位開発費回収
  const [displayBeforeRationalizationDevSalesByYear, setDisplayBeforeRationalizationDevSalesByYear] = useState<Decimal[]>([]);
  // 【年度別損益・合理化前】【画面表示用】PJ単位設備費回収
  const [displayBeforeRationalizationEquipmentSalesByYear, setDisplayBeforeRationalizationEquipmentSalesByYear] = useState<Decimal[]>([]);
  // 【年度別損益・合理化前】【画面表示用】売上原価
  const [displayBeforeRationalizationSalesCostByYear, setDisplayBeforeRationalizationSalesCostByYear] = useState<Decimal[]>([]);
  // 【年度別損益・合理化前】【画面表示用】材料費
  const [displayBeforeRationalizationTotalMaterialCostByYear, setDisplayBeforeRationalizationTotalMaterialCostByYear] = useState<Decimal[]>([]);
  // 【年度別損益・合理化前】【画面表示用】外注加工費
  const [displayBeforeRationalizationOutsideCostByYear, setDisplayBeforeRationalizationOutsideCostByYear] = useState<Decimal[]>([]);
  // 【年度別損益・合理化前】【画面表示用】生産工場労務費
  const [displayBeforeRationalizationLaborCostByYear, setDisplayBeforeRationalizationLaborCostByYear] = useState<Decimal[]>([]);
  // 【年度別損益・合理化前】【画面表示用】国内工場労務費
  const [displayBeforeRationalizationDomesticLaborCostByYear, setDisplayBeforeRationalizationDomesticLaborCostByYear] = useState<Decimal[]>([]);
  // 【年度別損益・合理化前】【画面表示用】生産工場経費
  const [displayBeforeRationalizationFactoryExpensesByYear, setDisplayBeforeRationalizationFactoryExpensesByYear] = useState<Decimal[]>([]);
  // 【年度別損益・合理化前】【画面表示用】生産工場経費-償却費以外の工場経費
  const [displayBeforeRationalizationOverHeadCostByYear, setDisplayBeforeRationalizationOverHeadCostByYear] = useState<Decimal[]>([]);
  // 【年度別損益・合理化前】【画面表示用】生産工場経費-減価償却費
  const [displayBeforeRationalizationDepreciationByYear, setDisplayBeforeRationalizationDepreciationByYear] = useState<Decimal[]>([]);
  // 【年度別損益・合理化前】【画面表示用】生産工場経費-投資対象設備
  const [displayBeforeRationalizationEquipmentDepreciationByYear, setDisplayBeforeRationalizationEquipmentDepreciationByYear] = useState<Decimal[]>([]);
  // 【年度別損益・合理化前】【画面表示用】生産工場経費-工場建物
  const [displayBeforeRationalizationFactoryCostByYear, setDisplayBeforeRationalizationFactoryCostByYear] = useState<Decimal[]>([]);
  // 【年度別損益・合理化前】【画面表示用】生産工場経費-過去PJの投資対象設備
  const [displayBeforeRationalizationPastEquipmentCostByYear, setDisplayBeforeRationalizationPastEquipmentCostByYear] = useState<Decimal[]>([]);
  // 【年度別損益・合理化前】【画面表示用】生産工場経費-その他の設備
  const [displayBeforeRationalizationOtherEquipmentCostByYear, setDisplayBeforeRationalizationOtherEquipmentCostByYear] = useState<Decimal[]>([]);
  // 【年度別損益・合理化前】【画面表示用】国内工場経費
  const [displayBeforeRationalizationDomesticFactoryExpensesByYear, setDisplayBeforeRationalizationDomesticFactoryExpensesByYear] = useState<Decimal[]>([]);
  // 【年度別損益・合理化前】【画面表示用】国内工場経費-償却費以外の工場経費
  const [displayBeforeRationalizationDomesticOverHeadCostByYear, setDisplayBeforeRationalizationDomesticOverHeadCostByYear] = useState<Decimal[]>([]);
  // 【年度別損益・合理化前】【画面表示用】国内工場経費-減価償却費
  const [displayBeforeRationalizationDomesticDepreciationByYear, setDisplayBeforeRationalizationDomesticDepreciationByYear] = useState<Decimal[]>([]);
  // 【年度別損益・合理化前】【画面表示用】国内工場経費-投資対象設備
  const [displayBeforeRationalizationDomesticInvestmentDepreciationByYear, setDisplayBeforeRationalizationDomesticInvestmentDepreciationByYear] = useState<Decimal[]>([]);
  // 【年度別損益・合理化前】【画面表示用】国内工場経費-工場建物
  const [displayBeforeRationalizationDomesticFactoryBuildingDepreciationByYear, setDisplayBeforeRationalizationDomesticFactoryBuildingDepreciationByYear] = useState<Decimal[]>([]);
  // 【年度別損益・合理化前】【画面表示用】国内工場経費-その他の設備
  const [displayBeforeRationalizationDomesticOtherDepreciationByYear, setDisplayBeforeRationalizationDomesticOtherDepreciationByYear] = useState<Decimal[]>([]);
  // 【年度別損益・合理化前】【画面表示用】仕損費
  const [displayBeforeRationalizationScrapCostByYear, setDisplayBeforeRationalizationScrapCostByYear] = useState<Decimal[]>([]);
  // 【年度別損益・合理化前】【画面表示用】販売費及び一般管理費
  const [displayBeforeRationalizationSGAExpensesByYear, setDisplayBeforeRationalizationSGAExpensesByYear] = useState<Decimal[]>([]);
  // 【年度別損益・合理化前】【画面表示用】生産工場販管費
  const [displayBeforeRationalizationAppliedLaborCostByYear, setDisplayBeforeRationalizationAppliedLaborCostByYear] = useState<Decimal[]>([]);
  // 【年度別損益・合理化前】【画面表示用】国内工場販管費
  const [displayBeforeRationalizationDomesticSgaCostByYear, setDisplayBeforeRationalizationDomesticSgaCostByYear] = useState<Decimal[]>([]);
  // 【年度別損益・合理化前】【画面表示用】本社・販社販管費
  const [displayBeforeRationalizationSalesManagementCostByYear, setDisplayBeforeRationalizationSalesManagementCostByYear] = useState<Decimal[]>([]);
  // 【年度別損益・合理化前】【画面表示用】開発費
  const [displayBeforeRationalizationDevCostByYear, setDisplayBeforeRationalizationDevCostByYear] = useState<Decimal[]>([]);
  // 【年度別損益・合理化前】【画面表示用】ロイヤリティ
  const [displayBeforeRationalizationRoyaltyByYear, setDisplayBeforeRationalizationRoyaltyByYear] = useState<Decimal[]>([]);
  // 【年度別損益・合理化前】【画面表示用】その他の経費
  const [displayBeforeRationalizationOtherCostByYear, setDisplayBeforeRationalizationOtherCostByYear] = useState<Decimal[]>([]);
  // 【年度別損益・合理化前】【画面表示用】営業利益
  const [displayBeforeRationalizationProfitByYear, setDisplayBeforeRationalizationProfitByYear] = useState<Decimal[]>([]);
  // 【年度別損益・合理化前】【画面表示用】営業利益率
  const [displayBeforeRationalizationProfitRateByYear, setDisplayBeforeRationalizationProfitRateByYear] = useState<Decimal[]>([]);
  // 【年度別損益・合理化前】【画面表示用】実質的営業利益率
  const [displayBeforeRationalizationRealOperatingProfitRateByYear, setDisplayBeforeRationalizationRealOperatingProfitRateByYear] = useState<Decimal[]>([]);

  // #endregion

  // #region 【画面項目】【{t("features.InvestmentScreening.ProjectProfitablityStatement.heading.paybackPeriod")}】
  // 【画面項目】【{t("features.InvestmentScreening.ProjectProfitablityStatement.heading.paybackPeriod")}】 金利
  const [interestRateByYear, setInterestRateByYear] = useState<Decimal[]>([]);
  // 【画面項目】【{t("features.InvestmentScreening.ProjectProfitablityStatement.heading.paybackPeriod")}】 {t("features.InvestmentScreening.ProjectProfitablityStatement.heading.paybackPeriod")}
  const [paybackPeriod, setPaybackPeriod] = useState<string>("");
  // #endregion

  // #region 【画面項目】【回収シミュレーション】

  // 【画面項目】【回収シミュレーション】 設備投資額
  const [capexAmountByYear, setCapexAmountByYear] = useState<Decimal[]>([]);
  // 【画面項目】【回収シミュレーション】 回収原資
  const [recoveredResourcesByYear, setRecoveredResourcesByYear] = useState<Decimal[]>([]);
  // 【画面項目】【回収シミュレーション】 営業利益
  // profitByYear 参照
  // 【画面項目】【回収シミュレーション】 減価償却費
  // depreciationByYear 参照
  // 【画面項目】【回収シミュレーション】 工場建物・その他設備の減価償却費
  const [factoryAndOtherEquipmentCostByYear, setFactoryAndOtherEquipmentCostByYear] = useState<Decimal[]>([]);
  // 【画面項目】【回収シミュレーション】 未回収額
  const [uncollectedAmountByYear, setUncollectedAmountByYear] = useState<Decimal[]>([]);
  // 【画面項目】【回収シミュレーション】 {t("features.InvestmentScreening.ProjectProfitablityStatement.heading.paybackPeriod")}
  const [collectionPeriod, setCollectionPeriod] = useState<string>("");

  // #endregion

  // #region 【画面項目】【ROIC】

  // 【画面項目】【ROIC】 税引後営業利益（旧:【ROIC】NOPAT）
  const [nopatByYear, setNopatByYear] = useState<Decimal[]>([]);
  // 【画面項目】【ROIC】 営業利益
  // profitByYear 参照
  // 【画面項目】【ROIC】 目標税率（実効税率）
  const [taxRateByYear, setTaxRateByYear] = useState<Decimal[]>([]);
  // 【画面項目】【ROIC】 投下資本
  const [undepreciatedByYear, setUndepreciatedByYear] = useState<Decimal[]>([]);
  // 【画面項目】【ROIC】 累計設備投資額
  const [totalCapexAmountByYear, setTotalCapexAmountByYear] = useState<Decimal[]>([]);
  // 【画面項目】【ROIC】 投資対象設備の減価償却累計額
  // depreciationAmountByEquip 参照
  // 【画面項目】【ROIC】 ROIC（簡易計算）
  const [roicByYear, setRoicByYear] = useState<Decimal[]>([]);
  // 【画面項目】【ROIC】 ハードルレート
  const [hurdleRateByYear, setHurdleRateByYear] = useState<Decimal[]>([]);

  // #endregion

  // #region 【画面項目】【{t("features.InvestmentScreening.ProjectProfitablityStatement.heading.dcf")}】

  // 【画面項目】【{t("features.InvestmentScreening.ProjectProfitablityStatement.heading.dcf")}】 フリーキャッシュフロー（旧:【DCF】FCF）
  const [freeCashFlowByYear, setFreeCashFlowByYear] = useState<Decimal[]>([]);
  // 【画面項目】【{t("features.InvestmentScreening.ProjectProfitablityStatement.heading.dcf")}】 営業利益
  // profitByYear 参照
  // 【画面項目】【{t("features.InvestmentScreening.ProjectProfitablityStatement.heading.dcf")}】 目標税率（実効税率）
  // taxRateByYear 参照
  // 【画面項目】【{t("features.InvestmentScreening.ProjectProfitablityStatement.heading.dcf")}】 減価償却費（旧:【DCF】減価償却費）
  // depreciationByYear 参照
  // 【画面項目】【{t("features.InvestmentScreening.ProjectProfitablityStatement.heading.dcf")}】 設備投資額
  // capexAmountByYear 参照
  // 【画面項目】【{t("features.InvestmentScreening.ProjectProfitablityStatement.heading.dcf")}】 加重平均資本コスト
  const [discountRateByYear, setDiscountRateByYear] = useState<Decimal[]>([]);
  // 【画面項目】【{t("features.InvestmentScreening.ProjectProfitablityStatement.heading.dcf")}】 現在価値（旧:【DCF】PV）
  const [presentValueByYear, setPresentValueByYear] = useState<Decimal[]>([]);
  // 【画面項目】【{t("features.InvestmentScreening.ProjectProfitablityStatement.heading.dcf")}】 正味現在価値（旧:【DCF】NPV）
  const [netPresentValue, setNetPresentValue] = useState<Decimal>(new Decimal(0));

  // #endregion

  // #region 【画面項目】【{t("features.InvestmentScreening.ProjectProfitablityStatement.heading.paybackPeriod")}・{t("features.InvestmentScreening.ProjectProfitablityStatement.heading.paybackPeriod")}・回収シミュレーション・ROIC・{t("features.InvestmentScreening.ProjectProfitablityStatement.heading.dcf")} - 表示用（選択通貨）】

  // 【表示用】【{t("features.InvestmentScreening.ProjectProfitablityStatement.heading.paybackPeriod")}】 金利
  const [dispInterestRateByYear, setDispInterestRateByYear] = useState<Decimal[]>([]);

  // 【表示用】【回収シミュレーション】 設備投資額
  const [displayCapexAmountByYear, setDisplayCapexAmountByYear] = useState<Decimal[]>([]);
  // 【表示用】【回収シミュレーション】 回収原資
  const [displayRecoveredResourcesByYear, setDisplayRecoveredResourcesByYear] = useState<Decimal[]>([]);
  // 【表示用】【回収シミュレーション】 工場建物・その他設備の減価償却費
  const [displayFactoryAndOtherEquipmentCostByYear, setDisplayFactoryAndOtherEquipmentCostByYear] = useState<Decimal[]>([]);
  // 【表示用】【回収シミュレーション】 未回収額
  const [displayUncollectedAmountByYear, setDisplayUncollectedAmountByYear] = useState<Decimal[]>([]);

  // 【表示用】【ROIC】 税引後営業利益
  const [displayNopatByYear, setDisplayNopatByYear] = useState<Decimal[]>([]);
  // 【表示用】【ROIC】 投下資本
  const [displayUndepreciatedByYear, setDisplayUndepreciatedByYear] = useState<Decimal[]>([]);
  // 【表示用】【ROIC】 累計設備投資額
  const [displayTotalCapexAmountByYear, setDisplayTotalCapexAmountByYear] = useState<Decimal[]>([]);
  // 【表示用】【ROIC】 ROIC（簡易計算）
  const [displayRoicByYear, setDisplayRoicByYear] = useState<Decimal[]>([]);

  // 【表示用】【{t("features.InvestmentScreening.ProjectProfitablityStatement.heading.dcf")}】 フリーキャッシュフロー
  const [displayFreeCashFlowByYear, setDisplayFreeCashFlowByYear] = useState<Decimal[]>([]);
  // 【表示用】【{t("features.InvestmentScreening.ProjectProfitablityStatement.heading.dcf")}】 現在価値
  const [displayPresentValueByYear, setDisplayPresentValueByYear] = useState<Decimal[]>([]);
  // 【表示用】【{t("features.InvestmentScreening.ProjectProfitablityStatement.heading.dcf")}】 正味現在価値
  const [displayNetPresentValue, setDisplayNetPresentValue] = useState<Decimal>(new Decimal(0));

  // #endregion

  // #endregion

  // #region 【その他】

  // 【その他】 総会計年度数（PartNoのみ参照）
  const [totalFiscalYearsByPartNo, setTotalFiscalYearsByPartNo] = useState<number>(0);
  // 【その他】 総会計年度数（PartNo, PJ別金型費回収, PJ別開発費回収を参照）
  const [totalFiscalYearsByProject, setTotalFiscalYearsByProject] = useState<number>(0);

  // 【その他】 年月ラベル
  const [labelsByMonth, setLabelsByMonth] = useState<string[]>([]);
  // 【その他】 売上合計（月別）
  const [totalSalesByMonth, setTotalSalesByMonth] = useState<Decimal[]>([]);
  // 【その他】 営業利益（月別）
  const [profitByMonth, setProfitByMonth] = useState<Decimal[]>([]);
  // 【その他】 販売数量（PartNo別）（月別）
  const [quantityByPartNoByMonth, setQuantityByPartNoByMonth] = useState<Record<string, string[]>>({});

  // 【表示用】【その他】 売上合計（月別）
  const [displayTotalSalesByMonth, setDisplayTotalSalesByMonth] = useState<Decimal[]>([]);
  // 【表示用】【その他】 営業利益（月別）
  const [displayProfitByMonth, setDisplayProfitByMonth] = useState<Decimal[]>([]);
  // 【その他】 計算データ有無
  const [hasCalculationData, setHasCalculationData] = useState<boolean>(false);
  // 【その他】 計算処理状態
  const [isCalculation, setIsCalculation] = useState<boolean>(false);
  // 【その他】 客先指定部品の有無
  const [hasCustomerSpecifiedParts, setHasCustomerSpecifiedParts] = useState<boolean>(false);
  // 【その他】 客先指定部品材料費（年度別）
  const [customerSpecifiedPartsCostByYear, setCustomerSpecifiedPartsCostByYear] = useState<Decimal[]>([]);
  // 【その他】 工場通貨情報
  const [selectedCurrency, setSelectedCurrency] = useState<{ id: string; name: string }>({ id: "", name: "" });
  // 【その他】 工場通貨小数有無
  const [decimalCurrency, setDecimalCurrency] = useState<boolean | null>(null);
  // 【その他】 確認ボタン幅
  const [buttonWidths, setButtonWidths] = useState<Record<string, number>>({});
  // 【その他】 確認ボタン部署名
  const [confirmSource, setConfirmSource] = useState<string>("");
  // 【その他】 権限用画面パーミッション
  const [pagePermissions, setPagePermissions] = useState<PagePermissions>();
  // 【その他】 各部門担当者名
  const [names, setNames] = useState<Record<string, ConfirmorInfo[]>>({
    companyDivision: [],
    companyHeadDivision: [],
  });
  /** 【その他】 確認モーダル保存ボタン活性制御 */
  const [isSaving, setIsSaving] = useState(false);
  // 【その他】 ベンチマークデータを管理
  const [benchmarkData, setBenchmarkData] = useState<ProductionCapacityBenchmark[]>([]);
  // 【その他】 更新前後サマリーデータを管理
  const [updateSummaryData, setUpdateSummaryData] = useState<UpdateSummaryInputData[]>([]);
  // 【その他】 合理化前後サマリーデータを管理
  const [rationalizationSummaryData, setRationalizationSummaryData] = useState<RationalizationSummaryInputData[]>([]);
  // 【その他】 更新投資損益試算データ（保存用）- ローカル通貨版
  const [updateInvestmentProfitLossData, setUpdateInvestmentProfitLossData] = useState<UpdateInvestmentProfitLossData | null>(null);
  // 【その他】 更新投資損益試算データ（保存用）- JPY版
  const [displayUpdateInvestmentProfitLossData, setDisplayUpdateInvestmentProfitLossData] = useState<UpdateInvestmentProfitLossData | null>(null);
  // 【その他】 合理化投資損益試算データ（保存用）- ローカル通貨版
  const [rationalizationInvestmentProfitLossData, setRationalizationInvestmentProfitLossData] = useState<RationalizationInvestmentProfitLossData | null>(null);
  // 【その他】 合理化投資損益試算データ（保存用）- JPY版
  const [displayRationalizationInvestmentProfitLossData, setDisplayRationalizationInvestmentProfitLossData] = useState<RationalizationInvestmentProfitLossData | null>(null);
  // 【その他】 月次損益データ（Excel出力用）
  const [monthlyProfitAndLossData, setMonthlyProfitAndLossData] = useState<MonthlyProfitAndLossData | null>(null);
  // 【その他】 月次損益データ・更新前（Excel出力用）- 更新投資の場合のみ
  const [beforeUpdateMonthlyProfitAndLossData, setBeforeUpdateMonthlyProfitAndLossData] = useState<MonthlyProfitAndLossData | null>(null);
  // 【その他】 月次損益データ・合理化前（Excel出力用）- 合理化投資の場合のみ
  const [beforeRationalizationMonthlyProfitAndLossData, setBeforeRationalizationMonthlyProfitAndLossData] = useState<MonthlyProfitAndLossData | null>(null);
  // 【その他】 月次損益データ（Excel出力用）- JPY版（二重変換回避）
  const [displayMonthlyProfitAndLossData, setDisplayMonthlyProfitAndLossData] = useState<MonthlyProfitAndLossData | null>(null);
  // 【その他】 月次損益データ・更新前（Excel出力用）- JPY版
  const [displayBeforeUpdateMonthlyProfitAndLossData, setDisplayBeforeUpdateMonthlyProfitAndLossData] = useState<MonthlyProfitAndLossData | null>(null);
  // 【その他】 月次損益データ・合理化前（Excel出力用）- JPY版
  const [displayBeforeRationalizationMonthlyProfitAndLossData, setDisplayBeforeRationalizationMonthlyProfitAndLossData] = useState<MonthlyProfitAndLossData | null>(null);

  // #endregion

  // #region 【JSONデータ】
  // 【JSONデータ】稼働時間情報
  const [workingDays, setWorkingDays] = useState<{ fiscalYear: string, workingDaysData: WorkingDaysType }[] | null>(null);

  // 【JSONデータ】非稼働時間情報
  const [nonWorkingHours, setNonWorkingHours] = useState<{ fiscalYear: string, nonWorkingHoursByMonth: NonWorkingHoursType[] }[] | null>(null);

  // 【JSONデータ】 DB保存用年度別データ（ローカル通貨版）
  const [originalProfitAndLoss, setOriginalProfitAndLoss] = useState<OriginalProfitAndLossTable | null>(null);
  // 【JSONデータ】 DB保存用月別データ（ローカル通貨版）
  const [originalProfitAndLossByMonth, setOriginalProfitAndLossByMonth] = useState<OriginalProfitAndLossByMonthTable | null>(null);
  // 【JSONデータ】 DB保存用年度別データ（JPY版）
  const [displayOriginalProfitAndLoss, setDisplayOriginalProfitAndLoss] = useState<OriginalProfitAndLossTable | null>(null);
  // 【JSONデータ】 DB保存用月別データ（JPY版）
  const [displayOriginalProfitAndLossByMonth, setDisplayOriginalProfitAndLossByMonth] = useState<OriginalProfitAndLossByMonthTable | null>(null);

  // 振り返りモード用: JPY版データ（初回計算時=JPYで保存、DB保存時に使用）
  const [jpySavedProfitAndLoss, setJpySavedProfitAndLoss] = useState<OriginalProfitAndLossTable | null>(null);
  const [jpySavedProfitAndLossByMonth, setJpySavedProfitAndLossByMonth] = useState<OriginalProfitAndLossByMonthTable | null>(null);
  const [jpySavedUpdateInvestmentData, setJpySavedUpdateInvestmentData] = useState<UpdateInvestmentProfitLossData | null>(null);
  const [jpySavedRationalizationInvestmentData, setJpySavedRationalizationInvestmentData] = useState<RationalizationInvestmentProfitLossData | null>(null);

  // #endregion

  // -----------------------------
  // API
  // -----------------------------

  // プロジェクト情報取得API
  const getProjectById = async (projectId: string): Promise<ProjectWithRdsData> => {

    const functionName = "getProjectById";

    try {

      // API実行
      const response = await WebAPI.request({
        query: `
          query ${functionName}($projectId: String, $useRDSData: Boolean, $reviewDate: String) {
            ${functionName}(projectId: $projectId, useRDSData: $useRDSData, reviewDate: $reviewDate) {
              projectId
              projectStartDate
              projectEndDate
              otherEquipmentCost
              pastEquipmentCost
              pastProjectId
              devCost
              otherCost
              productionLineId
              baseLaborCostIncreaseRate
              salesManagementRate
              updateDate
              currencyId
              companyPlantId
              companyPlantSnapshot
              baseInterestRate
              registrantDivisionId
              moldSalesByProject
              developmentSalesByProject
              equipmentSalesByProject
              purpose
              applicationFormType
              profitLossDisplayType
              equipmentUsageType
              updateAndOtherStartDate
              rationalizationStartDate
              monthlyExpenseData
              domesticLaborCostCurrencyId
              domesticSgaExpensesCurrencyId
              productionSgaExpensesCurrencyId
              domesticFactoryExpensesCurrencyId
              domesticInvestmentDepreciationCurrencyId
              domesticFactoryBuildingDepreciationCurrencyId
              domesticOtherDepreciationCurrencyId
              salesCompanySgaCurrencyId
              royaltyCurrencyId
              otherEquipmentCostCurrencyId
              pastEquipmentCostCurrencyId
              devCostCurrencyId
              otherCostCurrencyId
              productionFactoryLaborUnitPrice
              productionFactoryExpenseUnitPrice
              outsourcingProcessingCostCurrencyId
              factoryBuildingDepreciationCurrencyId
              productionLaborCostCurrencyId
              overseasFactoryExpensesCurrencyId
            }
          }`,
        variables: {
          projectId: projectId,
          useRDSData: true,
          ...(reviewDate && { reviewDate })
        }
      });

      if (!response?.data?.getProjectById) {
        // DBデータ取得失敗
        failedNotice("プロジェクト情報", functionName, response?.errors);
      }

      // DBデータを返却
      return response?.data?.getProjectById;

    } catch (error) {
      errorNotice(functionName, error);
      return {};
    }

  };

  // PartNo情報取得API
  const getPartNo = async (projectId: string): Promise<PartNo[]> => {

    const functionName = "getPartNo";

    try {

      // API実行（materialCost, materialCostWithInhouseProfitはサイズが大きいため別APIで取得）
      const response = await WebAPI.request({
        query: `
          query ${functionName}($projectId: String, $reviewDate: String) {
            ${functionName}(projectId: $projectId, reviewDate: $reviewDate) {
              partNo
              displayPartNo
              quantityAndPrice
              moldSalesByPartNo
              developmentSalesByPartNo
            }
          }`,
        variables: {
          projectId: projectId,
          ...(reviewDate && { reviewDate })
        }
      });

      if (!response?.data?.getPartNo) {
        // DBデータ取得失敗
        failedNotice("PartNo情報", functionName, response?.errors);
        return [];
      }

      const partNoList: PartNo[] = response?.data?.getPartNo;

      // 各PartNoのmaterialCostを取得してマージ
      const partNoWithMaterialCost = await Promise.all(
        partNoList.map(async (part) => {
          const materialCostData = await getMaterialCost(projectId, part.partNo!);
          return {
            ...part,
            materialCost: materialCostData?.materialCost,
            materialCostWithInhouseProfit: materialCostData?.materialCostWithInhouseProfit,
          };
        })
      );

      return partNoWithMaterialCost;

    } catch (error) {
      errorNotice(functionName, error);
      return [];
    }

  };

  // 材料費情報取得API（PartNo単位）
  const getMaterialCost = async (projectId: string, partNo: string): Promise<{ materialCost?: string, materialCostWithInhouseProfit?: string } | null> => {

    const functionName = "getMaterialCost";

    try {

      const response = await WebAPI.request({
        query: `
          query getMaterialCost($projectId: String, $partNo: String, $reviewDate: String) {
            getMaterialCost(projectId: $projectId, partNo: $partNo, reviewDate: $reviewDate) {
              materialCost
              materialCostWithInhouseProfit
            }
          }`,
        variables: { projectId, partNo, ...(reviewDate && { reviewDate }) }
      });

      if (!response?.data?.getMaterialCost) {
        console.log(`${functionName} データなし: partNo=${partNo}`);
        return null;
      }

      return response?.data?.getMaterialCost;

    } catch (error) {
      console.error(`${functionName} error: partNo=${partNo}`, error);
      return null;
    }

  };

  // 設備情報取得API
  const getEquipmentByProjectId = async (projectId: string): Promise<EquipmentType[]> => {

    const functionName = "getEquipmentByProjectId";

    try {

      // API実行
      const response = await WebAPI.request({
        query: `
          query getEquipmentByProjectId($projectId: String, $reviewDate: String) {
            getEquipmentByProjectId(projectId: $projectId, reviewDate: $reviewDate) {
              equipmentId
              equipmentName
              unitPrice
              quantity
              amount
              numberOfPeopleRequiredForEquipment
              unskilledWorker
              multiSkilledWorker
              technician
              currencyId
              depreciationPeriod
              depreciationStartMonth
              acceptanceMonth
              processId
            }
          }`,
        variables: { projectId, ...(reviewDate && { reviewDate }) }
      });

      if (!response?.data?.getEquipmentByProjectId) {
        // DBデータ取得失敗
        failedNotice("設備情報", functionName, response?.errors);
      }

      // DBデータを返却
      return response?.data?.getEquipmentByProjectId;

    } catch (error) {
      errorNotice(functionName, error);
      return [];
    }

  };


  // 設定値取得API
  const getSettingValueByKey = async (settingKey: string): Promise<string> => {

    const functionName = "getSettingValueByKey";

    try {

      // API実行
      const response = await WebAPI.request({
        query: `
          query getSettingValueByKey($settingKey: String, $hasDeleted: Boolean) {
            getSettingValueByKey(settingKey: $settingKey, hasDeleted: $hasDeleted) {
              settingValue
            }
          }`,
        variables: { settingKey, hasDeleted: false }
      });

      if (!response?.data[functionName]?.settingValue) {
        // DBデータ取得失敗
        failedNotice("設定値", functionName, response?.errors);
      }

      // DBデータを返却
      return response?.data[functionName]?.settingValue;

    } catch (error) {
      errorNotice(functionName, error);
      return "";
    }

  };

  // BOMデータ取得API
  const getBomByProjectId = async (projectId: string): Promise<BomData[]> => {

    const functionName = "getBomByProjectId";

    try {

      // API実行
      const response = await WebAPI.request({
        query: `
          query getBomByProjectId($projectId: String, $reviewDate: String) {
            getBomByProjectId(projectId: $projectId, reviewDate: $reviewDate) {
              projectId
              partNo
              seq
              level
              isCustomerSpecifiedParts
              purchaseCurrencyId
              amountMonthly
              cycleTimeMonthly
              yieldMonthly
            }
          }`,
        variables: { projectId, ...(reviewDate && { reviewDate }) }
      });

      if (!response?.data[functionName]) {
        // DBデータ取得失敗
        console.log(`${functionName} データなし`);
        return [];
      }

      // DBデータを返却
      return response?.data[functionName];

    } catch (error) {
      errorNotice(functionName, error);
      return [];
    }

  };

  /**
   * 製造ライン情報取得
   * @param productionLineId 製造ラインID
   */
  const getProductionLineById = async (productionLineId: string): Promise<ProductionLineType> => {

    const functionName = "getProductionLineById";

    try {
      const payload = {
        query: `
          query getProductionLineById($projectId: String, $productionLineId: String, $reviewDate: String) {
            getProductionLineById(projectId: $projectId, productionLineId: $productionLineId, reviewDate: $reviewDate) {
              workingDays
              nonWorkingHours
            }
          }`,
        variables: { projectId, productionLineId, ...(reviewDate && { reviewDate }) }
      };

      // DB情報取得
      const response = await WebAPI.request(payload);

      if (!response?.data[functionName]) {
        // DBデータ取得失敗
        failedNotice("設定値", functionName, response?.errors);
      }

      return response.data[functionName];

    } catch (error) {
      errorNotice(functionName, error);
      return {};
    }
  }

  /**
   * 工程情報取得（プロジェクトID指定）
   * @param projectId プロジェクトID
   */
  const getUniqueProcessByProjectId = async (projectId: string): Promise<UniqueProcessType[]> => {

    const functionName = "getUniqueProcessByProjectId";

    try {
      const payload = {
        query: `
          query getUniqueProcessByProjectId($projectId: String, $reviewDate: String) {
            getUniqueProcessByProjectId(projectId: $projectId, reviewDate: $reviewDate) {
              uniqueProcessId
              uniqueProcessName
              uniqueProcessCycleTimeInSecondByPartNo
            }
          }`,
        variables: { projectId, ...(reviewDate && { reviewDate }) }
      };

      // DB情報取得
      const response = await WebAPI.request(payload);

      if (!response?.data[functionName]) {
        // DBデータ取得失敗
        failedNotice("工程情報", functionName, response?.errors);
        return [];
      }

      // uniqueProcessCycleTimeInSecondByPartNoListに変換
      const result: UniqueProcessType[] = response.data[functionName].map((process: any) => {
        const cycleTimeList = process.uniqueProcessCycleTimeInSecondByPartNo
          ? JSON.parse(process.uniqueProcessCycleTimeInSecondByPartNo)
          : [];
        return {
          ...process,
          uniqueProcessCycleTimeInSecondByPartNoList: cycleTimeList,
        };
      });

      return result;

    } catch (error) {
      errorNotice(functionName, error);
      return [];
    }
  }

  // 利益試算情報更新API
  const updateProfitablityStatement = async (
    projectId: string,
    originalProfitAndLossData: any,
    monthlyDataToSave: any,
    updateInvestmentData?: UpdateInvestmentProfitLossData | null,
    rationalizationInvestmentData?: RationalizationInvestmentProfitLossData | null,
    displayProfitAndLossData?: any,
    displayMonthlyDataToSave?: any,
    displayUpdateInvestmentData?: UpdateInvestmentProfitLossData | null,
    displayRationalizationInvestmentData?: RationalizationInvestmentProfitLossData | null
  ): Promise<string> => {

    const functionName = "updateProfitablityStatement";

    try {

      const input: any = {
        projectId: projectId,
        originalProfitAndLoss: JSON.stringify(roundNumericStringsDeep(originalProfitAndLossData)),
        originalProfitAndLossByMonth: JSON.stringify(roundNumericStringsDeep(monthlyDataToSave)),
        profitablityStatementChartData: JSON.stringify(""),
        updateDate: capexProjectData.updateDate,
        ...(reviewDate && { reviewDate }),
      };

      // 更新投資損益試算データがある場合は追加（ローカル通貨版）
      if (updateInvestmentData) {
        input.updateInvestmentProfitLossData = JSON.stringify(roundNumericStringsDeep(updateInvestmentData));
      }

      // 合理化投資損益試算データがある場合は追加（ローカル通貨版）
      if (rationalizationInvestmentData) {
        input.rationalizationProfitLossData = JSON.stringify(roundNumericStringsDeep(rationalizationInvestmentData));
      }

      // JPY版データがある場合は追加
      if (displayProfitAndLossData) {
        input.displayOriginalProfitAndLoss = JSON.stringify(roundNumericStringsDeep(displayProfitAndLossData));
      }
      if (displayMonthlyDataToSave) {
        input.displayOriginalProfitAndLossByMonth = JSON.stringify(roundNumericStringsDeep(displayMonthlyDataToSave));
      }
      // 更新投資損益試算データ（JPY版）がある場合は追加
      if (displayUpdateInvestmentData) {
        input.displayUpdateInvestmentProfitLossData = JSON.stringify(roundNumericStringsDeep(displayUpdateInvestmentData));
      }
      // 合理化投資損益試算データ（JPY版）がある場合は追加
      if (displayRationalizationInvestmentData) {
        input.displayRationalizationProfitLossData = JSON.stringify(roundNumericStringsDeep(displayRationalizationInvestmentData));
      }

      const query = `
        mutation ${functionName} ($input: ProfitablityStatementInput!) {
          ${functionName}(input: $input) {status}
        }
      `;

      // API実行
      const result = await WebAPI.request({
        query: query,
        variables: { input }
      });

      // DBデータを返却
      return result?.data?.updateProfitablityStatement?.status;

    } catch (error) {
      errorNotice(functionName, error);
      return "error";
    }

  };

  // プロジェクト追加情報取得
  const getProjectAdditionalInfoById = async (projectId: string, capexEquipmentData: any): Promise<boolean> => {
    const functionName = "getProjectAdditionalInfoById";
    try {
      // AppSync
      const payload = {
        query: `
          query getProjectAdditionalInfoById($projectId: String, $reviewDate: String) {
            getProjectAdditionalInfoById(projectId: $projectId, reviewDate: $reviewDate) {
              projectId
              benchmarkData
              updateSummaryData
              rationalizationSummaryData
              updateDate
            }
          }`,
        variables: { projectId, ...(reviewDate && { reviewDate }) }
      };

      // DB情報取得
      const response = await WebAPI.request(payload);
      const additionalInfo = response.data.getProjectAdditionalInfoById;

      if (!response?.data[functionName]) {
        // DBデータ取得失敗
        failedNotice("設定値", functionName, response?.errors);
      }

      // ベンチマークデータが存在する場合
      if (additionalInfo?.benchmarkData) {
        // JSONパース
        const getBenchmarkData = JSON.parse(additionalInfo.benchmarkData);
        setBenchmarkData(getBenchmarkData)
        // 設備数とベンチマーク数が存在しない場合終了
        if (!getBenchmarkData?.length || !capexEquipmentData?.length
          || getBenchmarkData[0]?.inputType !== "equipment") return true;
        // 全件登録必須チェックは「1件以上必須チェック」のみ残す方針のためコメントアウト
        // 両方のIDを取得
        // const capexIds = capexEquipmentData.map((eq: any) => eq.equipmentId);
        // const benchmarkIds = getBenchmarkData.map((b: any) => b.equipmentId);
        // 両方のID配列が完全一致かチェック
        // const allMatch =
        //   capexIds.length === benchmarkIds.length && // 要素数が同じ
        //   capexIds.every((id: any) => benchmarkIds.includes(id)); // capexのIDが全てbenchmarkにある
        // Booleanを返す
        // return allMatch;
        // ここまで到達した時点でベンチマークが1件以上登録されているためOK
        return true;
      }

      // 更新前後サマリーデータが存在する場合
      if (additionalInfo?.updateSummaryData) {
        // JSONパース
        const parsedUpdateSummaryData: UpdateSummaryInputData[] = JSON.parse(additionalInfo.updateSummaryData);
        setUpdateSummaryData(parsedUpdateSummaryData);
        // 設備数と更新前後サマリー数が存在しない場合終了
        if (!parsedUpdateSummaryData?.length || !capexEquipmentData?.length) return true;
        // 全件登録必須チェックは「1件以上必須チェック」のみ残す方針のためコメントアウト
        // 設備に紐づくプロセスIDを取得（ユニーク）
        // const processIds = [...new Set(capexEquipmentData.map((eq: any) => eq.processId).filter((id: any) => id))];
        // 更新前後サマリーのプロセスIDを取得
        // const updateSummaryProcessIds = parsedUpdateSummaryData.map((data: UpdateSummaryInputData) => data.productionLineId);
        // 両方のID配列が完全一致かチェック
        // const allMatch =
        //   processIds.length === updateSummaryProcessIds.length && // 要素数が同じ
        //   processIds.every((id: any) => updateSummaryProcessIds.includes(id)); // processIdが全てupdateSummaryにある
        // Booleanを返す
        // return allMatch;
        // ここまで到達した時点で更新前後サマリーが1件以上登録されているためOK
        return true;
      }

      // 合理化前後サマリーデータが存在する場合
      if (additionalInfo?.rationalizationSummaryData) {
        // JSONパース
        const parsedRationalizationSummaryData: RationalizationSummaryInputData[] = JSON.parse(additionalInfo.rationalizationSummaryData);
        setRationalizationSummaryData(parsedRationalizationSummaryData);
        // 設備数と合理化前後サマリー数が存在しない場合終了
        if (!parsedRationalizationSummaryData?.length || !capexEquipmentData?.length) return true;
        // 全件登録必須チェックは「1件以上必須チェック」のみ残す方針のためコメントアウト
        // 設備に紐づくプロセスIDを取得（ユニーク）
        // const processIds = [...new Set(capexEquipmentData.map((eq: any) => eq.processId).filter((id: any) => id))];
        // 合理化前後サマリーのプロセスIDを取得
        // const rationalizationSummaryProcessIds = parsedRationalizationSummaryData.map((data: RationalizationSummaryInputData) => data.processId);
        // 両方のID配列が完全一致かチェック
        // const allMatch =
        //   processIds.length === rationalizationSummaryProcessIds.length && // 要素数が同じ
        //   processIds.every((id: any) => rationalizationSummaryProcessIds.includes(id)); // processIdが全てrationalizationSummaryにある
        // Booleanを返す
        // return allMatch;
        // ここまで到達した時点で合理化前後サマリーが1件以上登録されているためOK
        return true;
      }

      return true;

    } catch (error) {
      errorNotice(functionName, error);
      return false;
    }
  }

  // 失敗通知
  const failedNotice = (dataName: string, apiName: string, error: any) => {
    toast({
      title: TITLE,
      description: t("features.InvestmentScreening.ProjectProfitablityStatement.message.fetchFailed", { dataName }),
      status: 'error',
      duration: 5000,
      isClosable: true,
    });
    console.error(`Failed to call API ${apiName}. errors = ${JSON.stringify(error)}`);
  }

  // エラー通知
  const errorNotice = (apiName: string, error: any) => {
    toast({
      title: TITLE,
      description: t("features.InvestmentScreening.ProjectProfitablityStatement.message.fetchDataFailed"),
      status: 'error',
      duration: 5000,
      isClosable: true,
    });
    console.error(`Error calling API ${apiName}. errors = ${JSON.stringify(error)}`);
  }

  // -----------------------------
  // useEffect
  // -----------------------------

  // 初期処理
  useEffect(() => {

    if (projectId) {
      // DBデータを取得
      fetchData();
    } else {
      // エラートーストを表示
      toast({
        title: TITLE,
        description: t("features.InvestmentScreening.ProjectProfitablityStatement.message.registerFromBasicInfo"),
        status: "warning",
        duration: 5000,
        isClosable: true,
      });
    }

  }, []);

  // 確認ボタンの横幅を取得
  useEffect(() => {

    const widths: Record<string, number> = Object.keys(refs).reduce((acc, key) => {
      acc[key] = refs[key]?.current?.offsetWidth || 0;
      return acc;
    }, {} as Record<string, number>);
    setButtonWidths(widths);

  }, []);

  // 確認者JSONを取得
  useEffect(() => {

    const fetchConfirmor = async () => {
      const confirmorData = await getConfirmor(
        WebAPI,
        projectId,
        reviewDate,
      );
      setConfirmor(confirmorData);
    };

    if (projectId) {
      fetchConfirmor();
    }

  }, [projectId, reviewDate]);

  // 部門ごとの担当者名を取得
  useEffect(() => {

    if (confirmor?.projectProfitablityStatement) {
      const updatedNames = Object.keys(names).reduce((acc, division) => {
        const divisionData = confirmor.projectProfitablityStatement?.[division as keyof Confirmor["projectProfitablityStatement"]];
        if (Array.isArray(divisionData)) {
          acc[division] = divisionData;
        } else {
          acc[division] = [];
        }
        return acc;
      }, {} as Record<string, ConfirmorInfo[]>);
      setNames(updatedNames);
    }

  }, [confirmor]);

  // 表示通貨情報を取得（初期値：JPY）
  useEffect(() => {

    if (localCurrencyId && currencyData.length > 0) {
      const selected = currencyData.find(currency => currency.currencyName === CurrencyNameJPY);
      if (selected && selected.currencyId && selected.currencyName) {
        setSelectedCurrency({ id: selected.currencyId, name: selected.currencyName });
      }
    }

  }, [capexProjectData, currencyData]);

  // decimalCurrencyはローカル通貨（companyPlantSnapshot.currencyId）基準で設定
  // 表示通貨切り替え時にスループット率等が変動しないよう、元データの計算設定を固定
  useEffect(() => {

    if (localCurrencyId && currencyData.length > 0) {
      const localCurrency = currencyData.find(c => c.currencyId === localCurrencyId);
      if (localCurrency?.currencyName) {
        // 小数点あり通貨かを取得（true: 小数点あり, false: 小数点なし）
        const hasDecimalCurrency = hasDecimal(localCurrency.currencyName);
        setDecimalCurrency(hasDecimalCurrency);
      }
    }

  }, [capexProjectData, currencyData]);

  // 権限情報を取得
  useEffect(() => {

    const pagePermissions = getPermissions(
      userPermissions,
      WorkflowId.ProjectProfitablityStatement,
      capexProjectData.purpose,
      employeeId,
      divisionId,
      capexProjectData?.registrantDivisionId
    );

    setPagePermissions(pagePermissions);

  }, [userPermissions, employeeId, divisionId, capexProjectData]);

  // 表データを作成（表示通貨切り替え時にも再計算）
  useEffect(() => {

    const fetchTableData = async () => {
      // 表示通貨の情報を取得
      const displayCurrencyName = selectedCurrency?.name || CurrencyNameJPY;
      const displayCurrencyDecimal = hasDecimal(displayCurrencyName);

      const result = await createTableData(
        capexProjectData,
        capexPartNoData,
        capexEquipmentData,
        (decimalCurrency || false),
        bomData,
        displayCurrencyName,
        displayCurrencyDecimal
      );
      setIsCalculation(result);
    };

    if (
      !isProfitLossHiddenMode     // 損益非表示モードでは表を作成しない
      && hasCalculationData       // 計算データ有無
      && decimalCurrency != null  // 工場通貨小数有無
      && capexProjectData         // T_CEAS_PROJECT データ
      && capexPartNoData          // M_CEAS_PART_NO データ
      && capexEquipmentData       // M_CEAS_EQUIPMENT データ
      && uniqueProcessData        // M_CEAS_UNIQUE_PROCESS データ
      && selectedCurrency         // 表示通貨
    ) {
      fetchTableData();
    }

  }, [hasCalculationData, capexProjectData, capexPartNoData, capexEquipmentData, uniqueProcessData, selectedCurrency, reviewDate, monthlyRateMap, updateSummaryData, rationalizationSummaryData]);

  // -----------------------------
  // 業務ロジック
  // -----------------------------

  /**
   * DBデータを取得
   *
   * @returns {void} 返却値なし
   */
  const fetchData = async (): Promise<void> => {

    const functionName = "fetchData";

    try {

      // プロジェクト情報取得APIを実行
      const capexProjectData = await getProjectById(projectId);
      setCapexProjectData(capexProjectData);

      // PartNo情報取得APIを実行
      const capexPartNoData = await getPartNo(projectId);
      setCapexPartNoData(capexPartNoData);

      // 設備情報取得APIを実行
      const capexEquipmentData = await getEquipmentByProjectId(projectId);
      setCapexEquipmentData(capexEquipmentData);

      //  製造ライン情報取得APIを実行
      const productionLineData = await getProductionLineById(capexProjectData.productionLineId || "");
      setProductionLineData(productionLineData);

      //  工程情報取得APIを実行（サイクルタイム合計計算用）
      const uniqueProcessDataResult = await getUniqueProcessByProjectId(projectId);
      setUniqueProcessData(uniqueProcessDataResult);

      //  BOMデータ取得APIを実行（仕損費計算用）
      const bomDataResult = await getBomByProjectId(projectId);
      setBomData(bomDataResult);

      //  プロジェクト追加情報取得APIを実行
      const additionalInfoCheckResult = await getProjectAdditionalInfoById(projectId, capexEquipmentData);

      if (productionLineData?.workingDays) {
        const workingDays: { fiscalYear: string, workingDaysData: WorkingDaysType }[]
          = JSON.parse(productionLineData.workingDays);
        // 稼働時間情報設定
        setWorkingDays(workingDays);
      }

      if (productionLineData?.nonWorkingHours) {
        const nonWorkingHours: { fiscalYear: string, nonWorkingHoursByMonth: NonWorkingHoursType[] }[]
          = JSON.parse(productionLineData?.nonWorkingHours);
        // 非稼働時間情報設定
        setNonWorkingHours(nonWorkingHours);
      }

      // 税率取得（会社/工場スナップショットから）
      let taxRate = getTaxRateFromSnapshot(capexProjectData?.companyPlantSnapshot) ?? 0;
      setTaxRate(new Decimal(taxRate));

      // 設定値取得APIを実行
      const discountRate = await getSettingValueByKey(SettingKey.discountRate);
      setDiscountRate(new Decimal(discountRate || 0));

      // 必須データチェック（損益非表示モード時はチェック・トーストともスキップ）
      if (isProfitLossHiddenMode) {
        setHasCalculationData(false);
      } else {
        const result = await validateRequiredData(
          capexProjectData,
          capexPartNoData,
          capexEquipmentData,
          productionLineData,
          new Decimal(taxRate || 0),
          new Decimal(discountRate || 0),
          additionalInfoCheckResult,
        );
        setHasCalculationData(result);
      }

    } catch (error) {
      console.error(`${functionName} ERROR:`, error);
      toast({
        title: TITLE,
        description: t("features.InvestmentScreening.ProjectProfitablityStatement.message.fetchDataFailed"),
        status: "error",
        duration: 5000,
        isClosable: true,
      });
    }

  };

  /**
   * 必須データチェック
   *
   * @param {ProjectWithRdsData} capexProjectData T_CEAS_PROJECT データ
   * @param {PartNo[]} capexPartNoData M_CEAS_PART_NO データ
   * @param {EquipmentType[]} capexEquipmentData M_CEAS_EQUIPMENT データ
   * @param {ProductionLineType} productionLineData M_CEAS_PRODUCTION_LINE データ
   * @param {Decimal} taxRate M_CEAS_TAX データ
   * @param {Decimal} discountRate M_CEAS_SETTING .discountRate データ
   * @param {boolean} additionalInfoCheckResult ベンチマーク・更新前後サマリーチェック結果
   * @returns {boolean} true: OK false: NG
   */
  const validateRequiredData = async (
    capexProjectData: ProjectWithRdsData,
    capexPartNoData: PartNo[],
    capexEquipmentData: EquipmentType[],
    productionLineData: ProductionLineType,
    taxRate: Decimal,
    discountRate: Decimal,
    additionalInfoCheckResult: boolean,

  ): Promise<boolean> => {

    const functionName = "validateRequiredData";

    try {
      const workingDays: { fiscalYear: string, workingDaysData: WorkingDaysType }[] | null =
        productionLineData.workingDays ? JSON.parse(productionLineData.workingDays) : null;

      const nonWorkingHours: { fiscalYear: string, nonWorkingHoursByMonth: NonWorkingHoursType[] }[] | null =
        productionLineData.nonWorkingHours ? JSON.parse(productionLineData.nonWorkingHours) : null;

      // 変数宣言
      let displayNameArray: string[] = [];                                        // データ不足画面名配列

      // ローカル通貨IDを取得（会社/工場スナップショットから取得）
      const localCurrencyIdForValidation = getLocalCurrencyId(capexProjectData?.companyPlantSnapshot);

      if (
        !capexProjectData                                                         // T_CEAS_PROJECT データ
        || !capexProjectData.projectId                                            // プロジェクトID
        || !capexProjectData.projectStartDate                                     // プロジェクト開始日
        || !capexProjectData.companyPlantId                                       // 会社/工場ID
        || !localCurrencyIdForValidation                                          // ローカル通貨ID
      ) {
        displayNameArray.push(t("features.InvestmentScreening.ProjectProfitablityStatement.validation.basicInfo"));
      }
      // 簡易モード時は販売数量・販売単価/費用入力をスキップ可能としているため、当該データの必須チェックを省略する
      const isSimple = isSimpleApplicationForm(capexProjectData?.purpose, capexProjectData?.applicationFormType);
      if (!isSimple && (
        capexPartNoData.length === 0                                              // M_CEAS_PART_NO データ
        || capexPartNoData.some((p) => !p.quantityAndPrice)                       // 数量単価JSON
        || !capexProjectData.currencyId                                           // 販売通貨ID
      )) {
        displayNameArray.push(labelQuantityAndPrice);
      }
      if (!isSimple && (
        capexPartNoData.some((p) => !p.materialCostWithInhouseProfit)             // 材料単価
        || !workingDays
        || !Array.isArray(workingDays)
        || workingDays.some((data) =>
          !data?.workingDaysData?.workOnHolidays ||
          !data?.workingDaysData?.workOnWeekendAndHolidays ||
          !data?.workingDaysData?.workingDays ||
          !data?.workingDaysData?.requiredOverTimes
        )
        || !nonWorkingHours
        || !Array.isArray(nonWorkingHours)
        || nonWorkingHours.some((data) =>
          data?.nonWorkingHoursByMonth?.some((monthData) =>
            !monthData?.workTime ||
            !monthData?.overTime ||
            !monthData?.shiftSystem
          )
        )
      )) {
        displayNameArray.push(t("features.InvestmentScreening.ProjectProfitablityStatement.validation.manufacturingProcess"));
      }
      if (
        capexEquipmentData.length === 0                                           // M_CEAS_EQUIPMENT データ
        || capexEquipmentData.some((p) => !p.depreciationStartMonth)              // 償却開始月
      ) {
        displayNameArray.push(t("features.InvestmentScreening.ProjectProfitablityStatement.validation.equipment"));
      }
      if (!(Number(discountRate) > 0)                                              // 割引率
        || (capexProjectData.companyPlantId && !(Number(taxRate) > 0))            // 税率（companyPlantIdがある場合のみ）
      ) {
        displayNameArray.push(t("features.InvestmentScreening.ProjectProfitablityStatement.validation.masterMaintenance"));
      }
      // 投資拡大の場合、ベンチマークが必須
      if (capexProjectData.purpose === "2" && !additionalInfoCheckResult) {
        displayNameArray.push(t("features.InvestmentScreening.ProjectProfitablityStatement.validation.equipmentBenchmark"));
      }
      // 更新投資の場合、更新前後サマリーが必須
      if (capexProjectData.purpose === PurposeType.UpdateAndOther.code && !additionalInfoCheckResult) {
        displayNameArray.push(t("features.InvestmentScreening.ProjectProfitablityStatement.validation.equipmentUpdateSummary"));
      }
      // 合理化投資の場合、合理化前後サマリーが必須
      if (capexProjectData.purpose === PurposeType.Rationalization.code && !additionalInfoCheckResult) {
        displayNameArray.push(t("features.InvestmentScreening.ProjectProfitablityStatement.validation.equipmentRationalizationSummary"));
      }

      // 必須データチェック
      if (displayNameArray.length !== 0) {

        // エラートーストを表示
        displayNameArray.forEach((displayName: string) => {
          toast({
            title: TITLE,
            description: t("features.InvestmentScreening.ProjectProfitablityStatement.message.registerFrom", { displayName }),
            status: "warning",
            duration: 5000,
            isClosable: true,
          });
        })

        // false: NG を返却
        return false;

      }

      // true: OK を返却
      return true;

    } catch (error) {
      console.error(`${functionName} ERROR:`, error);
      return false;
    }

  };

  /**
   * 画面に表示する年次・月次データを作成
   *
   * @param {ProjectWithRdsData} capexProject T_CEAS_PROJECT データ
   * @param {PartNo[]} capexPartNo M_CEAS_PART_NO データ
   * @param {EquipmentType[]} capexEquipment M_CEAS_EQUIPMENT データ
   * @param {boolean} decimalCurrency 工場通貨小数有無（DB保存用）
   * @param {BomData[]} capexBomData R_CEAS_BOM データ
   * @param {string} displayCurrencyName 表示通貨名（計算・表示用）
   * @param {boolean} displayCurrencyDecimal 表示通貨小数有無（計算・表示用）
   * @returns {boolean} true: OK false: NG
   */
  const createTableData = async (
    capexProject: ProjectWithRdsData,
    capexPartNo: PartNo[],
    capexEquipment: EquipmentType[],
    decimalCurrency: boolean,
    capexBomData: BomData[],
    displayCurrencyName: string,
    displayCurrencyDecimal: boolean
  ): Promise<boolean> => {

    const functionName = "createTableData";
    try {

      // #region 【共通】

      // 販売通貨
      let salesCurrency = "";
      const salesCurrencyData = currencyData.find(currency => currency.currencyId === capexProject.currencyId);
      if (salesCurrencyData && salesCurrencyData.currencyName) {
        salesCurrency = salesCurrencyData.currencyName;
      }

      // ローカル通貨（会社/工場スナップショットから取得）
      let localCurrency = "";
      const localCurrencyId = getLocalCurrencyId(capexProject.companyPlantSnapshot);
      const localCurrencyData = currencyData.find(currency => currency.currencyId === localCurrencyId);
      if (localCurrencyData && localCurrencyData.currencyName) {
        localCurrency = localCurrencyData.currencyName;
      }

      // プロジェクト開始日 YYYY-MM-DD形式
      const startDate = capexProjectData?.projectStartDate || "";

      // 振り返りモード: 月別レートで変換するラッパー関数
      // reviewDate がある場合は月別レート、ない場合は従来の単一レートを使用
      const convertWithRate = (
        amount: number | Decimal,
        from: string,
        to: string,
        yearMonth?: string
      ): Decimal => {
        if (reviewDate && yearMonth && monthlyRateMap && monthlyRateMap.size > 0) {
          return convertCurrencyByMonth(amount, from, to, yearMonth, monthlyRateMap, currencyData);
        }
        return convertCurrency(amount, from, to, currencyData);
      };

      // 経過年数 year + カレンダー月 month → "YYYY-MM" 形式に変換するヘルパー
      // yearMonthListByElapsedYears のループ内で使用
      const startDateObj = startDate ? dayjs(startDate) : null;
      const toYearMonth = (elapsedYear: number, month: number): string | undefined => {
        if (!startDateObj) return undefined;
        const startMonth = startDateObj.month() + 1; // 1-12
        // 会計年度を考慮: 開始月が4月以降の場合、1-3月は翌年
        const calendarYear = startMonth >= 4
          ? startDateObj.year() + (elapsedYear - 1) + (month >= 1 && month <= 3 ? 1 : 0)
          : startDateObj.year() + (elapsedYear - 2) + (month >= 4 ? 0 : 1);
        return `${calendarYear}-${String(month).padStart(2, "0")}`;
      };

      // 設備投資額を工場通貨へ変換
      const convertedCapexEquipment = convertEquipmentData(capexEquipment, localCurrency, currencyData, convertWithRate);

      // 設備投資額合計
      const totalEquipmentCost = convertedCapexEquipment?.reduce(
        (acc: Decimal, equip: EquipmentType) => acc.add(new Decimal(equip.amount || 0)), new Decimal(0)
      ) || new Decimal(0);

      // 総会計年度数の取得（PartNoのみ参照）
      // 簡易モード時は販売数量・販売単価未入力のため、量産開始日〜EOL予定日から年度数を算出
      const totalFiscalYearsByPartNo = isSimpleApplicationForm(capexProjectData?.purpose, capexProjectData?.applicationFormType)
        ? getFiscalYearsByProjectDates(capexProjectData.projectStartDate, capexProjectData.projectEndDate)
        : getTotalFiscalYearsByPartNo(capexPartNo);
      setTotalFiscalYearsByPartNo(totalFiscalYearsByPartNo);

      // 総会計年度数の取得
      // 簡易モード時は販売数量・販売単価未入力のため、量産開始日〜EOL予定日から年度数を算出
      const totalFiscalYearsByProject = isSimpleApplicationForm(capexProjectData?.purpose, capexProjectData?.applicationFormType)
        ? getFiscalYearsByProjectDates(capexProjectData.projectStartDate, capexProjectData.projectEndDate)
        : getTotalFiscalYearsByProject(capexPartNo, capexProjectData.moldSalesByProject,
          capexProjectData.developmentSalesByProject, capexProjectData.equipmentSalesByProject);
      setTotalFiscalYearsByProject(totalFiscalYearsByProject);

      // 年度文字列配列の取得
      const fiscalYearsString = getFiscalYearsString((capexProjectData.projectStartDate || dayjs().format("YYYY-MM-DD")), totalFiscalYearsByProject);
      setFiscalYearsString(fiscalYearsString);

      // 年月リスト生成（年度）
      // （例: [{ year: 2023, month: 1 },{ year: 2023, month: 2 }...]）
      const yearMonthListByFiscalYear = generateYearMonthList(startDate, totalFiscalYearsByPartNo);

      // 年月リスト生成（年数）
      // （例: [{ year: 1, month: 1 },{ year: 1, month: 2 }...]）
      const yearMonthListByElapsedYears = generateYearMonthList(startDate, totalFiscalYearsByPartNo, true);

      // EOL予定日以降のインデックスを計算（この月以降を0にする）
      const projectStartDateObj = dayjs(startDate);
      const projectEndDateObj = capexProjectData.projectEndDate ? dayjs(capexProjectData.projectEndDate) : null;
      const eolMonthIndex = projectEndDateObj
        ? projectEndDateObj.diff(projectStartDateObj, 'month') + 1
        : Infinity;

      // #endregion

      // #region 【PartNo別】

      // PartNo単位の数量を取得（月毎、年度毎）
      const quantitiesByPartNoMonth = getQuantitiesByPartNoMonth(capexPartNo, startDate);
      const quantitiesByPartNoYear = getQuantitiesByPartNoYear(quantitiesByPartNoMonth);
      setQuantityByPartNo(quantitiesByPartNoYear);

      // PartNo単位の単価を取得（月毎、年度毎）
      const pricesByPartNoMonth = getPricesByPartNoMonth(capexPartNo, localCurrency, currencyData, startDate, convertWithRate);
      const pricesByPartNoYear = getPricesByPartNoYear(pricesByPartNoMonth, quantitiesByPartNoMonth);
      setPriceByPartNo(pricesByPartNoYear);

      // PartNo単位の製品売上高を取得（月毎、年度毎）
      const totalSalesByPartNoMonth = calcTotalSalesByPartNoMonth(quantitiesByPartNoMonth, pricesByPartNoMonth);
      const totalSalesByPartNoYear = calcTotalSalesByPartNoYear(totalSalesByPartNoMonth, decimalCurrency);
      setProductSalesByPartNo(totalSalesByPartNoYear);

      // 金型年度合計(PartNo単位)を取得
      const moldSalesPartNoMonth = calcMoldSalesByPartNoMonth(startDate, capexPartNo, localCurrency, currencyData, convertWithRate);
      const moldSalesPartNoYear = calcTotalSalesByPartNoYear(moldSalesPartNoMonth, decimalCurrency);
      setMoldSalesByPartNo(moldSalesPartNoYear);

      // 開発費年度合計(PartNo単位)を取得
      const devSalesPartNoMonth = calcDevSalesByPartNoMonth(startDate, capexPartNo, localCurrency, currencyData, convertWithRate);
      const devSalesPartNoYear = calcTotalSalesByPartNoYear(devSalesPartNoMonth, decimalCurrency);
      setDevSalesByPartNo(devSalesPartNoYear);

      // 製品売上高、金型(PartNo単位)、開発費合計(PartNo単位)を取得
      const mergePartNoSalesByYear = calcPartNoSalesMergeByYear(totalSalesByPartNoYear, moldSalesPartNoYear, devSalesPartNoYear);
      setTotalSalesByPartNo(mergePartNoSalesByYear);

      // PartNo別 材料単価の取得（月別）
      const materialCostByPartNoByMonth = getMaterialCostByPartNoByMonth(
        capexPartNo,
        undefined,  // sourceCurrencyName (通貨変換なし)
        undefined,  // targetCurrencyName (通貨変換なし)
        undefined,  // currencyData (通貨変換なし)
        yearMonthListByFiscalYear
      );

      // PartNo別 材料単価（年度別加重平均）を計算（小数点以下4桁で丸め）
      const materialCostByPartNoYearData = getMaterialCostByPartNoYear(
        materialCostByPartNoByMonth,
        quantitiesByPartNoMonth,
        yearMonthListByFiscalYear
      );
      const roundMaterialCostByPartNoYearData = materialCostByPartNoYearData.map(({ partNo, materialCosts }) => ({
        partNo,
        materialCosts: materialCosts.map(cost => cost.toDecimalPlaces(4, Decimal.ROUND_HALF_UP))
      }));
      setMaterialCostByPartNo(roundMaterialCostByPartNoYearData);

      // PartNo別 材料費の算出
      const { totalMaterialCostsByPartNoMonth, totalMaterialCostsByPartNoYear } = calcTotalMaterialCostsByPartNoWithMonthlyData(
        quantitiesByPartNoMonth,
        materialCostByPartNoByMonth,
        yearMonthListByFiscalYear,
        convertWithRate
      );
      setTotalMaterialCostByPartNo(totalMaterialCostsByPartNoYear);

      // PartNo別 スループット額の算出
      const throughputsByPartNo = mergeSalesAndSubtractCosts(mergePartNoSalesByYear, totalMaterialCostsByPartNoYear);
      setThroughputByPartNo(throughputsByPartNo);

      // 【PartNo別】 スループット率の計算
      const throughputRateByPartNo = calcThroughputRate(throughputsByPartNo, mergePartNoSalesByYear);
      setThroughputRateByPartNo(throughputRateByPartNo);

      // #region 【PartNo別 - JPY版】
      // PartNo単位の単価を取得（JPY版）- 入力通貨から表示通貨に直接変換
      const pricesByPartNoMonthDisplay = getPricesByPartNoMonth(capexPartNo, displayCurrencyName, currencyData, startDate, convertWithRate);
      const pricesByPartNoYearDisplay = getPricesByPartNoYear(pricesByPartNoMonthDisplay, quantitiesByPartNoMonth);
      setDisplayPriceByPartNo(pricesByPartNoYearDisplay);

      // PartNo単位の製品売上高を取得（JPY版）
      const totalSalesByPartNoMonthDisplay = calcTotalSalesByPartNoMonth(quantitiesByPartNoMonth, pricesByPartNoMonthDisplay);
      const totalSalesByPartNoYearDisplay = calcTotalSalesByPartNoYear(totalSalesByPartNoMonthDisplay, displayCurrencyDecimal);
      setDisplayProductSalesByPartNo(totalSalesByPartNoYearDisplay);

      // 金型年度合計(PartNo単位)（JPY版）
      const moldSalesPartNoMonthDisplay = calcMoldSalesByPartNoMonth(startDate, capexPartNo, displayCurrencyName, currencyData, convertWithRate);
      const moldSalesPartNoYearDisplay = calcTotalSalesByPartNoYear(moldSalesPartNoMonthDisplay, displayCurrencyDecimal);
      setDisplayMoldSalesByPartNo(moldSalesPartNoYearDisplay);

      // 開発費年度合計(PartNo単位)（JPY版）
      const devSalesPartNoMonthDisplay = calcDevSalesByPartNoMonth(startDate, capexPartNo, displayCurrencyName, currencyData, convertWithRate);
      const devSalesPartNoYearDisplay = calcTotalSalesByPartNoYear(devSalesPartNoMonthDisplay, displayCurrencyDecimal);
      setDisplayDevSalesByPartNo(devSalesPartNoYearDisplay);

      // 製品売上高、金型(PartNo単位)、開発費合計(PartNo単位)（JPY版）
      const mergePartNoSalesByYearDisplay = calcPartNoSalesMergeByYear(totalSalesByPartNoYearDisplay, moldSalesPartNoYearDisplay, devSalesPartNoYearDisplay);
      setDisplayTotalSalesByPartNo(mergePartNoSalesByYearDisplay);

      // PartNo別 材料単価（JPY版）- 材料費はローカル通貨で保存されているため、ローカル通貨から表示通貨に変換
      const materialCostByPartNoYearDisplayData = reviewDate
        ? (() => {
          // 振り返りモード: 月次材料費(ローカル通貨)を月別レートで変換し、加重平均単価を算出
          // Σ(材料単価[月] × rate[月] × 数量[月]) / Σ(数量[月]) = Σ(材料費Display[月]) / Σ(数量[月])
          const partNos = [...new Set(totalMaterialCostsByPartNoMonth.map(item => item.partNo))];
          return partNos.map(partNo => {
            const partMonthData = totalMaterialCostsByPartNoMonth.filter(item => item.partNo === partNo);
            const qtyData = quantitiesByPartNoYear.find(q => q.partNo === partNo);
            const yearlyQuantities = qtyData ? qtyData.quantities : [];

            // 年度ごとに: 月次材料費をDisplay変換→合算 ÷ 年度数量
            let globalIdx = 0;
            const materialCosts = partMonthData.map((yearData, yearIdx) => {
              let yearCostDisplay = new Decimal(0);
              yearData.totalMaterialCosts.forEach((cost, monthIdx) => {
                const fiscalIdx = globalIdx + monthIdx;
                const ym = yearMonthListByFiscalYear[fiscalIdx]
                  ? `${yearMonthListByFiscalYear[fiscalIdx].year}-${String(yearMonthListByFiscalYear[fiscalIdx].month).padStart(2, "0")}`
                  : undefined;
                yearCostDisplay = yearCostDisplay.plus(
                  convertWithRate(cost, localCurrency, displayCurrencyName, ym)
                );
              });
              globalIdx += yearData.totalMaterialCosts.length;
              const qty = yearlyQuantities[yearIdx] || new Decimal(0);
              return qty.gt(0)
                ? yearCostDisplay.div(qty).toDecimalPlaces(4, Decimal.ROUND_HALF_UP)
                : new Decimal(0);
            });
            return { partNo, materialCosts };
          });
        })()
        : roundMaterialCostByPartNoYearData.map(({ partNo, materialCosts }) => ({
          partNo,
          materialCosts: materialCosts.map(cost =>
            convertWithRate(cost, localCurrency, displayCurrencyName).toDecimalPlaces(4, Decimal.ROUND_HALF_UP)
          )
        }));
      setDisplayMaterialCostByPartNo(materialCostByPartNoYearDisplayData);

      // PartNo別 材料費（JPY版）
      const totalMaterialCostsByPartNoYearDisplay = reviewDate
        ? (() => {
          // 振り返りモード: 月次材料費を月別レートで変換→PartNoごとに年度合算
          // totalMaterialCostsByPartNoMonth: { partNo, year, totalMaterialCosts: Decimal[] }[]
          // まずPartNoごとにグループ化
          const partNoMap = new Map<string, Decimal[]>();
          let globalIdx = 0;
          for (const item of totalMaterialCostsByPartNoMonth) {
            // 各月をlocal→displayに変換
            const convertedMonthly = item.totalMaterialCosts.map((cost, monthIdx) => {
              const fiscalIdx = globalIdx + monthIdx;
              const ym = yearMonthListByFiscalYear[fiscalIdx]
                ? `${yearMonthListByFiscalYear[fiscalIdx].year}-${String(yearMonthListByFiscalYear[fiscalIdx].month).padStart(2, "0")}`
                : undefined;
              return convertWithRate(cost, localCurrency, displayCurrencyName, ym).toDecimalPlaces(displayCurrencyDecimal ? 2 : 0, Decimal.ROUND_HALF_UP);
            });
            globalIdx += item.totalMaterialCosts.length;
            // 年度合計をPartNoごとに蓄積
            if (!partNoMap.has(item.partNo)) partNoMap.set(item.partNo, []);
            const arr = partNoMap.get(item.partNo)!;
            const yearIdx = item.year - 1;
            arr[yearIdx] = (arr[yearIdx] || new Decimal(0)).plus(
              convertedMonthly.reduce((sum, v) => sum.plus(v), new Decimal(0))
            );
          }
          return Array.from(partNoMap.entries()).map(([partNo, totalMaterialCosts]) => ({ partNo, totalMaterialCosts }));
        })()
        : totalMaterialCostsByPartNoYear.map(({ partNo, totalMaterialCosts }) => ({
          partNo,
          totalMaterialCosts: totalMaterialCosts.map(cost =>
            convertWithRate(cost, localCurrency, displayCurrencyName).toDecimalPlaces(displayCurrencyDecimal ? 2 : 0, Decimal.ROUND_HALF_UP)
          )
        }));
      setDisplayTotalMaterialCostByPartNo(totalMaterialCostsByPartNoYearDisplay);

      // PartNo別 スループット額（JPY版）
      const throughputsByPartNoDisplay = mergeSalesAndSubtractCosts(mergePartNoSalesByYearDisplay, totalMaterialCostsByPartNoYearDisplay);
      setDisplayThroughputByPartNo(throughputsByPartNoDisplay);
      // #endregion

      // #endregion

      // #region 【計算ロジック】年度別損益

      // ------------------------------
      // 年度別損益
      // |-売上合計
      // | |-製品売上
      // | |-PJ単位金型回収
      // | └-PJ単位開発費回収
      // | ...
      // ------------------------------

      // 製品売上
      // 計算方法: 全PartNoの売上合計
      const totalMoldSalesByMonth = getTotalSalesByMonth(moldSalesPartNoMonth);
      const totalDevSalesByMonth = getTotalSalesByMonth(devSalesPartNoMonth);
      const totalSalesByMonth = getTotalSalesByMonth(totalSalesByPartNoMonth);
      const productSalesByMonth = calcProductSalesByMonth(totalSalesByMonth, totalMoldSalesByMonth, totalDevSalesByMonth);
      const productSalesByYear = calcProductSalesByYear(productSalesByMonth);
      setProductSalesByYear(productSalesByYear);

      // PJ単位金型回収
      let sumMoldSales: Decimal[] = [];
      const projectMoldSalesByMonth = getProjectSalesByMonth(startDate, capexProjectData.moldSalesByProject, localCurrency, currencyData, convertWithRate);
      sumMoldSales = calculateProjectSalesSum(projectMoldSalesByMonth, decimalCurrency);
      setMoldSalesByYear(sumMoldSales);

      // PJ単位開発費回収
      let sumDevSales: Decimal[] = [];
      const projectDevSalesByMonth = getProjectSalesByMonth(startDate, capexProjectData.developmentSalesByProject, localCurrency, currencyData, convertWithRate);
      sumDevSales = calculateProjectSalesSum(projectDevSalesByMonth, decimalCurrency);
      setDevSalesByYear(sumDevSales);

      // PJ単位設備費回収
      let sumEquipmentSales: Decimal[] = [];
      const projectEquipmentSalesByMonth = getProjectSalesByMonth(startDate, capexProjectData.equipmentSalesByProject, localCurrency, currencyData, convertWithRate);
      sumEquipmentSales = calculateProjectSalesSum(projectEquipmentSalesByMonth, decimalCurrency);
      setEquipmentSalesByYear(sumEquipmentSales);

      // 売上合計
      // 計算方法: 製品売上 + PJ単位金型回収 + PJ単位開発費回収 + PJ単位設備費回収
      const totalSalesMoldDevByMonth = getTotalSalesMoldDevByMonth(totalSalesByMonth, totalMoldSalesByMonth, totalDevSalesByMonth, projectMoldSalesByMonth, projectDevSalesByMonth, projectEquipmentSalesByMonth);
      const totalSalesMoldDevByYear = getTotalSalesMoldDevByYear(totalSalesByPartNoYear, moldSalesPartNoYear, devSalesPartNoYear, sumMoldSales, sumDevSales, sumEquipmentSales);
      setTotalSalesByMonth(totalSalesMoldDevByMonth.map((entry) => entry.sales));
      setTotalSalesByYear(totalSalesMoldDevByYear);

      // 売上合計（JPY版）- 更新前/合理化前データ計算用
      const totalMoldSalesByMonthDisplay = getTotalSalesByMonth(moldSalesPartNoMonthDisplay);
      const totalDevSalesByMonthDisplay = getTotalSalesByMonth(devSalesPartNoMonthDisplay);
      const totalPartNoSalesByMonthDisplay = getTotalSalesByMonth(totalSalesByPartNoMonthDisplay);
      const projectMoldSalesByMonthDisplay = getProjectSalesByMonth(startDate, capexProjectData.moldSalesByProject, displayCurrencyName, currencyData, convertWithRate);
      const projectDevSalesByMonthDisplay = getProjectSalesByMonth(startDate, capexProjectData.developmentSalesByProject, displayCurrencyName, currencyData, convertWithRate);
      const projectEquipmentSalesByMonthDisplay = getProjectSalesByMonth(startDate, capexProjectData.equipmentSalesByProject, displayCurrencyName, currencyData, convertWithRate);
      const totalSalesMoldDevByMonthDisplay = getTotalSalesMoldDevByMonth(totalPartNoSalesByMonthDisplay, totalMoldSalesByMonthDisplay, totalDevSalesByMonthDisplay, projectMoldSalesByMonthDisplay, projectDevSalesByMonthDisplay, projectEquipmentSalesByMonthDisplay);
      const productSalesByMonthDisplay = calcProductSalesByMonth(totalPartNoSalesByMonthDisplay, totalMoldSalesByMonthDisplay, totalDevSalesByMonthDisplay);

      // ------------------------------
      // 年度別損益
      // | ...
      // |-売上原価
      // | |-材料費
      // | |-外注加工費
      // | |-生産工場労務費
      // | |-国内工場労務費
      // | └-生産工場経費
      // |   |-償却費以外の工場経費
      // |   └-減価償却費
      // |     |-投資対象設備
      // |     |-工場建物
      // |     └-その他の設備
      // | ...
      // ------------------------------

      // 月別の全PartNoのC/T合計（秒）を取得
      const totalCycleTimeByMonth: Decimal[] = calcTotalCycleTimeByMonth(uniqueProcessData, yearMonthListByFiscalYear);

      // 全PartNoの月別販売数量合計を取得
      const totalSalesQuantityByMonth: Decimal[] = calcTotalQuantityByMonth(
        quantitiesByPartNoMonth,
        yearMonthListByElapsedYears.length
      );

      // 月別費用データをパース（6項目共通で使用）
      const monthlyExpenseData: MonthlyExpenseData[] = capexProject.monthlyExpenseData
        ? JSON.parse(capexProject.monthlyExpenseData)
        : [];

      // #region 材料費

      let totalMaterialCostByMonth = getTotalMaterialCostByMonth(totalMaterialCostsByPartNoMonth);
      const totalMaterialCostByYear = getTotalMaterialCostByYear(totalMaterialCostByMonth);
      setTotalMaterialCostByYear(totalMaterialCostByYear);

      // 材料費（JPY版）- 更新前/合理化前データ計算用
      // 材料費はローカル通貨で保存されているため、ローカル通貨から表示通貨に変換
      const totalMaterialCostByMonthDisplay = totalMaterialCostByMonth.map(entry => ({
        year: entry.year,
        month: entry.month,
        materialCost: convertWithRate(entry.materialCost, localCurrency, displayCurrencyName, toYearMonth(entry.year, entry.month))
      }));

      // #endregion 材料費

      // #region 外注加工費

      // 通貨IDから通貨名を取得
      const outsourcingCostCurrency = currencyData?.find(
        c => c.currencyId === capexProject.outsourcingProcessingCostCurrencyId
      )?.currencyName;

      // 月別外注加工費を計算（ローカル通貨ベース、DB保存用）
      const outsideCostByMonth = convertMonthlyExpenseData(
        monthlyExpenseData,
        "outsourcingProcessingCost",
        outsourcingCostCurrency,
        localCurrency,
        currencyData,
        yearMonthListByFiscalYear,
        decimalCurrency,
        "convertOutsourcingCost",
        convertWithRate
      );

      const outsideCostByYearMonth = convertToYearMonthObject(yearMonthListByElapsedYears, outsideCostByMonth);
      const outsideCostByYear = convertMonthlyToYearly(outsideCostByMonth, decimalCurrency);
      setOutsideCostByYear(outsideCostByYear);

      // 月別外注加工費を計算（表示通貨ベース、表示用）
      const outsideCostByMonthDisplay = convertMonthlyExpenseData(
        monthlyExpenseData,
        "outsourcingProcessingCost",
        outsourcingCostCurrency,
        displayCurrencyName,
        currencyData,
        yearMonthListByFiscalYear,
        displayCurrencyDecimal,
        "convertOutsourcingCostDisplay",
        convertWithRate
      );
      const outsideCostByYearDisplay = convertMonthlyToYearly(outsideCostByMonthDisplay, displayCurrencyDecimal);
      setDisplayOutsideCostByYear(outsideCostByYearDisplay);

      // #endregion 外注加工費

      // #region 生産工場労務費

      // 通貨IDから通貨名を取得
      const productionLaborCostCurrency = currencyData?.find(
        c => c.currencyId === capexProject.productionLaborCostCurrencyId
      )?.currencyName;

      // 生産工場労務費単価を取得（仕損費の計算に使用）
      // 入力通貨からローカル通貨に変換
      const laborCostPerMinuteRaw = new Decimal(capexProject.productionFactoryLaborUnitPrice ?? 0);
      const laborCostPerMinute = productionLaborCostCurrency && localCurrency
        ? new Decimal(convertWithRate(laborCostPerMinuteRaw, productionLaborCostCurrency, localCurrency) || 0)
        : laborCostPerMinuteRaw;

      // 月別生産工場労務費を計算（monthlyExpenseDataから取得）
      const laborCostByMonth = convertMonthlyExpenseData(
        monthlyExpenseData,
        "productionLaborCost",
        productionLaborCostCurrency,
        localCurrency,
        currencyData,
        yearMonthListByFiscalYear,
        decimalCurrency,
        "calcProductionLaborCost",
        convertWithRate
      );

      // ベースアップ率はここでは適用しない（費用画面の自動算出時のみ適用）

      const laborCostByYearMonth = convertToYearMonthObject(yearMonthListByElapsedYears, laborCostByMonth);
      const laborCostByYear = convertMonthlyToYearly(laborCostByMonth, decimalCurrency);
      setLaborCostByYear(laborCostByYear);

      // 月別生産工場労務費を計算（表示通貨ベース、表示用）
      const laborCostByMonthDisplay = convertMonthlyExpenseData(
        monthlyExpenseData,
        "productionLaborCost",
        productionLaborCostCurrency,
        displayCurrencyName,
        currencyData,
        yearMonthListByFiscalYear,
        displayCurrencyDecimal,
        "calcProductionLaborCostDisplay",
        convertWithRate
      );
      // ベースアップ率はここでは適用しない（費用画面の自動算出時のみ適用）
      const laborCostByYearDisplay = convertMonthlyToYearly(laborCostByMonthDisplay, displayCurrencyDecimal);
      setDisplayLaborCostByYear(laborCostByYearDisplay);

      // #endregion 生産工場労務費

      // #region 国内工場労務費

      // 通貨IDから通貨名を取得
      const domesticLaborCostCurrency = currencyData?.find(
        c => c.currencyId === capexProject.domesticLaborCostCurrencyId
      )?.currencyName;

      // 月別国内工場労務費を計算（monthlyExpenseDataから取得）
      const domesticLaborCostByMonth = convertMonthlyExpenseData(
        monthlyExpenseData,
        "domesticLaborCost",
        domesticLaborCostCurrency,
        localCurrency,
        currencyData,
        yearMonthListByFiscalYear,
        decimalCurrency,
        "convertDomesticLaborCost",
        convertWithRate
      );

      // ベースアップ率はここでは適用しない（費用画面の自動算出時のみ適用）

      const domesticLaborCostByYearMonth = convertToYearMonthObject(yearMonthListByElapsedYears, domesticLaborCostByMonth);
      const domesticLaborCostByYear = convertMonthlyToYearly(domesticLaborCostByMonth, decimalCurrency);
      setDomesticLaborCostByYear(domesticLaborCostByYear);

      // 月別国内工場労務費を計算（表示通貨ベース、表示用）
      const domesticLaborCostByMonthDisplay = convertMonthlyExpenseData(
        monthlyExpenseData,
        "domesticLaborCost",
        domesticLaborCostCurrency,
        displayCurrencyName,
        currencyData,
        yearMonthListByFiscalYear,
        displayCurrencyDecimal,
        "convertDomesticLaborCostDisplay",
        convertWithRate
      );
      // ベースアップ率はここでは適用しない（費用画面の自動算出時のみ適用）
      const domesticLaborCostByYearDisplay = convertMonthlyToYearly(domesticLaborCostByMonthDisplay, displayCurrencyDecimal);
      setDisplayDomesticLaborCostByYear(domesticLaborCostByYearDisplay);

      // #endregion 国内工場労務費

      // #region 投資対象設備

      // 計算方法: 設備投資額 / 償却年数（設備別に償却開始月から償却年数で割る）
      const equipmentDepreciationByMonthRaw = calcEquipmentDepreciationByYearMonth(totalFiscalYearsByPartNo, convertedCapexEquipment, decimalCurrency, startDate, false);
      // EOL予定日以降は0にする
      let equipmentDepreciationByMonth = equipmentDepreciationByMonthRaw.map((entry, index) =>
        index >= eolMonthIndex ? { ...entry, value: new Decimal(0) } : entry
      );

      // 設備投資額を表示通貨へ変換（表示用）
      const displayConvertedCapexEquipment = convertEquipmentData(capexEquipment, displayCurrencyName, currencyData, convertWithRate);
      // 月別投資対象設備償却を計算（表示通貨ベース、表示用）
      const equipmentDepreciationByMonthDisplayRaw = calcEquipmentDepreciationByYearMonth(
        totalFiscalYearsByPartNo,
        displayConvertedCapexEquipment,
        displayCurrencyDecimal,
        startDate,
        false,
      );
      // EOL予定日以降は0にする
      let equipmentDepreciationByMonthDisplay = equipmentDepreciationByMonthDisplayRaw.map((entry, index) =>
        index >= eolMonthIndex ? { ...entry, value: new Decimal(0) } : entry
      );

      // 簡易モード: サマリーデータの指定 itemType について (after - before) 差分を月別集計するヘルパー
      const calcMonthlyDifferenceFromSummary = (
        summaryData: { costCurrencyId?: string; costItems?: RationalizationProfitLossItem[] }[],
        itemType: RationalizationProfitLossItemType,
        targetCurrencyName: string
      ): Decimal[] =>
        yearMonthListByElapsedYears.map(({ year: elapsedYear, month }, idx) => {
          const calendarEntry = yearMonthListByFiscalYear[idx];
          const monthKey = calendarEntry
            ? `${calendarEntry.year}-${String(calendarEntry.month).padStart(2, "0")}`
            : "";
          let monthTotal = new Decimal(0);
          summaryData.forEach((summary) => {
            if (!summary.costItems) return;
            const sourceCurrency = currencyData.find(c => c.currencyId === summary.costCurrencyId);
            const sourceCurrencyName = sourceCurrency?.currencyName || targetCurrencyName;
            summary.costItems.forEach((item) => {
              if (item.itemType !== itemType) return;
              const before = new Decimal(item.beforeMonthlyData[monthKey] || 0);
              const after = new Decimal(item.afterMonthlyData[monthKey] || 0);
              const beforeConverted = sourceCurrencyName === targetCurrencyName
                ? before : convertWithRate(before, sourceCurrencyName, targetCurrencyName);
              const afterConverted = sourceCurrencyName === targetCurrencyName
                ? after : convertWithRate(after, sourceCurrencyName, targetCurrencyName);
              monthTotal = monthTotal.plus(afterConverted.minus(beforeConverted));
            });
          });
          return monthTotal;
        });

      // 簡易モード時に参照するサマリーデータ（目的区分に応じて選択）
      const simpleSummaryData = (() => {
        if (!isSimpleApplicationForm(capexProjectData?.purpose, capexProjectData?.applicationFormType)) return null;
        if (capexProjectData?.purpose === PurposeType.Rationalization.code) return rationalizationSummaryData;
        if (capexProjectData?.purpose === PurposeType.UpdateAndOther.code) return updateSummaryData;
        return null;
      })();

      if (simpleSummaryData && simpleSummaryData.length > 0) {
        const calc = (cur: string) => calcMonthlyDifferenceFromSummary(simpleSummaryData, "targetEquipmentDepreciation", cur);
        equipmentDepreciationByMonth = convertToYearMonthObject(yearMonthListByElapsedYears, calc(localCurrency));
        equipmentDepreciationByMonthDisplay = convertToYearMonthObject(yearMonthListByElapsedYears, calc(displayCurrencyName));
      }

      // EOL処理済みの月別データから年度別に集計
      const depreciationAmountByEquip = convertMonthlyToYearly(equipmentDepreciationByMonth.map(d => d.value), decimalCurrency);
      setEquipmentDepreciationByYear(depreciationAmountByEquip);

      const depreciationAmountByEquipDisplay = convertMonthlyToYearly(equipmentDepreciationByMonthDisplay.map(d => d.value), displayCurrencyDecimal);
      setDisplayEquipmentDepreciationByYear(depreciationAmountByEquipDisplay);

      // #endregion 投資対象設備

      // #region 工場建物

      // 通貨IDから通貨名を取得
      const factoryBuildingCostCurrency = currencyData?.find(
        c => c.currencyId === capexProject.factoryBuildingDepreciationCurrencyId
      )?.currencyName;

      // 月別工場建物を計算（monthlyExpenseDataから取得）
      const factoryBuildingCostByMonthRaw = convertMonthlyExpenseData(
        monthlyExpenseData,
        "factoryBuildingDepreciation",
        factoryBuildingCostCurrency,
        localCurrency,
        currencyData,
        yearMonthListByFiscalYear,
        decimalCurrency,
        "convertFactoryBuildingCost",
        convertWithRate
      );
      // 簡易モード時は工場建物を除外、通常モード時はEOL予定日以降を0にする
      const factoryBuildingCostByMonth = isSimpleMode
        ? factoryBuildingCostByMonthRaw.map(() => new Decimal(0))
        : factoryBuildingCostByMonthRaw.map((value, index) =>
          index >= eolMonthIndex ? new Decimal(0) : value
        );

      const factoryCostByMonth = convertToYearMonthObject(yearMonthListByElapsedYears, factoryBuildingCostByMonth);
      const factoryCostByYear = convertMonthlyToYearly(factoryBuildingCostByMonth, decimalCurrency);
      setFactoryCostByYear(factoryCostByYear);

      // 月別工場建物を計算（表示通貨ベース、表示用）
      const factoryBuildingCostByMonthDisplayRaw = convertMonthlyExpenseData(
        monthlyExpenseData,
        "factoryBuildingDepreciation",
        factoryBuildingCostCurrency,
        displayCurrencyName,
        currencyData,
        yearMonthListByFiscalYear,
        displayCurrencyDecimal,
        "convertFactoryBuildingCostDisplay",
        convertWithRate
      );
      // 簡易モード時は工場建物を除外、通常モード時はEOL予定日以降を0にする
      const factoryBuildingCostByMonthDisplay = isSimpleMode
        ? factoryBuildingCostByMonthDisplayRaw.map(() => new Decimal(0))
        : factoryBuildingCostByMonthDisplayRaw.map((value, index) =>
          index >= eolMonthIndex ? new Decimal(0) : value
        );
      const factoryCostByMonthDisplay = convertToYearMonthObject(yearMonthListByElapsedYears, factoryBuildingCostByMonthDisplay);
      const factoryCostByYearDisplay = convertMonthlyToYearly(factoryBuildingCostByMonthDisplay, displayCurrencyDecimal);
      setDisplayFactoryCostByYear(factoryCostByYearDisplay);

      // #endregion 工場建物

      // #region 過去PJの投資対象設備

      let pastEquipmentCostByMonth: { year: number; month: number; value: Decimal }[] = [];
      let pastEquipmentCostByYear: Decimal[] = [];
      let pastEquipmentCostByMonthDisplay: { year: number; month: number; value: Decimal }[] = [];
      let pastEquipmentCostByYearDisplay: Decimal[] = [];
      if (capexProject.pastEquipmentCost && !isSimpleMode) {

        // JSONパース（年月単位のデータ構造）
        const pastEquipmentCost: MonthValue[] = JSON.parse(capexProject.pastEquipmentCost);

        // 通貨IDから通貨名を取得
        const pastEquipmentCostCurrency = currencyData?.find(
          c => c.currencyId === capexProject.pastEquipmentCostCurrencyId
        )?.currencyName;

        // 振り返りモード: 過去PJ設備の疑似検収月 = 最初に非ゼロ計上される月。全月同じレートで換算する
        const pastEquipPseudoAcceptanceYm = pastEquipmentCost
          .filter(e => Number(e.value ?? 0) !== 0)
          .sort((a, b) => a.year !== b.year ? a.year - b.year : a.month - b.month)[0];
        const pastEquipConvertFn = (reviewDate && pastEquipPseudoAcceptanceYm)
          ? (amount: number | Decimal, from: string, to: string) => convertWithRate(amount, from, to, `${pastEquipPseudoAcceptanceYm.year}-${String(pastEquipPseudoAcceptanceYm.month).padStart(2, "0")}`)
          : convertWithRate;

        // 月別過去PJの投資対象設備を計算（年月単位のデータを変換）
        const pastEquipmentCostByMonthRaw = convertMonthlyValueData(
          pastEquipmentCost,
          pastEquipmentCostCurrency,
          localCurrency,
          currencyData,
          yearMonthListByFiscalYear,
          decimalCurrency,
          "convertPastEquipmentCost",
          pastEquipConvertFn
        );
        // EOL予定日以降は0にする
        const pastEquipmentCostByMonthNew = pastEquipmentCostByMonthRaw.map((value, index) =>
          index >= eolMonthIndex ? new Decimal(0) : value
        );

        pastEquipmentCostByMonth = convertToYearMonthObject(yearMonthListByElapsedYears, pastEquipmentCostByMonthNew);
        pastEquipmentCostByYear = convertMonthlyToYearly(pastEquipmentCostByMonthNew, decimalCurrency);
        setPastEquipmentCostByYear(pastEquipmentCostByYear);

        // 月別過去PJの投資対象設備を計算（表示通貨ベース、表示用）
        const pastEquipmentCostByMonthDisplayRaw = convertMonthlyValueData(
          pastEquipmentCost,
          pastEquipmentCostCurrency,
          displayCurrencyName,
          currencyData,
          yearMonthListByFiscalYear,
          displayCurrencyDecimal,
          "convertPastEquipmentCostDisplay",
          pastEquipConvertFn
        );
        // EOL予定日以降は0にする
        const pastEquipmentCostByMonthDisplayNew = pastEquipmentCostByMonthDisplayRaw.map((value, index) =>
          index >= eolMonthIndex ? new Decimal(0) : value
        );
        pastEquipmentCostByMonthDisplay = convertToYearMonthObject(yearMonthListByElapsedYears, pastEquipmentCostByMonthDisplayNew);
        pastEquipmentCostByYearDisplay = convertMonthlyToYearly(pastEquipmentCostByMonthDisplayNew, displayCurrencyDecimal);
        setDisplayPastEquipmentCostByYear(pastEquipmentCostByYearDisplay);
      }

      // #endregion 過去PJの投資対象設備

      // #region その他の設備

      let otherEquipmentCostByMonth: { year: number; month: number; value: Decimal }[] = [];
      let otherEquipmentCostByYear: Decimal[] = [];
      let otherEquipmentCostByMonthDisplay: { year: number; month: number; value: Decimal }[] = [];
      let otherEquipmentCostByYearDisplay: Decimal[] = [];
      if (capexProject.otherEquipmentCost) {

        // JSONパース（年月単位のデータ構造）
        const otherEquipmentCost: MonthValue[] = JSON.parse(capexProject.otherEquipmentCost);

        // 通貨IDから通貨名を取得
        const otherEquipmentCostCurrency = currencyData?.find(
          c => c.currencyId === capexProject.otherEquipmentCostCurrencyId
        )?.currencyName;

        // 月別その他の設備を計算（年月単位のデータを変換）
        const otherEquipmentCostByMonthRaw = convertMonthlyValueData(
          otherEquipmentCost,
          otherEquipmentCostCurrency,
          localCurrency,
          currencyData,
          yearMonthListByFiscalYear,
          decimalCurrency,
          "convertOtherEquipmentCost",
          convertWithRate
        );
        // EOL予定日以降は0にする
        const otherEquipmentCostByMonthNew = otherEquipmentCostByMonthRaw.map((value, index) =>
          index >= eolMonthIndex ? new Decimal(0) : value
        );

        otherEquipmentCostByMonth = convertToYearMonthObject(yearMonthListByElapsedYears, otherEquipmentCostByMonthNew);
        otherEquipmentCostByYear = convertMonthlyToYearly(otherEquipmentCostByMonthNew, decimalCurrency);
        setOtherEquipmentCostByYear(otherEquipmentCostByYear);

        // 月別その他の設備を計算（表示通貨ベース、表示用）
        const otherEquipmentCostByMonthDisplayRaw = convertMonthlyValueData(
          otherEquipmentCost,
          otherEquipmentCostCurrency,
          displayCurrencyName,
          currencyData,
          yearMonthListByFiscalYear,
          displayCurrencyDecimal,
          "convertOtherEquipmentCostDisplay",
          convertWithRate
        );
        // EOL予定日以降は0にする
        const otherEquipmentCostByMonthDisplayNew = otherEquipmentCostByMonthDisplayRaw.map((value, index) =>
          index >= eolMonthIndex ? new Decimal(0) : value
        );

        otherEquipmentCostByMonthDisplay = convertToYearMonthObject(yearMonthListByElapsedYears, otherEquipmentCostByMonthDisplayNew);
        otherEquipmentCostByYearDisplay = convertMonthlyToYearly(otherEquipmentCostByMonthDisplayNew, displayCurrencyDecimal);
        setDisplayOtherEquipmentCostByYear(otherEquipmentCostByYearDisplay);
      }

      if (simpleSummaryData && simpleSummaryData.length > 0) {
        const calc = (cur: string) => calcMonthlyDifferenceFromSummary(simpleSummaryData, "otherDepreciation", cur);
        const otherEquipmentCostByMonthNew = calc(localCurrency);
        const otherEquipmentCostByMonthDisplayNew = calc(displayCurrencyName);
        otherEquipmentCostByMonth = convertToYearMonthObject(yearMonthListByElapsedYears, otherEquipmentCostByMonthNew);
        otherEquipmentCostByYear = convertMonthlyToYearly(otherEquipmentCostByMonthNew, decimalCurrency);
        setOtherEquipmentCostByYear(otherEquipmentCostByYear);
        otherEquipmentCostByMonthDisplay = convertToYearMonthObject(yearMonthListByElapsedYears, otherEquipmentCostByMonthDisplayNew);
        otherEquipmentCostByYearDisplay = convertMonthlyToYearly(otherEquipmentCostByMonthDisplayNew, displayCurrencyDecimal);
        setDisplayOtherEquipmentCostByYear(otherEquipmentCostByYearDisplay);
      }

      // #endregion その他の設備

      // #region 減価償却費

      // 計算方法: 投資対象設備 + 工場建物 + 過去PJの投資対象設備 + その他の設備

      const depreciationByMonth = mergeYearMonthValues(equipmentDepreciationByMonth, factoryCostByMonth, pastEquipmentCostByMonth, otherEquipmentCostByMonth);
      // EOL予定日以降を0にした月別データから年度別に集計
      const equipmentDepreciationByYear = convertMonthlyToYearly(equipmentDepreciationByMonth.map(d => d.value), decimalCurrency);
      const depreciationByYear = sumDecimalArray(equipmentDepreciationByYear, factoryCostByYear, pastEquipmentCostByYear, otherEquipmentCostByYear);
      setDepreciationByYear(depreciationByYear);

      // 減価償却費（表示通貨ベース、表示用）
      const depreciationByMonthDisplay = mergeYearMonthValues(equipmentDepreciationByMonthDisplay, factoryCostByMonthDisplay, pastEquipmentCostByMonthDisplay, otherEquipmentCostByMonthDisplay);
      const equipmentDepreciationByYearDisplay = convertMonthlyToYearly(equipmentDepreciationByMonthDisplay.map(d => d.value), displayCurrencyDecimal);
      const depreciationByYearDisplay = sumDecimalArray(equipmentDepreciationByYearDisplay, factoryCostByYearDisplay, pastEquipmentCostByYearDisplay, otherEquipmentCostByYearDisplay);
      setDisplayDepreciationByYear(depreciationByYearDisplay);

      // #endregion 減価償却費

      // #region 償却費以外の工場経費

      // 通貨IDから通貨名を取得
      const overseasFactoryExpensesCurrency = currencyData?.find(
        c => c.currencyId === capexProject.overseasFactoryExpensesCurrencyId
      )?.currencyName;

      // 生産工場経費単価を取得（仕損費の計算に使用）
      // 入力通貨からローカル通貨に変換
      const factoryExpensePerMinuteRaw = new Decimal(capexProject.productionFactoryExpenseUnitPrice ?? 0);
      const factoryExpensePerMinute = overseasFactoryExpensesCurrency && localCurrency
        ? new Decimal(convertWithRate(factoryExpensePerMinuteRaw, overseasFactoryExpensesCurrency, localCurrency) || 0)
        : factoryExpensePerMinuteRaw;

      // 月別償却費以外の工場経費を計算（monthlyExpenseDataから取得）
      const overHeadCostByMonth = convertMonthlyExpenseData(
        monthlyExpenseData,
        "overseasFactoryExpenses",
        overseasFactoryExpensesCurrency,
        localCurrency,
        currencyData,
        yearMonthListByFiscalYear,
        decimalCurrency,
        "calcFactoryOverheadCost",
        convertWithRate
      );

      const overHeadCostByYearMonth = convertToYearMonthObject(yearMonthListByElapsedYears, overHeadCostByMonth);
      const overHeadCostByYear = convertMonthlyToYearly(overHeadCostByMonth, decimalCurrency);
      setOverHeadCostByYear(overHeadCostByYear);

      // 月別償却費以外の工場経費を計算（表示通貨ベース、表示用）
      const overHeadCostByMonthDisplay = convertMonthlyExpenseData(
        monthlyExpenseData,
        "overseasFactoryExpenses",
        overseasFactoryExpensesCurrency,
        displayCurrencyName,
        currencyData,
        yearMonthListByFiscalYear,
        displayCurrencyDecimal,
        "calcFactoryOverheadCostDisplay",
        convertWithRate
      );
      const overHeadCostByYearMonthDisplay = convertToYearMonthObject(yearMonthListByElapsedYears, overHeadCostByMonthDisplay);
      const overHeadCostByYearDisplay = convertMonthlyToYearly(overHeadCostByMonthDisplay, displayCurrencyDecimal);
      setDisplayOverHeadCostByYear(overHeadCostByYearDisplay);

      // #endregion 償却費以外の工場経費

      // #region 生産工場経費

      // 計算方法: 償却費以外の工場経費 + 減価償却費

      const factoryExpensesByMonth = mergeYearMonthValues(overHeadCostByYearMonth, depreciationByMonth);
      const factoryExpensesByYear = sumDecimalArray(overHeadCostByYear, depreciationByYear);
      setFactoryExpensesByYear(factoryExpensesByYear);

      // 生産工場経費（表示通貨ベース、表示用）
      const factoryExpensesByMonthDisplay = mergeYearMonthValues(overHeadCostByYearMonthDisplay, depreciationByMonthDisplay);
      const factoryExpensesByYearDisplay = sumDecimalArray(overHeadCostByYearDisplay, depreciationByYearDisplay);
      setDisplayFactoryExpensesByYear(factoryExpensesByYearDisplay);

      // #endregion 生産工場経費

      // #region 国内工場経費

      // 通貨IDから通貨名を取得（償却費以外の工場経費）
      const domesticFactoryExpensesCurrency = currencyData?.find(
        c => c.currencyId === capexProject.domesticFactoryExpensesCurrencyId
      )?.currencyName;

      // 月別国内工場経費（償却費以外）を計算（monthlyExpenseDataから取得）
      // 注: monthlyExpenseData内のフィールド名はdomesticFactoryExpensesだが、これは「償却費以外の工場経費」を表す
      const domesticOverHeadCostByMonth = convertMonthlyExpenseData(
        monthlyExpenseData,
        "domesticFactoryExpenses",
        domesticFactoryExpensesCurrency,
        localCurrency,
        currencyData,
        yearMonthListByFiscalYear,
        decimalCurrency,
        "convertDomesticOverHeadCost",
        convertWithRate
      );

      const domesticOverHeadCostByYearMonth = convertToYearMonthObject(yearMonthListByElapsedYears, domesticOverHeadCostByMonth);
      const domesticOverHeadCostByYear = convertMonthlyToYearly(domesticOverHeadCostByMonth, decimalCurrency);
      setDomesticOverHeadCostByYear(domesticOverHeadCostByYear);

      // 月別国内工場経費（償却費以外）を計算（表示通貨ベース、表示用）
      const domesticOverHeadCostByMonthDisplay = convertMonthlyExpenseData(
        monthlyExpenseData,
        "domesticFactoryExpenses",
        domesticFactoryExpensesCurrency,
        displayCurrencyName,
        currencyData,
        yearMonthListByFiscalYear,
        displayCurrencyDecimal,
        "convertDomesticOverHeadCostDisplay",
        convertWithRate
      );
      const domesticOverHeadCostByYearMonthDisplay = convertToYearMonthObject(yearMonthListByElapsedYears, domesticOverHeadCostByMonthDisplay);
      const domesticOverHeadCostByYearDisplay = convertMonthlyToYearly(domesticOverHeadCostByMonthDisplay, displayCurrencyDecimal);
      setDisplayDomesticOverHeadCostByYear(domesticOverHeadCostByYearDisplay);

      // 通貨IDから通貨名を取得（国内工場経費-投資対象設備）
      const domesticInvestmentDepreciationCurrency = currencyData?.find(
        c => c.currencyId === capexProject.domesticInvestmentDepreciationCurrencyId
      )?.currencyName;

      // 月別国内工場経費（投資対象設備）を計算（monthlyExpenseDataから取得）
      const domesticInvestmentDepreciationByMonth = convertMonthlyExpenseData(
        monthlyExpenseData,
        "domesticInvestmentDepreciation",
        domesticInvestmentDepreciationCurrency,
        localCurrency,
        currencyData,
        yearMonthListByFiscalYear,
        decimalCurrency,
        "convertDomesticInvestmentDepreciation",
        convertWithRate
      );

      const domesticInvestmentDepreciationByYearMonth = convertToYearMonthObject(yearMonthListByElapsedYears, domesticInvestmentDepreciationByMonth);
      const domesticInvestmentDepreciationByYear = convertMonthlyToYearly(domesticInvestmentDepreciationByMonth, decimalCurrency);
      setDomesticInvestmentDepreciationByYear(domesticInvestmentDepreciationByYear);

      // 月別国内工場経費（投資対象設備）を計算（表示通貨ベース、表示用）
      const domesticInvestmentDepreciationByMonthDisplay = convertMonthlyExpenseData(
        monthlyExpenseData,
        "domesticInvestmentDepreciation",
        domesticInvestmentDepreciationCurrency,
        displayCurrencyName,
        currencyData,
        yearMonthListByFiscalYear,
        displayCurrencyDecimal,
        "convertDomesticInvestmentDepreciationDisplay",
        convertWithRate
      );
      const domesticInvestmentDepreciationByYearMonthDisplay = convertToYearMonthObject(yearMonthListByElapsedYears, domesticInvestmentDepreciationByMonthDisplay);
      const domesticInvestmentDepreciationByYearDisplay = convertMonthlyToYearly(domesticInvestmentDepreciationByMonthDisplay, displayCurrencyDecimal);
      setDisplayDomesticInvestmentDepreciationByYear(domesticInvestmentDepreciationByYearDisplay);

      // 通貨IDから通貨名を取得（国内工場経費-工場建物）
      const domesticFactoryBuildingDepreciationCurrency = currencyData?.find(
        c => c.currencyId === capexProject.domesticFactoryBuildingDepreciationCurrencyId
      )?.currencyName;

      // 月別国内工場経費（工場建物）を計算（monthlyExpenseDataから取得）
      const domesticFactoryBuildingDepreciationByMonth = convertMonthlyExpenseData(
        monthlyExpenseData,
        "domesticFactoryBuildingDepreciation",
        domesticFactoryBuildingDepreciationCurrency,
        localCurrency,
        currencyData,
        yearMonthListByFiscalYear,
        decimalCurrency,
        "convertDomesticFactoryBuildingDepreciation",
        convertWithRate
      );

      const domesticFactoryBuildingDepreciationByYearMonth = convertToYearMonthObject(yearMonthListByElapsedYears, domesticFactoryBuildingDepreciationByMonth);
      const domesticFactoryBuildingDepreciationByYear = convertMonthlyToYearly(domesticFactoryBuildingDepreciationByMonth, decimalCurrency);
      setDomesticFactoryBuildingDepreciationByYear(domesticFactoryBuildingDepreciationByYear);

      // 月別国内工場経費（工場建物）を計算（表示通貨ベース、表示用）
      const domesticFactoryBuildingDepreciationByMonthDisplay = convertMonthlyExpenseData(
        monthlyExpenseData,
        "domesticFactoryBuildingDepreciation",
        domesticFactoryBuildingDepreciationCurrency,
        displayCurrencyName,
        currencyData,
        yearMonthListByFiscalYear,
        displayCurrencyDecimal,
        "convertDomesticFactoryBuildingDepreciationDisplay",
        convertWithRate
      );
      const domesticFactoryBuildingDepreciationByYearMonthDisplay = convertToYearMonthObject(yearMonthListByElapsedYears, domesticFactoryBuildingDepreciationByMonthDisplay);
      const domesticFactoryBuildingDepreciationByYearDisplay = convertMonthlyToYearly(domesticFactoryBuildingDepreciationByMonthDisplay, displayCurrencyDecimal);
      setDisplayDomesticFactoryBuildingDepreciationByYear(domesticFactoryBuildingDepreciationByYearDisplay);

      // 通貨IDから通貨名を取得（国内工場経費-その他の設備）
      const domesticOtherDepreciationCurrency = currencyData?.find(
        c => c.currencyId === capexProject.domesticOtherDepreciationCurrencyId
      )?.currencyName;

      // 月別国内工場経費（その他の設備）を計算（monthlyExpenseDataから取得）
      const domesticOtherDepreciationByMonth = convertMonthlyExpenseData(
        monthlyExpenseData,
        "domesticOtherDepreciation",
        domesticOtherDepreciationCurrency,
        localCurrency,
        currencyData,
        yearMonthListByFiscalYear,
        decimalCurrency,
        "convertDomesticOtherDepreciation",
        convertWithRate
      );

      const domesticOtherDepreciationByYearMonth = convertToYearMonthObject(yearMonthListByElapsedYears, domesticOtherDepreciationByMonth);
      const domesticOtherDepreciationByYear = convertMonthlyToYearly(domesticOtherDepreciationByMonth, decimalCurrency);
      setDomesticOtherDepreciationByYear(domesticOtherDepreciationByYear);

      // 月別国内工場経費（その他の設備）を計算（表示通貨ベース、表示用）
      const domesticOtherDepreciationByMonthDisplay = convertMonthlyExpenseData(
        monthlyExpenseData,
        "domesticOtherDepreciation",
        domesticOtherDepreciationCurrency,
        displayCurrencyName,
        currencyData,
        yearMonthListByFiscalYear,
        displayCurrencyDecimal,
        "convertDomesticOtherDepreciationDisplay",
        convertWithRate
      );
      const domesticOtherDepreciationByYearMonthDisplay = convertToYearMonthObject(yearMonthListByElapsedYears, domesticOtherDepreciationByMonthDisplay);
      const domesticOtherDepreciationByYearDisplay = convertMonthlyToYearly(domesticOtherDepreciationByMonthDisplay, displayCurrencyDecimal);
      setDisplayDomesticOtherDepreciationByYear(domesticOtherDepreciationByYearDisplay);

      // 国内工場経費-減価償却費（3項目の合計）
      const domesticDepreciationByMonth = mergeYearMonthValues(
        domesticInvestmentDepreciationByYearMonth,
        domesticFactoryBuildingDepreciationByYearMonth,
        domesticOtherDepreciationByYearMonth
      );
      const domesticDepreciationByYear = sumDecimalArray(
        domesticInvestmentDepreciationByYear,
        domesticFactoryBuildingDepreciationByYear,
        domesticOtherDepreciationByYear
      );
      setDomesticDepreciationByYear(domesticDepreciationByYear);

      // 国内工場経費-減価償却費（表示通貨ベース、表示用）
      const domesticDepreciationByMonthDisplay = mergeYearMonthValues(
        domesticInvestmentDepreciationByYearMonthDisplay,
        domesticFactoryBuildingDepreciationByYearMonthDisplay,
        domesticOtherDepreciationByYearMonthDisplay
      );
      const domesticDepreciationByYearDisplay = sumDecimalArray(
        domesticInvestmentDepreciationByYearDisplay,
        domesticFactoryBuildingDepreciationByYearDisplay,
        domesticOtherDepreciationByYearDisplay
      );
      setDisplayDomesticDepreciationByYear(domesticDepreciationByYearDisplay);

      // 国内工場経費（合計）= 償却費以外 + 減価償却費
      const domesticFactoryExpensesByYearMonth = mergeYearMonthValues(
        domesticOverHeadCostByYearMonth,
        domesticDepreciationByMonth
      );
      const domesticFactoryExpensesByYear = sumDecimalArray(domesticOverHeadCostByYear, domesticDepreciationByYear);
      setDomesticFactoryExpensesByYear(domesticFactoryExpensesByYear);

      // 国内工場経費（合計）（表示通貨ベース、表示用）
      const domesticFactoryExpensesByYearMonthDisplay = mergeYearMonthValues(
        domesticOverHeadCostByYearMonthDisplay,
        domesticDepreciationByMonthDisplay
      );
      const domesticFactoryExpensesByYearDisplay = sumDecimalArray(domesticOverHeadCostByYearDisplay, domesticDepreciationByYearDisplay);
      setDisplayDomesticFactoryExpensesByYear(domesticFactoryExpensesByYearDisplay);

      // #endregion 国内工場経費

      // #region 仕損費

      // BOMデータをPartNo別にグループ化（ProcessBomRowForCalculation[]形式に変換）
      const bomDataByPartNo: Record<string, ProcessBomRowForCalculation[]> = {};

      // JSON文字列をRecord<string, string>形式にパース
      const parseMonthlyData = (jsonStr: string | null | undefined): Record<string, string> => {
        if (!jsonStr) return {};
        try {
          return JSON.parse(jsonStr);
        } catch {
          return {};
        }
      };

      for (const bomItem of capexBomData) {
        const partNo = bomItem.partNo ?? "";
        if (!bomDataByPartNo[partNo]) {
          bomDataByPartNo[partNo] = [];
        }

        bomDataByPartNo[partNo].push({
          seq: bomItem.seq ?? "",
          level: bomItem.level ?? "",
          isCustomerSpecifiedParts: bomItem.isCustomerSpecifiedParts,
          purchaseCurrencyId: bomItem.purchaseCurrencyId,
          amountMonthly: parseMonthlyData(bomItem.amountMonthly),
          cycleTimeMonthly: parseMonthlyData(bomItem.cycleTimeMonthly),
          yieldMonthly: parseMonthlyData(bomItem.yieldMonthly),
        });
      }

      // BOMデータの累積材料費（amountMonthly）を購入通貨からローカル通貨に変換
      const bomDataByPartNoLocal: Record<string, ProcessBomRowForCalculation[]> = {};
      for (const partNo of Object.keys(bomDataByPartNo)) {
        bomDataByPartNoLocal[partNo] = bomDataByPartNo[partNo].map(row => {
          // 購入通貨を取得
          const purchaseCurrencyName = currencyData?.find(
            c => c.currencyId === row.purchaseCurrencyId
          )?.currencyName;
          // 購入通貨とローカル通貨が同じ、または購入通貨が未設定の場合は変換不要
          if (!purchaseCurrencyName || purchaseCurrencyName === localCurrency) {
            return row;
          }
          // amountMonthlyの値を購入通貨からローカル通貨に変換
          const convertedAmountMonthly: Record<string, string> = {};
          for (const [yearMonth, amount] of Object.entries(row.amountMonthly || {})) {
            if (amount && amount !== "") {
              // 振り返りモード時は月別レートで変換（yearMonthキーをそのまま渡す）
              const convertedAmount = convertWithRate(
                new Decimal(amount).toNumber(),
                purchaseCurrencyName,
                localCurrency,
                yearMonth
              );
              convertedAmountMonthly[yearMonth] = convertedAmount.toString();
            } else {
              convertedAmountMonthly[yearMonth] = amount;
            }
          }
          return {
            ...row,
            amountMonthly: convertedAmountMonthly,
          };
        });
      }

      // 月別仕損費を計算（ローカル通貨ベース）
      // 振り返りモード: 労務費/経費単価を月別レートで変換する関数を渡す
      const laborCostRateByMonthLocal = reviewDate && productionLaborCostCurrency && localCurrency && productionLaborCostCurrency !== localCurrency
        ? (ym: string) => convertWithRate(laborCostPerMinuteRaw, productionLaborCostCurrency, localCurrency, ym)
        : undefined;
      const overheadRateByMonthLocal = reviewDate && overseasFactoryExpensesCurrency && localCurrency && overseasFactoryExpensesCurrency !== localCurrency
        ? (ym: string) => convertWithRate(factoryExpensePerMinuteRaw, overseasFactoryExpensesCurrency, localCurrency, ym)
        : undefined;
      const scrapCostByMonth = calculateScrapCostByMonth(
        bomDataByPartNoLocal,
        quantitiesByPartNoMonth,
        yearMonthListByFiscalYear,
        laborCostPerMinute,
        factoryExpensePerMinute,
        laborCostRateByMonthLocal,
        overheadRateByMonthLocal
      );

      const scrapCostByYearMonth = convertToYearMonthObject(yearMonthListByElapsedYears, scrapCostByMonth);
      const scrapCostByYear = convertMonthlyToYearly(scrapCostByMonth, decimalCurrency);
      setScrapCostByYear(scrapCostByYear);

      // 月別仕損費を計算（表示通貨ベース、表示用）
      // 労務費単価と工場経費単価を表示通貨に変換
      const laborCostCurrencyName = currencyData?.find(
        c => c.currencyId === capexProject.productionLaborCostCurrencyId
      )?.currencyName;
      const laborCostPerMinuteDisplay = new Decimal(
        convertWithRate(
          capexProject.productionFactoryLaborUnitPrice ?? 0,
          laborCostCurrencyName ?? "",
          displayCurrencyName
        ) || 0
      );
      const factoryExpenseCurrencyName = currencyData?.find(
        c => c.currencyId === capexProject.overseasFactoryExpensesCurrencyId
      )?.currencyName;
      const factoryExpensePerMinuteDisplay = new Decimal(
        convertWithRate(
          capexProject.productionFactoryExpenseUnitPrice ?? 0,
          factoryExpenseCurrencyName ?? "",
          displayCurrencyName
        ) || 0
      );

      // BOMデータの累積材料費（amountMonthly）を購入通貨から表示通貨に変換
      const bomDataByPartNoDisplay: Record<string, ProcessBomRowForCalculation[]> = {};
      for (const partNo of Object.keys(bomDataByPartNo)) {
        bomDataByPartNoDisplay[partNo] = bomDataByPartNo[partNo].map(row => {
          // 購入通貨を取得
          const purchaseCurrencyName = currencyData?.find(
            c => c.currencyId === row.purchaseCurrencyId
          )?.currencyName;
          // 購入通貨と表示通貨が同じ、または購入通貨が未設定の場合は変換不要
          if (!purchaseCurrencyName || purchaseCurrencyName === displayCurrencyName) {
            return row;
          }
          // amountMonthlyの値を購入通貨から表示通貨に変換
          const convertedAmountMonthly: Record<string, string> = {};
          for (const [yearMonth, amount] of Object.entries(row.amountMonthly || {})) {
            if (amount && amount !== "") {
              // 振り返りモード時は月別レートで変換
              const convertedAmount = convertWithRate(
                new Decimal(amount).toNumber(),
                purchaseCurrencyName,
                displayCurrencyName,
                yearMonth
              );
              convertedAmountMonthly[yearMonth] = convertedAmount.toString();
            } else {
              convertedAmountMonthly[yearMonth] = amount;
            }
          }
          return {
            ...row,
            amountMonthly: convertedAmountMonthly,
          };
        });
      }

      // 振り返りモード: 表示通貨版も月別レートで労務費/経費単価を変換
      const laborCostRateByMonthDisplay = reviewDate && laborCostCurrencyName && displayCurrencyName && laborCostCurrencyName !== displayCurrencyName
        ? (ym: string) => convertWithRate(capexProject.productionFactoryLaborUnitPrice ?? 0, laborCostCurrencyName, displayCurrencyName, ym)
        : undefined;
      const overheadRateByMonthDisplay = reviewDate && factoryExpenseCurrencyName && displayCurrencyName && factoryExpenseCurrencyName !== displayCurrencyName
        ? (ym: string) => convertWithRate(capexProject.productionFactoryExpenseUnitPrice ?? 0, factoryExpenseCurrencyName, displayCurrencyName, ym)
        : undefined;
      const scrapCostByMonthDisplay = calculateScrapCostByMonth(
        bomDataByPartNoDisplay,
        quantitiesByPartNoMonth,
        yearMonthListByFiscalYear,
        laborCostPerMinuteDisplay,
        factoryExpensePerMinuteDisplay,
        laborCostRateByMonthDisplay,
        overheadRateByMonthDisplay
      );
      const scrapCostByYearDisplay = convertMonthlyToYearly(scrapCostByMonthDisplay, displayCurrencyDecimal);
      setDisplayScrapCostByYear(scrapCostByYearDisplay);

      // #endregion 仕損費

      // #region 売上原価

      // 計算方法: 材料費 + 外注加工費 + 生産工場労務費 + 国内工場労務費 + 生産工場経費 + 国内工場経費 + 仕損費

      const salesCostByMonth = mergeYearMonthValues(
        totalMaterialCostByMonth.map(({ year, month, materialCost }) => ({ year, month, value: materialCost, })),
        outsideCostByYearMonth,
        laborCostByYearMonth,
        domesticLaborCostByYearMonth,
        factoryExpensesByMonth,
        domesticFactoryExpensesByYearMonth,
        scrapCostByYearMonth,
      );
      const salesCostByYear = sumDecimalArray(
        totalMaterialCostByYear,        // 材料費
        outsideCostByYear,              // 外注加工費
        laborCostByYear,                // 生産工場労務費
        domesticLaborCostByYear,        // 国内工場労務費
        factoryExpensesByYear,          // 生産工場経費
        domesticFactoryExpensesByYear,  // 国内工場経費
        scrapCostByYear                 // 仕損費
      );
      setSalesCostByYear(salesCostByYear);

      // 売上原価（表示通貨ベース、表示用）
      // 材料費は表示通貨に変換
      const totalMaterialCostByYearDisplay = reviewDate
        ? convertMonthlyToYearly(totalMaterialCostByMonthDisplay.map(entry => entry.materialCost), displayCurrencyDecimal)
        : totalMaterialCostByYear.map(value =>
          new Decimal(convertWithRate(value, localCurrency, displayCurrencyName) || 0)
        );
      setDisplayTotalMaterialCostByYear(totalMaterialCostByYearDisplay);

      const salesCostByYearDisplay = sumDecimalArray(
        totalMaterialCostByYearDisplay,      // 材料費
        outsideCostByYearDisplay,            // 外注加工費
        laborCostByYearDisplay,              // 生産工場労務費
        domesticLaborCostByYearDisplay,      // 国内工場労務費
        factoryExpensesByYearDisplay,        // 生産工場経費
        domesticFactoryExpensesByYearDisplay, // 国内工場経費
        scrapCostByYearDisplay               // 仕損費
      );
      setDisplaySalesCostByYear(salesCostByYearDisplay);

      // #endregion 売上原価

      // ------------------------------
      // 年度別損益
      // | ...
      // |-販売費及び一般管理費
      // | |-生産工場販管費
      // | |-国内工場販管費
      // | |-本社・販社販管費
      // | |-開発費
      // | └-ロイヤリティ
      // | ...
      // ------------------------------

      // #region 生産工場販管費

      // 通貨IDから通貨名を取得
      const productionSgaExpensesCurrency = currencyData?.find(
        c => c.currencyId === capexProject.productionSgaExpensesCurrencyId
      )?.currencyName;

      // 月別生産工場販管費を計算（monthlyExpenseDataから取得）
      const appliedLaborCostByMonthNew = convertMonthlyExpenseData(
        monthlyExpenseData,
        "productionSgaExpenses",
        productionSgaExpensesCurrency,
        localCurrency,
        currencyData,
        yearMonthListByFiscalYear,
        decimalCurrency,
        "convertProductionSgaExpenses",
        convertWithRate
      );

      const appliedLaborCostByYearMonth = convertToYearMonthObject(yearMonthListByElapsedYears, appliedLaborCostByMonthNew);
      const appliedLaborCostByYear = convertMonthlyToYearly(appliedLaborCostByMonthNew, decimalCurrency);
      setAppliedLaborCostByYear(appliedLaborCostByYear);

      // 月別生産工場販管費を計算（表示通貨ベース、表示用）
      const appliedLaborCostByMonthDisplay = convertMonthlyExpenseData(
        monthlyExpenseData,
        "productionSgaExpenses",
        productionSgaExpensesCurrency,
        displayCurrencyName,
        currencyData,
        yearMonthListByFiscalYear,
        displayCurrencyDecimal,
        "convertProductionSgaExpensesDisplay",
        convertWithRate
      );
      const appliedLaborCostByYearDisplay = convertMonthlyToYearly(appliedLaborCostByMonthDisplay, displayCurrencyDecimal);
      setDisplayAppliedLaborCostByYear(appliedLaborCostByYearDisplay);

      // #endregion 生産工場販管費

      // #region 国内工場販管費

      // 通貨IDから通貨名を取得
      const domesticSgaExpensesCurrency = currencyData?.find(
        c => c.currencyId === capexProject.domesticSgaExpensesCurrencyId
      )?.currencyName;

      // 月別国内工場販管費を計算（monthlyExpenseDataから取得）
      const domesticSgaCostByMonth = convertMonthlyExpenseData(
        monthlyExpenseData,
        "domesticSgaExpenses",
        domesticSgaExpensesCurrency,
        localCurrency,
        currencyData,
        yearMonthListByFiscalYear,
        decimalCurrency,
        "convertDomesticSgaExpenses",
        convertWithRate
      );

      const domesticSgaCostByYearMonth = convertToYearMonthObject(yearMonthListByElapsedYears, domesticSgaCostByMonth);
      const domesticSgaCostByYear = convertMonthlyToYearly(domesticSgaCostByMonth, decimalCurrency);
      setDomesticSgaCostByYear(domesticSgaCostByYear);

      // 月別国内工場販管費を計算（表示通貨ベース、表示用）
      const domesticSgaCostByMonthDisplay = convertMonthlyExpenseData(
        monthlyExpenseData,
        "domesticSgaExpenses",
        domesticSgaExpensesCurrency,
        displayCurrencyName,
        currencyData,
        yearMonthListByFiscalYear,
        displayCurrencyDecimal,
        "convertDomesticSgaExpensesDisplay",
        convertWithRate
      );
      const domesticSgaCostByYearDisplay = convertMonthlyToYearly(domesticSgaCostByMonthDisplay, displayCurrencyDecimal);
      setDisplayDomesticSgaCostByYear(domesticSgaCostByYearDisplay);

      // #endregion 国内工場販管費

      // #region 本社・販社販管費

      // 通貨IDから通貨名を取得
      const salesCompanySgaCurrency = currencyData?.find(
        c => c.currencyId === capexProject.salesCompanySgaCurrencyId
      )?.currencyName;

      // 月別本社・販社販管費を計算（monthlyExpenseDataから取得）
      const salesManagementCostByMonthNew = convertMonthlyExpenseData(
        monthlyExpenseData,
        "salesCompanySga",
        salesCompanySgaCurrency,
        localCurrency,
        currencyData,
        yearMonthListByFiscalYear,
        decimalCurrency,
        "convertSalesCompanySga",
        convertWithRate
      );

      const salesManagementCostByYearMonth = convertToYearMonthObject(yearMonthListByElapsedYears, salesManagementCostByMonthNew);
      const salesManagementCostByYear = convertMonthlyToYearly(salesManagementCostByMonthNew, decimalCurrency);
      setSalesManagementCostByYear(salesManagementCostByYear);

      // 月別本社・販社販管費を計算（表示通貨ベース、表示用）
      const salesManagementCostByMonthDisplay = convertMonthlyExpenseData(
        monthlyExpenseData,
        "salesCompanySga",
        salesCompanySgaCurrency,
        displayCurrencyName,
        currencyData,
        yearMonthListByFiscalYear,
        displayCurrencyDecimal,
        "convertSalesCompanySgaDisplay",
        convertWithRate
      );
      const salesManagementCostByYearDisplay = convertMonthlyToYearly(salesManagementCostByMonthDisplay, displayCurrencyDecimal);
      setDisplaySalesManagementCostByYear(salesManagementCostByYearDisplay);

      // #endregion 本社・販社販管費

      // #region 開発費

      let devCostByMonth: { year: number; month: number; value: Decimal }[] = [];
      let devCostByYear: Decimal[] = [];
      let devCostByYearDisplay: Decimal[] = [];
      let devCostByMonthDisplay: Decimal[] = [];
      if (capexProject.devCost) {

        // JSONパース（年月単位のデータ構造）
        const devCost: MonthValue[] = JSON.parse(capexProject.devCost);

        // 通貨IDから通貨名を取得
        const devCostCurrency = currencyData?.find(
          c => c.currencyId === capexProject.devCostCurrencyId
        )?.currencyName;

        // 月別開発費を計算（年月単位のデータを変換）
        const devCostByMonthNew = convertMonthlyValueData(
          devCost,
          devCostCurrency,
          localCurrency,
          currencyData,
          yearMonthListByFiscalYear,
          decimalCurrency,
          "convertDevCost",
          convertWithRate
        );

        devCostByMonth = convertToYearMonthObject(yearMonthListByElapsedYears, devCostByMonthNew);
        devCostByYear = convertMonthlyToYearly(devCostByMonthNew, decimalCurrency);
        setDevCostByYear(devCostByYear);

        // 月別開発費を計算（表示通貨ベース、表示用）
        const devCostByMonthDisplayNew = convertMonthlyValueData(
          devCost,
          devCostCurrency,
          displayCurrencyName,
          currencyData,
          yearMonthListByFiscalYear,
          displayCurrencyDecimal,
          "convertDevCostDisplay",
          convertWithRate
        );
        devCostByYearDisplay = convertMonthlyToYearly(devCostByMonthDisplayNew, displayCurrencyDecimal);
        setDisplayDevCostByYear(devCostByYearDisplay);
        devCostByMonthDisplay = devCostByMonthDisplayNew;
      }

      // #endregion 開発費

      // #region ロイヤリティ

      // 通貨IDから通貨名を取得
      const royaltyCurrency = currencyData?.find(
        c => c.currencyId === capexProject.royaltyCurrencyId
      )?.currencyName;

      // 月別ロイヤリティを計算（monthlyExpenseDataから取得）
      const royaltyByMonthNew = convertMonthlyExpenseData(
        monthlyExpenseData,
        "royalty",
        royaltyCurrency,
        localCurrency,
        currencyData,
        yearMonthListByFiscalYear,
        decimalCurrency,
        "convertRoyalty",
        convertWithRate
      );

      const royaltyByYearMonth = convertToYearMonthObject(yearMonthListByElapsedYears, royaltyByMonthNew);
      const royaltyByYear = convertMonthlyToYearly(royaltyByMonthNew, decimalCurrency);
      setRoyaltyByYear(royaltyByYear);

      // 月別ロイヤリティを計算（表示通貨ベース、表示用）
      const royaltyByMonthDisplay = convertMonthlyExpenseData(
        monthlyExpenseData,
        "royalty",
        royaltyCurrency,
        displayCurrencyName,
        currencyData,
        yearMonthListByFiscalYear,
        displayCurrencyDecimal,
        "convertRoyaltyDisplay",
        convertWithRate
      );
      const royaltyByYearDisplay = convertMonthlyToYearly(royaltyByMonthDisplay, displayCurrencyDecimal);
      setDisplayRoyaltyByYear(royaltyByYearDisplay);

      // #endregion ロイヤリティ

      // #region 販売費及び一般管理費

      // 計算方法: 生産工場販管費 + 国内工場販管費 + 本社・販社販管費 + 開発費 + ロイヤリティ

      const sGAExpensesByMonth = mergeYearMonthValues(
        appliedLaborCostByYearMonth,
        domesticSgaCostByYearMonth,
        salesManagementCostByYearMonth,
        devCostByMonth,
        royaltyByYearMonth,
      );
      const sGAExpensesByYear = sumDecimalArray(
        appliedLaborCostByYear,       // 生産工場販管費
        domesticSgaCostByYear,        // 国内工場販管費
        salesManagementCostByYear,    // 本社・販社販管費
        devCostByYear,                // 開発費
        royaltyByYear                 // ロイヤリティ
      );
      setSGAExpensesByYear(sGAExpensesByYear);

      // 販売費及び一般管理費（表示通貨ベース、表示用）
      const sGAExpensesByYearDisplay = sumDecimalArray(
        appliedLaborCostByYearDisplay,       // 生産工場販管費
        domesticSgaCostByYearDisplay,        // 国内工場販管費
        salesManagementCostByYearDisplay,    // 本社・販社販管費
        devCostByYearDisplay,                // 開発費
        royaltyByYearDisplay                 // ロイヤリティ
      );
      setDisplaySGAExpensesByYear(sGAExpensesByYearDisplay);

      // #endregion 販売費及び一般管理費

      // ------------------------------
      // 年度別損益
      // | ...
      // |-その他の経費
      // |-営業利益
      // |-営業利益率
      // └-実質的営業利益率
      // ------------------------------

      // #region その他の経費

      let otherCostByMonth: { year: number; month: number; value: Decimal }[] = [];
      let otherCostByYear: Decimal[] = [];
      let otherCostByYearDisplay: Decimal[] = [];
      let otherCostByMonthDisplay: Decimal[] = [];
      if (capexProject.otherCost) {

        // JSONパース（年月単位のデータ構造）
        const otherCost: MonthValue[] = JSON.parse(capexProject.otherCost);

        // 通貨IDから通貨名を取得
        const otherCostCurrency = currencyData?.find(
          c => c.currencyId === capexProject.otherCostCurrencyId
        )?.currencyName;

        // 月別その他の経費を計算（年月単位のデータを変換）
        const otherCostByMonthNew = convertMonthlyValueData(
          otherCost,
          otherCostCurrency,
          localCurrency,
          currencyData,
          yearMonthListByFiscalYear,
          decimalCurrency,
          "convertOtherCost",
          convertWithRate
        );

        otherCostByMonth = convertToYearMonthObject(yearMonthListByElapsedYears, otherCostByMonthNew);
        otherCostByYear = convertMonthlyToYearly(otherCostByMonthNew, decimalCurrency);
        setOtherCostByYear(otherCostByYear);

        // 月別その他の経費を計算（表示通貨ベース、表示用）
        const otherCostByMonthDisplayNew = convertMonthlyValueData(
          otherCost,
          otherCostCurrency,
          displayCurrencyName,
          currencyData,
          yearMonthListByFiscalYear,
          displayCurrencyDecimal,
          "convertOtherCostDisplay",
          convertWithRate
        );
        otherCostByYearDisplay = convertMonthlyToYearly(otherCostByMonthDisplayNew, displayCurrencyDecimal);
        setDisplayOtherCostByYear(otherCostByYearDisplay);
        otherCostByMonthDisplay = otherCostByMonthDisplayNew;
      }

      // #endregion その他の経費

      // #region 売上データ（JPY版）

      // 売上データを表示通貨に変換
      // 月次データ（表示通貨で変換済み）を年度合算する方式に統一（合理化前と同一方式）。
      // ローカル通貨で丸め済みの年度合計を表示通貨に変換する旧方式は、往復変換誤差の原因となるため廃止（No425）
      const totalSalesMoldDevByYearDisplay = convertMonthlyToYearly(totalSalesMoldDevByMonthDisplay.map(entry => entry.sales), displayCurrencyDecimal);
      setDisplayTotalSalesByYear(totalSalesMoldDevByYearDisplay);

      const productSalesByYearDisplay = convertMonthlyToYearly(productSalesByMonthDisplay.map(entry => entry.sales), displayCurrencyDecimal);
      setDisplayProductSalesByYear(productSalesByYearDisplay);

      // ステート変数ではなくローカル変数を使用（Reactのステート更新は非同期のため）
      // 月次データ（表示通貨で変換済み）を年度合算する方式に統一（合理化前と同一方式）。
      // ローカル通貨で丸め済みの年度合計を表示通貨に変換する旧方式は、往復変換誤差の原因となるため廃止（No425）
      const moldSalesByYearDisplay = calculateProjectSalesSum(projectMoldSalesByMonthDisplay, displayCurrencyDecimal);
      setDisplayMoldSalesByYear(moldSalesByYearDisplay);

      const devSalesByYearForDisplay = calculateProjectSalesSum(projectDevSalesByMonthDisplay, displayCurrencyDecimal);
      setDisplayDevSalesByYear(devSalesByYearForDisplay);

      const equipmentSalesByYearDisplay = convertMonthlyToYearly(projectEquipmentSalesByMonthDisplay.map(entry => entry.sales), displayCurrencyDecimal);
      setDisplayEquipmentSalesByYear(equipmentSalesByYearDisplay);

      // #endregion 売上データ（JPY版）

      // #region 営業利益

      // 計算方法: 売上合計 - 売上原価 - 販売費及び一般管理費 - その他の経費

      let profitByMonth = subtractYearMonthValues(
        totalSalesMoldDevByMonth.map(({ year, month, sales }) => ({ year, month, value: sales, })),
        salesCostByMonth,
        sGAExpensesByMonth,
        otherCostByMonth,
      );
      let profitByYear = subtractDecimalArray(totalSalesMoldDevByYear, salesCostByYear, sGAExpensesByYear, otherCostByYear);

      // 簡易モード時：合理化前後サマリーの費用効果から営業利益を算出（営業利益 = -費用効果合計、工場通貨に変換）
      // 詳細モードへの影響を避けるため簡易モード判定で分岐
      if (isSimpleApplicationForm(capexProjectData?.purpose, capexProjectData?.applicationFormType)
        && capexProjectData?.purpose === PurposeType.Rationalization.code
        && rationalizationSummaryData.length > 0) {
        // 月別の営業利益を計算（月次回収シミュレーション用）
        // 既存配列(depreciationByMonth等)が経過年フォーマット {year:1..N, month}なので合わせる
        // サマリーデータのルックアップにはカレンダー年フォーマットを使用
        profitByMonth = yearMonthListByElapsedYears.map(({ year: elapsedYear, month }, idx) => {
          const calendarEntry = yearMonthListByFiscalYear[idx];
          const monthKey = calendarEntry
            ? `${calendarEntry.year}-${String(calendarEntry.month).padStart(2, "0")}`
            : "";
          let monthTotal = new Decimal(0);
          rationalizationSummaryData.forEach((summary) => {
            if (!summary.costItems) return;
            const sourceCurrency = currencyData.find(c => c.currencyId === summary.costCurrencyId);
            const sourceCurrencyName = sourceCurrency?.currencyName || localCurrency;
            summary.costItems.forEach((item) => {
              const beforeMonth = new Decimal(item.beforeMonthlyData[monthKey] || 0);
              const afterMonth = new Decimal(item.afterMonthlyData[monthKey] || 0);
              const beforeConverted = sourceCurrencyName === localCurrency
                ? beforeMonth
                : convertWithRate(beforeMonth, sourceCurrencyName, localCurrency);
              const afterConverted = sourceCurrencyName === localCurrency
                ? afterMonth
                : convertWithRate(afterMonth, sourceCurrencyName, localCurrency);
              const diff = afterConverted.minus(beforeConverted);
              monthTotal = monthTotal.minus(diff);
            });
          });
          return { year: elapsedYear, month, value: monthTotal };
        });
        profitByYear = fiscalYearsString.map((fiscalYear) => {
          const yearMatch = fiscalYear.match(/(\d{4})年/);
          if (!yearMatch) return new Decimal(0);
          const endYear = parseInt(yearMatch[1], 10);
          let yearTotal = new Decimal(0);
          rationalizationSummaryData.forEach((summary) => {
            if (!summary.costItems) return;
            const sourceCurrency = currencyData.find(c => c.currencyId === summary.costCurrencyId);
            const sourceCurrencyName = sourceCurrency?.currencyName || localCurrency;
            summary.costItems.forEach((item) => {
              let beforeYearSum = new Decimal(0);
              let afterYearSum = new Decimal(0);
              for (let m = 4; m <= 12; m++) {
                const k = `${endYear - 1}-${String(m).padStart(2, "0")}`;
                beforeYearSum = beforeYearSum.plus(item.beforeMonthlyData[k] || 0);
                afterYearSum = afterYearSum.plus(item.afterMonthlyData[k] || 0);
              }
              for (let m = 1; m <= 3; m++) {
                const k = `${endYear}-${String(m).padStart(2, "0")}`;
                beforeYearSum = beforeYearSum.plus(item.beforeMonthlyData[k] || 0);
                afterYearSum = afterYearSum.plus(item.afterMonthlyData[k] || 0);
              }
              // 工程通貨から工場通貨に変換
              const beforeConverted = sourceCurrencyName === localCurrency
                ? beforeYearSum
                : convertWithRate(beforeYearSum, sourceCurrencyName, localCurrency);
              const afterConverted = sourceCurrencyName === localCurrency
                ? afterYearSum
                : convertWithRate(afterYearSum, sourceCurrencyName, localCurrency);
              const diff = afterConverted.minus(beforeConverted);
              // 営業利益 = -費用効果（コスト削減 = 利益増加）
              yearTotal = yearTotal.minus(diff);
            });
          });
          return yearTotal;
        });
      }

      // 簡易モード時：更新前後サマリーの費用効果から営業利益を算出（営業利益 = -費用効果合計、工場通貨に変換）
      if (isSimpleApplicationForm(capexProjectData?.purpose, capexProjectData?.applicationFormType)
        && capexProjectData?.purpose === PurposeType.UpdateAndOther.code
        && updateSummaryData.length > 0) {
        profitByMonth = yearMonthListByElapsedYears.map(({ year: elapsedYear, month }, idx) => {
          const calendarEntry = yearMonthListByFiscalYear[idx];
          const monthKey = calendarEntry
            ? `${calendarEntry.year}-${String(calendarEntry.month).padStart(2, "0")}`
            : "";
          let monthTotal = new Decimal(0);
          updateSummaryData.forEach((summary) => {
            if (!summary.costItems) return;
            const sourceCurrency = currencyData.find(c => c.currencyId === summary.costCurrencyId);
            const sourceCurrencyName = sourceCurrency?.currencyName || localCurrency;
            summary.costItems.forEach((item) => {
              const beforeMonth = new Decimal(item.beforeMonthlyData[monthKey] || 0);
              const afterMonth = new Decimal(item.afterMonthlyData[monthKey] || 0);
              const beforeConverted = sourceCurrencyName === localCurrency
                ? beforeMonth
                : convertWithRate(beforeMonth, sourceCurrencyName, localCurrency);
              const afterConverted = sourceCurrencyName === localCurrency
                ? afterMonth
                : convertWithRate(afterMonth, sourceCurrencyName, localCurrency);
              const diff = afterConverted.minus(beforeConverted);
              monthTotal = monthTotal.minus(diff);
            });
          });
          return { year: elapsedYear, month, value: monthTotal };
        });
        profitByYear = fiscalYearsString.map((fiscalYear) => {
          const yearMatch = fiscalYear.match(/(\d{4})年/);
          if (!yearMatch) return new Decimal(0);
          const endYear = parseInt(yearMatch[1], 10);
          let yearTotal = new Decimal(0);
          updateSummaryData.forEach((summary) => {
            if (!summary.costItems) return;
            const sourceCurrency = currencyData.find(c => c.currencyId === summary.costCurrencyId);
            const sourceCurrencyName = sourceCurrency?.currencyName || localCurrency;
            summary.costItems.forEach((item) => {
              let beforeYearSum = new Decimal(0);
              let afterYearSum = new Decimal(0);
              for (let m = 4; m <= 12; m++) {
                const k = `${endYear - 1}-${String(m).padStart(2, "0")}`;
                beforeYearSum = beforeYearSum.plus(item.beforeMonthlyData[k] || 0);
                afterYearSum = afterYearSum.plus(item.afterMonthlyData[k] || 0);
              }
              for (let m = 1; m <= 3; m++) {
                const k = `${endYear}-${String(m).padStart(2, "0")}`;
                beforeYearSum = beforeYearSum.plus(item.beforeMonthlyData[k] || 0);
                afterYearSum = afterYearSum.plus(item.afterMonthlyData[k] || 0);
              }
              const beforeConverted = sourceCurrencyName === localCurrency
                ? beforeYearSum
                : convertWithRate(beforeYearSum, sourceCurrencyName, localCurrency);
              const afterConverted = sourceCurrencyName === localCurrency
                ? afterYearSum
                : convertWithRate(afterYearSum, sourceCurrencyName, localCurrency);
              const diff = afterConverted.minus(beforeConverted);
              yearTotal = yearTotal.minus(diff);
            });
          });
          return yearTotal;
        });
      }

      setProfitByMonth(profitByMonth.map((entry) => entry.value));
      setProfitByYear(profitByYear);

      // 営業利益（表示通貨ベース、表示用）
      let profitByYearDisplay = subtractDecimalArray(
        totalSalesMoldDevByYearDisplay,
        salesCostByYearDisplay,
        sGAExpensesByYearDisplay,
        otherCostByYearDisplay
      );

      // 簡易モード時：合理化前後サマリーの費用効果から営業利益（表示通貨）を算出
      if (isSimpleApplicationForm(capexProjectData?.purpose, capexProjectData?.applicationFormType)
        && capexProjectData?.purpose === PurposeType.Rationalization.code
        && rationalizationSummaryData.length > 0) {
        const displayCurrencyName = selectedCurrency?.name || CurrencyNameJPY;
        profitByYearDisplay = fiscalYearsString.map((fiscalYear) => {
          const yearMatch = fiscalYear.match(/(\d{4})年/);
          if (!yearMatch) return new Decimal(0);
          const endYear = parseInt(yearMatch[1], 10);
          let yearTotal = new Decimal(0);
          rationalizationSummaryData.forEach((summary) => {
            if (!summary.costItems) return;
            const sourceCurrency = currencyData.find(c => c.currencyId === summary.costCurrencyId);
            const sourceCurrencyName = sourceCurrency?.currencyName || displayCurrencyName;
            summary.costItems.forEach((item) => {
              let beforeYearSum = new Decimal(0);
              let afterYearSum = new Decimal(0);
              for (let m = 4; m <= 12; m++) {
                const k = `${endYear - 1}-${String(m).padStart(2, "0")}`;
                beforeYearSum = beforeYearSum.plus(item.beforeMonthlyData[k] || 0);
                afterYearSum = afterYearSum.plus(item.afterMonthlyData[k] || 0);
              }
              for (let m = 1; m <= 3; m++) {
                const k = `${endYear}-${String(m).padStart(2, "0")}`;
                beforeYearSum = beforeYearSum.plus(item.beforeMonthlyData[k] || 0);
                afterYearSum = afterYearSum.plus(item.afterMonthlyData[k] || 0);
              }
              const beforeConverted = sourceCurrencyName === displayCurrencyName
                ? beforeYearSum
                : convertWithRate(beforeYearSum, sourceCurrencyName, displayCurrencyName);
              const afterConverted = sourceCurrencyName === displayCurrencyName
                ? afterYearSum
                : convertWithRate(afterYearSum, sourceCurrencyName, displayCurrencyName);
              const diff = afterConverted.minus(beforeConverted);
              yearTotal = yearTotal.minus(diff);
            });
          });
          return yearTotal;
        });
      }

      // 簡易モード時：更新前後サマリーの費用効果から営業利益（表示通貨）を算出
      if (isSimpleApplicationForm(capexProjectData?.purpose, capexProjectData?.applicationFormType)
        && capexProjectData?.purpose === PurposeType.UpdateAndOther.code
        && updateSummaryData.length > 0) {
        const displayCurrencyName = selectedCurrency?.name || CurrencyNameJPY;
        profitByYearDisplay = fiscalYearsString.map((fiscalYear) => {
          const yearMatch = fiscalYear.match(/(\d{4})年/);
          if (!yearMatch) return new Decimal(0);
          const endYear = parseInt(yearMatch[1], 10);
          let yearTotal = new Decimal(0);
          updateSummaryData.forEach((summary) => {
            if (!summary.costItems) return;
            const sourceCurrency = currencyData.find(c => c.currencyId === summary.costCurrencyId);
            const sourceCurrencyName = sourceCurrency?.currencyName || displayCurrencyName;
            summary.costItems.forEach((item) => {
              let beforeYearSum = new Decimal(0);
              let afterYearSum = new Decimal(0);
              for (let m = 4; m <= 12; m++) {
                const k = `${endYear - 1}-${String(m).padStart(2, "0")}`;
                beforeYearSum = beforeYearSum.plus(item.beforeMonthlyData[k] || 0);
                afterYearSum = afterYearSum.plus(item.afterMonthlyData[k] || 0);
              }
              for (let m = 1; m <= 3; m++) {
                const k = `${endYear}-${String(m).padStart(2, "0")}`;
                beforeYearSum = beforeYearSum.plus(item.beforeMonthlyData[k] || 0);
                afterYearSum = afterYearSum.plus(item.afterMonthlyData[k] || 0);
              }
              const beforeConverted = sourceCurrencyName === displayCurrencyName
                ? beforeYearSum
                : convertWithRate(beforeYearSum, sourceCurrencyName, displayCurrencyName);
              const afterConverted = sourceCurrencyName === displayCurrencyName
                ? afterYearSum
                : convertWithRate(afterYearSum, sourceCurrencyName, displayCurrencyName);
              const diff = afterConverted.minus(beforeConverted);
              yearTotal = yearTotal.minus(diff);
            });
          });
          return yearTotal;
        });
      }

      setDisplayProfitByYear(profitByYearDisplay);

      // #endregion 営業利益

      // #region 月次損益データ（Excel出力用）

      // 月次データを収集
      const monthlyLabels = calcLabelsByMonth(capexProject.projectStartDate, totalFiscalYearsByProject);

      // PJ単位金型回収（月次）- projectMoldSalesByMonthをyearMonthListByElapsedYearsの長さに合わせて配列化
      // ※ projectMoldSalesByMonthのyearは経過年数（1,2,3...）なのでyearMonthListByElapsedYearsを使用
      const moldSalesByMonthValues: Decimal[] = yearMonthListByElapsedYears.map(({ year, month }) => {
        const found = projectMoldSalesByMonth.find(e => e.year === year && e.month === month);
        return found?.sales || new Decimal(0);
      });

      // PJ単位開発費回収（月次）
      const devSalesByMonthValues: Decimal[] = yearMonthListByElapsedYears.map(({ year, month }) => {
        const found = projectDevSalesByMonth.find(e => e.year === year && e.month === month);
        return found?.sales || new Decimal(0);
      });

      // PJ単位設備費回収（月次）
      const equipmentSalesByMonthValues: Decimal[] = yearMonthListByElapsedYears.map(({ year, month }) => {
        const found = projectEquipmentSalesByMonth.find(e => e.year === year && e.month === month);
        return found?.sales || new Decimal(0);
      });

      // 月次損益データのセットは実質的営業利益率計算後に行う（customerSpecifiedPartsCostByMonthが必要なため）

      // #endregion 月次損益データ（データセットは後述）

      // #region 営業利益率

      // 計算方法: 売上合計 / 営業利益

      const profitRateByYear = calcProfitRateArray(totalSalesMoldDevByYear, profitByYear);
      setProfitRateByYear(profitRateByYear);

      // 営業利益率（表示通貨ベース、表示用）
      const profitRateByYearDisplay = calcProfitRateArray(totalSalesMoldDevByYearDisplay, profitByYearDisplay);
      setDisplayProfitRateByYear(profitRateByYearDisplay);

      // #endregion 営業利益率

      // #region 実質的営業利益率

      // 計算方法:
      // 客先指定部品は売上=材料費（利益ゼロ）として扱う
      // 実質的売上 = 売上合計 - 客先指定部品材料費
      // 実質的営業利益率 = 営業利益 / 実質的売上 × 100

      // 客先指定部品のseqリストをPartNo別に作成
      const customerSpecifiedSeqsByPartNo: Record<string, string[]> = {};
      for (const partNo of Object.keys(bomDataByPartNo)) {
        const bomRows = bomDataByPartNo[partNo];
        const customerSpecifiedSeqs = bomRows
          .filter(row => {
            const isCustomerSpecified: boolean = row.isCustomerSpecifiedParts
              ? JSON.parse(row.isCustomerSpecifiedParts)
              : false;
            return isCustomerSpecified;
          })
          .map(row => row.seq ?? "")
          .filter(seq => seq !== "");
        if (customerSpecifiedSeqs.length > 0) {
          customerSpecifiedSeqsByPartNo[partNo] = customerSpecifiedSeqs;
        }
      }

      // 客先指定部品が存在するかどうかをセット
      setHasCustomerSpecifiedParts(Object.keys(customerSpecifiedSeqsByPartNo).length > 0);

      // PartNo別のmaterialCostWithInhouseProfitデータを作成
      const materialCostByPartNo: { partNo: string, materialCostData: CBomDataWithMonthly[] }[] = capexPartNo
        .filter(part => part.partNo && part.materialCostWithInhouseProfit)
        .map(part => {
          try {
            const materialCostData: CBomDataWithMonthly[] = JSON.parse(part.materialCostWithInhouseProfit!);
            return { partNo: part.partNo!, materialCostData };
          } catch {
            return { partNo: part.partNo!, materialCostData: [] };
          }
        });

      // 客先指定部品材料費（月別）を計算
      // materialCostWithInhouseProfitは既にローカル通貨で保存されているため変換不要
      const customerSpecifiedPartsCostByMonth = calculateCustomerSpecifiedPartsCostByMonth(
        customerSpecifiedSeqsByPartNo,
        materialCostByPartNo,
        quantitiesByPartNoMonth,
        yearMonthListByFiscalYear
      );

      // 客先指定部品材料費（年度別）に集計
      const customerSpecifiedPartsCostByYearCalc = convertMonthlyToYearly(customerSpecifiedPartsCostByMonth, decimalCurrency);
      setCustomerSpecifiedPartsCostByYear(customerSpecifiedPartsCostByYearCalc);

      // 実質的売上（年度別）= 売上合計 - 客先指定部品材料費
      const realSalesByYear = totalSalesMoldDevByYear.map((sales, index) =>
        sales.minus(customerSpecifiedPartsCostByYearCalc[index] || new Decimal(0))
      );

      // 実質的営業利益率（年度別）= 営業利益 / 実質的売上 × 100
      const realOperatingProfitRateByYear = realSalesByYear.map((realSales, index) => {
        if (realSales.isZero()) {
          return new Decimal(0);
        }
        return (profitByYear[index] || new Decimal(0)).div(realSales).times(100);
      });
      setRealOperatingProfitRateByYear(realOperatingProfitRateByYear);

      // 実質的営業利益率（表示通貨ベース、表示用）
      // 客先指定部品材料費を表示通貨に変換
      const customerSpecifiedPartsCostByYearDisplay = reviewDate
        ? convertMonthlyToYearly(
          customerSpecifiedPartsCostByMonth.map((value, idx) => {
            const ym = yearMonthListByFiscalYear[idx]
              ? `${yearMonthListByFiscalYear[idx].year}-${String(yearMonthListByFiscalYear[idx].month).padStart(2, "0")}`
              : undefined;
            return new Decimal(convertWithRate(value, localCurrency, displayCurrencyName, ym) || 0);
          }),
          displayCurrencyDecimal
        )
        : customerSpecifiedPartsCostByYearCalc.map(value =>
          new Decimal(convertWithRate(value, localCurrency, displayCurrencyName) || 0)
        );
      // 実質的売上（表示通貨）= 売上合計 - 客先指定部品材料費
      const realSalesByYearDisplay = totalSalesMoldDevByYearDisplay.map((sales, index) =>
        sales.minus(customerSpecifiedPartsCostByYearDisplay[index] || new Decimal(0))
      );
      // 実質的営業利益率（表示通貨）= 営業利益 / 実質的売上 × 100
      const realOperatingProfitRateByYearDisplay = realSalesByYearDisplay.map((realSales, index) => {
        if (realSales.isZero()) {
          return new Decimal(0);
        }
        return (profitByYearDisplay[index] || new Decimal(0)).div(realSales).times(100);
      });
      setDisplayRealOperatingProfitRateByYear(realOperatingProfitRateByYearDisplay);

      // #endregion 実質的営業利益率

      // #region 月次データ（JPY版）

      // 売上合計（月次）を表示通貨に変換
      const totalSalesByMonthDisplay = totalSalesMoldDevByMonth.map(entry =>
        new Decimal(convertWithRate(entry.sales, localCurrency, displayCurrencyName, toYearMonth(entry.year, entry.month)) || 0)
      );
      setDisplayTotalSalesByMonth(totalSalesByMonthDisplay);

      // 営業利益（月次・表示通貨）は下の setDisplayMonthlyProfitAndLossData 直前で算出
      // 理由: 振り返りモード時は個別変換済みコスト合計から直接算出するため
      // （ローカル通貨経由で月別レート一括変換すると、設備償却が acceptanceMonth 固定と
      //  月別レートで2段変換され、個別表示と不整合になる問題を回避）

      // #endregion 月次データ（JPY版）

      // 月次損益データをセット（customerSpecifiedPartsCostByMonth計算後）
      setMonthlyProfitAndLossData({
        labels: monthlyLabels,
        totalSales: totalSalesMoldDevByMonth.map(entry => entry.sales),
        productSales: productSalesByMonth.map(entry => entry.sales),
        moldSales: moldSalesByMonthValues,
        devSales: devSalesByMonthValues,
        equipmentSales: equipmentSalesByMonthValues,
        salesCost: salesCostByMonth.map(entry => entry.value),
        materialCost: totalMaterialCostByMonth.map(entry => entry.materialCost),
        outsideCost: outsideCostByMonth,
        laborCost: laborCostByMonth,
        domesticLaborCost: domesticLaborCostByMonth,
        factoryExpenses: factoryExpensesByMonth.map(entry => entry.value),
        overHeadCost: overHeadCostByYearMonth.map(entry => entry.value),
        depreciation: depreciationByMonth.map(entry => entry.value),
        equipmentDepreciation: equipmentDepreciationByMonth.map(entry => entry.value),
        factoryCost: factoryCostByMonth.map(entry => entry.value),
        pastEquipmentCost: pastEquipmentCostByMonth.map(entry => entry.value),
        otherEquipmentCost: otherEquipmentCostByMonth.map(entry => entry.value),
        domesticFactoryExpenses: domesticFactoryExpensesByYearMonth.map(entry => entry.value),
        domesticOverHeadCost: domesticOverHeadCostByYearMonth.map(entry => entry.value),
        domesticDepreciation: domesticDepreciationByMonth.map(entry => entry.value),
        domesticInvestmentDepreciation: domesticInvestmentDepreciationByYearMonth.map(entry => entry.value),
        domesticFactoryBuildingDepreciation: domesticFactoryBuildingDepreciationByYearMonth.map(entry => entry.value),
        domesticOtherDepreciation: domesticOtherDepreciationByYearMonth.map(entry => entry.value),
        scrapCost: scrapCostByMonth,
        sGAExpenses: sGAExpensesByMonth.map(entry => entry.value),
        appliedLaborCost: appliedLaborCostByMonthNew,
        domesticSgaCost: domesticSgaCostByMonth,
        salesManagementCost: salesManagementCostByMonthNew,
        devCost: devCostByMonth.map(entry => entry.value),
        royalty: royaltyByMonthNew,
        otherCost: otherCostByMonth.map(entry => entry.value),
        profit: profitByMonth.map(entry => entry.value),
        customerSpecifiedPartsCost: customerSpecifiedPartsCostByMonth,
      });

      // 月次損益データ（JPY版）をセット - Excel出力での二重変換回避用
      // PJ単位金型回収（月次・JPY版）
      const moldSalesByMonthValuesDisplay: Decimal[] = yearMonthListByElapsedYears.map(({ year, month }) => {
        const found = projectMoldSalesByMonthDisplay.find(e => e.year === year && e.month === month);
        return found?.sales || new Decimal(0);
      });
      // PJ単位開発費回収（月次・JPY版）
      const devSalesByMonthValuesDisplay: Decimal[] = yearMonthListByElapsedYears.map(({ year, month }) => {
        const found = projectDevSalesByMonthDisplay.find(e => e.year === year && e.month === month);
        return found?.sales || new Decimal(0);
      });
      // PJ単位設備費回収（月次・JPY版）
      const equipmentSalesByMonthValuesDisplay: Decimal[] = yearMonthListByElapsedYears.map(({ year, month }) => {
        const found = projectEquipmentSalesByMonthDisplay.find(e => e.year === year && e.month === month);
        return found?.sales || new Decimal(0);
      });
      // 売上原価（月次・JPY版）
      const outsideCostByYearMonthDisplay = convertToYearMonthObject(yearMonthListByElapsedYears, outsideCostByMonthDisplay);
      const laborCostByYearMonthDisplay = convertToYearMonthObject(yearMonthListByElapsedYears, laborCostByMonthDisplay);
      const domesticLaborCostByYearMonthDisplay = convertToYearMonthObject(yearMonthListByElapsedYears, domesticLaborCostByMonthDisplay);
      const scrapCostByYearMonthDisplay = convertToYearMonthObject(yearMonthListByElapsedYears, scrapCostByMonthDisplay);
      const salesCostByMonthDisplayCalc = mergeYearMonthValues(
        totalMaterialCostByMonthDisplay.map(({ year, month, materialCost }) => ({ year, month, value: materialCost })),
        outsideCostByYearMonthDisplay,
        laborCostByYearMonthDisplay,
        domesticLaborCostByYearMonthDisplay,
        factoryExpensesByMonthDisplay,
        domesticFactoryExpensesByYearMonthDisplay,
        scrapCostByYearMonthDisplay,
      );
      // 販売費及び一般管理費（月次・JPY版）
      const appliedLaborCostByYearMonthDisplay = convertToYearMonthObject(yearMonthListByElapsedYears, appliedLaborCostByMonthDisplay);
      const domesticSgaCostByYearMonthDisplay = convertToYearMonthObject(yearMonthListByElapsedYears, domesticSgaCostByMonthDisplay);
      const salesManagementCostByYearMonthDisplay = convertToYearMonthObject(yearMonthListByElapsedYears, salesManagementCostByMonthDisplay);
      const devCostByYearMonthDisplay = convertToYearMonthObject(yearMonthListByElapsedYears, devCostByMonthDisplay);
      const royaltyByYearMonthDisplay = convertToYearMonthObject(yearMonthListByElapsedYears, royaltyByMonthDisplay);
      const sGAExpensesByMonthDisplayCalc = mergeYearMonthValues(
        appliedLaborCostByYearMonthDisplay,
        domesticSgaCostByYearMonthDisplay,
        salesManagementCostByYearMonthDisplay,
        devCostByYearMonthDisplay,
        royaltyByYearMonthDisplay,
      );
      // その他の経費（月次・JPY版）
      const otherCostByYearMonthDisplay = convertToYearMonthObject(yearMonthListByElapsedYears, otherCostByMonthDisplay);
      // 客先指定部品材料費（月次・JPY版）
      const customerSpecifiedPartsCostByMonthDisplay = customerSpecifiedPartsCostByMonth.map((value, idx) => {
        const ym = yearMonthListByFiscalYear[idx]
          ? `${yearMonthListByFiscalYear[idx].year}-${String(yearMonthListByFiscalYear[idx].month).padStart(2, "0")}`
          : undefined;
        return new Decimal(convertWithRate(value, localCurrency, displayCurrencyName, ym) || 0);
      });

      // 営業利益（月次・表示通貨）
      // 詳細モード 振り返り: 個別変換済みコスト合計から直接算出（設備償却の2段変換を回避）
      //   売上(display) - 売上原価(display) - 販管費(display) - その他経費(display)
      // 簡易モード / 通常モード: ローカル通貨利益を月別レートで一括変換（既存動作）
      // ※簡易モードでは売上データが入力されないため、振り返りでも profitByMonth ベースで変換する
      const profitByMonthDisplay: Decimal[] = (reviewDate && !isSimpleMode)
        ? totalSalesMoldDevByMonthDisplay.map((salesEntry, idx) => {
          const cost = salesCostByMonthDisplayCalc[idx]?.value || new Decimal(0);
          const sga = sGAExpensesByMonthDisplayCalc[idx]?.value || new Decimal(0);
          const other = otherCostByYearMonthDisplay[idx]?.value || new Decimal(0);
          return salesEntry.sales.minus(cost).minus(sga).minus(other);
        })
        : profitByMonth.map(entry =>
          new Decimal(convertWithRate(entry.value, localCurrency, displayCurrencyName, toYearMonth(entry.year, entry.month)) || 0)
        );
      setDisplayProfitByMonth(profitByMonthDisplay);

      setDisplayMonthlyProfitAndLossData({
        labels: monthlyLabels,
        totalSales: totalSalesMoldDevByMonthDisplay.map(entry => entry.sales),
        productSales: productSalesByMonthDisplay.map(entry => entry.sales),
        moldSales: moldSalesByMonthValuesDisplay,
        devSales: devSalesByMonthValuesDisplay,
        equipmentSales: equipmentSalesByMonthValuesDisplay,
        salesCost: salesCostByMonthDisplayCalc.map(entry => entry.value),
        materialCost: totalMaterialCostByMonthDisplay.map(entry => entry.materialCost),
        outsideCost: outsideCostByMonthDisplay,
        laborCost: laborCostByMonthDisplay,
        domesticLaborCost: domesticLaborCostByMonthDisplay,
        factoryExpenses: factoryExpensesByMonthDisplay.map(entry => entry.value),
        overHeadCost: overHeadCostByYearMonthDisplay.map(entry => entry.value),
        depreciation: depreciationByMonthDisplay.map(entry => entry.value),
        equipmentDepreciation: equipmentDepreciationByMonthDisplay.map(entry => entry.value),
        factoryCost: factoryCostByMonthDisplay.map(entry => entry.value),
        pastEquipmentCost: pastEquipmentCostByMonthDisplay.map(entry => entry.value),
        otherEquipmentCost: otherEquipmentCostByMonthDisplay.map(entry => entry.value),
        domesticFactoryExpenses: domesticFactoryExpensesByYearMonthDisplay.map(entry => entry.value),
        domesticOverHeadCost: domesticOverHeadCostByYearMonthDisplay.map(entry => entry.value),
        domesticDepreciation: domesticDepreciationByMonthDisplay.map(entry => entry.value),
        domesticInvestmentDepreciation: domesticInvestmentDepreciationByYearMonthDisplay.map(entry => entry.value),
        domesticFactoryBuildingDepreciation: domesticFactoryBuildingDepreciationByYearMonthDisplay.map(entry => entry.value),
        domesticOtherDepreciation: domesticOtherDepreciationByYearMonthDisplay.map(entry => entry.value),
        scrapCost: scrapCostByMonthDisplay,
        sGAExpenses: sGAExpensesByMonthDisplayCalc.map(entry => entry.value),
        appliedLaborCost: appliedLaborCostByMonthDisplay,
        domesticSgaCost: domesticSgaCostByMonthDisplay,
        salesManagementCost: salesManagementCostByMonthDisplay,
        devCost: devCostByMonthDisplay,
        royalty: royaltyByMonthDisplay,
        otherCost: otherCostByMonthDisplay,
        profit: profitByMonthDisplay,
        customerSpecifiedPartsCost: customerSpecifiedPartsCostByMonthDisplay,
      });

      // #endregion

      // #region 【計算ロジック】年度別損益（更新前）

      // 更新投資の場合のみ「更新前」データを計算
      if (capexProjectData?.purpose === PurposeType.UpdateAndOther.code && capexProjectData?.updateAndOtherStartDate) {
        const updateStartDate = dayjs(capexProjectData.updateAndOtherStartDate);
        const projectStartDateObj = dayjs(startDate);
        const projectEndDateObj = capexProjectData.projectEndDate ? dayjs(capexProjectData.projectEndDate) : null;

        // 量産開始日のインデックスを計算（プロジェクト開始日からの月数）
        const updateStartMonthIndex = updateStartDate.startOf('month').diff(projectStartDateObj.startOf('month'), 'month');

        // EOL予定日のインデックスを計算（プロジェクト開始日からの月数）
        // EOL予定日の月までを含める（+1）
        const eolMonthIndex = projectEndDateObj
          ? projectEndDateObj.diff(projectStartDateObj, 'month') + 1
          : Infinity;

        // 量産開始直前3ヶ月のインデックスを計算
        const avgStartIndex = Math.max(0, updateStartMonthIndex - 3);
        const avgEndIndex = updateStartMonthIndex; // 量産開始月は含まない

        // 3ヶ月平均を計算するヘルパー関数
        const calcThreeMonthAverage = (
          monthlyData: { year: number, month: number, sales?: Decimal, value?: Decimal, materialCost?: Decimal, outsideCost?: Decimal, overheadCost?: Decimal, laborCost?: Decimal, salesManagementCost?: Decimal }[],
          fieldName: 'sales' | 'value' | 'materialCost' | 'outsideCost' | 'overheadCost' | 'laborCost' | 'salesManagementCost'
        ): Decimal => {
          let sum = new Decimal(0);
          let count = 0;
          for (let i = avgStartIndex; i < avgEndIndex && i < monthlyData.length; i++) {
            const val = monthlyData[i]?.[fieldName];
            if (val) {
              sum = sum.plus(val);
              count++;
            }
          }
          return count > 0 ? sum.div(count) : new Decimal(0);
        };

        // 「更新前」の月次データを作成（量産開始前は入力値、開始後～EOL予定日は3ヶ月平均、EOL予定日以降は0）
        const createBeforeUpdateMonthlyData = <T extends { year: number, month: number }>(
          monthlyData: T[],
          fieldName: keyof T,
          avgValue: Decimal
        ): T[] => {
          return monthlyData.map((entry, index) => {
            if (index >= eolMonthIndex) {
              // EOL予定日以降は0
              return { ...entry, [fieldName]: new Decimal(0) };
            } else if (index < updateStartMonthIndex) {
              // 量産開始前は入力値をそのまま使用
              return { ...entry };
            } else {
              // 量産開始後～EOL予定日は3ヶ月平均を使用
              return { ...entry, [fieldName]: avgValue };
            }
          });
        };

        // 各項目の3ヶ月平均を計算
        const avgTotalSales = calcThreeMonthAverage(totalSalesMoldDevByMonth, 'sales');
        const avgProductSales = calcThreeMonthAverage(
          productSalesByMonth.map(({ year, month }) => {
            const idx = totalSalesMoldDevByMonth.findIndex(e => e.year === year && e.month === month);
            return { year, month, sales: productSalesByMonth.find((_, i) => i === idx) ? productSalesByYear[Math.floor(idx / 12)] || new Decimal(0) : new Decimal(0) };
          }), 'sales'
        );
        const avgMaterialCost = calcThreeMonthAverage(totalMaterialCostByMonth, 'materialCost');
        const avgOutsideCost = outsideCostByMonth.length > 0
          ? outsideCostByMonth.slice(avgStartIndex, avgEndIndex).reduce((sum, val) => sum.plus(val), new Decimal(0)).div(Math.max(1, avgEndIndex - avgStartIndex))
          : new Decimal(0);
        const avgLaborCost = laborCostByMonth.length > 0
          ? laborCostByMonth.slice(avgStartIndex, avgEndIndex).reduce((sum, val) => sum.plus(val), new Decimal(0)).div(Math.max(1, avgEndIndex - avgStartIndex))
          : new Decimal(0);
        const avgDomesticLaborCost = domesticLaborCostByMonth.length > 0
          ? domesticLaborCostByMonth.slice(avgStartIndex, avgEndIndex).reduce((sum, val) => sum.plus(val), new Decimal(0)).div(Math.max(1, avgEndIndex - avgStartIndex))
          : new Decimal(0);
        const avgDomesticFactoryExpenses = domesticFactoryExpensesByYear.length > 0
          ? domesticFactoryExpensesByYearMonth.slice(avgStartIndex, avgEndIndex).reduce((sum, val) => sum.plus(val.value || new Decimal(0)), new Decimal(0)).div(Math.max(1, avgEndIndex - avgStartIndex))
          : new Decimal(0);
        const avgDomesticOverHeadCost = calcThreeMonthAverage(domesticOverHeadCostByYearMonth, 'value');
        const avgDomesticDepreciation = calcThreeMonthAverage(domesticDepreciationByMonth, 'value');
        const avgDomesticInvestmentDepreciation = calcThreeMonthAverage(domesticInvestmentDepreciationByYearMonth, 'value');
        const avgDomesticFactoryBuildingDepreciation = calcThreeMonthAverage(domesticFactoryBuildingDepreciationByYearMonth, 'value');
        const avgDomesticOtherDepreciation = calcThreeMonthAverage(domesticOtherDepreciationByYearMonth, 'value');
        const avgOverheadCost = calcThreeMonthAverage(overHeadCostByYearMonth, 'value');
        const avgEquipmentDepreciation = calcThreeMonthAverage(equipmentDepreciationByMonth, 'value');
        const avgFactoryCost = calcThreeMonthAverage(factoryCostByMonth, 'value');
        const avgPastEquipmentCost = calcThreeMonthAverage(pastEquipmentCostByMonth, 'value');
        const avgOtherEquipmentCost = calcThreeMonthAverage(otherEquipmentCostByMonth, 'value');
        const avgAppliedLaborCost = calcThreeMonthAverage(appliedLaborCostByYearMonth, 'value');
        const avgSalesManagementCost = calcThreeMonthAverage(salesManagementCostByYearMonth, 'value');
        const avgScrapCost = scrapCostByMonth.length > 0
          ? scrapCostByMonth.slice(avgStartIndex, avgEndIndex).reduce((sum, val) => sum.plus(val), new Decimal(0)).div(Math.max(1, avgEndIndex - avgStartIndex))
          : new Decimal(0);
        const avgDomesticSgaCost = domesticSgaCostByMonth.length > 0
          ? domesticSgaCostByMonth.slice(avgStartIndex, avgEndIndex).reduce((sum, val) => sum.plus(val), new Decimal(0)).div(Math.max(1, avgEndIndex - avgStartIndex))
          : new Decimal(0);
        const avgDevCost = calcThreeMonthAverage(devCostByMonth, 'value');
        const avgRoyalty = royaltyByMonthNew.length > 0
          ? royaltyByMonthNew.slice(avgStartIndex, avgEndIndex).reduce((sum, val) => sum.plus(val), new Decimal(0)).div(Math.max(1, avgEndIndex - avgStartIndex))
          : new Decimal(0);
        const avgOtherCost = calcThreeMonthAverage(otherCostByMonth, 'value');

        // 「更新前」の月次データを作成
        const beforeUpdateTotalSalesByMonth = createBeforeUpdateMonthlyData(totalSalesMoldDevByMonth, 'sales', avgTotalSales);
        const beforeUpdateMaterialCostByMonth = createBeforeUpdateMonthlyData(totalMaterialCostByMonth, 'materialCost', avgMaterialCost);
        const beforeUpdateOutsideCostByMonth = outsideCostByMonth.map((val, index) => {
          if (index >= eolMonthIndex) {
            // EOL予定日以降は0
            return new Decimal(0);
          } else if (index < updateStartMonthIndex) {
            // 量産開始前は入力値をそのまま使用
            return val;
          } else {
            // 量産開始後～EOL予定日は3ヶ月平均を使用
            return avgOutsideCost;
          }
        });
        const beforeUpdateLaborCostByMonth = laborCostByMonth.map((val, index) => {
          if (index >= eolMonthIndex) {
            // EOL予定日以降は0
            return new Decimal(0);
          } else if (index < updateStartMonthIndex) {
            // 量産開始前は入力値をそのまま使用
            return val;
          } else {
            // 量産開始後～EOL予定日は3ヶ月平均を使用
            return avgLaborCost;
          }
        });
        const beforeUpdateDomesticLaborCostByMonth = domesticLaborCostByMonth.map((val, index) => {
          if (index >= eolMonthIndex) {
            // EOL予定日以降は0
            return new Decimal(0);
          } else if (index < updateStartMonthIndex) {
            // 量産開始前は入力値をそのまま使用
            return val;
          } else {
            // 量産開始後～EOL予定日は3ヶ月平均を使用
            return avgDomesticLaborCost;
          }
        });
        const beforeUpdateDomesticFactoryExpensesByMonth = domesticFactoryExpensesByYearMonth.map((entry, index) => {
          if (index >= eolMonthIndex) {
            // EOL予定日以降は0
            return new Decimal(0);
          } else if (index < updateStartMonthIndex) {
            // 量産開始前は入力値をそのまま使用
            return entry.value || new Decimal(0);
          } else {
            // 量産開始後～EOL予定日は3ヶ月平均を使用
            return avgDomesticFactoryExpenses;
          }
        });
        // 国内工場経費内訳（5項目）の月次データ
        const beforeUpdateDomesticOverHeadCostByMonth = createBeforeUpdateMonthlyData(domesticOverHeadCostByYearMonth, 'value', avgDomesticOverHeadCost);
        const beforeUpdateDomesticDepreciationByMonth = createBeforeUpdateMonthlyData(domesticDepreciationByMonth, 'value', avgDomesticDepreciation);
        const beforeUpdateDomesticInvestmentDepreciationByMonth = createBeforeUpdateMonthlyData(domesticInvestmentDepreciationByYearMonth, 'value', avgDomesticInvestmentDepreciation);
        const beforeUpdateDomesticFactoryBuildingDepreciationByMonth = createBeforeUpdateMonthlyData(domesticFactoryBuildingDepreciationByYearMonth, 'value', avgDomesticFactoryBuildingDepreciation);
        const beforeUpdateDomesticOtherDepreciationByMonth = createBeforeUpdateMonthlyData(domesticOtherDepreciationByYearMonth, 'value', avgDomesticOtherDepreciation);
        const beforeUpdateOverheadCostByMonth = createBeforeUpdateMonthlyData(overHeadCostByYearMonth, 'value', avgOverheadCost);
        const beforeUpdateEquipmentDepreciationByMonth = createBeforeUpdateMonthlyData(equipmentDepreciationByMonth, 'value', avgEquipmentDepreciation);
        const beforeUpdateFactoryCostByMonth = createBeforeUpdateMonthlyData(factoryCostByMonth, 'value', avgFactoryCost);
        const beforeUpdatePastEquipmentCostByMonth = createBeforeUpdateMonthlyData(pastEquipmentCostByMonth, 'value', avgPastEquipmentCost);
        const beforeUpdateOtherEquipmentCostByMonth = createBeforeUpdateMonthlyData(otherEquipmentCostByMonth, 'value', avgOtherEquipmentCost);
        const beforeUpdateAppliedLaborCostByMonth = createBeforeUpdateMonthlyData(appliedLaborCostByYearMonth, 'value', avgAppliedLaborCost);
        const beforeUpdateSalesManagementCostByMonth = createBeforeUpdateMonthlyData(salesManagementCostByYearMonth, 'value', avgSalesManagementCost);
        const beforeUpdateScrapCostByMonth = scrapCostByMonth.map((val, index) => {
          if (index >= eolMonthIndex) {
            return new Decimal(0);
          } else if (index < updateStartMonthIndex) {
            return val;
          } else {
            return avgScrapCost;
          }
        });
        const beforeUpdateDomesticSgaCostByMonth = domesticSgaCostByMonth.map((val, index) => {
          if (index >= eolMonthIndex) {
            return new Decimal(0);
          } else if (index < updateStartMonthIndex) {
            return val;
          } else {
            return avgDomesticSgaCost;
          }
        });
        const beforeUpdateDevCostByMonth = createBeforeUpdateMonthlyData(devCostByMonth, 'value', avgDevCost);
        const beforeUpdateRoyaltyByMonth = royaltyByMonthNew.map((val, index) => {
          if (index >= eolMonthIndex) {
            return new Decimal(0);
          } else if (index < updateStartMonthIndex) {
            return val;
          } else {
            return avgRoyalty;
          }
        });
        const beforeUpdateOtherCostByMonth = createBeforeUpdateMonthlyData(otherCostByMonth, 'value', avgOtherCost);

        // 月次データを年度別に集計するヘルパー関数（convertMonthlyToYearlyと同じ丸め処理を適用）
        const aggregateByYear = (
          monthlyData: { year: number, month: number }[],
          getValue: (entry: any) => Decimal
        ): Decimal[] => {
          const yearlyTotals: { [fiscalYear: number]: Decimal } = {};
          monthlyData.forEach((entry, index) => {
            const currentDate = projectStartDateObj.add(index, 'month');
            const year = currentDate.year();
            const month = currentDate.month() + 1;
            const fiscalYear = month >= 4 ? year : year - 1;
            if (!yearlyTotals[fiscalYear]) {
              yearlyTotals[fiscalYear] = new Decimal(0);
            }
            yearlyTotals[fiscalYear] = yearlyTotals[fiscalYear].plus(getValue(entry));
          });
          // convertMonthlyToYearlyと同じ丸め処理を適用
          return Object.values(yearlyTotals).map(total => new Decimal(total.toFixed(decimalCurrency ? 3 : 0)));
        };

        // 「更新前」の年度別データを計算
        const beforeUpdateTotalSalesByYearCalc = aggregateByYear(beforeUpdateTotalSalesByMonth, (e) => e.sales || new Decimal(0));
        const beforeUpdateMaterialCostByYearCalc = aggregateByYear(beforeUpdateMaterialCostByMonth, (e) => e.materialCost || new Decimal(0));
        const beforeUpdateOutsideCostByYearCalc = convertMonthlyToYearly(beforeUpdateOutsideCostByMonth, decimalCurrency);
        const beforeUpdateLaborCostByYearCalc = convertMonthlyToYearly(beforeUpdateLaborCostByMonth, decimalCurrency);
        const beforeUpdateDomesticLaborCostByYearCalc = convertMonthlyToYearly(beforeUpdateDomesticLaborCostByMonth, decimalCurrency);
        const beforeUpdateDomesticFactoryExpensesByYearCalc = convertMonthlyToYearly(beforeUpdateDomesticFactoryExpensesByMonth, decimalCurrency);
        // 国内工場経費内訳（5項目）の年度別データ
        const beforeUpdateDomesticOverHeadCostByYearCalc = aggregateByYear(beforeUpdateDomesticOverHeadCostByMonth, (e) => e.value || new Decimal(0));
        const beforeUpdateDomesticDepreciationByYearCalc = aggregateByYear(beforeUpdateDomesticDepreciationByMonth, (e) => e.value || new Decimal(0));
        const beforeUpdateDomesticInvestmentDepreciationByYearCalc = aggregateByYear(beforeUpdateDomesticInvestmentDepreciationByMonth, (e) => e.value || new Decimal(0));
        const beforeUpdateDomesticFactoryBuildingDepreciationByYearCalc = aggregateByYear(beforeUpdateDomesticFactoryBuildingDepreciationByMonth, (e) => e.value || new Decimal(0));
        const beforeUpdateDomesticOtherDepreciationByYearCalc = aggregateByYear(beforeUpdateDomesticOtherDepreciationByMonth, (e) => e.value || new Decimal(0));
        const beforeUpdateOverheadCostByYearCalc = aggregateByYear(beforeUpdateOverheadCostByMonth, (e) => e.value || new Decimal(0));
        const beforeUpdateEquipmentDepreciationByYearCalc = aggregateByYear(beforeUpdateEquipmentDepreciationByMonth, (e) => e.value || new Decimal(0));
        const beforeUpdateFactoryCostByYearCalc = aggregateByYear(beforeUpdateFactoryCostByMonth, (e) => e.value || new Decimal(0));
        const beforeUpdatePastEquipmentCostByYearCalc = aggregateByYear(beforeUpdatePastEquipmentCostByMonth, (e) => e.value || new Decimal(0));
        const beforeUpdateOtherEquipmentCostByYearCalc = aggregateByYear(beforeUpdateOtherEquipmentCostByMonth, (e) => e.value || new Decimal(0));
        const beforeUpdateAppliedLaborCostByYearCalc = aggregateByYear(beforeUpdateAppliedLaborCostByMonth, (e) => e.value || new Decimal(0));
        const beforeUpdateSalesManagementCostByYearCalc = aggregateByYear(beforeUpdateSalesManagementCostByMonth, (e) => e.value || new Decimal(0));

        // 減価償却費合計 = 投資対象設備 + 工場建物 + 過去PJの投資対象設備 + その他の設備
        const beforeUpdateDepreciationByYearCalc = beforeUpdateEquipmentDepreciationByYearCalc.map((val, i) =>
          val.plus(beforeUpdateFactoryCostByYearCalc[i] || new Decimal(0)).plus(beforeUpdatePastEquipmentCostByYearCalc[i] || new Decimal(0)).plus(beforeUpdateOtherEquipmentCostByYearCalc[i] || new Decimal(0))
        );

        // 工場経費 = 償却費以外の工場経費 + 減価償却費
        const beforeUpdateFactoryExpensesByYearCalc = beforeUpdateOverheadCostByYearCalc.map((val, i) =>
          val.plus(beforeUpdateDepreciationByYearCalc[i] || new Decimal(0))
        );

        // 仕損費（3ヶ月平均を適用）
        const beforeUpdateScrapCostByYearCalc = convertMonthlyToYearly(beforeUpdateScrapCostByMonth, decimalCurrency);

        // 売上原価 = 材料費 + 外注加工費 + 生産工場労務費 + 国内工場労務費 + 生産工場経費 + 国内工場経費 + 仕損費
        const beforeUpdateSalesCostByYearCalc = beforeUpdateMaterialCostByYearCalc.map((val, i) =>
          val
            .plus(beforeUpdateOutsideCostByYearCalc[i] || new Decimal(0))
            .plus(beforeUpdateLaborCostByYearCalc[i] || new Decimal(0))
            .plus(beforeUpdateDomesticLaborCostByYearCalc[i] || new Decimal(0))
            .plus(beforeUpdateFactoryExpensesByYearCalc[i] || new Decimal(0))
            .plus(beforeUpdateDomesticFactoryExpensesByYearCalc[i] || new Decimal(0))
            .plus(beforeUpdateScrapCostByYearCalc[i] || new Decimal(0))
        );

        // 国内工場販管費・開発費・ロイヤリティ・その他の経費（3ヶ月平均を適用）
        const beforeUpdateDomesticSgaCostByYearCalc = convertMonthlyToYearly(beforeUpdateDomesticSgaCostByMonth, decimalCurrency);
        const beforeUpdateDevCostByYearCalc = aggregateByYear(beforeUpdateDevCostByMonth, (e) => e.value || new Decimal(0));
        const beforeUpdateRoyaltyByYearCalc = convertMonthlyToYearly(beforeUpdateRoyaltyByMonth, decimalCurrency);
        const beforeUpdateOtherCostByYearCalc = aggregateByYear(beforeUpdateOtherCostByMonth, (e) => e.value || new Decimal(0));

        // 販売費及び一般管理費 = 生産工場販管費 + 国内工場販管費 + 本社・販社販管費 + 開発費 + ロイヤリティ
        const beforeUpdateSGAExpensesByYearCalc = beforeUpdateAppliedLaborCostByYearCalc.map((val, i) =>
          val
            .plus(beforeUpdateDomesticSgaCostByYearCalc[i] || new Decimal(0))
            .plus(beforeUpdateSalesManagementCostByYearCalc[i] || new Decimal(0))
            .plus(beforeUpdateDevCostByYearCalc[i] || new Decimal(0))
            .plus(beforeUpdateRoyaltyByYearCalc[i] || new Decimal(0))
        );

        // 営業利益 = 売上合計 - 売上原価 - 販売費及び一般管理費 - その他の経費
        // ※ 開発費・ロイヤリティは販売費及び一般管理費に含まれるため、ここでは引かない
        const beforeUpdateProfitByYearCalc = beforeUpdateTotalSalesByYearCalc.map((val, i) =>
          val
            .minus(beforeUpdateSalesCostByYearCalc[i] || new Decimal(0))
            .minus(beforeUpdateSGAExpensesByYearCalc[i] || new Decimal(0))
            .minus(beforeUpdateOtherCostByYearCalc[i] || new Decimal(0))
        );

        // 営業利益率 = 営業利益 / 売上合計 * 100
        const beforeUpdateProfitRateByYearCalc = beforeUpdateTotalSalesByYearCalc.map((val, i) =>
          val.isZero() ? new Decimal(0) : beforeUpdateProfitByYearCalc[i].div(val).times(100)
        );

        // 製品売上は売上合計と同じ比率で調整（簡易計算）
        const beforeUpdateProductSalesByYearCalc = beforeUpdateTotalSalesByYearCalc.map((val, i) => {
          const originalTotal = totalSalesMoldDevByYear[i] || new Decimal(1);
          const originalProduct = productSalesByYear[i] || new Decimal(0);
          return originalTotal.isZero() ? new Decimal(0) : val.times(originalProduct).div(originalTotal);
        });

        // PJ単位売上は入力値と同じ（更新前も更新後も同じ）
        const beforeUpdateMoldSalesByYearCalc = moldSalesByYear;
        const beforeUpdateDevSalesByYearCalc = devSalesByYear;
        const beforeUpdateEquipmentSalesByYearCalc = equipmentSalesByYear;

        // state更新
        setBeforeUpdateTotalSalesByYear(beforeUpdateTotalSalesByYearCalc);
        setBeforeUpdateProductSalesByYear(beforeUpdateProductSalesByYearCalc);
        setBeforeUpdateMoldSalesByYear(beforeUpdateMoldSalesByYearCalc);
        setBeforeUpdateDevSalesByYear(beforeUpdateDevSalesByYearCalc);
        setBeforeUpdateEquipmentSalesByYear(beforeUpdateEquipmentSalesByYearCalc);
        setBeforeUpdateSalesCostByYear(beforeUpdateSalesCostByYearCalc);
        setBeforeUpdateTotalMaterialCostByYear(beforeUpdateMaterialCostByYearCalc);
        setBeforeUpdateOutsideCostByYear(beforeUpdateOutsideCostByYearCalc);
        setBeforeUpdateLaborCostByYear(beforeUpdateLaborCostByYearCalc);
        setBeforeUpdateDomesticLaborCostByYear(beforeUpdateDomesticLaborCostByYearCalc);
        setBeforeUpdateFactoryExpensesByYear(beforeUpdateFactoryExpensesByYearCalc);
        setBeforeUpdateDomesticFactoryExpensesByYear(beforeUpdateDomesticFactoryExpensesByYearCalc);
        setBeforeUpdateDomesticOverHeadCostByYear(beforeUpdateDomesticOverHeadCostByYearCalc);
        setBeforeUpdateDomesticDepreciationByYear(beforeUpdateDomesticDepreciationByYearCalc);
        setBeforeUpdateDomesticInvestmentDepreciationByYear(beforeUpdateDomesticInvestmentDepreciationByYearCalc);
        setBeforeUpdateDomesticFactoryBuildingDepreciationByYear(beforeUpdateDomesticFactoryBuildingDepreciationByYearCalc);
        setBeforeUpdateDomesticOtherDepreciationByYear(beforeUpdateDomesticOtherDepreciationByYearCalc);
        setBeforeUpdateOverHeadCostByYear(beforeUpdateOverheadCostByYearCalc);
        setBeforeUpdateDepreciationByYear(beforeUpdateDepreciationByYearCalc);
        setBeforeUpdateEquipmentDepreciationByYear(beforeUpdateEquipmentDepreciationByYearCalc);
        setBeforeUpdateFactoryCostByYear(beforeUpdateFactoryCostByYearCalc);
        setBeforeUpdatePastEquipmentCostByYear(beforeUpdatePastEquipmentCostByYearCalc);
        setBeforeUpdateOtherEquipmentCostByYear(beforeUpdateOtherEquipmentCostByYearCalc);
        setBeforeUpdateScrapCostByYear(beforeUpdateScrapCostByYearCalc);
        setBeforeUpdateSGAExpensesByYear(beforeUpdateSGAExpensesByYearCalc);
        setBeforeUpdateAppliedLaborCostByYear(beforeUpdateAppliedLaborCostByYearCalc);
        setBeforeUpdateDomesticSgaCostByYear(beforeUpdateDomesticSgaCostByYearCalc);
        setBeforeUpdateSalesManagementCostByYear(beforeUpdateSalesManagementCostByYearCalc);
        setBeforeUpdateDevCostByYear(beforeUpdateDevCostByYearCalc);
        setBeforeUpdateRoyaltyByYear(beforeUpdateRoyaltyByYearCalc);
        setBeforeUpdateOtherCostByYear(beforeUpdateOtherCostByYearCalc);
        setBeforeUpdateProfitByYear(beforeUpdateProfitByYearCalc);
        setBeforeUpdateProfitRateByYear(beforeUpdateProfitRateByYearCalc);

        // 更新前の客先指定部品材料費（月別）を計算
        // 量産開始前は入力値、量産開始後～EOL予定日は3ヶ月平均、EOL予定日以降は0
        const avgCustomerSpecifiedPartsCost = customerSpecifiedPartsCostByMonth.length > 0
          ? customerSpecifiedPartsCostByMonth.slice(avgStartIndex, avgEndIndex).reduce((sum, val) => sum.plus(val), new Decimal(0)).div(Math.max(1, avgEndIndex - avgStartIndex))
          : new Decimal(0);

        const beforeUpdateCustomerSpecifiedPartsCostByMonth = customerSpecifiedPartsCostByMonth.map((val, index) => {
          if (index >= eolMonthIndex) {
            // EOL予定日以降は0
            return new Decimal(0);
          } else if (index < updateStartMonthIndex) {
            // 量産開始前は入力値をそのまま使用
            return val;
          } else {
            // 量産開始後～EOL予定日は3ヶ月平均を使用
            return avgCustomerSpecifiedPartsCost;
          }
        });

        // 更新前の客先指定部品材料費（年度別）に集計
        const beforeUpdateCustomerSpecifiedPartsCostByYear = convertMonthlyToYearly(beforeUpdateCustomerSpecifiedPartsCostByMonth, decimalCurrency);

        // 更新前の実質的営業利益率
        // 実質的売上 = 売上合計 - 客先指定部品材料費
        const beforeUpdateRealSalesByYear = beforeUpdateTotalSalesByYearCalc.map((sales, index) =>
          sales.minus(beforeUpdateCustomerSpecifiedPartsCostByYear[index] || new Decimal(0))
        );

        // 実質的営業利益率 = 営業利益 / 実質的売上 × 100
        const beforeUpdateRealOperatingProfitRateByYearCalc = beforeUpdateRealSalesByYear.map((realSales, index) => {
          if (realSales.isZero()) {
            return new Decimal(0);
          }
          return (beforeUpdateProfitByYearCalc[index] || new Decimal(0)).div(realSales).times(100);
        });
        setBeforeUpdateRealOperatingProfitRateByYear(beforeUpdateRealOperatingProfitRateByYearCalc);

        // 更新前データのJPY版を設定（表示通貨の月次データから再計算して二重変換を回避）

        // JPY版のYearMonthオブジェクトを作成（calcThreeMonthAverage用）
        const appliedLaborCostByYearMonthDisplay = convertToYearMonthObject(yearMonthListByElapsedYears, appliedLaborCostByMonthDisplay);
        const salesManagementCostByYearMonthDisplay = convertToYearMonthObject(yearMonthListByElapsedYears, salesManagementCostByMonthDisplay);

        // 3ヶ月平均を計算するヘルパー関数（JPY版）
        const calcThreeMonthAverageDisplay = (
          monthlyData: { year: number, month: number, sales?: Decimal, value?: Decimal, materialCost?: Decimal }[],
          fieldName: 'sales' | 'value' | 'materialCost'
        ): Decimal => {
          let sum = new Decimal(0);
          let count = 0;
          for (let i = avgStartIndex; i < avgEndIndex && i < monthlyData.length; i++) {
            const val = monthlyData[i]?.[fieldName];
            if (val) {
              sum = sum.plus(val);
              count++;
            }
          }
          return count > 0 ? sum.div(count) : new Decimal(0);
        };

        // 「更新前」の月次データを作成するヘルパー関数（JPY版）
        const createBeforeUpdateMonthlyDataDisplay = <T extends { year: number, month: number }>(
          monthlyData: T[],
          fieldName: keyof T,
          avgValue: Decimal
        ): T[] => {
          return monthlyData.map((entry, index) => {
            if (index >= eolMonthIndex) {
              return { ...entry, [fieldName]: new Decimal(0) };
            } else if (index < updateStartMonthIndex) {
              return { ...entry };
            } else {
              return { ...entry, [fieldName]: avgValue };
            }
          });
        };

        // 各項目の3ヶ月平均を計算（JPY版）
        const avgTotalSalesDisplay = calcThreeMonthAverageDisplay(totalSalesMoldDevByMonthDisplay, 'sales');
        const avgMaterialCostDisplay = calcThreeMonthAverageDisplay(totalMaterialCostByMonthDisplay, 'materialCost');
        const avgOutsideCostDisplay = outsideCostByMonthDisplay.length > 0
          ? outsideCostByMonthDisplay.slice(avgStartIndex, avgEndIndex).reduce((sum, val) => sum.plus(val), new Decimal(0)).div(Math.max(1, avgEndIndex - avgStartIndex))
          : new Decimal(0);
        const avgLaborCostDisplay = laborCostByMonthDisplay.length > 0
          ? laborCostByMonthDisplay.slice(avgStartIndex, avgEndIndex).reduce((sum, val) => sum.plus(val), new Decimal(0)).div(Math.max(1, avgEndIndex - avgStartIndex))
          : new Decimal(0);
        const avgDomesticLaborCostDisplay = domesticLaborCostByMonthDisplay.length > 0
          ? domesticLaborCostByMonthDisplay.slice(avgStartIndex, avgEndIndex).reduce((sum, val) => sum.plus(val), new Decimal(0)).div(Math.max(1, avgEndIndex - avgStartIndex))
          : new Decimal(0);
        const avgDomesticFactoryExpensesDisplay = domesticFactoryExpensesByYearMonthDisplay.length > 0
          ? domesticFactoryExpensesByYearMonthDisplay.slice(avgStartIndex, avgEndIndex).reduce((sum, val) => sum.plus(val.value || new Decimal(0)), new Decimal(0)).div(Math.max(1, avgEndIndex - avgStartIndex))
          : new Decimal(0);
        const avgDomesticOverHeadCostDisplay = calcThreeMonthAverageDisplay(domesticOverHeadCostByYearMonthDisplay, 'value');
        const avgDomesticDepreciationDisplay = calcThreeMonthAverageDisplay(domesticDepreciationByMonthDisplay, 'value');
        const avgDomesticInvestmentDepreciationDisplay = calcThreeMonthAverageDisplay(domesticInvestmentDepreciationByYearMonthDisplay, 'value');
        const avgDomesticFactoryBuildingDepreciationDisplay = calcThreeMonthAverageDisplay(domesticFactoryBuildingDepreciationByYearMonthDisplay, 'value');
        const avgDomesticOtherDepreciationDisplay = calcThreeMonthAverageDisplay(domesticOtherDepreciationByYearMonthDisplay, 'value');
        const avgOverheadCostDisplay = calcThreeMonthAverageDisplay(overHeadCostByYearMonthDisplay, 'value');
        const avgEquipmentDepreciationDisplay = calcThreeMonthAverageDisplay(equipmentDepreciationByMonthDisplay, 'value');
        const avgFactoryCostDisplay = calcThreeMonthAverageDisplay(factoryCostByMonthDisplay, 'value');
        const avgPastEquipmentCostDisplay = calcThreeMonthAverageDisplay(pastEquipmentCostByMonthDisplay, 'value');
        const avgOtherEquipmentCostDisplay = calcThreeMonthAverageDisplay(otherEquipmentCostByMonthDisplay, 'value');
        const avgAppliedLaborCostDisplay = calcThreeMonthAverageDisplay(appliedLaborCostByYearMonthDisplay, 'value');
        const avgSalesManagementCostDisplay = calcThreeMonthAverageDisplay(salesManagementCostByYearMonthDisplay, 'value');
        const avgScrapCostDisplay = scrapCostByMonthDisplay.length > 0
          ? scrapCostByMonthDisplay.slice(avgStartIndex, avgEndIndex).reduce((sum, val) => sum.plus(val), new Decimal(0)).div(Math.max(1, avgEndIndex - avgStartIndex))
          : new Decimal(0);
        const avgDomesticSgaCostDisplay = domesticSgaCostByMonthDisplay.length > 0
          ? domesticSgaCostByMonthDisplay.slice(avgStartIndex, avgEndIndex).reduce((sum, val) => sum.plus(val), new Decimal(0)).div(Math.max(1, avgEndIndex - avgStartIndex))
          : new Decimal(0);
        const avgDevCostDisplay = devCostByMonth.length > 0
          ? calcThreeMonthAverageDisplay(devCostByMonth.map((entry, i) => ({
            ...entry,
            value: convertWithRate(entry.value, localCurrency, displayCurrencyName, toYearMonth(entry.year, entry.month)).toDecimalPlaces(displayCurrencyDecimal ? 2 : 0)
          })), 'value')
          : new Decimal(0);
        const avgRoyaltyDisplay = royaltyByMonthDisplay.length > 0
          ? royaltyByMonthDisplay.slice(avgStartIndex, avgEndIndex).reduce((sum, val) => sum.plus(val), new Decimal(0)).div(Math.max(1, avgEndIndex - avgStartIndex))
          : new Decimal(0);
        const avgOtherCostDisplay = otherCostByMonth.length > 0
          ? calcThreeMonthAverageDisplay(otherCostByMonth.map((entry, i) => ({
            ...entry,
            value: convertWithRate(entry.value, localCurrency, displayCurrencyName, toYearMonth(entry.year, entry.month)).toDecimalPlaces(displayCurrencyDecimal ? 2 : 0)
          })), 'value')
          : new Decimal(0);

        // 「更新前」の月次データを作成（JPY版）
        const beforeUpdateTotalSalesByMonthDisplay = createBeforeUpdateMonthlyDataDisplay(totalSalesMoldDevByMonthDisplay, 'sales', avgTotalSalesDisplay);
        const beforeUpdateMaterialCostByMonthDisplay = createBeforeUpdateMonthlyDataDisplay(totalMaterialCostByMonthDisplay, 'materialCost', avgMaterialCostDisplay);
        const beforeUpdateOutsideCostByMonthDisplayCalc = outsideCostByMonthDisplay.map((val, index) => {
          if (index >= eolMonthIndex) return new Decimal(0);
          else if (index < updateStartMonthIndex) return val;
          else return avgOutsideCostDisplay;
        });
        const beforeUpdateLaborCostByMonthDisplayCalc = laborCostByMonthDisplay.map((val, index) => {
          if (index >= eolMonthIndex) return new Decimal(0);
          else if (index < updateStartMonthIndex) return val;
          else return avgLaborCostDisplay;
        });
        const beforeUpdateDomesticLaborCostByMonthDisplayCalc = domesticLaborCostByMonthDisplay.map((val, index) => {
          if (index >= eolMonthIndex) return new Decimal(0);
          else if (index < updateStartMonthIndex) return val;
          else return avgDomesticLaborCostDisplay;
        });
        const beforeUpdateDomesticFactoryExpensesByMonthDisplayCalc = domesticFactoryExpensesByYearMonthDisplay.map((entry, index) => {
          if (index >= eolMonthIndex) return new Decimal(0);
          else if (index < updateStartMonthIndex) return entry.value || new Decimal(0);
          else return avgDomesticFactoryExpensesDisplay;
        });
        const beforeUpdateDomesticOverHeadCostByMonthDisplayCalc = createBeforeUpdateMonthlyDataDisplay(domesticOverHeadCostByYearMonthDisplay, 'value', avgDomesticOverHeadCostDisplay);
        const beforeUpdateDomesticDepreciationByMonthDisplayCalc = createBeforeUpdateMonthlyDataDisplay(domesticDepreciationByMonthDisplay, 'value', avgDomesticDepreciationDisplay);
        const beforeUpdateDomesticInvestmentDepreciationByMonthDisplayCalc = createBeforeUpdateMonthlyDataDisplay(domesticInvestmentDepreciationByYearMonthDisplay, 'value', avgDomesticInvestmentDepreciationDisplay);
        const beforeUpdateDomesticFactoryBuildingDepreciationByMonthDisplayCalc = createBeforeUpdateMonthlyDataDisplay(domesticFactoryBuildingDepreciationByYearMonthDisplay, 'value', avgDomesticFactoryBuildingDepreciationDisplay);
        const beforeUpdateDomesticOtherDepreciationByMonthDisplayCalc = createBeforeUpdateMonthlyDataDisplay(domesticOtherDepreciationByYearMonthDisplay, 'value', avgDomesticOtherDepreciationDisplay);
        const beforeUpdateOverheadCostByMonthDisplayCalc = createBeforeUpdateMonthlyDataDisplay(overHeadCostByYearMonthDisplay, 'value', avgOverheadCostDisplay);
        const beforeUpdateEquipmentDepreciationByMonthDisplayCalc = createBeforeUpdateMonthlyDataDisplay(equipmentDepreciationByMonthDisplay, 'value', avgEquipmentDepreciationDisplay);
        const beforeUpdateFactoryCostByMonthDisplayCalc = createBeforeUpdateMonthlyDataDisplay(factoryCostByMonthDisplay, 'value', avgFactoryCostDisplay);
        const beforeUpdatePastEquipmentCostByMonthDisplayCalc = createBeforeUpdateMonthlyDataDisplay(pastEquipmentCostByMonthDisplay, 'value', avgPastEquipmentCostDisplay);
        const beforeUpdateOtherEquipmentCostByMonthDisplayCalc = createBeforeUpdateMonthlyDataDisplay(otherEquipmentCostByMonthDisplay, 'value', avgOtherEquipmentCostDisplay);
        const beforeUpdateAppliedLaborCostByMonthDisplayCalc = createBeforeUpdateMonthlyDataDisplay(appliedLaborCostByYearMonthDisplay, 'value', avgAppliedLaborCostDisplay);
        const beforeUpdateSalesManagementCostByMonthDisplayCalc = createBeforeUpdateMonthlyDataDisplay(salesManagementCostByYearMonthDisplay, 'value', avgSalesManagementCostDisplay);
        const beforeUpdateScrapCostByMonthDisplayCalc = scrapCostByMonthDisplay.map((val, index) => {
          if (index >= eolMonthIndex) return new Decimal(0);
          else if (index < updateStartMonthIndex) return val;
          else return avgScrapCostDisplay;
        });
        const beforeUpdateDomesticSgaCostByMonthDisplayCalc = domesticSgaCostByMonthDisplay.map((val, index) => {
          if (index >= eolMonthIndex) return new Decimal(0);
          else if (index < updateStartMonthIndex) return val;
          else return avgDomesticSgaCostDisplay;
        });
        const beforeUpdateDevCostByMonthDisplayCalc = devCostByMonth.map((entry, index) => {
          const displayValue = convertWithRate(entry.value, localCurrency, displayCurrencyName, toYearMonth(entry.year, entry.month)).toDecimalPlaces(displayCurrencyDecimal ? 2 : 0);
          if (index >= eolMonthIndex) return { ...entry, value: new Decimal(0) };
          else if (index < updateStartMonthIndex) return { ...entry, value: displayValue };
          else return { ...entry, value: avgDevCostDisplay };
        });
        const beforeUpdateRoyaltyByMonthDisplayCalc = royaltyByMonthDisplay.map((val, index) => {
          if (index >= eolMonthIndex) return new Decimal(0);
          else if (index < updateStartMonthIndex) return val;
          else return avgRoyaltyDisplay;
        });
        const beforeUpdateOtherCostByMonthDisplayCalc = otherCostByMonth.map((entry, index) => {
          const displayValue = convertWithRate(entry.value, localCurrency, displayCurrencyName, toYearMonth(entry.year, entry.month)).toDecimalPlaces(displayCurrencyDecimal ? 2 : 0);
          if (index >= eolMonthIndex) return { ...entry, value: new Decimal(0) };
          else if (index < updateStartMonthIndex) return { ...entry, value: displayValue };
          else return { ...entry, value: avgOtherCostDisplay };
        });

        // 月次データを年度別に集計するヘルパー関数（JPY版、displayCurrencyDecimalを使用）
        const aggregateByYearDisplay = (
          monthlyData: { year: number, month: number }[],
          getValue: (entry: any) => Decimal
        ): Decimal[] => {
          const yearlyTotals: { [fiscalYear: number]: Decimal } = {};
          monthlyData.forEach((entry, index) => {
            const currentDate = projectStartDateObj.add(index, 'month');
            const year = currentDate.year();
            const month = currentDate.month() + 1;
            const fiscalYear = month >= 4 ? year : year - 1;
            if (!yearlyTotals[fiscalYear]) {
              yearlyTotals[fiscalYear] = new Decimal(0);
            }
            yearlyTotals[fiscalYear] = yearlyTotals[fiscalYear].plus(getValue(entry));
          });
          // 丸めなし、フル精度で返す（年度ごとの個別丸めは「合計」列の誤差原因となるため撤廃、No425）
          return Object.values(yearlyTotals);
        };

        // 「更新前」の年度別データを計算（JPY版）
        const beforeUpdateTotalSalesByYearDisplay = aggregateByYearDisplay(beforeUpdateTotalSalesByMonthDisplay, (e) => e.sales || new Decimal(0));
        const beforeUpdateMaterialCostByYearDisplay = aggregateByYearDisplay(beforeUpdateMaterialCostByMonthDisplay, (e) => e.materialCost || new Decimal(0));
        const beforeUpdateOutsideCostByYearDisplay = convertMonthlyToYearly(beforeUpdateOutsideCostByMonthDisplayCalc, displayCurrencyDecimal);
        const beforeUpdateLaborCostByYearDisplay = convertMonthlyToYearly(beforeUpdateLaborCostByMonthDisplayCalc, displayCurrencyDecimal);
        const beforeUpdateDomesticLaborCostByYearDisplay = convertMonthlyToYearly(beforeUpdateDomesticLaborCostByMonthDisplayCalc, displayCurrencyDecimal);
        const beforeUpdateDomesticFactoryExpensesByYearDisplay = convertMonthlyToYearly(beforeUpdateDomesticFactoryExpensesByMonthDisplayCalc, displayCurrencyDecimal);
        const beforeUpdateDomesticOverHeadCostByYearDisplay = aggregateByYearDisplay(beforeUpdateDomesticOverHeadCostByMonthDisplayCalc, (e) => e.value || new Decimal(0));
        const beforeUpdateDomesticDepreciationByYearDisplay = aggregateByYearDisplay(beforeUpdateDomesticDepreciationByMonthDisplayCalc, (e) => e.value || new Decimal(0));
        const beforeUpdateDomesticInvestmentDepreciationByYearDisplay = aggregateByYearDisplay(beforeUpdateDomesticInvestmentDepreciationByMonthDisplayCalc, (e) => e.value || new Decimal(0));
        const beforeUpdateDomesticFactoryBuildingDepreciationByYearDisplay = aggregateByYearDisplay(beforeUpdateDomesticFactoryBuildingDepreciationByMonthDisplayCalc, (e) => e.value || new Decimal(0));
        const beforeUpdateDomesticOtherDepreciationByYearDisplay = aggregateByYearDisplay(beforeUpdateDomesticOtherDepreciationByMonthDisplayCalc, (e) => e.value || new Decimal(0));
        const beforeUpdateOverheadCostByYearDisplay = aggregateByYearDisplay(beforeUpdateOverheadCostByMonthDisplayCalc, (e) => e.value || new Decimal(0));
        const beforeUpdateEquipmentDepreciationByYearDisplay = aggregateByYearDisplay(beforeUpdateEquipmentDepreciationByMonthDisplayCalc, (e) => e.value || new Decimal(0));
        const beforeUpdateFactoryCostByYearDisplay = aggregateByYearDisplay(beforeUpdateFactoryCostByMonthDisplayCalc, (e) => e.value || new Decimal(0));
        const beforeUpdatePastEquipmentCostByYearDisplay = aggregateByYearDisplay(beforeUpdatePastEquipmentCostByMonthDisplayCalc, (e) => e.value || new Decimal(0));
        const beforeUpdateOtherEquipmentCostByYearDisplay = aggregateByYearDisplay(beforeUpdateOtherEquipmentCostByMonthDisplayCalc, (e) => e.value || new Decimal(0));
        const beforeUpdateAppliedLaborCostByYearDisplay = aggregateByYearDisplay(beforeUpdateAppliedLaborCostByMonthDisplayCalc, (e) => e.value || new Decimal(0));
        const beforeUpdateSalesManagementCostByYearDisplay = aggregateByYearDisplay(beforeUpdateSalesManagementCostByMonthDisplayCalc, (e) => e.value || new Decimal(0));
        const beforeUpdateScrapCostByYearDisplay = convertMonthlyToYearly(beforeUpdateScrapCostByMonthDisplayCalc, displayCurrencyDecimal);
        const beforeUpdateDomesticSgaCostByYearDisplay = convertMonthlyToYearly(beforeUpdateDomesticSgaCostByMonthDisplayCalc, displayCurrencyDecimal);
        const beforeUpdateDevCostByYearDisplay = aggregateByYearDisplay(beforeUpdateDevCostByMonthDisplayCalc, (e) => e.value || new Decimal(0));
        const beforeUpdateRoyaltyByYearDisplay = convertMonthlyToYearly(beforeUpdateRoyaltyByMonthDisplayCalc, displayCurrencyDecimal);
        const beforeUpdateOtherCostByYearDisplay = aggregateByYearDisplay(beforeUpdateOtherCostByMonthDisplayCalc, (e) => e.value || new Decimal(0));

        // 減価償却費合計（JPY版）
        const beforeUpdateDepreciationByYearDisplay = beforeUpdateEquipmentDepreciationByYearDisplay.map((val, i) =>
          val.plus(beforeUpdateFactoryCostByYearDisplay[i] || new Decimal(0)).plus(beforeUpdatePastEquipmentCostByYearDisplay[i] || new Decimal(0)).plus(beforeUpdateOtherEquipmentCostByYearDisplay[i] || new Decimal(0))
        );

        // 工場経費（JPY版）
        const beforeUpdateFactoryExpensesByYearDisplay = beforeUpdateOverheadCostByYearDisplay.map((val, i) =>
          val.plus(beforeUpdateDepreciationByYearDisplay[i] || new Decimal(0))
        );

        // 売上原価（JPY版）
        const beforeUpdateSalesCostByYearDisplay = beforeUpdateMaterialCostByYearDisplay.map((val, i) =>
          val
            .plus(beforeUpdateOutsideCostByYearDisplay[i] || new Decimal(0))
            .plus(beforeUpdateLaborCostByYearDisplay[i] || new Decimal(0))
            .plus(beforeUpdateDomesticLaborCostByYearDisplay[i] || new Decimal(0))
            .plus(beforeUpdateFactoryExpensesByYearDisplay[i] || new Decimal(0))
            .plus(beforeUpdateDomesticFactoryExpensesByYearDisplay[i] || new Decimal(0))
            .plus(beforeUpdateScrapCostByYearDisplay[i] || new Decimal(0))
        );

        // 販売費及び一般管理費（JPY版）
        const beforeUpdateSGAExpensesByYearDisplay = beforeUpdateAppliedLaborCostByYearDisplay.map((val, i) =>
          val
            .plus(beforeUpdateDomesticSgaCostByYearDisplay[i] || new Decimal(0))
            .plus(beforeUpdateSalesManagementCostByYearDisplay[i] || new Decimal(0))
            .plus(beforeUpdateDevCostByYearDisplay[i] || new Decimal(0))
            .plus(beforeUpdateRoyaltyByYearDisplay[i] || new Decimal(0))
        );

        // 営業利益（JPY版）
        const beforeUpdateProfitByYearDisplay = beforeUpdateTotalSalesByYearDisplay.map((val, i) =>
          val
            .minus(beforeUpdateSalesCostByYearDisplay[i] || new Decimal(0))
            .minus(beforeUpdateSGAExpensesByYearDisplay[i] || new Decimal(0))
            .minus(beforeUpdateOtherCostByYearDisplay[i] || new Decimal(0))
        );

        // 営業利益率（JPY版）
        const beforeUpdateProfitRateByYearDisplay = beforeUpdateTotalSalesByYearDisplay.map((val, i) =>
          val.isZero() ? new Decimal(0) : beforeUpdateProfitByYearDisplay[i].div(val).times(100)
        );

        // 製品売上（JPY版）
        const beforeUpdateProductSalesByYearDisplay = beforeUpdateTotalSalesByYearDisplay.map((val, i) => {
          // 比率計算用: 表示通貨で計算済みの年度別値をそのまま使用（ローカル通貨版を変換・丸めし直すと誤差が生じるため、No425）
          const originalTotalDisplay = totalSalesMoldDevByYearDisplay[i] || new Decimal(1);
          const originalProductDisplay = productSalesByYearDisplay[i] || new Decimal(0);
          return originalTotalDisplay.isZero() ? new Decimal(0) : val.times(originalProductDisplay).div(originalTotalDisplay);
        });

        // PJ単位売上（JPY版）- 入力通貨から表示通貨に直接変換
        const projectMoldSalesByYearDisplay = calculateProjectSalesSum(projectMoldSalesByMonthDisplay, displayCurrencyDecimal);
        const projectDevSalesByYearDisplay = calculateProjectSalesSum(projectDevSalesByMonthDisplay, displayCurrencyDecimal);
        const projectEquipmentSalesByYearDisplay = calculateProjectSalesSum(projectEquipmentSalesByMonthDisplay, displayCurrencyDecimal);

        // 更新前データのJPY版を設定
        setDisplayBeforeUpdateTotalSalesByYear(beforeUpdateTotalSalesByYearDisplay);
        setDisplayBeforeUpdateProductSalesByYear(beforeUpdateProductSalesByYearDisplay);
        setDisplayBeforeUpdateMoldSalesByYear(projectMoldSalesByYearDisplay);
        setDisplayBeforeUpdateDevSalesByYear(projectDevSalesByYearDisplay);
        setDisplayBeforeUpdateEquipmentSalesByYear(projectEquipmentSalesByYearDisplay);
        setDisplayBeforeUpdateSalesCostByYear(beforeUpdateSalesCostByYearDisplay);
        setDisplayBeforeUpdateTotalMaterialCostByYear(beforeUpdateMaterialCostByYearDisplay);
        setDisplayBeforeUpdateOutsideCostByYear(beforeUpdateOutsideCostByYearDisplay);
        setDisplayBeforeUpdateLaborCostByYear(beforeUpdateLaborCostByYearDisplay);
        setDisplayBeforeUpdateDomesticLaborCostByYear(beforeUpdateDomesticLaborCostByYearDisplay);
        setDisplayBeforeUpdateFactoryExpensesByYear(beforeUpdateFactoryExpensesByYearDisplay);
        setDisplayBeforeUpdateOverHeadCostByYear(beforeUpdateOverheadCostByYearDisplay);
        setDisplayBeforeUpdateDepreciationByYear(beforeUpdateDepreciationByYearDisplay);
        setDisplayBeforeUpdateEquipmentDepreciationByYear(beforeUpdateEquipmentDepreciationByYearDisplay);
        setDisplayBeforeUpdateFactoryCostByYear(beforeUpdateFactoryCostByYearDisplay);
        setDisplayBeforeUpdatePastEquipmentCostByYear(beforeUpdatePastEquipmentCostByYearDisplay);
        setDisplayBeforeUpdateOtherEquipmentCostByYear(beforeUpdateOtherEquipmentCostByYearDisplay);
        setDisplayBeforeUpdateDomesticFactoryExpensesByYear(beforeUpdateDomesticFactoryExpensesByYearDisplay);
        setDisplayBeforeUpdateDomesticOverHeadCostByYear(beforeUpdateDomesticOverHeadCostByYearDisplay);
        setDisplayBeforeUpdateDomesticDepreciationByYear(beforeUpdateDomesticDepreciationByYearDisplay);
        setDisplayBeforeUpdateDomesticInvestmentDepreciationByYear(beforeUpdateDomesticInvestmentDepreciationByYearDisplay);
        setDisplayBeforeUpdateDomesticFactoryBuildingDepreciationByYear(beforeUpdateDomesticFactoryBuildingDepreciationByYearDisplay);
        setDisplayBeforeUpdateDomesticOtherDepreciationByYear(beforeUpdateDomesticOtherDepreciationByYearDisplay);
        setDisplayBeforeUpdateScrapCostByYear(beforeUpdateScrapCostByYearDisplay);
        setDisplayBeforeUpdateSGAExpensesByYear(beforeUpdateSGAExpensesByYearDisplay);
        setDisplayBeforeUpdateAppliedLaborCostByYear(beforeUpdateAppliedLaborCostByYearDisplay);
        setDisplayBeforeUpdateDomesticSgaCostByYear(beforeUpdateDomesticSgaCostByYearDisplay);
        setDisplayBeforeUpdateSalesManagementCostByYear(beforeUpdateSalesManagementCostByYearDisplay);
        setDisplayBeforeUpdateDevCostByYear(beforeUpdateDevCostByYearDisplay);
        setDisplayBeforeUpdateRoyaltyByYear(beforeUpdateRoyaltyByYearDisplay);
        setDisplayBeforeUpdateOtherCostByYear(beforeUpdateOtherCostByYearDisplay);
        setDisplayBeforeUpdateProfitByYear(beforeUpdateProfitByYearDisplay);
        // 営業利益率は率なので、JPY版の値から計算した結果を使用
        setDisplayBeforeUpdateProfitRateByYear(beforeUpdateProfitRateByYearDisplay);
        // 実質的営業利益率も率なので通貨変換不要（ローカル通貨版をそのまま使用）
        setDisplayBeforeUpdateRealOperatingProfitRateByYear(beforeUpdateRealOperatingProfitRateByYearCalc);

        // 更新前の月次データを作成（Excel出力用）
        // 製品売上の3ヶ月平均を計算
        const avgProductSalesByMonth = productSalesByMonth.length > 0
          ? productSalesByMonth.slice(avgStartIndex, avgEndIndex).reduce((sum, e) => sum.plus(e.sales || new Decimal(0)), new Decimal(0)).div(Math.max(1, avgEndIndex - avgStartIndex))
          : new Decimal(0);

        // 製品売上（月次）
        const beforeUpdateProductSalesByMonth = productSalesByMonth.map((entry, index) => {
          if (index >= eolMonthIndex) {
            return new Decimal(0);
          } else if (index < updateStartMonthIndex) {
            return entry.sales || new Decimal(0);
          } else {
            return avgProductSalesByMonth;
          }
        });

        // PJ単位売上は更新前も更新後も同じ（入力値をそのまま使用）
        const beforeUpdateMoldSalesByMonth = moldSalesByMonthValues;
        const beforeUpdateDevSalesByMonth = devSalesByMonthValues;
        const beforeUpdateEquipmentSalesByMonth = equipmentSalesByMonthValues;

        // 減価償却費（月次）= 投資対象設備 + 工場建物 + 過去PJの投資対象設備 + その他の設備
        const beforeUpdateDepreciationByMonth = beforeUpdateEquipmentDepreciationByMonth.map((entry, i) => {
          const equipmentDep = entry.value || new Decimal(0);
          const factoryCostVal = beforeUpdateFactoryCostByMonth[i]?.value || new Decimal(0);
          const pastEquipmentVal = beforeUpdatePastEquipmentCostByMonth[i]?.value || new Decimal(0);
          const otherEquipmentVal = beforeUpdateOtherEquipmentCostByMonth[i]?.value || new Decimal(0);
          return equipmentDep.plus(factoryCostVal).plus(pastEquipmentVal).plus(otherEquipmentVal);
        });

        // 工場経費（月次）= 償却費以外の工場経費 + 減価償却費
        const beforeUpdateFactoryExpensesByMonth = beforeUpdateOverheadCostByMonth.map((entry, i) => {
          const overheadVal = entry.value || new Decimal(0);
          return overheadVal.plus(beforeUpdateDepreciationByMonth[i] || new Decimal(0));
        });

        // 売上原価（月次）= 材料費 + 外注加工費 + 生産工場労務費 + 国内工場労務費 + 生産工場経費 + 国内工場経費 + 仕損費
        const beforeUpdateSalesCostByMonth = beforeUpdateMaterialCostByMonth.map((entry, i) => {
          const materialVal = entry.materialCost || new Decimal(0);
          const outsideVal = beforeUpdateOutsideCostByMonth[i] || new Decimal(0);
          const laborVal = beforeUpdateLaborCostByMonth[i] || new Decimal(0);
          const domesticLaborVal = beforeUpdateDomesticLaborCostByMonth[i] || new Decimal(0);
          const factoryExpVal = beforeUpdateFactoryExpensesByMonth[i] || new Decimal(0);
          const domesticFactoryExpVal = beforeUpdateDomesticFactoryExpensesByMonth[i] || new Decimal(0);
          const scrapVal = beforeUpdateScrapCostByMonth[i] || new Decimal(0);
          return materialVal.plus(outsideVal).plus(laborVal).plus(domesticLaborVal).plus(factoryExpVal).plus(domesticFactoryExpVal).plus(scrapVal);
        });

        // 販売費及び一般管理費（月次）= 生産工場販管費 + 国内工場販管費 + 本社・販社販管費 + 開発費 + ロイヤリティ
        const beforeUpdateSGAExpensesByMonth = beforeUpdateAppliedLaborCostByMonth.map((entry, i) => {
          const appliedLaborVal = entry.value || new Decimal(0);
          const domesticSgaVal = beforeUpdateDomesticSgaCostByMonth[i] || new Decimal(0);
          const salesManagementVal = beforeUpdateSalesManagementCostByMonth[i]?.value || new Decimal(0);
          const devCostVal = beforeUpdateDevCostByMonth[i]?.value || new Decimal(0);
          const royaltyVal = beforeUpdateRoyaltyByMonth[i] || new Decimal(0);
          return appliedLaborVal.plus(domesticSgaVal).plus(salesManagementVal).plus(devCostVal).plus(royaltyVal);
        });

        // 営業利益（月次）= 売上合計 - 売上原価 - 販売費及び一般管理費 - その他の経費
        const beforeUpdateProfitByMonth = beforeUpdateTotalSalesByMonth.map((entry, i) => {
          const salesVal = entry.sales || new Decimal(0);
          const salesCostVal = beforeUpdateSalesCostByMonth[i] || new Decimal(0);
          const sgaVal = beforeUpdateSGAExpensesByMonth[i] || new Decimal(0);
          const otherCostVal = beforeUpdateOtherCostByMonth[i]?.value || new Decimal(0);
          return salesVal.minus(salesCostVal).minus(sgaVal).minus(otherCostVal);
        });

        // 更新前の月次損益データをセット
        setBeforeUpdateMonthlyProfitAndLossData({
          labels: monthlyLabels,
          totalSales: beforeUpdateTotalSalesByMonth.map(e => e.sales || new Decimal(0)),
          productSales: beforeUpdateProductSalesByMonth,
          moldSales: beforeUpdateMoldSalesByMonth,
          devSales: beforeUpdateDevSalesByMonth,
          equipmentSales: beforeUpdateEquipmentSalesByMonth,
          salesCost: beforeUpdateSalesCostByMonth,
          materialCost: beforeUpdateMaterialCostByMonth.map(e => e.materialCost || new Decimal(0)),
          outsideCost: beforeUpdateOutsideCostByMonth,
          laborCost: beforeUpdateLaborCostByMonth,
          domesticLaborCost: beforeUpdateDomesticLaborCostByMonth,
          factoryExpenses: beforeUpdateFactoryExpensesByMonth,
          overHeadCost: beforeUpdateOverheadCostByMonth.map(e => e.value || new Decimal(0)),
          depreciation: beforeUpdateDepreciationByMonth,
          equipmentDepreciation: beforeUpdateEquipmentDepreciationByMonth.map(e => e.value || new Decimal(0)),
          factoryCost: beforeUpdateFactoryCostByMonth.map(e => e.value || new Decimal(0)),
          pastEquipmentCost: beforeUpdatePastEquipmentCostByMonth.map(e => e.value || new Decimal(0)),
          otherEquipmentCost: beforeUpdateOtherEquipmentCostByMonth.map(e => e.value || new Decimal(0)),
          domesticFactoryExpenses: beforeUpdateDomesticFactoryExpensesByMonth,
          domesticOverHeadCost: beforeUpdateDomesticOverHeadCostByMonth.map(e => e.value || new Decimal(0)),
          domesticDepreciation: beforeUpdateDomesticDepreciationByMonth.map(e => e.value || new Decimal(0)),
          domesticInvestmentDepreciation: beforeUpdateDomesticInvestmentDepreciationByMonth.map(e => e.value || new Decimal(0)),
          domesticFactoryBuildingDepreciation: beforeUpdateDomesticFactoryBuildingDepreciationByMonth.map(e => e.value || new Decimal(0)),
          domesticOtherDepreciation: beforeUpdateDomesticOtherDepreciationByMonth.map(e => e.value || new Decimal(0)),
          scrapCost: beforeUpdateScrapCostByMonth,
          sGAExpenses: beforeUpdateSGAExpensesByMonth,
          appliedLaborCost: beforeUpdateAppliedLaborCostByMonth.map(e => e.value || new Decimal(0)),
          domesticSgaCost: beforeUpdateDomesticSgaCostByMonth,
          salesManagementCost: beforeUpdateSalesManagementCostByMonth.map(e => e.value || new Decimal(0)),
          devCost: beforeUpdateDevCostByMonth.map(e => e.value || new Decimal(0)),
          royalty: beforeUpdateRoyaltyByMonth,
          otherCost: beforeUpdateOtherCostByMonth.map(e => e.value || new Decimal(0)),
          profit: beforeUpdateProfitByMonth,
          customerSpecifiedPartsCost: beforeUpdateCustomerSpecifiedPartsCostByMonth,
        });

        // 更新前の月次損益データ（JPY版）をセット - Excel出力での二重変換回避用
        // 製品売上の3ヶ月平均（JPY版）を計算
        const avgProductSalesDisplay = productSalesByMonthDisplay.length > 0
          ? productSalesByMonthDisplay.slice(avgStartIndex, avgEndIndex).reduce((sum, e) => sum.plus(e.sales || new Decimal(0)), new Decimal(0)).div(Math.max(1, avgEndIndex - avgStartIndex))
          : new Decimal(0);
        // 製品売上（JPY版）
        const beforeUpdateProductSalesByMonthDisplay = productSalesByMonthDisplay.map((entry, index) => {
          if (index >= eolMonthIndex) return new Decimal(0);
          else if (index < updateStartMonthIndex) return entry.sales;
          else return avgProductSalesDisplay;
        });
        // PJ単位売上は更新前も更新後も同じ（JPY版を使用）
        const beforeUpdateMoldSalesByMonthDisplay = moldSalesByMonthValuesDisplay;
        const beforeUpdateDevSalesByMonthDisplay = devSalesByMonthValuesDisplay;
        const beforeUpdateEquipmentSalesByMonthDisplay = equipmentSalesByMonthValuesDisplay;
        // 生産工場経費（JPY版）
        const beforeUpdateFactoryExpensesByMonthDisplayCalc = beforeUpdateOverheadCostByMonthDisplayCalc.map((entry, i) => {
          const overheadVal = entry.value || new Decimal(0);
          const depVal = beforeUpdateEquipmentDepreciationByMonthDisplayCalc[i]?.value || new Decimal(0);
          const factoryVal = beforeUpdateFactoryCostByMonthDisplayCalc[i]?.value || new Decimal(0);
          const pastEquipVal = beforeUpdatePastEquipmentCostByMonthDisplayCalc[i]?.value || new Decimal(0);
          const otherEquipVal = beforeUpdateOtherEquipmentCostByMonthDisplayCalc[i]?.value || new Decimal(0);
          return overheadVal.plus(depVal).plus(factoryVal).plus(pastEquipVal).plus(otherEquipVal);
        });
        // 減価償却費（JPY版）
        const beforeUpdateDepreciationByMonthDisplayCalc = beforeUpdateEquipmentDepreciationByMonthDisplayCalc.map((entry, i) => {
          const equipDepVal = entry.value || new Decimal(0);
          const factoryVal = beforeUpdateFactoryCostByMonthDisplayCalc[i]?.value || new Decimal(0);
          const pastEquipVal = beforeUpdatePastEquipmentCostByMonthDisplayCalc[i]?.value || new Decimal(0);
          const otherEquipVal = beforeUpdateOtherEquipmentCostByMonthDisplayCalc[i]?.value || new Decimal(0);
          return equipDepVal.plus(factoryVal).plus(pastEquipVal).plus(otherEquipVal);
        });
        // 売上原価（JPY版）
        const beforeUpdateSalesCostByMonthDisplay = beforeUpdateMaterialCostByMonthDisplay.map((entry, i) => {
          const materialVal = entry.materialCost || new Decimal(0);
          const outsideVal = beforeUpdateOutsideCostByMonthDisplayCalc[i] || new Decimal(0);
          const laborVal = beforeUpdateLaborCostByMonthDisplayCalc[i] || new Decimal(0);
          const domesticLaborVal = beforeUpdateDomesticLaborCostByMonthDisplayCalc[i] || new Decimal(0);
          const factoryExpVal = beforeUpdateFactoryExpensesByMonthDisplayCalc[i] || new Decimal(0);
          const domesticFactoryExpVal = beforeUpdateDomesticFactoryExpensesByMonthDisplayCalc[i] || new Decimal(0);
          const scrapVal = beforeUpdateScrapCostByMonthDisplayCalc[i] || new Decimal(0);
          return materialVal.plus(outsideVal).plus(laborVal).plus(domesticLaborVal).plus(factoryExpVal).plus(domesticFactoryExpVal).plus(scrapVal);
        });
        // 販売費及び一般管理費（JPY版）
        const beforeUpdateSGAExpensesByMonthDisplay = beforeUpdateAppliedLaborCostByMonthDisplayCalc.map((entry, i) => {
          const appliedLaborVal = entry.value || new Decimal(0);
          const domesticSgaVal = beforeUpdateDomesticSgaCostByMonthDisplayCalc[i] || new Decimal(0);
          const salesManagementVal = beforeUpdateSalesManagementCostByMonthDisplayCalc[i]?.value || new Decimal(0);
          const devCostVal = beforeUpdateDevCostByMonthDisplayCalc[i]?.value || new Decimal(0);
          const royaltyVal = beforeUpdateRoyaltyByMonthDisplayCalc[i] || new Decimal(0);
          return appliedLaborVal.plus(domesticSgaVal).plus(salesManagementVal).plus(devCostVal).plus(royaltyVal);
        });
        // 営業利益（JPY版）
        const beforeUpdateProfitByMonthDisplay = beforeUpdateTotalSalesByMonthDisplay.map((entry, i) => {
          const salesVal = entry.sales || new Decimal(0);
          const salesCostVal = beforeUpdateSalesCostByMonthDisplay[i] || new Decimal(0);
          const sgaVal = beforeUpdateSGAExpensesByMonthDisplay[i] || new Decimal(0);
          const otherCostVal = beforeUpdateOtherCostByMonthDisplayCalc[i]?.value || new Decimal(0);
          return salesVal.minus(salesCostVal).minus(sgaVal).minus(otherCostVal);
        });
        // 客先指定部品材料費（JPY版）
        const beforeUpdateCustomerSpecifiedPartsCostByMonthDisplay = beforeUpdateCustomerSpecifiedPartsCostByMonth.map((value, idx) => {
          const ym = yearMonthListByFiscalYear[idx]
            ? `${yearMonthListByFiscalYear[idx].year}-${String(yearMonthListByFiscalYear[idx].month).padStart(2, "0")}`
            : undefined;
          return new Decimal(convertWithRate(value, localCurrency, displayCurrencyName, ym) || 0);
        });

        setDisplayBeforeUpdateMonthlyProfitAndLossData({
          labels: monthlyLabels,
          totalSales: beforeUpdateTotalSalesByMonthDisplay.map(e => e.sales || new Decimal(0)),
          productSales: beforeUpdateProductSalesByMonthDisplay,
          moldSales: beforeUpdateMoldSalesByMonthDisplay,
          devSales: beforeUpdateDevSalesByMonthDisplay,
          equipmentSales: beforeUpdateEquipmentSalesByMonthDisplay,
          salesCost: beforeUpdateSalesCostByMonthDisplay,
          materialCost: beforeUpdateMaterialCostByMonthDisplay.map(e => e.materialCost || new Decimal(0)),
          outsideCost: beforeUpdateOutsideCostByMonthDisplayCalc,
          laborCost: beforeUpdateLaborCostByMonthDisplayCalc,
          domesticLaborCost: beforeUpdateDomesticLaborCostByMonthDisplayCalc,
          factoryExpenses: beforeUpdateFactoryExpensesByMonthDisplayCalc,
          overHeadCost: beforeUpdateOverheadCostByMonthDisplayCalc.map(e => e.value || new Decimal(0)),
          depreciation: beforeUpdateDepreciationByMonthDisplayCalc,
          equipmentDepreciation: beforeUpdateEquipmentDepreciationByMonthDisplayCalc.map(e => e.value || new Decimal(0)),
          factoryCost: beforeUpdateFactoryCostByMonthDisplayCalc.map(e => e.value || new Decimal(0)),
          pastEquipmentCost: beforeUpdatePastEquipmentCostByMonthDisplayCalc.map(e => e.value || new Decimal(0)),
          otherEquipmentCost: beforeUpdateOtherEquipmentCostByMonthDisplayCalc.map(e => e.value || new Decimal(0)),
          domesticFactoryExpenses: beforeUpdateDomesticFactoryExpensesByMonthDisplayCalc,
          domesticOverHeadCost: beforeUpdateDomesticOverHeadCostByMonthDisplayCalc.map(e => e.value || new Decimal(0)),
          domesticDepreciation: beforeUpdateDomesticDepreciationByMonthDisplayCalc.map(e => e.value || new Decimal(0)),
          domesticInvestmentDepreciation: beforeUpdateDomesticInvestmentDepreciationByMonthDisplayCalc.map(e => e.value || new Decimal(0)),
          domesticFactoryBuildingDepreciation: beforeUpdateDomesticFactoryBuildingDepreciationByMonthDisplayCalc.map(e => e.value || new Decimal(0)),
          domesticOtherDepreciation: beforeUpdateDomesticOtherDepreciationByMonthDisplayCalc.map(e => e.value || new Decimal(0)),
          scrapCost: beforeUpdateScrapCostByMonthDisplayCalc,
          sGAExpenses: beforeUpdateSGAExpensesByMonthDisplay,
          appliedLaborCost: beforeUpdateAppliedLaborCostByMonthDisplayCalc.map(e => e.value || new Decimal(0)),
          domesticSgaCost: beforeUpdateDomesticSgaCostByMonthDisplayCalc,
          salesManagementCost: beforeUpdateSalesManagementCostByMonthDisplayCalc.map(e => e.value || new Decimal(0)),
          devCost: beforeUpdateDevCostByMonthDisplayCalc.map(e => e.value || new Decimal(0)),
          royalty: beforeUpdateRoyaltyByMonthDisplayCalc,
          otherCost: beforeUpdateOtherCostByMonthDisplayCalc.map(e => e.value || new Decimal(0)),
          profit: beforeUpdateProfitByMonthDisplay,
          customerSpecifiedPartsCost: beforeUpdateCustomerSpecifiedPartsCostByMonthDisplay,
        });
      }

      // #endregion

      // #region 【計算ロジック】年度別損益（合理化前）

      // 合理化投資の場合のみ「合理化前」データを計算
      if (capexProjectData?.purpose === PurposeType.Rationalization.code && capexProjectData?.rationalizationStartDate) {
        const rationalizationStartDate = dayjs(capexProjectData.rationalizationStartDate);
        const projectStartDateObj = dayjs(startDate);
        const projectEndDateObj = capexProjectData.projectEndDate ? dayjs(capexProjectData.projectEndDate) : null;

        // 合理化量産開始日のインデックスを計算（プロジェクト開始日からの月数）
        const rationalizationStartMonthIndex = rationalizationStartDate.startOf('month').diff(projectStartDateObj.startOf('month'), 'month');

        // EOL予定日のインデックスを計算（プロジェクト開始日からの月数）
        // EOL予定日の月までを含める（+1）
        const eolMonthIndexRat = projectEndDateObj
          ? projectEndDateObj.diff(projectStartDateObj, 'month') + 1
          : Infinity;

        // 合理化量産開始直前3ヶ月のインデックスを計算
        const avgStartIndexRat = Math.max(0, rationalizationStartMonthIndex - 3);
        const avgEndIndexRat = rationalizationStartMonthIndex; // 合理化量産開始月は含まない

        // 3ヶ月平均を計算するヘルパー関数
        const calcThreeMonthAverageRat = (
          monthlyData: { year: number, month: number, sales?: Decimal, value?: Decimal, materialCost?: Decimal, outsideCost?: Decimal, overheadCost?: Decimal, laborCost?: Decimal, salesManagementCost?: Decimal }[],
          fieldName: 'sales' | 'value' | 'materialCost' | 'outsideCost' | 'overheadCost' | 'laborCost' | 'salesManagementCost'
        ): Decimal => {
          let sum = new Decimal(0);
          let count = 0;
          for (let i = avgStartIndexRat; i < avgEndIndexRat && i < monthlyData.length; i++) {
            const val = monthlyData[i]?.[fieldName];
            if (val) {
              sum = sum.plus(val);
              count++;
            }
          }
          return count > 0 ? sum.div(count) : new Decimal(0);
        };

        // 「合理化前」の月次データを作成（合理化量産開始前は入力値、開始後～EOL予定日は3ヶ月平均、EOL予定日以降は0）
        const createBeforeRationalizationMonthlyData = <T extends { year: number, month: number }>(
          monthlyData: T[],
          fieldName: keyof T,
          avgValue: Decimal
        ): T[] => {
          return monthlyData.map((entry, index) => {
            if (index >= eolMonthIndexRat) {
              // EOL予定日以降は0
              return { ...entry, [fieldName]: new Decimal(0) };
            } else if (index < rationalizationStartMonthIndex) {
              // 合理化量産開始前は入力値をそのまま使用
              return { ...entry };
            } else {
              // 合理化量産開始後～EOL予定日は3ヶ月平均を使用
              return { ...entry, [fieldName]: avgValue };
            }
          });
        };

        // 各項目の3ヶ月平均を計算
        const avgTotalSalesRat = calcThreeMonthAverageRat(totalSalesMoldDevByMonth, 'sales');
        const avgMaterialCostRat = calcThreeMonthAverageRat(totalMaterialCostByMonth, 'materialCost');
        const avgOutsideCostRat = outsideCostByMonth.length > 0
          ? outsideCostByMonth.slice(avgStartIndexRat, avgEndIndexRat).reduce((sum, val) => sum.plus(val), new Decimal(0)).div(Math.max(1, avgEndIndexRat - avgStartIndexRat))
          : new Decimal(0);
        const avgLaborCostRat = laborCostByMonth.length > 0
          ? laborCostByMonth.slice(avgStartIndexRat, avgEndIndexRat).reduce((sum, val) => sum.plus(val), new Decimal(0)).div(Math.max(1, avgEndIndexRat - avgStartIndexRat))
          : new Decimal(0);
        const avgDomesticLaborCostRat = domesticLaborCostByMonth.length > 0
          ? domesticLaborCostByMonth.slice(avgStartIndexRat, avgEndIndexRat).reduce((sum, val) => sum.plus(val), new Decimal(0)).div(Math.max(1, avgEndIndexRat - avgStartIndexRat))
          : new Decimal(0);
        const avgDomesticFactoryExpensesRat = domesticFactoryExpensesByYearMonth.length > 0
          ? domesticFactoryExpensesByYearMonth.slice(avgStartIndexRat, avgEndIndexRat).reduce((sum, val) => sum.plus(val.value || new Decimal(0)), new Decimal(0)).div(Math.max(1, avgEndIndexRat - avgStartIndexRat))
          : new Decimal(0);
        const avgDomesticOverHeadCostRat = calcThreeMonthAverageRat(domesticOverHeadCostByYearMonth, 'value');
        const avgDomesticDepreciationRat = calcThreeMonthAverageRat(domesticDepreciationByMonth, 'value');
        const avgDomesticInvestmentDepreciationRat = calcThreeMonthAverageRat(domesticInvestmentDepreciationByYearMonth, 'value');
        const avgDomesticFactoryBuildingDepreciationRat = calcThreeMonthAverageRat(domesticFactoryBuildingDepreciationByYearMonth, 'value');
        const avgDomesticOtherDepreciationRat = calcThreeMonthAverageRat(domesticOtherDepreciationByYearMonth, 'value');
        const avgOverheadCostRat = calcThreeMonthAverageRat(overHeadCostByYearMonth, 'value');
        const avgEquipmentDepreciationRat = calcThreeMonthAverageRat(equipmentDepreciationByMonth, 'value');
        const avgFactoryCostRat = calcThreeMonthAverageRat(factoryCostByMonth, 'value');
        const avgPastEquipmentCostRat = calcThreeMonthAverageRat(pastEquipmentCostByMonth, 'value');
        const avgOtherEquipmentCostRat = calcThreeMonthAverageRat(otherEquipmentCostByMonth, 'value');
        const avgAppliedLaborCostRat = calcThreeMonthAverageRat(appliedLaborCostByYearMonth, 'value');
        const avgSalesManagementCostRat = calcThreeMonthAverageRat(salesManagementCostByYearMonth, 'value');
        const avgScrapCostRat = scrapCostByMonth.length > 0
          ? scrapCostByMonth.slice(avgStartIndexRat, avgEndIndexRat).reduce((sum, val) => sum.plus(val), new Decimal(0)).div(Math.max(1, avgEndIndexRat - avgStartIndexRat))
          : new Decimal(0);
        const avgDomesticSgaCostRat = domesticSgaCostByMonth.length > 0
          ? domesticSgaCostByMonth.slice(avgStartIndexRat, avgEndIndexRat).reduce((sum, val) => sum.plus(val), new Decimal(0)).div(Math.max(1, avgEndIndexRat - avgStartIndexRat))
          : new Decimal(0);
        const avgDevCostRat = calcThreeMonthAverageRat(devCostByMonth, 'value');
        const avgRoyaltyRat = royaltyByMonthNew.length > 0
          ? royaltyByMonthNew.slice(avgStartIndexRat, avgEndIndexRat).reduce((sum, val) => sum.plus(val), new Decimal(0)).div(Math.max(1, avgEndIndexRat - avgStartIndexRat))
          : new Decimal(0);
        const avgOtherCostRat = calcThreeMonthAverageRat(otherCostByMonth, 'value');

        // 「合理化前」の月次データを作成
        const beforeRatTotalSalesByMonth = createBeforeRationalizationMonthlyData(totalSalesMoldDevByMonth, 'sales', avgTotalSalesRat);
        const beforeRatMaterialCostByMonth = createBeforeRationalizationMonthlyData(totalMaterialCostByMonth, 'materialCost', avgMaterialCostRat);
        const beforeRatOutsideCostByMonth = outsideCostByMonth.map((val, index) => {
          if (index >= eolMonthIndexRat) {
            return new Decimal(0);
          } else if (index < rationalizationStartMonthIndex) {
            return val;
          } else {
            return avgOutsideCostRat;
          }
        });
        const beforeRatLaborCostByMonth = laborCostByMonth.map((val, index) => {
          if (index >= eolMonthIndexRat) {
            return new Decimal(0);
          } else if (index < rationalizationStartMonthIndex) {
            return val;
          } else {
            return avgLaborCostRat;
          }
        });
        const beforeRatDomesticLaborCostByMonth = domesticLaborCostByMonth.map((val, index) => {
          if (index >= eolMonthIndexRat) {
            return new Decimal(0);
          } else if (index < rationalizationStartMonthIndex) {
            return val;
          } else {
            return avgDomesticLaborCostRat;
          }
        });
        const beforeRatDomesticFactoryExpensesByMonth = domesticFactoryExpensesByYearMonth.map((entry, index) => {
          if (index >= eolMonthIndexRat) {
            return new Decimal(0);
          } else if (index < rationalizationStartMonthIndex) {
            return entry.value || new Decimal(0);
          } else {
            return avgDomesticFactoryExpensesRat;
          }
        });
        // 国内工場経費内訳（5項目）の月次データ
        const beforeRatDomesticOverHeadCostByMonth = createBeforeRationalizationMonthlyData(domesticOverHeadCostByYearMonth, 'value', avgDomesticOverHeadCostRat);
        const beforeRatDomesticDepreciationByMonth = createBeforeRationalizationMonthlyData(domesticDepreciationByMonth, 'value', avgDomesticDepreciationRat);
        const beforeRatDomesticInvestmentDepreciationByMonth = createBeforeRationalizationMonthlyData(domesticInvestmentDepreciationByYearMonth, 'value', avgDomesticInvestmentDepreciationRat);
        const beforeRatDomesticFactoryBuildingDepreciationByMonth = createBeforeRationalizationMonthlyData(domesticFactoryBuildingDepreciationByYearMonth, 'value', avgDomesticFactoryBuildingDepreciationRat);
        const beforeRatDomesticOtherDepreciationByMonth = createBeforeRationalizationMonthlyData(domesticOtherDepreciationByYearMonth, 'value', avgDomesticOtherDepreciationRat);
        const beforeRatOverheadCostByMonth = createBeforeRationalizationMonthlyData(overHeadCostByYearMonth, 'value', avgOverheadCostRat);
        const beforeRatEquipmentDepreciationByMonth = createBeforeRationalizationMonthlyData(equipmentDepreciationByMonth, 'value', avgEquipmentDepreciationRat);
        const beforeRatFactoryCostByMonth = createBeforeRationalizationMonthlyData(factoryCostByMonth, 'value', avgFactoryCostRat);
        const beforeRatPastEquipmentCostByMonth = createBeforeRationalizationMonthlyData(pastEquipmentCostByMonth, 'value', avgPastEquipmentCostRat);
        const beforeRatOtherEquipmentCostByMonth = createBeforeRationalizationMonthlyData(otherEquipmentCostByMonth, 'value', avgOtherEquipmentCostRat);
        const beforeRatAppliedLaborCostByMonth = createBeforeRationalizationMonthlyData(appliedLaborCostByYearMonth, 'value', avgAppliedLaborCostRat);
        const beforeRatSalesManagementCostByMonth = createBeforeRationalizationMonthlyData(salesManagementCostByYearMonth, 'value', avgSalesManagementCostRat);
        const beforeRatScrapCostByMonth = scrapCostByMonth.map((val, index) => {
          if (index >= eolMonthIndexRat) {
            return new Decimal(0);
          } else if (index < rationalizationStartMonthIndex) {
            return val;
          } else {
            return avgScrapCostRat;
          }
        });
        const beforeRatDomesticSgaCostByMonth = domesticSgaCostByMonth.map((val, index) => {
          if (index >= eolMonthIndexRat) {
            return new Decimal(0);
          } else if (index < rationalizationStartMonthIndex) {
            return val;
          } else {
            return avgDomesticSgaCostRat;
          }
        });
        const beforeRatDevCostByMonth = createBeforeRationalizationMonthlyData(devCostByMonth, 'value', avgDevCostRat);
        const beforeRatRoyaltyByMonth = royaltyByMonthNew.map((val, index) => {
          if (index >= eolMonthIndexRat) {
            return new Decimal(0);
          } else if (index < rationalizationStartMonthIndex) {
            return val;
          } else {
            return avgRoyaltyRat;
          }
        });
        const beforeRatOtherCostByMonth = createBeforeRationalizationMonthlyData(otherCostByMonth, 'value', avgOtherCostRat);

        // 月次データを年度別に集計するヘルパー関数（convertMonthlyToYearlyと同じ丸め処理を適用）
        const aggregateByYearRat = (
          monthlyData: { year: number, month: number }[],
          getValue: (entry: any) => Decimal
        ): Decimal[] => {
          const yearlyTotals: { [fiscalYear: number]: Decimal } = {};
          monthlyData.forEach((entry, index) => {
            const currentDate = projectStartDateObj.add(index, 'month');
            const year = currentDate.year();
            const month = currentDate.month() + 1;
            const fiscalYear = month >= 4 ? year : year - 1;
            if (!yearlyTotals[fiscalYear]) {
              yearlyTotals[fiscalYear] = new Decimal(0);
            }
            yearlyTotals[fiscalYear] = yearlyTotals[fiscalYear].plus(getValue(entry));
          });
          // convertMonthlyToYearlyと同じ丸め処理を適用
          return Object.values(yearlyTotals).map(total => new Decimal(total.toFixed(decimalCurrency ? 3 : 0)));
        };

        // 「合理化前」の年度別データを計算
        const beforeRatTotalSalesByYearCalc = aggregateByYearRat(beforeRatTotalSalesByMonth, (e) => e.sales || new Decimal(0));
        const beforeRatMaterialCostByYearCalc = aggregateByYearRat(beforeRatMaterialCostByMonth, (e) => e.materialCost || new Decimal(0));
        const beforeRatOutsideCostByYearCalc = convertMonthlyToYearly(beforeRatOutsideCostByMonth, decimalCurrency);
        const beforeRatLaborCostByYearCalc = convertMonthlyToYearly(beforeRatLaborCostByMonth, decimalCurrency);
        const beforeRatDomesticLaborCostByYearCalc = convertMonthlyToYearly(beforeRatDomesticLaborCostByMonth, decimalCurrency);
        const beforeRatDomesticFactoryExpensesByYearCalc = convertMonthlyToYearly(beforeRatDomesticFactoryExpensesByMonth, decimalCurrency);
        // 国内工場経費内訳（5項目）の年度別データ
        const beforeRatDomesticOverHeadCostByYearCalc = aggregateByYearRat(beforeRatDomesticOverHeadCostByMonth, (e) => e.value || new Decimal(0));
        const beforeRatDomesticDepreciationByYearCalc = aggregateByYearRat(beforeRatDomesticDepreciationByMonth, (e) => e.value || new Decimal(0));
        const beforeRatDomesticInvestmentDepreciationByYearCalc = aggregateByYearRat(beforeRatDomesticInvestmentDepreciationByMonth, (e) => e.value || new Decimal(0));
        const beforeRatDomesticFactoryBuildingDepreciationByYearCalc = aggregateByYearRat(beforeRatDomesticFactoryBuildingDepreciationByMonth, (e) => e.value || new Decimal(0));
        const beforeRatDomesticOtherDepreciationByYearCalc = aggregateByYearRat(beforeRatDomesticOtherDepreciationByMonth, (e) => e.value || new Decimal(0));
        const beforeRatOverheadCostByYearCalc = aggregateByYearRat(beforeRatOverheadCostByMonth, (e) => e.value || new Decimal(0));
        const beforeRatEquipmentDepreciationByYearCalc = aggregateByYearRat(beforeRatEquipmentDepreciationByMonth, (e) => e.value || new Decimal(0));
        const beforeRatFactoryCostByYearCalc = aggregateByYearRat(beforeRatFactoryCostByMonth, (e) => e.value || new Decimal(0));
        const beforeRatPastEquipmentCostByYearCalc = aggregateByYearRat(beforeRatPastEquipmentCostByMonth, (e) => e.value || new Decimal(0));
        const beforeRatOtherEquipmentCostByYearCalc = aggregateByYearRat(beforeRatOtherEquipmentCostByMonth, (e) => e.value || new Decimal(0));
        const beforeRatAppliedLaborCostByYearCalc = aggregateByYearRat(beforeRatAppliedLaborCostByMonth, (e) => e.value || new Decimal(0));
        const beforeRatSalesManagementCostByYearCalc = aggregateByYearRat(beforeRatSalesManagementCostByMonth, (e) => e.value || new Decimal(0));

        // 減価償却費合計 = 投資対象設備 + 工場建物 + 過去PJの投資対象設備 + その他の設備
        const beforeRatDepreciationByYearCalc = beforeRatEquipmentDepreciationByYearCalc.map((val, i) =>
          val.plus(beforeRatFactoryCostByYearCalc[i] || new Decimal(0)).plus(beforeRatPastEquipmentCostByYearCalc[i] || new Decimal(0)).plus(beforeRatOtherEquipmentCostByYearCalc[i] || new Decimal(0))
        );

        // 工場経費 = 償却費以外の工場経費 + 減価償却費
        const beforeRatFactoryExpensesByYearCalc = beforeRatOverheadCostByYearCalc.map((val, i) =>
          val.plus(beforeRatDepreciationByYearCalc[i] || new Decimal(0))
        );

        // 仕損費（3ヶ月平均を適用）
        const beforeRatScrapCostByYearCalc = convertMonthlyToYearly(beforeRatScrapCostByMonth, decimalCurrency);

        // 売上原価 = 材料費 + 外注加工費 + 生産工場労務費 + 国内工場労務費 + 生産工場経費 + 国内工場経費 + 仕損費
        const beforeRatSalesCostByYearCalc = beforeRatMaterialCostByYearCalc.map((val, i) =>
          val
            .plus(beforeRatOutsideCostByYearCalc[i] || new Decimal(0))
            .plus(beforeRatLaborCostByYearCalc[i] || new Decimal(0))
            .plus(beforeRatDomesticLaborCostByYearCalc[i] || new Decimal(0))
            .plus(beforeRatFactoryExpensesByYearCalc[i] || new Decimal(0))
            .plus(beforeRatDomesticFactoryExpensesByYearCalc[i] || new Decimal(0))
            .plus(beforeRatScrapCostByYearCalc[i] || new Decimal(0))
        );

        // 国内工場販管費・開発費・ロイヤリティ・その他の経費（3ヶ月平均を適用）
        const beforeRatDomesticSgaCostByYearCalc = convertMonthlyToYearly(beforeRatDomesticSgaCostByMonth, decimalCurrency);
        const beforeRatDevCostByYearCalc = aggregateByYearRat(beforeRatDevCostByMonth, (e) => e.value || new Decimal(0));
        const beforeRatRoyaltyByYearCalc = convertMonthlyToYearly(beforeRatRoyaltyByMonth, decimalCurrency);
        const beforeRatOtherCostByYearCalc = aggregateByYearRat(beforeRatOtherCostByMonth, (e) => e.value || new Decimal(0));

        // 販売費及び一般管理費 = 生産工場販管費 + 国内工場販管費 + 本社・販社販管費 + 開発費 + ロイヤリティ
        const beforeRatSGAExpensesByYearCalc = beforeRatAppliedLaborCostByYearCalc.map((val, i) =>
          val
            .plus(beforeRatDomesticSgaCostByYearCalc[i] || new Decimal(0))
            .plus(beforeRatSalesManagementCostByYearCalc[i] || new Decimal(0))
            .plus(beforeRatDevCostByYearCalc[i] || new Decimal(0))
            .plus(beforeRatRoyaltyByYearCalc[i] || new Decimal(0))
        );

        // 営業利益 = 売上合計 - 売上原価 - 販売費及び一般管理費 - その他の経費
        const beforeRatProfitByYearCalc = beforeRatTotalSalesByYearCalc.map((val, i) =>
          val
            .minus(beforeRatSalesCostByYearCalc[i] || new Decimal(0))
            .minus(beforeRatSGAExpensesByYearCalc[i] || new Decimal(0))
            .minus(beforeRatOtherCostByYearCalc[i] || new Decimal(0))
        );

        // 営業利益率 = 営業利益 / 売上合計 * 100
        const beforeRatProfitRateByYearCalc = beforeRatTotalSalesByYearCalc.map((val, i) =>
          val.isZero() ? new Decimal(0) : beforeRatProfitByYearCalc[i].div(val).times(100)
        );

        // 製品売上は売上合計と同じ比率で調整（簡易計算）
        const beforeRatProductSalesByYearCalc = beforeRatTotalSalesByYearCalc.map((val, i) => {
          const originalTotal = totalSalesMoldDevByYear[i] || new Decimal(1);
          const originalProduct = productSalesByYear[i] || new Decimal(0);
          return originalTotal.isZero() ? new Decimal(0) : val.times(originalProduct).div(originalTotal);
        });

        // PJ単位売上は入力値と同じ（合理化前も合理化後も同じ）
        // ステート変数ではなくローカル変数を使用（Reactのステート更新は非同期のため）
        const beforeRatMoldSalesByYearCalc = sumMoldSales;
        const beforeRatDevSalesByYearCalc = sumDevSales;
        const beforeRatEquipmentSalesByYearCalc = sumEquipmentSales;

        // state更新
        setBeforeRationalizationTotalSalesByYear(beforeRatTotalSalesByYearCalc);
        setBeforeRationalizationProductSalesByYear(beforeRatProductSalesByYearCalc);
        setBeforeRationalizationMoldSalesByYear(beforeRatMoldSalesByYearCalc);
        setBeforeRationalizationDevSalesByYear(beforeRatDevSalesByYearCalc);
        setBeforeRationalizationEquipmentSalesByYear(beforeRatEquipmentSalesByYearCalc);
        setBeforeRationalizationSalesCostByYear(beforeRatSalesCostByYearCalc);
        setBeforeRationalizationTotalMaterialCostByYear(beforeRatMaterialCostByYearCalc);
        setBeforeRationalizationOutsideCostByYear(beforeRatOutsideCostByYearCalc);
        setBeforeRationalizationLaborCostByYear(beforeRatLaborCostByYearCalc);
        setBeforeRationalizationDomesticLaborCostByYear(beforeRatDomesticLaborCostByYearCalc);
        setBeforeRationalizationFactoryExpensesByYear(beforeRatFactoryExpensesByYearCalc);
        setBeforeRationalizationDomesticFactoryExpensesByYear(beforeRatDomesticFactoryExpensesByYearCalc);
        setBeforeRationalizationDomesticOverHeadCostByYear(beforeRatDomesticOverHeadCostByYearCalc);
        setBeforeRationalizationDomesticDepreciationByYear(beforeRatDomesticDepreciationByYearCalc);
        setBeforeRationalizationDomesticInvestmentDepreciationByYear(beforeRatDomesticInvestmentDepreciationByYearCalc);
        setBeforeRationalizationDomesticFactoryBuildingDepreciationByYear(beforeRatDomesticFactoryBuildingDepreciationByYearCalc);
        setBeforeRationalizationDomesticOtherDepreciationByYear(beforeRatDomesticOtherDepreciationByYearCalc);
        setBeforeRationalizationOverHeadCostByYear(beforeRatOverheadCostByYearCalc);
        setBeforeRationalizationDepreciationByYear(beforeRatDepreciationByYearCalc);
        setBeforeRationalizationEquipmentDepreciationByYear(beforeRatEquipmentDepreciationByYearCalc);
        setBeforeRationalizationFactoryCostByYear(beforeRatFactoryCostByYearCalc);
        setBeforeRationalizationPastEquipmentCostByYear(beforeRatPastEquipmentCostByYearCalc);
        setBeforeRationalizationOtherEquipmentCostByYear(beforeRatOtherEquipmentCostByYearCalc);
        setBeforeRationalizationScrapCostByYear(beforeRatScrapCostByYearCalc);
        setBeforeRationalizationSGAExpensesByYear(beforeRatSGAExpensesByYearCalc);
        setBeforeRationalizationAppliedLaborCostByYear(beforeRatAppliedLaborCostByYearCalc);
        setBeforeRationalizationDomesticSgaCostByYear(beforeRatDomesticSgaCostByYearCalc);
        setBeforeRationalizationSalesManagementCostByYear(beforeRatSalesManagementCostByYearCalc);
        setBeforeRationalizationDevCostByYear(beforeRatDevCostByYearCalc);
        setBeforeRationalizationRoyaltyByYear(beforeRatRoyaltyByYearCalc);
        setBeforeRationalizationOtherCostByYear(beforeRatOtherCostByYearCalc);
        setBeforeRationalizationProfitByYear(beforeRatProfitByYearCalc);
        setBeforeRationalizationProfitRateByYear(beforeRatProfitRateByYearCalc);

        // 合理化前の客先指定部品材料費（月別）を計算
        const avgCustomerSpecifiedPartsCostRat = customerSpecifiedPartsCostByMonth.length > 0
          ? customerSpecifiedPartsCostByMonth.slice(avgStartIndexRat, avgEndIndexRat).reduce((sum, val) => sum.plus(val), new Decimal(0)).div(Math.max(1, avgEndIndexRat - avgStartIndexRat))
          : new Decimal(0);

        const beforeRatCustomerSpecifiedPartsCostByMonth = customerSpecifiedPartsCostByMonth.map((val, index) => {
          if (index >= eolMonthIndexRat) {
            return new Decimal(0);
          } else if (index < rationalizationStartMonthIndex) {
            return val;
          } else {
            return avgCustomerSpecifiedPartsCostRat;
          }
        });

        // 合理化前の客先指定部品材料費（年度別）に集計
        const beforeRatCustomerSpecifiedPartsCostByYear = convertMonthlyToYearly(beforeRatCustomerSpecifiedPartsCostByMonth, decimalCurrency);

        // 合理化前の実質的営業利益率
        const beforeRatRealSalesByYear = beforeRatTotalSalesByYearCalc.map((sales, index) =>
          sales.minus(beforeRatCustomerSpecifiedPartsCostByYear[index] || new Decimal(0))
        );

        const beforeRatRealOperatingProfitRateByYearCalc = beforeRatRealSalesByYear.map((realSales, index) => {
          if (realSales.isZero()) {
            return new Decimal(0);
          }
          return (beforeRatProfitByYearCalc[index] || new Decimal(0)).div(realSales).times(100);
        });
        setBeforeRationalizationRealOperatingProfitRateByYear(beforeRatRealOperatingProfitRateByYearCalc);
        setBeforeRationalizationCustomerSpecifiedPartsCostByYear(beforeRatCustomerSpecifiedPartsCostByYear);

        // 合理化前データのJPY版を設定（表示通貨の月次データから再計算して二重変換を回避）

        // JPY版のYearMonthオブジェクトを作成（calcThreeMonthAverageRat用）
        const appliedLaborCostByYearMonthDisplayRat = convertToYearMonthObject(yearMonthListByElapsedYears, appliedLaborCostByMonthDisplay);
        const salesManagementCostByYearMonthDisplayRat = convertToYearMonthObject(yearMonthListByElapsedYears, salesManagementCostByMonthDisplay);

        // 3ヶ月平均を計算するヘルパー関数（JPY版）
        const calcThreeMonthAverageDisplayRat = (
          monthlyData: { year: number, month: number, sales?: Decimal, value?: Decimal, materialCost?: Decimal }[],
          fieldName: 'sales' | 'value' | 'materialCost'
        ): Decimal => {
          let sum = new Decimal(0);
          let count = 0;
          for (let i = avgStartIndexRat; i < avgEndIndexRat && i < monthlyData.length; i++) {
            const val = monthlyData[i]?.[fieldName];
            if (val) {
              sum = sum.plus(val);
              count++;
            }
          }
          return count > 0 ? sum.div(count) : new Decimal(0);
        };

        // 「合理化前」の月次データを作成するヘルパー関数（JPY版）
        const createBeforeRatMonthlyDataDisplay = <T extends { year: number, month: number }>(
          monthlyData: T[],
          fieldName: keyof T,
          avgValue: Decimal
        ): T[] => {
          return monthlyData.map((entry, index) => {
            if (index >= eolMonthIndexRat) {
              return { ...entry, [fieldName]: new Decimal(0) };
            } else if (index < rationalizationStartMonthIndex) {
              return { ...entry };
            } else {
              return { ...entry, [fieldName]: avgValue };
            }
          });
        };

        // 各項目の3ヶ月平均を計算（JPY版）
        const avgTotalSalesDisplayRat = calcThreeMonthAverageDisplayRat(totalSalesMoldDevByMonthDisplay, 'sales');
        const avgMaterialCostDisplayRat = calcThreeMonthAverageDisplayRat(totalMaterialCostByMonthDisplay, 'materialCost');
        const avgOutsideCostDisplayRat = outsideCostByMonthDisplay.length > 0
          ? outsideCostByMonthDisplay.slice(avgStartIndexRat, avgEndIndexRat).reduce((sum, val) => sum.plus(val), new Decimal(0)).div(Math.max(1, avgEndIndexRat - avgStartIndexRat))
          : new Decimal(0);
        const avgLaborCostDisplayRat = laborCostByMonthDisplay.length > 0
          ? laborCostByMonthDisplay.slice(avgStartIndexRat, avgEndIndexRat).reduce((sum, val) => sum.plus(val), new Decimal(0)).div(Math.max(1, avgEndIndexRat - avgStartIndexRat))
          : new Decimal(0);
        const avgDomesticLaborCostDisplayRat = domesticLaborCostByMonthDisplay.length > 0
          ? domesticLaborCostByMonthDisplay.slice(avgStartIndexRat, avgEndIndexRat).reduce((sum, val) => sum.plus(val), new Decimal(0)).div(Math.max(1, avgEndIndexRat - avgStartIndexRat))
          : new Decimal(0);
        const avgDomesticFactoryExpensesDisplayRat = domesticFactoryExpensesByYearMonthDisplay.length > 0
          ? domesticFactoryExpensesByYearMonthDisplay.slice(avgStartIndexRat, avgEndIndexRat).reduce((sum, val) => sum.plus(val.value || new Decimal(0)), new Decimal(0)).div(Math.max(1, avgEndIndexRat - avgStartIndexRat))
          : new Decimal(0);
        const avgDomesticOverHeadCostDisplayRat = calcThreeMonthAverageDisplayRat(domesticOverHeadCostByYearMonthDisplay, 'value');
        const avgDomesticDepreciationDisplayRat = calcThreeMonthAverageDisplayRat(domesticDepreciationByMonthDisplay, 'value');
        const avgDomesticInvestmentDepreciationDisplayRat = calcThreeMonthAverageDisplayRat(domesticInvestmentDepreciationByYearMonthDisplay, 'value');
        const avgDomesticFactoryBuildingDepreciationDisplayRat = calcThreeMonthAverageDisplayRat(domesticFactoryBuildingDepreciationByYearMonthDisplay, 'value');
        const avgDomesticOtherDepreciationDisplayRat = calcThreeMonthAverageDisplayRat(domesticOtherDepreciationByYearMonthDisplay, 'value');
        const avgOverheadCostDisplayRat = calcThreeMonthAverageDisplayRat(overHeadCostByYearMonthDisplay, 'value');
        const avgEquipmentDepreciationDisplayRat = calcThreeMonthAverageDisplayRat(equipmentDepreciationByMonthDisplay, 'value');
        const avgFactoryCostDisplayRat = calcThreeMonthAverageDisplayRat(factoryCostByMonthDisplay, 'value');
        const avgPastEquipmentCostDisplayRat = calcThreeMonthAverageDisplayRat(pastEquipmentCostByMonthDisplay, 'value');
        const avgOtherEquipmentCostDisplayRat = calcThreeMonthAverageDisplayRat(otherEquipmentCostByMonthDisplay, 'value');
        const avgAppliedLaborCostDisplayRat = calcThreeMonthAverageDisplayRat(appliedLaborCostByYearMonthDisplayRat, 'value');
        const avgSalesManagementCostDisplayRat = calcThreeMonthAverageDisplayRat(salesManagementCostByYearMonthDisplayRat, 'value');
        const avgScrapCostDisplayRat = scrapCostByMonthDisplay.length > 0
          ? scrapCostByMonthDisplay.slice(avgStartIndexRat, avgEndIndexRat).reduce((sum, val) => sum.plus(val), new Decimal(0)).div(Math.max(1, avgEndIndexRat - avgStartIndexRat))
          : new Decimal(0);
        const avgDomesticSgaCostDisplayRat = domesticSgaCostByMonthDisplay.length > 0
          ? domesticSgaCostByMonthDisplay.slice(avgStartIndexRat, avgEndIndexRat).reduce((sum, val) => sum.plus(val), new Decimal(0)).div(Math.max(1, avgEndIndexRat - avgStartIndexRat))
          : new Decimal(0);
        const avgDevCostDisplayRat = devCostByMonth.length > 0
          ? calcThreeMonthAverageDisplayRat(devCostByMonth.map((entry) => ({
            ...entry,
            value: convertWithRate(entry.value, localCurrency, displayCurrencyName, toYearMonth(entry.year, entry.month)).toDecimalPlaces(displayCurrencyDecimal ? 2 : 0)
          })), 'value')
          : new Decimal(0);
        const avgRoyaltyDisplayRat = royaltyByMonthDisplay.length > 0
          ? royaltyByMonthDisplay.slice(avgStartIndexRat, avgEndIndexRat).reduce((sum, val) => sum.plus(val), new Decimal(0)).div(Math.max(1, avgEndIndexRat - avgStartIndexRat))
          : new Decimal(0);
        const avgOtherCostDisplayRat = otherCostByMonth.length > 0
          ? calcThreeMonthAverageDisplayRat(otherCostByMonth.map((entry) => ({
            ...entry,
            value: convertWithRate(entry.value, localCurrency, displayCurrencyName, toYearMonth(entry.year, entry.month)).toDecimalPlaces(displayCurrencyDecimal ? 2 : 0)
          })), 'value')
          : new Decimal(0);

        // 「合理化前」の月次データを作成（JPY版）
        const beforeRatTotalSalesByMonthDisplay = createBeforeRatMonthlyDataDisplay(totalSalesMoldDevByMonthDisplay, 'sales', avgTotalSalesDisplayRat);
        const beforeRatMaterialCostByMonthDisplay = createBeforeRatMonthlyDataDisplay(totalMaterialCostByMonthDisplay, 'materialCost', avgMaterialCostDisplayRat);
        const beforeRatOutsideCostByMonthDisplayCalc = outsideCostByMonthDisplay.map((val, index) => {
          if (index >= eolMonthIndexRat) return new Decimal(0);
          else if (index < rationalizationStartMonthIndex) return val;
          else return avgOutsideCostDisplayRat;
        });
        const beforeRatLaborCostByMonthDisplayCalc = laborCostByMonthDisplay.map((val, index) => {
          if (index >= eolMonthIndexRat) return new Decimal(0);
          else if (index < rationalizationStartMonthIndex) return val;
          else return avgLaborCostDisplayRat;
        });
        const beforeRatDomesticLaborCostByMonthDisplayCalc = domesticLaborCostByMonthDisplay.map((val, index) => {
          if (index >= eolMonthIndexRat) return new Decimal(0);
          else if (index < rationalizationStartMonthIndex) return val;
          else return avgDomesticLaborCostDisplayRat;
        });
        const beforeRatDomesticFactoryExpensesByMonthDisplayCalc = domesticFactoryExpensesByYearMonthDisplay.map((entry, index) => {
          if (index >= eolMonthIndexRat) return new Decimal(0);
          else if (index < rationalizationStartMonthIndex) return entry.value || new Decimal(0);
          else return avgDomesticFactoryExpensesDisplayRat;
        });
        const beforeRatDomesticOverHeadCostByMonthDisplayCalc = createBeforeRatMonthlyDataDisplay(domesticOverHeadCostByYearMonthDisplay, 'value', avgDomesticOverHeadCostDisplayRat);
        const beforeRatDomesticDepreciationByMonthDisplayCalc = createBeforeRatMonthlyDataDisplay(domesticDepreciationByMonthDisplay, 'value', avgDomesticDepreciationDisplayRat);
        const beforeRatDomesticInvestmentDepreciationByMonthDisplayCalc = createBeforeRatMonthlyDataDisplay(domesticInvestmentDepreciationByYearMonthDisplay, 'value', avgDomesticInvestmentDepreciationDisplayRat);
        const beforeRatDomesticFactoryBuildingDepreciationByMonthDisplayCalc = createBeforeRatMonthlyDataDisplay(domesticFactoryBuildingDepreciationByYearMonthDisplay, 'value', avgDomesticFactoryBuildingDepreciationDisplayRat);
        const beforeRatDomesticOtherDepreciationByMonthDisplayCalc = createBeforeRatMonthlyDataDisplay(domesticOtherDepreciationByYearMonthDisplay, 'value', avgDomesticOtherDepreciationDisplayRat);
        const beforeRatOverheadCostByMonthDisplayCalc = createBeforeRatMonthlyDataDisplay(overHeadCostByYearMonthDisplay, 'value', avgOverheadCostDisplayRat);
        const beforeRatEquipmentDepreciationByMonthDisplayCalc = createBeforeRatMonthlyDataDisplay(equipmentDepreciationByMonthDisplay, 'value', avgEquipmentDepreciationDisplayRat);
        const beforeRatFactoryCostByMonthDisplayCalc = createBeforeRatMonthlyDataDisplay(factoryCostByMonthDisplay, 'value', avgFactoryCostDisplayRat);
        const beforeRatPastEquipmentCostByMonthDisplayCalc = createBeforeRatMonthlyDataDisplay(pastEquipmentCostByMonthDisplay, 'value', avgPastEquipmentCostDisplayRat);
        const beforeRatOtherEquipmentCostByMonthDisplayCalc = createBeforeRatMonthlyDataDisplay(otherEquipmentCostByMonthDisplay, 'value', avgOtherEquipmentCostDisplayRat);
        const beforeRatAppliedLaborCostByMonthDisplayCalc = createBeforeRatMonthlyDataDisplay(appliedLaborCostByYearMonthDisplayRat, 'value', avgAppliedLaborCostDisplayRat);
        const beforeRatSalesManagementCostByMonthDisplayCalc = createBeforeRatMonthlyDataDisplay(salesManagementCostByYearMonthDisplayRat, 'value', avgSalesManagementCostDisplayRat);
        const beforeRatScrapCostByMonthDisplayCalc = scrapCostByMonthDisplay.map((val, index) => {
          if (index >= eolMonthIndexRat) return new Decimal(0);
          else if (index < rationalizationStartMonthIndex) return val;
          else return avgScrapCostDisplayRat;
        });
        const beforeRatDomesticSgaCostByMonthDisplayCalc = domesticSgaCostByMonthDisplay.map((val, index) => {
          if (index >= eolMonthIndexRat) return new Decimal(0);
          else if (index < rationalizationStartMonthIndex) return val;
          else return avgDomesticSgaCostDisplayRat;
        });
        const beforeRatDevCostByMonthDisplayCalc = devCostByMonth.map((entry, index) => {
          const displayValue = convertWithRate(entry.value, localCurrency, displayCurrencyName, toYearMonth(entry.year, entry.month)).toDecimalPlaces(displayCurrencyDecimal ? 2 : 0);
          if (index >= eolMonthIndexRat) return { ...entry, value: new Decimal(0) };
          else if (index < rationalizationStartMonthIndex) return { ...entry, value: displayValue };
          else return { ...entry, value: avgDevCostDisplayRat };
        });
        const beforeRatRoyaltyByMonthDisplayCalc = royaltyByMonthDisplay.map((val, index) => {
          if (index >= eolMonthIndexRat) return new Decimal(0);
          else if (index < rationalizationStartMonthIndex) return val;
          else return avgRoyaltyDisplayRat;
        });
        const beforeRatOtherCostByMonthDisplayCalc = otherCostByMonth.map((entry, index) => {
          const displayValue = convertWithRate(entry.value, localCurrency, displayCurrencyName, toYearMonth(entry.year, entry.month)).toDecimalPlaces(displayCurrencyDecimal ? 2 : 0);
          if (index >= eolMonthIndexRat) return { ...entry, value: new Decimal(0) };
          else if (index < rationalizationStartMonthIndex) return { ...entry, value: displayValue };
          else return { ...entry, value: avgOtherCostDisplayRat };
        });

        // 月次データを年度別に集計するヘルパー関数（JPY版、displayCurrencyDecimalを使用）
        const aggregateByYearDisplayRat = (
          monthlyData: { year: number, month: number }[],
          getValue: (entry: any) => Decimal
        ): Decimal[] => {
          const yearlyTotals: { [fiscalYear: number]: Decimal } = {};
          monthlyData.forEach((entry, index) => {
            const currentDate = projectStartDateObj.add(index, 'month');
            const year = currentDate.year();
            const month = currentDate.month() + 1;
            const fiscalYear = month >= 4 ? year : year - 1;
            if (!yearlyTotals[fiscalYear]) {
              yearlyTotals[fiscalYear] = new Decimal(0);
            }
            yearlyTotals[fiscalYear] = yearlyTotals[fiscalYear].plus(getValue(entry));
          });
          // 丸めなし、フル精度で返す（年度ごとの個別丸めは「合計」列の誤差原因となるため撤廃、No425）
          return Object.values(yearlyTotals);
        };

        // 「合理化前」の年度別データを計算（JPY版）
        const beforeRatTotalSalesByYearDisplay = aggregateByYearDisplayRat(beforeRatTotalSalesByMonthDisplay, (e) => e.sales || new Decimal(0));
        const beforeRatMaterialCostByYearDisplay = aggregateByYearDisplayRat(beforeRatMaterialCostByMonthDisplay, (e) => e.materialCost || new Decimal(0));
        const beforeRatOutsideCostByYearDisplay = convertMonthlyToYearly(beforeRatOutsideCostByMonthDisplayCalc, displayCurrencyDecimal);
        const beforeRatLaborCostByYearDisplay = convertMonthlyToYearly(beforeRatLaborCostByMonthDisplayCalc, displayCurrencyDecimal);
        const beforeRatDomesticLaborCostByYearDisplay = convertMonthlyToYearly(beforeRatDomesticLaborCostByMonthDisplayCalc, displayCurrencyDecimal);
        const beforeRatDomesticFactoryExpensesByYearDisplay = convertMonthlyToYearly(beforeRatDomesticFactoryExpensesByMonthDisplayCalc, displayCurrencyDecimal);
        const beforeRatDomesticOverHeadCostByYearDisplay = aggregateByYearDisplayRat(beforeRatDomesticOverHeadCostByMonthDisplayCalc, (e) => e.value || new Decimal(0));
        const beforeRatDomesticDepreciationByYearDisplay = aggregateByYearDisplayRat(beforeRatDomesticDepreciationByMonthDisplayCalc, (e) => e.value || new Decimal(0));
        const beforeRatDomesticInvestmentDepreciationByYearDisplay = aggregateByYearDisplayRat(beforeRatDomesticInvestmentDepreciationByMonthDisplayCalc, (e) => e.value || new Decimal(0));
        const beforeRatDomesticFactoryBuildingDepreciationByYearDisplay = aggregateByYearDisplayRat(beforeRatDomesticFactoryBuildingDepreciationByMonthDisplayCalc, (e) => e.value || new Decimal(0));
        const beforeRatDomesticOtherDepreciationByYearDisplay = aggregateByYearDisplayRat(beforeRatDomesticOtherDepreciationByMonthDisplayCalc, (e) => e.value || new Decimal(0));
        const beforeRatOverheadCostByYearDisplay = aggregateByYearDisplayRat(beforeRatOverheadCostByMonthDisplayCalc, (e) => e.value || new Decimal(0));
        const beforeRatEquipmentDepreciationByYearDisplay = aggregateByYearDisplayRat(beforeRatEquipmentDepreciationByMonthDisplayCalc, (e) => e.value || new Decimal(0));
        const beforeRatFactoryCostByYearDisplay = aggregateByYearDisplayRat(beforeRatFactoryCostByMonthDisplayCalc, (e) => e.value || new Decimal(0));
        const beforeRatPastEquipmentCostByYearDisplay = aggregateByYearDisplayRat(beforeRatPastEquipmentCostByMonthDisplayCalc, (e) => e.value || new Decimal(0));
        const beforeRatOtherEquipmentCostByYearDisplay = aggregateByYearDisplayRat(beforeRatOtherEquipmentCostByMonthDisplayCalc, (e) => e.value || new Decimal(0));
        const beforeRatAppliedLaborCostByYearDisplay = aggregateByYearDisplayRat(beforeRatAppliedLaborCostByMonthDisplayCalc, (e) => e.value || new Decimal(0));
        const beforeRatSalesManagementCostByYearDisplay = aggregateByYearDisplayRat(beforeRatSalesManagementCostByMonthDisplayCalc, (e) => e.value || new Decimal(0));
        const beforeRatScrapCostByYearDisplay = convertMonthlyToYearly(beforeRatScrapCostByMonthDisplayCalc, displayCurrencyDecimal);
        const beforeRatDomesticSgaCostByYearDisplay = convertMonthlyToYearly(beforeRatDomesticSgaCostByMonthDisplayCalc, displayCurrencyDecimal);
        const beforeRatDevCostByYearDisplay = aggregateByYearDisplayRat(beforeRatDevCostByMonthDisplayCalc, (e) => e.value || new Decimal(0));
        const beforeRatRoyaltyByYearDisplay = convertMonthlyToYearly(beforeRatRoyaltyByMonthDisplayCalc, displayCurrencyDecimal);
        const beforeRatOtherCostByYearDisplay = aggregateByYearDisplayRat(beforeRatOtherCostByMonthDisplayCalc, (e) => e.value || new Decimal(0));

        // 減価償却費合計（JPY版）
        const beforeRatDepreciationByYearDisplay = beforeRatEquipmentDepreciationByYearDisplay.map((val, i) =>
          val.plus(beforeRatFactoryCostByYearDisplay[i] || new Decimal(0)).plus(beforeRatPastEquipmentCostByYearDisplay[i] || new Decimal(0)).plus(beforeRatOtherEquipmentCostByYearDisplay[i] || new Decimal(0))
        );

        // 工場経費（JPY版）
        const beforeRatFactoryExpensesByYearDisplay = beforeRatOverheadCostByYearDisplay.map((val, i) =>
          val.plus(beforeRatDepreciationByYearDisplay[i] || new Decimal(0))
        );

        // 売上原価（JPY版）
        const beforeRatSalesCostByYearDisplay = beforeRatMaterialCostByYearDisplay.map((val, i) =>
          val
            .plus(beforeRatOutsideCostByYearDisplay[i] || new Decimal(0))
            .plus(beforeRatLaborCostByYearDisplay[i] || new Decimal(0))
            .plus(beforeRatDomesticLaborCostByYearDisplay[i] || new Decimal(0))
            .plus(beforeRatFactoryExpensesByYearDisplay[i] || new Decimal(0))
            .plus(beforeRatDomesticFactoryExpensesByYearDisplay[i] || new Decimal(0))
            .plus(beforeRatScrapCostByYearDisplay[i] || new Decimal(0))
        );

        // 販売費及び一般管理費（JPY版）
        const beforeRatSGAExpensesByYearDisplay = beforeRatAppliedLaborCostByYearDisplay.map((val, i) =>
          val
            .plus(beforeRatDomesticSgaCostByYearDisplay[i] || new Decimal(0))
            .plus(beforeRatSalesManagementCostByYearDisplay[i] || new Decimal(0))
            .plus(beforeRatDevCostByYearDisplay[i] || new Decimal(0))
            .plus(beforeRatRoyaltyByYearDisplay[i] || new Decimal(0))
        );

        // 営業利益（JPY版）
        const beforeRatProfitByYearDisplay = beforeRatTotalSalesByYearDisplay.map((val, i) =>
          val
            .minus(beforeRatSalesCostByYearDisplay[i] || new Decimal(0))
            .minus(beforeRatSGAExpensesByYearDisplay[i] || new Decimal(0))
            .minus(beforeRatOtherCostByYearDisplay[i] || new Decimal(0))
        );

        // 営業利益率（JPY版）
        const beforeRatProfitRateByYearDisplay = beforeRatTotalSalesByYearDisplay.map((val, i) =>
          val.isZero() ? new Decimal(0) : beforeRatProfitByYearDisplay[i].div(val).times(100)
        );

        // 製品売上（JPY版）
        const beforeRatProductSalesByYearDisplay = beforeRatTotalSalesByYearDisplay.map((val, i) => {
          // 比率計算用: 表示通貨で計算済みの年度別値をそのまま使用（ローカル通貨版を変換・丸めし直すと誤差が生じるため、No425）
          const originalTotalDisplay = totalSalesMoldDevByYearDisplay[i] || new Decimal(1);
          const originalProductDisplay = productSalesByYearDisplay[i] || new Decimal(0);
          return originalTotalDisplay.isZero() ? new Decimal(0) : val.times(originalProductDisplay).div(originalTotalDisplay);
        });

        // PJ単位売上（JPY版）- 入力通貨から表示通貨に直接変換
        const projectMoldSalesByYearDisplayRat = calculateProjectSalesSum(projectMoldSalesByMonthDisplay, displayCurrencyDecimal);
        const projectDevSalesByYearDisplayRat = calculateProjectSalesSum(projectDevSalesByMonthDisplay, displayCurrencyDecimal);
        const projectEquipmentSalesByYearDisplayRat = calculateProjectSalesSum(projectEquipmentSalesByMonthDisplay, displayCurrencyDecimal);

        // 合理化前データのJPY版を設定
        setDisplayBeforeRationalizationTotalSalesByYear(beforeRatTotalSalesByYearDisplay);
        setDisplayBeforeRationalizationProductSalesByYear(beforeRatProductSalesByYearDisplay);
        setDisplayBeforeRationalizationMoldSalesByYear(projectMoldSalesByYearDisplayRat);
        setDisplayBeforeRationalizationDevSalesByYear(projectDevSalesByYearDisplayRat);
        setDisplayBeforeRationalizationEquipmentSalesByYear(projectEquipmentSalesByYearDisplayRat);
        setDisplayBeforeRationalizationSalesCostByYear(beforeRatSalesCostByYearDisplay);
        setDisplayBeforeRationalizationTotalMaterialCostByYear(beforeRatMaterialCostByYearDisplay);
        setDisplayBeforeRationalizationOutsideCostByYear(beforeRatOutsideCostByYearDisplay);
        setDisplayBeforeRationalizationLaborCostByYear(beforeRatLaborCostByYearDisplay);
        setDisplayBeforeRationalizationDomesticLaborCostByYear(beforeRatDomesticLaborCostByYearDisplay);
        setDisplayBeforeRationalizationFactoryExpensesByYear(beforeRatFactoryExpensesByYearDisplay);
        setDisplayBeforeRationalizationOverHeadCostByYear(beforeRatOverheadCostByYearDisplay);
        setDisplayBeforeRationalizationDepreciationByYear(beforeRatDepreciationByYearDisplay);
        setDisplayBeforeRationalizationEquipmentDepreciationByYear(beforeRatEquipmentDepreciationByYearDisplay);
        setDisplayBeforeRationalizationFactoryCostByYear(beforeRatFactoryCostByYearDisplay);
        setDisplayBeforeRationalizationPastEquipmentCostByYear(beforeRatPastEquipmentCostByYearDisplay);
        setDisplayBeforeRationalizationOtherEquipmentCostByYear(beforeRatOtherEquipmentCostByYearDisplay);
        setDisplayBeforeRationalizationDomesticFactoryExpensesByYear(beforeRatDomesticFactoryExpensesByYearDisplay);
        setDisplayBeforeRationalizationDomesticOverHeadCostByYear(beforeRatDomesticOverHeadCostByYearDisplay);
        setDisplayBeforeRationalizationDomesticDepreciationByYear(beforeRatDomesticDepreciationByYearDisplay);
        setDisplayBeforeRationalizationDomesticInvestmentDepreciationByYear(beforeRatDomesticInvestmentDepreciationByYearDisplay);
        setDisplayBeforeRationalizationDomesticFactoryBuildingDepreciationByYear(beforeRatDomesticFactoryBuildingDepreciationByYearDisplay);
        setDisplayBeforeRationalizationDomesticOtherDepreciationByYear(beforeRatDomesticOtherDepreciationByYearDisplay);
        setDisplayBeforeRationalizationScrapCostByYear(beforeRatScrapCostByYearDisplay);
        setDisplayBeforeRationalizationSGAExpensesByYear(beforeRatSGAExpensesByYearDisplay);
        setDisplayBeforeRationalizationAppliedLaborCostByYear(beforeRatAppliedLaborCostByYearDisplay);
        setDisplayBeforeRationalizationDomesticSgaCostByYear(beforeRatDomesticSgaCostByYearDisplay);
        setDisplayBeforeRationalizationSalesManagementCostByYear(beforeRatSalesManagementCostByYearDisplay);
        setDisplayBeforeRationalizationDevCostByYear(beforeRatDevCostByYearDisplay);
        setDisplayBeforeRationalizationRoyaltyByYear(beforeRatRoyaltyByYearDisplay);
        setDisplayBeforeRationalizationOtherCostByYear(beforeRatOtherCostByYearDisplay);
        setDisplayBeforeRationalizationProfitByYear(beforeRatProfitByYearDisplay);
        // 営業利益率は率なので、JPY版の値から計算した結果を使用
        setDisplayBeforeRationalizationProfitRateByYear(beforeRatProfitRateByYearDisplay);
        // 実質的営業利益率も率なので通貨変換不要（ローカル通貨版をそのまま使用）
        setDisplayBeforeRationalizationRealOperatingProfitRateByYear(beforeRatRealOperatingProfitRateByYearCalc);

        // 合理化前の月次データを計算
        // 製品売上は売上合計と同じ比率で調整
        const beforeRatProductSalesByMonth = beforeRatTotalSalesByMonth.map((entry, index) => {
          const originalTotal = totalSalesMoldDevByMonth[index]?.sales || new Decimal(1);
          const originalProduct = productSalesByMonth[index]?.sales || new Decimal(0);
          return originalTotal.isZero() ? new Decimal(0) : (entry.sales || new Decimal(0)).times(originalProduct).div(originalTotal);
        });

        // 金型売上、開発費売上、設備費売上は入力値と同じ（合理化前後で変わらない）
        // yearMonthListByElapsedYearsを使用してオブジェクト配列からDecimal[]に変換
        const beforeRatMoldSalesByMonth: Decimal[] = yearMonthListByElapsedYears.map(({ year, month }) => {
          const found = totalMoldSalesByMonth.find(e => e.year === year && e.month === month);
          return found?.sales || new Decimal(0);
        });
        const beforeRatDevSalesByMonth: Decimal[] = yearMonthListByElapsedYears.map(({ year, month }) => {
          const found = totalDevSalesByMonth.find(e => e.year === year && e.month === month);
          return found?.sales || new Decimal(0);
        });
        const beforeRatEquipmentSalesByMonth: Decimal[] = yearMonthListByElapsedYears.map(({ year, month }) => {
          const found = projectEquipmentSalesByMonth.find(e => e.year === year && e.month === month);
          return found?.sales || new Decimal(0);
        });

        // 減価償却費合計（月次）
        const beforeRatDepreciationByMonth = beforeRatEquipmentDepreciationByMonth.map((entry, index) =>
          (entry.value || new Decimal(0))
            .plus(beforeRatFactoryCostByMonth[index]?.value || new Decimal(0))
            .plus(beforeRatPastEquipmentCostByMonth[index]?.value || new Decimal(0))
            .plus(beforeRatOtherEquipmentCostByMonth[index]?.value || new Decimal(0))
        );

        // 工場経費（月次）= 償却費以外の工場経費 + 減価償却費
        const beforeRatFactoryExpensesByMonth = beforeRatOverheadCostByMonth.map((entry, index) =>
          (entry.value || new Decimal(0)).plus(beforeRatDepreciationByMonth[index] || new Decimal(0))
        );

        // 売上原価（月次）
        const beforeRatSalesCostByMonth = beforeRatMaterialCostByMonth.map((entry, index) =>
          (entry.materialCost || new Decimal(0))
            .plus(beforeRatOutsideCostByMonth[index] || new Decimal(0))
            .plus(beforeRatLaborCostByMonth[index] || new Decimal(0))
            .plus(beforeRatDomesticLaborCostByMonth[index] || new Decimal(0))
            .plus(beforeRatFactoryExpensesByMonth[index] || new Decimal(0))
            .plus(beforeRatDomesticFactoryExpensesByMonth[index] || new Decimal(0))
            .plus(beforeRatScrapCostByMonth[index] || new Decimal(0))
        );

        // 販売費及び一般管理費（月次）
        const beforeRatSGAExpensesByMonth = beforeRatAppliedLaborCostByMonth.map((entry, index) =>
          (entry.value || new Decimal(0))
            .plus(beforeRatDomesticSgaCostByMonth[index] || new Decimal(0))
            .plus(beforeRatSalesManagementCostByMonth[index]?.value || new Decimal(0))
            .plus(beforeRatDevCostByMonth[index]?.value || new Decimal(0))
            .plus(beforeRatRoyaltyByMonth[index] || new Decimal(0))
        );

        // 営業利益（月次）= 売上合計 - 売上原価 - 販管費 - その他経費
        const beforeRatProfitByMonth = beforeRatTotalSalesByMonth.map((entry, index) =>
          (entry.sales || new Decimal(0))
            .minus(beforeRatSalesCostByMonth[index] || new Decimal(0))
            .minus(beforeRatSGAExpensesByMonth[index] || new Decimal(0))
            .minus(beforeRatOtherCostByMonth[index]?.value || new Decimal(0))
        );

        // 合理化前の月次損益データをセット
        setBeforeRationalizationMonthlyProfitAndLossData({
          labels: monthlyLabels,
          totalSales: beforeRatTotalSalesByMonth.map(e => e.sales || new Decimal(0)),
          productSales: beforeRatProductSalesByMonth,
          moldSales: beforeRatMoldSalesByMonth,
          devSales: beforeRatDevSalesByMonth,
          equipmentSales: beforeRatEquipmentSalesByMonth,
          salesCost: beforeRatSalesCostByMonth,
          materialCost: beforeRatMaterialCostByMonth.map(e => e.materialCost || new Decimal(0)),
          outsideCost: beforeRatOutsideCostByMonth,
          laborCost: beforeRatLaborCostByMonth,
          domesticLaborCost: beforeRatDomesticLaborCostByMonth,
          factoryExpenses: beforeRatFactoryExpensesByMonth,
          overHeadCost: beforeRatOverheadCostByMonth.map(e => e.value || new Decimal(0)),
          depreciation: beforeRatDepreciationByMonth,
          equipmentDepreciation: beforeRatEquipmentDepreciationByMonth.map(e => e.value || new Decimal(0)),
          factoryCost: beforeRatFactoryCostByMonth.map(e => e.value || new Decimal(0)),
          pastEquipmentCost: beforeRatPastEquipmentCostByMonth.map(e => e.value || new Decimal(0)),
          otherEquipmentCost: beforeRatOtherEquipmentCostByMonth.map(e => e.value || new Decimal(0)),
          domesticFactoryExpenses: beforeRatDomesticFactoryExpensesByMonth,
          domesticOverHeadCost: beforeRatDomesticOverHeadCostByMonth.map(e => e.value || new Decimal(0)),
          domesticDepreciation: beforeRatDomesticDepreciationByMonth.map(e => e.value || new Decimal(0)),
          domesticInvestmentDepreciation: beforeRatDomesticInvestmentDepreciationByMonth.map(e => e.value || new Decimal(0)),
          domesticFactoryBuildingDepreciation: beforeRatDomesticFactoryBuildingDepreciationByMonth.map(e => e.value || new Decimal(0)),
          domesticOtherDepreciation: beforeRatDomesticOtherDepreciationByMonth.map(e => e.value || new Decimal(0)),
          scrapCost: beforeRatScrapCostByMonth,
          sGAExpenses: beforeRatSGAExpensesByMonth,
          appliedLaborCost: beforeRatAppliedLaborCostByMonth.map(e => e.value || new Decimal(0)),
          domesticSgaCost: beforeRatDomesticSgaCostByMonth,
          salesManagementCost: beforeRatSalesManagementCostByMonth.map(e => e.value || new Decimal(0)),
          devCost: beforeRatDevCostByMonth.map(e => e.value || new Decimal(0)),
          royalty: beforeRatRoyaltyByMonth,
          otherCost: beforeRatOtherCostByMonth.map(e => e.value || new Decimal(0)),
          profit: beforeRatProfitByMonth,
          customerSpecifiedPartsCost: beforeRatCustomerSpecifiedPartsCostByMonth,
        });

        // 合理化前の月次損益データ（JPY版）をセット - Excel出力での二重変換回避用
        // 製品売上の3ヶ月平均（JPY版）を計算
        const avgProductSalesDisplayRat = productSalesByMonthDisplay.length > 0
          ? productSalesByMonthDisplay.slice(avgStartIndexRat, avgEndIndexRat).reduce((sum, e) => sum.plus(e.sales || new Decimal(0)), new Decimal(0)).div(Math.max(1, avgEndIndexRat - avgStartIndexRat))
          : new Decimal(0);
        // 製品売上（JPY版）
        const beforeRatProductSalesByMonthDisplay = productSalesByMonthDisplay.map((entry, index) => {
          if (index >= eolMonthIndexRat) return new Decimal(0);
          else if (index < rationalizationStartMonthIndex) return entry.sales;
          else return avgProductSalesDisplayRat;
        });
        // PJ単位売上は合理化前も合理化後も同じ（JPY版を使用）
        const beforeRatMoldSalesByMonthDisplay = moldSalesByMonthValuesDisplay;
        const beforeRatDevSalesByMonthDisplay = devSalesByMonthValuesDisplay;
        const beforeRatEquipmentSalesByMonthDisplay = equipmentSalesByMonthValuesDisplay;
        // 生産工場経費（JPY版）
        const beforeRatFactoryExpensesByMonthDisplayCalc = beforeRatOverheadCostByMonthDisplayCalc.map((entry, i) => {
          const overheadVal = entry.value || new Decimal(0);
          const depVal = beforeRatEquipmentDepreciationByMonthDisplayCalc[i]?.value || new Decimal(0);
          const factoryVal = beforeRatFactoryCostByMonthDisplayCalc[i]?.value || new Decimal(0);
          const pastEquipVal = beforeRatPastEquipmentCostByMonthDisplayCalc[i]?.value || new Decimal(0);
          const otherEquipVal = beforeRatOtherEquipmentCostByMonthDisplayCalc[i]?.value || new Decimal(0);
          return overheadVal.plus(depVal).plus(factoryVal).plus(pastEquipVal).plus(otherEquipVal);
        });
        // 減価償却費（JPY版）
        const beforeRatDepreciationByMonthDisplayCalc = beforeRatEquipmentDepreciationByMonthDisplayCalc.map((entry, i) => {
          const equipDepVal = entry.value || new Decimal(0);
          const factoryVal = beforeRatFactoryCostByMonthDisplayCalc[i]?.value || new Decimal(0);
          const pastEquipVal = beforeRatPastEquipmentCostByMonthDisplayCalc[i]?.value || new Decimal(0);
          const otherEquipVal = beforeRatOtherEquipmentCostByMonthDisplayCalc[i]?.value || new Decimal(0);
          return equipDepVal.plus(factoryVal).plus(pastEquipVal).plus(otherEquipVal);
        });
        // 売上原価（JPY版）
        const beforeRatSalesCostByMonthDisplay = beforeRatMaterialCostByMonthDisplay.map((entry, i) => {
          const materialVal = entry.materialCost || new Decimal(0);
          const outsideVal = beforeRatOutsideCostByMonthDisplayCalc[i] || new Decimal(0);
          const laborVal = beforeRatLaborCostByMonthDisplayCalc[i] || new Decimal(0);
          const domesticLaborVal = beforeRatDomesticLaborCostByMonthDisplayCalc[i] || new Decimal(0);
          const factoryExpVal = beforeRatFactoryExpensesByMonthDisplayCalc[i] || new Decimal(0);
          const domesticFactoryExpVal = beforeRatDomesticFactoryExpensesByMonthDisplayCalc[i] || new Decimal(0);
          const scrapVal = beforeRatScrapCostByMonthDisplayCalc[i] || new Decimal(0);
          return materialVal.plus(outsideVal).plus(laborVal).plus(domesticLaborVal).plus(factoryExpVal).plus(domesticFactoryExpVal).plus(scrapVal);
        });
        // 販売費及び一般管理費（JPY版）
        const beforeRatSGAExpensesByMonthDisplay = beforeRatAppliedLaborCostByMonthDisplayCalc.map((entry, i) => {
          const appliedLaborVal = entry.value || new Decimal(0);
          const domesticSgaVal = beforeRatDomesticSgaCostByMonthDisplayCalc[i] || new Decimal(0);
          const salesManagementVal = beforeRatSalesManagementCostByMonthDisplayCalc[i]?.value || new Decimal(0);
          const devCostVal = beforeRatDevCostByMonthDisplayCalc[i]?.value || new Decimal(0);
          const royaltyVal = beforeRatRoyaltyByMonthDisplayCalc[i] || new Decimal(0);
          return appliedLaborVal.plus(domesticSgaVal).plus(salesManagementVal).plus(devCostVal).plus(royaltyVal);
        });
        // 営業利益（JPY版）
        const beforeRatProfitByMonthDisplay = beforeRatTotalSalesByMonthDisplay.map((entry, i) => {
          const salesVal = entry.sales || new Decimal(0);
          const salesCostVal = beforeRatSalesCostByMonthDisplay[i] || new Decimal(0);
          const sgaVal = beforeRatSGAExpensesByMonthDisplay[i] || new Decimal(0);
          const otherCostVal = beforeRatOtherCostByMonthDisplayCalc[i]?.value || new Decimal(0);
          return salesVal.minus(salesCostVal).minus(sgaVal).minus(otherCostVal);
        });
        // 客先指定部品材料費（JPY版）
        const beforeRatCustomerSpecifiedPartsCostByMonthDisplay = beforeRatCustomerSpecifiedPartsCostByMonth.map((value, idx) => {
          const ym = yearMonthListByFiscalYear[idx]
            ? `${yearMonthListByFiscalYear[idx].year}-${String(yearMonthListByFiscalYear[idx].month).padStart(2, "0")}`
            : undefined;
          return new Decimal(convertWithRate(value, localCurrency, displayCurrencyName, ym) || 0);
        });

        setDisplayBeforeRationalizationMonthlyProfitAndLossData({
          labels: monthlyLabels,
          totalSales: beforeRatTotalSalesByMonthDisplay.map(e => e.sales || new Decimal(0)),
          productSales: beforeRatProductSalesByMonthDisplay,
          moldSales: beforeRatMoldSalesByMonthDisplay,
          devSales: beforeRatDevSalesByMonthDisplay,
          equipmentSales: beforeRatEquipmentSalesByMonthDisplay,
          salesCost: beforeRatSalesCostByMonthDisplay,
          materialCost: beforeRatMaterialCostByMonthDisplay.map(e => e.materialCost || new Decimal(0)),
          outsideCost: beforeRatOutsideCostByMonthDisplayCalc,
          laborCost: beforeRatLaborCostByMonthDisplayCalc,
          domesticLaborCost: beforeRatDomesticLaborCostByMonthDisplayCalc,
          factoryExpenses: beforeRatFactoryExpensesByMonthDisplayCalc,
          overHeadCost: beforeRatOverheadCostByMonthDisplayCalc.map(e => e.value || new Decimal(0)),
          depreciation: beforeRatDepreciationByMonthDisplayCalc,
          equipmentDepreciation: beforeRatEquipmentDepreciationByMonthDisplayCalc.map(e => e.value || new Decimal(0)),
          factoryCost: beforeRatFactoryCostByMonthDisplayCalc.map(e => e.value || new Decimal(0)),
          pastEquipmentCost: beforeRatPastEquipmentCostByMonthDisplayCalc.map(e => e.value || new Decimal(0)),
          otherEquipmentCost: beforeRatOtherEquipmentCostByMonthDisplayCalc.map(e => e.value || new Decimal(0)),
          domesticFactoryExpenses: beforeRatDomesticFactoryExpensesByMonthDisplayCalc,
          domesticOverHeadCost: beforeRatDomesticOverHeadCostByMonthDisplayCalc.map(e => e.value || new Decimal(0)),
          domesticDepreciation: beforeRatDomesticDepreciationByMonthDisplayCalc.map(e => e.value || new Decimal(0)),
          domesticInvestmentDepreciation: beforeRatDomesticInvestmentDepreciationByMonthDisplayCalc.map(e => e.value || new Decimal(0)),
          domesticFactoryBuildingDepreciation: beforeRatDomesticFactoryBuildingDepreciationByMonthDisplayCalc.map(e => e.value || new Decimal(0)),
          domesticOtherDepreciation: beforeRatDomesticOtherDepreciationByMonthDisplayCalc.map(e => e.value || new Decimal(0)),
          scrapCost: beforeRatScrapCostByMonthDisplayCalc,
          sGAExpenses: beforeRatSGAExpensesByMonthDisplay,
          appliedLaborCost: beforeRatAppliedLaborCostByMonthDisplayCalc.map(e => e.value || new Decimal(0)),
          domesticSgaCost: beforeRatDomesticSgaCostByMonthDisplayCalc,
          salesManagementCost: beforeRatSalesManagementCostByMonthDisplayCalc.map(e => e.value || new Decimal(0)),
          devCost: beforeRatDevCostByMonthDisplayCalc.map(e => e.value || new Decimal(0)),
          royalty: beforeRatRoyaltyByMonthDisplayCalc,
          otherCost: beforeRatOtherCostByMonthDisplayCalc.map(e => e.value || new Decimal(0)),
          profit: beforeRatProfitByMonthDisplay,
          customerSpecifiedPartsCost: beforeRatCustomerSpecifiedPartsCostByMonthDisplay,
        });
      }

      // #endregion

      // #region 【回収シミュレーション】

      // ------------------------------
      // 回収シミュレーション
      // |-設備投資額
      // |-回収原資
      // | |-営業利益
      // | |-減価償却費
      // | └-工場建物・その他設備の減価償却費
      // |-未回収額
      // └-{t("features.InvestmentScreening.ProjectProfitablityStatement.heading.paybackPeriod")}
      // ------------------------------

      // 設備投資額
      // 計算方法: 設備投資額（設備別に検収月で計上）
      const capexAmountByMonth = convertedCapexEquipment.map((equip) => calcCapexAmountByMonth(equip, totalFiscalYearsByPartNo, startDate));
      const capexAmountByYear = calcCapexAmountByYear(capexAmountByMonth, totalFiscalYearsByPartNo, decimalCurrency, startDate);
      setCapexAmountByYear(capexAmountByYear);

      // 設備投資額（表示通貨ベース、表示用）
      const capexAmountByMonthDisplay = displayConvertedCapexEquipment.map((equip) => calcCapexAmountByMonth(equip, totalFiscalYearsByPartNo, startDate));
      const capexAmountByYearDisplay = calcCapexAmountByYear(capexAmountByMonthDisplay, totalFiscalYearsByPartNo, displayCurrencyDecimal, startDate);
      setDisplayCapexAmountByYear(capexAmountByYearDisplay);

      // 工場建物・その他設備の減価償却費
      // 計算方法: 工場建物 + 過去PJの投資対象設備 + その他の設備
      const factoryAndOtherEquipmentCostByMonth = mergeYearMonthValues(factoryCostByMonth, pastEquipmentCostByMonth, otherEquipmentCostByMonth);
      const factoryAndOtherEquipmentCostByYear = sumDecimalArray(factoryCostByYear, pastEquipmentCostByYear, otherEquipmentCostByYear);
      setFactoryAndOtherEquipmentCostByYear(factoryAndOtherEquipmentCostByYear);

      // 工場建物・その他設備の減価償却費（表示通貨ベース、表示用）
      const factoryAndOtherEquipmentCostByMonthDisplay = mergeYearMonthValues(factoryCostByMonthDisplay, pastEquipmentCostByMonthDisplay, otherEquipmentCostByMonthDisplay);
      const factoryAndOtherEquipmentCostByYearDisplay = sumDecimalArray(factoryCostByYearDisplay, pastEquipmentCostByYearDisplay, otherEquipmentCostByYearDisplay);
      setDisplayFactoryAndOtherEquipmentCostByYear(factoryAndOtherEquipmentCostByYearDisplay);

      // 回収原資
      // 計算方法: 営業利益 + 減価償却費 - 工場建物・その他設備の減価償却費
      const totalProfitByMonth = mergeYearMonthValues(profitByMonth, depreciationByMonth);
      const recoveredResourcesByMonth = subtractYearMonthValues(totalProfitByMonth, factoryCostByMonth, pastEquipmentCostByMonth, otherEquipmentCostByMonth)

      const totalProfitByYear = sumDecimalArray(profitByYear, depreciationByYear);
      const recoveredResourcesByYear = subtractDecimalArray(totalProfitByYear, factoryAndOtherEquipmentCostByYear);
      setRecoveredResourcesByYear(recoveredResourcesByYear);

      // 回収原資（表示通貨ベース、表示用）
      const totalProfitByYearDisplay = sumDecimalArray(profitByYearDisplay, depreciationByYearDisplay);
      const recoveredResourcesByYearDisplay = subtractDecimalArray(totalProfitByYearDisplay, factoryAndOtherEquipmentCostByYearDisplay);
      setDisplayRecoveredResourcesByYear(recoveredResourcesByYearDisplay);

      // 未回収額
      // 計算方法: 設備投資額 - 回収原資
      const capexEquipmentByMonth = calcAggregateCapexByMonth(capexAmountByMonth).map((entry) => entry.capexAmount);
      const uncollectedAmountByMonth = calcRecoverySimulation(recoveredResourcesByMonth.map((entry) => entry.value), capexEquipmentByMonth, decimalCurrency, false);
      const uncollectedAmountByYear = calcRecoverySimulation(recoveredResourcesByYear, capexAmountByYear, decimalCurrency, false);
      setUncollectedAmountByYear(uncollectedAmountByYear);

      // 未回収額（表示通貨ベース、表示用）
      const uncollectedAmountByYearDisplay = calcRecoverySimulation(recoveredResourcesByYearDisplay, capexAmountByYearDisplay, displayCurrencyDecimal, false);
      setDisplayUncollectedAmountByYear(uncollectedAmountByYearDisplay);

      // {t("features.InvestmentScreening.ProjectProfitablityStatement.heading.paybackPeriod")}
      // 計算方法: 設備投資額- 回収原資（プラスになった月数）
      const monthlyRecovery = calcMonthlyRecovery(recoveredResourcesByMonth.map((entry) => entry.value), capexAmountByMonth, decimalCurrency, startDate);
      setCollectionPeriod(monthlyRecovery);

      // 月次データから{t("features.InvestmentScreening.ProjectProfitablityStatement.heading.paybackPeriod")}を計算
      const collectionPeriodResult = calcMonthlyRecovery(
        recoveredResourcesByMonth.map((entry) => entry.value),
        capexAmountByMonth,
        decimalCurrency,
        startDate
      );
      setCollectionPeriod(collectionPeriodResult);
      // #region 【{t("features.InvestmentScreening.ProjectProfitablityStatement.heading.paybackPeriod")}】
      // ------------------------------
      // {t("features.InvestmentScreening.ProjectProfitablityStatement.heading.paybackPeriod")}
      // |-設備投資額                       回収シミュレーションと同じものを参照
      // | 営業利益                         回収シミュレーションと同じものを参照
      // | 金利
      // | 減価償却費                       回収シミュレーションと同じものを参照
      // | 工場建物・その他設備の減価償却費 回収シミュレーションと同じものを参照
      // └-{t("features.InvestmentScreening.ProjectProfitablityStatement.heading.paybackPeriod")}
      // ------------------------------

      // 金利
      // 計算方法: (設備投資額 - 残価(累積減価償却費)) × 基準金利
      const interestRateByYear = calcInterestRateByYear(capexAmountByYear, depreciationAmountByEquip, new Decimal(capexProjectData?.baseInterestRate || 0));
      setInterestRateByYear(interestRateByYear);

      // 金利（表示通貨ベース、表示用）
      const interestRateByYearDisplay = calcInterestRateByYear(capexAmountByYearDisplay, depreciationAmountByEquipDisplay, new Decimal(capexProjectData?.baseInterestRate || 0));
      setDispInterestRateByYear(interestRateByYearDisplay);

      // 製品寿命
      let productLife = calcMonthsBetweenDates(capexProjectData.projectStartDate, capexProjectData.projectEndDate);
      productLife = Number((productLife / 12).toFixed(2));

      // {t("features.InvestmentScreening.ProjectProfitablityStatement.heading.paybackPeriod")}
      const paybackPeriod = calcPaybackPeriod(capexAmountByYear, profitByYear, interestRateByYear, depreciationAmountByEquip, productLife);
      setPaybackPeriod(paybackPeriod);

      // #endregion

      // #region 【ROIC】

      // ------------------------------
      // ROIC
      // |-税引後営業利益
      // | |-営業利益
      // | |-目標税率（実効税率）
      // |-投下資本
      // | |-累計設備投資額
      // | |-投資対象設備の減価償却累計額
      // |-ROIC（簡易計算）
      // └-ハードルレート
      // ------------------------------

      // 目標税率（実効税率）
      const taxRateByYear: Decimal[] = Array.from({ length: totalFiscalYearsByPartNo }, () => taxRate);
      setTaxRateByYear(taxRateByYear);

      // 税引後営業利益
      // 計算方法: 営業利益 * (1 - 目標税率)
      const nopatByMonth = calcNopatByMonth(profitByMonth, taxRate, decimalCurrency);
      const nopatByYear = calcNopat(totalFiscalYearsByPartNo, profitByYear, taxRate, decimalCurrency);
      setNopatByYear(nopatByYear);

      // 税引後営業利益（表示通貨ベース、表示用）
      const nopatByYearDisplay = calcNopat(totalFiscalYearsByPartNo, profitByYearDisplay, taxRate, displayCurrencyDecimal);
      setDisplayNopatByYear(nopatByYearDisplay);

      // 累計設備投資額
      // 計算方法: 1年目に設備投資額の合計、残りは0固定
      const totalCapexAmountByYear = calcTotalCapexAmount(capexAmountByYear);
      setTotalCapexAmountByYear(totalCapexAmountByYear);

      // 累計設備投資額（表示通貨ベース、表示用）
      const totalCapexAmountByYearDisplay = calcTotalCapexAmount(capexAmountByYearDisplay);
      setDisplayTotalCapexAmountByYear(totalCapexAmountByYearDisplay);

      // 投下資本
      // 計算方法: 累計設備投資額 - 投資対象設備の減価償却累計額
      const undepreciatedByYear = calcUndepreciated(totalFiscalYearsByPartNo, totalCapexAmountByYear, depreciationAmountByEquip, decimalCurrency);
      setUndepreciatedByYear(undepreciatedByYear);

      // 投下資本（表示通貨ベース、表示用）
      const undepreciatedByYearDisplay = calcUndepreciated(totalFiscalYearsByPartNo, totalCapexAmountByYearDisplay, depreciationAmountByEquipDisplay, displayCurrencyDecimal);
      setDisplayUndepreciatedByYear(undepreciatedByYearDisplay);

      // ROIC（簡易計算）
      // 計算方法: 税引後営業利益 / 投下資本
      const roicByYear = calcRoic(totalFiscalYearsByPartNo, nopatByYear, undepreciatedByYear);
      setRoicByYear(roicByYear);

      // ROIC（表示通貨ベース、表示用）- 率なので通貨に依存しないが、計算元データが表示通貨なので再計算
      const roicByYearDisplay = calcRoic(totalFiscalYearsByPartNo, nopatByYearDisplay, undepreciatedByYearDisplay);
      setDisplayRoicByYear(roicByYearDisplay);

      // ハードルレート
      const hurdleRateByYear: Decimal[] = Array.from({ length: totalFiscalYearsByPartNo }, () => new Decimal(0));
      setHurdleRateByYear(hurdleRateByYear);

      // #endregion

      // #region 【{t("features.InvestmentScreening.ProjectProfitablityStatement.heading.dcf")}】

      // ------------------------------
      // {t("features.InvestmentScreening.ProjectProfitablityStatement.heading.dcf")}
      // |-フリーキャッシュフロー
      // | |-営業利益
      // | |-目標税率（実効税率）
      // | |-減価償却費
      // | |-設備投資額
      // |-加重平均資本コスト
      // |-現在価値
      // └-正味現在価値
      // ------------------------------

      // フリーキャッシュフロー
      // 計算方法: 税引後営業利益 + 減価償却費 - 設備投資額
      const nopatAndDepreciationByMonth = mergeYearMonthValues(nopatByMonth, depreciationByMonth);
      const aggregateCapexByMonth = calcAggregateCapexByMonth(capexAmountByMonth, true, startDate).map(({ year, month, capexAmount }) => ({ year, month, value: capexAmount, }));
      const freeCashFlowByMonth = subtractYearMonthValues(nopatAndDepreciationByMonth, aggregateCapexByMonth);
      const nopatAndDepreciationByYear = sumDecimalArray(nopatByYear, depreciationByYear);
      const freeCashFlowByYear = subtractDecimalArray(nopatAndDepreciationByYear, capexAmountByYear);
      setFreeCashFlowByYear(freeCashFlowByYear);

      // フリーキャッシュフロー（表示通貨ベース、表示用）
      const nopatAndDepreciationByYearDisplay = sumDecimalArray(nopatByYearDisplay, depreciationByYearDisplay);
      const freeCashFlowByYearDisplay = subtractDecimalArray(nopatAndDepreciationByYearDisplay, capexAmountByYearDisplay);
      setDisplayFreeCashFlowByYear(freeCashFlowByYearDisplay);

      // 加重平均資本コスト
      const discountRateByYear: Decimal[] = Array.from({ length: totalFiscalYearsByPartNo }, () => discountRate);
      setDiscountRateByYear(discountRateByYear);

      // 現在価値（旧:【DCF】PV）
      const presentValueByYear = calcPresentValue(freeCashFlowByMonth, discountRate, decimalCurrency);
      setPresentValueByYear(presentValueByYear);

      // 現在価値（表示通貨ベース、表示用）- 月次データから計算するため、JPY版月次データを作成
      // 振り返りモード: 個別変換済みコスト合計から算出済みの profitByMonthDisplay と
      //   mergeYearMonthValuesで統合済みの depreciationByMonthDisplay を再利用
      //   （ローカル通貨利益を月別レートで一括変換する2段変換を回避）
      // 通常モード: ローカル通貨から月別レートで変換（既存動作）
      const profitByMonthDisplayConverted = reviewDate
        ? profitByMonth.map((entry, idx) => ({
          year: entry.year,
          month: entry.month,
          value: profitByMonthDisplay[idx] || new Decimal(0),
        }))
        : profitByMonth.map(v => ({
          year: v.year,
          month: v.month,
          value: new Decimal(convertWithRate(v.value, localCurrency, displayCurrencyName, toYearMonth(v.year, v.month)) || 0)
        }));
      const nopatByMonthDisplay = calcNopatByMonth(profitByMonthDisplayConverted, taxRate, displayCurrencyDecimal);
      const depreciationByMonthDisplayValues = reviewDate
        ? depreciationByMonthDisplay  // 既に個別変換済みの表示通貨値（{year, month, value}[]形式）
        : depreciationByMonth.map(entry => ({
          year: entry.year, month: entry.month, value: new Decimal(convertWithRate(entry.value, localCurrency, displayCurrencyName, toYearMonth(entry.year, entry.month)) || 0)
        }));
      const nopatAndDepreciationByMonthDisplay = mergeYearMonthValues(nopatByMonthDisplay, depreciationByMonthDisplayValues);
      const aggregateCapexByMonthDisplay = calcAggregateCapexByMonth(capexAmountByMonthDisplay, true, startDate).map(({ year, month, capexAmount }) => ({ year, month, value: capexAmount, }));
      const freeCashFlowByMonthDisplay = subtractYearMonthValues(nopatAndDepreciationByMonthDisplay, aggregateCapexByMonthDisplay);
      const presentValueByYearDisplay = calcPresentValue(freeCashFlowByMonthDisplay, discountRate, displayCurrencyDecimal);
      setDisplayPresentValueByYear(presentValueByYearDisplay);

      // 正味現在価値（旧:【DCF】NPV）
      const netPresentValueByYear = calcDiscountedCashFlow(presentValueByYear, totalEquipmentCost.abs(), decimalCurrency);
      setNetPresentValue(netPresentValueByYear[0]);

      // 正味現在価値（表示通貨ベース、表示用）
      const totalEquipmentCostDisplay = displayConvertedCapexEquipment?.reduce(
        (acc: Decimal, equip: EquipmentType) => acc.add(new Decimal(equip.amount || 0)), new Decimal(0)
      ) || new Decimal(0);
      const netPresentValueByYearDisplay = calcDiscountedCashFlow(presentValueByYearDisplay, totalEquipmentCostDisplay.abs(), displayCurrencyDecimal);
      setDisplayNetPresentValue(netPresentValueByYearDisplay[0]);

      // #endregion

      // 年月ラベルの作成
      const labelsByMonth = calcLabelsByMonth(capexProject.projectStartDate, totalFiscalYearsByProject);
      setLabelsByMonth(labelsByMonth);

      // PartNo月別数量をDecimal→Stringに変換
      const convQuantitiesByPartNoMonth = convertQuantitiesByPartNoMonth(quantitiesByPartNoMonth, labelsByMonth.length);
      setQuantityByPartNoByMonth(convQuantitiesByPartNoMonth);

      // true: OK を返却
      return true;

    } catch (error) {
      console.error(`${functionName} ERROR:`, error);
      return false;
    }

  }

  // -----------------------------
  // EventHandler
  // -----------------------------

  // 部署確認ボタン押下処理
  const handleDepartmentConfirm: React.MouseEventHandler<HTMLButtonElement> = async (event) => {

    const functionName = "handleDepartmentConfirm";

    try {

      // フォーカスを外す
      event.currentTarget.blur();

      // 計算処理状態が false: NG の場合は処理を抜ける（損益非表示モードでは表計算を行わないため対象外）
      if (!isProfitLossHiddenMode && !isCalculation) {
        return;
      }

      // 部署名保存
      const { name } = event.target as HTMLButtonElement;
      setConfirmSource(name);

      // 損益非表示モードでは表データ依存の構築処理をすべてスキップし、確認モーダルを開くのみとする
      if (isProfitLossHiddenMode) {
        onOpen();
        return;
      }

      // ローカル通貨版の年度別損益試算を構築
      const originalProfitAndLoss: OriginalProfitAndLossTable = {
        /** 年度別損益（ProfitAndLossItemsの表示順に合わせて並べる） */
        profitAndLossByYear: {
          /** 売上合計 */
          totalSales: convertToStringArray(totalSalesByYear, totalFiscalYearsByProject),
          /** 製品売上 */
          productSales: convertToStringArray(productSalesByYear, totalFiscalYearsByProject),
          /** PJ単位金型回収 */
          moldSales: convertToStringArray(moldSalesByYear, totalFiscalYearsByProject),
          /** PJ単位開発費回収 */
          devSales: convertToStringArray(devSalesByYear, totalFiscalYearsByProject),
          /** PJ単位設備費回収 */
          equipmentSales: convertToStringArray(equipmentSalesByYear, totalFiscalYearsByProject),
          /** 売上原価 */
          salesCost: convertToStringArray(salesCostByYear, totalFiscalYearsByProject),
          /** 材料費 */
          totalMaterialCost: convertToStringArray(totalMaterialCostByYear, totalFiscalYearsByProject),
          /** 外注加工費 */
          outsideCost: convertToStringArray(outsideCostByYear, totalFiscalYearsByProject),
          /** 生産工場労務費 */
          laborCost: convertToStringArray(laborCostByYear, totalFiscalYearsByProject),
          /** 国内工場労務費 */
          domesticLaborCost: convertToStringArray(domesticLaborCostByYear, totalFiscalYearsByProject),
          /** 生産工場経費 */
          factoryExpenses: convertToStringArray(factoryExpensesByYear, totalFiscalYearsByProject),
          /** 生産工場経費-償却費以外の工場経費 */
          overHeadCost: convertToStringArray(overHeadCostByYear, totalFiscalYearsByProject),
          /** 生産工場経費-減価償却費 */
          depreciation: convertToStringArray(depreciationByYear, totalFiscalYearsByProject),
          /** 生産工場経費-投資対象設備 */
          equipmentDepreciation: convertToStringArray(equipmentDepreciationByYear, totalFiscalYearsByProject),
          /** 生産工場経費-工場建物 */
          factoryCost: convertToStringArray(factoryCostByYear, totalFiscalYearsByProject),
          /** 生産工場経費-過去PJの投資対象設備 */
          pastEquipmentCost: convertToStringArray(pastEquipmentCostByYear, totalFiscalYearsByProject),
          /** 生産工場経費-その他の設備 */
          otherEquipmentCost: convertToStringArray(otherEquipmentCostByYear, totalFiscalYearsByProject),
          /** 国内工場経費 */
          domesticFactoryExpenses: convertToStringArray(domesticFactoryExpensesByYear, totalFiscalYearsByProject),
          /** 国内工場経費-償却費以外の工場経費 */
          domesticOverHeadCost: convertToStringArray(domesticOverHeadCostByYear, totalFiscalYearsByProject),
          /** 国内工場経費-減価償却費 */
          domesticDepreciation: convertToStringArray(domesticDepreciationByYear, totalFiscalYearsByProject),
          /** 国内工場経費-投資対象設備 */
          domesticInvestmentDepreciation: convertToStringArray(domesticInvestmentDepreciationByYear, totalFiscalYearsByProject),
          /** 国内工場経費-工場建物 */
          domesticFactoryBuildingDepreciation: convertToStringArray(domesticFactoryBuildingDepreciationByYear, totalFiscalYearsByProject),
          /** 国内工場経費-その他の設備 */
          domesticOtherDepreciation: convertToStringArray(domesticOtherDepreciationByYear, totalFiscalYearsByProject),
          /** 仕損費 */
          scrapCost: convertToStringArray(scrapCostByYear, totalFiscalYearsByProject),
          /** 販売費及び一般管理費 */
          sGAExpenses: convertToStringArray(sGAExpensesByYear, totalFiscalYearsByProject),
          /** 生産工場販管費 */
          appliedLaborCost: convertToStringArray(appliedLaborCostByYear, totalFiscalYearsByProject),
          /** 国内工場販管費 */
          domesticSgaCost: convertToStringArray(domesticSgaCostByYear, totalFiscalYearsByProject),
          /** 本社・販社販管費 */
          salesManagementCost: convertToStringArray(salesManagementCostByYear, totalFiscalYearsByProject),
          /** 開発費 */
          devCost: convertToStringArray(devCostByYear, totalFiscalYearsByProject),
          /** ロイヤリティ */
          royalty: convertToStringArray(royaltyByYear, totalFiscalYearsByProject),
          /** その他の経費 */
          otherCost: convertToStringArray(otherCostByYear, totalFiscalYearsByProject),
          /** 営業利益 */
          profit: convertToStringArray(profitByYear, totalFiscalYearsByProject),
          /** 営業利益率 */
          profitRate: convertToStringArray(profitRateByYear, totalFiscalYearsByProject),
          /** 実質的営業利益率 */
          realOperatingProfitRate: hasCustomerSpecifiedParts
            ? convertToStringArray(realOperatingProfitRateByYear, totalFiscalYearsByProject)
            : [],
          /** 実質的営業利益率（合計） */
          totalRealOperatingProfitRate: hasCustomerSpecifiedParts
            ? (() => {
              const totalProfit = profitByYear.reduce((sum, v) => sum.plus(v), new Decimal(0));
              const totalSales = totalSalesByYear.reduce((sum, v) => sum.plus(v), new Decimal(0));
              const totalCustCost = customerSpecifiedPartsCostByYear.reduce((sum, v) => sum.plus(v), new Decimal(0));
              const totalRealSales = totalSales.minus(totalCustCost);
              if (totalRealSales.isZero()) return "0";
              return totalProfit.div(totalRealSales).times(100).toString();
            })()
            : undefined,
        },
        /** {t("features.InvestmentScreening.ProjectProfitablityStatement.heading.paybackPeriod")} */
        paybackPeriodByYear: {
          /** 金利 */
          interestRateByYear: convertToStringArray(interestRateByYear, totalFiscalYearsByProject),
          /** {t("features.InvestmentScreening.ProjectProfitablityStatement.heading.paybackPeriod")} */
          paybackPeriod: paybackPeriod,
        },
        /** 回収シミュレーション */
        RecoverySimulationByYear: {
          /** 設備投資額 */
          capexAmount: convertToStringArray(capexAmountByYear, totalFiscalYearsByProject),
          /** 回収原資 */
          recoveredResources: convertToStringArray(recoveredResourcesByYear, totalFiscalYearsByProject),
          /** 営業利益 */
          profit: convertToStringArray(profitByYear, totalFiscalYearsByProject),
          /** 減価償却費 */
          depreciation: convertToStringArray(depreciationByYear, totalFiscalYearsByProject),
          /** 工場建物・その他設備の減価償却費 */
          factoryAndOtherEquipmentCost: convertToStringArray(factoryAndOtherEquipmentCostByYear, totalFiscalYearsByProject),
          /** 未回収額 */
          uncollectedAmount: convertToStringArray(uncollectedAmountByYear, totalFiscalYearsByProject),
          /** {t("features.InvestmentScreening.ProjectProfitablityStatement.heading.paybackPeriod")} */
          collectionPeriod: collectionPeriod,
        },
        /** ROIC */
        roicByYear: {
          /** 税引後営業利益 */
          nopatByYear: convertToStringArray(nopatByYear, totalFiscalYearsByProject),
          /** 営業利益 */
          profit: convertToStringArray(profitByYear, totalFiscalYearsByProject),
          /** 目標税率（実効税率） */
          setTaxRate: convertToStringArray(taxRateByYear, totalFiscalYearsByProject),
          /** 投下資本 */
          undepreciatedByYear: convertToStringArray(undepreciatedByYear, totalFiscalYearsByProject),
          /** 累計設備投資額 */
          totalCapexAmount: convertToStringArray(totalCapexAmountByYear, totalFiscalYearsByProject),
          /** 投資対象設備の減価償却累計額 */
          equipmentDepreciation: convertToStringArray(equipmentDepreciationByYear, totalFiscalYearsByProject),
          /** ROIC（簡易計算） */
          roicByYear: convertToStringArray(roicByYear, totalFiscalYearsByProject),
          /** ハードルレート */
          hurdleRate: convertToStringArray(hurdleRateByYear, totalFiscalYearsByProject),
        },
        /** {t("features.InvestmentScreening.ProjectProfitablityStatement.heading.dcf")}（0年目を含むため totalFiscalYearsByProject + 1） */
        dcf: {
          /** フリーキャッシュフロー */
          freeCashFlow: convertToStringArray([new Decimal(0), ...freeCashFlowByYear], totalFiscalYearsByProject + 1),
          /** 営業利益 */
          profit: convertToStringArray([new Decimal(0), ...profitByYear], totalFiscalYearsByProject + 1),
          /** 目標税率（実効税率） */
          setTaxRate: convertToStringArray([new Decimal(0), ...taxRateByYear], totalFiscalYearsByProject + 1),
          /** 減価償却費 */
          depreciation: convertToStringArray([new Decimal(0), ...depreciationByYear], totalFiscalYearsByProject + 1),
          /** 設備投資額 */
          capexAmount: convertToStringArray([new Decimal(0), ...capexAmountByYear], totalFiscalYearsByProject + 1),
          /** 加重平均資本コスト */
          discountRate: convertToStringArray([new Decimal(0), ...discountRateByYear], totalFiscalYearsByProject + 1),
          /** 現在価値 */
          presentValue: convertToStringArray([new Decimal(0), ...presentValueByYear], totalFiscalYearsByProject + 1),
          /** 正味現在価値 */
          netPresentValue: netPresentValue?.toString() || "",
        },
      };
      setOriginalProfitAndLoss(originalProfitAndLoss);

      // ローカル通貨版の月別損益試算を構築
      const originalProfitAndLossByMonth: OriginalProfitAndLossByMonthTable = {
        /** 年月ラベル */
        labels: labelsByMonth,
        /** 売上合計 */
        totalSales: convertToStringArray(totalSalesByMonth, labelsByMonth.length),
        /** 売上原価（製造原価） */
        salesCost: convertToStringArray(monthlyProfitAndLossData?.salesCost ?? [], labelsByMonth.length),
        /** 営業利益 */
        profit: convertToStringArray(profitByMonth, labelsByMonth.length),
        /** 販売数量（PartNo別） */
        quantityByPartNo: quantityByPartNoByMonth,
      };
      setOriginalProfitAndLossByMonth(originalProfitAndLossByMonth);

      // JPY版の年度別損益試算を構築
      const displayOriginalProfitAndLoss: OriginalProfitAndLossTable = {
        profitAndLossByYear: {
          totalSales: convertToStringArray(displayTotalSalesByYear, totalFiscalYearsByProject),
          productSales: convertToStringArray(displayProductSalesByYear, totalFiscalYearsByProject),
          moldSales: convertToStringArray(displayMoldSalesByYear, totalFiscalYearsByProject),
          devSales: convertToStringArray(displayDevSalesByYear, totalFiscalYearsByProject),
          equipmentSales: convertToStringArray(displayEquipmentSalesByYear, totalFiscalYearsByProject),
          salesCost: convertToStringArray(displaySalesCostByYear, totalFiscalYearsByProject),
          totalMaterialCost: convertToStringArray(displayTotalMaterialCostByYear, totalFiscalYearsByProject),
          outsideCost: convertToStringArray(displayOutsideCostByYear, totalFiscalYearsByProject),
          laborCost: convertToStringArray(displayLaborCostByYear, totalFiscalYearsByProject),
          domesticLaborCost: convertToStringArray(displayDomesticLaborCostByYear, totalFiscalYearsByProject),
          factoryExpenses: convertToStringArray(displayFactoryExpensesByYear, totalFiscalYearsByProject),
          overHeadCost: convertToStringArray(displayOverHeadCostByYear, totalFiscalYearsByProject),
          depreciation: convertToStringArray(displayDepreciationByYear, totalFiscalYearsByProject),
          equipmentDepreciation: convertToStringArray(displayEquipmentDepreciationByYear, totalFiscalYearsByProject),
          factoryCost: convertToStringArray(displayFactoryCostByYear, totalFiscalYearsByProject),
          pastEquipmentCost: convertToStringArray(displayPastEquipmentCostByYear, totalFiscalYearsByProject),
          otherEquipmentCost: convertToStringArray(displayOtherEquipmentCostByYear, totalFiscalYearsByProject),
          domesticFactoryExpenses: convertToStringArray(displayDomesticFactoryExpensesByYear, totalFiscalYearsByProject),
          domesticOverHeadCost: convertToStringArray(displayDomesticOverHeadCostByYear, totalFiscalYearsByProject),
          domesticDepreciation: convertToStringArray(displayDomesticDepreciationByYear, totalFiscalYearsByProject),
          domesticInvestmentDepreciation: convertToStringArray(displayDomesticInvestmentDepreciationByYear, totalFiscalYearsByProject),
          domesticFactoryBuildingDepreciation: convertToStringArray(displayDomesticFactoryBuildingDepreciationByYear, totalFiscalYearsByProject),
          domesticOtherDepreciation: convertToStringArray(displayDomesticOtherDepreciationByYear, totalFiscalYearsByProject),
          scrapCost: convertToStringArray(displayScrapCostByYear, totalFiscalYearsByProject),
          sGAExpenses: convertToStringArray(displaySGAExpensesByYear, totalFiscalYearsByProject),
          appliedLaborCost: convertToStringArray(displayAppliedLaborCostByYear, totalFiscalYearsByProject),
          domesticSgaCost: convertToStringArray(displayDomesticSgaCostByYear, totalFiscalYearsByProject),
          salesManagementCost: convertToStringArray(displaySalesManagementCostByYear, totalFiscalYearsByProject),
          devCost: convertToStringArray(displayDevCostByYear, totalFiscalYearsByProject),
          royalty: convertToStringArray(displayRoyaltyByYear, totalFiscalYearsByProject),
          otherCost: convertToStringArray(displayOtherCostByYear, totalFiscalYearsByProject),
          profit: convertToStringArray(displayProfitByYear, totalFiscalYearsByProject),
          profitRate: convertToStringArray(displayProfitRateByYear, totalFiscalYearsByProject),
          realOperatingProfitRate: hasCustomerSpecifiedParts
            ? convertToStringArray(displayRealOperatingProfitRateByYear, totalFiscalYearsByProject)
            : [],
          /** 実質的営業利益率（合計） */
          totalRealOperatingProfitRate: originalProfitAndLoss.profitAndLossByYear.totalRealOperatingProfitRate,
        },
        /** {t("features.InvestmentScreening.ProjectProfitablityStatement.heading.paybackPeriod")}（金利は金額のためJPY版、{t("features.InvestmentScreening.ProjectProfitablityStatement.heading.paybackPeriod")}は年数のため変換不要） */
        paybackPeriodByYear: {
          interestRateByYear: convertToStringArray(dispInterestRateByYear, totalFiscalYearsByProject),
          paybackPeriod: originalProfitAndLoss.paybackPeriodByYear.paybackPeriod,
        },
        /** 回収シミュレーション（金額フィールドはJPY版、{t("features.InvestmentScreening.ProjectProfitablityStatement.heading.paybackPeriod")}は年数のため変換不要） */
        RecoverySimulationByYear: {
          capexAmount: convertToStringArray(displayCapexAmountByYear, totalFiscalYearsByProject),
          recoveredResources: convertToStringArray(displayRecoveredResourcesByYear, totalFiscalYearsByProject),
          profit: convertToStringArray(displayProfitByYear, totalFiscalYearsByProject),
          depreciation: convertToStringArray(displayDepreciationByYear, totalFiscalYearsByProject),
          factoryAndOtherEquipmentCost: convertToStringArray(displayFactoryAndOtherEquipmentCostByYear, totalFiscalYearsByProject),
          uncollectedAmount: convertToStringArray(displayUncollectedAmountByYear, totalFiscalYearsByProject),
          collectionPeriod: originalProfitAndLoss.RecoverySimulationByYear.collectionPeriod,
        },
        /** ROIC（金額フィールドはJPY版、比率フィールドもJPY版データから再計算したものを使用） */
        roicByYear: {
          nopatByYear: convertToStringArray(displayNopatByYear, totalFiscalYearsByProject),
          profit: convertToStringArray(displayProfitByYear, totalFiscalYearsByProject),
          setTaxRate: originalProfitAndLoss.roicByYear.setTaxRate,
          undepreciatedByYear: convertToStringArray(displayUndepreciatedByYear, totalFiscalYearsByProject),
          totalCapexAmount: convertToStringArray(displayTotalCapexAmountByYear, totalFiscalYearsByProject),
          equipmentDepreciation: convertToStringArray(displayEquipmentDepreciationByYear, totalFiscalYearsByProject),
          roicByYear: convertToStringArray(displayRoicByYear, totalFiscalYearsByProject),
          hurdleRate: originalProfitAndLoss.roicByYear.hurdleRate,
        },
        /** {t("features.InvestmentScreening.ProjectProfitablityStatement.heading.dcf")}（金額フィールドはJPY版、比率フィールドは変換不要、0年目を含む） */
        dcf: {
          freeCashFlow: convertToStringArray([new Decimal(0), ...displayFreeCashFlowByYear], totalFiscalYearsByProject + 1),
          profit: convertToStringArray([new Decimal(0), ...displayProfitByYear], totalFiscalYearsByProject + 1),
          setTaxRate: originalProfitAndLoss.dcf.setTaxRate,
          depreciation: convertToStringArray([new Decimal(0), ...displayDepreciationByYear], totalFiscalYearsByProject + 1),
          capexAmount: convertToStringArray([new Decimal(0), ...displayCapexAmountByYear], totalFiscalYearsByProject + 1),
          discountRate: originalProfitAndLoss.dcf.discountRate,
          presentValue: convertToStringArray([new Decimal(0), ...displayPresentValueByYear], totalFiscalYearsByProject + 1),
          netPresentValue: displayNetPresentValue?.toString() || "",
        },
      };
      setDisplayOriginalProfitAndLoss(displayOriginalProfitAndLoss);
      // 振り返りモード: JPY版をstateに保存（初回計算=JPY時のみ）
      if (reviewDate && (selectedCurrency?.name || CurrencyNameJPY) === CurrencyNameJPY) {
        setJpySavedProfitAndLoss(displayOriginalProfitAndLoss);
      }

      // JPY版の月別損益試算を構築
      // displayMonthlyProfitAndLossDataには既にJPY版のデータが入っている
      if (displayMonthlyProfitAndLossData) {
        const displayOriginalProfitAndLossByMonth: OriginalProfitAndLossByMonthTable = {
          labels: labelsByMonth,
          totalSales: displayMonthlyProfitAndLossData.totalSales.map(v => (v || new Decimal(0)).toString()),
          salesCost: displayMonthlyProfitAndLossData.salesCost.map(v => (v || new Decimal(0)).toString()),
          profit: displayMonthlyProfitAndLossData.profit.map(v => (v || new Decimal(0)).toString()),
          quantityByPartNo: quantityByPartNoByMonth, // 数量は通貨変換不要
        };
        setDisplayOriginalProfitAndLossByMonth(displayOriginalProfitAndLossByMonth);
        // 振り返りモード: JPY版をstateに保存
        if (reviewDate && (selectedCurrency?.name || CurrencyNameJPY) === CurrencyNameJPY) {
          setJpySavedProfitAndLossByMonth(displayOriginalProfitAndLossByMonth);
        }
      }

      // 更新投資の場合は、更新投資損益試算データを準備
      if (capexProjectData.purpose === PurposeType.UpdateAndOther.code) {
        const isSimple = isSimpleApplicationForm(capexProjectData?.purpose, capexProjectData?.applicationFormType);

        // 簡易モード用変数（工場通貨版）
        let updateImprovementEffectIndicatorByProcess: RationalizationImprovementEffectByProcess[] = [];
        let updateImprovementEffectCostByProcess: RationalizationImprovementEffectByProcess[] = [];
        let updateImprovementTotalIndicator: RationalizationImprovementTotalItem[] = [];
        let updateImprovementTotalCost: RationalizationImprovementTotalItem[] = [];
        // 簡易モード用変数（表示通貨版）
        let updateDisplayImprovementEffectCostByProcess: RationalizationImprovementEffectByProcess[] = [];
        let updateDisplayImprovementTotalCost: RationalizationImprovementTotalItem[] = [];

        if (isSimple && updateSummaryData.length > 0) {
          const updateProjectStartDate = capexProjectData?.projectStartDate || dayjs().format("YYYY-MM-DD");
          const updateFactoryCurrency = currencyData.find(c => c.currencyId === localCurrencyId);
          const updateFactoryCurrencyName = updateFactoryCurrency?.currencyName || CurrencyNameJPY;
          const updateDisplayCurrencyName = CurrencyNameJPY;

          const getUpdateItemName = (item: RationalizationProfitLossItem): string => {
            if (item.itemType === "custom" && item.customItemName) return item.customItemName;
            const itemNameMap: Record<string, string> = {
              productionQuantity: t("features.InvestmentScreening.ProjectProfitablityStatement.improvementItem.productionQuantity"),
              salesQuantity: t("features.InvestmentScreening.ProjectProfitablityStatement.improvementItem.salesQuantity"),
              laborCost: t("features.InvestmentScreening.ProjectProfitablityStatement.improvementItem.laborCost"),
              repairCost: t("features.InvestmentScreening.ProjectProfitablityStatement.improvementItem.repairCost"),
              consumablesCost: t("features.InvestmentScreening.ProjectProfitablityStatement.improvementItem.consumablesCost"),
              maintenanceCost: t("features.InvestmentScreening.ProjectProfitablityStatement.improvementItem.maintenanceCost"),
              scrapCost: t("features.InvestmentScreening.ProjectProfitablityStatement.improvementItem.scrapCost"),
              totalCost: t("features.InvestmentScreening.ProjectProfitablityStatement.improvementItem.totalCost"),
              targetEquipmentDepreciation: t("features.InvestmentScreening.ProjectProfitablityStatement.improvementItem.targetEquipmentDepreciation"),
              otherDepreciation: t("features.InvestmentScreening.ProjectProfitablityStatement.improvementItem.otherDepreciation"),
            };
            return itemNameMap[item.itemType] || item.itemType;
          };

          const aggregateUpdateMonthlyDataByYear = (
            monthlyData: Record<string, number>,
            fiscalYears: string[]
          ): Decimal[] => {
            return fiscalYears.map((fiscalYear) => {
              const yearMatch = fiscalYear.match(/(\d{4})年/);
              if (!yearMatch) return new Decimal(0);
              const year = parseInt(yearMatch[1]);
              let total = new Decimal(0);
              for (let month = 4; month <= 12; month++) {
                const key = `${year - 1}-${String(month).padStart(2, "0")}`;
                total = total.plus(monthlyData[key] || 0);
              }
              for (let month = 1; month <= 3; month++) {
                const key = `${year}-${String(month).padStart(2, "0")}`;
                total = total.plus(monthlyData[key] || 0);
              }
              return total;
            });
          };

          const convertUpdateToFactoryCurrency = (value: Decimal, sourceCurrencyId: string | undefined): Decimal => {
            if (!sourceCurrencyId) return value;
            const sourceCurrency = currencyData.find(c => c.currencyId === sourceCurrencyId);
            if (!sourceCurrency?.currencyName || sourceCurrency.currencyName === updateFactoryCurrencyName) return value;
            return convertWithRate(value, sourceCurrency.currencyName, updateFactoryCurrencyName);
          };

          const convertUpdateToDisplayCurrency = (value: Decimal, sourceCurrencyId: string | undefined): Decimal => {
            if (!sourceCurrencyId) return value;
            const sourceCurrency = currencyData.find(c => c.currencyId === sourceCurrencyId);
            if (!sourceCurrency?.currencyName || sourceCurrency.currencyName === updateDisplayCurrencyName) return value;
            return convertWithRate(value, sourceCurrency.currencyName, updateDisplayCurrencyName);
          };

          const updateFiscalYearsString = getFiscalYearsString(updateProjectStartDate, totalFiscalYearsByProject);

          // 改善効果（指標）- 工程別
          updateImprovementEffectIndicatorByProcess = updateSummaryData
            .filter(summary => !!(summary.indicatorItems && summary.indicatorItems.length > 0))
            .map(summary => {
              const processName = summary.productionLineName || "";
              const items: RationalizationImprovementEffectItem[] = summary.indicatorItems!.map(item => {
                const beforeByYear = aggregateUpdateMonthlyDataByYear(item.beforeMonthlyData, updateFiscalYearsString);
                const afterByYear = aggregateUpdateMonthlyDataByYear(item.afterMonthlyData, updateFiscalYearsString);
                const beforeTotal = beforeByYear.reduce((sum, v) => sum.plus(v), new Decimal(0));
                const afterTotal = afterByYear.reduce((sum, v) => sum.plus(v), new Decimal(0));
                return {
                  itemType: item.itemType,
                  itemName: getUpdateItemName(item),
                  beforeByYear: beforeByYear.map(v => v.toString()),
                  beforeTotal: beforeTotal.toString(),
                  afterByYear: afterByYear.map(v => v.toString()),
                  afterTotal: afterTotal.toString(),
                };
              });
              return { processId: summary.productionLineId || "", processName, items };
            });
          // 改善効果（費用）- 工程別（工場通貨版）
          updateImprovementEffectCostByProcess = updateSummaryData
            .filter(summary => summary.costItems && summary.costItems.length > 0)
            .map(summary => {
              const processName = summary.productionLineName || "";
              const items: RationalizationImprovementEffectItem[] = summary.costItems!.map(item => {
                const beforeByYear = aggregateUpdateMonthlyDataByYear(item.beforeMonthlyData, updateFiscalYearsString);
                const afterByYear = aggregateUpdateMonthlyDataByYear(item.afterMonthlyData, updateFiscalYearsString);
                const beforeByYearConverted = beforeByYear.map(v => convertUpdateToFactoryCurrency(v, summary.costCurrencyId));
                const afterByYearConverted = afterByYear.map(v => convertUpdateToFactoryCurrency(v, summary.costCurrencyId));
                const beforeTotal = beforeByYearConverted.reduce((sum, v) => sum.plus(v), new Decimal(0));
                const afterTotal = afterByYearConverted.reduce((sum, v) => sum.plus(v), new Decimal(0));
                return {
                  itemType: item.itemType,
                  itemName: getUpdateItemName(item),
                  beforeByYear: beforeByYearConverted.map(v => v.toString()),
                  beforeTotal: beforeTotal.toString(),
                  afterByYear: afterByYearConverted.map(v => v.toString()),
                  afterTotal: afterTotal.toString(),
                };
              });
              return { processId: summary.productionLineId || "", processName, items };
            });

          // 改善効果の合計（指標）- 全工程集約
          const allUpdateIndicatorItemsMap = new Map<string, { itemType: string; itemName: string; beforeByYear: Decimal[]; afterByYear: Decimal[] }>();
          updateSummaryData.forEach(summary => {
            if (summary.indicatorItems) {
              summary.indicatorItems.forEach(item => {
                const key = item.itemType === "custom" ? `custom_${item.customItemName}` : item.itemType;
                const beforeByYear = aggregateUpdateMonthlyDataByYear(item.beforeMonthlyData, updateFiscalYearsString);
                const afterByYear = aggregateUpdateMonthlyDataByYear(item.afterMonthlyData, updateFiscalYearsString);
                if (allUpdateIndicatorItemsMap.has(key)) {
                  const existing = allUpdateIndicatorItemsMap.get(key)!;
                  existing.beforeByYear = existing.beforeByYear.map((v, i) => v.plus(beforeByYear[i] || 0));
                  existing.afterByYear = existing.afterByYear.map((v, i) => v.plus(afterByYear[i] || 0));
                } else {
                  allUpdateIndicatorItemsMap.set(key, { itemType: item.itemType, itemName: getUpdateItemName(item), beforeByYear, afterByYear });
                }
              });
            }
          });
          updateImprovementTotalIndicator = Array.from(allUpdateIndicatorItemsMap.values()).map(item => {
            const differenceByYear = item.afterByYear.map((after, i) => after.minus(item.beforeByYear[i]));
            const differenceTotal = differenceByYear.reduce((sum, v) => sum.plus(v), new Decimal(0));
            return { itemType: item.itemType, itemName: item.itemName, differenceByYear: differenceByYear.map(v => v.toString()), differenceTotal: differenceTotal.toString() };
          });

          // 改善効果の合計（費用）- 全工程集約（工場通貨版）
          const allUpdateCostItemsMap = new Map<string, { itemType: string; itemName: string; beforeByYear: Decimal[]; afterByYear: Decimal[] }>();
          updateSummaryData.forEach(summary => {
            if (summary.costItems) {
              summary.costItems.forEach(item => {
                const key = item.itemType === "custom" ? `custom_${item.customItemName}` : item.itemType;
                const beforeByYear = aggregateUpdateMonthlyDataByYear(item.beforeMonthlyData, updateFiscalYearsString);
                const afterByYear = aggregateUpdateMonthlyDataByYear(item.afterMonthlyData, updateFiscalYearsString);
                const beforeByYearConverted = beforeByYear.map(v => convertUpdateToFactoryCurrency(v, summary.costCurrencyId));
                const afterByYearConverted = afterByYear.map(v => convertUpdateToFactoryCurrency(v, summary.costCurrencyId));
                if (allUpdateCostItemsMap.has(key)) {
                  const existing = allUpdateCostItemsMap.get(key)!;
                  existing.beforeByYear = existing.beforeByYear.map((v, i) => v.plus(beforeByYearConverted[i] || 0));
                  existing.afterByYear = existing.afterByYear.map((v, i) => v.plus(afterByYearConverted[i] || 0));
                } else {
                  allUpdateCostItemsMap.set(key, { itemType: item.itemType, itemName: getUpdateItemName(item), beforeByYear: beforeByYearConverted, afterByYear: afterByYearConverted });
                }
              });
            }
          });
          updateImprovementTotalCost = Array.from(allUpdateCostItemsMap.values()).map(item => {
            const differenceByYear = item.afterByYear.map((after, i) => after.minus(item.beforeByYear[i]));
            const differenceTotal = differenceByYear.reduce((sum, v) => sum.plus(v), new Decimal(0));
            return { itemType: item.itemType, itemName: item.itemName, differenceByYear: differenceByYear.map(v => v.toString()), differenceTotal: differenceTotal.toString() };
          });

          // 改善効果（費用）- 工程別（表示通貨版）
          updateDisplayImprovementEffectCostByProcess = updateSummaryData
            .filter(summary => summary.costItems && summary.costItems.length > 0)
            .map(summary => {
              const processName = summary.productionLineName || "";
              const items: RationalizationImprovementEffectItem[] = summary.costItems!.map(item => {
                const beforeByYear = aggregateUpdateMonthlyDataByYear(item.beforeMonthlyData, updateFiscalYearsString);
                const afterByYear = aggregateUpdateMonthlyDataByYear(item.afterMonthlyData, updateFiscalYearsString);
                const beforeByYearConverted = beforeByYear.map(v => convertUpdateToDisplayCurrency(v, summary.costCurrencyId));
                const afterByYearConverted = afterByYear.map(v => convertUpdateToDisplayCurrency(v, summary.costCurrencyId));
                const beforeTotal = beforeByYearConverted.reduce((sum, v) => sum.plus(v), new Decimal(0));
                const afterTotal = afterByYearConverted.reduce((sum, v) => sum.plus(v), new Decimal(0));
                return {
                  itemType: item.itemType,
                  itemName: getUpdateItemName(item),
                  beforeByYear: beforeByYearConverted.map(v => v.toString()),
                  beforeTotal: beforeTotal.toString(),
                  afterByYear: afterByYearConverted.map(v => v.toString()),
                  afterTotal: afterTotal.toString(),
                };
              });
              return { processId: summary.productionLineId || "", processName, items };
            });

          // 改善効果の合計（費用）- 全工程集約（表示通貨版）
          const allUpdateDisplayCostItemsMap = new Map<string, { itemType: string; itemName: string; beforeByYear: Decimal[]; afterByYear: Decimal[] }>();
          updateSummaryData.forEach(summary => {
            if (summary.costItems) {
              summary.costItems.forEach(item => {
                const key = item.itemType === "custom" ? `custom_${item.customItemName}` : item.itemType;
                const beforeByYear = aggregateUpdateMonthlyDataByYear(item.beforeMonthlyData, updateFiscalYearsString);
                const afterByYear = aggregateUpdateMonthlyDataByYear(item.afterMonthlyData, updateFiscalYearsString);
                const beforeByYearConverted = beforeByYear.map(v => convertUpdateToDisplayCurrency(v, summary.costCurrencyId));
                const afterByYearConverted = afterByYear.map(v => convertUpdateToDisplayCurrency(v, summary.costCurrencyId));
                if (allUpdateDisplayCostItemsMap.has(key)) {
                  const existing = allUpdateDisplayCostItemsMap.get(key)!;
                  existing.beforeByYear = existing.beforeByYear.map((v, i) => v.plus(beforeByYearConverted[i] || 0));
                  existing.afterByYear = existing.afterByYear.map((v, i) => v.plus(afterByYearConverted[i] || 0));
                } else {
                  allUpdateDisplayCostItemsMap.set(key, { itemType: item.itemType, itemName: getUpdateItemName(item), beforeByYear: beforeByYearConverted, afterByYear: afterByYearConverted });
                }
              });
            }
          });
          updateDisplayImprovementTotalCost = Array.from(allUpdateDisplayCostItemsMap.values()).map(item => {
            const differenceByYear = item.afterByYear.map((after, i) => after.minus(item.beforeByYear[i]));
            const differenceTotal = differenceByYear.reduce((sum, v) => sum.plus(v), new Decimal(0));
            return { itemType: item.itemType, itemName: item.itemName, differenceByYear: differenceByYear.map(v => v.toString()), differenceTotal: differenceTotal.toString() };
          });
        }

        // {t("features.InvestmentScreening.ProjectProfitablityStatement.heading.improvementByUpdate")}（工程別）を構築
        const improvementEffectByProcess: UpdateImprovementEffectByProcess[] = updateSummaryData.map((process) => {
          // 歩留計算（100% - スクラップ率）
          const calcYield = (scrapRate: number | undefined): number | undefined => {
            return scrapRate !== undefined ? 100 - scrapRate : undefined;
          };
          // 差異計算
          const calcDifference = (actual: number | undefined, target: number | undefined): number | undefined => {
            if (actual === undefined || target === undefined) return undefined;
            return target - actual;
          };

          return {
            processName: process.productionLineName || "",
            yield: {
              actual: calcYield(process.profitLoss?.recentActual?.scrapRate)?.toString() ?? "",
              initial: calcYield(process.profitLoss?.theoretical?.scrapRate)?.toString() ?? "",
              target: calcYield(process.profitLoss?.investmentPlan?.scrapRate)?.toString() ?? "",
              difference: calcDifference(
                calcYield(process.profitLoss?.recentActual?.scrapRate),
                calcYield(process.profitLoss?.investmentPlan?.scrapRate)
              )?.toString() ?? "",
            },
            scrapCost: {
              actual: process.profitLoss?.recentActual?.scrapCost?.toString() ?? "",
              initial: process.profitLoss?.theoretical?.scrapCost?.toString() ?? "",
              target: process.profitLoss?.investmentPlan?.scrapCost?.toString() ?? "",
              difference: calcDifference(
                process.profitLoss?.recentActual?.scrapCost,
                process.profitLoss?.investmentPlan?.scrapCost
              )?.toString() ?? "",
            },
            repairCost: {
              actual: process.profitLoss?.recentActual?.repairCost?.toString() ?? "",
              initial: process.profitLoss?.theoretical?.repairCost?.toString() ?? "",
              target: process.profitLoss?.investmentPlan?.repairCost?.toString() ?? "",
              difference: calcDifference(
                process.profitLoss?.recentActual?.repairCost ?? undefined,
                process.profitLoss?.investmentPlan?.repairCost ?? undefined
              )?.toString() ?? "",
            },
            consumablesCost: {
              actual: process.profitLoss?.recentActual?.consumablesCost?.toString() ?? "",
              initial: process.profitLoss?.theoretical?.consumablesCost?.toString() ?? "",
              target: process.profitLoss?.investmentPlan?.consumablesCost?.toString() ?? "",
              difference: calcDifference(
                process.profitLoss?.recentActual?.consumablesCost ?? undefined,
                process.profitLoss?.investmentPlan?.consumablesCost ?? undefined
              )?.toString() ?? "",
            },
          };
        });

        // 改善額合計を計算
        const calcTotalDifference = (
          getValue: (p: UpdateSummaryInputData) => number | undefined,
          getTarget: (p: UpdateSummaryInputData) => number | undefined
        ): number => {
          return updateSummaryData.reduce((acc, process) => {
            const actual = getValue(process);
            const target = getTarget(process);
            if (actual !== undefined && target !== undefined) {
              return acc + (target - actual);
            }
            return acc;
          }, 0);
        };

        const scrapCostTotal = calcTotalDifference(
          p => p.profitLoss?.recentActual?.scrapCost,
          p => p.profitLoss?.investmentPlan?.scrapCost
        );
        const repairCostTotal = calcTotalDifference(
          p => p.profitLoss?.recentActual?.repairCost ?? undefined,
          p => p.profitLoss?.investmentPlan?.repairCost ?? undefined
        );
        const consumablesCostTotal = calcTotalDifference(
          p => p.profitLoss?.recentActual?.consumablesCost ?? undefined,
          p => p.profitLoss?.investmentPlan?.consumablesCost ?? undefined
        );

        // 更新投資損益試算データを構築（ローカル通貨版）
        // 更新投資損益試算データ（ProfitAndLossItemsの表示順に合わせて並べる）
        const updateInvestmentProfitLoss: UpdateInvestmentProfitLossData = {
          beforeUpdateProfitAndLossByYear: {
            /** 売上合計 */
            totalSales: convertToStringArray(beforeUpdateTotalSalesByYear, totalFiscalYearsByProject),
            /** 製品売上 */
            productSales: convertToStringArray(beforeUpdateProductSalesByYear, totalFiscalYearsByProject),
            /** PJ単位金型回収 */
            moldSales: convertToStringArray(beforeUpdateMoldSalesByYear, totalFiscalYearsByProject),
            /** PJ単位開発費回収 */
            devSales: convertToStringArray(beforeUpdateDevSalesByYear, totalFiscalYearsByProject),
            /** PJ単位設備費回収 */
            equipmentSales: convertToStringArray(beforeUpdateEquipmentSalesByYear, totalFiscalYearsByProject),
            /** 売上原価 */
            salesCost: convertToStringArray(beforeUpdateSalesCostByYear, totalFiscalYearsByProject),
            /** 材料費 */
            totalMaterialCost: convertToStringArray(beforeUpdateTotalMaterialCostByYear, totalFiscalYearsByProject),
            /** 外注加工費 */
            outsideCost: convertToStringArray(beforeUpdateOutsideCostByYear, totalFiscalYearsByProject),
            /** 生産工場労務費 */
            laborCost: convertToStringArray(beforeUpdateLaborCostByYear, totalFiscalYearsByProject),
            /** 国内工場労務費 */
            domesticLaborCost: convertToStringArray(beforeUpdateDomesticLaborCostByYear, totalFiscalYearsByProject),
            /** 生産工場経費 */
            factoryExpenses: convertToStringArray(beforeUpdateFactoryExpensesByYear, totalFiscalYearsByProject),
            /** 生産工場経費-償却費以外の工場経費 */
            overHeadCost: convertToStringArray(beforeUpdateOverHeadCostByYear, totalFiscalYearsByProject),
            /** 生産工場経費-減価償却費 */
            depreciation: convertToStringArray(beforeUpdateDepreciationByYear, totalFiscalYearsByProject),
            /** 生産工場経費-投資対象設備 */
            equipmentDepreciation: convertToStringArray(beforeUpdateEquipmentDepreciationByYear, totalFiscalYearsByProject),
            /** 生産工場経費-工場建物 */
            factoryCost: convertToStringArray(beforeUpdateFactoryCostByYear, totalFiscalYearsByProject),
            /** 生産工場経費-過去PJの投資対象設備 */
            pastEquipmentCost: convertToStringArray(beforeUpdatePastEquipmentCostByYear, totalFiscalYearsByProject),
            /** 生産工場経費-その他の設備 */
            otherEquipmentCost: convertToStringArray(beforeUpdateOtherEquipmentCostByYear, totalFiscalYearsByProject),
            /** 国内工場経費 */
            domesticFactoryExpenses: convertToStringArray(beforeUpdateDomesticFactoryExpensesByYear, totalFiscalYearsByProject),
            /** 国内工場経費-償却費以外の工場経費 */
            domesticOverHeadCost: convertToStringArray(beforeUpdateDomesticOverHeadCostByYear, totalFiscalYearsByProject),
            /** 国内工場経費-減価償却費 */
            domesticDepreciation: convertToStringArray(beforeUpdateDomesticDepreciationByYear, totalFiscalYearsByProject),
            /** 国内工場経費-投資対象設備 */
            domesticInvestmentDepreciation: convertToStringArray(beforeUpdateDomesticInvestmentDepreciationByYear, totalFiscalYearsByProject),
            /** 国内工場経費-工場建物 */
            domesticFactoryBuildingDepreciation: convertToStringArray(beforeUpdateDomesticFactoryBuildingDepreciationByYear, totalFiscalYearsByProject),
            /** 国内工場経費-その他の設備 */
            domesticOtherDepreciation: convertToStringArray(beforeUpdateDomesticOtherDepreciationByYear, totalFiscalYearsByProject),
            /** 仕損費 */
            scrapCost: convertToStringArray(beforeUpdateScrapCostByYear, totalFiscalYearsByProject),
            /** 販売費及び一般管理費 */
            sGAExpenses: convertToStringArray(beforeUpdateSGAExpensesByYear, totalFiscalYearsByProject),
            /** 生産工場販管費 */
            appliedLaborCost: convertToStringArray(beforeUpdateAppliedLaborCostByYear, totalFiscalYearsByProject),
            /** 国内工場販管費 */
            domesticSgaCost: convertToStringArray(beforeUpdateDomesticSgaCostByYear, totalFiscalYearsByProject),
            /** 本社・販社販管費 */
            salesManagementCost: convertToStringArray(beforeUpdateSalesManagementCostByYear, totalFiscalYearsByProject),
            /** 開発費 */
            devCost: convertToStringArray(beforeUpdateDevCostByYear, totalFiscalYearsByProject),
            /** ロイヤリティ */
            royalty: convertToStringArray(beforeUpdateRoyaltyByYear, totalFiscalYearsByProject),
            /** その他の経費 */
            otherCost: convertToStringArray(beforeUpdateOtherCostByYear, totalFiscalYearsByProject),
            /** 営業利益 */
            profit: convertToStringArray(beforeUpdateProfitByYear, totalFiscalYearsByProject),
            /** 営業利益率 */
            profitRate: convertToStringArray(beforeUpdateProfitRateByYear, totalFiscalYearsByProject),
            /** 実質的営業利益率 */
            realOperatingProfitRate: hasCustomerSpecifiedParts
              ? convertToStringArray(beforeUpdateRealOperatingProfitRateByYear, totalFiscalYearsByProject)
              : [],
            /** 実質的営業利益率（合計） */
            // 更新前の客先指定部品材料費はbeforeUpdateMonthlyProfitAndLossDataから取得
            totalRealOperatingProfitRate: hasCustomerSpecifiedParts
              ? (() => {
                const totalProfit = beforeUpdateProfitByYear.reduce((sum, v) => sum.plus(v), new Decimal(0));
                const totalSales = beforeUpdateTotalSalesByYear.reduce((sum, v) => sum.plus(v), new Decimal(0));
                const totalCustCost = beforeUpdateMonthlyProfitAndLossData?.customerSpecifiedPartsCost
                  ? beforeUpdateMonthlyProfitAndLossData.customerSpecifiedPartsCost.reduce((sum, v) => sum.plus(v || new Decimal(0)), new Decimal(0))
                  : new Decimal(0);
                const totalRealSales = totalSales.minus(totalCustCost);
                if (totalRealSales.isZero()) return "0";
                return totalProfit.div(totalRealSales).times(100).toString();
              })()
              : undefined,
          },
          improvementEffectByProcess: isSimple ? undefined : improvementEffectByProcess,
          improvementTotal: isSimple ? undefined : {
            scrapCost: scrapCostTotal.toString(),
            repairCost: repairCostTotal.toString(),
            consumablesCost: consumablesCostTotal.toString(),
            total: (scrapCostTotal + repairCostTotal + consumablesCostTotal).toString(),
          },
          improvementEffectIndicatorByProcess: updateImprovementEffectIndicatorByProcess,
          improvementEffectCostByProcess: updateImprovementEffectCostByProcess,
          improvementTotalIndicator: updateImprovementTotalIndicator,
          improvementTotalCost: updateImprovementTotalCost,
        };
        setUpdateInvestmentProfitLossData(updateInvestmentProfitLoss);

        // 更新投資損益試算データ（JPY版）を構築
        const displayUpdateInvestmentProfitLoss: UpdateInvestmentProfitLossData = {
          beforeUpdateProfitAndLossByYear: {
            totalSales: convertToStringArray(displayBeforeUpdateTotalSalesByYear, totalFiscalYearsByProject),
            productSales: convertToStringArray(displayBeforeUpdateProductSalesByYear, totalFiscalYearsByProject),
            moldSales: convertToStringArray(displayBeforeUpdateMoldSalesByYear, totalFiscalYearsByProject),
            devSales: convertToStringArray(displayBeforeUpdateDevSalesByYear, totalFiscalYearsByProject),
            equipmentSales: convertToStringArray(displayBeforeUpdateEquipmentSalesByYear, totalFiscalYearsByProject),
            salesCost: convertToStringArray(displayBeforeUpdateSalesCostByYear, totalFiscalYearsByProject),
            totalMaterialCost: convertToStringArray(displayBeforeUpdateTotalMaterialCostByYear, totalFiscalYearsByProject),
            outsideCost: convertToStringArray(displayBeforeUpdateOutsideCostByYear, totalFiscalYearsByProject),
            laborCost: convertToStringArray(displayBeforeUpdateLaborCostByYear, totalFiscalYearsByProject),
            domesticLaborCost: convertToStringArray(displayBeforeUpdateDomesticLaborCostByYear, totalFiscalYearsByProject),
            factoryExpenses: convertToStringArray(displayBeforeUpdateFactoryExpensesByYear, totalFiscalYearsByProject),
            overHeadCost: convertToStringArray(displayBeforeUpdateOverHeadCostByYear, totalFiscalYearsByProject),
            depreciation: convertToStringArray(displayBeforeUpdateDepreciationByYear, totalFiscalYearsByProject),
            equipmentDepreciation: convertToStringArray(displayBeforeUpdateEquipmentDepreciationByYear, totalFiscalYearsByProject),
            factoryCost: convertToStringArray(displayBeforeUpdateFactoryCostByYear, totalFiscalYearsByProject),
            pastEquipmentCost: convertToStringArray(displayBeforeUpdatePastEquipmentCostByYear, totalFiscalYearsByProject),
            otherEquipmentCost: convertToStringArray(displayBeforeUpdateOtherEquipmentCostByYear, totalFiscalYearsByProject),
            domesticFactoryExpenses: convertToStringArray(displayBeforeUpdateDomesticFactoryExpensesByYear, totalFiscalYearsByProject),
            domesticOverHeadCost: convertToStringArray(displayBeforeUpdateDomesticOverHeadCostByYear, totalFiscalYearsByProject),
            domesticDepreciation: convertToStringArray(displayBeforeUpdateDomesticDepreciationByYear, totalFiscalYearsByProject),
            domesticInvestmentDepreciation: convertToStringArray(displayBeforeUpdateDomesticInvestmentDepreciationByYear, totalFiscalYearsByProject),
            domesticFactoryBuildingDepreciation: convertToStringArray(displayBeforeUpdateDomesticFactoryBuildingDepreciationByYear, totalFiscalYearsByProject),
            domesticOtherDepreciation: convertToStringArray(displayBeforeUpdateDomesticOtherDepreciationByYear, totalFiscalYearsByProject),
            scrapCost: convertToStringArray(displayBeforeUpdateScrapCostByYear, totalFiscalYearsByProject),
            sGAExpenses: convertToStringArray(displayBeforeUpdateSGAExpensesByYear, totalFiscalYearsByProject),
            appliedLaborCost: convertToStringArray(displayBeforeUpdateAppliedLaborCostByYear, totalFiscalYearsByProject),
            domesticSgaCost: convertToStringArray(displayBeforeUpdateDomesticSgaCostByYear, totalFiscalYearsByProject),
            salesManagementCost: convertToStringArray(displayBeforeUpdateSalesManagementCostByYear, totalFiscalYearsByProject),
            devCost: convertToStringArray(displayBeforeUpdateDevCostByYear, totalFiscalYearsByProject),
            royalty: convertToStringArray(displayBeforeUpdateRoyaltyByYear, totalFiscalYearsByProject),
            otherCost: convertToStringArray(displayBeforeUpdateOtherCostByYear, totalFiscalYearsByProject),
            profit: convertToStringArray(displayBeforeUpdateProfitByYear, totalFiscalYearsByProject),
            profitRate: convertToStringArray(displayBeforeUpdateProfitRateByYear, totalFiscalYearsByProject),
            realOperatingProfitRate: hasCustomerSpecifiedParts
              ? convertToStringArray(displayBeforeUpdateRealOperatingProfitRateByYear, totalFiscalYearsByProject)
              : [],
            totalRealOperatingProfitRate: hasCustomerSpecifiedParts
              ? (() => {
                const totalProfit = displayBeforeUpdateProfitByYear.reduce((sum, v) => sum.plus(v), new Decimal(0));
                const totalSales = displayBeforeUpdateTotalSalesByYear.reduce((sum, v) => sum.plus(v), new Decimal(0));
                const totalCustCost = displayBeforeUpdateMonthlyProfitAndLossData?.customerSpecifiedPartsCost
                  ? displayBeforeUpdateMonthlyProfitAndLossData.customerSpecifiedPartsCost.reduce((sum, v) => sum.plus(v || new Decimal(0)), new Decimal(0))
                  : new Decimal(0);
                const totalRealSales = totalSales.minus(totalCustCost);
                if (totalRealSales.isZero()) return "0";
                return totalProfit.div(totalRealSales).times(100).toString();
              })()
              : undefined,
          },
          improvementEffectByProcess: isSimple ? undefined : improvementEffectByProcess,
          improvementTotal: isSimple ? undefined : {
            scrapCost: scrapCostTotal.toString(),
            repairCost: repairCostTotal.toString(),
            consumablesCost: consumablesCostTotal.toString(),
            total: (scrapCostTotal + repairCostTotal + consumablesCostTotal).toString(),
          },
          improvementEffectIndicatorByProcess: updateImprovementEffectIndicatorByProcess,
          improvementEffectCostByProcess: updateDisplayImprovementEffectCostByProcess,
          improvementTotalIndicator: updateImprovementTotalIndicator,
          improvementTotalCost: updateDisplayImprovementTotalCost,
        };
        setDisplayUpdateInvestmentProfitLossData(displayUpdateInvestmentProfitLoss);
        if (reviewDate && (selectedCurrency?.name || CurrencyNameJPY) === CurrencyNameJPY) {
          setJpySavedUpdateInvestmentData(displayUpdateInvestmentProfitLoss);
        }
      }

      // 合理化投資の場合は、合理化投資損益試算データを準備
      if (capexProjectData.purpose === PurposeType.Rationalization.code) {
        // プロジェクト開始日を取得
        const projectStartDate = capexProjectData?.projectStartDate || dayjs().format("YYYY-MM-DD");

        // 工場通貨を取得
        const factoryCurrency = currencyData.find(c => c.currencyId === localCurrencyId);
        const factoryCurrencyName = factoryCurrency?.currencyName || CurrencyNameJPY;

        // 表示通貨を取得
        const displayCurrencyName = CurrencyNameJPY;

        // 項目名取得関数
        const getItemName = (item: RationalizationProfitLossItem): string => {
          if (item.itemType === "custom" && item.customItemName) {
            return item.customItemName;
          }
          const itemNameMap: Record<string, string> = {
            productionQuantity: t("features.InvestmentScreening.ProjectProfitablityStatement.improvementItem.productionQuantity"),
            salesQuantity: t("features.InvestmentScreening.ProjectProfitablityStatement.improvementItem.salesQuantity"),
            laborCost: t("features.InvestmentScreening.ProjectProfitablityStatement.improvementItem.laborCost"),
            repairCost: t("features.InvestmentScreening.ProjectProfitablityStatement.improvementItem.repairCost"),
            consumablesCost: t("features.InvestmentScreening.ProjectProfitablityStatement.improvementItem.consumablesCost"),
            maintenanceCost: t("features.InvestmentScreening.ProjectProfitablityStatement.improvementItem.maintenanceCost"),
            scrapCost: t("features.InvestmentScreening.ProjectProfitablityStatement.improvementItem.scrapCost"),
            totalCost: t("features.InvestmentScreening.ProjectProfitablityStatement.improvementItem.totalCost"),
            targetEquipmentDepreciation: t("features.InvestmentScreening.ProjectProfitablityStatement.improvementItem.targetEquipmentDepreciation"),
            otherDepreciation: t("features.InvestmentScreening.ProjectProfitablityStatement.improvementItem.otherDepreciation"),
          };
          return itemNameMap[item.itemType] || item.itemType;
        };

        // 月次データを年度ごとに集計する関数
        const aggregateMonthlyDataByYearForSave = (
          monthlyData: Record<string, number>,
          fiscalYears: string[]
        ): Decimal[] => {
          return fiscalYears.map((fiscalYear) => {
            const yearMatch = fiscalYear.match(/(\d{4})年/);
            if (!yearMatch) return new Decimal(0);
            const endYear = parseInt(yearMatch[1], 10);
            let total = new Decimal(0);
            for (let month = 4; month <= 12; month++) {
              const key = `${endYear - 1}-${String(month).padStart(2, "0")}`;
              total = total.plus(monthlyData[key] || 0);
            }
            for (let month = 1; month <= 3; month++) {
              const key = `${endYear}-${String(month).padStart(2, "0")}`;
              total = total.plus(monthlyData[key] || 0);
            }
            return total;
          });
        };

        // 工程通貨から工場通貨への変換関数
        const convertToFactoryCurrency = (value: Decimal, sourceCurrencyId: string | undefined): Decimal => {
          if (!sourceCurrencyId) return value;
          const sourceCurrency = currencyData.find(c => c.currencyId === sourceCurrencyId);
          if (!sourceCurrency?.currencyName || sourceCurrency.currencyName === factoryCurrencyName) {
            return value;
          }
          return convertWithRate(value, sourceCurrency.currencyName, factoryCurrencyName);
        };

        // 工程通貨から表示通貨への変換関数
        const convertToDisplayCurrency = (value: Decimal, sourceCurrencyId: string | undefined): Decimal => {
          if (!sourceCurrencyId) return value;
          const sourceCurrency = currencyData.find(c => c.currencyId === sourceCurrencyId);
          if (!sourceCurrency?.currencyName || sourceCurrency.currencyName === displayCurrencyName) {
            return value;
          }
          return convertWithRate(value, sourceCurrency.currencyName, displayCurrencyName);
        };

        // 年度ラベル取得
        const fiscalYearsString = getFiscalYearsString(projectStartDate, totalFiscalYearsByProject);

        // 合理化による改善効果（指標）- 工程別
        const improvementEffectIndicatorByProcess: RationalizationImprovementEffectByProcess[] = rationalizationSummaryData
          .filter(summary => summary.indicatorItems && summary.indicatorItems.length > 0)
          .map(summary => {
            const processName = uniqueProcessData.find((p: UniqueProcessType) => p.uniqueProcessId === summary.processId)?.uniqueProcessName || "";
            const items: RationalizationImprovementEffectItem[] = summary.indicatorItems!.map(item => {
              const beforeByYear = aggregateMonthlyDataByYearForSave(item.beforeMonthlyData, fiscalYearsString);
              const afterByYear = aggregateMonthlyDataByYearForSave(item.afterMonthlyData, fiscalYearsString);
              const beforeTotal = beforeByYear.reduce((sum, v) => sum.plus(v), new Decimal(0));
              const afterTotal = afterByYear.reduce((sum, v) => sum.plus(v), new Decimal(0));
              return {
                itemType: item.itemType,
                itemName: getItemName(item),
                beforeByYear: beforeByYear.map(v => v.toString()),
                beforeTotal: beforeTotal.toString(),
                afterByYear: afterByYear.map(v => v.toString()),
                afterTotal: afterTotal.toString(),
              };
            });
            return {
              processId: summary.processId || "",
              processName: processName,
              items: items,
            };
          });

        // 合理化による改善効果（費用）- 工程別（工場通貨に変換）
        const improvementEffectCostByProcess: RationalizationImprovementEffectByProcess[] = rationalizationSummaryData
          .filter(summary => summary.costItems && summary.costItems.length > 0)
          .map(summary => {
            const processName = uniqueProcessData.find((p: UniqueProcessType) => p.uniqueProcessId === summary.processId)?.uniqueProcessName || "";
            const items: RationalizationImprovementEffectItem[] = summary.costItems!.map(item => {
              const beforeByYear = aggregateMonthlyDataByYearForSave(item.beforeMonthlyData, fiscalYearsString);
              const afterByYear = aggregateMonthlyDataByYearForSave(item.afterMonthlyData, fiscalYearsString);
              // 工場通貨に変換
              const beforeByYearConverted = beforeByYear.map(v => convertToFactoryCurrency(v, summary.costCurrencyId));
              const afterByYearConverted = afterByYear.map(v => convertToFactoryCurrency(v, summary.costCurrencyId));
              const beforeTotal = beforeByYearConverted.reduce((sum, v) => sum.plus(v), new Decimal(0));
              const afterTotal = afterByYearConverted.reduce((sum, v) => sum.plus(v), new Decimal(0));
              return {
                itemType: item.itemType,
                itemName: getItemName(item),
                beforeByYear: beforeByYearConverted.map(v => v.toString()),
                beforeTotal: beforeTotal.toString(),
                afterByYear: afterByYearConverted.map(v => v.toString()),
                afterTotal: afterTotal.toString(),
              };
            });
            return {
              processId: summary.processId || "",
              processName: processName,
              items: items,
            };
          });

        // 改善効果の合計（指標）- 全工程集約
        const allIndicatorItemsMap = new Map<string, { itemType: string; itemName: string; beforeByYear: Decimal[]; afterByYear: Decimal[] }>();
        rationalizationSummaryData.forEach(summary => {
          if (summary.indicatorItems) {
            summary.indicatorItems.forEach(item => {
              const key = item.itemType === "custom" ? `custom_${item.customItemName}` : item.itemType;
              const beforeByYear = aggregateMonthlyDataByYearForSave(item.beforeMonthlyData, fiscalYearsString);
              const afterByYear = aggregateMonthlyDataByYearForSave(item.afterMonthlyData, fiscalYearsString);
              if (allIndicatorItemsMap.has(key)) {
                const existing = allIndicatorItemsMap.get(key)!;
                existing.beforeByYear = existing.beforeByYear.map((v, i) => v.plus(beforeByYear[i] || 0));
                existing.afterByYear = existing.afterByYear.map((v, i) => v.plus(afterByYear[i] || 0));
              } else {
                allIndicatorItemsMap.set(key, {
                  itemType: item.itemType,
                  itemName: getItemName(item),
                  beforeByYear: beforeByYear,
                  afterByYear: afterByYear,
                });
              }
            });
          }
        });
        const improvementTotalIndicator: RationalizationImprovementTotalItem[] = Array.from(allIndicatorItemsMap.values()).map(item => {
          const differenceByYear = item.afterByYear.map((after, i) => after.minus(item.beforeByYear[i]));
          const differenceTotal = differenceByYear.reduce((sum, v) => sum.plus(v), new Decimal(0));
          return {
            itemType: item.itemType,
            itemName: item.itemName,
            differenceByYear: differenceByYear.map(v => v.toString()),
            differenceTotal: differenceTotal.toString(),
          };
        });

        // 改善効果の合計（費用）- 全工程集約（工場通貨に変換）
        const allCostItemsMap = new Map<string, { itemType: string; itemName: string; beforeByYear: Decimal[]; afterByYear: Decimal[] }>();
        rationalizationSummaryData.forEach(summary => {
          if (summary.costItems) {
            summary.costItems.forEach(item => {
              const key = item.itemType === "custom" ? `custom_${item.customItemName}` : item.itemType;
              const beforeByYear = aggregateMonthlyDataByYearForSave(item.beforeMonthlyData, fiscalYearsString);
              const afterByYear = aggregateMonthlyDataByYearForSave(item.afterMonthlyData, fiscalYearsString);
              // 工場通貨に変換
              const beforeByYearConverted = beforeByYear.map(v => convertToFactoryCurrency(v, summary.costCurrencyId));
              const afterByYearConverted = afterByYear.map(v => convertToFactoryCurrency(v, summary.costCurrencyId));
              if (allCostItemsMap.has(key)) {
                const existing = allCostItemsMap.get(key)!;
                existing.beforeByYear = existing.beforeByYear.map((v, i) => v.plus(beforeByYearConverted[i] || 0));
                existing.afterByYear = existing.afterByYear.map((v, i) => v.plus(afterByYearConverted[i] || 0));
              } else {
                allCostItemsMap.set(key, {
                  itemType: item.itemType,
                  itemName: getItemName(item),
                  beforeByYear: beforeByYearConverted,
                  afterByYear: afterByYearConverted,
                });
              }
            });
          }
        });
        const improvementTotalCost: RationalizationImprovementTotalItem[] = Array.from(allCostItemsMap.values()).map(item => {
          const differenceByYear = item.afterByYear.map((after, i) => after.minus(item.beforeByYear[i]));
          const differenceTotal = differenceByYear.reduce((sum, v) => sum.plus(v), new Decimal(0));
          return {
            itemType: item.itemType,
            itemName: item.itemName,
            differenceByYear: differenceByYear.map(v => v.toString()),
            differenceTotal: differenceTotal.toString(),
          };
        });

        // 改善効果（費用）- 工程別（表示通貨版）
        const displayImprovementEffectCostByProcess: RationalizationImprovementEffectByProcess[] = rationalizationSummaryData
          .filter(summary => summary.costItems && summary.costItems.length > 0)
          .map(summary => {
            const processName = uniqueProcessData.find((p: UniqueProcessType) => p.uniqueProcessId === summary.processId)?.uniqueProcessName || "";
            const items: RationalizationImprovementEffectItem[] = summary.costItems!.map(item => {
              const beforeByYear = aggregateMonthlyDataByYearForSave(item.beforeMonthlyData, fiscalYearsString);
              const afterByYear = aggregateMonthlyDataByYearForSave(item.afterMonthlyData, fiscalYearsString);
              const beforeByYearConverted = beforeByYear.map(v => convertToDisplayCurrency(v, summary.costCurrencyId));
              const afterByYearConverted = afterByYear.map(v => convertToDisplayCurrency(v, summary.costCurrencyId));
              const beforeTotal = beforeByYearConverted.reduce((sum, v) => sum.plus(v), new Decimal(0));
              const afterTotal = afterByYearConverted.reduce((sum, v) => sum.plus(v), new Decimal(0));
              return {
                itemType: item.itemType,
                itemName: getItemName(item),
                beforeByYear: beforeByYearConverted.map(v => v.toString()),
                beforeTotal: beforeTotal.toString(),
                afterByYear: afterByYearConverted.map(v => v.toString()),
                afterTotal: afterTotal.toString(),
              };
            });
            return {
              processId: summary.processId || "",
              processName: processName,
              items: items,
            };
          });

        // 改善効果の合計（費用）- 全工程集約（表示通貨版）
        const allDisplayCostItemsMap = new Map<string, { itemType: string; itemName: string; beforeByYear: Decimal[]; afterByYear: Decimal[] }>();
        rationalizationSummaryData.forEach(summary => {
          if (summary.costItems) {
            summary.costItems.forEach(item => {
              const key = item.itemType === "custom" ? `custom_${item.customItemName}` : item.itemType;
              const beforeByYear = aggregateMonthlyDataByYearForSave(item.beforeMonthlyData, fiscalYearsString);
              const afterByYear = aggregateMonthlyDataByYearForSave(item.afterMonthlyData, fiscalYearsString);
              const beforeByYearConverted = beforeByYear.map(v => convertToDisplayCurrency(v, summary.costCurrencyId));
              const afterByYearConverted = afterByYear.map(v => convertToDisplayCurrency(v, summary.costCurrencyId));
              if (allDisplayCostItemsMap.has(key)) {
                const existing = allDisplayCostItemsMap.get(key)!;
                existing.beforeByYear = existing.beforeByYear.map((v, i) => v.plus(beforeByYearConverted[i] || 0));
                existing.afterByYear = existing.afterByYear.map((v, i) => v.plus(afterByYearConverted[i] || 0));
              } else {
                allDisplayCostItemsMap.set(key, {
                  itemType: item.itemType,
                  itemName: getItemName(item),
                  beforeByYear: beforeByYearConverted,
                  afterByYear: afterByYearConverted,
                });
              }
            });
          }
        });
        const displayImprovementTotalCost: RationalizationImprovementTotalItem[] = Array.from(allDisplayCostItemsMap.values()).map(item => {
          const differenceByYear = item.afterByYear.map((after, i) => after.minus(item.beforeByYear[i]));
          const differenceTotal = differenceByYear.reduce((sum, v) => sum.plus(v), new Decimal(0));
          return {
            itemType: item.itemType,
            itemName: item.itemName,
            differenceByYear: differenceByYear.map(v => v.toString()),
            differenceTotal: differenceTotal.toString(),
          };
        });

        // 年度別損益（合理化前）を構築（ローカル通貨版）
        const beforeProfitAndLossByYear: RationalizationProfitAndLossByYear = {
          totalSales: beforeRationalizationTotalSalesByYear.map(v => v.toString()),
          productSales: beforeRationalizationProductSalesByYear.map(v => v.toString()),
          moldSales: beforeRationalizationMoldSalesByYear.map(v => v.toString()),
          devSales: beforeRationalizationDevSalesByYear.map(v => v.toString()),
          equipmentSales: beforeRationalizationEquipmentSalesByYear.map(v => v.toString()),
          salesCost: beforeRationalizationSalesCostByYear.map(v => v.toString()),
          totalMaterialCost: beforeRationalizationTotalMaterialCostByYear.map(v => v.toString()),
          outsideCost: beforeRationalizationOutsideCostByYear.map(v => v.toString()),
          laborCost: beforeRationalizationLaborCostByYear.map(v => v.toString()),
          domesticLaborCost: beforeRationalizationDomesticLaborCostByYear.map(v => v.toString()),
          factoryExpenses: beforeRationalizationFactoryExpensesByYear.map(v => v.toString()),
          overHeadCost: beforeRationalizationOverHeadCostByYear.map(v => v.toString()),
          depreciation: beforeRationalizationDepreciationByYear.map(v => v.toString()),
          equipmentDepreciation: beforeRationalizationEquipmentDepreciationByYear.map(v => v.toString()),
          factoryCost: beforeRationalizationFactoryCostByYear.map(v => v.toString()),
          pastEquipmentCost: beforeRationalizationPastEquipmentCostByYear.map(v => v.toString()),
          otherEquipmentCost: beforeRationalizationOtherEquipmentCostByYear.map(v => v.toString()),
          domesticFactoryExpenses: beforeRationalizationDomesticFactoryExpensesByYear.map(v => v.toString()),
          domesticOverHeadCost: beforeRationalizationDomesticOverHeadCostByYear.map(v => v.toString()),
          domesticDepreciation: beforeRationalizationDomesticDepreciationByYear.map(v => v.toString()),
          domesticInvestmentDepreciation: beforeRationalizationDomesticInvestmentDepreciationByYear.map(v => v.toString()),
          domesticFactoryBuildingDepreciation: beforeRationalizationDomesticFactoryBuildingDepreciationByYear.map(v => v.toString()),
          domesticOtherDepreciation: beforeRationalizationDomesticOtherDepreciationByYear.map(v => v.toString()),
          scrapCost: beforeRationalizationScrapCostByYear.map(v => v.toString()),
          sGAExpenses: beforeRationalizationSGAExpensesByYear.map(v => v.toString()),
          appliedLaborCost: beforeRationalizationAppliedLaborCostByYear.map(v => v.toString()),
          domesticSgaCost: beforeRationalizationDomesticSgaCostByYear.map(v => v.toString()),
          salesManagementCost: beforeRationalizationSalesManagementCostByYear.map(v => v.toString()),
          devCost: beforeRationalizationDevCostByYear.map(v => v.toString()),
          royalty: beforeRationalizationRoyaltyByYear.map(v => v.toString()),
          otherCost: beforeRationalizationOtherCostByYear.map(v => v.toString()),
          profit: beforeRationalizationProfitByYear.map(v => v.toString()),
          profitRate: beforeRationalizationProfitRateByYear.map(v => v.toString()),
          realOperatingProfitRate: beforeRationalizationRealOperatingProfitRateByYear.map(v => v.toString()),
          // 合計
          totalSalesSum: beforeRationalizationTotalSalesByYear.reduce((sum, v) => sum.plus(v), new Decimal(0)).toString(),
          productSalesSum: beforeRationalizationProductSalesByYear.reduce((sum, v) => sum.plus(v), new Decimal(0)).toString(),
          moldSalesSum: beforeRationalizationMoldSalesByYear.reduce((sum, v) => sum.plus(v), new Decimal(0)).toString(),
          devSalesSum: beforeRationalizationDevSalesByYear.reduce((sum, v) => sum.plus(v), new Decimal(0)).toString(),
          equipmentSalesSum: beforeRationalizationEquipmentSalesByYear.reduce((sum, v) => sum.plus(v), new Decimal(0)).toString(),
          salesCostSum: beforeRationalizationSalesCostByYear.reduce((sum, v) => sum.plus(v), new Decimal(0)).toString(),
          totalMaterialCostSum: beforeRationalizationTotalMaterialCostByYear.reduce((sum, v) => sum.plus(v), new Decimal(0)).toString(),
          outsideCostSum: beforeRationalizationOutsideCostByYear.reduce((sum, v) => sum.plus(v), new Decimal(0)).toString(),
          laborCostSum: beforeRationalizationLaborCostByYear.reduce((sum, v) => sum.plus(v), new Decimal(0)).toString(),
          domesticLaborCostSum: beforeRationalizationDomesticLaborCostByYear.reduce((sum, v) => sum.plus(v), new Decimal(0)).toString(),
          factoryExpensesSum: beforeRationalizationFactoryExpensesByYear.reduce((sum, v) => sum.plus(v), new Decimal(0)).toString(),
          overHeadCostSum: beforeRationalizationOverHeadCostByYear.reduce((sum, v) => sum.plus(v), new Decimal(0)).toString(),
          depreciationSum: beforeRationalizationDepreciationByYear.reduce((sum, v) => sum.plus(v), new Decimal(0)).toString(),
          equipmentDepreciationSum: beforeRationalizationEquipmentDepreciationByYear.reduce((sum, v) => sum.plus(v), new Decimal(0)).toString(),
          factoryCostSum: beforeRationalizationFactoryCostByYear.reduce((sum, v) => sum.plus(v), new Decimal(0)).toString(),
          pastEquipmentCostSum: beforeRationalizationPastEquipmentCostByYear.reduce((sum, v) => sum.plus(v), new Decimal(0)).toString(),
          otherEquipmentCostSum: beforeRationalizationOtherEquipmentCostByYear.reduce((sum, v) => sum.plus(v), new Decimal(0)).toString(),
          domesticFactoryExpensesSum: beforeRationalizationDomesticFactoryExpensesByYear.reduce((sum, v) => sum.plus(v), new Decimal(0)).toString(),
          domesticOverHeadCostSum: beforeRationalizationDomesticOverHeadCostByYear.reduce((sum, v) => sum.plus(v), new Decimal(0)).toString(),
          domesticDepreciationSum: beforeRationalizationDomesticDepreciationByYear.reduce((sum, v) => sum.plus(v), new Decimal(0)).toString(),
          domesticInvestmentDepreciationSum: beforeRationalizationDomesticInvestmentDepreciationByYear.reduce((sum, v) => sum.plus(v), new Decimal(0)).toString(),
          domesticFactoryBuildingDepreciationSum: beforeRationalizationDomesticFactoryBuildingDepreciationByYear.reduce((sum, v) => sum.plus(v), new Decimal(0)).toString(),
          domesticOtherDepreciationSum: beforeRationalizationDomesticOtherDepreciationByYear.reduce((sum, v) => sum.plus(v), new Decimal(0)).toString(),
          scrapCostSum: beforeRationalizationScrapCostByYear.reduce((sum, v) => sum.plus(v), new Decimal(0)).toString(),
          sGAExpensesSum: beforeRationalizationSGAExpensesByYear.reduce((sum, v) => sum.plus(v), new Decimal(0)).toString(),
          appliedLaborCostSum: beforeRationalizationAppliedLaborCostByYear.reduce((sum, v) => sum.plus(v), new Decimal(0)).toString(),
          domesticSgaCostSum: beforeRationalizationDomesticSgaCostByYear.reduce((sum, v) => sum.plus(v), new Decimal(0)).toString(),
          salesManagementCostSum: beforeRationalizationSalesManagementCostByYear.reduce((sum, v) => sum.plus(v), new Decimal(0)).toString(),
          devCostSum: beforeRationalizationDevCostByYear.reduce((sum, v) => sum.plus(v), new Decimal(0)).toString(),
          royaltySum: beforeRationalizationRoyaltyByYear.reduce((sum, v) => sum.plus(v), new Decimal(0)).toString(),
          otherCostSum: beforeRationalizationOtherCostByYear.reduce((sum, v) => sum.plus(v), new Decimal(0)).toString(),
          profitSum: beforeRationalizationProfitByYear.reduce((sum, v) => sum.plus(v), new Decimal(0)).toString(),
          profitRateSum: (() => {
            const totalSales = beforeRationalizationTotalSalesByYear.reduce((sum, v) => sum.plus(v), new Decimal(0));
            const totalProfit = beforeRationalizationProfitByYear.reduce((sum, v) => sum.plus(v), new Decimal(0));
            return totalSales.isZero() ? "0" : totalProfit.div(totalSales).times(100).toString();
          })(),
          realOperatingProfitRateSum: (() => {
            // 比率計算のため、同一通貨内での計算が必要（ローカル通貨版を使用）
            const totalSales = beforeRationalizationTotalSalesByYear.reduce((sum, v) => sum.plus(v), new Decimal(0));
            const totalProfit = beforeRationalizationProfitByYear.reduce((sum, v) => sum.plus(v), new Decimal(0));
            const totalCustomerSpecified = beforeRationalizationCustomerSpecifiedPartsCostByYear.reduce((sum, v) => sum.plus(v), new Decimal(0));
            const realSales = totalSales.minus(totalCustomerSpecified);
            return realSales.isZero() ? "0" : totalProfit.div(realSales).times(100).toString();
          })(),
        };

        // 年度別損益（合理化後）を構築（ローカル通貨版）
        const afterProfitAndLossByYear: RationalizationProfitAndLossByYear = {
          totalSales: totalSalesByYear.map(v => v.toString()),
          productSales: productSalesByYear.map(v => v.toString()),
          moldSales: moldSalesByYear.map(v => v.toString()),
          devSales: devSalesByYear.map(v => v.toString()),
          equipmentSales: equipmentSalesByYear.map(v => v.toString()),
          salesCost: salesCostByYear.map(v => v.toString()),
          totalMaterialCost: totalMaterialCostByYear.map(v => v.toString()),
          outsideCost: outsideCostByYear.map(v => v.toString()),
          laborCost: laborCostByYear.map(v => v.toString()),
          domesticLaborCost: domesticLaborCostByYear.map(v => v.toString()),
          factoryExpenses: factoryExpensesByYear.map(v => v.toString()),
          overHeadCost: overHeadCostByYear.map(v => v.toString()),
          depreciation: depreciationByYear.map(v => v.toString()),
          equipmentDepreciation: equipmentDepreciationByYear.map(v => v.toString()),
          factoryCost: factoryCostByYear.map(v => v.toString()),
          pastEquipmentCost: pastEquipmentCostByYear.map(v => v.toString()),
          otherEquipmentCost: otherEquipmentCostByYear.map(v => v.toString()),
          domesticFactoryExpenses: domesticFactoryExpensesByYear.map(v => v.toString()),
          domesticOverHeadCost: domesticOverHeadCostByYear.map(v => v.toString()),
          domesticDepreciation: domesticDepreciationByYear.map(v => v.toString()),
          domesticInvestmentDepreciation: domesticInvestmentDepreciationByYear.map(v => v.toString()),
          domesticFactoryBuildingDepreciation: domesticFactoryBuildingDepreciationByYear.map(v => v.toString()),
          domesticOtherDepreciation: domesticOtherDepreciationByYear.map(v => v.toString()),
          scrapCost: scrapCostByYear.map(v => v.toString()),
          sGAExpenses: sGAExpensesByYear.map(v => v.toString()),
          appliedLaborCost: appliedLaborCostByYear.map(v => v.toString()),
          domesticSgaCost: domesticSgaCostByYear.map(v => v.toString()),
          salesManagementCost: salesManagementCostByYear.map(v => v.toString()),
          devCost: devCostByYear.map(v => v.toString()),
          royalty: royaltyByYear.map(v => v.toString()),
          otherCost: otherCostByYear.map(v => v.toString()),
          profit: profitByYear.map(v => v.toString()),
          profitRate: profitRateByYear.map(v => v.toString()),
          realOperatingProfitRate: realOperatingProfitRateByYear.map(v => v.toString()),
          // 合計
          totalSalesSum: totalSalesByYear.reduce((sum, v) => sum.plus(v), new Decimal(0)).toString(),
          productSalesSum: productSalesByYear.reduce((sum, v) => sum.plus(v), new Decimal(0)).toString(),
          moldSalesSum: moldSalesByYear.reduce((sum, v) => sum.plus(v), new Decimal(0)).toString(),
          devSalesSum: devSalesByYear.reduce((sum, v) => sum.plus(v), new Decimal(0)).toString(),
          equipmentSalesSum: equipmentSalesByYear.reduce((sum, v) => sum.plus(v), new Decimal(0)).toString(),
          salesCostSum: salesCostByYear.reduce((sum, v) => sum.plus(v), new Decimal(0)).toString(),
          totalMaterialCostSum: totalMaterialCostByYear.reduce((sum, v) => sum.plus(v), new Decimal(0)).toString(),
          outsideCostSum: outsideCostByYear.reduce((sum, v) => sum.plus(v), new Decimal(0)).toString(),
          laborCostSum: laborCostByYear.reduce((sum, v) => sum.plus(v), new Decimal(0)).toString(),
          domesticLaborCostSum: domesticLaborCostByYear.reduce((sum, v) => sum.plus(v), new Decimal(0)).toString(),
          factoryExpensesSum: factoryExpensesByYear.reduce((sum, v) => sum.plus(v), new Decimal(0)).toString(),
          overHeadCostSum: overHeadCostByYear.reduce((sum, v) => sum.plus(v), new Decimal(0)).toString(),
          depreciationSum: depreciationByYear.reduce((sum, v) => sum.plus(v), new Decimal(0)).toString(),
          equipmentDepreciationSum: equipmentDepreciationByYear.reduce((sum, v) => sum.plus(v), new Decimal(0)).toString(),
          factoryCostSum: factoryCostByYear.reduce((sum, v) => sum.plus(v), new Decimal(0)).toString(),
          pastEquipmentCostSum: pastEquipmentCostByYear.reduce((sum, v) => sum.plus(v), new Decimal(0)).toString(),
          otherEquipmentCostSum: otherEquipmentCostByYear.reduce((sum, v) => sum.plus(v), new Decimal(0)).toString(),
          domesticFactoryExpensesSum: domesticFactoryExpensesByYear.reduce((sum, v) => sum.plus(v), new Decimal(0)).toString(),
          domesticOverHeadCostSum: domesticOverHeadCostByYear.reduce((sum, v) => sum.plus(v), new Decimal(0)).toString(),
          domesticDepreciationSum: domesticDepreciationByYear.reduce((sum, v) => sum.plus(v), new Decimal(0)).toString(),
          domesticInvestmentDepreciationSum: domesticInvestmentDepreciationByYear.reduce((sum, v) => sum.plus(v), new Decimal(0)).toString(),
          domesticFactoryBuildingDepreciationSum: domesticFactoryBuildingDepreciationByYear.reduce((sum, v) => sum.plus(v), new Decimal(0)).toString(),
          domesticOtherDepreciationSum: domesticOtherDepreciationByYear.reduce((sum, v) => sum.plus(v), new Decimal(0)).toString(),
          scrapCostSum: scrapCostByYear.reduce((sum, v) => sum.plus(v), new Decimal(0)).toString(),
          sGAExpensesSum: sGAExpensesByYear.reduce((sum, v) => sum.plus(v), new Decimal(0)).toString(),
          appliedLaborCostSum: appliedLaborCostByYear.reduce((sum, v) => sum.plus(v), new Decimal(0)).toString(),
          domesticSgaCostSum: domesticSgaCostByYear.reduce((sum, v) => sum.plus(v), new Decimal(0)).toString(),
          salesManagementCostSum: salesManagementCostByYear.reduce((sum, v) => sum.plus(v), new Decimal(0)).toString(),
          devCostSum: devCostByYear.reduce((sum, v) => sum.plus(v), new Decimal(0)).toString(),
          royaltySum: royaltyByYear.reduce((sum, v) => sum.plus(v), new Decimal(0)).toString(),
          otherCostSum: otherCostByYear.reduce((sum, v) => sum.plus(v), new Decimal(0)).toString(),
          profitSum: profitByYear.reduce((sum, v) => sum.plus(v), new Decimal(0)).toString(),
          profitRateSum: (() => {
            const totalSales = totalSalesByYear.reduce((sum, v) => sum.plus(v), new Decimal(0));
            const totalProfit = profitByYear.reduce((sum, v) => sum.plus(v), new Decimal(0));
            return totalSales.isZero() ? "0" : totalProfit.div(totalSales).times(100).toString();
          })(),
          realOperatingProfitRateSum: (() => {
            // 比率計算のため、同一通貨内での計算が必要（ローカル通貨版を使用）
            const totalSales = totalSalesByYear.reduce((sum, v) => sum.plus(v), new Decimal(0));
            const totalProfit = profitByYear.reduce((sum, v) => sum.plus(v), new Decimal(0));
            const totalCustomerSpecified = customerSpecifiedPartsCostByYear.reduce((sum, v) => sum.plus(v), new Decimal(0));
            const realSales = totalSales.minus(totalCustomerSpecified);
            return realSales.isZero() ? "0" : totalProfit.div(realSales).times(100).toString();
          })(),
        };

        // 合理化投資損益試算データを構築（ローカル通貨版）
        const rationalizationInvestmentProfitLoss: RationalizationInvestmentProfitLossData = {
          fiscalYears: fiscalYearsString,
          beforeProfitAndLossByYear: beforeProfitAndLossByYear,
          afterProfitAndLossByYear: afterProfitAndLossByYear,
          improvementEffectIndicatorByProcess: improvementEffectIndicatorByProcess,
          improvementEffectCostByProcess: improvementEffectCostByProcess,
          improvementTotalIndicator: improvementTotalIndicator,
          improvementTotalCost: improvementTotalCost,
        };
        setRationalizationInvestmentProfitLossData(rationalizationInvestmentProfitLoss);

        // 合理化投資損益試算データ（JPY版）を構築
        const displayBeforeProfitAndLossByYear: RationalizationProfitAndLossByYear = {
          totalSales: displayBeforeRationalizationTotalSalesByYear.map(v => v.toString()),
          productSales: displayBeforeRationalizationProductSalesByYear.map(v => v.toString()),
          moldSales: displayBeforeRationalizationMoldSalesByYear.map(v => v.toString()),
          devSales: displayBeforeRationalizationDevSalesByYear.map(v => v.toString()),
          equipmentSales: displayBeforeRationalizationEquipmentSalesByYear.map(v => v.toString()),
          salesCost: displayBeforeRationalizationSalesCostByYear.map(v => v.toString()),
          totalMaterialCost: displayBeforeRationalizationTotalMaterialCostByYear.map(v => v.toString()),
          outsideCost: displayBeforeRationalizationOutsideCostByYear.map(v => v.toString()),
          laborCost: displayBeforeRationalizationLaborCostByYear.map(v => v.toString()),
          domesticLaborCost: displayBeforeRationalizationDomesticLaborCostByYear.map(v => v.toString()),
          factoryExpenses: displayBeforeRationalizationFactoryExpensesByYear.map(v => v.toString()),
          overHeadCost: displayBeforeRationalizationOverHeadCostByYear.map(v => v.toString()),
          depreciation: displayBeforeRationalizationDepreciationByYear.map(v => v.toString()),
          equipmentDepreciation: displayBeforeRationalizationEquipmentDepreciationByYear.map(v => v.toString()),
          factoryCost: displayBeforeRationalizationFactoryCostByYear.map(v => v.toString()),
          pastEquipmentCost: displayBeforeRationalizationPastEquipmentCostByYear.map(v => v.toString()),
          otherEquipmentCost: displayBeforeRationalizationOtherEquipmentCostByYear.map(v => v.toString()),
          domesticFactoryExpenses: displayBeforeRationalizationDomesticFactoryExpensesByYear.map(v => v.toString()),
          domesticOverHeadCost: displayBeforeRationalizationDomesticOverHeadCostByYear.map(v => v.toString()),
          domesticDepreciation: displayBeforeRationalizationDomesticDepreciationByYear.map(v => v.toString()),
          domesticInvestmentDepreciation: displayBeforeRationalizationDomesticInvestmentDepreciationByYear.map(v => v.toString()),
          domesticFactoryBuildingDepreciation: displayBeforeRationalizationDomesticFactoryBuildingDepreciationByYear.map(v => v.toString()),
          domesticOtherDepreciation: displayBeforeRationalizationDomesticOtherDepreciationByYear.map(v => v.toString()),
          scrapCost: displayBeforeRationalizationScrapCostByYear.map(v => v.toString()),
          sGAExpenses: displayBeforeRationalizationSGAExpensesByYear.map(v => v.toString()),
          appliedLaborCost: displayBeforeRationalizationAppliedLaborCostByYear.map(v => v.toString()),
          domesticSgaCost: displayBeforeRationalizationDomesticSgaCostByYear.map(v => v.toString()),
          salesManagementCost: displayBeforeRationalizationSalesManagementCostByYear.map(v => v.toString()),
          devCost: displayBeforeRationalizationDevCostByYear.map(v => v.toString()),
          royalty: displayBeforeRationalizationRoyaltyByYear.map(v => v.toString()),
          otherCost: displayBeforeRationalizationOtherCostByYear.map(v => v.toString()),
          profit: displayBeforeRationalizationProfitByYear.map(v => v.toString()),
          profitRate: displayBeforeRationalizationProfitRateByYear.map(v => v.toString()),
          realOperatingProfitRate: displayBeforeRationalizationRealOperatingProfitRateByYear.map(v => v.toString()),
          // 合計
          totalSalesSum: displayBeforeRationalizationTotalSalesByYear.reduce((sum, v) => sum.plus(v), new Decimal(0)).toString(),
          productSalesSum: displayBeforeRationalizationProductSalesByYear.reduce((sum, v) => sum.plus(v), new Decimal(0)).toString(),
          moldSalesSum: displayBeforeRationalizationMoldSalesByYear.reduce((sum, v) => sum.plus(v), new Decimal(0)).toString(),
          devSalesSum: displayBeforeRationalizationDevSalesByYear.reduce((sum, v) => sum.plus(v), new Decimal(0)).toString(),
          equipmentSalesSum: displayBeforeRationalizationEquipmentSalesByYear.reduce((sum, v) => sum.plus(v), new Decimal(0)).toString(),
          salesCostSum: displayBeforeRationalizationSalesCostByYear.reduce((sum, v) => sum.plus(v), new Decimal(0)).toString(),
          totalMaterialCostSum: displayBeforeRationalizationTotalMaterialCostByYear.reduce((sum, v) => sum.plus(v), new Decimal(0)).toString(),
          outsideCostSum: displayBeforeRationalizationOutsideCostByYear.reduce((sum, v) => sum.plus(v), new Decimal(0)).toString(),
          laborCostSum: displayBeforeRationalizationLaborCostByYear.reduce((sum, v) => sum.plus(v), new Decimal(0)).toString(),
          domesticLaborCostSum: displayBeforeRationalizationDomesticLaborCostByYear.reduce((sum, v) => sum.plus(v), new Decimal(0)).toString(),
          factoryExpensesSum: displayBeforeRationalizationFactoryExpensesByYear.reduce((sum, v) => sum.plus(v), new Decimal(0)).toString(),
          overHeadCostSum: displayBeforeRationalizationOverHeadCostByYear.reduce((sum, v) => sum.plus(v), new Decimal(0)).toString(),
          depreciationSum: displayBeforeRationalizationDepreciationByYear.reduce((sum, v) => sum.plus(v), new Decimal(0)).toString(),
          equipmentDepreciationSum: displayBeforeRationalizationEquipmentDepreciationByYear.reduce((sum, v) => sum.plus(v), new Decimal(0)).toString(),
          factoryCostSum: displayBeforeRationalizationFactoryCostByYear.reduce((sum, v) => sum.plus(v), new Decimal(0)).toString(),
          pastEquipmentCostSum: displayBeforeRationalizationPastEquipmentCostByYear.reduce((sum, v) => sum.plus(v), new Decimal(0)).toString(),
          otherEquipmentCostSum: displayBeforeRationalizationOtherEquipmentCostByYear.reduce((sum, v) => sum.plus(v), new Decimal(0)).toString(),
          domesticFactoryExpensesSum: displayBeforeRationalizationDomesticFactoryExpensesByYear.reduce((sum, v) => sum.plus(v), new Decimal(0)).toString(),
          domesticOverHeadCostSum: displayBeforeRationalizationDomesticOverHeadCostByYear.reduce((sum, v) => sum.plus(v), new Decimal(0)).toString(),
          domesticDepreciationSum: displayBeforeRationalizationDomesticDepreciationByYear.reduce((sum, v) => sum.plus(v), new Decimal(0)).toString(),
          domesticInvestmentDepreciationSum: displayBeforeRationalizationDomesticInvestmentDepreciationByYear.reduce((sum, v) => sum.plus(v), new Decimal(0)).toString(),
          domesticFactoryBuildingDepreciationSum: displayBeforeRationalizationDomesticFactoryBuildingDepreciationByYear.reduce((sum, v) => sum.plus(v), new Decimal(0)).toString(),
          domesticOtherDepreciationSum: displayBeforeRationalizationDomesticOtherDepreciationByYear.reduce((sum, v) => sum.plus(v), new Decimal(0)).toString(),
          scrapCostSum: displayBeforeRationalizationScrapCostByYear.reduce((sum, v) => sum.plus(v), new Decimal(0)).toString(),
          sGAExpensesSum: displayBeforeRationalizationSGAExpensesByYear.reduce((sum, v) => sum.plus(v), new Decimal(0)).toString(),
          appliedLaborCostSum: displayBeforeRationalizationAppliedLaborCostByYear.reduce((sum, v) => sum.plus(v), new Decimal(0)).toString(),
          domesticSgaCostSum: displayBeforeRationalizationDomesticSgaCostByYear.reduce((sum, v) => sum.plus(v), new Decimal(0)).toString(),
          salesManagementCostSum: displayBeforeRationalizationSalesManagementCostByYear.reduce((sum, v) => sum.plus(v), new Decimal(0)).toString(),
          devCostSum: displayBeforeRationalizationDevCostByYear.reduce((sum, v) => sum.plus(v), new Decimal(0)).toString(),
          royaltySum: displayBeforeRationalizationRoyaltyByYear.reduce((sum, v) => sum.plus(v), new Decimal(0)).toString(),
          otherCostSum: displayBeforeRationalizationOtherCostByYear.reduce((sum, v) => sum.plus(v), new Decimal(0)).toString(),
          profitSum: displayBeforeRationalizationProfitByYear.reduce((sum, v) => sum.plus(v), new Decimal(0)).toString(),
          profitRateSum: (() => {
            const totalSales = displayBeforeRationalizationTotalSalesByYear.reduce((sum, v) => sum.plus(v), new Decimal(0));
            const totalProfit = displayBeforeRationalizationProfitByYear.reduce((sum, v) => sum.plus(v), new Decimal(0));
            return totalSales.isZero() ? "0" : totalProfit.div(totalSales).times(100).toString();
          })(),
          realOperatingProfitRateSum: (() => {
            // 比率計算のため、同一通貨内での計算が必要（ローカル通貨版を使用）
            const totalSales = beforeRationalizationTotalSalesByYear.reduce((sum, v) => sum.plus(v), new Decimal(0));
            const totalProfit = beforeRationalizationProfitByYear.reduce((sum, v) => sum.plus(v), new Decimal(0));
            const totalCustomerSpecified = beforeRationalizationCustomerSpecifiedPartsCostByYear.reduce((sum, v) => sum.plus(v), new Decimal(0));
            const realSales = totalSales.minus(totalCustomerSpecified);
            return realSales.isZero() ? "0" : totalProfit.div(realSales).times(100).toString();
          })(),
        };

        const displayAfterProfitAndLossByYear: RationalizationProfitAndLossByYear = {
          totalSales: displayTotalSalesByYear.map(v => v.toString()),
          productSales: displayProductSalesByYear.map(v => v.toString()),
          moldSales: displayMoldSalesByYear.map(v => v.toString()),
          devSales: displayDevSalesByYear.map(v => v.toString()),
          equipmentSales: displayEquipmentSalesByYear.map(v => v.toString()),
          salesCost: displaySalesCostByYear.map(v => v.toString()),
          totalMaterialCost: displayTotalMaterialCostByYear.map(v => v.toString()),
          outsideCost: displayOutsideCostByYear.map(v => v.toString()),
          laborCost: displayLaborCostByYear.map(v => v.toString()),
          domesticLaborCost: displayDomesticLaborCostByYear.map(v => v.toString()),
          factoryExpenses: displayFactoryExpensesByYear.map(v => v.toString()),
          overHeadCost: displayOverHeadCostByYear.map(v => v.toString()),
          depreciation: displayDepreciationByYear.map(v => v.toString()),
          equipmentDepreciation: displayEquipmentDepreciationByYear.map(v => v.toString()),
          factoryCost: displayFactoryCostByYear.map(v => v.toString()),
          pastEquipmentCost: displayPastEquipmentCostByYear.map(v => v.toString()),
          otherEquipmentCost: displayOtherEquipmentCostByYear.map(v => v.toString()),
          domesticFactoryExpenses: displayDomesticFactoryExpensesByYear.map(v => v.toString()),
          domesticOverHeadCost: displayDomesticOverHeadCostByYear.map(v => v.toString()),
          domesticDepreciation: displayDomesticDepreciationByYear.map(v => v.toString()),
          domesticInvestmentDepreciation: displayDomesticInvestmentDepreciationByYear.map(v => v.toString()),
          domesticFactoryBuildingDepreciation: displayDomesticFactoryBuildingDepreciationByYear.map(v => v.toString()),
          domesticOtherDepreciation: displayDomesticOtherDepreciationByYear.map(v => v.toString()),
          scrapCost: displayScrapCostByYear.map(v => v.toString()),
          sGAExpenses: displaySGAExpensesByYear.map(v => v.toString()),
          appliedLaborCost: displayAppliedLaborCostByYear.map(v => v.toString()),
          domesticSgaCost: displayDomesticSgaCostByYear.map(v => v.toString()),
          salesManagementCost: displaySalesManagementCostByYear.map(v => v.toString()),
          devCost: displayDevCostByYear.map(v => v.toString()),
          royalty: displayRoyaltyByYear.map(v => v.toString()),
          otherCost: displayOtherCostByYear.map(v => v.toString()),
          profit: displayProfitByYear.map(v => v.toString()),
          profitRate: displayProfitRateByYear.map(v => v.toString()),
          realOperatingProfitRate: displayRealOperatingProfitRateByYear.map(v => v.toString()),
          // 合計
          totalSalesSum: displayTotalSalesByYear.reduce((sum, v) => sum.plus(v), new Decimal(0)).toString(),
          productSalesSum: displayProductSalesByYear.reduce((sum, v) => sum.plus(v), new Decimal(0)).toString(),
          moldSalesSum: displayMoldSalesByYear.reduce((sum, v) => sum.plus(v), new Decimal(0)).toString(),
          devSalesSum: displayDevSalesByYear.reduce((sum, v) => sum.plus(v), new Decimal(0)).toString(),
          equipmentSalesSum: displayEquipmentSalesByYear.reduce((sum, v) => sum.plus(v), new Decimal(0)).toString(),
          salesCostSum: displaySalesCostByYear.reduce((sum, v) => sum.plus(v), new Decimal(0)).toString(),
          totalMaterialCostSum: displayTotalMaterialCostByYear.reduce((sum, v) => sum.plus(v), new Decimal(0)).toString(),
          outsideCostSum: displayOutsideCostByYear.reduce((sum, v) => sum.plus(v), new Decimal(0)).toString(),
          laborCostSum: displayLaborCostByYear.reduce((sum, v) => sum.plus(v), new Decimal(0)).toString(),
          domesticLaborCostSum: displayDomesticLaborCostByYear.reduce((sum, v) => sum.plus(v), new Decimal(0)).toString(),
          factoryExpensesSum: displayFactoryExpensesByYear.reduce((sum, v) => sum.plus(v), new Decimal(0)).toString(),
          overHeadCostSum: displayOverHeadCostByYear.reduce((sum, v) => sum.plus(v), new Decimal(0)).toString(),
          depreciationSum: displayDepreciationByYear.reduce((sum, v) => sum.plus(v), new Decimal(0)).toString(),
          equipmentDepreciationSum: displayEquipmentDepreciationByYear.reduce((sum, v) => sum.plus(v), new Decimal(0)).toString(),
          factoryCostSum: displayFactoryCostByYear.reduce((sum, v) => sum.plus(v), new Decimal(0)).toString(),
          pastEquipmentCostSum: displayPastEquipmentCostByYear.reduce((sum, v) => sum.plus(v), new Decimal(0)).toString(),
          otherEquipmentCostSum: displayOtherEquipmentCostByYear.reduce((sum, v) => sum.plus(v), new Decimal(0)).toString(),
          domesticFactoryExpensesSum: displayDomesticFactoryExpensesByYear.reduce((sum, v) => sum.plus(v), new Decimal(0)).toString(),
          domesticOverHeadCostSum: displayDomesticOverHeadCostByYear.reduce((sum, v) => sum.plus(v), new Decimal(0)).toString(),
          domesticDepreciationSum: displayDomesticDepreciationByYear.reduce((sum, v) => sum.plus(v), new Decimal(0)).toString(),
          domesticInvestmentDepreciationSum: displayDomesticInvestmentDepreciationByYear.reduce((sum, v) => sum.plus(v), new Decimal(0)).toString(),
          domesticFactoryBuildingDepreciationSum: displayDomesticFactoryBuildingDepreciationByYear.reduce((sum, v) => sum.plus(v), new Decimal(0)).toString(),
          domesticOtherDepreciationSum: displayDomesticOtherDepreciationByYear.reduce((sum, v) => sum.plus(v), new Decimal(0)).toString(),
          scrapCostSum: displayScrapCostByYear.reduce((sum, v) => sum.plus(v), new Decimal(0)).toString(),
          sGAExpensesSum: displaySGAExpensesByYear.reduce((sum, v) => sum.plus(v), new Decimal(0)).toString(),
          appliedLaborCostSum: displayAppliedLaborCostByYear.reduce((sum, v) => sum.plus(v), new Decimal(0)).toString(),
          domesticSgaCostSum: displayDomesticSgaCostByYear.reduce((sum, v) => sum.plus(v), new Decimal(0)).toString(),
          salesManagementCostSum: displaySalesManagementCostByYear.reduce((sum, v) => sum.plus(v), new Decimal(0)).toString(),
          devCostSum: displayDevCostByYear.reduce((sum, v) => sum.plus(v), new Decimal(0)).toString(),
          royaltySum: displayRoyaltyByYear.reduce((sum, v) => sum.plus(v), new Decimal(0)).toString(),
          otherCostSum: displayOtherCostByYear.reduce((sum, v) => sum.plus(v), new Decimal(0)).toString(),
          profitSum: displayProfitByYear.reduce((sum, v) => sum.plus(v), new Decimal(0)).toString(),
          profitRateSum: (() => {
            const totalSales = displayTotalSalesByYear.reduce((sum, v) => sum.plus(v), new Decimal(0));
            const totalProfit = displayProfitByYear.reduce((sum, v) => sum.plus(v), new Decimal(0));
            return totalSales.isZero() ? "0" : totalProfit.div(totalSales).times(100).toString();
          })(),
          realOperatingProfitRateSum: (() => {
            // 比率計算のため、同一通貨内での計算が必要（ローカル通貨版を使用）
            const totalSales = totalSalesByYear.reduce((sum, v) => sum.plus(v), new Decimal(0));
            const totalProfit = profitByYear.reduce((sum, v) => sum.plus(v), new Decimal(0));
            const totalCustomerSpecified = customerSpecifiedPartsCostByYear.reduce((sum, v) => sum.plus(v), new Decimal(0));
            const realSales = totalSales.minus(totalCustomerSpecified);
            return realSales.isZero() ? "0" : totalProfit.div(realSales).times(100).toString();
          })(),
        };

        const displayRationalizationInvestmentProfitLoss: RationalizationInvestmentProfitLossData = {
          fiscalYears: fiscalYearsString,
          beforeProfitAndLossByYear: displayBeforeProfitAndLossByYear,
          afterProfitAndLossByYear: displayAfterProfitAndLossByYear,
          improvementEffectIndicatorByProcess: improvementEffectIndicatorByProcess,
          improvementEffectCostByProcess: displayImprovementEffectCostByProcess,
          improvementTotalIndicator: improvementTotalIndicator,
          improvementTotalCost: displayImprovementTotalCost,
        };
        setDisplayRationalizationInvestmentProfitLossData(displayRationalizationInvestmentProfitLoss);
        if (reviewDate && (selectedCurrency?.name || CurrencyNameJPY) === CurrencyNameJPY) {
          setJpySavedRationalizationInvestmentData(displayRationalizationInvestmentProfitLoss);
        }
      }

      // 確認モーダルを開く
      onOpen();

    } catch (error) {
      console.error(`${functionName} ERROR: `, error);
    }

  };

  // 確認ボタン押下処理（確認モーダル）
  const handleConfirm: React.MouseEventHandler<HTMLButtonElement> = async (event) => {

    const functionName = "handleConfirm";

    try {

      // フォーカスを外す
      event.currentTarget.blur();

      if (isSaving) return;
      setIsSaving(true);

      // JPY版データを準備（二重通貨変換回避のため、常にJPY版を保存）
      let jpyProfitAndLoss: OriginalProfitAndLossTable | null = null;
      let jpyProfitAndLossByMonth: OriginalProfitAndLossByMonthTable | null = null;
      let jpyUpdateInvestmentProfitLoss: UpdateInvestmentProfitLossData | null = null;
      let jpyRationalizationInvestmentProfitLoss: RationalizationInvestmentProfitLossData | null = null;

      // 損益非表示モードでは表データが存在しないため、JPY版の構築処理自体をスキップする
      if (!isProfitLossHiddenMode) {

        const jpyCurrencyId = currencyData.find(c => c.currencyName === CurrencyNameJPY)?.currencyId;
        const localCurrencyForJpy = currencyData.find(c => c.currencyId === localCurrencyId)?.currencyName || "";

        if (reviewDate && jpySavedProfitAndLoss) {
          // 振り返りモード: 初回計算時のJPY版stateを使用（月別レート変換済み）
          jpyProfitAndLoss = jpySavedProfitAndLoss;
          jpyProfitAndLossByMonth = jpySavedProfitAndLossByMonth;
        } else if (selectedCurrency?.name === CurrencyNameJPY) {
          // 選択通貨がJPYの場合は、displayOriginalProfitAndLossをそのまま使用
          jpyProfitAndLoss = displayOriginalProfitAndLoss;
          jpyProfitAndLossByMonth = displayOriginalProfitAndLossByMonth;
        } else if (localCurrencyForJpy === CurrencyNameJPY) {
          // ローカル通貨がJPYの場合は、originalProfitAndLossをそのまま使用
          jpyProfitAndLoss = originalProfitAndLoss;
          jpyProfitAndLossByMonth = originalProfitAndLossByMonth;
        } else if (currencyData.length > 0 && localCurrencyForJpy) {
          // 選択通貨がJPY以外の場合は、displayOriginalProfitAndLossと同じロジックでJPY版を計算
          // ローカル通貨→JPYに変換（displayTotalSalesByYear等の計算と同じ方式）
          const convertDecimalArrayToJpy = (arr: Decimal[]): Decimal[] => {
            return arr.map((value, idx) => {
              return new Decimal(convertWithRate(value, localCurrencyForJpy, CurrencyNameJPY) || 0);
            });
          };
          const convertToStringArray = (arr: Decimal[], length: number): string[] => {
            return Array.from({ length }, (_, i) => (arr[i] || new Decimal(0)).toString());
          };

          // useState変数からJPY版を計算（displayOriginalProfitAndLossの計算と同じ方式）
          const jpyTotalSalesByYear = convertDecimalArrayToJpy(totalSalesByYear);
          const jpyProductSalesByYear = convertDecimalArrayToJpy(productSalesByYear);
          const jpyMoldSalesByYear = convertDecimalArrayToJpy(moldSalesByYear);
          const jpyDevSalesByYear = convertDecimalArrayToJpy(devSalesByYear);
          const jpyEquipmentSalesByYear = convertDecimalArrayToJpy(equipmentSalesByYear);
          const jpySalesCostByYear = convertDecimalArrayToJpy(salesCostByYear);
          const jpyTotalMaterialCostByYear = convertDecimalArrayToJpy(totalMaterialCostByYear);
          const jpyOutsideCostByYear = convertDecimalArrayToJpy(outsideCostByYear);
          const jpyLaborCostByYear = convertDecimalArrayToJpy(laborCostByYear);
          const jpyDomesticLaborCostByYear = convertDecimalArrayToJpy(domesticLaborCostByYear);
          const jpyFactoryExpensesByYear = convertDecimalArrayToJpy(factoryExpensesByYear);
          const jpyOverHeadCostByYear = convertDecimalArrayToJpy(overHeadCostByYear);
          const jpyDepreciationByYear = convertDecimalArrayToJpy(depreciationByYear);
          const jpyEquipmentDepreciationByYear = convertDecimalArrayToJpy(equipmentDepreciationByYear);
          const jpyFactoryCostByYear = convertDecimalArrayToJpy(factoryCostByYear);
          const jpyPastEquipmentCostByYear = convertDecimalArrayToJpy(pastEquipmentCostByYear);
          const jpyOtherEquipmentCostByYear = convertDecimalArrayToJpy(otherEquipmentCostByYear);
          const jpyDomesticFactoryExpensesByYear = convertDecimalArrayToJpy(domesticFactoryExpensesByYear);
          const jpyDomesticOverHeadCostByYear = convertDecimalArrayToJpy(domesticOverHeadCostByYear);
          const jpyDomesticDepreciationByYear = convertDecimalArrayToJpy(domesticDepreciationByYear);
          const jpyDomesticInvestmentDepreciationByYear = convertDecimalArrayToJpy(domesticInvestmentDepreciationByYear);
          const jpyDomesticFactoryBuildingDepreciationByYear = convertDecimalArrayToJpy(domesticFactoryBuildingDepreciationByYear);
          const jpyDomesticOtherDepreciationByYear = convertDecimalArrayToJpy(domesticOtherDepreciationByYear);
          const jpyScrapCostByYear = convertDecimalArrayToJpy(scrapCostByYear);
          const jpySGAExpensesByYear = convertDecimalArrayToJpy(sGAExpensesByYear);
          const jpyAppliedLaborCostByYear = convertDecimalArrayToJpy(appliedLaborCostByYear);
          const jpyDomesticSgaCostByYear = convertDecimalArrayToJpy(domesticSgaCostByYear);
          const jpySalesManagementCostByYear = convertDecimalArrayToJpy(salesManagementCostByYear);
          const jpyDevCostByYear = convertDecimalArrayToJpy(devCostByYear);
          const jpyRoyaltyByYear = convertDecimalArrayToJpy(royaltyByYear);
          const jpyOtherCostByYear = convertDecimalArrayToJpy(otherCostByYear);
          const jpyProfitByYear = convertDecimalArrayToJpy(profitByYear);
          const jpyInterestRateByYear = convertDecimalArrayToJpy(interestRateByYear);
          const jpyCapexAmountByYear = convertDecimalArrayToJpy(capexAmountByYear);
          const jpyRecoveredResourcesByYear = convertDecimalArrayToJpy(recoveredResourcesByYear);
          const jpyFactoryAndOtherEquipmentCostByYear = convertDecimalArrayToJpy(factoryAndOtherEquipmentCostByYear);
          const jpyUncollectedAmountByYear = convertDecimalArrayToJpy(uncollectedAmountByYear);
          const jpyNopatByYear = convertDecimalArrayToJpy(nopatByYear);
          const jpyUndepreciatedByYear = convertDecimalArrayToJpy(undepreciatedByYear);
          const jpyTotalCapexAmountByYear = convertDecimalArrayToJpy(totalCapexAmountByYear);
          const jpyFreeCashFlowByYear = convertDecimalArrayToJpy(freeCashFlowByYear);
          const jpyPresentValueByYear = convertDecimalArrayToJpy(presentValueByYear);
          const jpyNetPresentValue = netPresentValue ? new Decimal(convertWithRate(netPresentValue, localCurrencyForJpy, CurrencyNameJPY) || 0) : null;

          jpyProfitAndLoss = {
            profitAndLossByYear: {
              totalSales: convertToStringArray(jpyTotalSalesByYear, totalFiscalYearsByProject),
              productSales: convertToStringArray(jpyProductSalesByYear, totalFiscalYearsByProject),
              moldSales: convertToStringArray(jpyMoldSalesByYear, totalFiscalYearsByProject),
              devSales: convertToStringArray(jpyDevSalesByYear, totalFiscalYearsByProject),
              equipmentSales: convertToStringArray(jpyEquipmentSalesByYear, totalFiscalYearsByProject),
              salesCost: convertToStringArray(jpySalesCostByYear, totalFiscalYearsByProject),
              totalMaterialCost: convertToStringArray(jpyTotalMaterialCostByYear, totalFiscalYearsByProject),
              outsideCost: convertToStringArray(jpyOutsideCostByYear, totalFiscalYearsByProject),
              laborCost: convertToStringArray(jpyLaborCostByYear, totalFiscalYearsByProject),
              domesticLaborCost: convertToStringArray(jpyDomesticLaborCostByYear, totalFiscalYearsByProject),
              factoryExpenses: convertToStringArray(jpyFactoryExpensesByYear, totalFiscalYearsByProject),
              overHeadCost: convertToStringArray(jpyOverHeadCostByYear, totalFiscalYearsByProject),
              depreciation: convertToStringArray(jpyDepreciationByYear, totalFiscalYearsByProject),
              equipmentDepreciation: convertToStringArray(jpyEquipmentDepreciationByYear, totalFiscalYearsByProject),
              factoryCost: convertToStringArray(jpyFactoryCostByYear, totalFiscalYearsByProject),
              pastEquipmentCost: convertToStringArray(jpyPastEquipmentCostByYear, totalFiscalYearsByProject),
              otherEquipmentCost: convertToStringArray(jpyOtherEquipmentCostByYear, totalFiscalYearsByProject),
              domesticFactoryExpenses: convertToStringArray(jpyDomesticFactoryExpensesByYear, totalFiscalYearsByProject),
              domesticOverHeadCost: convertToStringArray(jpyDomesticOverHeadCostByYear, totalFiscalYearsByProject),
              domesticDepreciation: convertToStringArray(jpyDomesticDepreciationByYear, totalFiscalYearsByProject),
              domesticInvestmentDepreciation: convertToStringArray(jpyDomesticInvestmentDepreciationByYear, totalFiscalYearsByProject),
              domesticFactoryBuildingDepreciation: convertToStringArray(jpyDomesticFactoryBuildingDepreciationByYear, totalFiscalYearsByProject),
              domesticOtherDepreciation: convertToStringArray(jpyDomesticOtherDepreciationByYear, totalFiscalYearsByProject),
              scrapCost: convertToStringArray(jpyScrapCostByYear, totalFiscalYearsByProject),
              sGAExpenses: convertToStringArray(jpySGAExpensesByYear, totalFiscalYearsByProject),
              appliedLaborCost: convertToStringArray(jpyAppliedLaborCostByYear, totalFiscalYearsByProject),
              domesticSgaCost: convertToStringArray(jpyDomesticSgaCostByYear, totalFiscalYearsByProject),
              salesManagementCost: convertToStringArray(jpySalesManagementCostByYear, totalFiscalYearsByProject),
              devCost: convertToStringArray(jpyDevCostByYear, totalFiscalYearsByProject),
              royalty: convertToStringArray(jpyRoyaltyByYear, totalFiscalYearsByProject),
              otherCost: convertToStringArray(jpyOtherCostByYear, totalFiscalYearsByProject),
              profit: convertToStringArray(jpyProfitByYear, totalFiscalYearsByProject),
              profitRate: profitRateByYear.map(v => v.toString()), // 率は変換不要
              realOperatingProfitRate: realOperatingProfitRateByYear.map(v => v.toString()), // 率は変換不要
              totalRealOperatingProfitRate: originalProfitAndLoss?.profitAndLossByYear.totalRealOperatingProfitRate, // 率は変換不要
            },
            paybackPeriodByYear: {
              interestRateByYear: convertToStringArray(jpyInterestRateByYear, totalFiscalYearsByProject),
              paybackPeriod: originalProfitAndLoss?.paybackPeriodByYear.paybackPeriod || "", // 年数は変換不要
            },
            RecoverySimulationByYear: {
              capexAmount: convertToStringArray(jpyCapexAmountByYear, totalFiscalYearsByProject),
              recoveredResources: convertToStringArray(jpyRecoveredResourcesByYear, totalFiscalYearsByProject),
              profit: convertToStringArray(jpyProfitByYear, totalFiscalYearsByProject),
              depreciation: convertToStringArray(jpyDepreciationByYear, totalFiscalYearsByProject),
              factoryAndOtherEquipmentCost: convertToStringArray(jpyFactoryAndOtherEquipmentCostByYear, totalFiscalYearsByProject),
              uncollectedAmount: convertToStringArray(jpyUncollectedAmountByYear, totalFiscalYearsByProject),
              collectionPeriod: originalProfitAndLoss?.RecoverySimulationByYear.collectionPeriod || "", // 年数は変換不要
            },
            roicByYear: {
              nopatByYear: convertToStringArray(jpyNopatByYear, totalFiscalYearsByProject),
              profit: convertToStringArray(jpyProfitByYear, totalFiscalYearsByProject),
              setTaxRate: originalProfitAndLoss?.roicByYear.setTaxRate || [], // 率は変換不要
              undepreciatedByYear: convertToStringArray(jpyUndepreciatedByYear, totalFiscalYearsByProject),
              totalCapexAmount: convertToStringArray(jpyTotalCapexAmountByYear, totalFiscalYearsByProject),
              equipmentDepreciation: convertToStringArray(jpyEquipmentDepreciationByYear, totalFiscalYearsByProject),
              roicByYear: roicByYear.map(v => v.toString()), // 率は変換不要
              hurdleRate: originalProfitAndLoss?.roicByYear.hurdleRate || [], // 率は変換不要
            },
            dcf: {
              freeCashFlow: convertToStringArray([new Decimal(0), ...jpyFreeCashFlowByYear], totalFiscalYearsByProject + 1),
              profit: convertToStringArray([new Decimal(0), ...jpyProfitByYear], totalFiscalYearsByProject + 1),
              setTaxRate: originalProfitAndLoss?.dcf.setTaxRate || [], // 率は変換不要
              depreciation: convertToStringArray([new Decimal(0), ...jpyDepreciationByYear], totalFiscalYearsByProject + 1),
              capexAmount: convertToStringArray([new Decimal(0), ...jpyCapexAmountByYear], totalFiscalYearsByProject + 1),
              discountRate: originalProfitAndLoss?.dcf.discountRate || [], // 率は変換不要
              presentValue: convertToStringArray([new Decimal(0), ...jpyPresentValueByYear], totalFiscalYearsByProject + 1),
              netPresentValue: jpyNetPresentValue?.toString() || "",
            },
          };

          // 月別データのJPY版を計算
          if (monthlyProfitAndLossData) {
            const jpyTotalSalesByMonth = monthlyProfitAndLossData.totalSales.map((value, idx) => {
              return new Decimal(convertWithRate(value || new Decimal(0), localCurrencyForJpy, CurrencyNameJPY) || 0);
            });
            const jpySalesCostByMonth = monthlyProfitAndLossData.salesCost.map((value, idx) => {
              return new Decimal(convertWithRate(value || new Decimal(0), localCurrencyForJpy, CurrencyNameJPY) || 0);
            });
            const jpyProfitByMonth = monthlyProfitAndLossData.profit.map((value, idx) => {
              return new Decimal(convertWithRate(value || new Decimal(0), localCurrencyForJpy, CurrencyNameJPY) || 0);
            });
            jpyProfitAndLossByMonth = {
              labels: labelsByMonth,
              totalSales: jpyTotalSalesByMonth.map(v => v.toString()),
              salesCost: jpySalesCostByMonth.map(v => v.toString()),
              profit: jpyProfitByMonth.map(v => v.toString()),
              quantityByPartNo: quantityByPartNoByMonth, // 数量は変換不要
            };
          }
        }

        // 更新投資・合理化投資のJPY版データを準備（対象PJ本体のjpyProfitAndLossと同じ二重通貨変換回避ロジック）
        // 金額系フィールドのみJPYへ変換し、比率系フィールドは変換しない汎用ヘルパー
        const ratioFieldNames = new Set([
          "profitRate", "realOperatingProfitRate", "totalRealOperatingProfitRate",
          "profitRateSum", "realOperatingProfitRateSum",
        ]);
        const convertMoneyFieldsToJpy = (data: Record<string, any>): any => {
          const result: Record<string, any> = { ...data };
          for (const key of Object.keys(data)) {
            if (ratioFieldNames.has(key)) continue;
            const value = data[key];
            if (Array.isArray(value)) {
              result[key] = value.map((v: any) =>
                typeof v === "string" && v !== ""
                  ? convertWithRate(new Decimal(v), localCurrencyForJpy, CurrencyNameJPY).toString()
                  : v
              );
            } else if (typeof value === "string" && value !== "") {
              result[key] = convertWithRate(new Decimal(value), localCurrencyForJpy, CurrencyNameJPY).toString();
            }
          }
          return result;
        };

        if (capexProjectData.purpose === PurposeType.UpdateAndOther.code) {
          if (reviewDate && jpySavedUpdateInvestmentData) {
            // 振り返りモード: 初回計算時のJPY版stateを使用（月別レート変換済み）
            jpyUpdateInvestmentProfitLoss = jpySavedUpdateInvestmentData;
          } else if (selectedCurrency?.name === CurrencyNameJPY) {
            // 選択通貨がJPYの場合は、displayUpdateInvestmentProfitLossDataをそのまま使用
            jpyUpdateInvestmentProfitLoss = displayUpdateInvestmentProfitLossData;
          } else if (localCurrencyForJpy === CurrencyNameJPY) {
            // ローカル通貨がJPYの場合は、updateInvestmentProfitLossDataをそのまま使用
            jpyUpdateInvestmentProfitLoss = updateInvestmentProfitLossData;
          } else if (updateInvestmentProfitLossData && displayUpdateInvestmentProfitLossData && localCurrencyForJpy) {
            // 選択通貨がJPY以外の場合は、ローカル通貨版(updateInvestmentProfitLossData)からJPY版を計算
            // 改善効果系は既にJPY固定計算済みのdisplayUpdateInvestmentProfitLossDataから引き継ぐ
            jpyUpdateInvestmentProfitLoss = {
              ...displayUpdateInvestmentProfitLossData,
              beforeUpdateProfitAndLossByYear: convertMoneyFieldsToJpy(updateInvestmentProfitLossData.beforeUpdateProfitAndLossByYear),
            };
          }
        }

        if (capexProjectData.purpose === PurposeType.Rationalization.code) {
          if (reviewDate && jpySavedRationalizationInvestmentData) {
            // 振り返りモード: 初回計算時のJPY版stateを使用（月別レート変換済み）
            jpyRationalizationInvestmentProfitLoss = jpySavedRationalizationInvestmentData;
          } else if (selectedCurrency?.name === CurrencyNameJPY) {
            // 選択通貨がJPYの場合は、displayRationalizationInvestmentProfitLossDataをそのまま使用
            jpyRationalizationInvestmentProfitLoss = displayRationalizationInvestmentProfitLossData;
          } else if (localCurrencyForJpy === CurrencyNameJPY) {
            // ローカル通貨がJPYの場合は、rationalizationInvestmentProfitLossDataをそのまま使用
            jpyRationalizationInvestmentProfitLoss = rationalizationInvestmentProfitLossData;
          } else if (rationalizationInvestmentProfitLossData && displayRationalizationInvestmentProfitLossData && localCurrencyForJpy) {
            // 選択通貨がJPY以外の場合は、ローカル通貨版(rationalizationInvestmentProfitLossData)からJPY版を計算
            // 改善効果系は既にJPY固定計算済みのdisplayRationalizationInvestmentProfitLossDataから引き継ぐ
            jpyRationalizationInvestmentProfitLoss = {
              ...displayRationalizationInvestmentProfitLossData,
              beforeProfitAndLossByYear: rationalizationInvestmentProfitLossData.beforeProfitAndLossByYear
                ? convertMoneyFieldsToJpy(rationalizationInvestmentProfitLossData.beforeProfitAndLossByYear)
                : undefined,
              afterProfitAndLossByYear: rationalizationInvestmentProfitLossData.afterProfitAndLossByYear
                ? convertMoneyFieldsToJpy(rationalizationInvestmentProfitLossData.afterProfitAndLossByYear)
                : undefined,
            };
          }
        }

      } // !isProfitLossHiddenMode

      // 損益非表示モードでは損益試算データの保存自体が不要なため、確認APIのみ呼び出す
      if (!isProfitLossHiddenMode) {

        // プロジェクトテーブルを更新（更新投資/合理化投資の場合は損益試算データも一緒に保存）
        // ローカル通貨版とJPY版の両方を保存（二重通貨変換回避のため）
        const status = await updateProfitablityStatement(
          projectId,
          originalProfitAndLoss,  // ローカル通貨版データ
          originalProfitAndLossByMonth,  // ローカル通貨版データ
          capexProjectData.purpose === PurposeType.UpdateAndOther.code ? updateInvestmentProfitLossData : null,  // 更新投資（ローカル通貨版）
          capexProjectData.purpose === PurposeType.Rationalization.code ? rationalizationInvestmentProfitLossData : null,  // 合理化投資（ローカル通貨版）
          jpyProfitAndLoss,  // JPY版データ（displayOriginalProfitAndLoss、常にJPY固定）
          jpyProfitAndLossByMonth,  // JPY版データ（displayOriginalProfitAndLossByMonth、常にJPY固定）
          capexProjectData.purpose === PurposeType.UpdateAndOther.code
            ? jpyUpdateInvestmentProfitLoss
            : null,  // 更新投資（JPY版、常にJPY固定）
          capexProjectData.purpose === PurposeType.Rationalization.code
            ? jpyRationalizationInvestmentProfitLoss
            : null  // 合理化投資（JPY版、常にJPY固定）
        );

        if (status === "success") {
          toast({
            title: TITLE,
            description: t("features.InvestmentScreening.ProjectProfitablityStatement.message.saveSuccess"),
            status: "success",
            duration: 5000,
            isClosable: true,
          });
          // DBデータを再取得
          await fetchData();
        } else if (status === "expired") {
          toast({
            title: TITLE,
            description: Messages.expiredMessage,
            status: "warning",
            duration: 5000,
            isClosable: true,
          });
          console.warn("Conflict error. updateProfitablityStatement.");
        } else {
          // 失敗した場合は処理を中断
          toast({
            title: TITLE,
            description: t("features.InvestmentScreening.ProjectProfitablityStatement.message.saveFailed"),
            status: "error",
            duration: 5000,
            isClosable: true,
          });
        }

      } // !isProfitLossHiddenMode

      // ワークフローテーブルを更新
      await updateConfirm(
        WebAPI,
        projectId,
        WorkflowId.ProjectProfitablityStatement,
        employeeId,
        confirmSource,
        "true",
        reviewDate,
      );

      // 確認担当者JSONを取得
      const confirmor = await getConfirmor(WebAPI, projectId, reviewDate);
      setConfirmor(confirmor);

      // 確認モーダルを閉じる
      onClose();

      setIsSaving(false);

    } catch (error) {
      // 失敗した場合は処理を中断
      toast({
        title: t("features.InvestmentScreening.ProjectProfitablityStatement.message.saveFailedTitle"),
        description: t("features.InvestmentScreening.ProjectProfitablityStatement.message.dbUpdateError"),
        status: "error",
        duration: 5000,
        isClosable: true,
      });
      console.error(`${functionName} ERROR:`, error);
      return;
    }

  };

  // -----------------------------
  // UI表示
  // -----------------------------
  // 0年目列を含むデータのための処理

  const fiscalYearsAdjusted = ["0", ...fiscalYearsString];
  // {t("features.InvestmentScreening.ProjectProfitablityStatement.heading.dcf")}は0年目を含むため、先頭に0を追加（表示用stateを使用）
  const freeCashFlowAdjustedByYear = [new Decimal(0), ...displayFreeCashFlowByYear];
  const profitAdjustedByYear = [new Decimal(0), ...displayProfitByYear];
  const taxRateAdjustedByYear = [new Decimal(0), ...taxRateByYear]; // 率なので元state
  const depreciationAdjustedByYear = [new Decimal(0), ...displayDepreciationByYear];
  const capexAmountAdjustedByYear = [new Decimal(0), ...displayCapexAmountByYear];
  const discountRateAdjustedByYear = [new Decimal(0), ...discountRateByYear]; // 率なので元state
  const presentValueAdjustedByYear = [new Decimal(0), ...displayPresentValueByYear];

  // 関数宣言
  const adjustArrayLength = (array: Decimal[], max: number, defaultValue: Decimal) => {
    const result = [...array];
    // 配列の長さを調整
    while (result.length < max) {
      result.push(new Decimal(defaultValue));
    }
    return result;
  };

  /**
   *  ここにダウンロードボタンの処理項目を作成
  */

  // #region Excelダウンロード

  // PartNo
  const createExportDataForPartNo = (partNo: string, displayPartNo: string): (string | number)[][] => {
    // Excel出力用：JPY版のdisplay系stateを使用（二重変換を回避）

    // 各データ取得（display系stateを使用 - 入力通貨→表示通貨で直接変換済み）
    // 数量配列の取得（通貨に依存しないため元stateを使用）
    const partQuantitiesData = quantityByPartNo.find(item => item.partNo === partNo);
    let quantities: Decimal[] = partQuantitiesData ? partQuantitiesData.quantities : [];
    quantities = adjustArrayLength(quantities, totalFiscalYearsByProject, new Decimal(0));

    // 単価配列の取得（display系stateを使用）
    const partPricesData = displayPriceByPartNo.find(item => item.partNo === partNo);
    const prices: Decimal[] = adjustArrayLength(partPricesData ? partPricesData.prices : [], totalFiscalYearsByProject, new Decimal(0));

    // 売上金額配列の取得（display系stateを使用）
    const partTotalSalesData = displayProductSalesByPartNo.find(item => item.partNo === partNo);
    const totalSales: Decimal[] = adjustArrayLength(partTotalSalesData ? partTotalSalesData.totalSales : [], totalFiscalYearsByProject, new Decimal(0));

    // PartNo金型配列の取得（display系stateを使用）
    const partMoldsData = displayMoldSalesByPartNo.find(item => item.partNo === partNo);
    const moldPrices: Decimal[] = adjustArrayLength(partMoldsData ? partMoldsData.totalSales : [], totalFiscalYearsByProject, new Decimal(0));

    // PartNo開発費配列の取得（display系stateを使用）
    const partDevsData = displayDevSalesByPartNo.find(item => item.partNo === partNo);
    const devPrices: Decimal[] = adjustArrayLength(partDevsData ? partDevsData.totalSales : [], totalFiscalYearsByProject, new Decimal(0));

    // PartNo製品売上高、金型、開発費合計（display系stateを使用）
    const partMergesData = displayTotalSalesByPartNo.find(item => item.partNo === partNo);
    const mergePrices: Decimal[] = adjustArrayLength(partMergesData ? partMergesData.totalSales : [], totalFiscalYearsByProject, new Decimal(0));

    // 材料単価配列の取得（年度別加重平均、display系stateを使用）
    const partMaterialCostData = displayMaterialCostByPartNo.find(item => item.partNo === partNo);
    const materialCosts: Decimal[] = adjustArrayLength(partMaterialCostData ? partMaterialCostData.materialCosts : [], totalFiscalYearsByProject, new Decimal(0));

    // 材料費配列の取得（display系stateを使用）
    const partTotalMaterialCostsData = displayTotalMaterialCostByPartNo.find(item => item.partNo === partNo);
    const totalMaterialCosts: Decimal[] = adjustArrayLength(partTotalMaterialCostsData ? partTotalMaterialCostsData.totalMaterialCosts : [], totalFiscalYearsByProject, new Decimal(0));

    // スループット額配列の取得（display系stateを使用）
    const partThroughputsData = displayThroughputByPartNo.find(item => item.partNo === partNo);
    const throughputs: Decimal[] = adjustArrayLength(partThroughputsData ? partThroughputsData.throughputs : [], totalFiscalYearsByProject, new Decimal(0));

    // スループット率（通貨に依存しない）
    const partThroughputRateData = throughputRateByPartNo.find(item => item.partNo === partNo);
    const throughputRate: Decimal[] = adjustArrayLength(partThroughputRateData ? partThroughputRateData.throughputRate : [], totalFiscalYearsByProject, new Decimal(0));

    const yearsRow = [resolveDisplayPartNo({ partNo, displayPartNo }), ...fiscalYearsString.map((year) => year), t("features.InvestmentScreening.ProjectProfitablityStatement.tableHeader.total")];

    // 合計値の計算
    const quantitiesTotal = quantities.reduce((sum, v) => sum.plus(v), new Decimal(0)).toNumber();
    const totalSalesSum = totalSales.reduce((sum, v) => sum.plus(v), new Decimal(0)).toNumber();
    const moldPricesTotal = moldPrices.reduce((sum, v) => sum.plus(v), new Decimal(0)).toNumber();
    const devPricesTotal = devPrices.reduce((sum, v) => sum.plus(v), new Decimal(0)).toNumber();
    const mergePricesTotal = mergePrices.reduce((sum, v) => sum.plus(v), new Decimal(0)).toNumber();
    const totalMaterialCostsSum = totalMaterialCosts.reduce((sum, v) => sum.plus(v), new Decimal(0)).toNumber();
    const throughputsTotal = throughputs.reduce((sum, v) => sum.plus(v), new Decimal(0)).toNumber();

    // 各データ行（項目名 + 各年度データ + 合計）- 選択通貨に変換されたデータを出力
    const rows = [
      [labelQuantity, ...quantities.map(q => q.toNumber()), quantitiesTotal],
      [`${labelPrice}${t("features.InvestmentScreening.ProjectProfitablityStatement.tableHeader.weightedAverageSuffix")}`, ...prices.map(p => p.toNumber()), "-"],
      [t("features.InvestmentScreening.ProjectProfitablityStatement.tableHeader.productSales"), ...totalSales.map(v => v.toNumber()), totalSalesSum],
      [t("features.InvestmentScreening.ProjectProfitablityStatement.tableHeader.moldSales"), ...moldPrices.map(v => v.toNumber()), moldPricesTotal],
      [t("features.InvestmentScreening.ProjectProfitablityStatement.tableHeader.devSales"), ...devPrices.map(v => v.toNumber()), devPricesTotal],
      [t("features.InvestmentScreening.ProjectProfitablityStatement.tableHeader.totalSales"), ...mergePrices.map(v => v.toNumber()), mergePricesTotal],
      [t("features.InvestmentScreening.ProjectProfitablityStatement.tableHeader.materialCostWeightedAverage"), ...materialCosts.map(v => v.toNumber()), "-"],
      [t("features.InvestmentScreening.ProjectProfitablityStatement.tableHeader.materialCost"), ...totalMaterialCosts.map(v => v.toNumber()), totalMaterialCostsSum],
      [t("features.InvestmentScreening.ProjectProfitablityStatement.tableHeader.throughputAmount"), ...throughputs.map(v => v.toNumber()), throughputsTotal],
      [t("features.InvestmentScreening.ProjectProfitablityStatement.tableHeader.throughputRatePercent"), ...throughputRate.map(v => v.toNumber()), "-"],
    ];

    return [
      [`Part No: ${resolveDisplayPartNo({ partNo, displayPartNo })}`],
      [t("features.InvestmentScreening.ProjectProfitablityStatement.label.unitPrefixCurrency", { currency: selectedCurrency.name })],
      [],
      yearsRow,
      ...rows,
    ];
  }

  // 年度別損益
  const createExportDataForProfitandLoss = (): (string | number)[][] => {
    // Excel出力用：JPY版のdisplay系stateを使用（二重変換を回避）

    // 各データの取得（display系stateを使用 - 入力通貨→表示通貨で直接変換済み）
    // 売上合計
    const totalSalesValues = fiscalYearsString.map((_, index) => (displayTotalSalesByYear[index] || new Decimal(0)).toNumber());
    const totalSales: number = displayTotalSalesByYear.reduce((sum, value) => sum.plus(value || new Decimal(0)), new Decimal(0)).toNumber();
    // 製品売上
    const productSalesValues = fiscalYearsString.map((_, index) => (displayProductSalesByYear[index] || new Decimal(0)).toNumber());
    const totalproductSales: number = displayProductSalesByYear.reduce((sum, value) => sum.plus(value || new Decimal(0)), new Decimal(0)).toNumber();
    // PJ単位金型回収
    const moldSalesValues = fiscalYearsString.map((_, index) => (displayMoldSalesByYear[index] || new Decimal(0)).toNumber());
    const totalmoldSales: number = displayMoldSalesByYear.reduce((sum, value) => sum.plus(value || new Decimal(0)), new Decimal(0)).toNumber();
    // PJ単位開発費回収
    const devSalesValues = fiscalYearsString.map((_, index) => (displayDevSalesByYear[index] || new Decimal(0)).toNumber());
    const totaldevSales: number = displayDevSalesByYear.reduce((sum, value) => sum.plus(value || new Decimal(0)), new Decimal(0)).toNumber();
    // PJ単位設備費回収
    const equipmentSalesValues = fiscalYearsString.map((_, index) => (displayEquipmentSalesByYear[index] || new Decimal(0)).toNumber());
    const totalequipmentSales: number = displayEquipmentSalesByYear.reduce((sum, value) => sum.plus(value || new Decimal(0)), new Decimal(0)).toNumber();
    // 売上原価
    const salesCostValues = fiscalYearsString.map((_, index) => (displaySalesCostByYear[index] || new Decimal(0)).toNumber());
    const totalsalesCost: number = displaySalesCostByYear.reduce((sum, value) => sum.plus(value || new Decimal(0)), new Decimal(0)).toNumber();
    // 材料費
    const MaterialCostValues = fiscalYearsString.map((_, index) => (displayTotalMaterialCostByYear[index] || new Decimal(0)).toNumber());
    const totalMaterialCost: number = displayTotalMaterialCostByYear.reduce((sum, value) => sum.plus(value || new Decimal(0)), new Decimal(0)).toNumber();
    // 外注加工費
    const outsideCostValues = fiscalYearsString.map((_, index) => (displayOutsideCostByYear[index] || new Decimal(0)).toNumber());
    const totaloutsideCost: number = displayOutsideCostByYear.reduce((sum, value) => sum.plus(value || new Decimal(0)), new Decimal(0)).toNumber();
    // 労務費
    const laborCostValues = fiscalYearsString.map((_, index) => (displayLaborCostByYear[index] || new Decimal(0)).toNumber());
    const totallaborCost: number = displayLaborCostByYear.reduce((sum, value) => sum.plus(value || new Decimal(0)), new Decimal(0)).toNumber();
    // 国内工場労務費
    const domesticLaborCostValues = fiscalYearsString.map((_, index) => (displayDomesticLaborCostByYear[index] || new Decimal(0)).toNumber());
    const totalDomesticLaborCost: number = displayDomesticLaborCostByYear.reduce((sum, value) => sum.plus(value || new Decimal(0)), new Decimal(0)).toNumber();
    // 工場経費
    const factoryExpensesValues = fiscalYearsString.map((_, index) => (displayFactoryExpensesByYear[index] || new Decimal(0)).toNumber());
    const totalfactoryExpenses: number = displayFactoryExpensesByYear.reduce((sum, value) => sum.plus(value || new Decimal(0)), new Decimal(0)).toNumber();
    // 償却費以外の工場経費
    const overHeadCostValues = fiscalYearsString.map((_, index) => (displayOverHeadCostByYear[index] || new Decimal(0)).toNumber());
    const totaloverHeadCost: number = displayOverHeadCostByYear.reduce((sum, value) => sum.plus(value || new Decimal(0)), new Decimal(0)).toNumber();
    // 減価償却費
    const depreciationValues = fiscalYearsString.map((_, index) => (displayDepreciationByYear[index] || new Decimal(0)).toNumber());
    const totaldepreciation: number = displayDepreciationByYear.reduce((sum, value) => sum.plus(value || new Decimal(0)), new Decimal(0)).toNumber();
    // 投資対象設備
    const equipmentDepreciationValues = fiscalYearsString.map((_, index) => (displayEquipmentDepreciationByYear[index] || new Decimal(0)).toNumber());
    const totalequipmentDepreciation: number = displayEquipmentDepreciationByYear.reduce((sum, value) => sum.plus(value || new Decimal(0)), new Decimal(0)).toNumber();
    // 工場建物
    const factoryCostValues = fiscalYearsString.map((_, index) => (displayFactoryCostByYear[index] || new Decimal(0)).toNumber());
    const totalfactoryCost: number = displayFactoryCostByYear.reduce((sum, value) => sum.plus(value || new Decimal(0)), new Decimal(0)).toNumber();
    // 過去PJの投資対象設備
    const pastEquipmentCostValues = fiscalYearsString.map((_, index) => (displayPastEquipmentCostByYear[index] || new Decimal(0)).toNumber());
    const totalpastEquipmentCost: number = displayPastEquipmentCostByYear.reduce((sum, value) => sum.plus(value || new Decimal(0)), new Decimal(0)).toNumber();
    // その他の設備
    const otherEquipmentCostValues = fiscalYearsString.map((_, index) => (displayOtherEquipmentCostByYear[index] || new Decimal(0)).toNumber());
    const totalotherEquipmentCost: number = displayOtherEquipmentCostByYear.reduce((sum, value) => sum.plus(value || new Decimal(0)), new Decimal(0)).toNumber();
    // 国内工場経費
    const domesticFactoryExpensesValues = fiscalYearsString.map((_, index) => (displayDomesticFactoryExpensesByYear[index] || new Decimal(0)).toNumber());
    const totaldomesticFactoryExpenses: number = displayDomesticFactoryExpensesByYear.reduce((sum, value) => sum.plus(value || new Decimal(0)), new Decimal(0)).toNumber();
    // 国内工場経費-償却費以外の工場経費
    const domesticOverHeadCostValues = fiscalYearsString.map((_, index) => (displayDomesticOverHeadCostByYear[index] || new Decimal(0)).toNumber());
    const totalDomesticOverHeadCost: number = displayDomesticOverHeadCostByYear.reduce((sum, value) => sum.plus(value || new Decimal(0)), new Decimal(0)).toNumber();
    // 国内工場経費-減価償却費
    const domesticDepreciationValues = fiscalYearsString.map((_, index) => (displayDomesticDepreciationByYear[index] || new Decimal(0)).toNumber());
    const totalDomesticDepreciation: number = displayDomesticDepreciationByYear.reduce((sum, value) => sum.plus(value || new Decimal(0)), new Decimal(0)).toNumber();
    // 国内工場経費-投資対象設備
    const domesticInvestmentDepreciationValues = fiscalYearsString.map((_, index) => (displayDomesticInvestmentDepreciationByYear[index] || new Decimal(0)).toNumber());
    const totalDomesticInvestmentDepreciation: number = displayDomesticInvestmentDepreciationByYear.reduce((sum, value) => sum.plus(value || new Decimal(0)), new Decimal(0)).toNumber();
    // 国内工場経費-工場建物
    const domesticFactoryBuildingDepreciationValues = fiscalYearsString.map((_, index) => (displayDomesticFactoryBuildingDepreciationByYear[index] || new Decimal(0)).toNumber());
    const totalDomesticFactoryBuildingDepreciation: number = displayDomesticFactoryBuildingDepreciationByYear.reduce((sum, value) => sum.plus(value || new Decimal(0)), new Decimal(0)).toNumber();
    // 国内工場経費-その他の設備
    const domesticOtherDepreciationValues = fiscalYearsString.map((_, index) => (displayDomesticOtherDepreciationByYear[index] || new Decimal(0)).toNumber());
    const totalDomesticOtherDepreciation: number = displayDomesticOtherDepreciationByYear.reduce((sum, value) => sum.plus(value || new Decimal(0)), new Decimal(0)).toNumber();
    // 仕損費
    const scrapCostValues = fiscalYearsString.map((_, index) => (displayScrapCostByYear[index] || new Decimal(0)).toNumber());
    const totalScrapCost: number = displayScrapCostByYear.reduce((sum, value) => sum.plus(value || new Decimal(0)), new Decimal(0)).toNumber();
    // 国内工場販管費
    const domesticSgaCostValues = fiscalYearsString.map((_, index) => (displayDomesticSgaCostByYear[index] || new Decimal(0)).toNumber());
    const totalDomesticSgaCost: number = displayDomesticSgaCostByYear.reduce((sum, value) => sum.plus(value || new Decimal(0)), new Decimal(0)).toNumber();
    // 販売費及び一般管理費
    const sGAExpensesValues = fiscalYearsString.map((_, index) => (displaySGAExpensesByYear[index] || new Decimal(0)).toNumber());
    const totalsGAExpenses: number = displaySGAExpensesByYear.reduce((sum, value) => sum.plus(value || new Decimal(0)), new Decimal(0)).toNumber();
    // 工場販管費
    const appliedLaborCostValues = fiscalYearsString.map((_, index) => (displayAppliedLaborCostByYear[index] || new Decimal(0)).toNumber());
    const totalappliedLaborCost: number = displayAppliedLaborCostByYear.reduce((sum, value) => sum.plus(value || new Decimal(0)), new Decimal(0)).toNumber();
    // 本社・販社販管費
    const salesManagementCostValues = fiscalYearsString.map((_, index) => (displaySalesManagementCostByYear[index] || new Decimal(0)).toNumber());
    const totalsalesManagementCost: number = displaySalesManagementCostByYear.reduce((sum, value) => sum.plus(value || new Decimal(0)), new Decimal(0)).toNumber();
    // 開発費
    const devCostValues = fiscalYearsString.map((_, index) => (displayDevCostByYear[index] || new Decimal(0)).toNumber());
    const totaldevCost: number = displayDevCostByYear.reduce((sum, value) => sum.plus(value || new Decimal(0)), new Decimal(0)).toNumber();
    // ロイヤリティ
    const royaltyValues = fiscalYearsString.map((_, index) => (displayRoyaltyByYear[index] || new Decimal(0)).toNumber());
    const totalRoyalty: number = displayRoyaltyByYear.reduce((sum, value) => sum.plus(value || new Decimal(0)), new Decimal(0)).toNumber();
    // その他の経費
    const otherCostValues = fiscalYearsString.map((_, index) => (displayOtherCostByYear[index] || new Decimal(0)).toNumber());
    const totalotherCost: number = displayOtherCostByYear.reduce((sum, value) => sum.plus(value || new Decimal(0)), new Decimal(0)).toNumber();
    // 営業利益
    const profitValues = fiscalYearsString.map((_, index) => (displayProfitByYear[index] || new Decimal(0)).toNumber());
    const totalProfit: number = displayProfitByYear.reduce((sum, value) => sum.plus(value || new Decimal(0)), new Decimal(0)).toNumber();
    // 営業利益率（通貨に依存しない比率なので変換不要、ただしdisplay版を使用）
    const profitRateValues = fiscalYearsString.map((_, index) => (displayProfitRateByYear[index] || new Decimal(0)).toNumber());
    const totalProfitRate: number = calcProfitRate(new Decimal(totalSales), new Decimal(totalProfit)).toNumber();
    // 実質的営業利益率（通貨に依存しない比率なので変換不要、ただしdisplay版を使用）
    const realOperatingProfitRateValues = fiscalYearsString.map((_, index) => (displayRealOperatingProfitRateByYear[index] || new Decimal(0)).toNumber());
    // 実質的営業利益率の合計 = 合計営業利益 / 合計実質売上（display版monthly dataから計算）
    const totalCustomerSpecifiedPartsCost = displayMonthlyProfitAndLossData?.customerSpecifiedPartsCost
      ?.reduce((sum, v) => sum.plus(v || new Decimal(0)), new Decimal(0)) || new Decimal(0);
    const totalRealSales = new Decimal(totalSales).minus(totalCustomerSpecifiedPartsCost);
    const totalRealOperatingProfitRate: number = totalRealSales.isZero()
      ? 0
      : new Decimal(totalProfit).div(totalRealSales).times(100).toNumber();

    // 合理化投資の場合は「年度別損益（合理化後）」、それ以外は「年度別損益」
    const tableName = capexProjectData?.purpose === PurposeType.Rationalization.code
      ? t("features.InvestmentScreening.ProjectProfitablityStatement.label.profitLossByYearRationalizationAfter")
      : t("features.InvestmentScreening.ProjectProfitablityStatement.label.profitLossByYear");
    const yearsRow = [tableName, ...fiscalYearsString.map((year) => year), t("features.InvestmentScreening.ProjectProfitablityStatement.tableHeader.total")];

    const rows = [
      [ProfitAndLossItems.totalSales.name, ...totalSalesValues, totalSales],
      [ProfitAndLossItems.productSales.name, ...productSalesValues, totalproductSales],
      [ProfitAndLossItems.moldRecovery.name, ...moldSalesValues, totalmoldSales],
      [ProfitAndLossItems.devRecovery.name, ...devSalesValues, totaldevSales],
      [ProfitAndLossItems.equipmentRecovery.name, ...equipmentSalesValues, totalequipmentSales],
      [ProfitAndLossItems.salesCost.name, ...salesCostValues, totalsalesCost],
      [ProfitAndLossItems.materialCost.name, ...MaterialCostValues, totalMaterialCost],
      [ProfitAndLossItems.outsideCost.name, ...outsideCostValues, totaloutsideCost],
      [ProfitAndLossItems.laborCost.name, ...laborCostValues, totallaborCost],
      [ProfitAndLossItems.domesticLaborCost.name, ...domesticLaborCostValues, totalDomesticLaborCost],
      [ProfitAndLossItems.factoryExpenses.name, ...factoryExpensesValues, totalfactoryExpenses],
      [ProfitAndLossItems.overHeadCost.name, ...overHeadCostValues, totaloverHeadCost],
      [ProfitAndLossItems.depreciation.name, ...depreciationValues, totaldepreciation],
      [ProfitAndLossItems.equipmentDepreciation.name, ...equipmentDepreciationValues, totalequipmentDepreciation],
      [ProfitAndLossItems.factoryCost.name, ...factoryCostValues, totalfactoryCost],
      [ProfitAndLossItems.pastEquipmentCost.name, ...pastEquipmentCostValues, totalpastEquipmentCost],
      [ProfitAndLossItems.otherEquipmentCost.name, ...otherEquipmentCostValues, totalotherEquipmentCost],
      [ProfitAndLossItems.domesticFactoryExpenses.name, ...domesticFactoryExpensesValues, totaldomesticFactoryExpenses],
      [ProfitAndLossItems.domesticOverHeadCost.name, ...domesticOverHeadCostValues, totalDomesticOverHeadCost],
      [ProfitAndLossItems.domesticDepreciation.name, ...domesticDepreciationValues, totalDomesticDepreciation],
      [ProfitAndLossItems.domesticInvestmentDepreciation.name, ...domesticInvestmentDepreciationValues, totalDomesticInvestmentDepreciation],
      [ProfitAndLossItems.domesticFactoryBuildingDepreciation.name, ...domesticFactoryBuildingDepreciationValues, totalDomesticFactoryBuildingDepreciation],
      [ProfitAndLossItems.domesticOtherDepreciation.name, ...domesticOtherDepreciationValues, totalDomesticOtherDepreciation],
      [ProfitAndLossItems.scrapCost.name, ...scrapCostValues, totalScrapCost],
      [ProfitAndLossItems.sgaExpenses.name, ...sGAExpensesValues, totalsGAExpenses],
      [ProfitAndLossItems.appliedLaborCost.name, ...appliedLaborCostValues, totalappliedLaborCost],
      [ProfitAndLossItems.domesticSgaCost.name, ...domesticSgaCostValues, totalDomesticSgaCost],
      [ProfitAndLossItems.salesManagementCost.name, ...salesManagementCostValues, totalsalesManagementCost],
      [ProfitAndLossItems.devCost.name, ...devCostValues, totaldevCost],
      [ProfitAndLossItems.royalty.name, ...royaltyValues, totalRoyalty],
      [ProfitAndLossItems.otherCost.name, ...otherCostValues, totalotherCost],
      [ProfitAndLossItems.operatingProfit.name, ...profitValues, totalProfit],
      [`${ProfitAndLossItems.operatingProfitRate.name}(%)`, ...profitRateValues, totalProfitRate],
      // 客先指定部品が存在する場合のみ実質的営業利益率を出力
      ...(hasCustomerSpecifiedParts ? [[`${ProfitAndLossItems.realOperatingProfitRate.name}(%)`, ...realOperatingProfitRateValues, totalRealOperatingProfitRate]] : []),
    ];

    return [
      [t("features.InvestmentScreening.ProjectProfitablityStatement.label.unitPrefixCurrency", { currency: selectedCurrency.name })],
      [],
      yearsRow,
      ...rows,
    ];
  };

  // 年度別損益（合理化前）（Excel出力用）
  const createExportDataForRationalizationBeforeProfitLoss = (): (string | number)[][] => {
    // Excel出力用：JPY版のdisplay系stateを使用（二重変換を回避）

    // 各データの取得（display系stateを使用 - 入力通貨→表示通貨で直接変換済み）
    // 売上合計
    const totalSalesValues = fiscalYearsString.map((_, index) => (displayBeforeRationalizationTotalSalesByYear[index] || new Decimal(0)).toNumber());
    const totalSales: number = displayBeforeRationalizationTotalSalesByYear.reduce((sum, value) => sum.plus(value || new Decimal(0)), new Decimal(0)).toNumber();
    // 製品売上
    const productSalesValues = fiscalYearsString.map((_, index) => (displayBeforeRationalizationProductSalesByYear[index] || new Decimal(0)).toNumber());
    const totalproductSales: number = displayBeforeRationalizationProductSalesByYear.reduce((sum, value) => sum.plus(value || new Decimal(0)), new Decimal(0)).toNumber();
    // PJ単位金型回収
    const moldSalesValues = fiscalYearsString.map((_, index) => (displayBeforeRationalizationMoldSalesByYear[index] || new Decimal(0)).toNumber());
    const totalmoldSales: number = displayBeforeRationalizationMoldSalesByYear.reduce((sum, value) => sum.plus(value || new Decimal(0)), new Decimal(0)).toNumber();
    // PJ単位開発費回収
    const devSalesValues = fiscalYearsString.map((_, index) => (displayBeforeRationalizationDevSalesByYear[index] || new Decimal(0)).toNumber());
    const totaldevSales: number = displayBeforeRationalizationDevSalesByYear.reduce((sum, value) => sum.plus(value || new Decimal(0)), new Decimal(0)).toNumber();
    // PJ単位設備費回収
    const equipmentSalesValues = fiscalYearsString.map((_, index) => (displayBeforeRationalizationEquipmentSalesByYear[index] || new Decimal(0)).toNumber());
    const totalequipmentSales: number = displayBeforeRationalizationEquipmentSalesByYear.reduce((sum, value) => sum.plus(value || new Decimal(0)), new Decimal(0)).toNumber();
    // 売上原価
    const salesCostValues = fiscalYearsString.map((_, index) => (displayBeforeRationalizationSalesCostByYear[index] || new Decimal(0)).toNumber());
    const totalsalesCost: number = displayBeforeRationalizationSalesCostByYear.reduce((sum, value) => sum.plus(value || new Decimal(0)), new Decimal(0)).toNumber();
    // 材料費
    const MaterialCostValues = fiscalYearsString.map((_, index) => (displayBeforeRationalizationTotalMaterialCostByYear[index] || new Decimal(0)).toNumber());
    const totalMaterialCost: number = displayBeforeRationalizationTotalMaterialCostByYear.reduce((sum, value) => sum.plus(value || new Decimal(0)), new Decimal(0)).toNumber();
    // 外注加工費
    const outsideCostValues = fiscalYearsString.map((_, index) => (displayBeforeRationalizationOutsideCostByYear[index] || new Decimal(0)).toNumber());
    const totaloutsideCost: number = displayBeforeRationalizationOutsideCostByYear.reduce((sum, value) => sum.plus(value || new Decimal(0)), new Decimal(0)).toNumber();
    // 労務費
    const laborCostValues = fiscalYearsString.map((_, index) => (displayBeforeRationalizationLaborCostByYear[index] || new Decimal(0)).toNumber());
    const totallaborCost: number = displayBeforeRationalizationLaborCostByYear.reduce((sum, value) => sum.plus(value || new Decimal(0)), new Decimal(0)).toNumber();
    // 国内工場労務費
    const domesticLaborCostValues = fiscalYearsString.map((_, index) => (displayBeforeRationalizationDomesticLaborCostByYear[index] || new Decimal(0)).toNumber());
    const totalDomesticLaborCost: number = displayBeforeRationalizationDomesticLaborCostByYear.reduce((sum, value) => sum.plus(value || new Decimal(0)), new Decimal(0)).toNumber();
    // 工場経費
    const factoryExpensesValues = fiscalYearsString.map((_, index) => (displayBeforeRationalizationFactoryExpensesByYear[index] || new Decimal(0)).toNumber());
    const totalfactoryExpenses: number = displayBeforeRationalizationFactoryExpensesByYear.reduce((sum, value) => sum.plus(value || new Decimal(0)), new Decimal(0)).toNumber();
    // 償却費以外の工場経費
    const overHeadCostValues = fiscalYearsString.map((_, index) => (displayBeforeRationalizationOverHeadCostByYear[index] || new Decimal(0)).toNumber());
    const totaloverHeadCost: number = displayBeforeRationalizationOverHeadCostByYear.reduce((sum, value) => sum.plus(value || new Decimal(0)), new Decimal(0)).toNumber();
    // 減価償却費
    const depreciationValues = fiscalYearsString.map((_, index) => (displayBeforeRationalizationDepreciationByYear[index] || new Decimal(0)).toNumber());
    const totaldepreciation: number = displayBeforeRationalizationDepreciationByYear.reduce((sum, value) => sum.plus(value || new Decimal(0)), new Decimal(0)).toNumber();
    // 投資対象設備
    const equipmentDepreciationValues = fiscalYearsString.map((_, index) => (displayBeforeRationalizationEquipmentDepreciationByYear[index] || new Decimal(0)).toNumber());
    const totalequipmentDepreciation: number = displayBeforeRationalizationEquipmentDepreciationByYear.reduce((sum, value) => sum.plus(value || new Decimal(0)), new Decimal(0)).toNumber();
    // 工場建物
    const factoryCostValues = fiscalYearsString.map((_, index) => (displayBeforeRationalizationFactoryCostByYear[index] || new Decimal(0)).toNumber());
    const totalfactoryCost: number = displayBeforeRationalizationFactoryCostByYear.reduce((sum, value) => sum.plus(value || new Decimal(0)), new Decimal(0)).toNumber();
    // 過去PJの投資対象設備
    const pastEquipmentCostValues = fiscalYearsString.map((_, index) => (displayBeforeRationalizationPastEquipmentCostByYear[index] || new Decimal(0)).toNumber());
    const totalpastEquipmentCost: number = displayBeforeRationalizationPastEquipmentCostByYear.reduce((sum, value) => sum.plus(value || new Decimal(0)), new Decimal(0)).toNumber();
    // その他の設備
    const otherEquipmentCostValues = fiscalYearsString.map((_, index) => (displayBeforeRationalizationOtherEquipmentCostByYear[index] || new Decimal(0)).toNumber());
    const totalotherEquipmentCost: number = displayBeforeRationalizationOtherEquipmentCostByYear.reduce((sum, value) => sum.plus(value || new Decimal(0)), new Decimal(0)).toNumber();
    // 国内工場経費
    const domesticFactoryExpensesValues = fiscalYearsString.map((_, index) => (displayBeforeRationalizationDomesticFactoryExpensesByYear[index] || new Decimal(0)).toNumber());
    const totaldomesticFactoryExpenses: number = displayBeforeRationalizationDomesticFactoryExpensesByYear.reduce((sum, value) => sum.plus(value || new Decimal(0)), new Decimal(0)).toNumber();
    // 国内工場経費-償却費以外の工場経費
    const domesticOverHeadCostValues = fiscalYearsString.map((_, index) => (displayBeforeRationalizationDomesticOverHeadCostByYear[index] || new Decimal(0)).toNumber());
    const totalDomesticOverHeadCost: number = displayBeforeRationalizationDomesticOverHeadCostByYear.reduce((sum, value) => sum.plus(value || new Decimal(0)), new Decimal(0)).toNumber();
    // 国内工場経費-減価償却費
    const domesticDepreciationValues = fiscalYearsString.map((_, index) => (displayBeforeRationalizationDomesticDepreciationByYear[index] || new Decimal(0)).toNumber());
    const totalDomesticDepreciation: number = displayBeforeRationalizationDomesticDepreciationByYear.reduce((sum, value) => sum.plus(value || new Decimal(0)), new Decimal(0)).toNumber();
    // 国内工場経費-投資対象設備
    const domesticInvestmentDepreciationValues = fiscalYearsString.map((_, index) => (displayBeforeRationalizationDomesticInvestmentDepreciationByYear[index] || new Decimal(0)).toNumber());
    const totalDomesticInvestmentDepreciation: number = displayBeforeRationalizationDomesticInvestmentDepreciationByYear.reduce((sum, value) => sum.plus(value || new Decimal(0)), new Decimal(0)).toNumber();
    // 国内工場経費-工場建物
    const domesticFactoryBuildingDepreciationValues = fiscalYearsString.map((_, index) => (displayBeforeRationalizationDomesticFactoryBuildingDepreciationByYear[index] || new Decimal(0)).toNumber());
    const totalDomesticFactoryBuildingDepreciation: number = displayBeforeRationalizationDomesticFactoryBuildingDepreciationByYear.reduce((sum, value) => sum.plus(value || new Decimal(0)), new Decimal(0)).toNumber();
    // 国内工場経費-その他の設備
    const domesticOtherDepreciationValues = fiscalYearsString.map((_, index) => (displayBeforeRationalizationDomesticOtherDepreciationByYear[index] || new Decimal(0)).toNumber());
    const totalDomesticOtherDepreciation: number = displayBeforeRationalizationDomesticOtherDepreciationByYear.reduce((sum, value) => sum.plus(value || new Decimal(0)), new Decimal(0)).toNumber();
    // 仕損費
    const scrapCostValues = fiscalYearsString.map((_, index) => (displayBeforeRationalizationScrapCostByYear[index] || new Decimal(0)).toNumber());
    const totalScrapCost: number = displayBeforeRationalizationScrapCostByYear.reduce((sum, value) => sum.plus(value || new Decimal(0)), new Decimal(0)).toNumber();
    // 国内工場販管費
    const domesticSgaCostValues = fiscalYearsString.map((_, index) => (displayBeforeRationalizationDomesticSgaCostByYear[index] || new Decimal(0)).toNumber());
    const totalDomesticSgaCost: number = displayBeforeRationalizationDomesticSgaCostByYear.reduce((sum, value) => sum.plus(value || new Decimal(0)), new Decimal(0)).toNumber();
    // 販売費及び一般管理費
    const sGAExpensesValues = fiscalYearsString.map((_, index) => (displayBeforeRationalizationSGAExpensesByYear[index] || new Decimal(0)).toNumber());
    const totalsGAExpenses: number = displayBeforeRationalizationSGAExpensesByYear.reduce((sum, value) => sum.plus(value || new Decimal(0)), new Decimal(0)).toNumber();
    // 工場販管費
    const appliedLaborCostValues = fiscalYearsString.map((_, index) => (displayBeforeRationalizationAppliedLaborCostByYear[index] || new Decimal(0)).toNumber());
    const totalappliedLaborCost: number = displayBeforeRationalizationAppliedLaborCostByYear.reduce((sum, value) => sum.plus(value || new Decimal(0)), new Decimal(0)).toNumber();
    // 本社・販社販管費
    const salesManagementCostValues = fiscalYearsString.map((_, index) => (displayBeforeRationalizationSalesManagementCostByYear[index] || new Decimal(0)).toNumber());
    const totalsalesManagementCost: number = displayBeforeRationalizationSalesManagementCostByYear.reduce((sum, value) => sum.plus(value || new Decimal(0)), new Decimal(0)).toNumber();
    // 開発費
    const devCostValues = fiscalYearsString.map((_, index) => (displayBeforeRationalizationDevCostByYear[index] || new Decimal(0)).toNumber());
    const totaldevCost: number = displayBeforeRationalizationDevCostByYear.reduce((sum, value) => sum.plus(value || new Decimal(0)), new Decimal(0)).toNumber();
    // ロイヤリティ
    const royaltyValues = fiscalYearsString.map((_, index) => (displayBeforeRationalizationRoyaltyByYear[index] || new Decimal(0)).toNumber());
    const totalRoyalty: number = displayBeforeRationalizationRoyaltyByYear.reduce((sum, value) => sum.plus(value || new Decimal(0)), new Decimal(0)).toNumber();
    // その他の経費
    const otherCostValues = fiscalYearsString.map((_, index) => (displayBeforeRationalizationOtherCostByYear[index] || new Decimal(0)).toNumber());
    const totalotherCost: number = displayBeforeRationalizationOtherCostByYear.reduce((sum, value) => sum.plus(value || new Decimal(0)), new Decimal(0)).toNumber();
    // 営業利益
    const profitValues = fiscalYearsString.map((_, index) => (displayBeforeRationalizationProfitByYear[index] || new Decimal(0)).toNumber());
    const totalProfit: number = displayBeforeRationalizationProfitByYear.reduce((sum, value) => sum.plus(value || new Decimal(0)), new Decimal(0)).toNumber();
    // 営業利益率（通貨に依存しない比率だが、display版を使用）
    const profitRateValues = fiscalYearsString.map((_, index) => (displayBeforeRationalizationProfitRateByYear[index] || new Decimal(0)).toNumber());
    const totalProfitRate: number = calcProfitRate(new Decimal(totalSales), new Decimal(totalProfit)).toNumber();
    // 実質的営業利益率（通貨に依存しない比率だが、display版を使用）
    const realOperatingProfitRateValues = fiscalYearsString.map((_, index) => (displayBeforeRationalizationRealOperatingProfitRateByYear[index] || new Decimal(0)).toNumber());
    // 実質的営業利益率の合計 = 合計営業利益 / 合計実質売上（display版monthly dataから計算）
    const totalCustomerSpecifiedPartsCost = displayBeforeRationalizationMonthlyProfitAndLossData?.customerSpecifiedPartsCost
      ?.reduce((sum, v) => sum.plus(v || new Decimal(0)), new Decimal(0)) || new Decimal(0);
    const totalRealSales = new Decimal(totalSales).minus(totalCustomerSpecifiedPartsCost);
    const totalRealOperatingProfitRate: number = totalRealSales.isZero()
      ? 0
      : new Decimal(totalProfit).div(totalRealSales).times(100).toNumber();

    const yearsRow = [t("features.InvestmentScreening.ProjectProfitablityStatement.heading.profitLossByYearRationalizationBefore"), ...fiscalYearsString.map((year) => year), t("features.InvestmentScreening.ProjectProfitablityStatement.tableHeader.total")];

    const rows = [
      [ProfitAndLossItems.totalSales.name, ...totalSalesValues, totalSales],
      [ProfitAndLossItems.productSales.name, ...productSalesValues, totalproductSales],
      [ProfitAndLossItems.moldRecovery.name, ...moldSalesValues, totalmoldSales],
      [ProfitAndLossItems.devRecovery.name, ...devSalesValues, totaldevSales],
      [ProfitAndLossItems.equipmentRecovery.name, ...equipmentSalesValues, totalequipmentSales],
      [ProfitAndLossItems.salesCost.name, ...salesCostValues, totalsalesCost],
      [ProfitAndLossItems.materialCost.name, ...MaterialCostValues, totalMaterialCost],
      [ProfitAndLossItems.outsideCost.name, ...outsideCostValues, totaloutsideCost],
      [ProfitAndLossItems.laborCost.name, ...laborCostValues, totallaborCost],
      [ProfitAndLossItems.domesticLaborCost.name, ...domesticLaborCostValues, totalDomesticLaborCost],
      [ProfitAndLossItems.factoryExpenses.name, ...factoryExpensesValues, totalfactoryExpenses],
      [ProfitAndLossItems.overHeadCost.name, ...overHeadCostValues, totaloverHeadCost],
      [ProfitAndLossItems.depreciation.name, ...depreciationValues, totaldepreciation],
      [ProfitAndLossItems.equipmentDepreciation.name, ...equipmentDepreciationValues, totalequipmentDepreciation],
      [ProfitAndLossItems.factoryCost.name, ...factoryCostValues, totalfactoryCost],
      [ProfitAndLossItems.pastEquipmentCost.name, ...pastEquipmentCostValues, totalpastEquipmentCost],
      [ProfitAndLossItems.otherEquipmentCost.name, ...otherEquipmentCostValues, totalotherEquipmentCost],
      [ProfitAndLossItems.domesticFactoryExpenses.name, ...domesticFactoryExpensesValues, totaldomesticFactoryExpenses],
      [ProfitAndLossItems.domesticOverHeadCost.name, ...domesticOverHeadCostValues, totalDomesticOverHeadCost],
      [ProfitAndLossItems.domesticDepreciation.name, ...domesticDepreciationValues, totalDomesticDepreciation],
      [ProfitAndLossItems.domesticInvestmentDepreciation.name, ...domesticInvestmentDepreciationValues, totalDomesticInvestmentDepreciation],
      [ProfitAndLossItems.domesticFactoryBuildingDepreciation.name, ...domesticFactoryBuildingDepreciationValues, totalDomesticFactoryBuildingDepreciation],
      [ProfitAndLossItems.domesticOtherDepreciation.name, ...domesticOtherDepreciationValues, totalDomesticOtherDepreciation],
      [ProfitAndLossItems.scrapCost.name, ...scrapCostValues, totalScrapCost],
      [ProfitAndLossItems.sgaExpenses.name, ...sGAExpensesValues, totalsGAExpenses],
      [ProfitAndLossItems.appliedLaborCost.name, ...appliedLaborCostValues, totalappliedLaborCost],
      [ProfitAndLossItems.domesticSgaCost.name, ...domesticSgaCostValues, totalDomesticSgaCost],
      [ProfitAndLossItems.salesManagementCost.name, ...salesManagementCostValues, totalsalesManagementCost],
      [ProfitAndLossItems.devCost.name, ...devCostValues, totaldevCost],
      [ProfitAndLossItems.royalty.name, ...royaltyValues, totalRoyalty],
      [ProfitAndLossItems.otherCost.name, ...otherCostValues, totalotherCost],
      [ProfitAndLossItems.operatingProfit.name, ...profitValues, totalProfit],
      [`${ProfitAndLossItems.operatingProfitRate.name}(%)`, ...profitRateValues, totalProfitRate],
      // 客先指定部品が存在する場合のみ実質的営業利益率を出力
      ...(hasCustomerSpecifiedParts ? [[`${ProfitAndLossItems.realOperatingProfitRate.name}(%)`, ...realOperatingProfitRateValues, totalRealOperatingProfitRate]] : []),
    ];

    return [
      [t("features.InvestmentScreening.ProjectProfitablityStatement.label.unitPrefixCurrency", { currency: selectedCurrency.name })],
      [],
      yearsRow,
      ...rows,
    ];
  };

  // 月別損益（Excel出力用）
  const createExportDataForMonthlyProfitAndLoss = (): (string | number)[][] => {
    // displayMonthlyProfitAndLossDataを使用（入力通貨→表示通貨に直接変換済み、二重変換回避）
    const monthlyData = displayMonthlyProfitAndLossData;
    if (!monthlyData) {
      return [[t("features.InvestmentScreening.ProjectProfitablityStatement.message.noMonthlyData")]];
    }

    // 月次データ処理用ヘルパー関数（JPY版は変換済みなので丸め処理のみ）
    const processMonthlyData = (data: Decimal[]): { values: number[], total: number } => {
      // 通貨ごとの小数有無を判定し、小数なし通貨（JPY等）の場合は整数に丸める
      const decimalPlaces = hasDecimal(selectedCurrency.name) ? 2 : 0;
      const values = data.map(v => (v || new Decimal(0)).toDecimalPlaces(decimalPlaces, Decimal.ROUND_HALF_UP).toNumber());
      const total = data.reduce((sum, value) => sum.plus(value || new Decimal(0)), new Decimal(0)).toDecimalPlaces(decimalPlaces, Decimal.ROUND_HALF_UP).toNumber();
      return { values, total };
    };

    // 各データの取得（JPY版は変換済み）
    const totalSales = processMonthlyData(monthlyData.totalSales);
    const productSales = processMonthlyData(monthlyData.productSales);
    const moldSales = processMonthlyData(monthlyData.moldSales);
    const devSales = processMonthlyData(monthlyData.devSales);
    const equipmentSales = processMonthlyData(monthlyData.equipmentSales);
    const salesCost = processMonthlyData(monthlyData.salesCost);
    const materialCost = processMonthlyData(monthlyData.materialCost);
    const outsideCost = processMonthlyData(monthlyData.outsideCost);
    const laborCost = processMonthlyData(monthlyData.laborCost);
    const domesticLaborCost = processMonthlyData(monthlyData.domesticLaborCost);
    const factoryExpenses = processMonthlyData(monthlyData.factoryExpenses);
    const overHeadCost = processMonthlyData(monthlyData.overHeadCost);
    const depreciation = processMonthlyData(monthlyData.depreciation);
    const equipmentDepreciation = processMonthlyData(monthlyData.equipmentDepreciation);
    const factoryCost = processMonthlyData(monthlyData.factoryCost);
    const pastEquipmentCost = processMonthlyData(monthlyData.pastEquipmentCost || []);
    const otherEquipmentCost = processMonthlyData(monthlyData.otherEquipmentCost);
    const domesticFactoryExpenses = processMonthlyData(monthlyData.domesticFactoryExpenses);
    const domesticOverHeadCost = processMonthlyData(monthlyData.domesticOverHeadCost ?? []);
    const domesticDepreciation = processMonthlyData(monthlyData.domesticDepreciation ?? []);
    const domesticInvestmentDepreciation = processMonthlyData(monthlyData.domesticInvestmentDepreciation ?? []);
    const domesticFactoryBuildingDepreciation = processMonthlyData(monthlyData.domesticFactoryBuildingDepreciation ?? []);
    const domesticOtherDepreciation = processMonthlyData(monthlyData.domesticOtherDepreciation ?? []);
    const scrapCost = processMonthlyData(monthlyData.scrapCost);
    const sGAExpenses = processMonthlyData(monthlyData.sGAExpenses);
    const appliedLaborCost = processMonthlyData(monthlyData.appliedLaborCost);
    const domesticSgaCost = processMonthlyData(monthlyData.domesticSgaCost);
    const salesManagementCost = processMonthlyData(monthlyData.salesManagementCost);
    const devCost = processMonthlyData(monthlyData.devCost);
    const royalty = processMonthlyData(monthlyData.royalty);
    const otherCost = processMonthlyData(monthlyData.otherCost);
    const profit = processMonthlyData(monthlyData.profit);

    // 営業利益率（月別）
    const profitRateValues = monthlyData.labels.map((_, index) => {
      const sales = monthlyData.totalSales[index] || new Decimal(0);
      const profitVal = monthlyData.profit[index] || new Decimal(0);
      return sales.isZero() ? 0 : profitVal.div(sales).times(100).toNumber();
    });
    const totalProfitRate = totalSales.total === 0 ? 0 : (profit.total / totalSales.total) * 100;

    // 合理化投資の場合は「月別損益（合理化後）」、それ以外は「月別損益」
    const tableName = capexProjectData?.purpose === PurposeType.Rationalization.code
      ? t("features.InvestmentScreening.ProjectProfitablityStatement.heading.profitLossByMonthRationalizationAfter")
      : t("features.InvestmentScreening.ProjectProfitablityStatement.heading.profitLossByMonth");
    const monthsRow = [tableName, ...monthlyData.labels, t("features.InvestmentScreening.ProjectProfitablityStatement.tableHeader.total")];

    const rows = [
      [ProfitAndLossItems.totalSales.name, ...totalSales.values, totalSales.total],
      [ProfitAndLossItems.productSales.name, ...productSales.values, productSales.total],
      [ProfitAndLossItems.moldRecovery.name, ...moldSales.values, moldSales.total],
      [ProfitAndLossItems.devRecovery.name, ...devSales.values, devSales.total],
      [ProfitAndLossItems.equipmentRecovery.name, ...equipmentSales.values, equipmentSales.total],
      [ProfitAndLossItems.salesCost.name, ...salesCost.values, salesCost.total],
      [ProfitAndLossItems.materialCost.name, ...materialCost.values, materialCost.total],
      [ProfitAndLossItems.outsideCost.name, ...outsideCost.values, outsideCost.total],
      [ProfitAndLossItems.laborCost.name, ...laborCost.values, laborCost.total],
      [ProfitAndLossItems.domesticLaborCost.name, ...domesticLaborCost.values, domesticLaborCost.total],
      [ProfitAndLossItems.factoryExpenses.name, ...factoryExpenses.values, factoryExpenses.total],
      [ProfitAndLossItems.overHeadCost.name, ...overHeadCost.values, overHeadCost.total],
      [ProfitAndLossItems.depreciation.name, ...depreciation.values, depreciation.total],
      [ProfitAndLossItems.equipmentDepreciation.name, ...equipmentDepreciation.values, equipmentDepreciation.total],
      [ProfitAndLossItems.factoryCost.name, ...factoryCost.values, factoryCost.total],
      [ProfitAndLossItems.pastEquipmentCost.name, ...pastEquipmentCost.values, pastEquipmentCost.total],
      [ProfitAndLossItems.otherEquipmentCost.name, ...otherEquipmentCost.values, otherEquipmentCost.total],
      [ProfitAndLossItems.domesticFactoryExpenses.name, ...domesticFactoryExpenses.values, domesticFactoryExpenses.total],
      [ProfitAndLossItems.domesticOverHeadCost.name, ...domesticOverHeadCost.values, domesticOverHeadCost.total],
      [ProfitAndLossItems.domesticDepreciation.name, ...domesticDepreciation.values, domesticDepreciation.total],
      [ProfitAndLossItems.domesticInvestmentDepreciation.name, ...domesticInvestmentDepreciation.values, domesticInvestmentDepreciation.total],
      [ProfitAndLossItems.domesticFactoryBuildingDepreciation.name, ...domesticFactoryBuildingDepreciation.values, domesticFactoryBuildingDepreciation.total],
      [ProfitAndLossItems.domesticOtherDepreciation.name, ...domesticOtherDepreciation.values, domesticOtherDepreciation.total],
      [ProfitAndLossItems.scrapCost.name, ...scrapCost.values, scrapCost.total],
      [ProfitAndLossItems.sgaExpenses.name, ...sGAExpenses.values, sGAExpenses.total],
      [ProfitAndLossItems.appliedLaborCost.name, ...appliedLaborCost.values, appliedLaborCost.total],
      [ProfitAndLossItems.domesticSgaCost.name, ...domesticSgaCost.values, domesticSgaCost.total],
      [ProfitAndLossItems.salesManagementCost.name, ...salesManagementCost.values, salesManagementCost.total],
      [ProfitAndLossItems.devCost.name, ...devCost.values, devCost.total],
      [ProfitAndLossItems.royalty.name, ...royalty.values, royalty.total],
      [ProfitAndLossItems.otherCost.name, ...otherCost.values, otherCost.total],
      [ProfitAndLossItems.operatingProfit.name, ...profit.values, profit.total],
      [`${ProfitAndLossItems.operatingProfitRate.name}(%)`, ...profitRateValues, totalProfitRate],
    ];

    // 実質的営業利益率（客先指定部品がある場合のみ）
    if (hasCustomerSpecifiedParts && monthlyData.customerSpecifiedPartsCost) {
      // 実質的売上（月別）= 売上合計 - 客先指定部品材料費
      const realOperatingProfitRateValues = monthlyData.labels.map((_, index) => {
        const sales = monthlyData.totalSales[index] || new Decimal(0);
        const customerCost = monthlyData.customerSpecifiedPartsCost[index] || new Decimal(0);
        const realSales = sales.minus(customerCost);
        const profitVal = monthlyData.profit[index] || new Decimal(0);
        return realSales.isZero() ? 0 : profitVal.div(realSales).times(100).toNumber();
      });

      // 合計の実質的営業利益率（JPY版は変換済み）
      const totalCustomerSpecifiedPartsCost = monthlyData.customerSpecifiedPartsCost
        .reduce((sum, v) => sum.plus(v || new Decimal(0)), new Decimal(0));
      const totalRealSales = new Decimal(totalSales.total).minus(totalCustomerSpecifiedPartsCost);
      const totalRealOperatingProfitRate = totalRealSales.isZero()
        ? 0
        : new Decimal(profit.total).div(totalRealSales).times(100).toNumber();

      rows.push([`${ProfitAndLossItems.realOperatingProfitRate.name}(%)`, ...realOperatingProfitRateValues, totalRealOperatingProfitRate]);
    }

    return [
      [t("features.InvestmentScreening.ProjectProfitablityStatement.label.unitPrefixCurrency", { currency: selectedCurrency.name })],
      [],
      monthsRow,
      ...rows,
    ];
  };

  // 合理化投資用：月別損益（合理化前）
  const createExportDataForRationalizationBeforeMonthlyProfitLoss = (): (string | number)[][] => {
    // displayBeforeRationalizationMonthlyProfitAndLossDataを使用（入力通貨→表示通貨に直接変換済み、二重変換回避）
    if (!displayBeforeRationalizationMonthlyProfitAndLossData) {
      return [[t("features.InvestmentScreening.ProjectProfitablityStatement.message.noMonthlyData")]];
    }

    // 月次データ処理用ヘルパー関数（JPY版は変換済みなので丸め処理のみ）
    const processMonthlyData = (data: Decimal[]): { values: number[], total: number } => {
      const decimalPlaces = hasDecimal(selectedCurrency.name) ? 2 : 0;
      const values = data.map(v => (v || new Decimal(0)).toDecimalPlaces(decimalPlaces, Decimal.ROUND_HALF_UP).toNumber());
      const total = data.reduce((sum, value) => sum.plus(value || new Decimal(0)), new Decimal(0)).toDecimalPlaces(decimalPlaces, Decimal.ROUND_HALF_UP).toNumber();
      return { values, total };
    };

    const data = displayBeforeRationalizationMonthlyProfitAndLossData;
    const totalSales = processMonthlyData(data.totalSales);
    const productSales = processMonthlyData(data.productSales);
    const moldSales = processMonthlyData(data.moldSales);
    const devSales = processMonthlyData(data.devSales);
    const equipmentSales = processMonthlyData(data.equipmentSales);
    const salesCost = processMonthlyData(data.salesCost);
    const materialCost = processMonthlyData(data.materialCost);
    const outsideCost = processMonthlyData(data.outsideCost);
    const laborCost = processMonthlyData(data.laborCost);
    const domesticLaborCost = processMonthlyData(data.domesticLaborCost);
    const factoryExpenses = processMonthlyData(data.factoryExpenses);
    const overHeadCost = processMonthlyData(data.overHeadCost);
    const depreciation = processMonthlyData(data.depreciation);
    const equipmentDepreciation = processMonthlyData(data.equipmentDepreciation);
    const factoryCost = processMonthlyData(data.factoryCost);
    const pastEquipmentCost = processMonthlyData(data.pastEquipmentCost || []);
    const otherEquipmentCost = processMonthlyData(data.otherEquipmentCost);
    const domesticFactoryExpenses = processMonthlyData(data.domesticFactoryExpenses);
    const domesticOverHeadCost = processMonthlyData(data.domesticOverHeadCost || []);
    const domesticDepreciation = processMonthlyData(data.domesticDepreciation || []);
    const domesticInvestmentDepreciation = processMonthlyData(data.domesticInvestmentDepreciation || []);
    const domesticFactoryBuildingDepreciation = processMonthlyData(data.domesticFactoryBuildingDepreciation || []);
    const domesticOtherDepreciation = processMonthlyData(data.domesticOtherDepreciation || []);
    const scrapCost = processMonthlyData(data.scrapCost);
    const sGAExpenses = processMonthlyData(data.sGAExpenses);
    const appliedLaborCost = processMonthlyData(data.appliedLaborCost);
    const domesticSgaCost = processMonthlyData(data.domesticSgaCost);
    const salesManagementCost = processMonthlyData(data.salesManagementCost);
    const devCost = processMonthlyData(data.devCost);
    const royalty = processMonthlyData(data.royalty);
    const otherCost = processMonthlyData(data.otherCost);
    const profit = processMonthlyData(data.profit);

    // 営業利益率
    const profitRateValues = data.totalSales.map((sales, index) => {
      const profitVal = data.profit[index] || new Decimal(0);
      return sales.isZero() ? 0 : profitVal.div(sales).times(100).toNumber();
    });
    const totalProfitRate = totalSales.total === 0 ? 0 : (profit.total / totalSales.total) * 100;

    const monthsRow = [t("features.InvestmentScreening.ProjectProfitablityStatement.heading.profitLossByMonthRationalizationBefore"), ...data.labels, t("features.InvestmentScreening.ProjectProfitablityStatement.tableHeader.total")];

    const rows = [
      [ProfitAndLossItems.totalSales.name, ...totalSales.values, totalSales.total],
      [ProfitAndLossItems.productSales.name, ...productSales.values, productSales.total],
      [ProfitAndLossItems.moldRecovery.name, ...moldSales.values, moldSales.total],
      [ProfitAndLossItems.devRecovery.name, ...devSales.values, devSales.total],
      [ProfitAndLossItems.equipmentRecovery.name, ...equipmentSales.values, equipmentSales.total],
      [ProfitAndLossItems.salesCost.name, ...salesCost.values, salesCost.total],
      [ProfitAndLossItems.materialCost.name, ...materialCost.values, materialCost.total],
      [ProfitAndLossItems.outsideCost.name, ...outsideCost.values, outsideCost.total],
      [ProfitAndLossItems.laborCost.name, ...laborCost.values, laborCost.total],
      [ProfitAndLossItems.domesticLaborCost.name, ...domesticLaborCost.values, domesticLaborCost.total],
      [ProfitAndLossItems.factoryExpenses.name, ...factoryExpenses.values, factoryExpenses.total],
      [ProfitAndLossItems.overHeadCost.name, ...overHeadCost.values, overHeadCost.total],
      [ProfitAndLossItems.depreciation.name, ...depreciation.values, depreciation.total],
      [ProfitAndLossItems.equipmentDepreciation.name, ...equipmentDepreciation.values, equipmentDepreciation.total],
      [ProfitAndLossItems.factoryCost.name, ...factoryCost.values, factoryCost.total],
      [ProfitAndLossItems.pastEquipmentCost.name, ...pastEquipmentCost.values, pastEquipmentCost.total],
      [ProfitAndLossItems.otherEquipmentCost.name, ...otherEquipmentCost.values, otherEquipmentCost.total],
      [ProfitAndLossItems.domesticFactoryExpenses.name, ...domesticFactoryExpenses.values, domesticFactoryExpenses.total],
      [ProfitAndLossItems.domesticOverHeadCost.name, ...domesticOverHeadCost.values, domesticOverHeadCost.total],
      [ProfitAndLossItems.domesticDepreciation.name, ...domesticDepreciation.values, domesticDepreciation.total],
      [ProfitAndLossItems.domesticInvestmentDepreciation.name, ...domesticInvestmentDepreciation.values, domesticInvestmentDepreciation.total],
      [ProfitAndLossItems.domesticFactoryBuildingDepreciation.name, ...domesticFactoryBuildingDepreciation.values, domesticFactoryBuildingDepreciation.total],
      [ProfitAndLossItems.domesticOtherDepreciation.name, ...domesticOtherDepreciation.values, domesticOtherDepreciation.total],
      [ProfitAndLossItems.scrapCost.name, ...scrapCost.values, scrapCost.total],
      [ProfitAndLossItems.sgaExpenses.name, ...sGAExpenses.values, sGAExpenses.total],
      [ProfitAndLossItems.appliedLaborCost.name, ...appliedLaborCost.values, appliedLaborCost.total],
      [ProfitAndLossItems.domesticSgaCost.name, ...domesticSgaCost.values, domesticSgaCost.total],
      [ProfitAndLossItems.salesManagementCost.name, ...salesManagementCost.values, salesManagementCost.total],
      [ProfitAndLossItems.devCost.name, ...devCost.values, devCost.total],
      [ProfitAndLossItems.royalty.name, ...royalty.values, royalty.total],
      [ProfitAndLossItems.otherCost.name, ...otherCost.values, otherCost.total],
      [ProfitAndLossItems.operatingProfit.name, ...profit.values, profit.total],
      [`${ProfitAndLossItems.operatingProfitRate.name}(%)`, ...profitRateValues, totalProfitRate],
    ];

    // 実質的営業利益率（客先指定部品がある場合のみ）
    if (hasCustomerSpecifiedParts && data.customerSpecifiedPartsCost) {
      const realOperatingProfitRateValues = data.labels.map((_, index) => {
        const sales = data.totalSales[index] || new Decimal(0);
        const customerCost = data.customerSpecifiedPartsCost[index] || new Decimal(0);
        const realSales = sales.minus(customerCost);
        const profitVal = data.profit[index] || new Decimal(0);
        return realSales.isZero() ? 0 : profitVal.div(realSales).times(100).toNumber();
      });

      const totalCustomerSpecifiedPartsCost = data.customerSpecifiedPartsCost.reduce(
        (sum, v) => sum.plus(v || new Decimal(0)), new Decimal(0)
      );
      const totalRealSales = new Decimal(totalSales.total).minus(totalCustomerSpecifiedPartsCost);
      const totalRealOperatingProfitRate = totalRealSales.isZero()
        ? 0
        : new Decimal(profit.total).div(totalRealSales).times(100).toNumber();

      rows.push([`${ProfitAndLossItems.realOperatingProfitRate.name}(%)`, ...realOperatingProfitRateValues, totalRealOperatingProfitRate]);
    }

    return [
      [t("features.InvestmentScreening.ProjectProfitablityStatement.label.unitPrefixCurrency", { currency: selectedCurrency.name })],
      [],
      monthsRow,
      ...rows,
    ];
  };

  // 更新投資用：月別損益（更新前/更新後比較）
  const createExportDataForUpdateMonthlyProfitLossComparison = (): (string | number)[][] => {
    // display版を使用（入力通貨→表示通貨に直接変換済み、二重変換回避）
    if (!displayMonthlyProfitAndLossData || !displayBeforeUpdateMonthlyProfitAndLossData) {
      return [[t("features.InvestmentScreening.ProjectProfitablityStatement.message.noMonthlyData")]];
    }

    // 月次データ処理用ヘルパー関数（JPY版は変換済みなので丸め処理のみ）
    const processMonthlyData = (data: Decimal[]): { values: number[], total: number } => {
      const decimalPlaces = hasDecimal(selectedCurrency.name) ? 2 : 0;
      const values = data.map(v => (v || new Decimal(0)).toDecimalPlaces(decimalPlaces, Decimal.ROUND_HALF_UP).toNumber());
      const total = data.reduce((sum, value) => sum.plus(value || new Decimal(0)), new Decimal(0)).toDecimalPlaces(decimalPlaces, Decimal.ROUND_HALF_UP).toNumber();
      return { values, total };
    };

    // 営業利益率計算用ヘルパー関数
    const calcProfitRateValues = (salesData: Decimal[], profitData: Decimal[]): number[] => {
      return salesData.map((sales, index) => {
        const profitVal = profitData[index] || new Decimal(0);
        return sales.isZero() ? 0 : profitVal.div(sales).times(100).toNumber();
      });
    };

    // 更新前データ（JPY版）
    const beforeTotalSales = processMonthlyData(displayBeforeUpdateMonthlyProfitAndLossData.totalSales);
    const beforeProductSales = processMonthlyData(displayBeforeUpdateMonthlyProfitAndLossData.productSales);
    const beforeMoldSales = processMonthlyData(displayBeforeUpdateMonthlyProfitAndLossData.moldSales);
    const beforeDevSales = processMonthlyData(displayBeforeUpdateMonthlyProfitAndLossData.devSales);
    const beforeEquipmentSales = processMonthlyData(displayBeforeUpdateMonthlyProfitAndLossData.equipmentSales);
    const beforeSalesCost = processMonthlyData(displayBeforeUpdateMonthlyProfitAndLossData.salesCost);
    const beforeMaterialCost = processMonthlyData(displayBeforeUpdateMonthlyProfitAndLossData.materialCost);
    const beforeOutsideCost = processMonthlyData(displayBeforeUpdateMonthlyProfitAndLossData.outsideCost);
    const beforeLaborCost = processMonthlyData(displayBeforeUpdateMonthlyProfitAndLossData.laborCost);
    const beforeDomesticLaborCost = processMonthlyData(displayBeforeUpdateMonthlyProfitAndLossData.domesticLaborCost);
    const beforeFactoryExpenses = processMonthlyData(displayBeforeUpdateMonthlyProfitAndLossData.factoryExpenses);
    const beforeOverHeadCost = processMonthlyData(displayBeforeUpdateMonthlyProfitAndLossData.overHeadCost);
    const beforeDepreciation = processMonthlyData(displayBeforeUpdateMonthlyProfitAndLossData.depreciation);
    const beforeEquipmentDepreciation = processMonthlyData(displayBeforeUpdateMonthlyProfitAndLossData.equipmentDepreciation);
    const beforeFactoryCost = processMonthlyData(displayBeforeUpdateMonthlyProfitAndLossData.factoryCost);
    const beforePastEquipmentCost = processMonthlyData(displayBeforeUpdateMonthlyProfitAndLossData.pastEquipmentCost || []);
    const beforeOtherEquipmentCost = processMonthlyData(displayBeforeUpdateMonthlyProfitAndLossData.otherEquipmentCost);
    const beforeDomesticFactoryExpenses = processMonthlyData(displayBeforeUpdateMonthlyProfitAndLossData.domesticFactoryExpenses);
    const beforeDomesticOverHeadCost = processMonthlyData(displayBeforeUpdateMonthlyProfitAndLossData.domesticOverHeadCost || []);
    const beforeDomesticDepreciation = processMonthlyData(displayBeforeUpdateMonthlyProfitAndLossData.domesticDepreciation || []);
    const beforeDomesticInvestmentDepreciation = processMonthlyData(displayBeforeUpdateMonthlyProfitAndLossData.domesticInvestmentDepreciation || []);
    const beforeDomesticFactoryBuildingDepreciation = processMonthlyData(displayBeforeUpdateMonthlyProfitAndLossData.domesticFactoryBuildingDepreciation || []);
    const beforeDomesticOtherDepreciation = processMonthlyData(displayBeforeUpdateMonthlyProfitAndLossData.domesticOtherDepreciation || []);
    const beforeScrapCost = processMonthlyData(displayBeforeUpdateMonthlyProfitAndLossData.scrapCost);
    const beforeSGAExpenses = processMonthlyData(displayBeforeUpdateMonthlyProfitAndLossData.sGAExpenses);
    const beforeAppliedLaborCost = processMonthlyData(displayBeforeUpdateMonthlyProfitAndLossData.appliedLaborCost);
    const beforeDomesticSgaCost = processMonthlyData(displayBeforeUpdateMonthlyProfitAndLossData.domesticSgaCost);
    const beforeSalesManagementCost = processMonthlyData(displayBeforeUpdateMonthlyProfitAndLossData.salesManagementCost);
    const beforeDevCost = processMonthlyData(displayBeforeUpdateMonthlyProfitAndLossData.devCost);
    const beforeRoyalty = processMonthlyData(displayBeforeUpdateMonthlyProfitAndLossData.royalty);
    const beforeOtherCost = processMonthlyData(displayBeforeUpdateMonthlyProfitAndLossData.otherCost);
    const beforeProfit = processMonthlyData(displayBeforeUpdateMonthlyProfitAndLossData.profit);
    const beforeProfitRateValues = calcProfitRateValues(displayBeforeUpdateMonthlyProfitAndLossData.totalSales, displayBeforeUpdateMonthlyProfitAndLossData.profit);
    const beforeTotalProfitRate = beforeTotalSales.total === 0 ? 0 : (beforeProfit.total / beforeTotalSales.total) * 100;

    // 更新後データ（JPY版）
    const afterTotalSales = processMonthlyData(displayMonthlyProfitAndLossData.totalSales);
    const afterProductSales = processMonthlyData(displayMonthlyProfitAndLossData.productSales);
    const afterMoldSales = processMonthlyData(displayMonthlyProfitAndLossData.moldSales);
    const afterDevSales = processMonthlyData(displayMonthlyProfitAndLossData.devSales);
    const afterEquipmentSales = processMonthlyData(displayMonthlyProfitAndLossData.equipmentSales);
    const afterSalesCost = processMonthlyData(displayMonthlyProfitAndLossData.salesCost);
    const afterMaterialCost = processMonthlyData(displayMonthlyProfitAndLossData.materialCost);
    const afterOutsideCost = processMonthlyData(displayMonthlyProfitAndLossData.outsideCost);
    const afterLaborCost = processMonthlyData(displayMonthlyProfitAndLossData.laborCost);
    const afterDomesticLaborCost = processMonthlyData(displayMonthlyProfitAndLossData.domesticLaborCost);
    const afterFactoryExpenses = processMonthlyData(displayMonthlyProfitAndLossData.factoryExpenses);
    const afterOverHeadCost = processMonthlyData(displayMonthlyProfitAndLossData.overHeadCost);
    const afterDepreciation = processMonthlyData(displayMonthlyProfitAndLossData.depreciation);
    const afterEquipmentDepreciation = processMonthlyData(displayMonthlyProfitAndLossData.equipmentDepreciation);
    const afterFactoryCost = processMonthlyData(displayMonthlyProfitAndLossData.factoryCost);
    const afterPastEquipmentCost = processMonthlyData(displayMonthlyProfitAndLossData.pastEquipmentCost || []);
    const afterOtherEquipmentCost = processMonthlyData(displayMonthlyProfitAndLossData.otherEquipmentCost);
    const afterDomesticFactoryExpenses = processMonthlyData(displayMonthlyProfitAndLossData.domesticFactoryExpenses);
    const afterDomesticOverHeadCost = processMonthlyData(displayMonthlyProfitAndLossData.domesticOverHeadCost || []);
    const afterDomesticDepreciation = processMonthlyData(displayMonthlyProfitAndLossData.domesticDepreciation || []);
    const afterDomesticInvestmentDepreciation = processMonthlyData(displayMonthlyProfitAndLossData.domesticInvestmentDepreciation || []);
    const afterDomesticFactoryBuildingDepreciation = processMonthlyData(displayMonthlyProfitAndLossData.domesticFactoryBuildingDepreciation || []);
    const afterDomesticOtherDepreciation = processMonthlyData(displayMonthlyProfitAndLossData.domesticOtherDepreciation || []);
    const afterScrapCost = processMonthlyData(displayMonthlyProfitAndLossData.scrapCost);
    const afterSGAExpenses = processMonthlyData(displayMonthlyProfitAndLossData.sGAExpenses);
    const afterAppliedLaborCost = processMonthlyData(displayMonthlyProfitAndLossData.appliedLaborCost);
    const afterDomesticSgaCost = processMonthlyData(displayMonthlyProfitAndLossData.domesticSgaCost);
    const afterSalesManagementCost = processMonthlyData(displayMonthlyProfitAndLossData.salesManagementCost);
    const afterDevCost = processMonthlyData(displayMonthlyProfitAndLossData.devCost);
    const afterRoyalty = processMonthlyData(displayMonthlyProfitAndLossData.royalty);
    const afterOtherCost = processMonthlyData(displayMonthlyProfitAndLossData.otherCost);
    const afterProfit = processMonthlyData(displayMonthlyProfitAndLossData.profit);
    const afterProfitRateValues = calcProfitRateValues(displayMonthlyProfitAndLossData.totalSales, displayMonthlyProfitAndLossData.profit);
    const afterTotalProfitRate = afterTotalSales.total === 0 ? 0 : (afterProfit.total / afterTotalSales.total) * 100;

    // ヘッダー行
    const headerRow = [
      t("features.InvestmentScreening.ProjectProfitablityStatement.tableHeader.item"),
      t("features.InvestmentScreening.ProjectProfitablityStatement.tableHeader.classification"),
      ...displayMonthlyProfitAndLossData.labels,
      t("features.InvestmentScreening.ProjectProfitablityStatement.tableHeader.total"),
    ];

    // 各項目について更新前/更新後の2行を生成するヘルパー関数
    const createComparisonRows = (
      name: string,
      before: { values: number[], total: number },
      after: { values: number[], total: number }
    ): (string | number)[][] => [
        [name, t("features.InvestmentScreening.ProjectProfitablityStatement.label.beforeUpdate"), ...before.values, before.total],
        ["", t("features.InvestmentScreening.ProjectProfitablityStatement.label.afterUpdate"), ...after.values, after.total],
      ];

    // 営業利益率用（%値）
    const createRateComparisonRows = (
      name: string,
      beforeValues: number[],
      beforeTotal: number,
      afterValues: number[],
      afterTotal: number
    ): (string | number)[][] => [
        [name, t("features.InvestmentScreening.ProjectProfitablityStatement.label.beforeUpdate"), ...beforeValues, beforeTotal],
        ["", t("features.InvestmentScreening.ProjectProfitablityStatement.label.afterUpdate"), ...afterValues, afterTotal],
      ];

    const rows: (string | number)[][] = [
      ...createComparisonRows(ProfitAndLossItems.totalSales.name, beforeTotalSales, afterTotalSales),
      ...createComparisonRows(ProfitAndLossItems.productSales.name, beforeProductSales, afterProductSales),
      ...createComparisonRows(ProfitAndLossItems.moldRecovery.name, beforeMoldSales, afterMoldSales),
      ...createComparisonRows(ProfitAndLossItems.devRecovery.name, beforeDevSales, afterDevSales),
      ...createComparisonRows(ProfitAndLossItems.equipmentRecovery.name, beforeEquipmentSales, afterEquipmentSales),
      ...createComparisonRows(ProfitAndLossItems.salesCost.name, beforeSalesCost, afterSalesCost),
      ...createComparisonRows(ProfitAndLossItems.materialCost.name, beforeMaterialCost, afterMaterialCost),
      ...createComparisonRows(ProfitAndLossItems.outsideCost.name, beforeOutsideCost, afterOutsideCost),
      ...createComparisonRows(ProfitAndLossItems.laborCost.name, beforeLaborCost, afterLaborCost),
      ...createComparisonRows(ProfitAndLossItems.domesticLaborCost.name, beforeDomesticLaborCost, afterDomesticLaborCost),
      ...createComparisonRows(ProfitAndLossItems.factoryExpenses.name, beforeFactoryExpenses, afterFactoryExpenses),
      ...createComparisonRows(ProfitAndLossItems.overHeadCost.name, beforeOverHeadCost, afterOverHeadCost),
      ...createComparisonRows(ProfitAndLossItems.depreciation.name, beforeDepreciation, afterDepreciation),
      ...createComparisonRows(ProfitAndLossItems.equipmentDepreciation.name, beforeEquipmentDepreciation, afterEquipmentDepreciation),
      ...createComparisonRows(ProfitAndLossItems.factoryCost.name, beforeFactoryCost, afterFactoryCost),
      ...createComparisonRows(ProfitAndLossItems.pastEquipmentCost.name, beforePastEquipmentCost, afterPastEquipmentCost),
      ...createComparisonRows(ProfitAndLossItems.otherEquipmentCost.name, beforeOtherEquipmentCost, afterOtherEquipmentCost),
      ...createComparisonRows(ProfitAndLossItems.domesticFactoryExpenses.name, beforeDomesticFactoryExpenses, afterDomesticFactoryExpenses),
      ...createComparisonRows(ProfitAndLossItems.domesticOverHeadCost.name, beforeDomesticOverHeadCost, afterDomesticOverHeadCost),
      ...createComparisonRows(ProfitAndLossItems.domesticDepreciation.name, beforeDomesticDepreciation, afterDomesticDepreciation),
      ...createComparisonRows(ProfitAndLossItems.domesticInvestmentDepreciation.name, beforeDomesticInvestmentDepreciation, afterDomesticInvestmentDepreciation),
      ...createComparisonRows(ProfitAndLossItems.domesticFactoryBuildingDepreciation.name, beforeDomesticFactoryBuildingDepreciation, afterDomesticFactoryBuildingDepreciation),
      ...createComparisonRows(ProfitAndLossItems.domesticOtherDepreciation.name, beforeDomesticOtherDepreciation, afterDomesticOtherDepreciation),
      ...createComparisonRows(ProfitAndLossItems.scrapCost.name, beforeScrapCost, afterScrapCost),
      ...createComparisonRows(ProfitAndLossItems.sgaExpenses.name, beforeSGAExpenses, afterSGAExpenses),
      ...createComparisonRows(ProfitAndLossItems.appliedLaborCost.name, beforeAppliedLaborCost, afterAppliedLaborCost),
      ...createComparisonRows(ProfitAndLossItems.domesticSgaCost.name, beforeDomesticSgaCost, afterDomesticSgaCost),
      ...createComparisonRows(ProfitAndLossItems.salesManagementCost.name, beforeSalesManagementCost, afterSalesManagementCost),
      ...createComparisonRows(ProfitAndLossItems.devCost.name, beforeDevCost, afterDevCost),
      ...createComparisonRows(ProfitAndLossItems.royalty.name, beforeRoyalty, afterRoyalty),
      ...createComparisonRows(ProfitAndLossItems.otherCost.name, beforeOtherCost, afterOtherCost),
      ...createComparisonRows(ProfitAndLossItems.operatingProfit.name, beforeProfit, afterProfit),
      ...createRateComparisonRows(`${ProfitAndLossItems.operatingProfitRate.name}(%)`, beforeProfitRateValues, beforeTotalProfitRate, afterProfitRateValues, afterTotalProfitRate),
    ];

    // 実質的営業利益率（客先指定部品がある場合のみ）
    if (hasCustomerSpecifiedParts && displayMonthlyProfitAndLossData.customerSpecifiedPartsCost && displayBeforeUpdateMonthlyProfitAndLossData.customerSpecifiedPartsCost) {
      // 更新前の実質的営業利益率
      const beforeRealProfitRateValues = displayBeforeUpdateMonthlyProfitAndLossData.labels.map((_, index) => {
        const sales = displayBeforeUpdateMonthlyProfitAndLossData.totalSales[index] || new Decimal(0);
        const customerCost = displayBeforeUpdateMonthlyProfitAndLossData.customerSpecifiedPartsCost[index] || new Decimal(0);
        const realSales = sales.minus(customerCost);
        const profitVal = displayBeforeUpdateMonthlyProfitAndLossData.profit[index] || new Decimal(0);
        return realSales.isZero() ? 0 : profitVal.div(realSales).times(100).toNumber();
      });
      const beforeTotalCustomerCost = displayBeforeUpdateMonthlyProfitAndLossData.customerSpecifiedPartsCost.reduce(
        (sum, v) => sum.plus(v || new Decimal(0)), new Decimal(0)
      );
      const beforeTotalRealSales = new Decimal(beforeTotalSales.total).minus(beforeTotalCustomerCost);
      const beforeTotalRealProfitRate = beforeTotalRealSales.isZero() ? 0 : (beforeProfit.total / beforeTotalRealSales.toNumber()) * 100;

      // 更新後の実質的営業利益率
      const afterRealProfitRateValues = displayMonthlyProfitAndLossData.labels.map((_, index) => {
        const sales = displayMonthlyProfitAndLossData.totalSales[index] || new Decimal(0);
        const customerCost = displayMonthlyProfitAndLossData.customerSpecifiedPartsCost[index] || new Decimal(0);
        const realSales = sales.minus(customerCost);
        const profitVal = displayMonthlyProfitAndLossData.profit[index] || new Decimal(0);
        return realSales.isZero() ? 0 : profitVal.div(realSales).times(100).toNumber();
      });
      const afterTotalCustomerCost = displayMonthlyProfitAndLossData.customerSpecifiedPartsCost.reduce(
        (sum, v) => sum.plus(v || new Decimal(0)), new Decimal(0)
      );
      const afterTotalRealSales = new Decimal(afterTotalSales.total).minus(afterTotalCustomerCost);
      const afterTotalRealProfitRate = afterTotalRealSales.isZero() ? 0 : (afterProfit.total / afterTotalRealSales.toNumber()) * 100;

      rows.push(
        ...createRateComparisonRows(
          `${ProfitAndLossItems.realOperatingProfitRate.name}(%)`,
          beforeRealProfitRateValues,
          beforeTotalRealProfitRate,
          afterRealProfitRateValues,
          afterTotalRealProfitRate
        )
      );
    }

    return [
      [t("features.InvestmentScreening.ProjectProfitablityStatement.label.unitPrefixCurrency", { currency: selectedCurrency.name })],
      [],
      headerRow,
      ...rows,
    ];
  };

  // 更新投資用：年度別損益（更新前/更新後比較）
  const createExportDataForUpdateProfitLossComparison = (): (string | number)[][] => {
    // display版state変数を使用（入力通貨→表示通貨に直接変換済み、二重変換回避）

    // 配列データを値と合計を取得（JPY版は変換済みなので丸め処理のみ）
    const processData = (data: Decimal[]): { values: number[], total: number } => {
      const values = fiscalYearsString.map((_, index) => (data[index] || new Decimal(0)).toNumber());
      const total = data.reduce((sum, value) => sum.plus(value || new Decimal(0)), new Decimal(0)).toNumber();
      return { values, total };
    };

    // 営業利益率用
    const processRateData = (data: Decimal[], totalSales: number, totalProfit: number): { values: number[], total: number } => {
      const values = fiscalYearsString.map((_, index) => (data[index] || new Decimal(0)).toNumber());
      const total = calcProfitRate(new Decimal(totalSales), new Decimal(totalProfit)).toNumber();
      return { values, total };
    };

    // 更新前データ（JPY版）
    const beforeTotalSales = processData(displayBeforeUpdateTotalSalesByYear);
    const beforeProductSales = processData(displayBeforeUpdateProductSalesByYear);
    const beforeMoldSales = processData(displayBeforeUpdateMoldSalesByYear);
    const beforeDevSales = processData(displayBeforeUpdateDevSalesByYear);
    const beforeEquipmentSales = processData(displayBeforeUpdateEquipmentSalesByYear);
    const beforeSalesCost = processData(displayBeforeUpdateSalesCostByYear);
    const beforeMaterialCost = processData(displayBeforeUpdateTotalMaterialCostByYear);
    const beforeOutsideCost = processData(displayBeforeUpdateOutsideCostByYear);
    const beforeLaborCost = processData(displayBeforeUpdateLaborCostByYear);
    const beforeDomesticLaborCost = processData(displayBeforeUpdateDomesticLaborCostByYear);
    const beforeFactoryExpenses = processData(displayBeforeUpdateFactoryExpensesByYear);
    const beforeOverHeadCost = processData(displayBeforeUpdateOverHeadCostByYear);
    const beforeDepreciation = processData(displayBeforeUpdateDepreciationByYear);
    const beforeEquipmentDepreciation = processData(displayBeforeUpdateEquipmentDepreciationByYear);
    const beforeFactoryCost = processData(displayBeforeUpdateFactoryCostByYear);
    const beforePastEquipmentCost = processData(displayBeforeUpdatePastEquipmentCostByYear);
    const beforeOtherEquipmentCost = processData(displayBeforeUpdateOtherEquipmentCostByYear);
    const beforeDomesticFactoryExpenses = processData(displayBeforeUpdateDomesticFactoryExpensesByYear);
    const beforeDomesticOverHeadCost = processData(displayBeforeUpdateDomesticOverHeadCostByYear);
    const beforeDomesticDepreciation = processData(displayBeforeUpdateDomesticDepreciationByYear);
    const beforeDomesticInvestmentDepreciation = processData(displayBeforeUpdateDomesticInvestmentDepreciationByYear);
    const beforeDomesticFactoryBuildingDepreciation = processData(displayBeforeUpdateDomesticFactoryBuildingDepreciationByYear);
    const beforeDomesticOtherDepreciation = processData(displayBeforeUpdateDomesticOtherDepreciationByYear);
    const beforeScrapCost = processData(displayBeforeUpdateScrapCostByYear);
    const beforeDomesticSgaCost = processData(displayBeforeUpdateDomesticSgaCostByYear);
    const beforeSGAExpenses = processData(displayBeforeUpdateSGAExpensesByYear);
    const beforeAppliedLaborCost = processData(displayBeforeUpdateAppliedLaborCostByYear);
    const beforeSalesManagementCost = processData(displayBeforeUpdateSalesManagementCostByYear);
    const beforeDevCost = processData(displayBeforeUpdateDevCostByYear);
    const beforeRoyalty = processData(displayBeforeUpdateRoyaltyByYear);
    const beforeOtherCost = processData(displayBeforeUpdateOtherCostByYear);
    const beforeProfit = processData(displayBeforeUpdateProfitByYear);
    const beforeProfitRate = processRateData(displayBeforeUpdateProfitRateByYear, beforeTotalSales.total, beforeProfit.total);

    // 更新後データ（JPY版）
    const afterTotalSales = processData(displayTotalSalesByYear);
    const afterProductSales = processData(displayProductSalesByYear);
    const afterMoldSales = processData(displayMoldSalesByYear);
    const afterDevSales = processData(displayDevSalesByYear);
    const afterEquipmentSales = processData(displayEquipmentSalesByYear);
    const afterSalesCost = processData(displaySalesCostByYear);
    const afterMaterialCost = processData(displayTotalMaterialCostByYear);
    const afterOutsideCost = processData(displayOutsideCostByYear);
    const afterLaborCost = processData(displayLaborCostByYear);
    const afterDomesticLaborCost = processData(displayDomesticLaborCostByYear);
    const afterFactoryExpenses = processData(displayFactoryExpensesByYear);
    const afterOverHeadCost = processData(displayOverHeadCostByYear);
    const afterDepreciation = processData(displayDepreciationByYear);
    const afterEquipmentDepreciation = processData(displayEquipmentDepreciationByYear);
    const afterFactoryCost = processData(displayFactoryCostByYear);
    const afterPastEquipmentCost = processData(displayPastEquipmentCostByYear);
    const afterOtherEquipmentCost = processData(displayOtherEquipmentCostByYear);
    const afterDomesticFactoryExpenses = processData(displayDomesticFactoryExpensesByYear);
    const afterDomesticOverHeadCost = processData(displayDomesticOverHeadCostByYear);
    const afterDomesticDepreciation = processData(displayDomesticDepreciationByYear);
    const afterDomesticInvestmentDepreciation = processData(displayDomesticInvestmentDepreciationByYear);
    const afterDomesticFactoryBuildingDepreciation = processData(displayDomesticFactoryBuildingDepreciationByYear);
    const afterDomesticOtherDepreciation = processData(displayDomesticOtherDepreciationByYear);
    const afterScrapCost = processData(displayScrapCostByYear);
    const afterDomesticSgaCost = processData(displayDomesticSgaCostByYear);
    const afterSGAExpenses = processData(displaySGAExpensesByYear);
    const afterAppliedLaborCost = processData(displayAppliedLaborCostByYear);
    const afterSalesManagementCost = processData(displaySalesManagementCostByYear);
    const afterDevCost = processData(displayDevCostByYear);
    const afterRoyalty = processData(displayRoyaltyByYear);
    const afterOtherCost = processData(displayOtherCostByYear);
    const afterProfit = processData(displayProfitByYear);
    const afterProfitRate = processRateData(displayProfitRateByYear, afterTotalSales.total, afterProfit.total);

    // 実質的営業利益率（率なので通貨変換不要）
    // 実質的営業利益率の合計 = 合計営業利益 / 合計実質売上
    // 更新前の客先指定部品材料費はdisplayBeforeUpdateMonthlyProfitAndLossDataから取得（JPY版）
    const beforeTotalCustomerSpecifiedPartsCostExcel = displayBeforeUpdateMonthlyProfitAndLossData?.customerSpecifiedPartsCost
      ? displayBeforeUpdateMonthlyProfitAndLossData.customerSpecifiedPartsCost.reduce((sum, v) => sum.plus(v || new Decimal(0)), new Decimal(0))
      : new Decimal(0);
    // 更新後の客先指定部品材料費（JPY版）
    const afterTotalCustomerSpecifiedPartsCostExcel = displayMonthlyProfitAndLossData?.customerSpecifiedPartsCost
      ? displayMonthlyProfitAndLossData.customerSpecifiedPartsCost.reduce((sum, v) => sum.plus(v || new Decimal(0)), new Decimal(0))
      : new Decimal(0);
    // 更新前（JPY版）
    const beforeRealOperatingProfitRateValues = fiscalYearsString.map((_, index) => (displayBeforeUpdateRealOperatingProfitRateByYear[index] || new Decimal(0)).toNumber());
    const beforeRealSalesTotal = new Decimal(beforeTotalSales.total).minus(beforeTotalCustomerSpecifiedPartsCostExcel);
    const beforeRealOperatingProfitRateTotal = beforeRealSalesTotal.isZero()
      ? 0
      : new Decimal(beforeProfit.total).div(beforeRealSalesTotal).times(100).toNumber();
    const beforeRealOperatingProfitRate = { values: beforeRealOperatingProfitRateValues, total: beforeRealOperatingProfitRateTotal };
    // 更新後（JPY版）
    const afterRealOperatingProfitRateValues = fiscalYearsString.map((_, index) => (displayRealOperatingProfitRateByYear[index] || new Decimal(0)).toNumber());
    const afterRealSalesTotal = new Decimal(afterTotalSales.total).minus(afterTotalCustomerSpecifiedPartsCostExcel);
    const afterRealOperatingProfitRateTotal = afterRealSalesTotal.isZero()
      ? 0
      : new Decimal(afterProfit.total).div(afterRealSalesTotal).times(100).toNumber();
    const afterRealOperatingProfitRate = { values: afterRealOperatingProfitRateValues, total: afterRealOperatingProfitRateTotal };

    const yearsRow = [
      t("features.InvestmentScreening.ProjectProfitablityStatement.tableHeader.item"),
      t("features.InvestmentScreening.ProjectProfitablityStatement.tableHeader.classification"),
      ...fiscalYearsString.map((year) => year),
      t("features.InvestmentScreening.ProjectProfitablityStatement.tableHeader.total"),
    ];

    // 各項目について更新前/更新後の2行を生成
    const createRowPair = (name: string, before: { values: number[], total: number }, after: { values: number[], total: number }): (string | number)[][] => {
      return [
        [name, t("features.InvestmentScreening.ProjectProfitablityStatement.tableHeader.beforeUpdate"), ...before.values, before.total],
        ["", t("features.InvestmentScreening.ProjectProfitablityStatement.tableHeader.afterUpdate"), ...after.values, after.total],
      ];
    };

    const rows = [
      ...createRowPair(ProfitAndLossItems.totalSales.name, beforeTotalSales, afterTotalSales),
      ...createRowPair(ProfitAndLossItems.productSales.name, beforeProductSales, afterProductSales),
      ...createRowPair(ProfitAndLossItems.moldRecovery.name, beforeMoldSales, afterMoldSales),
      ...createRowPair(ProfitAndLossItems.devRecovery.name, beforeDevSales, afterDevSales),
      ...createRowPair(ProfitAndLossItems.equipmentRecovery.name, beforeEquipmentSales, afterEquipmentSales),
      ...createRowPair(ProfitAndLossItems.salesCost.name, beforeSalesCost, afterSalesCost),
      ...createRowPair(ProfitAndLossItems.materialCost.name, beforeMaterialCost, afterMaterialCost),
      ...createRowPair(ProfitAndLossItems.outsideCost.name, beforeOutsideCost, afterOutsideCost),
      ...createRowPair(ProfitAndLossItems.laborCost.name, beforeLaborCost, afterLaborCost),
      ...createRowPair(ProfitAndLossItems.domesticLaborCost.name, beforeDomesticLaborCost, afterDomesticLaborCost),
      ...createRowPair(ProfitAndLossItems.factoryExpenses.name, beforeFactoryExpenses, afterFactoryExpenses),
      ...createRowPair(ProfitAndLossItems.overHeadCost.name, beforeOverHeadCost, afterOverHeadCost),
      ...createRowPair(ProfitAndLossItems.depreciation.name, beforeDepreciation, afterDepreciation),
      ...createRowPair(ProfitAndLossItems.equipmentDepreciation.name, beforeEquipmentDepreciation, afterEquipmentDepreciation),
      ...createRowPair(ProfitAndLossItems.factoryCost.name, beforeFactoryCost, afterFactoryCost),
      ...createRowPair(ProfitAndLossItems.pastEquipmentCost.name, beforePastEquipmentCost, afterPastEquipmentCost),
      ...createRowPair(ProfitAndLossItems.otherEquipmentCost.name, beforeOtherEquipmentCost, afterOtherEquipmentCost),
      ...createRowPair(ProfitAndLossItems.domesticFactoryExpenses.name, beforeDomesticFactoryExpenses, afterDomesticFactoryExpenses),
      ...createRowPair(ProfitAndLossItems.domesticOverHeadCost.name, beforeDomesticOverHeadCost, afterDomesticOverHeadCost),
      ...createRowPair(ProfitAndLossItems.domesticDepreciation.name, beforeDomesticDepreciation, afterDomesticDepreciation),
      ...createRowPair(ProfitAndLossItems.domesticInvestmentDepreciation.name, beforeDomesticInvestmentDepreciation, afterDomesticInvestmentDepreciation),
      ...createRowPair(ProfitAndLossItems.domesticFactoryBuildingDepreciation.name, beforeDomesticFactoryBuildingDepreciation, afterDomesticFactoryBuildingDepreciation),
      ...createRowPair(ProfitAndLossItems.domesticOtherDepreciation.name, beforeDomesticOtherDepreciation, afterDomesticOtherDepreciation),
      ...createRowPair(ProfitAndLossItems.scrapCost.name, beforeScrapCost, afterScrapCost),
      ...createRowPair(ProfitAndLossItems.sgaExpenses.name, beforeSGAExpenses, afterSGAExpenses),
      ...createRowPair(ProfitAndLossItems.appliedLaborCost.name, beforeAppliedLaborCost, afterAppliedLaborCost),
      ...createRowPair(ProfitAndLossItems.domesticSgaCost.name, beforeDomesticSgaCost, afterDomesticSgaCost),
      ...createRowPair(ProfitAndLossItems.salesManagementCost.name, beforeSalesManagementCost, afterSalesManagementCost),
      ...createRowPair(ProfitAndLossItems.devCost.name, beforeDevCost, afterDevCost),
      ...createRowPair(ProfitAndLossItems.royalty.name, beforeRoyalty, afterRoyalty),
      ...createRowPair(ProfitAndLossItems.otherCost.name, beforeOtherCost, afterOtherCost),
      ...createRowPair(ProfitAndLossItems.operatingProfit.name, beforeProfit, afterProfit),
      ...createRowPair(`${ProfitAndLossItems.operatingProfitRate.name}(%)`, beforeProfitRate, afterProfitRate),
      // 客先指定部品が存在する場合のみ実質的営業利益率を出力
      ...(hasCustomerSpecifiedParts ? createRowPair(`${ProfitAndLossItems.realOperatingProfitRate.name}(%)`, beforeRealOperatingProfitRate, afterRealOperatingProfitRate) : []),
    ];

    return [
      [t("features.InvestmentScreening.ProjectProfitablityStatement.label.unitPrefixCurrency", { currency: selectedCurrency.name })],
      [],
      yearsRow,
      ...rows,
    ];
  };

  // 更新投資用：{t("features.InvestmentScreening.ProjectProfitablityStatement.heading.improvementByUpdate")}
  const createExportDataForImprovementEffect = (): (string | number)[][] => {
    // 簡易モードの場合は updateSummaryData から直接計算（合理化と同様のアプローチ）
    if (isSimpleMode) {
      const displayCurrencyName = selectedCurrency?.name || "JPY";
      const yearHeaders = fiscalYearsString.map((_, i) => `${i + 1}年目`);
      const yearLabels = [...fiscalYearsString];
      const rows: (string | number)[][] = [];

      // 月次データを年度ごとに集計
      const aggregateByYear = (monthlyData: Record<string, number>, years: string[]): number[] => {
        return years.map((fiscalYear) => {
          const m = fiscalYear.match(/(\d{4})年/);
          if (!m) return 0;
          const endYear = parseInt(m[1], 10);
          let total = 0;
          for (let mo = 4; mo <= 12; mo++) total += monthlyData[`${endYear - 1}-${String(mo).padStart(2, "0")}`] || 0;
          for (let mo = 1; mo <= 3; mo++) total += monthlyData[`${endYear}-${String(mo).padStart(2, "0")}`] || 0;
          return total;
        });
      };

      // 費用通貨を表示通貨に変換
      const convertCostVal = (value: number, sourceCurrencyId: string | undefined): number => {
        if (!sourceCurrencyId) return value;
        const src = currencyData.find(c => c.currencyId === sourceCurrencyId);
        if (!src?.currencyName || src.currencyName === displayCurrencyName) return value;
        const converted = convertWithRate(new Decimal(value), src.currencyName, displayCurrencyName);
        const dp = hasDecimal(displayCurrencyName) ? 2 : 0;
        return converted.toDecimalPlaces(dp, Decimal.ROUND_HALF_UP).toNumber();
      };

      // 項目名取得
      const getUpdateItemName = (item: RationalizationProfitLossItem): string => {
        if (item.itemType === "custom" && item.customItemName) return item.customItemName;
        const nameMap: Record<string, string> = {
          productionQuantity: t("features.InvestmentScreening.ProjectProfitablityStatement.improvementItem.productionQuantity"),
          salesQuantity: t("features.InvestmentScreening.ProjectProfitablityStatement.improvementItem.salesQuantity"),
          laborCost: t("features.InvestmentScreening.ProjectProfitablityStatement.improvementItem.laborCost"),
          repairCost: t("features.InvestmentScreening.ProjectProfitablityStatement.improvementItem.repairCost"),
          consumablesCost: t("features.InvestmentScreening.ProjectProfitablityStatement.improvementItem.consumablesCost"),
          maintenanceCost: t("features.InvestmentScreening.ProjectProfitablityStatement.improvementItem.maintenanceCost"),
          scrapCost: t("features.InvestmentScreening.ProjectProfitablityStatement.improvementItem.scrapCost"),
          totalCost: t("features.InvestmentScreening.ProjectProfitablityStatement.improvementItem.totalCost"),
          targetEquipmentDepreciation: t("features.InvestmentScreening.ProjectProfitablityStatement.improvementItem.targetEquipmentDepreciation"),
          otherDepreciation: t("features.InvestmentScreening.ProjectProfitablityStatement.improvementItem.otherDepreciation"),
        };
        return nameMap[item.itemType] || item.itemType;
      };

      // ========== 改善効果（指標）- 工程別 ==========
      rows.push([t("features.InvestmentScreening.ProjectProfitablityStatement.heading.improvementByUpdate") + "（指標）"]);
      rows.push([t("features.InvestmentScreening.ProjectProfitablityStatement.label.unitPcs")]);
      rows.push([]);
      const indicatorProcesses = updateSummaryData.filter(s => s.indicatorItems && s.indicatorItems.length > 0);
      if (indicatorProcesses.length > 0) {
        indicatorProcesses.forEach(summary => {
          rows.push([summary.productionLineName || summary.productionLineId || ""]);
          rows.push(["No.", "項目", "区分", ...yearHeaders, "合計"]);
          rows.push(["", "", "", ...yearLabels, ""]);
          summary.indicatorItems!.forEach((item, index) => {
            const beforeByYear = aggregateByYear(item.beforeMonthlyData, fiscalYearsString);
            const afterByYear = aggregateByYear(item.afterMonthlyData, fiscalYearsString);
            rows.push([index + 1, getUpdateItemName(item), t("features.InvestmentScreening.ProjectProfitablityStatement.label.beforeUpdate"), ...beforeByYear, beforeByYear.reduce((a, b) => a + b, 0)]);
            rows.push(["", "", t("features.InvestmentScreening.ProjectProfitablityStatement.label.afterUpdate"), ...afterByYear, afterByYear.reduce((a, b) => a + b, 0)]);
          });
          rows.push([]);
        });
      } else {
        rows.push([t("features.InvestmentScreening.ProjectProfitablityStatement.message.noIndicatorData")]);
      }

      // ========== 改善効果（費用）- 工程別 ==========
      rows.push([]);
      rows.push([t("features.InvestmentScreening.ProjectProfitablityStatement.heading.improvementByUpdate") + "（費用）"]);
      rows.push([t("features.InvestmentScreening.ProjectProfitablityStatement.label.unitPrefixCurrency", { currency: displayCurrencyName })]);
      rows.push([]);
      const costProcesses = updateSummaryData.filter(s => s.costItems && s.costItems.length > 0);
      if (costProcesses.length > 0) {
        costProcesses.forEach(summary => {
          rows.push([summary.productionLineName || summary.productionLineId || ""]);
          rows.push(["No.", "項目", "区分", ...yearHeaders, "合計"]);
          rows.push(["", "", "", ...yearLabels, ""]);
          summary.costItems!.forEach((item, index) => {
            const beforeByYear = aggregateByYear(item.beforeMonthlyData, fiscalYearsString).map(v => convertCostVal(v, summary.costCurrencyId));
            const afterByYear = aggregateByYear(item.afterMonthlyData, fiscalYearsString).map(v => convertCostVal(v, summary.costCurrencyId));
            rows.push([index + 1, getUpdateItemName(item), t("features.InvestmentScreening.ProjectProfitablityStatement.label.beforeUpdate"), ...beforeByYear, beforeByYear.reduce((a, b) => a + b, 0)]);
            rows.push(["", "", t("features.InvestmentScreening.ProjectProfitablityStatement.label.afterUpdate"), ...afterByYear, afterByYear.reduce((a, b) => a + b, 0)]);
          });
          rows.push([]);
        });
      } else {
        rows.push([t("features.InvestmentScreening.ProjectProfitablityStatement.message.noCostData")]);
      }

      // ========== 改善効果の合計（指標）==========
      rows.push([]);
      rows.push([t("features.InvestmentScreening.ProjectProfitablityStatement.heading.improvementTotalIndicator")]);
      rows.push([t("features.InvestmentScreening.ProjectProfitablityStatement.label.unitPcs")]);
      rows.push([]);
      const allIndicatorMap = new Map<string, { name: string; byYear: number[] }>();
      updateSummaryData.forEach(summary => {
        summary.indicatorItems?.forEach(item => {
          const key = item.itemType === "custom" ? `custom_${item.customItemName}` : item.itemType;
          const byYear = aggregateByYear(item.afterMonthlyData, fiscalYearsString).map(
            (after, i) => after - aggregateByYear(item.beforeMonthlyData, fiscalYearsString)[i]
          );
          if (allIndicatorMap.has(key)) {
            const ex = allIndicatorMap.get(key)!;
            ex.byYear = ex.byYear.map((v, i) => v + (byYear[i] || 0));
          } else {
            allIndicatorMap.set(key, { name: getUpdateItemName(item), byYear });
          }
        });
      });
      if (allIndicatorMap.size > 0) {
        rows.push(["No.", "項目", ...yearHeaders, "合計"]);
        rows.push(["", "", ...yearLabels, ""]);
        let idx = 1;
        allIndicatorMap.forEach(item => {
          rows.push([idx++, item.name, ...item.byYear, item.byYear.reduce((a, b) => a + b, 0)]);
        });
      } else {
        rows.push([t("features.InvestmentScreening.ProjectProfitablityStatement.message.noIndicatorData")]);
      }

      // ========== 改善効果の合計（費用）==========
      rows.push([]);
      rows.push([t("features.InvestmentScreening.ProjectProfitablityStatement.heading.improvementTotalCost")]);
      rows.push([t("features.InvestmentScreening.ProjectProfitablityStatement.label.unitPrefixCurrency", { currency: displayCurrencyName })]);
      rows.push([]);
      const allCostMap = new Map<string, { name: string; byYear: number[] }>();
      updateSummaryData.forEach(summary => {
        summary.costItems?.forEach(item => {
          const key = item.itemType === "custom" ? `custom_${item.customItemName}` : item.itemType;
          const beforeByYear = aggregateByYear(item.beforeMonthlyData, fiscalYearsString).map(v => convertCostVal(v, summary.costCurrencyId));
          const afterByYear = aggregateByYear(item.afterMonthlyData, fiscalYearsString).map(v => convertCostVal(v, summary.costCurrencyId));
          const diffByYear = afterByYear.map((a, i) => a - beforeByYear[i]);
          if (allCostMap.has(key)) {
            const ex = allCostMap.get(key)!;
            ex.byYear = ex.byYear.map((v, i) => v + (diffByYear[i] || 0));
          } else {
            allCostMap.set(key, { name: getUpdateItemName(item), byYear: diffByYear });
          }
        });
      });
      if (allCostMap.size > 0) {
        rows.push(["No.", "項目", ...yearHeaders, "合計"]);
        rows.push(["", "", ...yearLabels, ""]);
        const allDiffs: number[][] = [];
        let idx = 1;
        allCostMap.forEach(item => {
          allDiffs.push(item.byYear);
          rows.push([idx++, item.name, ...item.byYear, item.byYear.reduce((a, b) => a + b, 0)]);
        });
        // 合計（損益）行
        const totalsByYear = fiscalYearsString.map((_, yi) => allDiffs.reduce((sum, d) => sum + (d[yi] || 0), 0));
        const grandTotal = totalsByYear.reduce((a, b) => a + b, 0);
        rows.push([
          allCostMap.size + 1,
          t("features.InvestmentScreening.ProjectProfitablityStatement.tableHeader.totalProfit"),
          ...totalsByYear,
          grandTotal,
        ]);
        rows.push([]);
        rows.push([t("features.InvestmentScreening.ProjectProfitablityStatement.message.costImprovementNote")]);
      } else {
        rows.push([t("features.InvestmentScreening.ProjectProfitablityStatement.message.noCostData")]);
      }

      return rows;
    }

    // 詳細モード: 既存の処理（profitLoss-based）
    // 通貨変換用ヘルパー関数（更新前後サマリーの入力はJPY固定）
    const convertValue = (value: number | undefined | null): number | undefined => {
      if (value === undefined || value === null) return undefined;
      if (!selectedCurrency?.name) return value;
      return convertWithRate(new Decimal(value), "JPY", selectedCurrency.name).toNumber();
    };

    // 差異計算ヘルパー関数（投資計画 - 直近実績または想定）
    const calcDifference = (
      investmentPlan: number | undefined,
      recentActual: number | undefined,
      theoretical: number | undefined
    ): number | undefined => {
      if (investmentPlan === undefined) return undefined;
      const base = recentActual ?? theoretical;
      if (base === undefined) return undefined;
      return investmentPlan - base;
    };

    const formatValue = (value: number | undefined): string | number => {
      return value !== undefined ? value : "-";
    };

    const formatRate = (value: number | undefined): string => {
      return value !== undefined ? `${(100 - value).toFixed(2)}%` : "-";
    };

    const formatRateDiff = (
      investmentPlan: number | undefined,
      recentActual: number | undefined,
      theoretical: number | undefined
    ): string => {
      const diff = calcDifference(
        investmentPlan !== undefined ? 100 - investmentPlan : undefined,
        recentActual !== undefined ? 100 - recentActual : undefined,
        theoretical !== undefined ? 100 - theoretical : undefined
      );
      return diff !== undefined ? `${diff.toFixed(2)}%` : "-";
    };

    const rows: (string | number)[][] = [
      [t("features.InvestmentScreening.ProjectProfitablityStatement.label.unitPrefixCurrency", { currency: selectedCurrency?.name || "JPY" })],
      [],
    ];

    // 各工程ごとにデータを出力
    updateSummaryData.forEach((summary, summaryIndex) => {
      const processName = summary.productionLineName || summary.productionLineId || t("features.InvestmentScreening.ProjectProfitablityStatement.label.processPrefix", { number: summaryIndex + 1 });

      // 工程名ヘッダー
      rows.push([processName]);
      rows.push(
        [
          t("features.InvestmentScreening.ProjectProfitablityStatement.tableHeader.item"),
          t("features.InvestmentScreening.ProjectProfitablityStatement.tableHeader.recentActual"),
          t("features.InvestmentScreening.ProjectProfitablityStatement.tableHeader.theoretical"),
          t("features.InvestmentScreening.ProjectProfitablityStatement.tableHeader.updateInvestment"),
          t("features.InvestmentScreening.ProjectProfitablityStatement.tableHeader.difference")
        ]
      );

      // 歩留
      rows.push([
        t("features.InvestmentScreening.ProjectProfitablityStatement.improvementItem.yield"),
        formatRate(summary.profitLoss?.recentActual?.scrapRate),
        formatRate(summary.profitLoss?.theoretical?.scrapRate),
        formatRate(summary.profitLoss?.investmentPlan?.scrapRate),
        formatRateDiff(
          summary.profitLoss?.investmentPlan?.scrapRate,
          summary.profitLoss?.recentActual?.scrapRate,
          summary.profitLoss?.theoretical?.scrapRate
        ),
      ]);

      // 仕損費
      const scrapCostDiff = calcDifference(
        summary.profitLoss?.investmentPlan?.scrapCost,
        summary.profitLoss?.recentActual?.scrapCost,
        summary.profitLoss?.theoretical?.scrapCost
      );
      rows.push([
        t("features.InvestmentScreening.ProjectProfitablityStatement.improvementItem.scrapCostShort"),
        formatValue(convertValue(summary.profitLoss?.recentActual?.scrapCost)),
        formatValue(convertValue(summary.profitLoss?.theoretical?.scrapCost)),
        formatValue(convertValue(summary.profitLoss?.investmentPlan?.scrapCost)),
        formatValue(convertValue(scrapCostDiff)),
      ]);

      // 修繕費
      const repairCostDiff = calcDifference(
        summary.profitLoss?.investmentPlan?.repairCost ?? undefined,
        summary.profitLoss?.recentActual?.repairCost ?? undefined,
        summary.profitLoss?.theoretical?.repairCost ?? undefined
      );
      rows.push([
        t("features.InvestmentScreening.ProjectProfitablityStatement.improvementItem.repairCost"),
        formatValue(convertValue(summary.profitLoss?.recentActual?.repairCost)),
        formatValue(convertValue(summary.profitLoss?.theoretical?.repairCost)),
        formatValue(convertValue(summary.profitLoss?.investmentPlan?.repairCost)),
        formatValue(convertValue(repairCostDiff)),
      ]);

      // 消耗品費
      const consumablesCostDiff = calcDifference(
        summary.profitLoss?.investmentPlan?.consumablesCost ?? undefined,
        summary.profitLoss?.recentActual?.consumablesCost ?? undefined,
        summary.profitLoss?.theoretical?.consumablesCost ?? undefined
      );
      rows.push([
        t("features.InvestmentScreening.ProjectProfitablityStatement.improvementItem.consumablesCost"),
        formatValue(convertValue(summary.profitLoss?.recentActual?.consumablesCost)),
        formatValue(convertValue(summary.profitLoss?.theoretical?.consumablesCost)),
        formatValue(convertValue(summary.profitLoss?.investmentPlan?.consumablesCost)),
        formatValue(convertValue(consumablesCostDiff)),
      ]);

      // 空行
      rows.push([]);
    });

    // 全工程の改善額合計
    let totalScrapCostImprovement = 0;
    let totalRepairCostImprovement = 0;
    let totalConsumablesCostImprovement = 0;

    updateSummaryData.forEach(summary => {
      const scrapDiff = calcDifference(
        summary.profitLoss?.investmentPlan?.scrapCost,
        summary.profitLoss?.recentActual?.scrapCost,
        summary.profitLoss?.theoretical?.scrapCost
      );
      const repairDiff = calcDifference(
        summary.profitLoss?.investmentPlan?.repairCost ?? undefined,
        summary.profitLoss?.recentActual?.repairCost ?? undefined,
        summary.profitLoss?.theoretical?.repairCost ?? undefined
      );
      const consumablesDiff = calcDifference(
        summary.profitLoss?.investmentPlan?.consumablesCost ?? undefined,
        summary.profitLoss?.recentActual?.consumablesCost ?? undefined,
        summary.profitLoss?.theoretical?.consumablesCost ?? undefined
      );

      if (scrapDiff !== undefined) totalScrapCostImprovement += scrapDiff;
      if (repairDiff !== undefined) totalRepairCostImprovement += repairDiff;
      if (consumablesDiff !== undefined) totalConsumablesCostImprovement += consumablesDiff;
    });

    const totalImprovement = totalScrapCostImprovement + totalRepairCostImprovement + totalConsumablesCostImprovement;

    rows.push([t("features.InvestmentScreening.ProjectProfitablityStatement.heading.improvementByUpdateTotal")]);
    rows.push([t("features.InvestmentScreening.ProjectProfitablityStatement.improvementItem.scrapCostShort"), formatValue(convertValue(totalScrapCostImprovement))]);
    rows.push([t("features.InvestmentScreening.ProjectProfitablityStatement.improvementItem.repairCost"), formatValue(convertValue(totalRepairCostImprovement))]);
    rows.push([t("features.InvestmentScreening.ProjectProfitablityStatement.improvementItem.consumablesCost"), formatValue(convertValue(totalConsumablesCostImprovement))]);
    rows.push([t("features.InvestmentScreening.ProjectProfitablityStatement.tableHeader.total"), formatValue(convertValue(totalImprovement))]);

    return rows;
  };

  // 合理化による改善効果（Excel出力用）
  const createExportDataForRationalizationImprovementEffect = (): (string | number)[][] => {
    // 項目名取得関数
    const getItemName = (item: RationalizationProfitLossItem): string => {
      if (item.itemType === "custom" && item.customItemName) {
        return item.customItemName;
      }
      const itemNameMap: Record<string, string> = {
        productionQuantity: t("features.InvestmentScreening.ProjectProfitablityStatement.improvementItem.productionQuantity"),
        salesQuantity: t("features.InvestmentScreening.ProjectProfitablityStatement.improvementItem.salesQuantity"),
        laborCost: t("features.InvestmentScreening.ProjectProfitablityStatement.improvementItem.laborCost"),
        repairCost: t("features.InvestmentScreening.ProjectProfitablityStatement.improvementItem.repairCost"),
        consumablesCost: t("features.InvestmentScreening.ProjectProfitablityStatement.improvementItem.consumablesCost"),
        maintenanceCost: t("features.InvestmentScreening.ProjectProfitablityStatement.improvementItem.maintenanceCost"),
        scrapCost: t("features.InvestmentScreening.ProjectProfitablityStatement.improvementItem.scrapCost"),
        totalCost: t("features.InvestmentScreening.ProjectProfitablityStatement.improvementItem.totalCost"),
        targetEquipmentDepreciation: t("features.InvestmentScreening.ProjectProfitablityStatement.improvementItem.targetEquipmentDepreciation"),
        otherDepreciation: t("features.InvestmentScreening.ProjectProfitablityStatement.improvementItem.otherDepreciation"),
      };
      return itemNameMap[item.itemType] || item.itemType;
    };

    // 月次データを年度ごとに集計する関数
    const aggregateMonthlyDataByYear = (
      monthlyData: Record<string, number>,
      fiscalYears: string[]
    ): number[] => {
      return fiscalYears.map((fiscalYear) => {
        const yearMatch = fiscalYear.match(/(\d{4})年/);
        if (!yearMatch) return 0;
        const endYear = parseInt(yearMatch[1], 10);
        let total = 0;
        for (let month = 4; month <= 12; month++) {
          const key = `${endYear - 1}-${String(month).padStart(2, "0")}`;
          total += monthlyData[key] || 0;
        }
        for (let month = 1; month <= 3; month++) {
          const key = `${endYear}-${String(month).padStart(2, "0")}`;
          total += monthlyData[key] || 0;
        }
        return total;
      });
    };

    // 工程名取得関数
    const getProcessName = (processId: string | undefined): string => {
      if (!processId) return "";
      const process = uniqueProcessData.find(p => p.uniqueProcessId === processId);
      return process?.uniqueProcessName || processId;
    };

    // 工程ごとの元通貨名取得関数
    const getProcessCurrencyName = (summary: RationalizationSummaryInputData): string => {
      if (summary.costCurrencyId) {
        const currency = currencyData.find(c => c.currencyId === summary.costCurrencyId);
        if (currency?.currencyName) {
          return currency.currencyName;
        }
      }
      return selectedCurrency.name;
    };

    const rows: (string | number)[][] = [];

    // ヘッダー行を作成
    const yearHeaders = fiscalYearsString.map((_, i) => `${i + 1}年目`);
    const yearLabels = [...fiscalYearsString];

    // ========================================
    // 合理化による改善効果（指標）セクション
    // ========================================
    rows.push(["合理化による改善効果（指標）"]);
    rows.push([t("features.InvestmentScreening.ProjectProfitablityStatement.label.unitPcs")]);
    rows.push([]);

    // 各工程ごとの指標データ
    rationalizationSummaryData
      .filter(summary => summary.indicatorItems && summary.indicatorItems.length > 0)
      .forEach((summary, summaryIndex) => {
        // 工程名
        rows.push([getProcessName(summary.processId)]);

        // ヘッダー行
        rows.push(["No.", "項目", "区分", ...yearHeaders, "合計"]);
        rows.push(["", "", "", ...yearLabels, ""]);

        // 各指標項目
        summary.indicatorItems!.forEach((item, index) => {
          const beforeByYear = aggregateMonthlyDataByYear(item.beforeMonthlyData, fiscalYearsString);
          const afterByYear = aggregateMonthlyDataByYear(item.afterMonthlyData, fiscalYearsString);
          const beforeTotal = beforeByYear.reduce((a, b) => a + b, 0);
          const afterTotal = afterByYear.reduce((a, b) => a + b, 0);

          // 合理化前の行
          rows.push([index + 1, getItemName(item), "合理化前", ...beforeByYear, beforeTotal]);
          // 合理化後の行
          rows.push(["", "", "合理化後", ...afterByYear, afterTotal]);
        });

        rows.push([]);
      });

    // ========================================
    // 合理化による改善効果（費用）セクション
    // ========================================
    rows.push([]);
    rows.push(["合理化による改善効果（費用）"]);
    rows.push([t("features.InvestmentScreening.ProjectProfitablityStatement.label.unitPrefixCurrency", { currency: selectedCurrency.name })]);
    rows.push([]);

    // 通貨変換関数
    const convertCostValueForExport = (value: number, sourceCurrencyName: string): number => {
      if (sourceCurrencyName === selectedCurrency.name) {
        return value;
      }
      const converted = convertWithRate(
        new Decimal(value),
        sourceCurrencyName,
        selectedCurrency.name
      );
      const decimalPlaces = hasDecimal(selectedCurrency.name) ? 2 : 0;
      return converted.toDecimalPlaces(decimalPlaces, Decimal.ROUND_HALF_UP).toNumber();
    };

    // 各工程ごとの費用データ
    rationalizationSummaryData
      .filter(summary => summary.costItems && summary.costItems.length > 0)
      .forEach((summary, summaryIndex) => {
        const sourceCurrencyName = getProcessCurrencyName(summary);

        // 工程名
        rows.push([getProcessName(summary.processId)]);

        // ヘッダー行
        rows.push(["No.", "項目", "区分", ...yearHeaders, "合計"]);
        rows.push(["", "", "", ...yearLabels, ""]);

        // 各費用項目
        summary.costItems!.forEach((item, index) => {
          const beforeByYear = aggregateMonthlyDataByYear(item.beforeMonthlyData, fiscalYearsString);
          const afterByYear = aggregateMonthlyDataByYear(item.afterMonthlyData, fiscalYearsString);
          // 通貨変換を適用
          const convertedBeforeByYear = beforeByYear.map(v => convertCostValueForExport(v, sourceCurrencyName));
          const convertedAfterByYear = afterByYear.map(v => convertCostValueForExport(v, sourceCurrencyName));
          const beforeTotal = convertedBeforeByYear.reduce((a, b) => a + b, 0);
          const afterTotal = convertedAfterByYear.reduce((a, b) => a + b, 0);

          // 合理化前の行
          rows.push([index + 1, getItemName(item), "合理化前", ...convertedBeforeByYear, beforeTotal]);
          // 合理化後の行
          rows.push(["", "", "合理化後", ...convertedAfterByYear, afterTotal]);
        });

        rows.push([]);
      });

    // ========================================
    // 改善効果の合計（指標）セクション
    // ========================================
    rows.push([]);
    rows.push(["改善効果の合計（指標）"]);
    rows.push([t("features.InvestmentScreening.ProjectProfitablityStatement.label.unitPcs")]);
    rows.push([]);

    // 全工程の指標項目を集約
    const allIndicatorItems: RationalizationProfitLossItem[] = [];
    rationalizationSummaryData.forEach(summary => {
      if (summary.indicatorItems) {
        summary.indicatorItems.forEach(item => {
          const existingIndex = allIndicatorItems.findIndex(existing =>
            item.itemType === "custom"
              ? existing.itemType === "custom" && existing.customItemName === item.customItemName
              : existing.itemType === item.itemType
          );
          if (existingIndex >= 0) {
            const existing = allIndicatorItems[existingIndex];
            Object.keys(item.beforeMonthlyData).forEach(key => {
              existing.beforeMonthlyData[key] = (existing.beforeMonthlyData[key] || 0) + (item.beforeMonthlyData[key] || 0);
            });
            Object.keys(item.afterMonthlyData).forEach(key => {
              existing.afterMonthlyData[key] = (existing.afterMonthlyData[key] || 0) + (item.afterMonthlyData[key] || 0);
            });
          } else {
            allIndicatorItems.push({
              ...item,
              beforeMonthlyData: { ...item.beforeMonthlyData },
              afterMonthlyData: { ...item.afterMonthlyData },
            });
          }
        });
      }
    });

    if (allIndicatorItems.length > 0) {
      // ヘッダー行
      rows.push(["No.", "項目", ...yearHeaders, "合計"]);
      rows.push(["", "", ...yearLabels, ""]);

      // 各項目の差異（合理化後 - 合理化前）
      allIndicatorItems.forEach((item, index) => {
        const beforeByYear = aggregateMonthlyDataByYear(item.beforeMonthlyData, fiscalYearsString);
        const afterByYear = aggregateMonthlyDataByYear(item.afterMonthlyData, fiscalYearsString);
        const differenceByYear = afterByYear.map((after, i) => after - beforeByYear[i]);
        const differenceTotal = differenceByYear.reduce((a, b) => a + b, 0);

        rows.push([index + 1, getItemName(item), ...differenceByYear, differenceTotal]);
      });
    } else {
      rows.push(["指標データがありません"]);
    }

    // ========================================
    // 改善効果の合計（費用）セクション
    // ========================================
    rows.push([]);
    rows.push(["改善効果の合計（費用）"]);
    rows.push([t("features.InvestmentScreening.ProjectProfitablityStatement.label.unitPrefixCurrency", { currency: selectedCurrency.name })]);
    rows.push([]);

    // 全工程の費用項目を集約（通貨変換を適用してから合計）
    const allCostItems: RationalizationProfitLossItem[] = [];
    rationalizationSummaryData.forEach(summary => {
      if (summary.costItems) {
        const sourceCurrencyName = getProcessCurrencyName(summary);
        summary.costItems.forEach(item => {
          // 通貨変換を適用した月次データを作成
          const convertedBeforeMonthlyData: Record<string, number> = {};
          const convertedAfterMonthlyData: Record<string, number> = {};
          Object.keys(item.beforeMonthlyData).forEach(key => {
            convertedBeforeMonthlyData[key] = convertCostValueForExport(item.beforeMonthlyData[key] || 0, sourceCurrencyName);
          });
          Object.keys(item.afterMonthlyData).forEach(key => {
            convertedAfterMonthlyData[key] = convertCostValueForExport(item.afterMonthlyData[key] || 0, sourceCurrencyName);
          });

          const existingIndex = allCostItems.findIndex(existing =>
            item.itemType === "custom"
              ? existing.itemType === "custom" && existing.customItemName === item.customItemName
              : existing.itemType === item.itemType
          );
          if (existingIndex >= 0) {
            const existing = allCostItems[existingIndex];
            Object.keys(convertedBeforeMonthlyData).forEach(key => {
              existing.beforeMonthlyData[key] = (existing.beforeMonthlyData[key] || 0) + convertedBeforeMonthlyData[key];
            });
            Object.keys(convertedAfterMonthlyData).forEach(key => {
              existing.afterMonthlyData[key] = (existing.afterMonthlyData[key] || 0) + convertedAfterMonthlyData[key];
            });
          } else {
            allCostItems.push({
              ...item,
              beforeMonthlyData: { ...convertedBeforeMonthlyData },
              afterMonthlyData: { ...convertedAfterMonthlyData },
            });
          }
        });
      }
    });

    if (allCostItems.length > 0) {
      // ヘッダー行
      rows.push(["No.", "項目", ...yearHeaders, "合計"]);
      rows.push(["", "", ...yearLabels, ""]);

      // 各項目の差異（合理化後 - 合理化前）
      const allDifferencesByYear: number[][] = [];
      allCostItems.forEach((item, index) => {
        const beforeByYear = aggregateMonthlyDataByYear(item.beforeMonthlyData, fiscalYearsString);
        const afterByYear = aggregateMonthlyDataByYear(item.afterMonthlyData, fiscalYearsString);
        const differenceByYear = afterByYear.map((after, i) => after - beforeByYear[i]);
        const differenceTotal = differenceByYear.reduce((a, b) => a + b, 0);
        allDifferencesByYear.push(differenceByYear);

        rows.push([index + 1, getItemName(item), ...differenceByYear, differenceTotal]);
      });

      // 合計（損益）行：各年度ごとに全項目の差異を集約
      const totalsByYear = fiscalYearsString.map((_, yearIndex) =>
        allDifferencesByYear.reduce((sum, diffs) => sum + (diffs[yearIndex] || 0), 0)
      );
      const grandTotal = totalsByYear.reduce((a, b) => a + b, 0);
      rows.push([
        allCostItems.length + 1,
        t("features.InvestmentScreening.ProjectProfitablityStatement.tableHeader.totalProfit"),
        ...totalsByYear,
        grandTotal,
      ]);

      rows.push([]);
      rows.push([t("features.InvestmentScreening.ProjectProfitablityStatement.message.costImprovementNote")]);
    } else {
      rows.push(["費用データがありません"]);
    }

    return rows;
  };

  // {t("features.InvestmentScreening.ProjectProfitablityStatement.heading.paybackPeriod")}
  const createExportDataForPaybackPeriod = (): (string | number)[][] => {
    // Excel出力用：JPY版のdisplay系stateを使用（二重変換を回避）

    // 各データの取得（display系stateを使用 - 入力通貨→表示通貨で直接変換済み）
    // 設備投資額
    const capexAmountValue = fiscalYearsString.map((_, index) =>
      (displayCapexAmountByYear[index] || new Decimal(0)).toNumber()
    );
    // 営業利益
    const profitValue = fiscalYearsString.map((_, index) =>
      (displayProfitByYear[index] || new Decimal(0)).toNumber()
    );
    // 金利（JPY版を使用）
    const interestRate = fiscalYearsString.map((_, index) =>
      (dispInterestRateByYear[index] || new Decimal(0)).toNumber()
    );
    // 投資対象設備の減価償却費
    const equipmentDepreciationValue = fiscalYearsString.map((_, index) =>
      (displayEquipmentDepreciationByYear[index] || new Decimal(0)).toNumber()
    );
    // {t("features.InvestmentScreening.ProjectProfitablityStatement.heading.paybackPeriod")}（合計列に表示）（通貨に依存しない）
    const recoveryPeriod = fiscalYearsString.map(() => "");

    // 合計値の計算
    const capexAmountTotal = capexAmountValue.reduce((sum, v) => sum + v, 0);
    const profitTotal = profitValue.reduce((sum, v) => sum + v, 0);
    const interestRateTotal = interestRate.reduce((sum, v) => sum + v, 0);
    const equipmentDepreciationTotal = equipmentDepreciationValue.reduce((sum, v) => sum + v, 0);

    const yearsRow = [t("features.InvestmentScreening.ProjectProfitablityStatement.heading.paybackPeriod"), ...fiscalYearsString.map((year) => year), t("features.InvestmentScreening.ProjectProfitablityStatement.tableHeader.total") ?? "合計"];

    const rows = [
      [PaybackPeriodItems.capexAmount.name, ...capexAmountValue, capexAmountTotal],
      [PaybackPeriodItems.operatingProfit.name, ...profitValue, profitTotal],
      [PaybackPeriodItems.interestRate.name, ...interestRate, interestRateTotal],
      [PaybackPeriodItems.equipmentDepreciation.name, ...equipmentDepreciationValue, equipmentDepreciationTotal],
      [PaybackPeriodItems.paybackPeriod.name, ...recoveryPeriod, paybackPeriod],
    ];

    return [
      [t("features.InvestmentScreening.ProjectProfitablityStatement.label.unitPrefixCurrency", { currency: selectedCurrency.name })],
      [],
      yearsRow,
      ...rows
    ]
  }

  // 回収シミュレーション
  const createExportDataForRecoveredSimulation = (): (string | number)[][] => {
    // Excel出力用：JPY版のdisplay系stateを使用（二重変換を回避）

    // 各データの取得（display系stateを使用 - 入力通貨→表示通貨で直接変換済み）
    // 設備投資額
    const capexAmountValue = fiscalYearsString.map((_, index) =>
      (displayCapexAmountByYear[index] || new Decimal(0)).toNumber()
    );
    // 回収原資
    const recoveredResourcesValue = fiscalYearsString.map((_, index) =>
      (displayRecoveredResourcesByYear[index] || new Decimal(0)).toNumber()
    );
    // 営業利益
    const profitValue = fiscalYearsString.map((_, index) =>
      (displayProfitByYear[index] || new Decimal(0)).toNumber()
    );
    // 投資対象設備の減価償却費
    const equipmentDepreciationValue = fiscalYearsString.map((_, index) =>
      (displayEquipmentDepreciationByYear[index] || new Decimal(0)).toNumber()
    );
    // 未回収額
    const uncollectedAmountValue = fiscalYearsString.map((_, index) =>
      (displayUncollectedAmountByYear[index] || new Decimal(0)).toNumber()
    );
    // 回収時点（合計列に表示）（通貨に依存しない）
    const recoveryPeriod = fiscalYearsString.map(() => "");

    // 合計値の計算
    const capexAmountTotal = capexAmountValue.reduce((sum, v) => sum + v, 0);
    const recoveredResourcesTotal = recoveredResourcesValue.reduce((sum, v) => sum + v, 0);
    const profitTotal = profitValue.reduce((sum, v) => sum + v, 0);
    const equipmentDepreciationTotal = equipmentDepreciationValue.reduce((sum, v) => sum + v, 0);

    const yearsRow = [t("features.InvestmentScreening.ProjectProfitablityStatement.heading.incrementalCashFlow"), ...fiscalYearsString.map((year) => year), t("features.InvestmentScreening.ProjectProfitablityStatement.tableHeader.total") ?? "合計"];

    const rows = [
      [RecoverySimulationItems.capexAmount.name, ...capexAmountValue, capexAmountTotal],
      [RecoverySimulationItems.recoveredResources.name, ...recoveredResourcesValue, recoveredResourcesTotal],
      [RecoverySimulationItems.operatingProfit.name, ...profitValue, profitTotal],
      [RecoverySimulationItems.equipmentDepreciation.name, ...equipmentDepreciationValue, equipmentDepreciationTotal],
      [RecoverySimulationItems.uncollectedAmount.name, ...uncollectedAmountValue, "-"],
      [RecoverySimulationItems.collectionPeriod.name, ...recoveryPeriod, collectionPeriod],
    ];

    return [
      [t("features.InvestmentScreening.ProjectProfitablityStatement.label.unitPrefixCurrency", { currency: selectedCurrency.name })],
      [],
      yearsRow,
      ...rows
    ]
  }

  // {t("features.InvestmentScreening.ProjectProfitablityStatement.heading.dcf")}
  const createExportDataForDCF = (): (string | number)[][] => {
    // Excel出力用：JPY版のdisplay系stateを使用（二重変換を回避）

    // 元データから0年目を含む配列を作成（display系stateを使用）
    // fiscalYearsString の長さに合わせてスライス（余分な年度データを除去）
    const freeCashFlowForExport = [new Decimal(0), ...displayFreeCashFlowByYear.slice(0, fiscalYearsString.length)];
    const profitForExport = [new Decimal(0), ...displayProfitByYear.slice(0, fiscalYearsString.length)];
    const taxRateForExport = [new Decimal(0), ...taxRateByYear.slice(0, fiscalYearsString.length)]; // 比率なので変換不要
    const depreciationForExport = [new Decimal(0), ...displayDepreciationByYear.slice(0, fiscalYearsString.length)];
    const capexAmountForExport = [new Decimal(0), ...displayCapexAmountByYear.slice(0, fiscalYearsString.length)];
    const discountRateForExport = [new Decimal(0), ...discountRateByYear.slice(0, fiscalYearsString.length)]; // 比率なので変換不要
    const presentValueForExport = [new Decimal(0), ...displayPresentValueByYear.slice(0, fiscalYearsString.length)];

    // フリーキャッシュフロー
    const freeCashFlow = freeCashFlowForExport.map(d =>
      (d || new Decimal(0)).toNumber()
    );
    // 営業利益
    const profit = profitForExport.map(d =>
      (d || new Decimal(0)).toNumber()
    );
    // 目標税率（実効税率）（通貨に依存しない比率）
    const taxRate = taxRateForExport.map(d => (d || new Decimal(0)).toNumber());
    // 減価償却費
    const depreciation = depreciationForExport.map(d =>
      (d || new Decimal(0)).toNumber()
    );
    // 設備投資額
    const capexAmount = capexAmountForExport.map(d =>
      (d || new Decimal(0)).toNumber()
    );
    // 加重平均資本コスト（通貨に依存しない比率）
    const discountRate = discountRateForExport.map(d => (d || new Decimal(0)).toNumber());
    // 現在価値
    const presentValue = presentValueForExport.map(d =>
      (d || new Decimal(0)).toNumber()
    );
    // 正味現在価値（合計列に表示）
    const netPresent = freeCashFlow.map(() => "");

    // 0年目の年度を計算（初年度-1年）
    // ロケール依存のラベル文字列（"XXXX年3月期" / "FYXXXX" 等）から正規表現で
    // 年を抽出すると、ロケールによっては一致せずハードコードされた
    // 日本語ラベルにフォールバックしてしまうため、ラベルなしの数値配列から
    // 直接年度を取得する
    const rawFiscalYears = getFiscalYearsString(
      capexProjectData?.projectStartDate || dayjs().format("YYYY-MM-DD"),
      1,
      false
    );
    let zeroYearLabel = t("common.dateFormat.fiscalYear", { year: 0 });
    if (rawFiscalYears.length > 0) {
      const firstYear = parseInt(rawFiscalYears[0], 10);
      if (!isNaN(firstYear)) {
        const year = firstYear - 1;
        zeroYearLabel = t("common.dateFormat.fiscalYear", { year });
      }
    }

    // 合計値の計算
    const freeCashFlowTotal = freeCashFlow.reduce((sum, v) => sum + v, 0);
    const profitTotal = profit.reduce((sum, v) => sum + v, 0);
    const depreciationTotal = depreciation.reduce((sum, v) => sum + v, 0);
    const capexAmountTotal = capexAmount.reduce((sum, v) => sum + v, 0);
    const presentValueTotal = presentValue.reduce((sum, v) => sum + v, 0);

    // 0年目を含めた年度ヘッダー
    const yearsRow = [t("features.InvestmentScreening.ProjectProfitablityStatement.heading.dcf"), zeroYearLabel, ...fiscalYearsString.map((year) => year), t("features.InvestmentScreening.ProjectProfitablityStatement.tableHeader.total") ?? "合計"];

    const rows = [
      [DCFItems.freeCashFlow.name, ...freeCashFlow, freeCashFlowTotal],
      [DCFItems.operatingProfit.name, ...profit, profitTotal],
      [`${DCFItems.taxRate.name}(%)`, ...taxRate, "-"],
      [DCFItems.depreciation.name, ...depreciation, depreciationTotal],
      [DCFItems.capexAmount.name, ...capexAmount, capexAmountTotal],
      [`${DCFItems.wacc.name}(%)`, ...discountRate, "-"],
      [DCFItems.presentValue.name, ...presentValue, presentValueTotal],
      [DCFItems.netPresentValue.name, ...netPresent, displayNetPresentValue.toNumber()],
    ];

    return [
      [t("features.InvestmentScreening.ProjectProfitablityStatement.label.unitPrefixCurrency", { currency: selectedCurrency.name })],
      [],
      yearsRow,
      ...rows
    ]

  }

  // 列幅の計算
  const getAutoColWidths = (data: (string | number)[][]): XLSX.ColInfo[] => {
    const colWidths: number[] = [];

    for (const row of data) {
      row.forEach((cell, i) => {
        const cellValue = String(cell ?? "");
        const cellLength = [...cellValue].reduce(
          (sum, ch) => sum + (ch.charCodeAt(0) > 255 ? 2 : 1),
          0
        );

        colWidths[i] = Math.max(colWidths[i] || 10, cellLength);
      });
    }

    return colWidths.map(w => ({ wch: w + 2 }));
  }

  // 日付ページの作成
  const createDate = () => {
    const d = new Date(Date.now() + 9 * 60 * 60 * 1000);
    const f = (n: number) => String(n).padStart(2, "0");

    const today = `${d.getUTCFullYear()}-${f(d.getUTCMonth() + 1)}-${f(d.getUTCDate())} ` +
      `${f(d.getUTCHours())}:${f(d.getUTCMinutes())}:${f(d.getUTCSeconds())}`;
    return [[today]]
  }

  /**
   * Excel作成
   */
  const exportCapexToExcelPerPartNo = async () => {
    try {
      const workbook = XLSX.utils.book_new();

      // 日付ページを１シート追加
      const downloadTimeData = createDate();
      const downloadTimeSheet = XLSX.utils.aoa_to_sheet(downloadTimeData);
      XLSX.utils.book_append_sheet(workbook, downloadTimeSheet, "downloadTIme");

      // 各PartNoごとに通常データのシートを追加（簡易モード時は画面でも非表示のためスキップ）
      if (!isSimpleMode) {
        for (const { partNo, displayPartNo } of capexPartNoData) {
          const data = createExportDataForPartNo(partNo!, displayPartNo!);
          const sheet = XLSX.utils.aoa_to_sheet(data);

          // 自動列幅を計算してセット
          sheet["!cols"] = getAutoColWidths(data);

          const sheetName = resolveDisplayPartNo({ partNo, displayPartNo })?.substring(0, 31);
          XLSX.utils.book_append_sheet(workbook, sheet, sheetName);
        }
      }

      // 損益試算データを全体で1シート追加（更新投資の場合は更新前/更新後比較）
      if (capexProjectData?.purpose === PurposeType.UpdateAndOther.code) {
        // 更新投資の場合：損益試算は詳細モード時のみ出力（簡易モード時は合理化と同様にスキップ）
        if (!isSimpleMode) {
          const profitLossData = createExportDataForUpdateProfitLossComparison();
          const profitLossSheet = XLSX.utils.aoa_to_sheet(profitLossData);
          profitLossSheet["!cols"] = getAutoColWidths(profitLossData);
          XLSX.utils.book_append_sheet(workbook, profitLossSheet, t("features.InvestmentScreening.ProjectProfitablityStatement.heading.profitLossSheet"));
        }

        // 更新による改善効果シートを追加（簡易モード時も出力）
        if (updateSummaryData.length > 0) {
          const improvementEffectData = createExportDataForImprovementEffect();
          const improvementEffectSheet = XLSX.utils.aoa_to_sheet(improvementEffectData);
          improvementEffectSheet["!cols"] = getAutoColWidths(improvementEffectData);
          XLSX.utils.book_append_sheet(workbook, improvementEffectSheet, t("features.InvestmentScreening.ProjectProfitablityStatement.heading.improvementByUpdate"));
        }
      } else if (capexProjectData?.purpose === PurposeType.Rationalization.code) {
        // 合理化投資の場合：合理化前/合理化後を別シートで出力（簡易モード時は画面でも非表示のためスキップ）
        if (!isSimpleMode) {
          // 損益試算（合理化前）シート
          const beforeProfitLossData = createExportDataForRationalizationBeforeProfitLoss();
          const beforeProfitLossSheet = XLSX.utils.aoa_to_sheet(beforeProfitLossData);
          beforeProfitLossSheet["!cols"] = getAutoColWidths(beforeProfitLossData);
          XLSX.utils.book_append_sheet(workbook, beforeProfitLossSheet, t("features.InvestmentScreening.ProjectProfitablityStatement.heading.profitLossSheetRationalizationBefore"));

          // 損益試算（合理化後）シート
          const afterProfitLossData = createExportDataForProfitandLoss();
          const afterProfitLossSheet = XLSX.utils.aoa_to_sheet(afterProfitLossData);
          afterProfitLossSheet["!cols"] = getAutoColWidths(afterProfitLossData);
          XLSX.utils.book_append_sheet(workbook, afterProfitLossSheet, t("features.InvestmentScreening.ProjectProfitablityStatement.heading.profitLossSheetRationalizationAfter"));
        }
      } else {
        // その他の場合：従来の損益試算データを出力
        const profitLossData = createExportDataForProfitandLoss();
        const profitLossSheet = XLSX.utils.aoa_to_sheet(profitLossData);
        profitLossSheet["!cols"] = getAutoColWidths(profitLossData);
        XLSX.utils.book_append_sheet(workbook, profitLossSheet, t("features.InvestmentScreening.ProjectProfitablityStatement.heading.profitLossSheet"));
      }

      // 合理化による改善効果シートを追加（簡易モード時、月次データ無くても出力する保険）
      if (isSimpleMode
        && capexProjectData?.purpose === PurposeType.Rationalization.code
        && rationalizationSummaryData.length > 0
        && !monthlyProfitAndLossData
      ) {
        const improvementEffectData = createExportDataForRationalizationImprovementEffect();
        const improvementEffectSheet = XLSX.utils.aoa_to_sheet(improvementEffectData);
        improvementEffectSheet["!cols"] = getAutoColWidths(improvementEffectData);
        XLSX.utils.book_append_sheet(workbook, improvementEffectSheet, t("features.InvestmentScreening.ProjectProfitablityStatement.heading.improvementByRationalization"));
      }

      // 月次損益試算データを1シート追加
      if (monthlyProfitAndLossData) {
        // 更新投資の場合：詳細モード時のみ損益試算（月次）を出力（簡易モード時は合理化と同様にスキップ）
        if (capexProjectData?.purpose === PurposeType.UpdateAndOther.code) {
          if (!isSimpleMode && beforeUpdateMonthlyProfitAndLossData) {
            const monthlyProfitLossData = createExportDataForUpdateMonthlyProfitLossComparison();
            const monthlyProfitLossSheet = XLSX.utils.aoa_to_sheet(monthlyProfitLossData);
            monthlyProfitLossSheet["!cols"] = getAutoColWidths(monthlyProfitLossData);
            XLSX.utils.book_append_sheet(workbook, monthlyProfitLossSheet, t("features.InvestmentScreening.ProjectProfitablityStatement.heading.monthlyProfitLossSheet"));
          }
        } else if (capexProjectData?.purpose === PurposeType.Rationalization.code && beforeRationalizationMonthlyProfitAndLossData) {
          // 合理化投資の場合は合理化前/合理化後を別シートで出力（簡易モード時は画面でも非表示のためスキップ）
          if (!isSimpleMode) {
            // 損益試算（月次・合理化前）シート
            const beforeMonthlyProfitLossData = createExportDataForRationalizationBeforeMonthlyProfitLoss();
            const beforeMonthlyProfitLossSheet = XLSX.utils.aoa_to_sheet(beforeMonthlyProfitLossData);
            beforeMonthlyProfitLossSheet["!cols"] = getAutoColWidths(beforeMonthlyProfitLossData);
            XLSX.utils.book_append_sheet(workbook, beforeMonthlyProfitLossSheet, t("features.InvestmentScreening.ProjectProfitablityStatement.heading.monthlyProfitLossSheetRationalizationBefore"));

            // 損益試算（月次・合理化後）シート
            const afterMonthlyProfitLossData = createExportDataForMonthlyProfitAndLoss();
            const afterMonthlyProfitLossSheet = XLSX.utils.aoa_to_sheet(afterMonthlyProfitLossData);
            afterMonthlyProfitLossSheet["!cols"] = getAutoColWidths(afterMonthlyProfitLossData);
            XLSX.utils.book_append_sheet(workbook, afterMonthlyProfitLossSheet, t("features.InvestmentScreening.ProjectProfitablityStatement.heading.monthlyProfitLossSheetRationalizationAfter"));
          }

          // 合理化による改善効果シートを追加（簡易モード時も出力）
          if (rationalizationSummaryData.length > 0) {
            const improvementEffectData = createExportDataForRationalizationImprovementEffect();
            const improvementEffectSheet = XLSX.utils.aoa_to_sheet(improvementEffectData);
            improvementEffectSheet["!cols"] = getAutoColWidths(improvementEffectData);
            XLSX.utils.book_append_sheet(workbook, improvementEffectSheet, t("features.InvestmentScreening.ProjectProfitablityStatement.heading.improvementByRationalization"));
          }
        } else {
          const monthlyProfitLossData = createExportDataForMonthlyProfitAndLoss();
          const monthlyProfitLossSheet = XLSX.utils.aoa_to_sheet(monthlyProfitLossData);
          monthlyProfitLossSheet["!cols"] = getAutoColWidths(monthlyProfitLossData);
          XLSX.utils.book_append_sheet(workbook, monthlyProfitLossSheet, t("features.InvestmentScreening.ProjectProfitablityStatement.heading.monthlyProfitLossSheet"));
        }
      }

      // {t("features.InvestmentScreening.ProjectProfitablityStatement.heading.paybackPeriod")}データを全体で1シート追加
      const paybackPeriodData = createExportDataForPaybackPeriod();
      const paybackPeriodSheet = XLSX.utils.aoa_to_sheet(paybackPeriodData);
      paybackPeriodSheet["!cols"] = getAutoColWidths(paybackPeriodData);
      XLSX.utils.book_append_sheet(workbook, paybackPeriodSheet, t("features.InvestmentScreening.ProjectProfitablityStatement.heading.paybackPeriod"));

      // {t("features.InvestmentScreening.ProjectProfitablityStatement.heading.incrementalCashFlow")}データを全体で1シート追加
      const RecoveredSimulationData = createExportDataForRecoveredSimulation();
      const RecoveredSimulationSheet = XLSX.utils.aoa_to_sheet(RecoveredSimulationData);
      RecoveredSimulationSheet["!cols"] = getAutoColWidths(RecoveredSimulationData);
      XLSX.utils.book_append_sheet(workbook, RecoveredSimulationSheet, t("features.InvestmentScreening.ProjectProfitablityStatement.heading.incrementalCashFlow"));


      // {t("features.InvestmentScreening.ProjectProfitablityStatement.heading.dcf")}データを全体で1シート追加
      const DCFData = createExportDataForDCF();
      const DCFSheet = XLSX.utils.aoa_to_sheet(DCFData);
      DCFSheet["!cols"] = getAutoColWidths(DCFData);
      XLSX.utils.book_append_sheet(workbook, DCFSheet, t("features.InvestmentScreening.ProjectProfitablityStatement.heading.dcf"));

      // ファイル名の日付を設定
      const today = new Date();
      const formatted = today.toISOString().split("T")[0].replace(/-/g, "")
      // ファイルとして保存
      XLSX.writeFile(workbook, `${t('features.InvestmentScreening.ProjectProfitablityStatement.excel.fileNamePrefix')}${projectId}_${formatted}.xlsx`);

      toast({
        title: TITLE,
        description: t("features.InvestmentScreening.ProjectProfitablityStatement.message.excelExportSuccess"),
        status: "success",
        duration: 5000,
        isClosable: true,
      });
    } catch (error) {
      toast({
        title: TITLE,
        description: t("features.InvestmentScreening.ProjectProfitablityStatement.message.excelExportFailed"),
        status: "error",
        duration: 5000,
        isClosable: true,
      })
      console.error(`ERROR:`, error);
    }
  };

  // 通貨リスト(ローカル通貨、JPY設定)
  const currencyOptions = currencyData.filter(
    (currencyItem) =>
      currencyItem.currencyId === localCurrencyId ||
      currencyItem.currencyName === CurrencyNameJPY
  )
    .map((currencyItem) => ({
      value: currencyItem.currencyId,
      label: currencyItem.currencyName,
    }));

  return (
    <Box
      position="fixed"
      top="10vh"
      ml="2vw"
      mr="2vw"
      left="250px"
      width="calc(96vw - 250px)"
      height="85vh"
      p={6}
      overflowY="auto"
      bg="white"
    >
      <Heading size="md" mb={4}>
        {TITLE}
        {reviewDate && (
          <Text as="span" ml={4} fontSize="sm" color="white" bg="green.500" px={3} py={1} borderRadius="md">
            {t('features.InvestmentScreening.ProjectProfitablityStatement.label.reviewModeActive', { count: monthlyRateMap?.size || 0 })}
          </Text>
        )}
      </Heading>

      {/* プロジェクト開始日 */}
      <Box>
        <Text fontWeight="bold" mb={2}>
          {t("features.InvestmentScreening.ProjectProfitablityStatement.label.projectStartDate")}
        </Text>
        <Text>{formatDateByLocale(capexProjectData?.projectStartDate, false, true, "-") || formatDateByLocale(dayjs().toDate(), false, true, "-")}</Text>
      </Box>

      {/* 損益非表示モードの案内 */}
      {isProfitLossHiddenMode && (
        <Box
          mx="auto"
          mt={4}
          borderWidth="1px"
          borderRadius="md"
          boxShadow="md"
          p={6}
          mb={6}
          bg="gray.50"
        >
          <Text color="gray.600" fontSize="md" textAlign="center">
            {t('common.message.profitLossHiddenNote')}
          </Text>
        </Box>
      )}

      {/* 表示通貨選択（損益非表示モード時は非表示） */}
      {!isProfitLossHiddenMode && (
        <Box mt={4}>
          <Flex align="center" mb={2}>
            <Text alignItems="center" mr={4}>{t("features.InvestmentScreening.ProjectProfitablityStatement.label.displayCurrency")}</Text>
            <Select
              value={selectedCurrency.id}
              onChange={(e) => {
                const selectedOption = currencyOptions.find(option => option.value === e.target.value);
                if (selectedOption && selectedOption.value && selectedOption.label) {
                  setSelectedCurrency({ id: selectedOption.value, name: selectedOption.label });
                }
              }}
              width="250px"
            >
              {currencyOptions.map((option) => (
                <option key={option.value} value={option.value}>
                  {option.label}
                </option>
              ))}
            </Select>
          </Flex>
        </Box>
      )}

      {!isProfitLossHiddenMode && isCalculation && (

        <Stack spacing={4}>

          {/* PartNo別総計（簡易モード時は非表示） */}
          {!isSimpleMode && capexPartNoData.map(({ partNo, displayPartNo }) => {
            // 数量配列の取得
            const partQuantitiesData = quantityByPartNo.find(item => item.partNo === partNo);
            let quantities: Decimal[] = partQuantitiesData ? partQuantitiesData.quantities : [];
            const curNumberOfYear: number = quantities.length; // PartNoの年数を取得 材料単価配列の取得で使用
            quantities = adjustArrayLength(quantities, totalFiscalYearsByProject, new Decimal(0));

            // 単価配列の取得
            const partPricesData = displayPriceByPartNo.find(item => item.partNo === partNo);
            let prices: Decimal[] = partPricesData ? partPricesData.prices : [];
            prices = adjustArrayLength(prices, totalFiscalYearsByProject, new Decimal(0));
            // 売上金額配列の取得
            const partTotalSalesData = displayProductSalesByPartNo.find(item => item.partNo === partNo);
            let totalSales: Decimal[] = partTotalSalesData ? partTotalSalesData.totalSales : [];
            totalSales = adjustArrayLength(totalSales, totalFiscalYearsByProject, new Decimal(0));
            // PartNo金型配列の取得
            const partMoldsData = displayMoldSalesByPartNo.find(item => item.partNo === partNo);
            let moldPrices: Decimal[] = partMoldsData ? partMoldsData.totalSales : [];
            moldPrices = adjustArrayLength(moldPrices, totalFiscalYearsByProject, new Decimal(0));
            // PartNo開発費配列の取得
            const partDevsData = displayDevSalesByPartNo.find(item => item.partNo === partNo);
            let devPrices: Decimal[] = partDevsData ? partDevsData.totalSales : [];
            devPrices = adjustArrayLength(devPrices, totalFiscalYearsByProject, new Decimal(0));
            // PartNo製品売上高、金型、開発費合計
            const partMergesData = displayTotalSalesByPartNo.find(item => item.partNo === partNo);
            let mergePrices: Decimal[] = partMergesData ? partMergesData.totalSales : [];
            mergePrices = adjustArrayLength(mergePrices, totalFiscalYearsByProject, new Decimal(0));
            // 材料単価配列の取得（年度別加重平均）
            const partMaterialCostData = displayMaterialCostByPartNo.find(item => item.partNo === partNo);
            let materialCosts: Decimal[] = partMaterialCostData ? [...partMaterialCostData.materialCosts] : [];
            materialCosts = adjustArrayLength(materialCosts, totalFiscalYearsByProject, new Decimal(0));
            // 材料費配列の取得
            const partTotalMaterialCostsData = displayTotalMaterialCostByPartNo.find(item => item.partNo === partNo);
            let totalMaterialCosts: Decimal[] = partTotalMaterialCostsData ? partTotalMaterialCostsData.totalMaterialCosts : [];
            totalMaterialCosts = adjustArrayLength(totalMaterialCosts, totalFiscalYearsByProject, new Decimal(0));
            // スループット額配列の取得
            const partThroughputsData = displayThroughputByPartNo.find(item => item.partNo === partNo);
            let throughputs: Decimal[] = partThroughputsData ? partThroughputsData.throughputs : [];
            throughputs = adjustArrayLength(throughputs, totalFiscalYearsByProject, new Decimal(0));
            // スループット率
            const partThroughputRateData = throughputRateByPartNo.find(item => item.partNo === partNo);
            let throughputRate: Decimal[] = partThroughputRateData ? partThroughputRateData.throughputRate : [];
            throughputRate = adjustArrayLength(throughputRate, totalFiscalYearsByProject, new Decimal(0));
            return (
              <Box mt={2}>
                <Text fontSize="lg" fontWeight="bold" textAlign="center">
                  {resolveDisplayPartNo({ partNo, displayPartNo })}
                </Text>
                <Text>
                  unit: {selectedCurrency.name}
                </Text>
                <Box display="flex">
                  <Box flex="0 0 auto">
                    <Table size="sm" variant="simple" sx={{ tableLayout: "auto" }} className="mm-ceas-table">
                      <Thead>
                        <Tr><Th height="50px" whiteSpace="nowrap">{t("features.InvestmentScreening.ProjectProfitablityStatement.tableHeader.item")}</Th></Tr>
                      </Thead>
                      <Tbody>
                        <Tr><Td height="20px" whiteSpace="nowrap">{labelQuantity}</Td></Tr>
                        <Tr><Td height="20px" whiteSpace="nowrap" className="border-bottom-bold">{labelPrice}{t("features.InvestmentScreening.ProjectProfitablityStatement.tableHeader.weightedAverageSuffix")}</Td></Tr>
                        <Tr><Td height="20px" whiteSpace="nowrap">{t("features.InvestmentScreening.ProjectProfitablityStatement.tableHeader.productSales")}</Td></Tr>
                        <Tr><Td height="20px" whiteSpace="nowrap">{t("features.InvestmentScreening.ProjectProfitablityStatement.tableHeader.moldSales")}</Td></Tr>
                        <Tr><Td height="20px" whiteSpace="nowrap" className="border-bottom-bold">{t("features.InvestmentScreening.ProjectProfitablityStatement.tableHeader.devSales")}</Td></Tr>
                        <Tr><Td height="20px" whiteSpace="nowrap">{t("features.InvestmentScreening.ProjectProfitablityStatement.tableHeader.totalSales")}</Td></Tr>
                        <Tr><Td height="20px" whiteSpace="nowrap">{t("features.InvestmentScreening.ProjectProfitablityStatement.tableHeader.materialCostWeightedAverage")}</Td></Tr>
                        <Tr><Td height="20px" whiteSpace="nowrap" className="border-bottom-bold">{t("features.InvestmentScreening.ProjectProfitablityStatement.tableHeader.materialCost")}</Td></Tr>
                        <Tr><Td height="20px" whiteSpace="nowrap">{t("features.InvestmentScreening.ProjectProfitablityStatement.tableHeader.throughputAmount")}</Td></Tr>
                        <Tr><Td height="20px" whiteSpace="nowrap">{t("features.InvestmentScreening.ProjectProfitablityStatement.tableHeader.throughputRate")}</Td></Tr>
                      </Tbody>
                    </Table>
                  </Box>
                  <Box flex={1} overflowX="auto">
                    <Table {...defaultTableStyle} className="mm-ceas-table">
                      <Thead>
                        <Tr>
                          {fiscalYearsString.map((_, index) => (
                            <Th style={defaultThStyle}>
                              {t("features.InvestmentScreening.ProjectProfitablityStatement.tableHeader.yearSuffix", { year: index + 1 })}
                            </Th>
                          ))}
                          <Th style={{ ...defaultThStyle }} rowSpan={2}>
                            {t("features.InvestmentScreening.ProjectProfitablityStatement.tableHeader.total")}
                          </Th>
                        </Tr>
                        <Tr>
                          {fiscalYearsString.map((year) => (
                            <Th style={defaultThStyle}>
                              {year}
                            </Th>
                          ))}
                        </Tr>
                      </Thead>
                      <Tbody>
                        <Tr>
                          {quantities.map((quantity) => (
                            <Td style={defaultTdStyle}>
                              {formatNumber(quantity, "")}
                            </Td>
                          ))}
                          <Td style={defaultTdStyle}>
                            {formatNumber(quantities.reduce((sum, value) => sum.plus(value), new Decimal(0)), "")}
                          </Td>
                        </Tr>
                        <Tr>
                          {prices.map((price) => (
                            <Td style={defaultTdStyle} className="border-bottom-bold">
                              {formatNumber(price, "WithDecimal", 4)}
                            </Td>
                          ))}
                          <Td style={defaultTdStyle} className="border-bottom-bold">
                            {"-"}
                          </Td>
                        </Tr>
                        <Tr>
                          {totalSales.map((totalSales) => (
                            <Td style={defaultTdStyle}>
                              {formatNumber(totalSales, selectedCurrency.name)}
                            </Td>
                          ))}
                          <Td style={defaultTdStyle}>
                            {formatNumber(totalSales.reduce((sum, value) => sum.plus(value), new Decimal(0)), selectedCurrency.name)}
                          </Td>
                        </Tr>
                        <Tr>
                          {moldPrices.map((moldPrice) => (
                            <Td style={defaultTdStyle}>
                              {formatNumber(moldPrice, selectedCurrency.name)}
                            </Td>
                          ))}
                          <Td style={defaultTdStyle}>
                            {formatNumber(moldPrices.reduce((sum, value) => sum.plus(value), new Decimal(0)), selectedCurrency.name)}
                          </Td>
                        </Tr>
                        <Tr>
                          {devPrices.map((devPrice) => (
                            <Td style={defaultTdStyle} className="border-bottom-bold">
                              {formatNumber(devPrice, selectedCurrency.name)}
                            </Td>
                          ))}
                          <Td style={defaultTdStyle} className="border-bottom-bold">
                            {formatNumber(devPrices.reduce((sum, value) => sum.plus(value), new Decimal(0)), selectedCurrency.name)}
                          </Td>
                        </Tr>
                        <Tr>
                          {mergePrices.map((price) => (
                            <Td style={defaultTdStyle}>
                              {formatNumber(price, selectedCurrency.name)}
                            </Td>
                          ))}
                          <Td style={defaultTdStyle}>
                            {formatNumber(mergePrices.reduce((sum, value) => sum.plus(value), new Decimal(0)), selectedCurrency.name)}
                          </Td>
                        </Tr>
                        <Tr>
                          {materialCosts.map((materialCost) => (
                            <Td style={defaultTdStyle}>
                              {formatNumber(materialCost, "WithDecimal", 4)}
                            </Td>
                          ))}
                          <Td style={defaultTdStyle}>
                            {"-"}
                          </Td>
                        </Tr>
                        <Tr>
                          {totalMaterialCosts.map((totalMaterialCost) => (
                            <Td style={defaultTdStyle} className="border-bottom-bold">
                              {formatCostSum(totalMaterialCost, selectedCurrency.name, 2)}
                            </Td>
                          ))}
                          <Td style={defaultTdStyle} className="border-bottom-bold">
                            {formatCostSum(totalMaterialCosts.reduce((sum, value) => sum.plus(value), new Decimal(0)), selectedCurrency.name, 2)}
                          </Td>
                        </Tr>
                        <Tr>
                          {throughputs.map((throughput) => (
                            <Td style={defaultTdStyle}>
                              {formatNumber(throughput, selectedCurrency.name)}
                            </Td>
                          ))}
                          <Td style={defaultTdStyle}>
                            {formatNumber(throughputs.reduce((sum, value) => sum.plus(value), new Decimal(0)), selectedCurrency.name)}
                          </Td>
                        </Tr>
                        <Tr>
                          {throughputRate.map((throughputRate) => (
                            <Td style={defaultTdStyle}>
                              {throughputRate?.toFixed(1)?.replace(/\B(?=(\d{3})+(?!\d))/g, ',') + "%"}
                            </Td>
                          ))}
                          <Td style={defaultTdStyle}>
                            {"-"}
                          </Td>
                        </Tr>
                      </Tbody>
                    </Table>
                  </Box>
                </Box>
              </Box>
            );
          })}

          {/* 年度別損益（更新投資の場合：更新前/更新後表示）: 簡易モード時は非表示 */}
          {!isSimpleMode && capexProjectData?.purpose === PurposeType.UpdateAndOther.code && displayBeforeUpdateTotalSalesByYear.length > 0 && (
            <Box mt={2}>
              <Flex alignItems="center" gap={2}>
                <Heading size={"sm"}>
                  {t("features.InvestmentScreening.ProjectProfitablityStatement.heading.profitLossByYearUpdate")}
                </Heading>
                <UpdateProfitLossExplanationPopover isGeneralPurpose={isGeneralPurpose} />
              </Flex>
              <Text>
                unit: {selectedCurrency.name}
              </Text>
              {(() => {
                // 合計列の営業利益率・実質的営業利益率を正しく計算（単純平均ではなく加重計算）
                // 表示通貨ベースで計算（Excel出力と一致させる）
                // 更新前
                const beforeUpdateTotalSalesSum = displayBeforeUpdateTotalSalesByYear.reduce((sum, v) => sum.plus(v || new Decimal(0)), new Decimal(0));
                const beforeUpdateTotalProfitSum = displayBeforeUpdateProfitByYear.reduce((sum, v) => sum.plus(v || new Decimal(0)), new Decimal(0));
                const beforeUpdateTotalProfitRate = beforeUpdateTotalSalesSum.isZero()
                  ? new Decimal(0)
                  : beforeUpdateTotalProfitSum.div(beforeUpdateTotalSalesSum).times(100);
                // 更新前の客先指定部品材料費はdisplayBeforeUpdateMonthlyProfitAndLossDataから取得（JPY版）
                const beforeUpdateTotalCustomerSpecifiedPartsCostSum = displayBeforeUpdateMonthlyProfitAndLossData?.customerSpecifiedPartsCost
                  ? displayBeforeUpdateMonthlyProfitAndLossData.customerSpecifiedPartsCost.reduce((sum, v) => sum.plus(v || new Decimal(0)), new Decimal(0))
                  : new Decimal(0);
                const beforeUpdateTotalRealSales = beforeUpdateTotalSalesSum.minus(beforeUpdateTotalCustomerSpecifiedPartsCostSum);
                const beforeUpdateTotalRealOperatingProfitRate = beforeUpdateTotalRealSales.isZero()
                  ? new Decimal(0)
                  : beforeUpdateTotalProfitSum.div(beforeUpdateTotalRealSales).times(100);
                // 更新後
                const afterUpdateTotalSalesSum = displayTotalSalesByYear.reduce((sum, v) => sum.plus(v || new Decimal(0)), new Decimal(0));
                const afterUpdateTotalProfitSum = displayProfitByYear.reduce((sum, v) => sum.plus(v || new Decimal(0)), new Decimal(0));
                const afterUpdateTotalProfitRate = afterUpdateTotalSalesSum.isZero()
                  ? new Decimal(0)
                  : afterUpdateTotalProfitSum.div(afterUpdateTotalSalesSum).times(100);
                // 更新後の客先指定部品材料費（JPY版、月次データから計算）
                const afterUpdateTotalCustomerSpecifiedPartsCostSum = displayMonthlyProfitAndLossData?.customerSpecifiedPartsCost
                  ? displayMonthlyProfitAndLossData.customerSpecifiedPartsCost.reduce((sum, v) => sum.plus(v || new Decimal(0)), new Decimal(0))
                  : new Decimal(0);
                const afterUpdateTotalRealSales = afterUpdateTotalSalesSum.minus(afterUpdateTotalCustomerSpecifiedPartsCostSum);
                const afterUpdateTotalRealOperatingProfitRate = afterUpdateTotalRealSales.isZero()
                  ? new Decimal(0)
                  : afterUpdateTotalProfitSum.div(afterUpdateTotalRealSales).times(100);

                // 新規追加項目用の0固定データ
                const zeroDataArray = fiscalYearsString.map(() => new Decimal(0));
                // 各項目のデータ定義（更新前と更新後のペア）
                const dataItems = [
                  {
                    name: ProfitAndLossItems.totalSales.name, level: ProfitAndLossItems.totalSales.level,
                    beforeData: displayBeforeUpdateTotalSalesByYear,
                    afterData: displayTotalSalesByYear,
                  },
                  {
                    name: ProfitAndLossItems.productSales.name, level: ProfitAndLossItems.productSales.level,
                    beforeData: displayBeforeUpdateProductSalesByYear,
                    afterData: displayProductSalesByYear,
                  },
                  {
                    name: ProfitAndLossItems.moldRecovery.name, level: ProfitAndLossItems.moldRecovery.level,
                    beforeData: displayBeforeUpdateMoldSalesByYear,
                    afterData: displayMoldSalesByYear,
                  },
                  {
                    name: ProfitAndLossItems.devRecovery.name, level: ProfitAndLossItems.devRecovery.level,
                    beforeData: displayBeforeUpdateDevSalesByYear,
                    afterData: displayDevSalesByYear,
                  },
                  {
                    name: ProfitAndLossItems.equipmentRecovery.name, level: ProfitAndLossItems.equipmentRecovery.level,
                    beforeData: displayBeforeUpdateEquipmentSalesByYear,
                    afterData: displayEquipmentSalesByYear,
                  },
                  {
                    name: ProfitAndLossItems.salesCost.name, level: ProfitAndLossItems.salesCost.level,
                    beforeData: displayBeforeUpdateSalesCostByYear,
                    afterData: displaySalesCostByYear,
                  },
                  {
                    name: ProfitAndLossItems.materialCost.name, level: ProfitAndLossItems.materialCost.level,
                    beforeData: displayBeforeUpdateTotalMaterialCostByYear,
                    afterData: displayTotalMaterialCostByYear,
                    isMaterialCost: true,
                  },
                  {
                    name: ProfitAndLossItems.outsideCost.name, level: ProfitAndLossItems.outsideCost.level,
                    beforeData: displayBeforeUpdateOutsideCostByYear,
                    afterData: displayOutsideCostByYear,
                  },
                  {
                    name: ProfitAndLossItems.laborCost.name, level: ProfitAndLossItems.laborCost.level,
                    beforeData: displayBeforeUpdateLaborCostByYear,
                    afterData: displayLaborCostByYear,
                  },
                  {
                    name: ProfitAndLossItems.domesticLaborCost.name, level: ProfitAndLossItems.domesticLaborCost.level,
                    beforeData: displayBeforeUpdateDomesticLaborCostByYear,
                    afterData: displayDomesticLaborCostByYear,
                  },
                  {
                    name: ProfitAndLossItems.factoryExpenses.name, level: ProfitAndLossItems.factoryExpenses.level,
                    beforeData: displayBeforeUpdateFactoryExpensesByYear,
                    afterData: displayFactoryExpensesByYear,
                  },
                  {
                    name: ProfitAndLossItems.overHeadCost.name, level: ProfitAndLossItems.overHeadCost.level,
                    beforeData: displayBeforeUpdateOverHeadCostByYear,
                    afterData: displayOverHeadCostByYear,
                  },
                  {
                    name: ProfitAndLossItems.depreciation.name, level: ProfitAndLossItems.depreciation.level,
                    beforeData: displayBeforeUpdateDepreciationByYear,
                    afterData: displayDepreciationByYear,
                  },
                  {
                    name: ProfitAndLossItems.equipmentDepreciation.name, level: ProfitAndLossItems.equipmentDepreciation.level,
                    beforeData: displayBeforeUpdateEquipmentDepreciationByYear,
                    afterData: displayEquipmentDepreciationByYear,
                  },
                  {
                    name: ProfitAndLossItems.factoryCost.name, level: ProfitAndLossItems.factoryCost.level,
                    beforeData: displayBeforeUpdateFactoryCostByYear,
                    afterData: displayFactoryCostByYear,
                  },
                  ...(capexProjectData?.pastProjectId ? [{
                    name: ProfitAndLossItems.pastEquipmentCost.name, level: ProfitAndLossItems.pastEquipmentCost.level,
                    beforeData: displayBeforeUpdatePastEquipmentCostByYear,
                    afterData: displayPastEquipmentCostByYear,
                  }] : []),
                  {
                    name: ProfitAndLossItems.otherEquipmentCost.name, level: ProfitAndLossItems.otherEquipmentCost.level,
                    beforeData: displayBeforeUpdateOtherEquipmentCostByYear,
                    afterData: displayOtherEquipmentCostByYear,
                  },
                  {
                    name: ProfitAndLossItems.domesticFactoryExpenses.name, level: ProfitAndLossItems.domesticFactoryExpenses.level,
                    beforeData: displayBeforeUpdateDomesticFactoryExpensesByYear,
                    afterData: displayDomesticFactoryExpensesByYear,
                  },
                  {
                    name: ProfitAndLossItems.domesticOverHeadCost.name, level: ProfitAndLossItems.domesticOverHeadCost.level,
                    beforeData: displayBeforeUpdateDomesticOverHeadCostByYear,
                    afterData: displayDomesticOverHeadCostByYear,
                  },
                  {
                    name: ProfitAndLossItems.domesticDepreciation.name, level: ProfitAndLossItems.domesticDepreciation.level,
                    beforeData: displayBeforeUpdateDomesticDepreciationByYear,
                    afterData: displayDomesticDepreciationByYear,
                  },
                  {
                    name: ProfitAndLossItems.domesticInvestmentDepreciation.name, level: ProfitAndLossItems.domesticInvestmentDepreciation.level,
                    beforeData: displayBeforeUpdateDomesticInvestmentDepreciationByYear,
                    afterData: displayDomesticInvestmentDepreciationByYear,
                  },
                  {
                    name: ProfitAndLossItems.domesticFactoryBuildingDepreciation.name, level: ProfitAndLossItems.domesticFactoryBuildingDepreciation.level,
                    beforeData: displayBeforeUpdateDomesticFactoryBuildingDepreciationByYear,
                    afterData: displayDomesticFactoryBuildingDepreciationByYear,
                  },
                  {
                    name: ProfitAndLossItems.domesticOtherDepreciation.name, level: ProfitAndLossItems.domesticOtherDepreciation.level,
                    beforeData: displayBeforeUpdateDomesticOtherDepreciationByYear,
                    afterData: displayDomesticOtherDepreciationByYear,
                  },
                  {
                    name: ProfitAndLossItems.scrapCost.name, level: ProfitAndLossItems.scrapCost.level,
                    beforeData: displayBeforeUpdateScrapCostByYear,
                    afterData: displayScrapCostByYear,
                  },
                  {
                    name: ProfitAndLossItems.sgaExpenses.name, level: ProfitAndLossItems.sgaExpenses.level,
                    beforeData: displayBeforeUpdateSGAExpensesByYear,
                    afterData: displaySGAExpensesByYear,
                  },
                  {
                    name: ProfitAndLossItems.appliedLaborCost.name, level: ProfitAndLossItems.appliedLaborCost.level,
                    beforeData: displayBeforeUpdateAppliedLaborCostByYear,
                    afterData: displayAppliedLaborCostByYear,
                  },
                  {
                    name: ProfitAndLossItems.domesticSgaCost.name, level: ProfitAndLossItems.domesticSgaCost.level,
                    beforeData: displayBeforeUpdateDomesticSgaCostByYear,
                    afterData: displayDomesticSgaCostByYear,
                  },
                  {
                    name: ProfitAndLossItems.salesManagementCost.name, level: ProfitAndLossItems.salesManagementCost.level,
                    beforeData: displayBeforeUpdateSalesManagementCostByYear,
                    afterData: displaySalesManagementCostByYear,
                  },
                  {
                    name: ProfitAndLossItems.devCost.name, level: ProfitAndLossItems.devCost.level,
                    beforeData: displayBeforeUpdateDevCostByYear,
                    afterData: displayDevCostByYear,
                  },
                  {
                    name: ProfitAndLossItems.royalty.name, level: ProfitAndLossItems.royalty.level,
                    beforeData: displayBeforeUpdateRoyaltyByYear,
                    afterData: displayRoyaltyByYear,
                  },
                  {
                    name: ProfitAndLossItems.otherCost.name, level: ProfitAndLossItems.otherCost.level,
                    beforeData: displayBeforeUpdateOtherCostByYear,
                    afterData: displayOtherCostByYear,
                  },
                  {
                    name: ProfitAndLossItems.operatingProfit.name, level: ProfitAndLossItems.operatingProfit.level,
                    beforeData: displayBeforeUpdateProfitByYear,
                    afterData: displayProfitByYear,
                  },
                  {
                    name: ProfitAndLossItems.operatingProfitRate.name, level: ProfitAndLossItems.operatingProfitRate.level,
                    beforeData: displayBeforeUpdateProfitRateByYear,
                    afterData: displayProfitRateByYear,
                    isRate: true,
                    beforeTotalRate: beforeUpdateTotalProfitRate,
                    afterTotalRate: afterUpdateTotalProfitRate,
                  },
                  // 客先指定部品が存在する場合のみ実質的営業利益率を表示
                  ...(hasCustomerSpecifiedParts ? [{
                    name: ProfitAndLossItems.realOperatingProfitRate.name, level: ProfitAndLossItems.realOperatingProfitRate.level,
                    beforeData: displayBeforeUpdateRealOperatingProfitRateByYear,
                    afterData: displayRealOperatingProfitRateByYear,
                    isRate: true,
                    beforeTotalRate: beforeUpdateTotalRealOperatingProfitRate,
                    afterTotalRate: afterUpdateTotalRealOperatingProfitRate,
                  }] : []),
                ];

                return (
                  <Box display={"flex"}>
                    <Box flex={"0 0 auto"} style={{ minWidth: "400px" }}>
                      <Table size={"sm"} variant={"simple"} sx={{ tableLayout: "auto" }} className="mm-ceas-table">
                        <Thead>
                          <Tr>
                            <Th h={50} minW={70}>{t("features.InvestmentScreening.ProjectProfitablityStatement.tableHeader.no")}</Th>
                            <Th h={50} minW={250}>{t("features.InvestmentScreening.ProjectProfitablityStatement.tableHeader.item")}</Th>
                            <Th h={50} w={20}>{t("features.InvestmentScreening.ProjectProfitablityStatement.tableHeader.classification")}</Th>
                          </Tr>
                        </Thead>
                        <Tbody>
                          {dataItems.map((item, index) => (
                            <React.Fragment key={index}>
                              <Tr>
                                <Td rowSpan={2}>{index + 1}</Td>
                                <Td rowSpan={2} style={{ paddingLeft: `${item.level * 20}px` }}>
                                  {item.name}
                                </Td>
                                <Td>{t("features.InvestmentScreening.ProjectProfitablityStatement.label.beforeUpdate")}</Td>
                              </Tr>
                              <Tr>
                                <Td>{t("features.InvestmentScreening.ProjectProfitablityStatement.label.afterUpdate")}</Td>
                              </Tr>
                            </React.Fragment>
                          ))}
                        </Tbody>
                      </Table>
                    </Box>
                    <Box flex={1} overflowX={"auto"}>
                      <Table {...defaultTableStyle} className="mm-ceas-table">
                        <Thead>
                          <Tr>
                            {fiscalYearsString.map((_, index) => (
                              <Th key={index} style={{ ...defaultThStyle }}>
                                {t("features.InvestmentScreening.ProjectProfitablityStatement.tableHeader.yearSuffix", { year: index + 1 })}
                              </Th>
                            ))}
                            <Th style={{ ...defaultThStyle }} rowSpan={2}>
                              {t("features.InvestmentScreening.ProjectProfitablityStatement.tableHeader.total")}
                            </Th>
                          </Tr>
                          <Tr>
                            {fiscalYearsString.map((year, index) => (
                              <Th key={index} style={{ ...defaultThStyle }}>
                                {year}
                              </Th>
                            ))}
                          </Tr>
                        </Thead>
                        <Tbody>
                          {dataItems.map((item, itemIndex) => (
                            <React.Fragment key={itemIndex}>
                              {/* 更新前の行 */}
                              <Tr>
                                {fiscalYearsString.map((_, yearIndex) => (
                                  <Td key={yearIndex} style={defaultTdStyle}>
                                    {item.isRate
                                      ? `${(item.beforeData[yearIndex] || new Decimal(0)).toFixed(1)}%`
                                      : item.isMaterialCost
                                        ? formatCostSum(item.beforeData[yearIndex] || 0, selectedCurrency.name, 2)
                                        : formatNumber(item.beforeData[yearIndex] || 0, selectedCurrency.name)
                                    }
                                  </Td>
                                ))}
                                <Td style={defaultTdStyle}>
                                  {item.isRate
                                    ? `${(item.beforeTotalRate || new Decimal(0)).toFixed(1)}%`
                                    : item.isMaterialCost
                                      ? formatCostSum(item.beforeData.reduce((sum, v) => sum.plus(v || 0), new Decimal(0)), selectedCurrency.name, 2)
                                      : formatNumber(item.beforeData.reduce((sum, v) => sum.plus(v || 0), new Decimal(0)), selectedCurrency.name)
                                  }
                                </Td>
                              </Tr>
                              {/* 更新後の行 */}
                              <Tr>
                                {fiscalYearsString.map((_, yearIndex) => (
                                  <Td key={yearIndex} style={defaultTdStyle}>
                                    {item.isRate
                                      ? `${(item.afterData[yearIndex] || new Decimal(0)).toFixed(1)}%`
                                      : item.isMaterialCost
                                        ? formatCostSum(item.afterData[yearIndex] || 0, selectedCurrency.name, 2)
                                        : formatNumber(item.afterData[yearIndex] || 0, selectedCurrency.name)
                                    }
                                  </Td>
                                ))}
                                <Td style={defaultTdStyle}>
                                  {item.isRate
                                    ? `${(item.afterTotalRate || new Decimal(0)).toFixed(1)}%`
                                    : item.isMaterialCost
                                      ? formatCostSum(item.afterData.reduce((sum, v) => sum.plus(v || 0), new Decimal(0)), selectedCurrency.name, 2)
                                      : formatNumber(item.afterData.reduce((sum, v) => sum.plus(v || 0), new Decimal(0)), selectedCurrency.name)
                                  }
                                </Td>
                              </Tr>
                            </React.Fragment>
                          ))}
                        </Tbody>
                      </Table>
                    </Box>
                  </Box>
                );
              })()}
            </Box>
          )}

          {/* 年度別損益（合理化の場合：合理化前） */}
          {/* 簡易モード時は非表示 */}
          {!isSimpleMode && capexProjectData?.purpose === PurposeType.Rationalization.code && displayBeforeRationalizationTotalSalesByYear.length > 0 && (
            <Box mt={2}>
              <Flex alignItems="center" gap={2}>
                <Heading size={"sm"}>
                  {t("features.InvestmentScreening.ProjectProfitablityStatement.heading.profitLossByYearRationalizationBefore")}
                </Heading>
                <RationalizationBeforeProfitLossExplanationPopover isGeneralPurpose={isGeneralPurpose} />
              </Flex>
              <Text>
                unit: {selectedCurrency.name}
              </Text>
              {(() => {
                // 合計列の営業利益率・実質的営業利益率を正しく計算（単純平均ではなく加重計算）
                // 表示通貨ベースで計算（Excel出力と一致させる）
                const beforeRatTotalSales = displayBeforeRationalizationTotalSalesByYear.reduce((sum, v) => sum.plus(v || new Decimal(0)), new Decimal(0));
                const beforeRatTotalProfit = displayBeforeRationalizationProfitByYear.reduce((sum, v) => sum.plus(v || new Decimal(0)), new Decimal(0));
                const beforeRatTotalProfitRate = beforeRatTotalSales.isZero()
                  ? new Decimal(0)
                  : beforeRatTotalProfit.div(beforeRatTotalSales).times(100);
                // 客先指定部品材料費は月次データから計算（JPY版）
                const beforeRatTotalCustomerSpecifiedPartsCost = displayBeforeRationalizationMonthlyProfitAndLossData?.customerSpecifiedPartsCost
                  ? displayBeforeRationalizationMonthlyProfitAndLossData.customerSpecifiedPartsCost.reduce((sum, v) => sum.plus(v || new Decimal(0)), new Decimal(0))
                  : new Decimal(0);
                const beforeRatTotalRealSales = beforeRatTotalSales.minus(beforeRatTotalCustomerSpecifiedPartsCost);
                const beforeRatTotalRealOperatingProfitRate = beforeRatTotalRealSales.isZero()
                  ? new Decimal(0)
                  : beforeRatTotalProfit.div(beforeRatTotalRealSales).times(100);

                // 各項目のデータ定義（合理化前）
                const dataItems = [
                  { name: ProfitAndLossItems.totalSales.name, level: ProfitAndLossItems.totalSales.level, data: displayBeforeRationalizationTotalSalesByYear },
                  { name: ProfitAndLossItems.productSales.name, level: ProfitAndLossItems.productSales.level, data: displayBeforeRationalizationProductSalesByYear },
                  { name: ProfitAndLossItems.moldRecovery.name, level: ProfitAndLossItems.moldRecovery.level, data: displayBeforeRationalizationMoldSalesByYear },
                  { name: ProfitAndLossItems.devRecovery.name, level: ProfitAndLossItems.devRecovery.level, data: displayBeforeRationalizationDevSalesByYear },
                  { name: ProfitAndLossItems.equipmentRecovery.name, level: ProfitAndLossItems.equipmentRecovery.level, data: displayBeforeRationalizationEquipmentSalesByYear },
                  { name: ProfitAndLossItems.salesCost.name, level: ProfitAndLossItems.salesCost.level, data: displayBeforeRationalizationSalesCostByYear },
                  { name: ProfitAndLossItems.materialCost.name, level: ProfitAndLossItems.materialCost.level, data: displayBeforeRationalizationTotalMaterialCostByYear, isMaterialCost: true },
                  { name: ProfitAndLossItems.outsideCost.name, level: ProfitAndLossItems.outsideCost.level, data: displayBeforeRationalizationOutsideCostByYear },
                  { name: ProfitAndLossItems.laborCost.name, level: ProfitAndLossItems.laborCost.level, data: displayBeforeRationalizationLaborCostByYear },
                  { name: ProfitAndLossItems.domesticLaborCost.name, level: ProfitAndLossItems.domesticLaborCost.level, data: displayBeforeRationalizationDomesticLaborCostByYear },
                  { name: ProfitAndLossItems.factoryExpenses.name, level: ProfitAndLossItems.factoryExpenses.level, data: displayBeforeRationalizationFactoryExpensesByYear },
                  { name: ProfitAndLossItems.overHeadCost.name, level: ProfitAndLossItems.overHeadCost.level, data: displayBeforeRationalizationOverHeadCostByYear },
                  { name: ProfitAndLossItems.depreciation.name, level: ProfitAndLossItems.depreciation.level, data: displayBeforeRationalizationDepreciationByYear },
                  { name: ProfitAndLossItems.equipmentDepreciation.name, level: ProfitAndLossItems.equipmentDepreciation.level, data: displayBeforeRationalizationEquipmentDepreciationByYear },
                  { name: ProfitAndLossItems.factoryCost.name, level: ProfitAndLossItems.factoryCost.level, data: displayBeforeRationalizationFactoryCostByYear },
                  ...(capexProjectData?.pastProjectId ? [{ name: ProfitAndLossItems.pastEquipmentCost.name, level: ProfitAndLossItems.pastEquipmentCost.level, data: displayBeforeRationalizationPastEquipmentCostByYear }] : []),
                  { name: ProfitAndLossItems.otherEquipmentCost.name, level: ProfitAndLossItems.otherEquipmentCost.level, data: displayBeforeRationalizationOtherEquipmentCostByYear },
                  { name: ProfitAndLossItems.domesticFactoryExpenses.name, level: ProfitAndLossItems.domesticFactoryExpenses.level, data: displayBeforeRationalizationDomesticFactoryExpensesByYear },
                  { name: ProfitAndLossItems.domesticOverHeadCost.name, level: ProfitAndLossItems.domesticOverHeadCost.level, data: displayBeforeRationalizationDomesticOverHeadCostByYear },
                  { name: ProfitAndLossItems.domesticDepreciation.name, level: ProfitAndLossItems.domesticDepreciation.level, data: displayBeforeRationalizationDomesticDepreciationByYear },
                  { name: ProfitAndLossItems.domesticInvestmentDepreciation.name, level: ProfitAndLossItems.domesticInvestmentDepreciation.level, data: displayBeforeRationalizationDomesticInvestmentDepreciationByYear },
                  { name: ProfitAndLossItems.domesticFactoryBuildingDepreciation.name, level: ProfitAndLossItems.domesticFactoryBuildingDepreciation.level, data: displayBeforeRationalizationDomesticFactoryBuildingDepreciationByYear },
                  { name: ProfitAndLossItems.domesticOtherDepreciation.name, level: ProfitAndLossItems.domesticOtherDepreciation.level, data: displayBeforeRationalizationDomesticOtherDepreciationByYear },
                  { name: ProfitAndLossItems.scrapCost.name, level: ProfitAndLossItems.scrapCost.level, data: displayBeforeRationalizationScrapCostByYear },
                  { name: ProfitAndLossItems.sgaExpenses.name, level: ProfitAndLossItems.sgaExpenses.level, data: displayBeforeRationalizationSGAExpensesByYear },
                  { name: ProfitAndLossItems.appliedLaborCost.name, level: ProfitAndLossItems.appliedLaborCost.level, data: displayBeforeRationalizationAppliedLaborCostByYear },
                  { name: ProfitAndLossItems.domesticSgaCost.name, level: ProfitAndLossItems.domesticSgaCost.level, data: displayBeforeRationalizationDomesticSgaCostByYear },
                  { name: ProfitAndLossItems.salesManagementCost.name, level: ProfitAndLossItems.salesManagementCost.level, data: displayBeforeRationalizationSalesManagementCostByYear },
                  { name: ProfitAndLossItems.devCost.name, level: ProfitAndLossItems.devCost.level, data: displayBeforeRationalizationDevCostByYear },
                  { name: ProfitAndLossItems.royalty.name, level: ProfitAndLossItems.royalty.level, data: displayBeforeRationalizationRoyaltyByYear },
                  { name: ProfitAndLossItems.otherCost.name, level: ProfitAndLossItems.otherCost.level, data: displayBeforeRationalizationOtherCostByYear },
                  { name: ProfitAndLossItems.operatingProfit.name, level: ProfitAndLossItems.operatingProfit.level, data: displayBeforeRationalizationProfitByYear },
                  { name: ProfitAndLossItems.operatingProfitRate.name, level: ProfitAndLossItems.operatingProfitRate.level, data: displayBeforeRationalizationProfitRateByYear, isRate: true, totalRate: beforeRatTotalProfitRate },
                  // 客先指定部品が存在する場合のみ実質的営業利益率を表示
                  ...(hasCustomerSpecifiedParts ? [{ name: ProfitAndLossItems.realOperatingProfitRate.name, level: ProfitAndLossItems.realOperatingProfitRate.level, data: displayBeforeRationalizationRealOperatingProfitRateByYear, isRate: true, totalRate: beforeRatTotalRealOperatingProfitRate }] : []),
                ];

                return (
                  <Box display={"flex"}>
                    <Box flex={"0 0 auto"} style={{ minWidth: "350px" }}>
                      <Table size={"sm"} variant={"simple"} sx={{ tableLayout: "auto" }} className="mm-ceas-table">
                        <Thead>
                          <Tr>
                            <Th h={50} minW={70}>{t("features.InvestmentScreening.ProjectProfitablityStatement.tableHeader.no")}</Th>
                            <Th h={50} minW={250}>{t("features.InvestmentScreening.ProjectProfitablityStatement.tableHeader.item")}</Th>
                          </Tr>
                        </Thead>
                        <Tbody>
                          {dataItems.map((item, index) => (
                            <Tr key={index}>
                              <Td>{index + 1}</Td>
                              <Td style={{ paddingLeft: `${item.level * 20}px` }}>
                                {item.name}
                              </Td>
                            </Tr>
                          ))}
                        </Tbody>
                      </Table>
                    </Box>
                    <Box flex={1} overflowX={"auto"}>
                      <Table {...defaultTableStyle} className="mm-ceas-table">
                        <Thead>
                          <Tr>
                            {fiscalYearsString.map((_, index) => (
                              <Th key={index} style={{ ...defaultThStyle }}>
                                {t("features.InvestmentScreening.ProjectProfitablityStatement.tableHeader.yearSuffix", { year: index + 1 })}
                              </Th>
                            ))}
                            <Th style={{ ...defaultThStyle }} rowSpan={2}>
                              {t("features.InvestmentScreening.ProjectProfitablityStatement.tableHeader.total")}
                            </Th>
                          </Tr>
                          <Tr>
                            {fiscalYearsString.map((year, index) => (
                              <Th key={index} style={{ ...defaultThStyle }}>
                                {year}
                              </Th>
                            ))}
                          </Tr>
                        </Thead>
                        <Tbody>
                          {dataItems.map((item, itemIndex) => (
                            <Tr key={itemIndex}>
                              {fiscalYearsString.map((_, yearIndex) => (
                                <Td key={yearIndex} style={defaultTdStyle}>
                                  {item.isRate
                                    ? `${(item.data[yearIndex] || new Decimal(0)).toFixed(1)}%`
                                    : item.isMaterialCost
                                      ? formatCostSum(item.data[yearIndex] || new Decimal(0), selectedCurrency.name, 2)
                                      : formatNumber(item.data[yearIndex] || new Decimal(0), selectedCurrency.name)
                                  }
                                </Td>
                              ))}
                              <Td style={defaultTdStyle}>
                                {item.isRate
                                  ? `${(item.totalRate || new Decimal(0)).toFixed(1)}%`
                                  : item.isMaterialCost
                                    ? formatCostSum(item.data.reduce((a, b) => a.plus(b), new Decimal(0)), selectedCurrency.name, 2)
                                    : formatNumber(item.data.reduce((a, b) => a.plus(b), new Decimal(0)), selectedCurrency.name)
                                }
                              </Td>
                            </Tr>
                          ))}
                        </Tbody>
                      </Table>
                    </Box>
                  </Box>
                );
              })()}
            </Box>
          )}

          {/* 年度別損益（合理化の場合：合理化後） */}
          {/* 簡易モード時は非表示 */}
          {!isSimpleMode && capexProjectData?.purpose === PurposeType.Rationalization.code && displayTotalSalesByYear.length > 0 && (
            <Box mt={4}>
              <Flex alignItems="center" gap={2}>
                <Heading size={"sm"}>
                  {t("features.InvestmentScreening.ProjectProfitablityStatement.heading.profitLossByYearRationalizationAfter")}
                </Heading>
                <RationalizationAfterProfitLossExplanationPopover isGeneralPurpose={isGeneralPurpose} />
              </Flex>
              <Text>
                unit: {selectedCurrency.name}
              </Text>
              {(() => {
                // 合計列の営業利益率・実質的営業利益率を正しく計算（単純平均ではなく加重計算）
                // 表示通貨ベースで計算（Excel出力と一致させる）
                const afterRatTotalSales = displayTotalSalesByYear.reduce((sum, v) => sum.plus(v || new Decimal(0)), new Decimal(0));
                const afterRatTotalProfit = displayProfitByYear.reduce((sum, v) => sum.plus(v || new Decimal(0)), new Decimal(0));
                const afterRatTotalProfitRate = afterRatTotalSales.isZero()
                  ? new Decimal(0)
                  : afterRatTotalProfit.div(afterRatTotalSales).times(100);
                // 客先指定部品材料費は月次データから計算（JPY版）
                const afterRatTotalCustomerSpecifiedPartsCost = displayMonthlyProfitAndLossData?.customerSpecifiedPartsCost
                  ? displayMonthlyProfitAndLossData.customerSpecifiedPartsCost.reduce((sum, v) => sum.plus(v || new Decimal(0)), new Decimal(0))
                  : new Decimal(0);
                const afterRatTotalRealSales = afterRatTotalSales.minus(afterRatTotalCustomerSpecifiedPartsCost);
                const afterRatTotalRealOperatingProfitRate = afterRatTotalRealSales.isZero()
                  ? new Decimal(0)
                  : afterRatTotalProfit.div(afterRatTotalRealSales).times(100);

                // 各項目のデータ定義（合理化後）
                const dataItems = [
                  { name: ProfitAndLossItems.totalSales.name, level: ProfitAndLossItems.totalSales.level, data: displayTotalSalesByYear },
                  { name: ProfitAndLossItems.productSales.name, level: ProfitAndLossItems.productSales.level, data: displayProductSalesByYear },
                  { name: ProfitAndLossItems.moldRecovery.name, level: ProfitAndLossItems.moldRecovery.level, data: displayMoldSalesByYear },
                  { name: ProfitAndLossItems.devRecovery.name, level: ProfitAndLossItems.devRecovery.level, data: displayDevSalesByYear },
                  { name: ProfitAndLossItems.equipmentRecovery.name, level: ProfitAndLossItems.equipmentRecovery.level, data: displayEquipmentSalesByYear },
                  { name: ProfitAndLossItems.salesCost.name, level: ProfitAndLossItems.salesCost.level, data: displaySalesCostByYear },
                  { name: ProfitAndLossItems.materialCost.name, level: ProfitAndLossItems.materialCost.level, data: displayTotalMaterialCostByYear, isMaterialCost: true },
                  { name: ProfitAndLossItems.outsideCost.name, level: ProfitAndLossItems.outsideCost.level, data: displayOutsideCostByYear },
                  { name: ProfitAndLossItems.laborCost.name, level: ProfitAndLossItems.laborCost.level, data: displayLaborCostByYear },
                  { name: ProfitAndLossItems.domesticLaborCost.name, level: ProfitAndLossItems.domesticLaborCost.level, data: displayDomesticLaborCostByYear },
                  { name: ProfitAndLossItems.factoryExpenses.name, level: ProfitAndLossItems.factoryExpenses.level, data: displayFactoryExpensesByYear },
                  { name: ProfitAndLossItems.overHeadCost.name, level: ProfitAndLossItems.overHeadCost.level, data: displayOverHeadCostByYear },
                  { name: ProfitAndLossItems.depreciation.name, level: ProfitAndLossItems.depreciation.level, data: displayDepreciationByYear },
                  { name: ProfitAndLossItems.equipmentDepreciation.name, level: ProfitAndLossItems.equipmentDepreciation.level, data: displayEquipmentDepreciationByYear },
                  { name: ProfitAndLossItems.factoryCost.name, level: ProfitAndLossItems.factoryCost.level, data: displayFactoryCostByYear },
                  ...(capexProjectData?.pastProjectId ? [{ name: ProfitAndLossItems.pastEquipmentCost.name, level: ProfitAndLossItems.pastEquipmentCost.level, data: displayPastEquipmentCostByYear }] : []),
                  { name: ProfitAndLossItems.otherEquipmentCost.name, level: ProfitAndLossItems.otherEquipmentCost.level, data: displayOtherEquipmentCostByYear },
                  { name: ProfitAndLossItems.domesticFactoryExpenses.name, level: ProfitAndLossItems.domesticFactoryExpenses.level, data: displayDomesticFactoryExpensesByYear },
                  { name: ProfitAndLossItems.domesticOverHeadCost.name, level: ProfitAndLossItems.domesticOverHeadCost.level, data: displayDomesticOverHeadCostByYear },
                  { name: ProfitAndLossItems.domesticDepreciation.name, level: ProfitAndLossItems.domesticDepreciation.level, data: displayDomesticDepreciationByYear },
                  { name: ProfitAndLossItems.domesticInvestmentDepreciation.name, level: ProfitAndLossItems.domesticInvestmentDepreciation.level, data: displayDomesticInvestmentDepreciationByYear },
                  { name: ProfitAndLossItems.domesticFactoryBuildingDepreciation.name, level: ProfitAndLossItems.domesticFactoryBuildingDepreciation.level, data: displayDomesticFactoryBuildingDepreciationByYear },
                  { name: ProfitAndLossItems.domesticOtherDepreciation.name, level: ProfitAndLossItems.domesticOtherDepreciation.level, data: displayDomesticOtherDepreciationByYear },
                  { name: ProfitAndLossItems.scrapCost.name, level: ProfitAndLossItems.scrapCost.level, data: displayScrapCostByYear },
                  { name: ProfitAndLossItems.sgaExpenses.name, level: ProfitAndLossItems.sgaExpenses.level, data: displaySGAExpensesByYear },
                  { name: ProfitAndLossItems.appliedLaborCost.name, level: ProfitAndLossItems.appliedLaborCost.level, data: displayAppliedLaborCostByYear },
                  { name: ProfitAndLossItems.domesticSgaCost.name, level: ProfitAndLossItems.domesticSgaCost.level, data: displayDomesticSgaCostByYear },
                  { name: ProfitAndLossItems.salesManagementCost.name, level: ProfitAndLossItems.salesManagementCost.level, data: displaySalesManagementCostByYear },
                  { name: ProfitAndLossItems.devCost.name, level: ProfitAndLossItems.devCost.level, data: displayDevCostByYear },
                  { name: ProfitAndLossItems.royalty.name, level: ProfitAndLossItems.royalty.level, data: displayRoyaltyByYear },
                  { name: ProfitAndLossItems.otherCost.name, level: ProfitAndLossItems.otherCost.level, data: displayOtherCostByYear },
                  { name: ProfitAndLossItems.operatingProfit.name, level: ProfitAndLossItems.operatingProfit.level, data: displayProfitByYear },
                  { name: ProfitAndLossItems.operatingProfitRate.name, level: ProfitAndLossItems.operatingProfitRate.level, data: displayProfitRateByYear, isRate: true, totalRate: afterRatTotalProfitRate },
                  // 客先指定部品が存在する場合のみ実質的営業利益率を表示
                  ...(hasCustomerSpecifiedParts ? [{ name: ProfitAndLossItems.realOperatingProfitRate.name, level: ProfitAndLossItems.realOperatingProfitRate.level, data: displayRealOperatingProfitRateByYear, isRate: true, totalRate: afterRatTotalRealOperatingProfitRate }] : []),
                ];

                return (
                  <Box display={"flex"}>
                    <Box flex={"0 0 auto"} style={{ minWidth: "350px" }}>
                      <Table size={"sm"} variant={"simple"} sx={{ tableLayout: "auto" }} className="mm-ceas-table">
                        <Thead>
                          <Tr>
                            <Th h={50} minW={70}>{t("features.InvestmentScreening.ProjectProfitablityStatement.tableHeader.no")}</Th>
                            <Th h={50} minW={250}>{t("features.InvestmentScreening.ProjectProfitablityStatement.tableHeader.item")}</Th>
                          </Tr>
                        </Thead>
                        <Tbody>
                          {dataItems.map((item, index) => (
                            <Tr key={index}>
                              <Td>{index + 1}</Td>
                              <Td style={{ paddingLeft: `${item.level * 20}px` }}>
                                {item.name}
                              </Td>
                            </Tr>
                          ))}
                        </Tbody>
                      </Table>
                    </Box>
                    <Box flex={1} overflowX={"auto"}>
                      <Table {...defaultTableStyle} className="mm-ceas-table">
                        <Thead>
                          <Tr>
                            {fiscalYearsString.map((_, index) => (
                              <Th key={index} style={{ ...defaultThStyle }}>
                                {t("features.InvestmentScreening.ProjectProfitablityStatement.tableHeader.yearSuffix", { year: index + 1 })}
                              </Th>
                            ))}
                            <Th style={{ ...defaultThStyle }} rowSpan={2}>
                              {t("features.InvestmentScreening.ProjectProfitablityStatement.tableHeader.total")}
                            </Th>
                          </Tr>
                          <Tr>
                            {fiscalYearsString.map((year, index) => (
                              <Th key={index} style={{ ...defaultThStyle }}>
                                {year}
                              </Th>
                            ))}
                          </Tr>
                        </Thead>
                        <Tbody>
                          {dataItems.map((item, itemIndex) => (
                            <Tr key={itemIndex}>
                              {fiscalYearsString.map((_, yearIndex) => (
                                <Td key={yearIndex} style={defaultTdStyle}>
                                  {item.isRate
                                    ? `${(item.data[yearIndex] || new Decimal(0)).toFixed(1)}%`
                                    : item.isMaterialCost
                                      ? formatCostSum(item.data[yearIndex] || new Decimal(0), selectedCurrency.name, 2)
                                      : formatNumber(item.data[yearIndex] || new Decimal(0), selectedCurrency.name)
                                  }
                                </Td>
                              ))}
                              <Td style={defaultTdStyle}>
                                {item.isRate
                                  ? `${(item.totalRate || new Decimal(0)).toFixed(1)}%`
                                  : item.isMaterialCost
                                    ? formatCostSum(item.data.reduce((a, b) => a.plus(b), new Decimal(0)), selectedCurrency.name, 2)
                                    : formatNumber(item.data.reduce((a, b) => a.plus(b), new Decimal(0)), selectedCurrency.name)
                                }
                              </Td>
                            </Tr>
                          ))}
                        </Tbody>
                      </Table>
                    </Box>
                  </Box>
                );
              })()}
            </Box>
          )}

          {/* 合理化による改善効果（指標）（合理化の場合） */}
          {capexProjectData?.purpose === PurposeType.Rationalization.code && rationalizationSummaryData.length > 0 && (
            <Box mt={4}>
              <Flex alignItems="center" gap={2}>
                <Heading size={"sm"}>
                  {t("features.InvestmentScreening.ProjectProfitablityStatement.heading.improvementByRationalizationIndicator")}
                </Heading>
                <RationalizationImprovementExplanationPopover isSimpleMode={isSimpleMode} />
              </Flex>
              {(() => {
                // 項目名取得関数
                const getItemName = (item: RationalizationProfitLossItem): string => {
                  if (item.itemType === "custom" && item.customItemName) {
                    return item.customItemName;
                  }
                  const itemNameMap: Record<string, string> = {
                    productionQuantity: t("features.InvestmentScreening.ProjectProfitablityStatement.improvementItem.productionQuantity"),
                    salesQuantity: t("features.InvestmentScreening.ProjectProfitablityStatement.improvementItem.salesQuantity"),
                    laborCost: t("features.InvestmentScreening.ProjectProfitablityStatement.improvementItem.laborCost"),
                    repairCost: t("features.InvestmentScreening.ProjectProfitablityStatement.improvementItem.repairCost"),
                    consumablesCost: t("features.InvestmentScreening.ProjectProfitablityStatement.improvementItem.consumablesCost"),
                    maintenanceCost: t("features.InvestmentScreening.ProjectProfitablityStatement.improvementItem.maintenanceCost"),
                    scrapCost: t("features.InvestmentScreening.ProjectProfitablityStatement.improvementItem.scrapCost"),
                    totalCost: t("features.InvestmentScreening.ProjectProfitablityStatement.improvementItem.totalCost"),
                    targetEquipmentDepreciation: t("features.InvestmentScreening.ProjectProfitablityStatement.improvementItem.targetEquipmentDepreciation"),
                    otherDepreciation: t("features.InvestmentScreening.ProjectProfitablityStatement.improvementItem.otherDepreciation"),
                  };
                  return itemNameMap[item.itemType] || item.itemType;
                };

                // 月次データを年度ごとに集計する関数
                const aggregateMonthlyDataByYear = (
                  monthlyData: Record<string, number>,
                  fiscalYears: string[]
                ): number[] => {
                  return fiscalYears.map((fiscalYear) => {
                    // 年度文字列から年を抽出（例：「2026年3月期」→ 2026）
                    const yearMatch = fiscalYear.match(/(\d{4})年/);
                    if (!yearMatch) return 0;
                    const endYear = parseInt(yearMatch[1], 10);
                    // 年度は前年4月から当年3月まで
                    let total = 0;
                    for (let month = 4; month <= 12; month++) {
                      const key = `${endYear - 1}-${String(month).padStart(2, "0")}`;
                      total += monthlyData[key] || 0;
                    }
                    for (let month = 1; month <= 3; month++) {
                      const key = `${endYear}-${String(month).padStart(2, "0")}`;
                      total += monthlyData[key] || 0;
                    }
                    return total;
                  });
                };

                // 工程名取得関数
                const getProcessName = (processId: string | undefined): string => {
                  if (!processId) return "";
                  const process = uniqueProcessData.find(p => p.uniqueProcessId === processId);
                  return process?.uniqueProcessName || processId;
                };

                const indicatorSummaryData = rationalizationSummaryData
                  .filter(summary => summary.indicatorItems && summary.indicatorItems.length > 0);

                if (indicatorSummaryData.length === 0) {
                  return <Text color="gray.500">{t("features.InvestmentScreening.ProjectProfitablityStatement.message.noIndicatorData")}</Text>;
                }

                return (
                  <>
                    <Text mb={1}>
                      unit: pcs
                    </Text>
                    {indicatorSummaryData
                      .map((summary, summaryIndex) => (
                        <Box key={summary.processId || summaryIndex} mb={4}>
                          <Text fontWeight="bold" mt={2} mb={1} textAlign="center">
                            {getProcessName(summary.processId)}
                          </Text>
                          <Box display={"flex"}>
                            <Box flex={"0 0 auto"} style={{ minWidth: "350px" }}>
                              <Table size={"sm"} variant={"simple"} sx={{ tableLayout: "auto" }} className="mm-ceas-table">
                                <Thead>
                                  <Tr>
                                    <Th h={50} minW={70}>{t("features.InvestmentScreening.ProjectProfitablityStatement.tableHeader.no")}</Th>
                                    <Th h={50} minW={180}>{t("features.InvestmentScreening.ProjectProfitablityStatement.tableHeader.item")}</Th>
                                    <Th h={50} minW={70}>{t("features.InvestmentScreening.ProjectProfitablityStatement.tableHeader.classification")}</Th>
                                  </Tr>
                                </Thead>
                                <Tbody>
                                  {summary.indicatorItems!.map((item, index) => (
                                    <React.Fragment key={item.id || index}>
                                      <Tr>
                                        <Td rowSpan={2}>{index + 1}</Td>
                                        <Td rowSpan={2}>
                                          {getItemName(item)}
                                        </Td>
                                        <Td whiteSpace="nowrap">{t("features.InvestmentScreening.ProjectProfitablityStatement.label.beforeRationalization")}</Td>
                                      </Tr>
                                      <Tr>
                                        <Td whiteSpace="nowrap">{t("features.InvestmentScreening.ProjectProfitablityStatement.label.afterRationalization")}</Td>
                                      </Tr>
                                    </React.Fragment>
                                  ))}
                                </Tbody>
                              </Table>
                            </Box>
                            <Box flex={1} overflowX={"auto"}>
                              <Table {...defaultTableStyle} className="mm-ceas-table">
                                <Thead>
                                  <Tr>
                                    {fiscalYearsString.map((_, index) => (
                                      <Th key={index} style={{ ...defaultThStyle }}>
                                        {t("features.InvestmentScreening.ProjectProfitablityStatement.tableHeader.yearSuffix", { year: index + 1 })}
                                      </Th>
                                    ))}
                                    <Th style={{ ...defaultThStyle }} rowSpan={2}>
                                      {t("features.InvestmentScreening.ProjectProfitablityStatement.tableHeader.total")}
                                    </Th>
                                  </Tr>
                                  <Tr>
                                    {fiscalYearsString.map((year, index) => (
                                      <Th key={index} style={{ ...defaultThStyle }}>
                                        {year}
                                      </Th>
                                    ))}
                                  </Tr>
                                </Thead>
                                <Tbody>
                                  {summary.indicatorItems!.map((item, itemIndex) => {
                                    const beforeByYear = aggregateMonthlyDataByYear(item.beforeMonthlyData, fiscalYearsString);
                                    const afterByYear = aggregateMonthlyDataByYear(item.afterMonthlyData, fiscalYearsString);
                                    return (
                                      <React.Fragment key={item.id || itemIndex}>
                                        {/* 合理化前の行 */}
                                        <Tr>
                                          {beforeByYear.map((value, yearIndex) => (
                                            <Td key={yearIndex} style={defaultTdStyle}>
                                              {formatNumber(value, "")}
                                            </Td>
                                          ))}
                                          <Td style={defaultTdStyle}>
                                            {formatNumber(beforeByYear.reduce((a, b) => a + b, 0), "")}
                                          </Td>
                                        </Tr>
                                        {/* 合理化後の行 */}
                                        <Tr>
                                          {afterByYear.map((value, yearIndex) => (
                                            <Td key={yearIndex} style={defaultTdStyle}>
                                              {formatNumber(value, "")}
                                            </Td>
                                          ))}
                                          <Td style={defaultTdStyle}>
                                            {formatNumber(afterByYear.reduce((a, b) => a + b, 0), "")}
                                          </Td>
                                        </Tr>
                                      </React.Fragment>
                                    );
                                  })}
                                </Tbody>
                              </Table>
                            </Box>
                          </Box>
                        </Box>
                      ))}
                  </>
                );
              })()}
            </Box>
          )}

          {/* 合理化による改善効果（費用）（合理化の場合） */}
          {capexProjectData?.purpose === PurposeType.Rationalization.code && rationalizationSummaryData.length > 0 && (
            <Box mt={4}>
              <Flex alignItems="center" gap={2}>
                <Heading size={"sm"}>
                  {t("features.InvestmentScreening.ProjectProfitablityStatement.heading.improvementByRationalizationCost")}
                </Heading>
                <RationalizationImprovementExplanationPopover isSimpleMode={isSimpleMode} />
              </Flex>
              {(() => {
                // 項目名取得関数
                const getItemName = (item: RationalizationProfitLossItem): string => {
                  if (item.itemType === "custom" && item.customItemName) {
                    return item.customItemName;
                  }
                  const itemNameMap: Record<string, string> = {
                    productionQuantity: t("features.InvestmentScreening.ProjectProfitablityStatement.improvementItem.productionQuantity"),
                    salesQuantity: t("features.InvestmentScreening.ProjectProfitablityStatement.improvementItem.salesQuantity"),
                    laborCost: t("features.InvestmentScreening.ProjectProfitablityStatement.improvementItem.laborCost"),
                    repairCost: t("features.InvestmentScreening.ProjectProfitablityStatement.improvementItem.repairCost"),
                    consumablesCost: t("features.InvestmentScreening.ProjectProfitablityStatement.improvementItem.consumablesCost"),
                    maintenanceCost: t("features.InvestmentScreening.ProjectProfitablityStatement.improvementItem.maintenanceCost"),
                    scrapCost: t("features.InvestmentScreening.ProjectProfitablityStatement.improvementItem.scrapCost"),
                    totalCost: t("features.InvestmentScreening.ProjectProfitablityStatement.improvementItem.totalCost"),
                    targetEquipmentDepreciation: t("features.InvestmentScreening.ProjectProfitablityStatement.improvementItem.targetEquipmentDepreciation"),
                    otherDepreciation: t("features.InvestmentScreening.ProjectProfitablityStatement.improvementItem.otherDepreciation"),
                  };
                  return itemNameMap[item.itemType] || item.itemType;
                };

                // 月次データを年度ごとに集計する関数
                const aggregateMonthlyDataByYear = (
                  monthlyData: Record<string, number>,
                  fiscalYears: string[]
                ): number[] => {
                  return fiscalYears.map((fiscalYear) => {
                    const yearMatch = fiscalYear.match(/(\d{4})年/);
                    if (!yearMatch) return 0;
                    const endYear = parseInt(yearMatch[1], 10);
                    let total = 0;
                    for (let month = 4; month <= 12; month++) {
                      const key = `${endYear - 1}-${String(month).padStart(2, "0")}`;
                      total += monthlyData[key] || 0;
                    }
                    for (let month = 1; month <= 3; month++) {
                      const key = `${endYear}-${String(month).padStart(2, "0")}`;
                      total += monthlyData[key] || 0;
                    }
                    return total;
                  });
                };

                // 工程名取得関数
                const getProcessName = (processId: string | undefined): string => {
                  if (!processId) return "";
                  const process = uniqueProcessData.find(p => p.uniqueProcessId === processId);
                  return process?.uniqueProcessName || processId;
                };

                // 工程ごとの元通貨名取得関数
                const getSourceCurrencyName = (summary: RationalizationSummaryInputData): string => {
                  if (summary.costCurrencyId) {
                    const currency = currencyData.find(c => c.currencyId === summary.costCurrencyId);
                    if (currency?.currencyName) {
                      return currency.currencyName;
                    }
                  }
                  return selectedCurrency.name;
                };

                // 通貨変換関数（工程の通貨から選択通貨へ変換）
                const convertCostValue = (value: number, summary: RationalizationSummaryInputData): number => {
                  const sourceCurrencyName = getSourceCurrencyName(summary);
                  // 同じ通貨なら変換不要
                  if (sourceCurrencyName === selectedCurrency.name) {
                    return value;
                  }
                  // 通貨変換
                  const converted = convertWithRate(
                    new Decimal(value),
                    sourceCurrencyName,
                    selectedCurrency.name
                  );
                  return converted.toNumber();
                };

                return (
                  <>
                    <Text mb={1}>
                      unit: {selectedCurrency.name}
                    </Text>
                    {rationalizationSummaryData
                      .filter(summary => summary.costItems && summary.costItems.length > 0)
                      .map((summary, summaryIndex) => (
                        <Box key={summary.processId || summaryIndex} mb={4}>
                          <Text fontWeight="bold" mt={2} mb={1} textAlign="center">
                            {getProcessName(summary.processId)}
                          </Text>
                          <Box display={"flex"}>
                            <Box flex={"0 0 auto"} style={{ minWidth: "350px" }}>
                              <Table size={"sm"} variant={"simple"} sx={{ tableLayout: "auto" }} className="mm-ceas-table">
                                <Thead>
                                  <Tr>
                                    <Th h={50} minW={70}>{t("features.InvestmentScreening.ProjectProfitablityStatement.tableHeader.no")}</Th>
                                    <Th h={50} minW={180}>{t("features.InvestmentScreening.ProjectProfitablityStatement.tableHeader.item")}</Th>
                                    <Th h={50} minW={70}>{t("features.InvestmentScreening.ProjectProfitablityStatement.tableHeader.classification")}</Th>
                                  </Tr>
                                </Thead>
                                <Tbody>
                                  {summary.costItems!.map((item, index) => (
                                    <React.Fragment key={item.id || index}>
                                      <Tr>
                                        <Td rowSpan={2}>{index + 1}</Td>
                                        <Td rowSpan={2}>
                                          {getItemName(item)}
                                        </Td>
                                        <Td whiteSpace="nowrap">{t("features.InvestmentScreening.ProjectProfitablityStatement.label.beforeRationalization")}</Td>
                                      </Tr>
                                      <Tr>
                                        <Td whiteSpace="nowrap">{t("features.InvestmentScreening.ProjectProfitablityStatement.label.afterRationalization")}</Td>
                                      </Tr>
                                    </React.Fragment>
                                  ))}
                                </Tbody>
                              </Table>
                            </Box>
                            <Box flex={1} overflowX={"auto"}>
                              <Table {...defaultTableStyle} className="mm-ceas-table">
                                <Thead>
                                  <Tr>
                                    {fiscalYearsString.map((_, index) => (
                                      <Th key={index} style={{ ...defaultThStyle }}>
                                        {t("features.InvestmentScreening.ProjectProfitablityStatement.tableHeader.yearSuffix", { year: index + 1 })}
                                      </Th>
                                    ))}
                                    <Th style={{ ...defaultThStyle }} rowSpan={2}>
                                      {t("features.InvestmentScreening.ProjectProfitablityStatement.tableHeader.total")}
                                    </Th>
                                  </Tr>
                                  <Tr>
                                    {fiscalYearsString.map((year, index) => (
                                      <Th key={index} style={{ ...defaultThStyle }}>
                                        {year}
                                      </Th>
                                    ))}
                                  </Tr>
                                </Thead>
                                <Tbody>
                                  {summary.costItems!.map((item, itemIndex) => {
                                    const beforeByYear = aggregateMonthlyDataByYear(item.beforeMonthlyData, fiscalYearsString);
                                    const afterByYear = aggregateMonthlyDataByYear(item.afterMonthlyData, fiscalYearsString);
                                    // 通貨変換を適用
                                    const convertedBeforeByYear = beforeByYear.map(v => convertCostValue(v, summary));
                                    const convertedAfterByYear = afterByYear.map(v => convertCostValue(v, summary));
                                    return (
                                      <React.Fragment key={item.id || itemIndex}>
                                        {/* 合理化前の行 */}
                                        <Tr>
                                          {convertedBeforeByYear.map((value, yearIndex) => (
                                            <Td key={yearIndex} style={defaultTdStyle}>
                                              {formatNumber(value, selectedCurrency.name)}
                                            </Td>
                                          ))}
                                          <Td style={defaultTdStyle}>
                                            {formatNumber(convertedBeforeByYear.reduce((a, b) => a + b, 0), selectedCurrency.name)}
                                          </Td>
                                        </Tr>
                                        {/* 合理化後の行 */}
                                        <Tr>
                                          {convertedAfterByYear.map((value, yearIndex) => (
                                            <Td key={yearIndex} style={defaultTdStyle}>
                                              {formatNumber(value, selectedCurrency.name)}
                                            </Td>
                                          ))}
                                          <Td style={defaultTdStyle}>
                                            {formatNumber(convertedAfterByYear.reduce((a, b) => a + b, 0), selectedCurrency.name)}
                                          </Td>
                                        </Tr>
                                      </React.Fragment>
                                    );
                                  })}
                                </Tbody>
                              </Table>
                            </Box>
                          </Box>
                        </Box>
                      ))}
                  </>
                );
              })()}
            </Box>
          )}

          {/* 改善効果の合計（指標）（合理化の場合） */}
          {capexProjectData?.purpose === PurposeType.Rationalization.code && rationalizationSummaryData.length > 0 && (
            <Box mt={4}>
              <Flex alignItems="center" gap={2}>
                <Heading size={"sm"}>
                  {t("features.InvestmentScreening.ProjectProfitablityStatement.heading.improvementTotalIndicator")}
                </Heading>
                <RationalizationImprovementTotalExplanationPopover isSimpleMode={isSimpleMode} />
              </Flex>
              <Text>
                unit: pcs
              </Text>
              {(() => {
                // 項目名取得関数
                const getItemName = (item: RationalizationProfitLossItem): string => {
                  if (item.itemType === "custom" && item.customItemName) {
                    return item.customItemName;
                  }
                  const itemNameMap: Record<string, string> = {
                    productionQuantity: t("features.InvestmentScreening.ProjectProfitablityStatement.improvementItem.productionQuantity"),
                    salesQuantity: t("features.InvestmentScreening.ProjectProfitablityStatement.improvementItem.salesQuantity"),
                    laborCost: t("features.InvestmentScreening.ProjectProfitablityStatement.improvementItem.laborCost"),
                    repairCost: t("features.InvestmentScreening.ProjectProfitablityStatement.improvementItem.repairCost"),
                    consumablesCost: t("features.InvestmentScreening.ProjectProfitablityStatement.improvementItem.consumablesCost"),
                    maintenanceCost: t("features.InvestmentScreening.ProjectProfitablityStatement.improvementItem.maintenanceCost"),
                    scrapCost: t("features.InvestmentScreening.ProjectProfitablityStatement.improvementItem.scrapCost"),
                    totalCost: t("features.InvestmentScreening.ProjectProfitablityStatement.improvementItem.totalCost"),
                    targetEquipmentDepreciation: t("features.InvestmentScreening.ProjectProfitablityStatement.improvementItem.targetEquipmentDepreciation"),
                    otherDepreciation: t("features.InvestmentScreening.ProjectProfitablityStatement.improvementItem.otherDepreciation"),
                  };
                  return itemNameMap[item.itemType] || item.itemType;
                };

                // 月次データを年度ごとに集計する関数
                const aggregateMonthlyDataByYear = (
                  monthlyData: Record<string, number>,
                  fiscalYears: string[]
                ): number[] => {
                  return fiscalYears.map((fiscalYear) => {
                    const yearMatch = fiscalYear.match(/(\d{4})年/);
                    if (!yearMatch) return 0;
                    const endYear = parseInt(yearMatch[1], 10);
                    let total = 0;
                    for (let month = 4; month <= 12; month++) {
                      const key = `${endYear - 1}-${String(month).padStart(2, "0")}`;
                      total += monthlyData[key] || 0;
                    }
                    for (let month = 1; month <= 3; month++) {
                      const key = `${endYear}-${String(month).padStart(2, "0")}`;
                      total += monthlyData[key] || 0;
                    }
                    return total;
                  });
                };

                // 全工程の指標項目を集約（itemTypeごとにグループ化）
                const allIndicatorItems: RationalizationProfitLossItem[] = [];
                rationalizationSummaryData.forEach(summary => {
                  if (summary.indicatorItems) {
                    summary.indicatorItems.forEach(item => {
                      // 同じitemType（またはcustomItemName）の項目を探す
                      const existingIndex = allIndicatorItems.findIndex(existing =>
                        item.itemType === "custom"
                          ? existing.itemType === "custom" && existing.customItemName === item.customItemName
                          : existing.itemType === item.itemType
                      );
                      if (existingIndex >= 0) {
                        // 既存項目に月次データを加算
                        const existing = allIndicatorItems[existingIndex];
                        Object.keys(item.beforeMonthlyData).forEach(key => {
                          existing.beforeMonthlyData[key] = (existing.beforeMonthlyData[key] || 0) + (item.beforeMonthlyData[key] || 0);
                        });
                        Object.keys(item.afterMonthlyData).forEach(key => {
                          existing.afterMonthlyData[key] = (existing.afterMonthlyData[key] || 0) + (item.afterMonthlyData[key] || 0);
                        });
                      } else {
                        // 新規追加（コピーを作成）
                        allIndicatorItems.push({
                          ...item,
                          beforeMonthlyData: { ...item.beforeMonthlyData },
                          afterMonthlyData: { ...item.afterMonthlyData },
                        });
                      }
                    });
                  }
                });

                if (allIndicatorItems.length === 0) {
                  return <Text color="gray.500">{t("features.InvestmentScreening.ProjectProfitablityStatement.message.noIndicatorData")}</Text>;
                }

                return (
                  <Box display={"flex"}>
                    <Box flex={"0 0 auto"} style={{ minWidth: "350px" }}>
                      <Table size={"sm"} variant={"simple"} sx={{ tableLayout: "auto" }} className="mm-ceas-table">
                        <Thead>
                          <Tr>
                            <Th h={50} minW={70}>{t("features.InvestmentScreening.ProjectProfitablityStatement.tableHeader.no")}</Th>
                            <Th h={50} minW={250}>{t("features.InvestmentScreening.ProjectProfitablityStatement.tableHeader.item")}</Th>
                          </Tr>
                        </Thead>
                        <Tbody>
                          {allIndicatorItems.map((item, index) => (
                            <Tr key={item.id || index}>
                              <Td>{index + 1}</Td>
                              <Td>
                                {getItemName(item)}
                              </Td>
                            </Tr>
                          ))}
                        </Tbody>
                      </Table>
                    </Box>
                    <Box flex={1} overflowX={"auto"}>
                      <Table {...defaultTableStyle} className="mm-ceas-table">
                        <Thead>
                          <Tr>
                            {fiscalYearsString.map((_, index) => (
                              <Th key={index} style={{ ...defaultThStyle }}>
                                {t("features.InvestmentScreening.ProjectProfitablityStatement.tableHeader.yearSuffix", { year: index + 1 })}
                              </Th>
                            ))}
                            <Th style={{ ...defaultThStyle }} rowSpan={2}>
                              {t("features.InvestmentScreening.ProjectProfitablityStatement.tableHeader.total")}
                            </Th>
                          </Tr>
                          <Tr>
                            {fiscalYearsString.map((year, index) => (
                              <Th key={index} style={{ ...defaultThStyle }}>
                                {year}
                              </Th>
                            ))}
                          </Tr>
                        </Thead>
                        <Tbody>
                          {allIndicatorItems.map((item, itemIndex) => {
                            const beforeByYear = aggregateMonthlyDataByYear(item.beforeMonthlyData, fiscalYearsString);
                            const afterByYear = aggregateMonthlyDataByYear(item.afterMonthlyData, fiscalYearsString);
                            // 差異 = 合理化後 - 合理化前
                            const differenceByYear = afterByYear.map((after, i) => after - beforeByYear[i]);
                            return (
                              <Tr key={item.id || itemIndex}>
                                {differenceByYear.map((value, yearIndex) => (
                                  <Td key={yearIndex} style={defaultTdStyle}>
                                    {formatNumber(value, "")}
                                  </Td>
                                ))}
                                <Td style={defaultTdStyle}>
                                  {formatNumber(differenceByYear.reduce((a, b) => a + b, 0), "")}
                                </Td>
                              </Tr>
                            );
                          })}
                        </Tbody>
                      </Table>
                    </Box>
                  </Box>
                );
              })()}
            </Box>
          )}

          {/* 改善効果の合計（費用）（合理化の場合） */}
          {capexProjectData?.purpose === PurposeType.Rationalization.code && rationalizationSummaryData.length > 0 && (
            <Box mt={4}>
              <Flex alignItems="center" gap={2}>
                <Heading size={"sm"}>
                  {t("features.InvestmentScreening.ProjectProfitablityStatement.heading.improvementTotalCost")}
                </Heading>
                <RationalizationImprovementTotalExplanationPopover isSimpleMode={isSimpleMode} />
              </Flex>
              {(() => {
                // 項目名取得関数
                const getItemName = (item: RationalizationProfitLossItem): string => {
                  if (item.itemType === "custom" && item.customItemName) {
                    return item.customItemName;
                  }
                  const itemNameMap: Record<string, string> = {
                    productionQuantity: t("features.InvestmentScreening.ProjectProfitablityStatement.improvementItem.productionQuantity"),
                    salesQuantity: t("features.InvestmentScreening.ProjectProfitablityStatement.improvementItem.salesQuantity"),
                    laborCost: t("features.InvestmentScreening.ProjectProfitablityStatement.improvementItem.laborCost"),
                    repairCost: t("features.InvestmentScreening.ProjectProfitablityStatement.improvementItem.repairCost"),
                    consumablesCost: t("features.InvestmentScreening.ProjectProfitablityStatement.improvementItem.consumablesCost"),
                    maintenanceCost: t("features.InvestmentScreening.ProjectProfitablityStatement.improvementItem.maintenanceCost"),
                    scrapCost: t("features.InvestmentScreening.ProjectProfitablityStatement.improvementItem.scrapCost"),
                    totalCost: t("features.InvestmentScreening.ProjectProfitablityStatement.improvementItem.totalCost"),
                    targetEquipmentDepreciation: t("features.InvestmentScreening.ProjectProfitablityStatement.improvementItem.targetEquipmentDepreciation"),
                    otherDepreciation: t("features.InvestmentScreening.ProjectProfitablityStatement.improvementItem.otherDepreciation"),
                  };
                  return itemNameMap[item.itemType] || item.itemType;
                };

                // 月次データを年度ごとに集計する関数
                const aggregateMonthlyDataByYear = (
                  monthlyData: Record<string, number>,
                  fiscalYears: string[]
                ): number[] => {
                  return fiscalYears.map((fiscalYear) => {
                    const yearMatch = fiscalYear.match(/(\d{4})年/);
                    if (!yearMatch) return 0;
                    const endYear = parseInt(yearMatch[1], 10);
                    let total = 0;
                    for (let month = 4; month <= 12; month++) {
                      const key = `${endYear - 1}-${String(month).padStart(2, "0")}`;
                      total += monthlyData[key] || 0;
                    }
                    for (let month = 1; month <= 3; month++) {
                      const key = `${endYear}-${String(month).padStart(2, "0")}`;
                      total += monthlyData[key] || 0;
                    }
                    return total;
                  });
                };

                // 工程ごとの元通貨名取得関数
                const getSourceCurrencyName = (summary: RationalizationSummaryInputData): string => {
                  if (summary.costCurrencyId) {
                    const currency = currencyData.find(c => c.currencyId === summary.costCurrencyId);
                    if (currency?.currencyName) {
                      return currency.currencyName;
                    }
                  }
                  return selectedCurrency.name;
                };

                // 通貨変換関数（工程の通貨から選択通貨へ変換）
                const convertCostValue = (value: number, sourceCurrencyName: string): number => {
                  // 同じ通貨なら変換不要
                  if (sourceCurrencyName === selectedCurrency.name) {
                    return value;
                  }
                  // 通貨変換
                  const converted = convertWithRate(
                    new Decimal(value),
                    sourceCurrencyName,
                    selectedCurrency.name
                  );
                  return converted.toNumber();
                };

                // 全工程の費用項目を集約（通貨変換を適用してからitemTypeごとにグループ化）
                const allCostItems: RationalizationProfitLossItem[] = [];
                rationalizationSummaryData.forEach(summary => {
                  if (summary.costItems) {
                    const sourceCurrencyName = getSourceCurrencyName(summary);
                    summary.costItems.forEach(item => {
                      // 通貨変換を適用した月次データを作成
                      const convertedBeforeMonthlyData: Record<string, number> = {};
                      const convertedAfterMonthlyData: Record<string, number> = {};
                      Object.keys(item.beforeMonthlyData).forEach(key => {
                        convertedBeforeMonthlyData[key] = convertCostValue(item.beforeMonthlyData[key] || 0, sourceCurrencyName);
                      });
                      Object.keys(item.afterMonthlyData).forEach(key => {
                        convertedAfterMonthlyData[key] = convertCostValue(item.afterMonthlyData[key] || 0, sourceCurrencyName);
                      });

                      const existingIndex = allCostItems.findIndex(existing =>
                        item.itemType === "custom"
                          ? existing.itemType === "custom" && existing.customItemName === item.customItemName
                          : existing.itemType === item.itemType
                      );
                      if (existingIndex >= 0) {
                        const existing = allCostItems[existingIndex];
                        Object.keys(convertedBeforeMonthlyData).forEach(key => {
                          existing.beforeMonthlyData[key] = (existing.beforeMonthlyData[key] || 0) + convertedBeforeMonthlyData[key];
                        });
                        Object.keys(convertedAfterMonthlyData).forEach(key => {
                          existing.afterMonthlyData[key] = (existing.afterMonthlyData[key] || 0) + convertedAfterMonthlyData[key];
                        });
                      } else {
                        allCostItems.push({
                          ...item,
                          beforeMonthlyData: { ...convertedBeforeMonthlyData },
                          afterMonthlyData: { ...convertedAfterMonthlyData },
                        });
                      }
                    });
                  }
                });

                if (allCostItems.length === 0) {
                  return <Text color="gray.500">{t("features.InvestmentScreening.ProjectProfitablityStatement.message.noCostData")}</Text>;
                }

                return (
                  <>
                    <Text>
                      unit: {selectedCurrency.name}
                    </Text>
                    <Box display={"flex"}>
                      <Box flex={"0 0 auto"} style={{ minWidth: "350px" }}>
                        <Table size={"sm"} variant={"simple"} sx={{ tableLayout: "auto" }} className="mm-ceas-table">
                          <Thead>
                            <Tr>
                              <Th h={50} minW={70}>{t("features.InvestmentScreening.ProjectProfitablityStatement.tableHeader.no")}</Th>
                              <Th h={50} minW={250}>{t("features.InvestmentScreening.ProjectProfitablityStatement.tableHeader.item")}</Th>
                            </Tr>
                          </Thead>
                          <Tbody>
                            {allCostItems.map((item, index) => (
                              <Tr key={item.id || index}>
                                <Td>{index + 1}</Td>
                                <Td>
                                  {getItemName(item)}
                                </Td>
                              </Tr>
                            ))}
                            {/* 年度ごとの合計行（項目名側）= 損益 */}
                            <Tr>
                              <Td>{allCostItems.length + 1}</Td>
                              <Td>{t("features.InvestmentScreening.ProjectProfitablityStatement.tableHeader.totalProfit")}</Td>
                            </Tr>
                          </Tbody>
                        </Table>
                      </Box>
                      <Box flex={1} overflowX={"auto"}>
                        <Table {...defaultTableStyle} className="mm-ceas-table">
                          <Thead>
                            <Tr>
                              {fiscalYearsString.map((_, index) => (
                                <Th key={index} style={{ ...defaultThStyle }}>
                                  {t("features.InvestmentScreening.ProjectProfitablityStatement.tableHeader.yearSuffix", { year: index + 1 })}
                                </Th>
                              ))}
                              <Th style={{ ...defaultThStyle }} rowSpan={2}>
                                {t("features.InvestmentScreening.ProjectProfitablityStatement.tableHeader.total")}
                              </Th>
                            </Tr>
                            <Tr>
                              {fiscalYearsString.map((year, index) => (
                                <Th key={index} style={{ ...defaultThStyle }}>
                                  {year}
                                </Th>
                              ))}
                            </Tr>
                          </Thead>
                          <Tbody>
                            {allCostItems.map((item, itemIndex) => {
                              const beforeByYear = aggregateMonthlyDataByYear(item.beforeMonthlyData, fiscalYearsString);
                              const afterByYear = aggregateMonthlyDataByYear(item.afterMonthlyData, fiscalYearsString);
                              // 差異 = 合理化後 - 合理化前（費用削減はマイナス）
                              const differenceByYear = afterByYear.map((after, i) => after - beforeByYear[i]);
                              return (
                                <Tr key={item.id || itemIndex}>
                                  {differenceByYear.map((value, yearIndex) => (
                                    <Td key={yearIndex} style={defaultTdStyle}>
                                      {formatNumber(value, selectedCurrency.name)}
                                    </Td>
                                  ))}
                                  <Td style={defaultTdStyle}>
                                    {formatNumber(differenceByYear.reduce((a, b) => a + b, 0), selectedCurrency.name)}
                                  </Td>
                                </Tr>
                              );
                            })}
                            {/* 年度ごとの合計行（全項目の年度別差異を集約） */}
                            {(() => {
                              const totalsByYear = fiscalYearsString.map((_, yearIndex) =>
                                allCostItems.reduce((sum, item) => {
                                  const beforeByYear = aggregateMonthlyDataByYear(item.beforeMonthlyData, fiscalYearsString);
                                  const afterByYear = aggregateMonthlyDataByYear(item.afterMonthlyData, fiscalYearsString);
                                  return sum + (afterByYear[yearIndex] - beforeByYear[yearIndex]);
                                }, 0)
                              );
                              const grandTotal = totalsByYear.reduce((a, b) => a + b, 0);
                              return (
                                <Tr>
                                  {totalsByYear.map((value, yearIndex) => (
                                    <Td key={yearIndex} style={defaultTdStyle}>
                                      {formatNumber(value, selectedCurrency.name)}
                                    </Td>
                                  ))}
                                  <Td style={defaultTdStyle}>
                                    {formatNumber(grandTotal, selectedCurrency.name)}
                                  </Td>
                                </Tr>
                              );
                            })()}
                          </Tbody>
                        </Table>
                      </Box>
                    </Box>
                    <Text fontSize="xs" color="gray.600" mt={2}>
                      {t("features.InvestmentScreening.ProjectProfitablityStatement.message.costImprovementNote")}
                    </Text>
                  </>
                );
              })()}
            </Box>
          )}

          {/* 年度別損益（新規投資の場合：従来表示） */}
          {capexProjectData?.purpose !== PurposeType.UpdateAndOther.code && capexProjectData?.purpose !== PurposeType.Rationalization.code && (
            <Box mt={2}>
              <Flex alignItems="center" gap={2}>
                <Heading size={"sm"}>
                  {t("features.InvestmentScreening.ProjectProfitablityStatement.heading.profitLossByYear")}
                </Heading>
                <ProfitLossExplanationPopover isGeneralPurpose={isGeneralPurpose} />
              </Flex>
              <Text>
                unit: {selectedCurrency.name}
              </Text>
              {(() => {
                const data = [
                  ProfitAndLossItems.totalSales,
                  ProfitAndLossItems.productSales,
                  ProfitAndLossItems.moldRecovery,
                  ProfitAndLossItems.devRecovery,
                  ProfitAndLossItems.equipmentRecovery,
                  ProfitAndLossItems.salesCost,
                  ProfitAndLossItems.materialCost,
                  ProfitAndLossItems.outsideCost,
                  ProfitAndLossItems.laborCost,
                  ProfitAndLossItems.domesticLaborCost,
                  ProfitAndLossItems.factoryExpenses,
                  ProfitAndLossItems.overHeadCost,
                  ProfitAndLossItems.depreciation,
                  ProfitAndLossItems.equipmentDepreciation,
                  ProfitAndLossItems.factoryCost,
                  ...(capexProjectData?.pastProjectId ? [ProfitAndLossItems.pastEquipmentCost] : []),
                  ProfitAndLossItems.otherEquipmentCost,
                  ProfitAndLossItems.domesticFactoryExpenses,
                  ProfitAndLossItems.domesticOverHeadCost,
                  ProfitAndLossItems.domesticDepreciation,
                  ProfitAndLossItems.domesticInvestmentDepreciation,
                  ProfitAndLossItems.domesticFactoryBuildingDepreciation,
                  ProfitAndLossItems.domesticOtherDepreciation,
                  ProfitAndLossItems.scrapCost,
                  ProfitAndLossItems.sgaExpenses,
                  ProfitAndLossItems.appliedLaborCost,
                  ProfitAndLossItems.domesticSgaCost,
                  ProfitAndLossItems.salesManagementCost,
                  ProfitAndLossItems.devCost,
                  ProfitAndLossItems.royalty,
                  ProfitAndLossItems.otherCost,
                  ProfitAndLossItems.operatingProfit,
                  ProfitAndLossItems.operatingProfitRate,
                  // 客先指定部品が存在する場合のみ実質的営業利益率を表示
                  ...(hasCustomerSpecifiedParts ? [ProfitAndLossItems.realOperatingProfitRate] : []),
                ];
                return (
                  <Box display={"flex"}>
                    <Box flex={"0 0 auto"} style={{ minWidth: "350px" }}>
                      <Table size={"sm"} variant={"simple"} sx={{ tableLayout: "auto" }} className="mm-ceas-table">
                        <Thead>
                          <Tr>
                            <Th h={50} minW={70}>{t("features.InvestmentScreening.ProjectProfitablityStatement.tableHeader.no")}</Th>
                            <Th h={50} minW={250}>{t("features.InvestmentScreening.ProjectProfitablityStatement.tableHeader.item")}</Th>
                          </Tr>
                        </Thead>
                        <Tbody>
                          {data.map((item, index) => (
                            <Tr>
                              <Td>{index + 1}</Td>
                              <Td style={{ paddingLeft: `${item.level * 20}px` }}>
                                {item.name}
                              </Td>
                            </Tr>
                          ))}
                        </Tbody>
                      </Table>
                    </Box>
                    <Box flex={1} overflowX={"auto"}>
                      <Table {...defaultTableStyle} className="mm-ceas-table">
                        <Thead>
                          <Tr>
                            {fiscalYearsString.map((_, index) => (
                              <Th style={{ ...defaultThStyle }}>
                                {t("features.InvestmentScreening.ProjectProfitablityStatement.tableHeader.yearSuffix", { year: index + 1 })}
                              </Th>
                            ))}
                            <Th style={{ ...defaultThStyle }} rowSpan={2}>
                              {t("features.InvestmentScreening.ProjectProfitablityStatement.tableHeader.total")}
                            </Th>
                          </Tr>
                          <Tr>
                            {fiscalYearsString.map((year) => (
                              <Th style={{ ...defaultThStyle }}>
                                {year}
                              </Th>
                            ))}
                          </Tr>
                        </Thead>
                        <Tbody>
                          <Tr>
                            {/* 売上合計 */}
                            {fiscalYearsString.map((_, index) => (
                              <Td style={defaultTdStyle}>
                                {formatNumber(displayTotalSalesByYear[index] || 0, selectedCurrency.name)}
                              </Td>
                            ))}
                            <Td style={defaultTdStyle}>
                              {formatNumber(
                                displayTotalSalesByYear.reduce((sum, value) => sum.plus(value), new Decimal(0)),
                                selectedCurrency.name
                              )}
                            </Td>
                          </Tr>
                          <Tr>
                            {/* 製品売上 */}
                            {fiscalYearsString.map((_, index) => (
                              <Td style={defaultTdStyle}>
                                {formatNumber(displayProductSalesByYear[index] || 0, selectedCurrency.name)}
                              </Td>
                            ))}
                            <Td style={defaultTdStyle}>
                              {formatNumber(
                                displayProductSalesByYear.reduce((sum, value) => sum.plus(value), new Decimal(0)),
                                selectedCurrency.name
                              )}
                            </Td>
                          </Tr>
                          <Tr>
                            {/* PJ単位金型回収 */}
                            {fiscalYearsString.map((_, index) => (
                              <Td style={defaultTdStyle}>
                                {formatNumber(displayMoldSalesByYear[index] || 0, selectedCurrency.name)}
                              </Td>
                            ))}
                            <Td style={defaultTdStyle}>
                              {formatNumber(
                                displayMoldSalesByYear.reduce((sum, value) => sum.plus(value), new Decimal(0)),
                                selectedCurrency.name
                              )}
                            </Td>
                          </Tr>
                          <Tr>
                            {/* PJ単位開発費回収 */}
                            {fiscalYearsString.map((_, index) => (
                              <Td style={defaultTdStyle}>
                                {formatNumber(displayDevSalesByYear[index] || 0, selectedCurrency.name)}
                              </Td>
                            ))}
                            <Td style={defaultTdStyle}>
                              {formatNumber(
                                displayDevSalesByYear.reduce((sum, value) => sum.plus(value), new Decimal(0)),
                                selectedCurrency.name
                              )}
                            </Td>
                          </Tr>
                          <Tr>
                            {/* PJ単位設備費回収 */}
                            {fiscalYearsString.map((_, index) => (
                              <Td style={defaultTdStyle}>
                                {formatNumber(displayEquipmentSalesByYear[index] || 0, selectedCurrency.name)}
                              </Td>
                            ))}
                            <Td style={defaultTdStyle}>
                              {formatNumber(
                                displayEquipmentSalesByYear.reduce((sum, value) => sum.plus(value), new Decimal(0)),
                                selectedCurrency.name
                              )}
                            </Td>
                          </Tr>
                          <Tr>
                            {/* 売上原価 */}
                            {fiscalYearsString.map((_, index) => (
                              <Td style={defaultTdStyle}>
                                {formatNumber(displaySalesCostByYear[index] || 0, selectedCurrency.name)}
                              </Td>
                            ))}
                            <Td style={defaultTdStyle}>
                              {formatNumber(
                                displaySalesCostByYear.reduce((sum, value) => sum.plus(value), new Decimal(0)),
                                selectedCurrency.name
                              )}
                            </Td>
                          </Tr>
                          <Tr>
                            {/* 材料費 */}
                            {fiscalYearsString.map((_, index) => (
                              <Td style={defaultTdStyle}>
                                {formatCostSum(displayTotalMaterialCostByYear[index] || 0, selectedCurrency.name, 2)}
                              </Td>
                            ))}
                            <Td style={defaultTdStyle}>
                              {formatCostSum(
                                displayTotalMaterialCostByYear.reduce((sum, value) => sum.plus(value), new Decimal(0)),
                                selectedCurrency.name,
                                2
                              )}
                            </Td>
                          </Tr>
                          <Tr>
                            {/* 外注加工費 */}
                            {fiscalYearsString.map((_, index) => (
                              <Td style={defaultTdStyle}>
                                {formatNumber(displayOutsideCostByYear[index] || 0, selectedCurrency.name)}
                              </Td>
                            ))}
                            <Td style={defaultTdStyle}>
                              {formatNumber(
                                displayOutsideCostByYear.reduce((sum, value) => sum.plus(value), new Decimal(0)),
                                selectedCurrency.name
                              )}
                            </Td>
                          </Tr>
                          <Tr>
                            {/* 労務費 */}
                            {fiscalYearsString.map((_, index) => (
                              <Td style={defaultTdStyle}>
                                {formatNumber(displayLaborCostByYear[index] || 0, selectedCurrency.name)}
                              </Td>
                            ))}
                            <Td style={defaultTdStyle}>
                              {formatNumber(
                                displayLaborCostByYear.reduce((sum, value) => sum.plus(value), new Decimal(0)),
                                selectedCurrency.name
                              )}
                            </Td>
                          </Tr>
                          <Tr>
                            {/* 国内工場労務費 */}
                            {fiscalYearsString.map((_, index) => (
                              <Td style={defaultTdStyle}>
                                {formatNumber(displayDomesticLaborCostByYear[index] || 0, selectedCurrency.name)}
                              </Td>
                            ))}
                            <Td style={defaultTdStyle}>
                              {formatNumber(
                                displayDomesticLaborCostByYear.reduce((sum, value) => sum.plus(value), new Decimal(0)),
                                selectedCurrency.name
                              )}
                            </Td>
                          </Tr>
                          <Tr>
                            {/* 工場経費 */}
                            {fiscalYearsString.map((_, index) => (
                              <Td style={defaultTdStyle}>
                                {formatNumber(displayFactoryExpensesByYear[index] || 0, selectedCurrency.name)}
                              </Td>
                            ))}
                            <Td style={defaultTdStyle}>
                              {formatNumber(
                                displayFactoryExpensesByYear.reduce((sum, value) => sum.plus(value), new Decimal(0)),
                                selectedCurrency.name
                              )}
                            </Td>
                          </Tr>
                          <Tr>
                            {/* 償却費以外の工場経費 */}
                            {fiscalYearsString.map((_, index) => (
                              <Td style={defaultTdStyle}>
                                {formatNumber(displayOverHeadCostByYear[index] || 0, selectedCurrency.name)}
                              </Td>
                            ))}
                            <Td style={defaultTdStyle}>
                              {formatNumber(
                                displayOverHeadCostByYear.reduce((sum, value) => sum.plus(value), new Decimal(0)),
                                selectedCurrency.name
                              )}
                            </Td>
                          </Tr>
                          <Tr>
                            {/* 減価償却費 */}
                            {fiscalYearsString.map((_, index) => (
                              <Td style={defaultTdStyle}>
                                {formatNumber(displayDepreciationByYear[index] || 0, selectedCurrency.name)}
                              </Td>
                            ))}
                            <Td style={defaultTdStyle}>
                              {formatNumber(
                                displayDepreciationByYear.reduce((sum, value) => sum.plus(value), new Decimal(0)),
                                selectedCurrency.name
                              )}
                            </Td>
                          </Tr>
                          <Tr>
                            {/* 投資対象設備 */}
                            {fiscalYearsString.map((_, index) => (
                              <Td style={defaultTdStyle}>
                                {formatNumber(displayEquipmentDepreciationByYear[index] || 0, selectedCurrency.name)}
                              </Td>
                            ))}
                            <Td style={defaultTdStyle}>
                              {formatNumber(
                                displayEquipmentDepreciationByYear.reduce((sum, value) => sum.plus(value), new Decimal(0)),
                                selectedCurrency.name
                              )}
                            </Td>
                          </Tr>
                          <Tr>
                            {/* 工場建物 */}
                            {fiscalYearsString.map((_, index) => (
                              <Td style={defaultTdStyle}>
                                {formatNumber(displayFactoryCostByYear[index] || 0, selectedCurrency.name)}
                              </Td>
                            ))}
                            <Td style={defaultTdStyle}>
                              {formatNumber(
                                displayFactoryCostByYear.reduce((sum, value) => sum.plus(value), new Decimal(0)),
                                selectedCurrency.name
                              )}
                            </Td>
                          </Tr>
                          {capexProjectData?.pastProjectId && (
                            <Tr>
                              {/* 過去PJの投資対象設備 */}
                              {fiscalYearsString.map((_, index) => (
                                <Td style={defaultTdStyle}>
                                  {formatNumber(displayPastEquipmentCostByYear[index] || 0, selectedCurrency.name)}
                                </Td>
                              ))}
                              <Td style={defaultTdStyle}>
                                {formatNumber(
                                  displayPastEquipmentCostByYear.reduce((sum, value) => sum.plus(value), new Decimal(0)),
                                  selectedCurrency.name
                                )}
                              </Td>
                            </Tr>
                          )}
                          <Tr>
                            {/* その他の設備 */}
                            {fiscalYearsString.map((_, index) => (
                              <Td style={defaultTdStyle}>
                                {formatNumber(displayOtherEquipmentCostByYear[index] || 0, selectedCurrency.name)}
                              </Td>
                            ))}
                            <Td style={defaultTdStyle}>
                              {formatNumber(
                                displayOtherEquipmentCostByYear.reduce((sum, value) => sum.plus(value), new Decimal(0)),
                                selectedCurrency.name
                              )}
                            </Td>
                          </Tr>
                          <Tr>
                            {/* 国内工場経費 */}
                            {fiscalYearsString.map((_, index) => (
                              <Td style={defaultTdStyle}>
                                {formatNumber(displayDomesticFactoryExpensesByYear[index] || 0, selectedCurrency.name)}
                              </Td>
                            ))}
                            <Td style={defaultTdStyle}>
                              {formatNumber(
                                displayDomesticFactoryExpensesByYear.reduce((sum, value) => sum.plus(value), new Decimal(0)),
                                selectedCurrency.name
                              )}
                            </Td>
                          </Tr>
                          <Tr>
                            {/* 国内工場経費-償却費以外の工場経費 */}
                            {fiscalYearsString.map((_, index) => (
                              <Td style={defaultTdStyle}>
                                {formatNumber(displayDomesticOverHeadCostByYear[index] || 0, selectedCurrency.name)}
                              </Td>
                            ))}
                            <Td style={defaultTdStyle}>
                              {formatNumber(
                                displayDomesticOverHeadCostByYear.reduce((sum, value) => sum.plus(value), new Decimal(0)),
                                selectedCurrency.name
                              )}
                            </Td>
                          </Tr>
                          <Tr>
                            {/* 国内工場経費-減価償却費 */}
                            {fiscalYearsString.map((_, index) => (
                              <Td style={defaultTdStyle}>
                                {formatNumber(displayDomesticDepreciationByYear[index] || 0, selectedCurrency.name)}
                              </Td>
                            ))}
                            <Td style={defaultTdStyle}>
                              {formatNumber(
                                displayDomesticDepreciationByYear.reduce((sum, value) => sum.plus(value), new Decimal(0)),
                                selectedCurrency.name
                              )}
                            </Td>
                          </Tr>
                          <Tr>
                            {/* 国内工場経費-投資対象設備 */}
                            {fiscalYearsString.map((_, index) => (
                              <Td style={defaultTdStyle}>
                                {formatNumber(displayDomesticInvestmentDepreciationByYear[index] || 0, selectedCurrency.name)}
                              </Td>
                            ))}
                            <Td style={defaultTdStyle}>
                              {formatNumber(
                                displayDomesticInvestmentDepreciationByYear.reduce((sum, value) => sum.plus(value), new Decimal(0)),
                                selectedCurrency.name
                              )}
                            </Td>
                          </Tr>
                          <Tr>
                            {/* 国内工場経費-工場建物 */}
                            {fiscalYearsString.map((_, index) => (
                              <Td style={defaultTdStyle}>
                                {formatNumber(displayDomesticFactoryBuildingDepreciationByYear[index] || 0, selectedCurrency.name)}
                              </Td>
                            ))}
                            <Td style={defaultTdStyle}>
                              {formatNumber(
                                displayDomesticFactoryBuildingDepreciationByYear.reduce((sum, value) => sum.plus(value), new Decimal(0)),
                                selectedCurrency.name
                              )}
                            </Td>
                          </Tr>
                          <Tr>
                            {/* 国内工場経費-その他の設備 */}
                            {fiscalYearsString.map((_, index) => (
                              <Td style={defaultTdStyle}>
                                {formatNumber(displayDomesticOtherDepreciationByYear[index] || 0, selectedCurrency.name)}
                              </Td>
                            ))}
                            <Td style={defaultTdStyle}>
                              {formatNumber(
                                displayDomesticOtherDepreciationByYear.reduce((sum, value) => sum.plus(value), new Decimal(0)),
                                selectedCurrency.name
                              )}
                            </Td>
                          </Tr>
                          <Tr>
                            {/* 仕損費 */}
                            {fiscalYearsString.map((_, index) => (
                              <Td style={defaultTdStyle}>
                                {formatNumber(displayScrapCostByYear[index] || 0, selectedCurrency.name)}
                              </Td>
                            ))}
                            <Td style={defaultTdStyle}>
                              {formatNumber(
                                displayScrapCostByYear.reduce((sum, value) => sum.plus(value), new Decimal(0)),
                                selectedCurrency.name
                              )}
                            </Td>
                          </Tr>
                          <Tr>
                            {/* 販売費及び一般管理費 */}
                            {fiscalYearsString.map((_, index) => (
                              <Td style={defaultTdStyle}>
                                {formatNumber(displaySGAExpensesByYear[index] || 0, selectedCurrency.name)}
                              </Td>
                            ))}
                            <Td style={defaultTdStyle}>
                              {formatNumber(
                                displaySGAExpensesByYear.reduce((sum, value) => sum.plus(value), new Decimal(0)),
                                selectedCurrency.name
                              )}
                            </Td>
                          </Tr>
                          <Tr>
                            {/* 工場販管費 */}
                            {fiscalYearsString.map((_, index) => (
                              <Td style={defaultTdStyle}>
                                {formatNumber(displayAppliedLaborCostByYear[index] || 0, selectedCurrency.name)}
                              </Td>
                            ))}
                            <Td style={defaultTdStyle}>
                              {formatNumber(
                                displayAppliedLaborCostByYear.reduce((sum, value) => sum.plus(value), new Decimal(0)),
                                selectedCurrency.name
                              )}
                            </Td>
                          </Tr>
                          <Tr>
                            {/* 国内工場販管費 */}
                            {fiscalYearsString.map((_, index) => (
                              <Td style={defaultTdStyle}>
                                {formatNumber(displayDomesticSgaCostByYear[index] || 0, selectedCurrency.name)}
                              </Td>
                            ))}
                            <Td style={defaultTdStyle}>
                              {formatNumber(
                                displayDomesticSgaCostByYear.reduce((sum, value) => sum.plus(value), new Decimal(0)),
                                selectedCurrency.name
                              )}
                            </Td>
                          </Tr>
                          <Tr>
                            {/* 本社・販社販管費 */}
                            {fiscalYearsString.map((_, index) => (
                              <Td style={defaultTdStyle}>
                                {formatNumber(displaySalesManagementCostByYear[index] || 0, selectedCurrency.name)}
                              </Td>
                            ))}
                            <Td style={defaultTdStyle}>
                              {formatNumber(
                                displaySalesManagementCostByYear.reduce((sum, value) => sum.plus(value), new Decimal(0)),
                                selectedCurrency.name
                              )}
                            </Td>
                          </Tr>
                          <Tr>
                            {/* 開発費 */}
                            {fiscalYearsString.map((_, index) => (
                              <Td style={defaultTdStyle}>
                                {formatNumber(displayDevCostByYear[index] || 0, selectedCurrency.name)}
                              </Td>
                            ))}
                            <Td style={defaultTdStyle}>
                              {formatNumber(
                                displayDevCostByYear.reduce((sum, value) => sum.plus(value), new Decimal(0)),
                                selectedCurrency.name
                              )}
                            </Td>
                          </Tr>
                          <Tr>
                            {/* ロイヤリティ */}
                            {fiscalYearsString.map((_, index) => (
                              <Td style={defaultTdStyle}>
                                {formatNumber(displayRoyaltyByYear[index] || 0, selectedCurrency.name)}
                              </Td>
                            ))}
                            <Td style={defaultTdStyle}>
                              {formatNumber(
                                displayRoyaltyByYear.reduce((sum, value) => sum.plus(value), new Decimal(0)),
                                selectedCurrency.name
                              )}
                            </Td>
                          </Tr>
                          <Tr>
                            {/* その他の経費 */}
                            {fiscalYearsString.map((_, index) => (
                              <Td style={defaultTdStyle}>
                                {formatNumber(displayOtherCostByYear[index] || 0, selectedCurrency.name)}
                              </Td>
                            ))}
                            <Td style={defaultTdStyle}>
                              {formatNumber(
                                displayOtherCostByYear.reduce((sum, value) => sum.plus(value), new Decimal(0)),
                                selectedCurrency.name
                              )}
                            </Td>
                          </Tr>
                          <Tr>
                            {/* 営業利益 */}
                            {fiscalYearsString.map((_, index) => (
                              <Td style={defaultTdStyle}>
                                {formatNumber(displayProfitByYear[index] || 0, selectedCurrency.name)}
                              </Td>
                            ))}
                            <Td style={defaultTdStyle}>
                              {formatNumber(
                                displayProfitByYear.reduce((sum, value) => sum.plus(value), new Decimal(0)),
                                selectedCurrency.name
                              )}
                            </Td>
                          </Tr>
                          <Tr>
                            {/* 営業利益率（表示通貨ベースで計算、Excel出力と一致させる） */}
                            {fiscalYearsString.map((_, index) => (
                              <Td style={defaultTdStyle}>
                                {(displayProfitRateByYear[index] || new Decimal(0))?.toFixed(1)?.replace(/\B(?=(\d{3})+(?!\d))/g, ',') + "%"}
                              </Td>
                            ))}
                            <Td style={defaultTdStyle}>
                              {(() => {
                                const totalProfit = displayProfitByYear.reduce((sum, value) => sum.plus(value), new Decimal(0));
                                const totalSales = displayTotalSalesByYear.reduce((sum, value) => sum.plus(value), new Decimal(0));
                                const totalProfitRate = calcProfitRate(totalSales, totalProfit);
                                return `${totalProfitRate?.toFixed(1)}%`;
                              })()}
                            </Td>
                          </Tr>
                          {/* 客先指定部品が存在する場合のみ実質的営業利益率を表示 */}
                          {hasCustomerSpecifiedParts && (
                            <Tr>
                              {/* 実質的営業利益率（表示通貨ベースで計算、Excel出力と一致させる） */}
                              {fiscalYearsString.map((_, index) => (
                                <Td style={defaultTdStyle}>
                                  {(displayRealOperatingProfitRateByYear[index] || new Decimal(0))?.toFixed(1)?.replace(/\B(?=(\d{3})+(?!\d))/g, ',') + "%"}
                                </Td>
                              ))}
                              <Td style={defaultTdStyle}>
                                {(() => {
                                  // 実質的営業利益率の合計 = 合計営業利益 / 合計実質売上（表示通貨ベース）
                                  const totalProfit = displayProfitByYear.reduce((sum, value) => sum.plus(value), new Decimal(0));
                                  const totalSales = displayTotalSalesByYear.reduce((sum, value) => sum.plus(value), new Decimal(0));
                                  // 客先指定部品材料費は月次データから計算（JPY版）
                                  const totalCustomerSpecifiedPartsCost = displayMonthlyProfitAndLossData?.customerSpecifiedPartsCost
                                    ?.reduce((sum, v) => sum.plus(v || new Decimal(0)), new Decimal(0)) || new Decimal(0);
                                  const totalRealSales = totalSales.minus(totalCustomerSpecifiedPartsCost);
                                  if (totalRealSales.isZero()) {
                                    return "0.0%";
                                  }
                                  const totalRealProfitRate = totalProfit.div(totalRealSales).times(100);
                                  return `${totalRealProfitRate?.toFixed(1)}%`;
                                })()}
                              </Td>
                            </Tr>
                          )}
                        </Tbody>
                      </Table>
                    </Box>
                  </Box>
                )
              })()}
            </Box>
          )}

          {/* {t("features.InvestmentScreening.ProjectProfitablityStatement.heading.improvementByUpdate")}（更新投資の場合のみ表示） */}
          {capexProjectData?.purpose === PurposeType.UpdateAndOther.code && updateSummaryData.length > 0 && (() => {
            // 簡易モードの場合は合理化と同様の改善効果テーブルを表示
            if (isSimpleMode) {
              // updateSummaryData から直接改善効果を計算（合理化IIFEと同様のアプローチ）
              const getUpItemName = (item: RationalizationProfitLossItem): string => {
                if (item.itemType === "custom" && item.customItemName) return item.customItemName;
                const nameMap: Record<string, string> = {
                  productionQuantity: t("features.InvestmentScreening.ProjectProfitablityStatement.improvementItem.productionQuantity"),
                  salesQuantity: t("features.InvestmentScreening.ProjectProfitablityStatement.improvementItem.salesQuantity"),
                  laborCost: t("features.InvestmentScreening.ProjectProfitablityStatement.improvementItem.laborCost"),
                  repairCost: t("features.InvestmentScreening.ProjectProfitablityStatement.improvementItem.repairCost"),
                  consumablesCost: t("features.InvestmentScreening.ProjectProfitablityStatement.improvementItem.consumablesCost"),
                  maintenanceCost: t("features.InvestmentScreening.ProjectProfitablityStatement.improvementItem.maintenanceCost"),
                  scrapCost: t("features.InvestmentScreening.ProjectProfitablityStatement.improvementItem.scrapCost"),
                  totalCost: t("features.InvestmentScreening.ProjectProfitablityStatement.improvementItem.totalCost"),
                  targetEquipmentDepreciation: t("features.InvestmentScreening.ProjectProfitablityStatement.improvementItem.targetEquipmentDepreciation"),
                  otherDepreciation: t("features.InvestmentScreening.ProjectProfitablityStatement.improvementItem.otherDepreciation"),
                };
                return nameMap[item.itemType] || item.itemType;
              };

              const aggUpByYear = (monthlyData: Record<string, number>): number[] => {
                return fiscalYearsString.map((fiscalYear) => {
                  const yearMatch = fiscalYear.match(/(\d{4})年/);
                  if (!yearMatch) return 0;
                  const endYear = parseInt(yearMatch[1], 10);
                  let total = 0;
                  for (let m = 4; m <= 12; m++) total += monthlyData[`${endYear - 1}-${String(m).padStart(2, "0")}`] || 0;
                  for (let m = 1; m <= 3; m++) total += monthlyData[`${endYear}-${String(m).padStart(2, "0")}`] || 0;
                  return total;
                });
              };

              const upDisplayCurrencyName = selectedCurrency?.name || CurrencyNameJPY;
              const convUpCostToDisplay = (value: number, srcCurrencyId: string | undefined): number => {
                if (!srcCurrencyId) return value;
                const src = currencyData.find(c => c.currencyId === srcCurrencyId);
                if (!src?.currencyName || src.currencyName === upDisplayCurrencyName) return value;
                return convertWithRate(new Decimal(value), src.currencyName, upDisplayCurrencyName).toNumber();
              };

              // 改善効果（指標）- 工程別
              const indicatorByProcess: RationalizationImprovementEffectByProcess[] = updateSummaryData
                .filter(s => s.indicatorItems && s.indicatorItems.length > 0)
                .map(s => ({
                  processId: s.productionLineId || "",
                  processName: s.productionLineName || "",
                  items: s.indicatorItems!.map(item => {
                    const before = aggUpByYear(item.beforeMonthlyData);
                    const after = aggUpByYear(item.afterMonthlyData);
                    return {
                      itemType: item.itemType,
                      itemName: getUpItemName(item),
                      beforeByYear: before.map(v => String(v)),
                      beforeTotal: String(before.reduce((sum, v) => sum + v, 0)),
                      afterByYear: after.map(v => String(v)),
                      afterTotal: String(after.reduce((sum, v) => sum + v, 0)),
                    };
                  }),
                }));

              // 改善効果（費用）- 工程別（表示通貨版）
              const costByProcess: RationalizationImprovementEffectByProcess[] = updateSummaryData
                .filter(s => s.costItems && s.costItems.length > 0)
                .map(s => ({
                  processId: s.productionLineId || "",
                  processName: s.productionLineName || "",
                  items: s.costItems!.map(item => {
                    const before = aggUpByYear(item.beforeMonthlyData).map(v => convUpCostToDisplay(v, s.costCurrencyId));
                    const after = aggUpByYear(item.afterMonthlyData).map(v => convUpCostToDisplay(v, s.costCurrencyId));
                    return {
                      itemType: item.itemType,
                      itemName: getUpItemName(item),
                      beforeByYear: before.map(v => String(v)),
                      beforeTotal: String(before.reduce((sum, v) => sum + v, 0)),
                      afterByYear: after.map(v => String(v)),
                      afterTotal: String(after.reduce((sum, v) => sum + v, 0)),
                    };
                  }),
                }));

              // 改善効果の合計（指標）- 全工程集約
              const indicatorTotalMap = new Map<string, { itemType: string; itemName: string; before: number[]; after: number[] }>();
              updateSummaryData.forEach(s => {
                if (s.indicatorItems) {
                  s.indicatorItems.forEach(item => {
                    const key = item.itemType === "custom" ? `custom_${item.customItemName}` : item.itemType;
                    const before = aggUpByYear(item.beforeMonthlyData);
                    const after = aggUpByYear(item.afterMonthlyData);
                    if (indicatorTotalMap.has(key)) {
                      const ex = indicatorTotalMap.get(key)!;
                      ex.before = ex.before.map((v, i) => v + (before[i] || 0));
                      ex.after = ex.after.map((v, i) => v + (after[i] || 0));
                    } else {
                      indicatorTotalMap.set(key, { itemType: item.itemType, itemName: getUpItemName(item), before, after });
                    }
                  });
                }
              });
              const totalIndicator: RationalizationImprovementTotalItem[] = Array.from(indicatorTotalMap.values()).map(item => {
                const diff = item.after.map((a, i) => a - item.before[i]);
                return {
                  itemType: item.itemType,
                  itemName: item.itemName,
                  differenceByYear: diff.map(v => String(v)),
                  differenceTotal: String(diff.reduce((sum, v) => sum + v, 0)),
                };
              });

              // 改善効果の合計（費用）- 全工程集約（表示通貨版）
              const costTotalMap = new Map<string, { itemType: string; itemName: string; before: number[]; after: number[] }>();
              updateSummaryData.forEach(s => {
                if (s.costItems) {
                  s.costItems.forEach(item => {
                    const key = item.itemType === "custom" ? `custom_${item.customItemName}` : item.itemType;
                    const before = aggUpByYear(item.beforeMonthlyData).map(v => convUpCostToDisplay(v, s.costCurrencyId));
                    const after = aggUpByYear(item.afterMonthlyData).map(v => convUpCostToDisplay(v, s.costCurrencyId));
                    if (costTotalMap.has(key)) {
                      const ex = costTotalMap.get(key)!;
                      ex.before = ex.before.map((v, i) => v + (before[i] || 0));
                      ex.after = ex.after.map((v, i) => v + (after[i] || 0));
                    } else {
                      costTotalMap.set(key, { itemType: item.itemType, itemName: getUpItemName(item), before, after });
                    }
                  });
                }
              });
              const totalCost: RationalizationImprovementTotalItem[] = Array.from(costTotalMap.values()).map(item => {
                const diff = item.after.map((a, i) => a - item.before[i]);
                return {
                  itemType: item.itemType,
                  itemName: item.itemName,
                  differenceByYear: diff.map(v => String(v)),
                  differenceTotal: String(diff.reduce((sum, v) => sum + v, 0)),
                };
              });

              return (
                <>
                  {/* 改善効果（指標）- 工程別 */}
                  {indicatorByProcess.length > 0 && (
                    <Box mt={4}>
                      <Flex alignItems="center" gap={2}>
                        <Heading size={"sm"}>
                          {t("features.InvestmentScreening.ProjectProfitablityStatement.heading.improvementByUpdateIndicator")}
                        </Heading>
                      </Flex>
                      <Text mb={1}>unit: pcs</Text>
                      {indicatorByProcess.map((process, processIndex) => (
                        <Box key={process.processId || processIndex} mb={4}>
                          <Text fontWeight="bold" mt={2} mb={1} textAlign="center">{process.processName}</Text>
                          <Box display={"flex"}>
                            <Box flex={"0 0 auto"} style={{ minWidth: "350px" }}>
                              <Table size={"sm"} variant={"simple"} sx={{ tableLayout: "auto" }} className="mm-ceas-table">
                                <Thead>
                                  <Tr>
                                    <Th h={50} minW={70}>{t("features.InvestmentScreening.ProjectProfitablityStatement.tableHeader.no")}</Th>
                                    <Th h={50} minW={180}>{t("features.InvestmentScreening.ProjectProfitablityStatement.tableHeader.item")}</Th>
                                    <Th h={50} minW={70}>{t("features.InvestmentScreening.ProjectProfitablityStatement.tableHeader.classification")}</Th>
                                  </Tr>
                                </Thead>
                                <Tbody>
                                  {process.items.map((item, index) => (
                                    <React.Fragment key={index}>
                                      <Tr>
                                        <Td rowSpan={2}>{index + 1}</Td>
                                        <Td rowSpan={2}>{item.itemName}</Td>
                                        <Td whiteSpace="nowrap">{t("features.InvestmentScreening.ProjectProfitablityStatement.label.beforeUpdate")}</Td>
                                      </Tr>
                                      <Tr>
                                        <Td whiteSpace="nowrap">{t("features.InvestmentScreening.ProjectProfitablityStatement.label.afterUpdate")}</Td>
                                      </Tr>
                                    </React.Fragment>
                                  ))}
                                </Tbody>
                              </Table>
                            </Box>
                            <Box flex={1} overflowX={"auto"}>
                              <Table {...defaultTableStyle} className="mm-ceas-table">
                                <Thead>
                                  <Tr>
                                    {fiscalYearsString.map((_, index) => (
                                      <Th key={index} style={{ ...defaultThStyle }}>
                                        {t("features.InvestmentScreening.ProjectProfitablityStatement.tableHeader.yearSuffix", { year: index + 1 })}
                                      </Th>
                                    ))}
                                    <Th style={{ ...defaultThStyle }}>{t("features.InvestmentScreening.ProjectProfitablityStatement.tableHeader.total")}</Th>
                                  </Tr>
                                  <Tr>
                                    {fiscalYearsString.map((year, index) => (
                                      <Th key={index} style={{ ...defaultThStyle }}>{year}</Th>
                                    ))}
                                    <Th style={{ ...defaultThStyle }}></Th>
                                  </Tr>
                                </Thead>
                                <Tbody>
                                  {process.items.map((item, itemIndex) => (
                                    <React.Fragment key={itemIndex}>
                                      <Tr>
                                        {item.beforeByYear.map((value, yearIndex) => (
                                          <Td key={yearIndex} style={defaultTdStyle}>{formatNumber(parseFloat(value), "")}</Td>
                                        ))}
                                        <Td style={defaultTdStyle}>{formatNumber(parseFloat(item.beforeTotal), "")}</Td>
                                      </Tr>
                                      <Tr>
                                        {item.afterByYear.map((value, yearIndex) => (
                                          <Td key={yearIndex} style={defaultTdStyle}>{formatNumber(parseFloat(value), "")}</Td>
                                        ))}
                                        <Td style={defaultTdStyle}>{formatNumber(parseFloat(item.afterTotal), "")}</Td>
                                      </Tr>
                                    </React.Fragment>
                                  ))}
                                </Tbody>
                              </Table>
                            </Box>
                          </Box>
                        </Box>
                      ))}
                    </Box>
                  )}

                  {/* 改善効果（費用）- 工程別 */}
                  {costByProcess.length > 0 && (
                    <Box mt={4}>
                      <Flex alignItems="center" gap={2}>
                        <Heading size={"sm"}>
                          {t("features.InvestmentScreening.ProjectProfitablityStatement.heading.improvementByUpdateCost")}
                        </Heading>
                      </Flex>
                      <Text mb={1}>unit: {selectedCurrency?.name || "JPY"}</Text>
                      {costByProcess.map((process, processIndex) => (
                        <Box key={process.processId || processIndex} mb={4}>
                          <Text fontWeight="bold" mt={2} mb={1} textAlign="center">{process.processName}</Text>
                          <Box display={"flex"}>
                            <Box flex={"0 0 auto"} style={{ minWidth: "350px" }}>
                              <Table size={"sm"} variant={"simple"} sx={{ tableLayout: "auto" }} className="mm-ceas-table">
                                <Thead>
                                  <Tr>
                                    <Th h={50} minW={70}>{t("features.InvestmentScreening.ProjectProfitablityStatement.tableHeader.no")}</Th>
                                    <Th h={50} minW={180}>{t("features.InvestmentScreening.ProjectProfitablityStatement.tableHeader.item")}</Th>
                                    <Th h={50} minW={70}>{t("features.InvestmentScreening.ProjectProfitablityStatement.tableHeader.classification")}</Th>
                                  </Tr>
                                </Thead>
                                <Tbody>
                                  {process.items.map((item, index) => (
                                    <React.Fragment key={index}>
                                      <Tr>
                                        <Td rowSpan={2}>{index + 1}</Td>
                                        <Td rowSpan={2}>{item.itemName}</Td>
                                        <Td whiteSpace="nowrap">{t("features.InvestmentScreening.ProjectProfitablityStatement.label.beforeUpdate")}</Td>
                                      </Tr>
                                      <Tr>
                                        <Td whiteSpace="nowrap">{t("features.InvestmentScreening.ProjectProfitablityStatement.label.afterUpdate")}</Td>
                                      </Tr>
                                    </React.Fragment>
                                  ))}
                                </Tbody>
                              </Table>
                            </Box>
                            <Box flex={1} overflowX={"auto"}>
                              <Table {...defaultTableStyle} className="mm-ceas-table">
                                <Thead>
                                  <Tr>
                                    {fiscalYearsString.map((_, index) => (
                                      <Th key={index} style={{ ...defaultThStyle }}>
                                        {t("features.InvestmentScreening.ProjectProfitablityStatement.tableHeader.yearSuffix", { year: index + 1 })}
                                      </Th>
                                    ))}
                                    <Th style={{ ...defaultThStyle }}>{t("features.InvestmentScreening.ProjectProfitablityStatement.tableHeader.total")}</Th>
                                  </Tr>
                                  <Tr>
                                    {fiscalYearsString.map((year, index) => (
                                      <Th key={index} style={{ ...defaultThStyle }}>{year}</Th>
                                    ))}
                                    <Th style={{ ...defaultThStyle }}></Th>
                                  </Tr>
                                </Thead>
                                <Tbody>
                                  {process.items.map((item, itemIndex) => (
                                    <React.Fragment key={itemIndex}>
                                      <Tr>
                                        {item.beforeByYear.map((value, yearIndex) => (
                                          <Td key={yearIndex} style={defaultTdStyle}>{formatNumber(parseFloat(value), selectedCurrency?.name || "JPY")}</Td>
                                        ))}
                                        <Td style={defaultTdStyle}>{formatNumber(parseFloat(item.beforeTotal), selectedCurrency?.name || "JPY")}</Td>
                                      </Tr>
                                      <Tr>
                                        {item.afterByYear.map((value, yearIndex) => (
                                          <Td key={yearIndex} style={defaultTdStyle}>{formatNumber(parseFloat(value), selectedCurrency?.name || "JPY")}</Td>
                                        ))}
                                        <Td style={defaultTdStyle}>{formatNumber(parseFloat(item.afterTotal), selectedCurrency?.name || "JPY")}</Td>
                                      </Tr>
                                    </React.Fragment>
                                  ))}
                                </Tbody>
                              </Table>
                            </Box>
                          </Box>
                        </Box>
                      ))}
                    </Box>
                  )}

                  {/* 改善効果の合計（指標） */}
                  {totalIndicator.length > 0 && (
                    <Box mt={4}>
                      <Heading size={"sm"} mb={2}>
                        {t("features.InvestmentScreening.ProjectProfitablityStatement.heading.improvementTotalIndicator")}
                      </Heading>
                      <Text mb={1}>unit: pcs</Text>
                      <Box display={"flex"}>
                        <Box flex={"0 0 auto"} style={{ minWidth: "350px" }}>
                          <Table size={"sm"} variant={"simple"} sx={{ tableLayout: "auto" }} className="mm-ceas-table">
                            <Thead>
                              <Tr>
                                <Th h={50} minW={70}>{t("features.InvestmentScreening.ProjectProfitablityStatement.tableHeader.no")}</Th>
                                <Th h={50} minW={250}>{t("features.InvestmentScreening.ProjectProfitablityStatement.tableHeader.item")}</Th>
                              </Tr>
                            </Thead>
                            <Tbody>
                              {totalIndicator.map((item, index) => (
                                <Tr key={index}>
                                  <Td>{index + 1}</Td>
                                  <Td>{item.itemName}</Td>
                                </Tr>
                              ))}
                            </Tbody>
                          </Table>
                        </Box>
                        <Box flex={1} overflowX={"auto"}>
                          <Table {...defaultTableStyle} className="mm-ceas-table">
                            <Thead>
                              <Tr>
                                {fiscalYearsString.map((_, index) => (
                                  <Th key={index} style={{ ...defaultThStyle }}>
                                    {t("features.InvestmentScreening.ProjectProfitablityStatement.tableHeader.yearSuffix", { year: index + 1 })}
                                  </Th>
                                ))}
                                <Th style={{ ...defaultThStyle }}>{t("features.InvestmentScreening.ProjectProfitablityStatement.tableHeader.total")}</Th>
                              </Tr>
                              <Tr>
                                {fiscalYearsString.map((year, index) => (
                                  <Th key={index} style={{ ...defaultThStyle }}>{year}</Th>
                                ))}
                                <Th style={{ ...defaultThStyle }}></Th>
                              </Tr>
                            </Thead>
                            <Tbody>
                              {totalIndicator.map((item, itemIndex) => (
                                <Tr key={itemIndex}>
                                  {item.differenceByYear.map((value, yearIndex) => (
                                    <Td key={yearIndex} style={defaultTdStyle}>{formatNumber(parseFloat(value), "")}</Td>
                                  ))}
                                  <Td style={defaultTdStyle}>{formatNumber(parseFloat(item.differenceTotal), "")}</Td>
                                </Tr>
                              ))}
                            </Tbody>
                          </Table>
                        </Box>
                      </Box>
                    </Box>
                  )}

                  {/* 改善効果の合計（費用） */}
                  {totalCost.length > 0 && (
                    <Box mt={4}>
                      <Heading size={"sm"} mb={2}>
                        {t("features.InvestmentScreening.ProjectProfitablityStatement.heading.improvementTotalCost")}
                      </Heading>
                      <Text mb={1}>unit: {selectedCurrency?.name || "JPY"}</Text>
                      <Box display={"flex"}>
                        <Box flex={"0 0 auto"} style={{ minWidth: "350px" }}>
                          <Table size={"sm"} variant={"simple"} sx={{ tableLayout: "auto" }} className="mm-ceas-table">
                            <Thead>
                              <Tr>
                                <Th h={50} minW={70}>{t("features.InvestmentScreening.ProjectProfitablityStatement.tableHeader.no")}</Th>
                                <Th h={50} minW={250}>{t("features.InvestmentScreening.ProjectProfitablityStatement.tableHeader.item")}</Th>
                              </Tr>
                            </Thead>
                            <Tbody>
                              {totalCost.map((item, index) => (
                                <Tr key={index}>
                                  <Td>{index + 1}</Td>
                                  <Td>{item.itemName}</Td>
                                </Tr>
                              ))}
                              {/* 合計行（損益） */}
                              <Tr>
                                <Td>{totalCost.length + 1}</Td>
                                <Td>{t("features.InvestmentScreening.ProjectProfitablityStatement.tableHeader.totalProfit")}</Td>
                              </Tr>
                            </Tbody>
                          </Table>
                        </Box>
                        <Box flex={1} overflowX={"auto"}>
                          <Table {...defaultTableStyle} className="mm-ceas-table">
                            <Thead>
                              <Tr>
                                {fiscalYearsString.map((_, index) => (
                                  <Th key={index} style={{ ...defaultThStyle }}>
                                    {t("features.InvestmentScreening.ProjectProfitablityStatement.tableHeader.yearSuffix", { year: index + 1 })}
                                  </Th>
                                ))}
                                <Th style={{ ...defaultThStyle }}>{t("features.InvestmentScreening.ProjectProfitablityStatement.tableHeader.total")}</Th>
                              </Tr>
                              <Tr>
                                {fiscalYearsString.map((year, index) => (
                                  <Th key={index} style={{ ...defaultThStyle }}>{year}</Th>
                                ))}
                                <Th style={{ ...defaultThStyle }}></Th>
                              </Tr>
                            </Thead>
                            <Tbody>
                              {totalCost.map((item, itemIndex) => (
                                <Tr key={itemIndex}>
                                  {item.differenceByYear.map((value, yearIndex) => (
                                    <Td key={yearIndex} style={defaultTdStyle}>{formatNumber(parseFloat(value), selectedCurrency?.name || "JPY")}</Td>
                                  ))}
                                  <Td style={defaultTdStyle}>{formatNumber(parseFloat(item.differenceTotal), selectedCurrency?.name || "JPY")}</Td>
                                </Tr>
                              ))}
                              {/* 年度ごとの合計行（全項目の年度別差異を集約） */}
                              {(() => {
                                const totalsByYear = fiscalYearsString.map((_, yearIndex) =>
                                  totalCost.reduce((sum, item) => sum + parseFloat(item.differenceByYear[yearIndex] || "0"), 0)
                                );
                                const grandTotal = totalsByYear.reduce((a, b) => a + b, 0);
                                return (
                                  <Tr>
                                    {totalsByYear.map((value, yearIndex) => (
                                      <Td key={yearIndex} style={defaultTdStyle}>{formatNumber(value, selectedCurrency?.name || "JPY")}</Td>
                                    ))}
                                    <Td style={defaultTdStyle}>{formatNumber(grandTotal, selectedCurrency?.name || "JPY")}</Td>
                                  </Tr>
                                );
                              })()}
                            </Tbody>
                          </Table>
                        </Box>
                      </Box>
                    </Box>
                  )}
                </>
              );
            }

            // 詳細モード: 既存の改善効果テーブル
            const data = [
              { name: t("features.InvestmentScreening.ProjectProfitablityStatement.improvementItem.yield"), level: 1 },
              { name: t("features.InvestmentScreening.ProjectProfitablityStatement.improvementItem.scrapCostShort"), level: 1 },
              { name: t("features.InvestmentScreening.ProjectProfitablityStatement.improvementItem.repairCost"), level: 1 },
              { name: t("features.InvestmentScreening.ProjectProfitablityStatement.improvementItem.consumablesCost"), level: 1 },
            ];

            // 通貨変換用ヘルパー関数（更新前後サマリーの入力はJPY固定）
            const convertValue = (value: number | undefined | null): number | undefined => {
              if (value === undefined || value === null) return undefined;
              if (!selectedCurrency?.name) return value;
              return convertWithRate(new Decimal(value), "JPY", selectedCurrency.name).toNumber();
            };
            const displayCurrencyName = selectedCurrency?.name || "JPY";

            // 差異計算ヘルパー関数（投資計画 - 直近実績または想定）
            const calcDifference = (
              investmentPlan: number | undefined,
              recentActual: number | undefined,
              theoretical: number | undefined
            ): number | undefined => {
              if (investmentPlan === undefined) return undefined;
              const base = recentActual ?? theoretical;
              if (base === undefined) return undefined;
              return investmentPlan - base;
            };

            // 全工程の改善額合計を計算
            let totalScrapCostImprovement = 0;
            let totalRepairCostImprovement = 0;
            let totalConsumablesCostImprovement = 0;

            updateSummaryData.forEach(summary => {
              const scrapDiff = calcDifference(
                summary.profitLoss?.investmentPlan?.scrapCost,
                summary.profitLoss?.recentActual?.scrapCost,
                summary.profitLoss?.theoretical?.scrapCost
              );
              const repairDiff = calcDifference(
                summary.profitLoss?.investmentPlan?.repairCost ?? undefined,
                summary.profitLoss?.recentActual?.repairCost ?? undefined,
                summary.profitLoss?.theoretical?.repairCost ?? undefined
              );
              const consumablesDiff = calcDifference(
                summary.profitLoss?.investmentPlan?.consumablesCost ?? undefined,
                summary.profitLoss?.recentActual?.consumablesCost ?? undefined,
                summary.profitLoss?.theoretical?.consumablesCost ?? undefined
              );

              if (scrapDiff !== undefined) totalScrapCostImprovement += scrapDiff;
              if (repairDiff !== undefined) totalRepairCostImprovement += repairDiff;
              if (consumablesDiff !== undefined) totalConsumablesCostImprovement += consumablesDiff;
            });

            const totalImprovement = totalScrapCostImprovement + totalRepairCostImprovement + totalConsumablesCostImprovement;

            return (
              <>
                {/* {t("features.InvestmentScreening.ProjectProfitablityStatement.heading.improvementByUpdate")}セクション */}
                <Box mt={4}>
                  <Flex alignItems="center" gap={2}>
                    <Heading size={"sm"}>
                      {t("features.InvestmentScreening.ProjectProfitablityStatement.heading.improvementByUpdate")}
                    </Heading>
                    <UpdateImprovementExplanationPopover />
                  </Flex>
                  <Text>
                    unit: {displayCurrencyName}
                  </Text>
                  {/* 各工程ごとのテーブル */}
                  {updateSummaryData.map((summary, summaryIndex) => (
                    <Box key={summary.productionLineId || summaryIndex} mb={4}>
                      <Text fontWeight="bold" mt={2} mb={1} textAlign="center">
                        {summary.productionLineName || summary.productionLineId || t("features.InvestmentScreening.ProjectProfitablityStatement.label.processPrefix", { number: summaryIndex + 1 })}
                      </Text>
                      <Box display={"flex"}>
                        <Box flex={"0 0 auto"}>
                          <Table size={"sm"} variant={"simple"} sx={{ tableLayout: "auto" }} w="auto" className="mm-ceas-table">
                            <Thead>
                              <Tr>
                                <Th h={50} w={70}>{t("features.InvestmentScreening.ProjectProfitablityStatement.tableHeader.no")}</Th>
                                <Th h={50} w={180}>{t("features.InvestmentScreening.ProjectProfitablityStatement.tableHeader.item")}</Th>
                              </Tr>
                            </Thead>
                            <Tbody>
                              {data.map((item, index) => (
                                <Tr key={index}>
                                  <Td>{index + 1}</Td>
                                  <Td style={{ paddingLeft: `${item.level * 20}px` }}>
                                    {item.name}
                                  </Td>
                                </Tr>
                              ))}
                            </Tbody>
                          </Table>
                        </Box>
                        <Box flex={1} overflowX={"auto"}>
                          <Table {...defaultTableStyle} className="mm-ceas-table">
                            <Thead>
                              <Tr>
                                <Th colSpan={2} style={{ ...defaultThStyle }}>{t("features.InvestmentScreening.ProjectProfitablityStatement.tableHeader.current")}</Th>
                                <Th style={{ ...defaultThStyle }}>{t("features.InvestmentScreening.ProjectProfitablityStatement.tableHeader.updateInvestment")}</Th>
                                <Th style={{ ...defaultThStyle }}>{t("features.InvestmentScreening.ProjectProfitablityStatement.tableHeader.difference")}</Th>
                              </Tr>
                              <Tr>
                                <Th style={{ ...defaultThStyle }}>{t("features.InvestmentScreening.ProjectProfitablityStatement.tableHeader.recentActual")}</Th>
                                <Th style={{ ...defaultThStyle }}>{t("features.InvestmentScreening.ProjectProfitablityStatement.tableHeader.theoretical")}</Th>
                                <Th style={{ ...defaultThStyle }}>{t("features.InvestmentScreening.ProjectProfitablityStatement.tableHeader.plan")}</Th>
                                <Th style={{ ...defaultThStyle }}>{t("features.InvestmentScreening.ProjectProfitablityStatement.tableHeader.actualVsPlan")}</Th>
                              </Tr>
                            </Thead>
                            <Tbody>
                              {/* 歩留 */}
                              <Tr>
                                <Td style={defaultTdStyle}>
                                  {summary.profitLoss?.recentActual?.scrapRate !== undefined
                                    ? `${(100 - summary.profitLoss.recentActual.scrapRate).toFixed(2)} %`
                                    : "-"}
                                </Td>
                                <Td style={defaultTdStyle}>
                                  {summary.profitLoss?.theoretical?.scrapRate !== undefined
                                    ? `${(100 - summary.profitLoss.theoretical.scrapRate).toFixed(2)} %`
                                    : "-"}
                                </Td>
                                <Td style={defaultTdStyle}>
                                  {summary.profitLoss?.investmentPlan?.scrapRate !== undefined
                                    ? `${(100 - summary.profitLoss.investmentPlan.scrapRate).toFixed(2)} %`
                                    : "-"}
                                </Td>
                                <Td style={defaultTdStyle}>
                                  {(() => {
                                    const diff = calcDifference(
                                      summary.profitLoss?.investmentPlan?.scrapRate !== undefined
                                        ? 100 - summary.profitLoss.investmentPlan.scrapRate
                                        : undefined,
                                      summary.profitLoss?.recentActual?.scrapRate !== undefined
                                        ? 100 - summary.profitLoss.recentActual.scrapRate
                                        : undefined,
                                      summary.profitLoss?.theoretical?.scrapRate !== undefined
                                        ? 100 - summary.profitLoss.theoretical.scrapRate
                                        : undefined
                                    );
                                    return diff !== undefined ? `${diff.toFixed(2)} %` : "-";
                                  })()}
                                </Td>
                              </Tr>
                              {/* 仕損費 */}
                              <Tr>
                                <Td style={defaultTdStyle}>
                                  {(() => {
                                    const val = convertValue(summary.profitLoss?.recentActual?.scrapCost);
                                    return val !== undefined ? formatNumber(val, displayCurrencyName) : "-";
                                  })()}
                                </Td>
                                <Td style={defaultTdStyle}>
                                  {(() => {
                                    const val = convertValue(summary.profitLoss?.theoretical?.scrapCost);
                                    return val !== undefined ? formatNumber(val, displayCurrencyName) : "-";
                                  })()}
                                </Td>
                                <Td style={defaultTdStyle}>
                                  {(() => {
                                    const val = convertValue(summary.profitLoss?.investmentPlan?.scrapCost);
                                    return val !== undefined ? formatNumber(val, displayCurrencyName) : "-";
                                  })()}
                                </Td>
                                <Td style={defaultTdStyle}>
                                  {(() => {
                                    const diff = calcDifference(
                                      summary.profitLoss?.investmentPlan?.scrapCost,
                                      summary.profitLoss?.recentActual?.scrapCost,
                                      summary.profitLoss?.theoretical?.scrapCost
                                    );
                                    const convertedDiff = convertValue(diff);
                                    return convertedDiff !== undefined ? formatNumber(convertedDiff, displayCurrencyName) : "-";
                                  })()}
                                </Td>
                              </Tr>
                              {/* 修繕費 */}
                              <Tr>
                                <Td style={defaultTdStyle}>
                                  {(() => {
                                    const val = convertValue(summary.profitLoss?.recentActual?.repairCost);
                                    return val !== undefined ? formatNumber(val, displayCurrencyName) : "-";
                                  })()}
                                </Td>
                                <Td style={defaultTdStyle}>
                                  {(() => {
                                    const val = convertValue(summary.profitLoss?.theoretical?.repairCost);
                                    return val !== undefined ? formatNumber(val, displayCurrencyName) : "-";
                                  })()}
                                </Td>
                                <Td style={defaultTdStyle}>
                                  {(() => {
                                    const val = convertValue(summary.profitLoss?.investmentPlan?.repairCost);
                                    return val !== undefined ? formatNumber(val, displayCurrencyName) : "-";
                                  })()}
                                </Td>
                                <Td style={defaultTdStyle}>
                                  {(() => {
                                    const diff = calcDifference(
                                      summary.profitLoss?.investmentPlan?.repairCost ?? undefined,
                                      summary.profitLoss?.recentActual?.repairCost ?? undefined,
                                      summary.profitLoss?.theoretical?.repairCost ?? undefined
                                    );
                                    const convertedDiff = convertValue(diff);
                                    return convertedDiff !== undefined ? formatNumber(convertedDiff, displayCurrencyName) : "-";
                                  })()}
                                </Td>
                              </Tr>
                              {/* 消耗品費 */}
                              <Tr>
                                <Td style={defaultTdStyle}>
                                  {(() => {
                                    const val = convertValue(summary.profitLoss?.recentActual?.consumablesCost);
                                    return val !== undefined ? formatNumber(val, displayCurrencyName) : "-";
                                  })()}
                                </Td>
                                <Td style={defaultTdStyle}>
                                  {(() => {
                                    const val = convertValue(summary.profitLoss?.theoretical?.consumablesCost);
                                    return val !== undefined ? formatNumber(val, displayCurrencyName) : "-";
                                  })()}
                                </Td>
                                <Td style={defaultTdStyle}>
                                  {(() => {
                                    const val = convertValue(summary.profitLoss?.investmentPlan?.consumablesCost);
                                    return val !== undefined ? formatNumber(val, displayCurrencyName) : "-";
                                  })()}
                                </Td>
                                <Td style={defaultTdStyle}>
                                  {(() => {
                                    const diff = calcDifference(
                                      summary.profitLoss?.investmentPlan?.consumablesCost ?? undefined,
                                      summary.profitLoss?.recentActual?.consumablesCost ?? undefined,
                                      summary.profitLoss?.theoretical?.consumablesCost ?? undefined
                                    );
                                    const convertedDiff = convertValue(diff);
                                    return convertedDiff !== undefined ? formatNumber(convertedDiff, displayCurrencyName) : "-";
                                  })()}
                                </Td>
                              </Tr>
                            </Tbody>
                          </Table>
                        </Box>
                      </Box>
                    </Box>
                  ))}
                </Box>

                {/* {t("features.InvestmentScreening.ProjectProfitablityStatement.heading.improvementByUpdateTotal")}セクション */}
                <Box mt={2}>
                  <Flex alignItems="center" gap={2}>
                    <Heading size={"sm"}>
                      {t("features.InvestmentScreening.ProjectProfitablityStatement.heading.improvementByUpdateTotal")}
                    </Heading>
                    <ImprovementTotalExplanationPopover />
                  </Flex>
                  <Text>
                    unit: {displayCurrencyName}
                  </Text>
                  <Table size="sm" variant="simple" sx={{ tableLayout: "auto" }} w="auto" className="mm-ceas-table">
                    <Thead>
                      <Tr>
                        <Th w={70}>{t("features.InvestmentScreening.ProjectProfitablityStatement.tableHeader.no")}</Th>
                        <Th w={180}>{t("features.InvestmentScreening.ProjectProfitablityStatement.tableHeader.item")}</Th>
                        <Th textAlign="right">{t("features.InvestmentScreening.ProjectProfitablityStatement.tableHeader.amount")}</Th>
                      </Tr>
                    </Thead>
                    <Tbody>
                      <Tr>
                        <Td>1</Td>
                        <Td>{t("features.InvestmentScreening.ProjectProfitablityStatement.improvementItem.scrapCostShort")}</Td>
                        <Td textAlign="right">{formatNumber(convertValue(totalScrapCostImprovement) ?? 0, displayCurrencyName)} {displayCurrencyName}</Td>
                      </Tr>
                      <Tr>
                        <Td>2</Td>
                        <Td>{t("features.InvestmentScreening.ProjectProfitablityStatement.improvementItem.repairCost")}</Td>
                        <Td textAlign="right">{formatNumber(convertValue(totalRepairCostImprovement) ?? 0, displayCurrencyName)} {displayCurrencyName}</Td>
                      </Tr>
                      <Tr>
                        <Td>3</Td>
                        <Td>{t("features.InvestmentScreening.ProjectProfitablityStatement.improvementItem.consumablesCost")}</Td>
                        <Td textAlign="right">{formatNumber(convertValue(totalConsumablesCostImprovement) ?? 0, displayCurrencyName)} {displayCurrencyName}</Td>
                      </Tr>
                      <Tr fontWeight="bold" bg="gray.100">
                        <Td>4</Td>
                        <Td>{t("features.InvestmentScreening.ProjectProfitablityStatement.tableHeader.total")}</Td>
                        <Td textAlign="right">{formatNumber(convertValue(totalImprovement) ?? 0, displayCurrencyName)} {displayCurrencyName}</Td>
                      </Tr>
                    </Tbody>
                  </Table>
                  <Text fontSize="xs" color="gray.600" mt={2}>
                    {t("features.InvestmentScreening.ProjectProfitablityStatement.message.costImprovementNote")}
                  </Text>
                </Box>
              </>
            );
          })()}

          {/* {t("features.InvestmentScreening.ProjectProfitablityStatement.heading.paybackPeriod")} */}
          {(
            <Box mt={2}>
              <Flex alignItems="center" gap={2}>
                <Heading size={"sm"}>
                  {t("features.InvestmentScreening.ProjectProfitablityStatement.heading.paybackPeriod")}
                </Heading>
                <PaybackPeriodPopover isSimpleMode={isSimpleMode} />
              </Flex>
              <Text>
                unit: {selectedCurrency.name}
              </Text>
              {(() => {
                const data = [
                  PaybackPeriodItems.capexAmount,
                  PaybackPeriodItems.operatingProfit,
                  PaybackPeriodItems.interestRate,
                  PaybackPeriodItems.equipmentDepreciation,
                  PaybackPeriodItems.paybackPeriod,
                ];
                return (
                  <Box display={"flex"}>
                    <Box flex={"0 0 auto"} style={{ minWidth: "350px" }}>
                      <Table size={"sm"} variant={"simple"} sx={{ tableLayout: "auto" }} className="mm-ceas-table">
                        <Thead>
                          <Tr>
                            <Th h={50} minW={70}>{t("features.InvestmentScreening.ProjectProfitablityStatement.tableHeader.no")}</Th>
                            <Th h={50} minW={250}>{t("features.InvestmentScreening.ProjectProfitablityStatement.tableHeader.item")}</Th>
                          </Tr>
                        </Thead>
                        <Tbody>
                          {data.map((item, index) => (
                            <Tr>
                              <Td>{String.fromCharCode(65 + index)}</Td>
                              <Td style={{ paddingLeft: `${item.level * 20}px` }}>
                                {item.name}
                              </Td>
                            </Tr>
                          ))}
                        </Tbody>
                      </Table>
                    </Box>
                    <Box flex={1} overflowX={"auto"}>
                      <Table {...defaultTableStyle} className="mm-ceas-table">
                        <Thead>
                          <Tr>
                            {fiscalYearsString.map((_, index) => (
                              <Th style={{ ...defaultThStyle }}>
                                {t("features.InvestmentScreening.ProjectProfitablityStatement.tableHeader.yearSuffix", { year: index + 1 })}
                              </Th>
                            ))}
                            <Th style={{ ...defaultThStyle }} rowSpan={2}>
                              {t("features.InvestmentScreening.ProjectProfitablityStatement.tableHeader.total")}
                            </Th>
                          </Tr>
                          <Tr>
                            {fiscalYearsString.map((year) => (
                              <Th style={{ ...defaultThStyle }}>
                                {year}
                              </Th>
                            ))}
                          </Tr>
                        </Thead>
                        <Tbody>
                          <Tr>
                            {/* 設備投資額 */}
                            {fiscalYearsString.map((_, index) => (
                              <Td style={defaultTdStyle}>
                                {formatNumber(displayCapexAmountByYear[index] || 0, selectedCurrency.name)}
                              </Td>
                            ))}
                            <Td style={defaultTdStyle}>
                              {formatNumber(
                                displayCapexAmountByYear.reduce((sum, value) => sum.plus(value || 0), new Decimal(0)),
                                selectedCurrency.name
                              )}
                            </Td>
                          </Tr>
                          <Tr>
                            {/* 営業利益 */}
                            {fiscalYearsString.map((_, index) => (
                              <Td style={defaultTdStyle}>
                                {formatNumber(displayProfitByYear[index] || 0, selectedCurrency.name)}
                              </Td>
                            ))}
                            <Td style={defaultTdStyle}>
                              {formatNumber(
                                displayProfitByYear.reduce((sum, value) => sum.plus(value || 0), new Decimal(0)),
                                selectedCurrency.name
                              )}
                            </Td>
                          </Tr>
                          <Tr>
                            {/* 金利 */}
                            {fiscalYearsString.map((_, index) => (
                              <Td style={defaultTdStyle}>
                                {formatNumber(dispInterestRateByYear[index] || 0, selectedCurrency.name)}
                              </Td>
                            ))}
                            <Td style={defaultTdStyle}>
                              {formatNumber(
                                dispInterestRateByYear.reduce((sum, value) => sum.plus(value || 0), new Decimal(0)),
                                selectedCurrency.name
                              )}
                            </Td>
                          </Tr>
                          <Tr>
                            {/* 投資対象設備の減価償却費 */}
                            {fiscalYearsString.map((_, index) => (
                              <Td style={defaultTdStyle}>
                                {formatNumber(displayEquipmentDepreciationByYear[index] || 0, selectedCurrency.name)}
                              </Td>
                            ))}
                            <Td style={defaultTdStyle}>
                              {formatNumber(
                                displayEquipmentDepreciationByYear.reduce((sum, value) => sum.plus(value || 0), new Decimal(0)),
                                selectedCurrency.name
                              )}
                            </Td>
                          </Tr>
                          <Tr>
                            {/* {t("features.InvestmentScreening.ProjectProfitablityStatement.heading.paybackPeriod")} */}
                            {fiscalYearsString.map((_, index) => (
                              <Td key={index} style={defaultTdStyle}></Td>
                            ))}
                            <Td style={defaultTdStyle}>
                              {paybackPeriod}
                            </Td>
                          </Tr>
                        </Tbody>
                      </Table>
                    </Box>
                  </Box>
                )
              })()}
            </Box>
          )}

          {/* {t("features.InvestmentScreening.ProjectProfitablityStatement.heading.incrementalCashFlow")} */}
          {(
            <Box mt={2}>
              <Flex alignItems="center" gap={2}>
                <Heading size={"sm"}>
                  {t("features.InvestmentScreening.ProjectProfitablityStatement.heading.incrementalCashFlow")}
                </Heading>
                <RecoverySimulationExplanationPopover isSimpleMode={isSimpleMode} />
              </Flex>
              <Text>
                unit: {selectedCurrency.name}
              </Text>
              {(() => {
                const data = [
                  RecoverySimulationItems.capexAmount,
                  RecoverySimulationItems.recoveredResources,
                  RecoverySimulationItems.operatingProfit,
                  RecoverySimulationItems.equipmentDepreciation,
                  RecoverySimulationItems.uncollectedAmount,
                  RecoverySimulationItems.collectionPeriod,
                ];
                return (
                  <Box display={"flex"}>
                    <Box flex={"0 0 auto"} style={{ minWidth: "350px" }}>
                      <Table size={"sm"} variant={"simple"} sx={{ tableLayout: "auto" }} className="mm-ceas-table">
                        <Thead>
                          <Tr>
                            <Th h={50} minW={70}>{t("features.InvestmentScreening.ProjectProfitablityStatement.tableHeader.no")}</Th>
                            <Th h={50} minW={250}>{t("features.InvestmentScreening.ProjectProfitablityStatement.tableHeader.item")}</Th>
                          </Tr>
                        </Thead>
                        <Tbody>
                          {data.map((item, index) => (
                            <Tr>
                              <Td>{String.fromCharCode(65 + index)}</Td>
                              <Td style={{ paddingLeft: `${item.level * 20}px` }}>
                                {item.name}
                              </Td>
                            </Tr>
                          ))}
                        </Tbody>
                      </Table>
                    </Box>
                    <Box flex={1} overflowX={"auto"}>
                      <Table {...defaultTableStyle} className="mm-ceas-table">
                        <Thead>
                          <Tr>
                            {fiscalYearsString.map((_, index) => (
                              <Th style={{ ...defaultThStyle }}>
                                {t("features.InvestmentScreening.ProjectProfitablityStatement.tableHeader.yearSuffix", { year: index + 1 })}
                              </Th>
                            ))}
                            <Th style={{ ...defaultThStyle }} rowSpan={2}>
                              {t("features.InvestmentScreening.ProjectProfitablityStatement.tableHeader.total")}
                            </Th>
                          </Tr>
                          <Tr>
                            {fiscalYearsString.map((year) => (
                              <Th style={{ ...defaultThStyle }}>
                                {year}
                              </Th>
                            ))}
                          </Tr>
                        </Thead>
                        <Tbody>
                          <Tr>
                            {/* 設備投資額 */}
                            {fiscalYearsString.map((_, index) => (
                              <Td style={defaultTdStyle}>
                                {formatNumber(displayCapexAmountByYear[index] || 0, selectedCurrency.name)}
                              </Td>
                            ))}
                            <Td style={defaultTdStyle}>
                              {formatNumber(
                                displayCapexAmountByYear.reduce((sum, value) => sum.plus(value || 0), new Decimal(0)),
                                selectedCurrency.name
                              )}
                            </Td>
                          </Tr>
                          <Tr>
                            {/* 回収原資 */}
                            {fiscalYearsString.map((_, index) => (
                              <Td style={defaultTdStyle}>
                                {formatNumber(displayRecoveredResourcesByYear[index] || 0, selectedCurrency.name)}
                              </Td>
                            ))}
                            <Td style={defaultTdStyle}>
                              {formatNumber(
                                displayRecoveredResourcesByYear.reduce((sum, value) => sum.plus(value || 0), new Decimal(0)),
                                selectedCurrency.name
                              )}
                            </Td>
                          </Tr>
                          <Tr>
                            {/* 営業利益 */}
                            {fiscalYearsString.map((_, index) => (
                              <Td style={defaultTdStyle}>
                                {formatNumber(displayProfitByYear[index] || 0, selectedCurrency.name)}
                              </Td>
                            ))}
                            <Td style={defaultTdStyle}>
                              {formatNumber(
                                displayProfitByYear.reduce((sum, value) => sum.plus(value || 0), new Decimal(0)),
                                selectedCurrency.name
                              )}
                            </Td>
                          </Tr>
                          <Tr>
                            {/* 投資対象設備の減価償却費 */}
                            {fiscalYearsString.map((_, index) => (
                              <Td style={defaultTdStyle}>
                                {formatNumber(displayEquipmentDepreciationByYear[index] || 0, selectedCurrency.name)}
                              </Td>
                            ))}
                            <Td style={defaultTdStyle}>
                              {formatNumber(
                                displayEquipmentDepreciationByYear.reduce((sum, value) => sum.plus(value || 0), new Decimal(0)),
                                selectedCurrency.name
                              )}
                            </Td>
                          </Tr>
                          <Tr>
                            {/* 未回収額 */}
                            {fiscalYearsString.map((_, index) => (
                              <Td style={defaultTdStyle}>
                                {formatNumber(displayUncollectedAmountByYear[index] || 0, selectedCurrency.name)}
                              </Td>
                            ))}
                            <Td style={defaultTdStyle}>
                              {"-"}
                            </Td>
                          </Tr>
                          <Tr>
                            {/* 回収時点 */}
                            {fiscalYearsString.map((_, index) => (
                              <Td key={index} style={defaultTdStyle}></Td>
                            ))}
                            <Td style={defaultTdStyle}>
                              {collectionPeriod}
                            </Td>
                          </Tr>
                        </Tbody>
                      </Table>
                    </Box>
                  </Box>
                )
              })()}
            </Box>
          )}

          {/* {t("features.InvestmentScreening.ProjectProfitablityStatement.heading.dcf")} */}
          {(
            <Box mt={2}>
              <Flex alignItems="center" gap={2}>
                <Heading size={"sm"}>
                  {t("features.InvestmentScreening.ProjectProfitablityStatement.heading.dcf")}
                </Heading>
                <DCFExplanationPopover discountRate={discountRate} isSimpleMode={isSimpleMode} />
              </Flex>
              <Text>
                unit: {selectedCurrency.name}
              </Text>
              {(() => {
                const data = [
                  DCFItems.freeCashFlow,
                  DCFItems.operatingProfit,
                  DCFItems.taxRate,
                  DCFItems.depreciation,
                  DCFItems.capexAmount,
                  DCFItems.wacc,
                  DCFItems.presentValue,
                  DCFItems.netPresentValue,
                ];
                return (
                  <Box display={"flex"}>
                    <Box flex={"0 0 auto"} style={{ minWidth: "350px" }}>
                      <Table size={"sm"} variant={"simple"} sx={{ tableLayout: "auto" }} className="mm-ceas-table">
                        <Thead>
                          <Tr>
                            <Th h={50} minW={70}>{t("features.InvestmentScreening.ProjectProfitablityStatement.tableHeader.no")}</Th>
                            <Th h={50} minW={250}>{t("features.InvestmentScreening.ProjectProfitablityStatement.tableHeader.item")}</Th>
                          </Tr>
                        </Thead>
                        <Tbody>
                          {data.map((item, index) => (
                            <Tr>
                              <Td>{String.fromCharCode(65 + index)}</Td>
                              <Td style={{ paddingLeft: `${item.level * 20}px` }}>
                                {item.name}
                              </Td>
                            </Tr>
                          ))}
                        </Tbody>
                      </Table>
                    </Box>
                    <Box flex={1} overflowX={"auto"}>
                      <Table {...defaultTableStyle} className="mm-ceas-table">
                        <Thead>
                          <Tr>
                            {[t("features.InvestmentScreening.ProjectProfitablityStatement.tableHeader.yearSuffix", { year: 0 }), ...fiscalYearsString.map((_, index) => t("features.InvestmentScreening.ProjectProfitablityStatement.tableHeader.yearSuffix", { year: index + 1 }))].map((label, index) => (
                              <Th key={index} style={defaultThStyle}>{label}</Th>
                            ))}
                            <Th style={{ ...defaultThStyle }} rowSpan={2}>
                              {t("features.InvestmentScreening.ProjectProfitablityStatement.tableHeader.total")}
                            </Th>
                          </Tr>
                          <Tr>
                            {[t('common.dateFormat.fiscalYear', { year: (parseInt(fiscalYearsString[0], 10) - 1) }), ...fiscalYearsString.map((year) => `${year}`)].map((year, index) => (
                              <Th key={index} style={defaultThStyle}>{year}</Th>
                            ))}
                          </Tr>
                        </Thead>
                        <Tbody>
                          <Tr>
                            {/* フリーキャッシュフロー */}
                            {fiscalYearsAdjusted.map((_, index) => (
                              <Td style={defaultTdStyle}>
                                {formatNumber(freeCashFlowAdjustedByYear[index] || 0, selectedCurrency.name)}
                              </Td>
                            ))}
                            <Td style={defaultTdStyle}>
                              {formatNumber(
                                freeCashFlowAdjustedByYear.reduce((sum, value) => sum.plus(value || 0), new Decimal(0)),
                                selectedCurrency.name
                              )}
                            </Td>
                          </Tr>
                          <Tr>
                            {/* 営業利益 */}
                            {fiscalYearsAdjusted.map((_, index) => (
                              <Td style={defaultTdStyle}>
                                {formatNumber(profitAdjustedByYear[index] || 0, selectedCurrency.name)}
                              </Td>
                            ))}
                            <Td style={defaultTdStyle}>
                              {formatNumber(
                                profitAdjustedByYear.reduce((sum, value) => sum.plus(value || 0), new Decimal(0)),
                                selectedCurrency.name
                              )}
                            </Td>
                          </Tr>
                          <Tr>
                            {/* 目標税率（実効税率） */}
                            {fiscalYearsAdjusted.map((_, index) => (
                              <Td style={defaultTdStyle}>
                                {(taxRateAdjustedByYear[index] || 0)?.toFixed(1)?.replace(/\B(?=(\d{3})+(?!\d))/g, ',') + "%"}
                              </Td>
                            ))}
                            <Td style={defaultTdStyle}>
                              {"-"}
                            </Td>
                          </Tr>
                          <Tr>
                            {/* 減価償却費 */}
                            {fiscalYearsAdjusted.map((_, index) => (
                              <Td style={defaultTdStyle}>
                                {formatNumber(depreciationAdjustedByYear[index] || 0, selectedCurrency.name)}
                              </Td>
                            ))}
                            <Td style={defaultTdStyle}>
                              {formatNumber(
                                depreciationAdjustedByYear.reduce((sum, value) => sum.plus(value || 0), new Decimal(0)),
                                selectedCurrency.name
                              )}
                            </Td>
                          </Tr>
                          <Tr>
                            {/* 設備投資額 */}
                            {fiscalYearsAdjusted.map((_, index) => (
                              <Td style={defaultTdStyle}>
                                {formatNumber(capexAmountAdjustedByYear[index] || 0, selectedCurrency.name)}
                              </Td>
                            ))}
                            <Td style={defaultTdStyle}>
                              {formatNumber(
                                capexAmountAdjustedByYear.reduce((sum, value) => sum.plus(value || 0), new Decimal(0)),
                                selectedCurrency.name
                              )}
                            </Td>
                          </Tr>
                          <Tr>
                            {/* 加重平均資本コスト */}
                            {fiscalYearsAdjusted.map((_, index) => (
                              <Td style={defaultTdStyle}>
                                {(discountRateAdjustedByYear[index] || 0)?.toFixed(1)?.replace(/\B(?=(\d{3})+(?!\d))/g, ',') + "%"}
                              </Td>
                            ))}
                            <Td style={defaultTdStyle}>
                              {"-"}
                            </Td>
                          </Tr>
                          <Tr>
                            {/* 現在価値 */}
                            {fiscalYearsAdjusted.map((_, index) => (
                              <Td style={defaultTdStyle}>
                                {formatNumber(presentValueAdjustedByYear[index] || 0, selectedCurrency.name)}
                              </Td>
                            ))}
                            <Td style={defaultTdStyle}>
                              {formatNumber(
                                presentValueAdjustedByYear.reduce((sum, value) => sum.plus(value || 0), new Decimal(0)),
                                selectedCurrency.name
                              )}
                            </Td>
                          </Tr>
                          <Tr>
                            {/* 正味現在価値 */}
                            {fiscalYearsAdjusted.map((_, index) => (
                              <Td key={index} style={defaultTdStyle}></Td>
                            ))}
                            <Td style={defaultTdStyle}>
                              {formatNumber(displayNetPresentValue || 0, selectedCurrency.name)}
                            </Td>
                          </Tr>
                        </Tbody>
                      </Table>
                    </Box>
                  </Box>
                )
              })()}
            </Box>
          )}

        </Stack>

      )}


      <Flex justify="flex-end" mt={12}>
        {/* Excel出力（損益非表示モード時は出力対象の表が無いため非表示） */}
        {!isProfitLossHiddenMode && (
          <Box >
            <Button onClick={exportCapexToExcelPerPartNo}
              colorScheme="green"
            >
              {t("features.InvestmentScreening.ProjectProfitablityStatement.button.excelExport")}
            </Button>
          </Box>
        )}
        <Spacer />
        <Box>
          <Button
            colorScheme="blue"
            onClick={onOpenFileUploadModal}
            isDisabled={!pagePermissions?.isEditable}
          >
            {t("features.InvestmentScreening.ProjectProfitablityStatement.button.fileUpload")}
          </Button>
        </Box>
        <Box>
          <Button
            ml="10px"
            colorScheme="blue"
            name="companyDivision"
            onClick={handleDepartmentConfirm}
            isDisabled={!pagePermissions?.eabledCompanyDivision}
            ref={refs["companyDivision"]}
          >
            {t("features.InvestmentScreening.ProjectProfitablityStatement.button.divisionConfirm")}
          </Button>
          <ConfirmorList confirmorInfo={names["companyDivision"]} width={buttonWidths["companyDivision"]} />
        </Box>
        <Box>
          <Button
            ml="10px"
            colorScheme="blue"
            name="companyHeadDivision"
            onClick={handleDepartmentConfirm}
            isDisabled={!pagePermissions?.eabledCompanyHeadDivision}
            ref={refs["companyHeadDivision"]}
          >
            {t("features.InvestmentScreening.ProjectProfitablityStatement.button.headDivisionConfirm")}
          </Button>
          <ConfirmorList confirmorInfo={names["companyHeadDivision"]} width={buttonWidths["companyHeadDivision"]} />
        </Box>
      </Flex>

      {/* ファイルアップロードモーダル */}
      <FileUploadModal
        isOpen={isOpenFileUploadModal}
        onClose={onCloseFileUploadModal}
        title={TITLE}
        projectId={projectId}
        folderName={"Others"}
      />

      {/* 確認モーダル */}
      <Modal isOpen={isOpen} onClose={onClose} size="md">
        <ModalOverlay />
        <ModalContent>
          <ModalHeader>{t("features.InvestmentScreening.ProjectProfitablityStatement.modal.confirmTitle", { title: TITLE })}</ModalHeader>
          <ModalCloseButton />
          <ModalBody>
            <Text>
              {isProfitLossHiddenMode
                ? t("features.InvestmentScreening.ProjectProfitablityStatement.modal.profitLossHiddenConfirmMessage")
                : t("features.InvestmentScreening.ProjectProfitablityStatement.modal.confirmMessage")}
            </Text>
          </ModalBody>
          <ModalFooter>
            <Button colorScheme="blue" mr={3} isDisabled={isSaving} isLoading={isSaving} onClick={handleConfirm}>
              {t("features.InvestmentScreening.ProjectProfitablityStatement.button.save")}
            </Button>
            <Button variant="ghost" onClick={onClose}>
              {t("features.InvestmentScreening.ProjectProfitablityStatement.button.cancel")}
            </Button>
          </ModalFooter>
        </ModalContent>
      </Modal>
    </Box>
  );
};

export default ProjectProfitablityStatement;
